 // client/src/coms/views/canvas_parts/CanvasModelViewport.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import {
  drawAirbrushContinuousWithinSelection,
  drawAirbrushSprayWithinSelection,
  getAirbrushRendererSettings,
} from './canvas_room_view_parts/airbrushUtils';
import {
  drawImportedBrushImageCurveSegmentWithinSelection,
  drawImportedBrushImageFinalTailWithinSelection,
  drawImportedBrushImageSegment,
} from './canvas_room_view_parts/importedBrushRenderer';
import { drawPencilStroke } from './canvas_room_view_parts/pencilUtils';
import { createModelPhysicalPaint, isPhysicalPaint3dAvailable } from './canvas_room_view_parts/modelPhysicalPaint';
import {
  createCurveStrokeBuilder,
  createPointStabiliser,
  createPressureSmoother,
  getCompensatedDabFlow,
  readPointerPressure,
} from './canvas_room_view_parts/strokeInput';
import {
  FACE_SELECTION_SHAPE,
  applyFacesToSelection,
  buildStableMeshKey,
  cloneFaceMap,
  collectConnectedFaces,
  collectFacesWithMaterialIndex,
  countSelectedFaces,
  createFaceSelection,
  deserializeSelectionSnapshot,
  findUvOverlapWithSelection,
  getFaceMaterialIndex,
  growFaceSet,
  serializeSelectionSnapshot,
  shrinkFaceSet,
} from './canvas_room_view_parts/modelFaceSelection';

const DEFAULT_BACKGROUND = 0x666666;
const GRID_SIZE = 20;
const GRID_DIVISIONS = 20;
const MODEL_DB_NAME = 'voxemera-model-viewer';
const MODEL_DB_VERSION = 4;
const MODEL_STORE_NAME = 'models';
const MODEL_PAINT_STORE_NAME = 'model-paint-sessions';
const MODEL_PAINT_CACHE_VERSION = 7;
const LAST_MODEL_KEY = 'last-model';
const GENERATED_UV_CACHE_PREFIX = 'generated-uv-cache:armorpaint-v2:';
const GENERATED_UV_CACHE_VERSION = 2;
const TRANSFORM_PREFIX = 'model-transform:';
const UV_TILE_PREFIX = 'uv-tiles:';
const UV_LAYOUT_MODE_PREFIX = 'uv-layout-mode:';
const UV_LAYOUT_MODE_GENERATED = 'generated';
const UV_LAYOUT_MODE_ORIGINAL = 'original';
const DEFAULT_GENERATED_UV_RESOLUTION = 2048;
const ORIGINAL_UV_TEXTURE_RESOLUTION = 4096;
const UV_RESOLUTION_OPTIONS = [1080, 2048, 4096, 8192];
const EXPORT_BAKE_TEST_MIN_RESOLUTION = 8192;
// A paint session currently owns three full RGBA canvases plus one full RGBA
// WebGL render target. Limiting only maxTextureSize can therefore allow a
// technically valid atlas that consumes several gigabytes and freezes the
// browser during the first model import. Keep the combined allocation inside
// a predictable budget before any backing stores are created. The generated-UV
// resolution selector is now limited only by the GPU's texture dimension; the
// requested value itself is never forced back by this estimate.
const UV_PAINT_SESSION_FULL_SIZE_BUFFER_COUNT = 4;
const UV_PAINT_SESSION_MEMORY_BUDGET_BYTES = 512 * 1024 * 1024;
const UV_RESOLUTION_PREFIX = 'uv-resolution:';

const MAX_PROJECTION_DEPTH_CACHE_ENTRIES = 4;
const MAX_PROJECTION_DEPTH_DIMENSION = 1024;
const PROJECTION_CAMERA_CACHE_PRECISION = 4;
// Start persistence immediately after pointer-up. The work still runs from a
// timer, so it does not extend the pointer-up handler itself.
const PROJECTION_SAVE_DEBOUNCE_MS = 0;

// Multiple CanvasModelViewport instances can exist briefly during the Canvas UI
// layout transition.  They all restore the same persisted model/paint data.
// Share immutable startup reads and OBJ parsing so those instances do not fight
// over IndexedDB and parse the same 16 MB mesh twice on the main thread.
let savedModelFileRestorePromise = null;
const modelPaintRecordRestorePromises = new Map();
const parsedObjRestorePromises = new Map();

function getModelFileRestoreKey(file) {
  if (!file) return '';
  return [
    String(file.name || ''),
    Math.max(0, Number(file.size) || 0),
    Math.max(0, Number(file.lastModified) || 0),
  ].join('|');
}

function cloneParsedObjectForRuntime(source) {
  if (!source) return source;
  const clone = source.clone(true);
  clone.traverse((child) => {
    if (!child?.isMesh) return;
    if (child.geometry?.clone) child.geometry = child.geometry.clone();
    if (Array.isArray(child.material)) {
      child.material = child.material.map((material) => material?.clone ? material.clone() : material);
    } else if (child.material?.clone) {
      child.material = child.material.clone();
    }
  });
  return clone;
}

function createModelLoadProfiler() {
  const noop = () => {};
  const runSync = (step, reason, callback) => callback();
  const runAsync = async (step, reason, callback) => callback();
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
  return {
    mark: noop,
    measureSync: runSync,
    measureAsync: runAsync,
    nextFrame,
    getEntries: () => [],
  };
}

const DEFAULT_3D_PAINT_GLOSS = 35;
const DEFAULT_3D_PAINT_HEIGHT = 12;
const DEFAULT_3D_PAINT_METALNESS = 0;

// Plain-pen strokes now store pure coverage in the projected stroke texture
// and carry their opacity on the material. The old path multiplied opacity
// and pen pressure into the texture alpha, which the hard-pen shaders then
// thresholded: anything under ~40% vanished on release and anything above
// became fully opaque. Strokes saved before this change have no
// projectionOpacityMode and keep rendering exactly as they did.
function getProjectionStrokeOpacity(stroke) {
  const config = stroke?.brushConfig;
  if (!config || config.projectionOpacityMode !== 'material') return 1;
  if (stroke.isEraser || stroke.isAirbrush || stroke.isPencil || stroke.isImportedBrush || stroke.isImageDecal || stroke.isTextDecal) return 1;
  const opacity = Number(config.opacity);
  return Number.isFinite(opacity) ? Math.max(0.01, Math.min(1, opacity)) : 1;
}

const DEFAULT_PBR_TEXTURE_SCALE = 100;
const FLAT_NORMAL_RGB = { red: 128, green: 128, blue: 255 };
const PBR_IMAGE_FILE_EXTENSION_PATTERN = /\.(png|jpe?g|webp|bmp)$/i;
const PBR_MAP_TYPE_PATTERNS = [
  ['albedo', /(?:^|[^a-z0-9])(albedo|base ?colou?r|basecolou?r|basecolor|diffuse|color|colour|col|alb)(?:[^a-z0-9]|$)/i],
  ['normal', /(?:^|[^a-z0-9])(normal|normals|nor|nrm|opengl|directx)(?:[^a-z0-9]|$)/i],
  ['roughness', /(?:^|[^a-z0-9])(roughness|rough|rgh)(?:[^a-z0-9]|$)/i],
  ['metallic', /(?:^|[^a-z0-9])(metallic|metalness|metal|mtl|met)(?:[^a-z0-9]|$)/i],
  ['height', /(?:^|[^a-z0-9])(height|displacement|disp|bump|depth)(?:[^a-z0-9]|$)/i],
  ['ao', /(?:^|[^a-z0-9])(ao|ambient ?occlusion|occlusion)(?:[^a-z0-9]|$)/i],
];
const PBR_MAP_TYPE_LABELS = {
  albedo: 'Albedo',
  normal: 'Normal',
  roughness: 'Roughness',
  metallic: 'Metallic',
  height: 'Height',
  ao: 'AO',
};


function disposeMaterial(material) {
  if (!material) return;
  const materials = Array.isArray(material) ? material : [material];
  materials.forEach((entry) => {
    if (!entry) return;
    Object.keys(entry).forEach((key) => {
      const value = entry[key];
      if (value && value.isTexture && typeof value.dispose === 'function' && !value.userData?.voxemeraSharedTexture) value.dispose();
    });
    if (typeof entry.dispose === 'function') entry.dispose();
  });
}

function disposeObject(object) {
  if (!object || typeof object.traverse !== 'function') return;
  const disposedTextures = new Set();
  object.traverse((child) => {
    if (child?.geometry && typeof child.geometry.dispose === 'function') child.geometry.dispose();
    if (child?.userData?.voxemeraSourceGeometry && child.userData.voxemeraSourceGeometry !== child.geometry && typeof child.userData.voxemeraSourceGeometry.dispose === 'function') {
      child.userData.voxemeraSourceGeometry.dispose();
    }
    if (child?.userData?.voxemeraUvDebugTexture && typeof child.userData.voxemeraUvDebugTexture.dispose === 'function') {
      child.userData.voxemeraUvDebugTexture.dispose();
    }
    if (child?.material && !child?.userData?.voxemeraSharedSourceMaterial) disposeMaterial(child.material);
  });
  if (object.userData?.voxemeraUvDebugTexture && !disposedTextures.has(object.userData.voxemeraUvDebugTexture)) {
    object.userData.voxemeraUvDebugTexture.dispose();
    disposedTextures.add(object.userData.voxemeraUvDebugTexture);
  }
}



function cloneTextureImageForExport(image) {
  if (!image) return image;
  const width = Number(image.videoWidth || image.naturalWidth || image.width) || 0;
  const height = Number(image.videoHeight || image.naturalHeight || image.height) || 0;
  if (!width || !height || typeof document === 'undefined') return image;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: false });
    if (!context) return image;
    context.drawImage(image, 0, 0, width, height);
    return canvas;
  } catch {
    return image;
  }
}

function cloneTextureForExport(texture, textureCache) {
  if (!texture?.isTexture) return texture;
  if (textureCache.has(texture)) return textureCache.get(texture);

  // The current Voxemera appearance includes GPU render-target textures and
  // projection/depth textures whose pixels do not exist in texture.image.
  // Calling Texture.clone() on those resources creates an empty texture in a
  // second renderer, which made the export working copy appear white.
  //
  // Keep the live texture object read-only while the popup is only previewing
  // the replica. Geometry and materials are still independent. The later bake
  // must assign newly-created textures to the replica rather than modifying
  // any of these shared source textures.
  textureCache.set(texture, texture);
  return texture;
}

function cloneMaterialForExport(material, materialCache, textureCache) {
  if (!material) return material;
  if (materialCache.has(material)) return materialCache.get(material);

  // The replica only needs the live material long enough to render the three
  // UV bake passes. Sharing it read-only avoids ShaderMaterial.clone(), which
  // recursively clones uniforms and attempts to serialize every GPU texture.
  // The bake replaces these references with independent MeshStandardMaterials
  // before the exported file is written.
  materialCache.set(material, material);
  return material;
}

function createExactExportReplica(source) {
  if (!source) throw new Error('There is no loaded model to copy.');

  // Object3D/Mesh.clone() clones materials before we can replace them. For
  // Voxemera shader materials that recursively serializes render-target
  // textures thousands of times. Temporarily substitute one texture-free
  // material while cloning the hierarchy, then restore the live model
  // immediately and attach its materials to the replica read-only.
  const placeholderMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const swappedMaterials = [];
  let sourceNodeCount = 0;
  source.traverse((node) => {
    sourceNodeCount += 1;
    if (!node?.material) return;
    swappedMaterials.push([node, node.material]);
    node.material = Array.isArray(node.material)
      ? node.material.map(() => placeholderMaterial)
      : placeholderMaterial;
  });

  let replica;
  try {
    replica = source.clone(true);
  } finally {
    swappedMaterials.forEach(([node, material]) => {
      node.material = material;
    });
    placeholderMaterial.dispose();
  }

  const sourceNodes = [];
  const replicaNodes = [];
  source.traverse((node) => sourceNodes.push(node));
  replica.traverse((node) => replicaNodes.push(node));

  const geometryCache = new Map();
  const materialCache = new Map();
  const textureCache = new Map();

  replicaNodes.forEach((node, index) => {
    const sourceNode = sourceNodes[index];
    if (!sourceNode) {
      return;
    }

    node.userData = { ...(sourceNode.userData || {}), voxemeraExportReplica: true };
    delete node.userData.voxemeraSourceGeometry;
    delete node.userData.voxemeraSourceMaterial;
    delete node.userData.voxemeraPaintMaterial;

    if (sourceNode.geometry?.isBufferGeometry) {
      let geometry = geometryCache.get(sourceNode.geometry);
      if (!geometry) {
        geometry = sourceNode.geometry.clone();
        geometryCache.set(sourceNode.geometry, geometry);
      }
      node.geometry = geometry;
    }

    if (sourceNode.material) {
      // Dispose only the placeholder clone created during source.clone(true).
      disposeMaterial(node.material);
      if (Array.isArray(sourceNode.material)) {
        node.material = sourceNode.material.map((material) => cloneMaterialForExport(material, materialCache, textureCache));
      } else {
        node.material = cloneMaterialForExport(sourceNode.material, materialCache, textureCache);
      }
      node.userData.voxemeraSharedSourceMaterial = true;
    }

    if (sourceNode.skeleton && node.skeleton) {
      node.bindMatrix?.copy?.(sourceNode.bindMatrix);
      node.bindMatrixInverse?.copy?.(sourceNode.bindMatrixInverse);
    }
  });

  replica.name = `${source.name || 'Model'} Export Replica`;
  replica.userData = {
    ...(source.userData || {}),
    voxemeraExportReplica: true,
    voxemeraExportReplicaCreatedAt: Date.now(),
  };
  replica.updateMatrixWorld(true);
  return replica;
}

function downloadExportBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getBakedExportFilename(modelName, format) {
  const baseName = String(modelName || 'model')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9._-]+/gi, '_')
    .replace(/^_+|_+$/g, '') || 'model';
  return `${baseName}_baked.${format}`;
}

function getTextureImageMaximumDimension(image) {
  if (!image) return 0;
  if (Array.isArray(image)) {
    return image.reduce(
      (maximum, entry) => Math.max(maximum, getTextureImageMaximumDimension(entry)),
      0,
    );
  }
  return Math.max(
    Number(image.width) || 0,
    Number(image.height) || 0,
    Number(image.videoWidth) || 0,
    Number(image.videoHeight) || 0,
    Number(image.naturalWidth) || 0,
    Number(image.naturalHeight) || 0,
  );
}

function getReplicaExportTextureSize(replica) {
  if (!replica || typeof replica.traverse !== 'function') return 0;
  const textureKeys = [
    'map',
    'alphaMap',
    'aoMap',
    'bumpMap',
    'displacementMap',
    'emissiveMap',
    'lightMap',
    'metalnessMap',
    'normalMap',
    'roughnessMap',
    'specularMap',
  ];
  const seenTextureUuids = new Set();
  let maximumDimension = 0;

  const collectTexture = (texture) => {
    if (!texture) return;
    const textureUuid = String(texture.uuid || '');
    if (textureUuid && seenTextureUuids.has(textureUuid)) return;
    if (textureUuid) seenTextureUuids.add(textureUuid);
    maximumDimension = Math.max(
      maximumDimension,
      getTextureImageMaximumDimension(texture.image),
      getTextureImageMaximumDimension(texture.source?.data),
    );
  };

  replica.traverse((child) => {
    if (!child?.isMesh || !child.material) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material) return;
      textureKeys.forEach((key) => collectTexture(material[key] || null));
    });
  });

  return Math.max(0, Math.round(maximumDimension) || 0);
}

async function serializeAndDownloadBakedReplica(replica, format, modelName) {
  const safeFormat = String(format || 'glb').toLowerCase();
  const filename = getBakedExportFilename(modelName, safeFormat);

  if (safeFormat === 'glb' || safeFormat === 'gltf') {
    const exporter = new GLTFExporter();
    const exportTextureSize = getReplicaExportTextureSize(replica);
    const exportOptions = {
      binary: safeFormat === 'glb',
      onlyVisible: true,
      truncateDrawRange: true,
      embedImages: true,
    };
    if (exportTextureSize > 0) {
      // Preserve the baked atlas resolution exactly. The previous fixed 4096
      // cap forced GLTFExporter to downscale large baked colour/roughness/
      // metallic/normal atlases during export, which is why Open3D showed a
      // visibly mushier surface than Voxemera.
      exportOptions.maxTextureSize = exportTextureSize;
    }

    const exportedTextureRows = [];
    const seenTextureUuids = new Set();
    replica.traverse((child) => {
      if (!child?.isMesh || !child.material) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material, materialIndex) => {
        if (!material) return;
        [
          ['Base colour', material.map],
          ['Roughness', material.roughnessMap],
          ['Metallic', material.metalnessMap],
          ['Normal', material.normalMap],
          ['Bump', material.bumpMap],
        ].forEach(([slot, texture]) => {
          if (!texture) return;
          const uuid = String(texture.uuid || `${child.uuid}-${materialIndex}-${slot}`);
          if (seenTextureUuids.has(uuid)) return;
          seenTextureUuids.add(uuid);
          const image = texture.image || texture.source?.data || null;
          exportedTextureRows.push({
            mesh: child.name || '(unnamed mesh)',
            material: material.name || `material ${materialIndex}`,
            slot,
            width: Number(image?.width || image?.naturalWidth || image?.videoWidth || 0),
            height: Number(image?.height || image?.naturalHeight || image?.videoHeight || 0),
            minFilter: texture.minFilter,
            magFilter: texture.magFilter,
            generateMipmaps: texture.generateMipmaps,
            anisotropy: texture.anisotropy,
            colourSpace: texture.colorSpace,
          });
        });
      });
    });

    exportedTextureRows.forEach((row, index) => {
    });

    const result = await new Promise((resolve, reject) => {
      exporter.parse(
        replica,
        resolve,
        reject,
        exportOptions,
      );
    });
    const blob = safeFormat === 'glb'
      ? new Blob([result], { type: 'model/gltf-binary' })
      : new Blob([JSON.stringify(result, null, 2)], { type: 'model/gltf+json' });
    downloadExportBlob(blob, filename);
    return;
  }

  if (safeFormat === 'obj') {
    const exporter = new OBJExporter();
    const output = exporter.parse(replica);
    downloadExportBlob(new Blob([output], { type: 'text/plain;charset=utf-8' }), filename);
    return;
  }

  if (safeFormat === 'stl') {
    const exporter = new STLExporter();
    const output = exporter.parse(replica, { binary: true });
    downloadExportBlob(new Blob([output], { type: 'model/stl' }), filename);
    return;
  }

  throw new Error(`Unsupported 3D export format: ${safeFormat}`);
}

function ensureRenderableMaterials(root) {
  if (!root || typeof root.traverse !== 'function') return;
  root.traverse((child) => {
    if (!child?.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;

    if (!child.material) {
      child.material = new THREE.MeshStandardMaterial({
        color: 0xb8bdc5,
        roughness: 0.72,
        metalness: 0.05,
        side: THREE.DoubleSide,
      });
    }

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material) return;
      material.side = THREE.DoubleSide;
      material.needsUpdate = true;
    });
  });
}

function createObjectFromStlGeometry(geometry) {
  geometry.computeVertexNormals();
  return new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: 0xb8bdc5,
      roughness: 0.72,
      metalness: 0.05,
      side: THREE.DoubleSide,
    }),
  );
}

function clampUvTileCount(value) {
  return Math.max(1, Math.min(16, Math.round(Number(value) || 1)));
}

function uvTileStorageKey(modelName) {
  return `${UV_TILE_PREFIX}${String(modelName || 'model')}`;
}

function clampUvLayoutMode(value) {
  return String(value || '') === UV_LAYOUT_MODE_ORIGINAL ? UV_LAYOUT_MODE_ORIGINAL : UV_LAYOUT_MODE_GENERATED;
}

function uvLayoutModeStorageKey(modelName) {
  return `${UV_LAYOUT_MODE_PREFIX}${String(modelName || 'model')}`;
}

function saveStoredUvLayoutMode(modelName, layoutMode) {
  if (!modelName) return;
  try {
    localStorage.setItem(uvLayoutModeStorageKey(modelName), clampUvLayoutMode(layoutMode));
  } catch {}
}
function clampUvResolution(value) {
  const numeric = Number(value) || DEFAULT_GENERATED_UV_RESOLUTION;
  return UV_RESOLUTION_OPTIONS.reduce((best, option) => (
    Math.abs(option - numeric) < Math.abs(best - numeric) ? option : best
  ), UV_RESOLUTION_OPTIONS[0]);
}

function uvResolutionStorageKey(modelName) {
  return `${UV_RESOLUTION_PREFIX}${String(modelName || 'model')}`;
}

function saveStoredUvResolution(modelName, resolution) {
  if (!modelName) return;
  try {
    localStorage.setItem(uvResolutionStorageKey(modelName), String(clampUvResolution(resolution)));
  } catch {}
}

function loadStoredUvResolution(modelName) {
  if (!modelName) return null;
  try {
    const value = localStorage.getItem(uvResolutionStorageKey(modelName));
    if (value == null) return null;
    return clampUvResolution(value);
  } catch {
    return null;
  }
}

function getGeneratedAtlasDimensions(tileCount, resolutionPerTile) {
  const safeTileCount = clampUvTileCount(tileCount);
  const safeResolution = clampUvResolution(resolutionPerTile);
  const columns = Math.max(1, Math.ceil(Math.sqrt(safeTileCount)));
  const rows = Math.max(1, Math.ceil(safeTileCount / columns));
  return {
    columns,
    rows,
    width: columns * safeResolution,
    height: rows * safeResolution,
    resolutionPerTile: safeResolution,
  };
}

function getMaximumUvResolutionForTextureSize(tileCount, maximumTextureSize) {
  const dimensions = getGeneratedAtlasDimensions(tileCount, UV_RESOLUTION_OPTIONS[0]);
  const maxSize = Math.max(UV_RESOLUTION_OPTIONS[0], Number(maximumTextureSize) || 8192);
  return UV_RESOLUTION_OPTIONS.reduce((best, option) => {
    const width = dimensions.columns * option;
    const height = dimensions.rows * option;
    return width <= maxSize && height <= maxSize ? option : best;
  }, UV_RESOLUTION_OPTIONS[0]);
}

function formatTextureMemory(width, height) {
  const bytes = Math.max(0, Number(width) || 0) * Math.max(0, Number(height) || 0) * 4;
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  return `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
}

function saveStoredUvTileCount(modelName, tileCount) {
  if (!modelName) return;
  try {
    localStorage.setItem(uvTileStorageKey(modelName), String(clampUvTileCount(tileCount)));
  } catch {}
}

function loadStoredUvTileCount(modelName) {
  if (!modelName) return null;
  try {
    const value = localStorage.getItem(uvTileStorageKey(modelName));
    if (value == null) return null;
    return clampUvTileCount(value);
  } catch {
    return null;
  }
}


function armorPaintConvexHull2d(points) {
  if (!Array.isArray(points) || points.length <= 2) return Array.isArray(points) ? points.slice() : [];
  const sorted = points
    .filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y))
    .sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (origin, a, b) => ((a.x - origin.x) * (b.y - origin.y)) - ((a.y - origin.y) * (b.x - origin.x));
  const lower = [];
  sorted.forEach((point) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) lower.pop();
    lower.push(point);
  });
  const upper = [];
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const point = sorted[index];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) upper.pop();
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function armorPaintMinimumAreaDirection(points) {
  const hull = armorPaintConvexHull2d(points);
  if (hull.length < 2) return { cos: 1, sin: 0 };
  let bestArea = Infinity;
  let bestCos = 1;
  let bestSin = 0;
  const step = hull.length > 360 ? Math.max(1, Math.floor(hull.length / 360)) : 1;
  for (let index = -1; index < hull.length; index += step) {
    let cos = 1;
    let sin = 0;
    if (index >= 0) {
      const a = hull[index];
      const b = hull[(index + 1) % hull.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      if (length < 1e-12) continue;
      cos = dx / length;
      sin = dy / length;
    }
    let minAlong = Infinity;
    let maxAlong = -Infinity;
    let minAcross = Infinity;
    let maxAcross = -Infinity;
    hull.forEach((point) => {
      const along = (point.x * cos) + (point.y * sin);
      const across = (point.y * cos) - (point.x * sin);
      minAlong = Math.min(minAlong, along);
      maxAlong = Math.max(maxAlong, along);
      minAcross = Math.min(minAcross, across);
      maxAcross = Math.max(maxAcross, across);
    });
    const area = Math.max(1e-12, maxAlong - minAlong) * Math.max(1e-12, maxAcross - minAcross);
    if (area < bestArea) {
      bestArea = area;
      bestCos = cos;
      bestSin = sin;
    }
  }
  return { cos: bestCos, sin: bestSin };
}

function armorPaintSkylinePack(charts, scale, margin) {
  const ordered = charts.slice().sort((a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height));
  let skyline = [{ x: 0, y: 0 }];
  const placements = new Map();
  const findPlacement = (width, height) => {
    let best = null;
    for (let start = 0; start < skyline.length; start += 1) {
      const x = skyline[start].x;
      if (x + width > 1 + 1e-6) break;
      let y = 0;
      const xEnd = x + width;
      for (let index = start; index < skyline.length && skyline[index].x < xEnd - 1e-6; index += 1) y = Math.max(y, skyline[index].y);
      if (y + height <= 1 + 1e-6 && (!best || y < best.y || (y === best.y && x < best.x))) best = { x, y };
    }
    return best;
  };
  const insert = (x0, x1, height) => {
    const next = [];
    let inserted = false;
    for (let index = 0; index < skyline.length; index += 1) {
      const segmentStart = skyline[index].x;
      const segmentEnd = index + 1 < skyline.length ? skyline[index + 1].x : 1;
      const segmentHeight = skyline[index].y;
      if (segmentEnd <= x0 + 1e-6 || segmentStart >= x1 - 1e-6) {
        next.push({ x: segmentStart, y: segmentHeight });
      } else {
        if (segmentStart < x0 - 1e-6) next.push({ x: segmentStart, y: segmentHeight });
        if (!inserted) {
          next.push({ x: x0, y: height });
          inserted = true;
        }
        if (segmentEnd > x1 + 1e-6) next.push({ x: x1, y: segmentHeight });
      }
    }
    skyline = next.filter((entry, index) => index === 0 || Math.abs(entry.y - next[index - 1].y) >= 1e-6);
  };
  for (const chart of ordered) {
    const normalWidth = (chart.width * scale) + margin;
    const normalHeight = (chart.height * scale) + margin;
    const normal = findPlacement(normalWidth, normalHeight);
    const rotated = normalWidth !== normalHeight ? findPlacement(normalHeight, normalWidth) : null;
    const useRotated = Boolean(rotated && (!normal || rotated.y < normal.y || (rotated.y === normal.y && rotated.x < normal.x)));
    const chosen = useRotated ? rotated : normal;
    if (!chosen) return null;
    const packedWidth = useRotated ? normalHeight : normalWidth;
    const packedHeight = useRotated ? normalWidth : normalHeight;
    placements.set(chart.id, { x: chosen.x + (margin * 0.5), y: chosen.y + (margin * 0.5), rotated: useRotated });
    insert(chosen.x, chosen.x + packedWidth, chosen.y + packedHeight);
  }
  return placements;
}

function createTiledDebugUvAtlas(sourceGeometry, tileCount) {
  if (!sourceGeometry?.attributes?.position) return sourceGeometry?.clone?.() || sourceGeometry;

  const safeTileCount = clampUvTileCount(tileCount);
  const geometry = sourceGeometry.index ? sourceGeometry.toNonIndexed() : sourceGeometry.clone();
  const position = geometry.attributes.position;
  if (!position || position.count < 3) return geometry;

  geometry.clearGroups();
  geometry.setDrawRange(0, position.count);
  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  if (!bounds || bounds.isEmpty()) return geometry;

  const triangleCount = Math.floor(position.count / 3);
  const triangles = new Array(triangleCount);
  const edgeOwners = new Map();
  const modelScale = Math.max(
    bounds.max.x - bounds.min.x,
    bounds.max.y - bounds.min.y,
    bounds.max.z - bounds.min.z,
    1,
  );
  const quantiseScale = 100000 / modelScale;

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const faceNormal = new THREE.Vector3();
  const vertex = new THREE.Vector3();
  const centroid = new THREE.Vector3();

  const vertexKey = (index) => {
    vertex.fromBufferAttribute(position, index);
    return `${Math.round(vertex.x * quantiseScale)},${Math.round(vertex.y * quantiseScale)},${Math.round(vertex.z * quantiseScale)}`;
  };

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    const i0 = triangleIndex * 3;
    const i1 = i0 + 1;
    const i2 = i0 + 2;
    a.fromBufferAttribute(position, i0);
    b.fromBufferAttribute(position, i1);
    c.fromBufferAttribute(position, i2);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    faceNormal.crossVectors(ab, ac);
    const doubledArea = faceNormal.length();
    if (doubledArea > 1e-12) faceNormal.multiplyScalar(1 / doubledArea);
    else faceNormal.set(0, 1, 0);
    centroid.copy(a).add(b).add(c).multiplyScalar(1 / 3);

    const keys = [vertexKey(i0), vertexKey(i1), vertexKey(i2)];
    triangles[triangleIndex] = {
      index: triangleIndex,
      indices: [i0, i1, i2],
      normal: faceNormal.clone(),
      area: Math.max(doubledArea * 0.5, 1e-12),
      centroid: centroid.clone(),
      neighbours: [],
    };

    [[keys[0], keys[1]], [keys[1], keys[2]], [keys[2], keys[0]]].forEach(([left, right]) => {
      const edgeKey = left < right ? `${left}|${right}` : `${right}|${left}`;
      const owners = edgeOwners.get(edgeKey) || [];
      owners.push(triangleIndex);
      edgeOwners.set(edgeKey, owners);
    });
  }

  edgeOwners.forEach((owners) => {
    if (!Array.isArray(owners) || owners.length < 2) return;
    for (let left = 0; left < owners.length; left += 1) {
      for (let right = left + 1; right < owners.length; right += 1) {
        const leftIndex = owners[left];
        const rightIndex = owners[right];
        triangles[leftIndex].neighbours.push(rightIndex);
        triangles[rightIndex].neighbours.push(leftIndex);
      }
    }
  });

  const totalSurfaceArea = triangles.reduce((sum, triangle) => sum + triangle.area, 0) || 1;

  // Find disconnected mesh components first. Region growth never jumps between
  // components, so every tile colour is physically connected on the mesh.
  const componentIndexByTriangle = new Int32Array(triangleCount);
  componentIndexByTriangle.fill(-1);
  const components = [];
  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    if (componentIndexByTriangle[triangleIndex] >= 0) continue;
    const componentIndex = components.length;
    const queue = [triangleIndex];
    const members = [];
    let area = 0;
    componentIndexByTriangle[triangleIndex] = componentIndex;
    while (queue.length) {
      const current = queue.pop();
      members.push(current);
      area += triangles[current].area;
      triangles[current].neighbours.forEach((neighbourIndex) => {
        if (componentIndexByTriangle[neighbourIndex] >= 0) return;
        componentIndexByTriangle[neighbourIndex] = componentIndex;
        queue.push(neighbourIndex);
      });
    }
    components.push({ members, area });
  }

  const activeTileCount = Math.max(1, Math.min(safeTileCount, triangleCount));
  const seedsPerComponent = new Array(components.length).fill(0);
  const componentOrder = components
    .map((component, index) => ({ index, area: component.area }))
    .sort((left, right) => right.area - left.area);

  // Give each component one seed where possible, then distribute remaining
  // seeds proportionally by surface area.
  let remainingSeeds = activeTileCount;
  componentOrder.forEach(({ index }) => {
    if (remainingSeeds <= 0) return;
    seedsPerComponent[index] = 1;
    remainingSeeds -= 1;
  });
  while (remainingSeeds > 0) {
    let bestComponent = componentOrder[0]?.index || 0;
    let bestNeed = -Infinity;
    components.forEach((component, index) => {
      const need = component.area / (seedsPerComponent[index] + 1);
      if (need > bestNeed && seedsPerComponent[index] < component.members.length) {
        bestNeed = need;
        bestComponent = index;
      }
    });
    seedsPerComponent[bestComponent] += 1;
    remainingSeeds -= 1;
  }

  const tileAssignments = new Int32Array(triangleCount);
  tileAssignments.fill(-1);
  const tileAreas = [];
  let nextTileIndex = 0;

  // Fast connected partitioning: choose well-spaced seeds, then perform one
  // multi-source breadth-first growth across shared edges. The earlier version
  // repeatedly rescanned every tile frontier for every triangle, which became
  // effectively quadratic on dense models and could freeze page load.
  components.forEach((component, componentIndex) => {
    const seedCount = Math.max(1, Math.min(seedsPerComponent[componentIndex], component.members.length));
    const seedTriangles = [];
    const selectedSeedSet = new Set();
    const firstSeed = component.members.reduce((best, current) => (
      triangles[current].area > triangles[best].area ? current : best
    ), component.members[0]);
    seedTriangles.push(firstSeed);
    selectedSeedSet.add(firstSeed);

    while (seedTriangles.length < seedCount) {
      let bestTriangle = -1;
      let bestDistance = -1;
      for (const triangleIndex of component.members) {
        if (selectedSeedSet.has(triangleIndex)) continue;
        let nearest = Infinity;
        for (const seedIndex of seedTriangles) {
          const distance = triangles[triangleIndex].centroid.distanceToSquared(triangles[seedIndex].centroid);
          if (distance < nearest) nearest = distance;
        }
        if (nearest > bestDistance) {
          bestDistance = nearest;
          bestTriangle = triangleIndex;
        }
      }
      if (bestTriangle < 0) break;
      seedTriangles.push(bestTriangle);
      selectedSeedSet.add(bestTriangle);
    }

    const componentTileIndices = seedTriangles.map(() => nextTileIndex++);
    const queueTriangles = new Int32Array(component.members.length);
    const queueTiles = new Int32Array(component.members.length);
    let queueHead = 0;
    let queueTail = 0;

    seedTriangles.forEach((triangleIndex, seedIndex) => {
      const tileIndex = componentTileIndices[seedIndex];
      tileAssignments[triangleIndex] = tileIndex;
      tileAreas[tileIndex] = triangles[triangleIndex].area;
      queueTriangles[queueTail] = triangleIndex;
      queueTiles[queueTail] = tileIndex;
      queueTail += 1;
    });

    while (queueHead < queueTail) {
      const currentTriangle = queueTriangles[queueHead];
      const currentTile = queueTiles[queueHead];
      queueHead += 1;

      const neighbours = triangles[currentTriangle].neighbours;
      for (let neighbourOffset = 0; neighbourOffset < neighbours.length; neighbourOffset += 1) {
        const neighbourIndex = neighbours[neighbourOffset];
        if (componentIndexByTriangle[neighbourIndex] !== componentIndex) continue;
        if (tileAssignments[neighbourIndex] >= 0) continue;
        tileAssignments[neighbourIndex] = currentTile;
        tileAreas[currentTile] = (tileAreas[currentTile] || 0) + triangles[neighbourIndex].area;
        queueTriangles[queueTail] = neighbourIndex;
        queueTiles[queueTail] = currentTile;
        queueTail += 1;
      }
    }

    // Non-manifold leftovers are attached to the nearest seed tile without
    // another global growth loop.
    for (const triangleIndex of component.members) {
      if (tileAssignments[triangleIndex] >= 0) continue;
      let bestSeedOffset = 0;
      let bestDistance = Infinity;
      for (let seedOffset = 0; seedOffset < seedTriangles.length; seedOffset += 1) {
        const distance = triangles[triangleIndex].centroid.distanceToSquared(triangles[seedTriangles[seedOffset]].centroid);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestSeedOffset = seedOffset;
        }
      }
      const tileIndex = componentTileIndices[bestSeedOffset];
      tileAssignments[triangleIndex] = tileIndex;
      tileAreas[tileIndex] = (tileAreas[tileIndex] || 0) + triangles[triangleIndex].area;
    }
  });

  // Hard final invariant: no triangle may be an isolated tile colour. Any
  // singleton is absorbed into the neighbouring tile sharing the most edges.
  for (let pass = 0; pass < 12; pass += 1) {
    let changed = false;
    for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
      const currentTile = tileAssignments[triangleIndex];
      const neighbours = triangles[triangleIndex].neighbours;
      if (!neighbours.length) continue;
      if (neighbours.some((neighbourIndex) => tileAssignments[neighbourIndex] === currentTile)) continue;

      const scores = new Map();
      neighbours.forEach((neighbourIndex) => {
        const neighbourTile = tileAssignments[neighbourIndex];
        if (neighbourTile < 0) return;
        const normalWeight = 1 + Math.max(0, triangles[triangleIndex].normal.dot(triangles[neighbourIndex].normal));
        scores.set(neighbourTile, (scores.get(neighbourTile) || 0) + normalWeight);
      });
      let bestTile = currentTile;
      let bestScore = -Infinity;
      scores.forEach((score, tileIndex) => {
        if (score > bestScore) {
          bestScore = score;
          bestTile = tileIndex;
        }
      });
      if (bestTile !== currentTile) {
        tileAssignments[triangleIndex] = bestTile;
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Build smaller planar charts so curved surfaces are not forced through a
  // single projection. ArmorPaint groups adjacent faces while their normals
  // remain within its 66-degree chart threshold.
  const chartVisited = new Uint8Array(triangleCount);
  const charts = [];
  const localDotLimit = 0.4067; // ArmorPaint UV_ANGLE_THRESHOLD: cos(66 degrees)
  const averageNormal = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const bitangent = new THREE.Vector3();
  const helperAxis = new THREE.Vector3();

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    if (chartVisited[triangleIndex]) continue;
    const tileIndex = Math.max(0, tileAssignments[triangleIndex]);
    const queue = [triangleIndex];
    const chartTriangles = [];
    chartVisited[triangleIndex] = 1;
    averageNormal.set(0, 0, 0);
    let surfaceArea = 0;

    while (queue.length) {
      const current = queue.pop();
      const triangle = triangles[current];
      chartTriangles.push(current);
      averageNormal.addScaledVector(triangle.normal, triangle.area);
      surfaceArea += triangle.area;
      triangle.neighbours.forEach((neighbourIndex) => {
        if (chartVisited[neighbourIndex]) return;
        if (tileAssignments[neighbourIndex] !== tileIndex) return;
        if (triangle.normal.dot(triangles[neighbourIndex].normal) < localDotLimit) return;
        chartVisited[neighbourIndex] = 1;
        queue.push(neighbourIndex);
      });
    }

    if (averageNormal.lengthSq() < 1e-12) averageNormal.set(0, 1, 0);
    else averageNormal.normalize();
    helperAxis.set(Math.abs(averageNormal.y) < 0.9 ? 0 : 1, Math.abs(averageNormal.y) < 0.9 ? 1 : 0, 0);
    tangent.crossVectors(helperAxis, averageNormal).normalize();
    bitangent.crossVectors(averageNormal, tangent).normalize();

    // ArmorPaint rotates each projected chart to its near-minimum-area
    // bounding rectangle before packing it.
    const projectedChartPoints = [];
    chartTriangles.forEach((current) => {
      triangles[current].indices.forEach((vertexIndex) => {
        vertex.fromBufferAttribute(position, vertexIndex);
        projectedChartPoints.push({ x: vertex.dot(tangent), y: vertex.dot(bitangent) });
      });
    });
    const minimumAreaDirection = armorPaintMinimumAreaDirection(projectedChartPoints);
    const baseTangent = tangent.clone();
    const baseBitangent = bitangent.clone();
    tangent.copy(baseTangent).multiplyScalar(minimumAreaDirection.cos)
      .addScaledVector(baseBitangent, minimumAreaDirection.sin).normalize();
    bitangent.copy(baseBitangent).multiplyScalar(minimumAreaDirection.cos)
      .addScaledVector(baseTangent, -minimumAreaDirection.sin).normalize();

    let minU = Infinity;
    let minV = Infinity;
    let maxU = -Infinity;
    let maxV = -Infinity;
    chartTriangles.forEach((current) => {
      triangles[current].indices.forEach((vertexIndex) => {
        vertex.fromBufferAttribute(position, vertexIndex);
        const projectedU = vertex.dot(tangent);
        const projectedV = vertex.dot(bitangent);
        minU = Math.min(minU, projectedU);
        minV = Math.min(minV, projectedV);
        maxU = Math.max(maxU, projectedU);
        maxV = Math.max(maxV, projectedV);
      });
    });

    const width = Math.max(maxU - minU, modelScale * 1e-7);
    const height = Math.max(maxV - minV, modelScale * 1e-7);
    charts.push({
      id: charts.length,
      tileIndex,
      triangleIndices: chartTriangles,
      tangent: tangent.clone(),
      bitangent: bitangent.clone(),
      minU,
      minV,
      width,
      height,
      surfaceArea,
    });
  }

  const atlasColumns = Math.max(1, Math.ceil(Math.sqrt(safeTileCount)));
  const atlasRows = Math.max(1, Math.ceil(safeTileCount / atlasColumns));
  const tileWidth = 1 / atlasColumns;
  const tileHeight = 1 / atlasRows;
  const tilePadding = 0.004;
  const islandPadding = 0.006; // UV_PACK_MARGIN enlarged for a real mip-safe colour gutter
  const uv = new Float32Array(position.count * 2);
  const chartsByTile = Array.from({ length: safeTileCount }, () => []);
  charts.forEach((chart) => {
    chartsByTile[Math.max(0, Math.min(safeTileCount - 1, chart.tileIndex))].push(chart);
  });

  // Fast shelf packing. The previous MaxRects implementation split and
  // rescanned an ever-growing free-rectangle list for every chart, then did
  // that again during every binary-search iteration. Dense models with many
  // smart UV charts could therefore spend minutes here despite there being no
  // paint to process. Shelf packing remains deterministic, allows 90-degree
  // chart rotation, preserves one uniform texel-density scale, and is linear
  // per fit test.
  const packTileCharts = (tileCharts, globalScale) => (
    tileCharts.length ? armorPaintSkylinePack(tileCharts, globalScale, islandPadding) : new Map()
  );

  let maximumScale = Infinity;
  charts.forEach((chart) => {
    maximumScale = Math.min(
      maximumScale,
      Math.max(1e-9, (1 - (islandPadding * 2)) / Math.max(chart.width, chart.height)),
    );
  });
  if (!Number.isFinite(maximumScale)) maximumScale = 1;

  let lowScale = 0;
  let highScale = maximumScale;
  for (let iteration = 0; iteration < 18; iteration += 1) {
    const candidateScale = (lowScale + highScale) * 0.5;
    const fits = chartsByTile.every((tileCharts) => packTileCharts(tileCharts, candidateScale) !== null);
    if (fits) lowScale = candidateScale;
    else highScale = candidateScale;
  }
  const globalChartScale = Math.max(lowScale * 0.995, modelScale * 1e-10);

  chartsByTile.forEach((tileCharts, tileIndex) => {
    if (!tileCharts.length) return;
    const placements = packTileCharts(tileCharts, globalChartScale);
    if (!placements) return;
    const tileColumn = tileIndex % atlasColumns;
    const tileRow = Math.floor(tileIndex / atlasColumns);
    const innerOriginU = (tileColumn * tileWidth) + (tilePadding * tileWidth);
    const innerOriginV = (tileRow * tileHeight) + (tilePadding * tileHeight);
    const innerWidth = tileWidth * (1 - (tilePadding * 2));
    const innerHeight = tileHeight * (1 - (tilePadding * 2));

    tileCharts.forEach((chart) => {
      const placement = placements.get(chart.id);
      if (!placement) return;
      chart.triangleIndices.forEach((current) => {
        triangles[current].indices.forEach((vertexIndex) => {
          vertex.fromBufferAttribute(position, vertexIndex);
          const projectedU = THREE.MathUtils.clamp(vertex.dot(chart.tangent) - chart.minU, 0, chart.width);
          const projectedV = THREE.MathUtils.clamp(vertex.dot(chart.bitangent) - chart.minV, 0, chart.height);
          let packedU;
          let packedV;
          if (placement.rotated) {
            packedU = placement.x + (projectedV * globalChartScale);
            packedV = placement.y + ((chart.width - projectedU) * globalChartScale);
          } else {
            packedU = placement.x + (projectedU * globalChartScale);
            packedV = placement.y + (projectedV * globalChartScale);
          }
          uv[vertexIndex * 2] = innerOriginU + (packedU * innerWidth);
          uv[(vertexIndex * 2) + 1] = innerOriginV + (packedV * innerHeight);
        });
      });
    });
  });

  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geometry.attributes.uv.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.userData = {
    ...(geometry.userData || {}),
    voxemeraGeneratedUvAtlas: true,
    voxemeraUvTileCount: safeTileCount,
    voxemeraUvChartCount: charts.length,
    voxemeraConnectedTileRegions: true,
    voxemeraUniformTexelDensity: true,
    voxemeraUvWorldScale: globalChartScale,
    voxemeraUvChartAngleLimit: 66,
    voxemeraUvUnwrapAlgorithm: 'armorpaint-chart-skyline-mip-gutter-v3',
  };
  return geometry;
}

function applyOriginalUvLayout(root) {
  if (!root || typeof root.traverse !== 'function') return;
  root.userData = {
    ...(root.userData || {}),
    voxemeraUvLayoutMode: UV_LAYOUT_MODE_ORIGINAL,
  };

  root.traverse((child) => {
    if (!child?.isMesh || !child.geometry) return;
    if (!child.userData.voxemeraSourceGeometry) {
      child.userData.voxemeraSourceGeometry = child.geometry.clone();
    }
    if (!child.userData.voxemeraSourceMaterial) {
      child.userData.voxemeraSourceMaterial = child.material;
    }
    if (child.geometry && child.geometry !== child.userData.voxemeraSourceGeometry && typeof child.geometry.dispose === 'function') {
      child.geometry.dispose();
    }
    child.geometry = child.userData.voxemeraSourceGeometry.clone();
    const uvAttribute = child.geometry.getAttribute?.('uv');
    if (uvAttribute) uvAttribute.needsUpdate = true;
    child.geometry.computeBoundingBox?.();
    child.geometry.computeBoundingSphere?.();
  });
}

function modelSupportsOriginalUvLayout(root) {
  let supported = false;
  if (!root || typeof root.traverse !== 'function') return false;
  root.traverse((child) => {
    if (supported || !child?.isMesh) return;
    const geometry = child.userData?.voxemeraSourceGeometry || child.geometry;
    const uv = geometry?.getAttribute?.('uv');
    if (uv && uv.count >= 3) supported = true;
  });
  return supported;
}

function getMaterialEntry(material, materialIndex = 0) {
  if (!material) return null;
  if (Array.isArray(material)) return material[materialIndex] || material[0] || null;
  return material;
}

function normaliseUvCoordinate(value, wrapMode) {
  if (wrapMode === THREE.RepeatWrapping) return ((value % 1) + 1) % 1;
  if (wrapMode === THREE.MirroredRepeatWrapping) {
    const wrapped = ((value % 2) + 2) % 2;
    return wrapped > 1 ? 2 - wrapped : wrapped;
  }
  return THREE.MathUtils.clamp(value, 0, 1);
}

function mapUvToTexturePixels(texture, uv, imageWidth, imageHeight) {
  if (!texture || !uv) return null;
  texture.updateMatrix?.();
  const transformed = new THREE.Vector3(uv.x, uv.y, 1).applyMatrix3(texture.matrix || new THREE.Matrix3());
  const u = normaliseUvCoordinate(transformed.x, texture.wrapS);
  const v = normaliseUvCoordinate(transformed.y, texture.wrapT);
  return {
    x: u * imageWidth,
    y: (texture.flipY === false ? v : 1 - v) * imageHeight,
  };
}

function getTextureSourceImage(texture) {
  const image = texture?.image;
  if (!image) return null;
  const supports = (ctor) => typeof ctor !== 'undefined' && image instanceof ctor;
  if (supports(HTMLCanvasElement) || supports(HTMLImageElement) || supports(ImageBitmap) || supports(OffscreenCanvas) || supports(HTMLVideoElement)) {
    return image;
  }
  if (image.data && image.width && image.height) return image;
  return image.width && image.height ? image : null;
}

function drawTextureTriangle(context, image, sourceTriangle, destinationTriangle) {
  const [s0, s1, s2] = sourceTriangle;
  const [d0, d1, d2] = destinationTriangle;
  const denominator = (s0.x * (s1.y - s2.y)) + (s1.x * (s2.y - s0.y)) + (s2.x * (s0.y - s1.y));
  if (!Number.isFinite(denominator) || Math.abs(denominator) < 1e-6) return;

  const a = ((d0.x * (s1.y - s2.y)) + (d1.x * (s2.y - s0.y)) + (d2.x * (s0.y - s1.y))) / denominator;
  const b = ((d0.y * (s1.y - s2.y)) + (d1.y * (s2.y - s0.y)) + (d2.y * (s0.y - s1.y))) / denominator;
  const c = ((d0.x * (s2.x - s1.x)) + (d1.x * (s0.x - s2.x)) + (d2.x * (s1.x - s0.x))) / denominator;
  const d = ((d0.y * (s2.x - s1.x)) + (d1.y * (s0.x - s2.x)) + (d2.y * (s1.x - s0.x))) / denominator;
  const e = ((d0.x * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.x * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.x * ((s0.x * s1.y) - (s1.x * s0.y)))) / denominator;
  const f = ((d0.y * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.y * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.y * ((s0.x * s1.y) - (s1.x * s0.y)))) / denominator;

  context.save();
  context.beginPath();
  context.moveTo(d0.x, d0.y);
  context.lineTo(d1.x, d1.y);
  context.lineTo(d2.x, d2.y);
  context.closePath();
  context.clip();
  context.transform(a, b, c, d, e, f);
  context.drawImage(image, 0, 0);
  context.restore();
}

function bakeMeshTextureToUvCanvas(context, mesh, canvasWidth, canvasHeight) {
  if (!mesh?.isMesh || !mesh.geometry) return false;
  const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
  const uvAttribute = geometry.getAttribute?.('uv');
  const positionAttribute = geometry.getAttribute?.('position');
  if (!uvAttribute || !positionAttribute || uvAttribute.count < 3 || positionAttribute.count < 3) {
    if (geometry !== mesh.geometry) geometry.dispose?.();
    return false;
  }

  const materials = Array.isArray(mesh.userData?.voxemeraSourceMaterial || mesh.material)
    ? (mesh.userData?.voxemeraSourceMaterial || mesh.material)
    : [mesh.userData?.voxemeraSourceMaterial || mesh.material];

  const groupMaterialIndices = new Int32Array(Math.floor(positionAttribute.count / 3));
  if (Array.isArray(geometry.groups) && geometry.groups.length) {
    geometry.groups.forEach((group) => {
      const startTriangle = Math.floor((group.start || 0) / 3);
      const triangleCount = Math.ceil((group.count || 0) / 3);
      for (let triangleOffset = 0; triangleOffset < triangleCount; triangleOffset += 1) {
        groupMaterialIndices[startTriangle + triangleOffset] = group.materialIndex || 0;
      }
    });
  }

  let painted = false;
  const uvPoint = new THREE.Vector2();
  for (let triangleIndex = 0; triangleIndex < Math.floor(positionAttribute.count / 3); triangleIndex += 1) {
    const material = getMaterialEntry(materials, groupMaterialIndices[triangleIndex]);
    const texture = material?.map;
    const image = getTextureSourceImage(texture);
    const imageWidth = Number(image?.videoWidth || image?.naturalWidth || image?.width || 0);
    const imageHeight = Number(image?.videoHeight || image?.naturalHeight || image?.height || 0);
    if (!texture || !image || !imageWidth || !imageHeight) continue;

    const destinationTriangle = [];
    const sourceTriangle = [];
    let validTriangle = true;
    for (let corner = 0; corner < 3; corner += 1) {
      const vertexIndex = (triangleIndex * 3) + corner;
      uvPoint.fromBufferAttribute(uvAttribute, vertexIndex);
      if (!Number.isFinite(uvPoint.x) || !Number.isFinite(uvPoint.y)) {
        validTriangle = false;
        break;
      }
      destinationTriangle.push({
        x: uvPoint.x * canvasWidth,
        y: uvPoint.y * canvasHeight,
      });
      const sourcePoint = mapUvToTexturePixels(texture, uvPoint, imageWidth, imageHeight);
      if (!sourcePoint) {
        validTriangle = false;
        break;
      }
      sourceTriangle.push(sourcePoint);
    }
    if (!validTriangle) continue;
    drawTextureTriangle(context, image, sourceTriangle, destinationTriangle);
    painted = true;
  }

  if (geometry !== mesh.geometry) geometry.dispose?.();
  return painted;
}

function bakeMeshSourceTextureToActiveUvCanvas(context, mesh, canvasWidth, canvasHeight) {
  if (!mesh?.isMesh || !mesh.geometry) return false;

  const activeGeometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
  const sourceGeometryReference = mesh.userData?.voxemeraSourceGeometry || mesh.geometry;
  const sourceGeometry = sourceGeometryReference.index
    ? sourceGeometryReference.toNonIndexed()
    : sourceGeometryReference;

  const activeUv = activeGeometry.getAttribute?.('uv');
  const activePosition = activeGeometry.getAttribute?.('position');
  const sourceUv = sourceGeometry.getAttribute?.('uv');
  const sourcePosition = sourceGeometry.getAttribute?.('position');

  if (
    !activeUv
    || !activePosition
    || !sourceUv
    || !sourcePosition
    || activeUv.count < 3
    || sourceUv.count < 3
    || activePosition.count !== sourcePosition.count
  ) {
    if (activeGeometry !== mesh.geometry) activeGeometry.dispose?.();
    if (sourceGeometry !== sourceGeometryReference) sourceGeometry.dispose?.();
    return false;
  }

  const materials = Array.isArray(mesh.userData?.voxemeraSourceMaterial || mesh.material)
    ? (mesh.userData?.voxemeraSourceMaterial || mesh.material)
    : [mesh.userData?.voxemeraSourceMaterial || mesh.material];

  const triangleCount = Math.floor(activePosition.count / 3);
  const groupMaterialIndices = new Int32Array(triangleCount);
  const sourceGroups = Array.isArray(sourceGeometry.groups) ? sourceGeometry.groups : [];
  if (sourceGroups.length) {
    sourceGroups.forEach((group) => {
      const startTriangle = Math.floor((group.start || 0) / 3);
      const count = Math.ceil((group.count || 0) / 3);
      for (let offset = 0; offset < count; offset += 1) {
        const triangleIndex = startTriangle + offset;
        if (triangleIndex >= 0 && triangleIndex < triangleCount) {
          groupMaterialIndices[triangleIndex] = group.materialIndex || 0;
        }
      }
    });
  }

  let painted = false;
  const destinationUv = new THREE.Vector2();
  const originalUv = new THREE.Vector2();

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    const material = getMaterialEntry(materials, groupMaterialIndices[triangleIndex]);
    const texture = material?.map;
    const image = getTextureSourceImage(texture);
    const imageWidth = Number(image?.videoWidth || image?.naturalWidth || image?.width || 0);
    const imageHeight = Number(image?.videoHeight || image?.naturalHeight || image?.height || 0);
    if (!texture || !image || !imageWidth || !imageHeight) continue;

    const destinationTriangle = [];
    const sourceTriangle = [];
    let validTriangle = true;

    for (let corner = 0; corner < 3; corner += 1) {
      const vertexIndex = (triangleIndex * 3) + corner;
      destinationUv.fromBufferAttribute(activeUv, vertexIndex);
      originalUv.fromBufferAttribute(sourceUv, vertexIndex);

      if (
        !Number.isFinite(destinationUv.x)
        || !Number.isFinite(destinationUv.y)
        || !Number.isFinite(originalUv.x)
        || !Number.isFinite(originalUv.y)
      ) {
        validTriangle = false;
        break;
      }

      destinationTriangle.push({
        x: destinationUv.x * canvasWidth,
        y: destinationUv.y * canvasHeight,
      });

      const sourcePoint = mapUvToTexturePixels(texture, originalUv, imageWidth, imageHeight);
      if (!sourcePoint) {
        validTriangle = false;
        break;
      }
      sourceTriangle.push(sourcePoint);
    }

    if (!validTriangle) continue;
    drawTextureTriangle(context, image, sourceTriangle, destinationTriangle);
    painted = true;
  }

  if (activeGeometry !== mesh.geometry) activeGeometry.dispose?.();
  if (sourceGeometry !== sourceGeometryReference) sourceGeometry.dispose?.();
  return painted;
}


function dilateUvAtlasColourGutter(canvas, context, paddingPixels = 8) {
  if (!canvas || !context) return false;
  const width = Math.max(1, Number(canvas.width) || 1);
  const height = Math.max(1, Number(canvas.height) || 1);
  const passes = Math.max(1, Math.min(32, Math.round(Number(paddingPixels) || 8)));
  const source = document.createElement('canvas');
  source.width = width;
  source.height = height;
  const sourceContext = source.getContext('2d', { alpha: true });
  if (!sourceContext) return false;

  // Expand only already-covered texels. Because the atlas is transparent at
  // this stage, source-over copies each island's edge colour into its gutter
  // without changing the exact pixels inside the UV triangles.
  for (let pass = 0; pass < passes; pass += 1) {
    sourceContext.setTransform(1, 0, 0, 1, 0, 0);
    sourceContext.clearRect(0, 0, width, height);
    sourceContext.drawImage(canvas, 0, 0);
    context.save();
    context.globalCompositeOperation = 'destination-over';
    context.drawImage(source, -1, 0);
    context.drawImage(source, 1, 0);
    context.drawImage(source, 0, -1);
    context.drawImage(source, 0, 1);
    context.drawImage(source, -1, -1);
    context.drawImage(source, 1, -1);
    context.drawImage(source, -1, 1);
    context.drawImage(source, 1, 1);
    context.restore();
  }
  return true;
}

function initialisePaintCanvasFromModel(session, root, layoutMode) {
  const { canvas, context, materialCanvas, materialContext } = session || {};
  if (!canvas || !context) return false;
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  // Generated UV atlases must begin transparent. Filling white here destroyed
  // the coverage boundary, so bilinear filtering sampled white directly beside
  // every island and exposed the UV seams on the model.
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.restore();

  let painted = false;
  if (root && typeof root.traverse === 'function') {
    root.traverse((child) => {
      if (!child?.isMesh || child.userData?.voxemeraProjectionDecal) return;
      painted = (layoutMode === UV_LAYOUT_MODE_ORIGINAL
        ? bakeMeshTextureToUvCanvas(context, child, canvas.width, canvas.height)
        : bakeMeshSourceTextureToActiveUvCanvas(context, child, canvas.width, canvas.height)) || painted;
    });
  }

  if (painted) {
    const atlasScale = Math.max(canvas.width, canvas.height) / 2048;
    const gutterPixels = Math.max(6, Math.min(24, Math.round(8 * atlasScale)));
    dilateUvAtlasColourGutter(canvas, context, gutterPixels);
  }

  // Keep the legacy white fallback only behind the completed, padded islands.
  // It must never overwrite or sit immediately beside their sampled texels.
  context.save();
  context.globalCompositeOperation = 'destination-over';
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.restore();

  if (materialCanvas && materialContext) {
    materialContext.save();
    materialContext.setTransform(1, 0, 0, 1, 0, 0);
    materialContext.clearRect(0, 0, materialCanvas.width, materialCanvas.height);
    // Packed material atlas: R = height, G = roughness, B = metallic.
    // Defaults preserve the previous unpainted model appearance.
    materialContext.fillStyle = 'rgba(0,209,0,1)';
    materialContext.fillRect(0, 0, materialCanvas.width, materialCanvas.height);
    materialContext.restore();
    session.materialTexture.needsUpdate = true;
  }
  session.texture.needsUpdate = true;
  session.initialized = true;
  return painted;
}

function createUvDebugTexture(tileCount) {
  const safeTileCount = clampUvTileCount(tileCount);
  const atlasColumns = Math.max(1, Math.ceil(Math.sqrt(safeTileCount)));
  const atlasRows = Math.max(1, Math.ceil(safeTileCount / atlasColumns));
  const tileResolution = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = atlasColumns * tileResolution;
  canvas.height = atlasRows * tileResolution;
  const context = canvas.getContext('2d');
  const palette = [
    '#ff5f5f', '#42d4c8', '#ffd84d', '#4aa8e8',
    '#b77de0', '#4fd17e', '#f29f38', '#ed6d9b',
    '#2fc5a5', '#e6cf4c', '#74b9ff', '#a981d4',
    '#66c58b', '#f0b765', '#df7d69', '#65b9b0',
  ];

  context.clearRect(0, 0, canvas.width, canvas.height);
  const gridDivisions = 16;
  const gridStep = tileResolution / gridDivisions;

  for (let tileIndex = 0; tileIndex < safeTileCount; tileIndex += 1) {
    const column = tileIndex % atlasColumns;
    const row = Math.floor(tileIndex / atlasColumns);
    const x = column * tileResolution;
    const y = row * tileResolution;
    context.fillStyle = palette[tileIndex % palette.length];
    context.fillRect(x, y, tileResolution, tileResolution);

    context.save();
    context.translate(x, y);

    for (let line = 0; line <= gridDivisions; line += 1) {
      const offset = Math.round(line * gridStep) + 0.5;
      const major = line % 4 === 0;
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset, tileResolution);
      context.moveTo(0, offset);
      context.lineTo(tileResolution, offset);
      context.lineWidth = major ? 3 : 1;
      context.strokeStyle = major ? 'rgba(0,0,0,0.58)' : 'rgba(0,0,0,0.28)';
      context.stroke();

      if (major && line > 0 && line < gridDivisions) {
        const highlightOffset = offset + 2;
        context.beginPath();
        context.moveTo(highlightOffset, 0);
        context.lineTo(highlightOffset, tileResolution);
        context.moveTo(0, highlightOffset);
        context.lineTo(tileResolution, highlightOffset);
        context.lineWidth = 1;
        context.strokeStyle = 'rgba(255,255,255,0.45)';
        context.stroke();
      }
    }

    context.lineWidth = 6;
    context.strokeStyle = 'rgba(255,255,255,0.85)';
    context.strokeRect(3, 3, tileResolution - 6, tileResolution - 6);
    context.lineWidth = 2;
    context.strokeStyle = 'rgba(0,0,0,0.75)';
    context.strokeRect(7, 7, tileResolution - 14, tileResolution - 14);

    context.restore();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  texture.userData = {
    ...(texture.userData || {}),
    voxemeraSharedTexture: true,
  };
  return texture;
}

function setUvDebugVisible(root, visible) {
  if (!root || typeof root.traverse !== 'function') return;
  root.traverse((child) => {
    if (!child?.isMesh) return;

    if (!child.userData.voxemeraSourceMaterial) {
      child.userData.voxemeraSourceMaterial = child.material;
    }

    if (!visible) {
      if (child.material && child.material !== child.userData.voxemeraSourceMaterial && child.material !== child.userData.voxemeraPaintMaterial) {
        disposeMaterial(child.material);
      }
      if (child.userData.voxemeraUvDebugTexture && typeof child.userData.voxemeraUvDebugTexture.dispose === 'function') {
        child.userData.voxemeraUvDebugTexture.dispose();
      }
      child.userData.voxemeraUvDebugTexture = null;
      child.material = child.userData.voxemeraSourceMaterial;
      const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
      sourceMaterials.forEach((material) => {
        if (!material) return;
        material.side = THREE.DoubleSide;
        material.needsUpdate = true;
      });
      child.castShadow = true;
      child.receiveShadow = true;
      return;
    }

    if (child.material && child.material !== child.userData.voxemeraSourceMaterial && child.material !== child.userData.voxemeraPaintMaterial) {
      disposeMaterial(child.material);
    }
    if (child.userData.voxemeraUvDebugTexture && typeof child.userData.voxemeraUvDebugTexture.dispose === 'function') {
      child.userData.voxemeraUvDebugTexture.dispose();
    }

    const safeTileCount = clampUvTileCount(root.userData?.voxemeraUvTileCount || 1);
    const debugTexture = createUvDebugTexture(safeTileCount);
    child.userData.voxemeraUvDebugTexture = debugTexture;
    child.material = new THREE.MeshStandardMaterial({
      map: debugTexture,
      color: 0xffffff,
      roughness: 0.82,
      metalness: 0.0,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 1,
      toneMapped: true,
    });
    child.castShadow = true;
    child.receiveShadow = true;
  });
}

function applyDebugUvLayout(root, tileCount, showDebugColours = true) {
  if (!root || typeof root.traverse !== 'function') return;
  const safeTileCount = clampUvTileCount(tileCount);
  root.userData = {
    ...(root.userData || {}),
    voxemeraUvTileCount: safeTileCount,
    voxemeraUvLayoutMode: UV_LAYOUT_MODE_GENERATED,
  };

  // Generated UVs must be unique across the whole imported model. The old
  // implementation unwrapped every child mesh independently into the full
  // 0..1 atlas, so unrelated meshes reused identical texels. A paint mark then
  // appeared at every surface sharing those coordinates once it was baked from
  // a 3D decal into the real texture.
  const paintMeshes = [];
  root.traverse((child) => {
    if (!child?.isMesh || !child.geometry || child.userData?.voxemeraProjectionDecal) return;
    paintMeshes.push(child);
  });

  const meshColumns = Math.max(1, Math.ceil(Math.sqrt(Math.max(1, paintMeshes.length))));
  const meshRows = Math.max(1, Math.ceil(Math.max(1, paintMeshes.length) / meshColumns));
  const meshCellPadding = 0.004;

  paintMeshes.forEach((child, meshIndex) => {
    if (!child.userData.voxemeraSourceGeometry) {
      child.userData.voxemeraSourceGeometry = child.geometry.clone();
    }
    if (!child.userData.voxemeraSourceMaterial) {
      child.userData.voxemeraSourceMaterial = child.material;
    }

    const currentGeometry = child.geometry;
    const expectedMeshLayoutKey = `${safeTileCount}:${meshIndex}:${paintMeshes.length}`;
    const alreadyUsingRequestedAtlas = Boolean(
      currentGeometry?.userData?.voxemeraGeneratedUvAtlas
      && currentGeometry?.userData?.voxemeraModelWideUvLayoutKey === expectedMeshLayoutKey
    );

    if (!alreadyUsingRequestedAtlas) {
      const nextGeometry = createTiledDebugUvAtlas(child.userData.voxemeraSourceGeometry, safeTileCount);
      const uv = nextGeometry.getAttribute?.('uv');
      if (uv) {
        const meshColumn = meshIndex % meshColumns;
        const meshRow = Math.floor(meshIndex / meshColumns);
        const usable = 1 - (meshCellPadding * 2);
        for (let vertexIndex = 0; vertexIndex < uv.count; vertexIndex += 1) {
          const localU = THREE.MathUtils.clamp(uv.getX(vertexIndex), 0, 1);
          const localV = THREE.MathUtils.clamp(uv.getY(vertexIndex), 0, 1);
          uv.setXY(
            vertexIndex,
            (meshColumn + meshCellPadding + (localU * usable)) / meshColumns,
            (meshRow + meshCellPadding + (localV * usable)) / meshRows,
          );
        }
        uv.needsUpdate = true;
      }
      nextGeometry.userData = {
        ...(nextGeometry.userData || {}),
        voxemeraModelWideUvLayout: true,
        voxemeraModelWideUvLayoutKey: expectedMeshLayoutKey,
        voxemeraModelWideMeshIndex: meshIndex,
        voxemeraModelWideMeshCount: paintMeshes.length,
      };
      if (currentGeometry && currentGeometry !== child.userData.voxemeraSourceGeometry && typeof currentGeometry.dispose === 'function') {
        currentGeometry.dispose();
      }
      child.geometry = nextGeometry;
    }
  });

  root.userData.voxemeraModelWideUvMeshCount = paintMeshes.length;
  setUvDebugVisible(root, showDebugColours);
}

function loadJsZip() {
  if (typeof window === 'undefined') return Promise.reject(new Error('ZIP import is only available in the browser.'));
  if (window.JSZip) return Promise.resolve(window.JSZip);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-point-atlas-jszip="true"]');
    if (existing) {
      if (window.JSZip) {
        resolve(window.JSZip);
        return;
      }
      existing.addEventListener('load', () => {
        if (window.JSZip) resolve(window.JSZip);
        else reject(new Error('JSZip did not initialise.'));
      }, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load JSZip.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
    script.async = true;
    script.dataset.pointAtlasJszip = 'true';
    script.onload = () => {
      if (window.JSZip) resolve(window.JSZip);
      else reject(new Error('JSZip did not initialise.'));
    };
    script.onerror = () => reject(new Error('Failed to load JSZip.'));
    document.head.appendChild(script);
  });
}

function normaliseArchivePath(value) {
  const raw = String(value || '').replace(/\\/g, '/').replace(/^\.\//, '');
  const parts = [];
  raw.split('/').forEach((part) => {
    if (!part || part === '.') return;
    if (part === '..') parts.pop();
    else parts.push(part);
  });
  return parts.join('/');
}

function getArchiveDirectory(path) {
  const normalised = normaliseArchivePath(path);
  const slashIndex = normalised.lastIndexOf('/');
  return slashIndex >= 0 ? normalised.slice(0, slashIndex + 1) : '';
}

function getMimeTypeForArchivePath(path) {
  switch (getExtension(path)) {
    case 'glb': return 'model/gltf-binary';
    case 'gltf': return 'model/gltf+json';
    case 'obj': return 'text/plain';
    case 'mtl': return 'text/plain';
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'webp': return 'image/webp';
    case 'gif': return 'image/gif';
    case 'bmp': return 'image/bmp';
    case 'ktx2': return 'image/ktx2';
    case 'bin': return 'application/octet-stream';
    default: return 'application/octet-stream';
  }
}

function chooseArchiveModelPath(paths) {
  const candidates = paths.filter((path) => ['glb', 'gltf', 'obj', 'stl'].includes(getExtension(path)));
  if (!candidates.length) return null;
  const priority = { glb: 0, gltf: 1, obj: 2, stl: 3 };
  candidates.sort((left, right) => {
    const extensionDifference = priority[getExtension(left)] - priority[getExtension(right)];
    if (extensionDifference) return extensionDifference;
    const leftDepth = normaliseArchivePath(left).split('/').length;
    const rightDepth = normaliseArchivePath(right).split('/').length;
    if (leftDepth !== rightDepth) return leftDepth - rightDepth;
    const leftName = left.split('/').pop().toLowerCase();
    const rightName = right.split('/').pop().toLowerCase();
    const leftModelScore = /^(model|scene|mesh|source)\./.test(leftName) ? 0 : 1;
    const rightModelScore = /^(model|scene|mesh|source)\./.test(rightName) ? 0 : 1;
    if (leftModelScore !== rightModelScore) return leftModelScore - rightModelScore;
    return left.localeCompare(right);
  });
  return candidates[0];
}

async function loadModelArchive(file, profiler = null) {
  profiler?.mark('ZIP: loading JSZip', 'Downloading/parsing JSZip only occurs if it is not already present.');
  const JSZip = await loadJsZip();
  profiler?.mark('ZIP: JSZip ready', 'JSZip library is available.');
  const archive = await JSZip.loadAsync(file);
  profiler?.mark('ZIP: archive parsed', 'Time here is decompression and ZIP directory parsing.', { bytes: file?.size || 0 });
  const archiveEntries = Object.values(archive.files || {}).filter((entry) => entry && !entry.dir);
  profiler?.mark('ZIP: entries indexed', 'Walking the archive directory.', { entries: archiveEntries.length });
  const entryByPath = new Map();
  archiveEntries.forEach((entry) => {
    entryByPath.set(normaliseArchivePath(entry.name), entry);
  });

  const modelPath = chooseArchiveModelPath([...entryByPath.keys()]);
  profiler?.mark('ZIP: model selected', 'Choosing the primary model file inside the archive.', { modelPath });
  if (!modelPath) throw new Error('The ZIP did not contain a GLB, GLTF, OBJ or STL model.');

  const objectUrls = new Map();
  const getObjectUrl = async (archivePath) => {
    const normalised = normaliseArchivePath(archivePath);
    if (objectUrls.has(normalised)) return objectUrls.get(normalised);
    const entry = entryByPath.get(normalised);
    if (!entry) return null;
    const blob = await entry.async('blob');
    const typedBlob = blob.type ? blob : new Blob([blob], { type: getMimeTypeForArchivePath(normalised) });
    const objectUrl = URL.createObjectURL(typedBlob);
    objectUrls.set(normalised, objectUrl);
    return objectUrl;
  };

  try {
    profiler?.mark('ZIP: extracting files', 'Every archive file is inflated into a Blob URL. This can be expensive for texture-heavy archives.', { entries: entryByPath.size });
    await Promise.all([...entryByPath.keys()].map((path) => getObjectUrl(path)));
    profiler?.mark('ZIP: Blob URLs created', 'Archive extraction and Blob allocation completed.', { urls: objectUrls.size });
    const modelDirectory = getArchiveDirectory(modelPath);
    const manager = new THREE.LoadingManager();
    manager.setURLModifier((requestedUrl) => {
      const cleanUrl = String(requestedUrl || '').split('#')[0].split('?')[0];
      if (/^(blob:|data:|https?:)/i.test(cleanUrl)) return requestedUrl;
      let decodedUrl = cleanUrl;
      try { decodedUrl = decodeURIComponent(cleanUrl); } catch {}
      const directPath = normaliseArchivePath(decodedUrl);
      const relativePath = normaliseArchivePath(`${modelDirectory}${decodedUrl}`);
      return objectUrls.get(relativePath) || objectUrls.get(directPath) || requestedUrl;
    });

    const extension = getExtension(modelPath);
    const modelUrl = objectUrls.get(modelPath);
    if (!modelUrl) throw new Error('Unable to read the model file inside the ZIP.');

    if (extension === 'glb' || extension === 'gltf') {
      const loader = new GLTFLoader(manager);
      profiler?.mark('GLTF/GLB: parser started', 'GLTFLoader decodes geometry, materials, images and possibly compressed meshes.');
      const gltf = await loader.loadAsync(modelUrl);
      profiler?.mark('GLTF/GLB: parser completed', 'Model structure and referenced assets are decoded.');
      return gltf.scene || gltf.scenes?.[0];
    }

    if (extension === 'obj') {
      profiler?.mark('OBJ: reading text', 'Inflating and decoding OBJ text from the ZIP.');
      const objText = await entryByPath.get(modelPath).async('text');
      profiler?.mark('OBJ: text ready', 'OBJ source text decoded.', { characters: objText.length });
      const objLoader = new OBJLoader(manager);
      const modelBaseName = modelPath.replace(/\.[^.]+$/, '');
      const materialCandidates = [...entryByPath.keys()].filter((path) => getExtension(path) === 'mtl');
      const materialPath = materialCandidates.find((path) => path.replace(/\.[^.]+$/, '') === modelBaseName)
        || materialCandidates.find((path) => getArchiveDirectory(path) === modelDirectory)
        || materialCandidates[0];
      if (materialPath) {
        profiler?.mark('MTL: reading material file', 'Inflating and decoding the material library.');
        const materialText = await entryByPath.get(materialPath).async('text');
        const materialLoader = new MTLLoader(manager);
        materialLoader.setResourcePath('');
        const materials = materialLoader.parse(materialText, '');
        materials.preload();
        profiler?.mark('MTL: materials preloaded', 'Creating material definitions and resolving texture references.');
        objLoader.setMaterials(materials);
      }
      profiler?.mark('OBJ: parsing geometry', 'OBJLoader is converting text into Three.js geometry. Dense OBJ files can block the main thread.');
      const parsedObj = objLoader.parse(objText);
      profiler?.mark('OBJ: geometry parsed', 'OBJ parsing completed.');
      return parsedObj;
    }

    if (extension === 'stl') {
      profiler?.mark('STL: reading binary data', 'Inflating STL bytes from the ZIP.');
      const buffer = await entryByPath.get(modelPath).async('arraybuffer');
      profiler?.mark('STL: parsing geometry', 'STLLoader is converting bytes into geometry.', { bytes: buffer.byteLength });
      return createObjectFromStlGeometry(new STLLoader(manager).parse(buffer));
    }

    throw new Error('Unsupported model format inside ZIP.');
  } finally {
    objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  }
}

function getExtension(name) {
  return String(name || '').split('.').pop().toLowerCase();
}

async function loadModelFile(file, profiler = null) {
  if (!file) throw new Error('Choose a model file first.');

  const extension = getExtension(file.name);
  profiler?.mark('Model file dispatch', 'Selecting loader from the filename extension.', { name: file.name, extension, bytes: file.size });
  if (extension === 'zip') return loadModelArchive(file, profiler);

  const objectUrl = URL.createObjectURL(file);

  try {
    if (extension === 'glb' || extension === 'gltf') {
      const loader = new GLTFLoader();
      profiler?.mark('GLTF/GLB: loader started', 'Reading and decoding the model Blob URL.');
      const gltf = await loader.loadAsync(objectUrl);
      profiler?.mark('GLTF/GLB: loader completed', 'Geometry, materials and images have been decoded.');
      return gltf.scene || gltf.scenes?.[0];
    }

    if (extension === 'obj') {
      const restoreKey = getModelFileRestoreKey(file);
      let parsedPromise = parsedObjRestorePromises.get(restoreKey);
      if (!parsedPromise) {
        parsedPromise = (async () => {
          profiler?.mark('OBJ: reading file text', 'Browser is decoding the local file into a string.');
          const text = await file.text();
          profiler?.mark('OBJ: parsing geometry', 'OBJLoader is converting text into Three.js objects.', { characters: text.length });
          const parsedObj = new OBJLoader().parse(text);
          profiler?.mark('OBJ: geometry parsed', 'OBJ parsing completed.');
          return parsedObj;
        })().catch((error) => {
          parsedObjRestorePromises.delete(restoreKey);
          throw error;
        });
        parsedObjRestorePromises.set(restoreKey, parsedPromise);
      } else {
        profiler?.mark('OBJ: shared parsed model', 'Reusing the in-flight/already parsed startup OBJ instead of parsing the same model twice.');
      }
      const parsedObj = await parsedPromise;
      const cloneStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const runtimeObject = cloneParsedObjectForRuntime(parsedObj);
      profiler?.mark('OBJ: runtime clone ready', 'Created an isolated geometry/material clone from the shared parsed OBJ.', {
        durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - cloneStartedAt) * 10) / 10,
      });
      return runtimeObject;
    }

    if (extension === 'stl') {
      profiler?.mark('STL: reading file bytes', 'Browser is copying the local file into an ArrayBuffer.');
      const buffer = await file.arrayBuffer();
      profiler?.mark('STL: parsing geometry', 'STLLoader is decoding triangles.', { bytes: buffer.byteLength });
      const result = createObjectFromStlGeometry(new STLLoader().parse(buffer));
      profiler?.mark('STL: geometry parsed', 'STL parsing completed.');
      return result;
    }

    throw new Error('Unsupported model format. Use GLB, GLTF, OBJ, STL or ZIP.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function openModelDatabase() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB is unavailable.'));
      return;
    }

    const request = window.indexedDB.open(MODEL_DB_NAME, MODEL_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MODEL_STORE_NAME)) {
        database.createObjectStore(MODEL_STORE_NAME);
      }
      if (!database.objectStoreNames.contains(MODEL_PAINT_STORE_NAME)) {
        database.createObjectStore(MODEL_PAINT_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open model storage.'));
  });
}

async function saveModelFile(file) {
  if (!file) return;
  savedModelFileRestorePromise = null;
  const database = await openModelDatabase();
  try {
    const buffer = await file.arrayBuffer();
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(MODEL_STORE_NAME);
      store.put({
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        buffer,
      }, LAST_MODEL_KEY);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('Unable to store model.'));
      transaction.onabort = () => reject(transaction.error || new Error('Model storage was aborted.'));
    });
  } finally {
    database.close();
  }
}

async function loadSavedModelFileUncached() {
  const database = await openModelDatabase();
  try {
    const record = await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_STORE_NAME, 'readonly');
      const request = transaction.objectStore(MODEL_STORE_NAME).get(LAST_MODEL_KEY);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Unable to read saved model.'));
    });
    if (!record?.buffer || !record?.name) return null;
    return new File([record.buffer], record.name, {
      type: record.type || 'application/octet-stream',
      lastModified: Number(record.lastModified) || Date.now(),
    });
  } finally {
    database.close();
  }
}

async function loadSavedModelFile() {
  if (!savedModelFileRestorePromise) {
    savedModelFileRestorePromise = loadSavedModelFileUncached().catch((error) => {
      savedModelFileRestorePromise = null;
      throw error;
    });
  }
  return savedModelFileRestorePromise;
}

function getModelUvCacheFingerprint(file) {
  if (!file) return '';
  return [
    encodeURIComponent(String(file.name || 'model')),
    Math.max(0, Number(file.size) || 0),
    Math.max(0, Number(file.lastModified) || 0),
  ].join(':');
}

function generatedUvCacheKey(modelFingerprint, tileCount) {
  if (!modelFingerprint) return '';
  return `${GENERATED_UV_CACHE_PREFIX}${modelFingerprint}:${clampUvTileCount(tileCount)}`;
}

function getMeshList(root) {
  const meshes = [];
  root?.traverse?.((child) => {
    if (child?.isMesh && child.geometry) meshes.push(child);
  });
  return meshes;
}

function cloneTypedArray(array) {
  if (!array?.constructor) return null;
  return new array.constructor(array);
}

function serializeBufferGeometryForUvCache(geometry, sourcePositionCount) {
  const attributes = {};
  Object.entries(geometry?.attributes || {}).forEach(([name, attribute]) => {
    if (!attribute?.array || attribute.isInterleavedBufferAttribute) return;
    const array = cloneTypedArray(attribute.array);
    if (!array) return;
    attributes[name] = {
      array,
      itemSize: attribute.itemSize,
      normalized: Boolean(attribute.normalized),
    };
  });

  const indexAttribute = geometry?.getIndex?.();
  const index = indexAttribute?.array ? {
    array: cloneTypedArray(indexAttribute.array),
    itemSize: indexAttribute.itemSize || 1,
    normalized: Boolean(indexAttribute.normalized),
  } : null;

  return {
    sourcePositionCount: Math.max(0, Number(sourcePositionCount) || 0),
    attributes,
    index,
    groups: (geometry?.groups || []).map((group) => ({
      start: group.start,
      count: group.count,
      materialIndex: group.materialIndex,
    })),
    drawRange: {
      start: geometry?.drawRange?.start || 0,
      count: geometry?.drawRange?.count ?? Infinity,
    },
  };
}

function deserializeBufferGeometryFromUvCache(serialized, tileCount) {
  if (!serialized?.attributes?.position?.array || !serialized?.attributes?.uv?.array) return null;
  const geometry = new THREE.BufferGeometry();
  Object.entries(serialized.attributes).forEach(([name, data]) => {
    if (!data?.array || !data.itemSize) return;
    geometry.setAttribute(name, new THREE.BufferAttribute(data.array, data.itemSize, Boolean(data.normalized)));
  });
  if (serialized.index?.array) {
    geometry.setIndex(new THREE.BufferAttribute(serialized.index.array, serialized.index.itemSize || 1, Boolean(serialized.index.normalized)));
  }
  geometry.clearGroups();
  (serialized.groups || []).forEach((group) => {
    geometry.addGroup(group.start, group.count, group.materialIndex);
  });
  geometry.setDrawRange(serialized.drawRange?.start || 0, serialized.drawRange?.count ?? Infinity);
  geometry.userData = {
    ...(geometry.userData || {}),
    voxemeraGeneratedUvAtlas: true,
    voxemeraUvTileCount: clampUvTileCount(tileCount),
  };
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function getModelPaintSessionKey(modelFingerprint, layoutSessionKey) {
  if (!modelFingerprint || !layoutSessionKey) return '';
  return `model-paint:v${MODEL_PAINT_CACHE_VERSION}:${modelFingerprint}:${layoutSessionKey}`;
}

let voxemeraPaintSaveDebugSequence = 0;
const voxemeraPaintSaveDebugCanvasIds = new WeakMap();
let voxemeraPaintSaveDebugNextCanvasId = 1;

function getPaintSaveDebugCanvasId(canvas) {
  if (!canvas || (typeof canvas !== 'object' && typeof canvas !== 'function')) return null;
  if (!voxemeraPaintSaveDebugCanvasIds.has(canvas)) {
    voxemeraPaintSaveDebugCanvasIds.set(canvas, voxemeraPaintSaveDebugNextCanvasId++);
  }
  return voxemeraPaintSaveDebugCanvasIds.get(canvas);
}

function canvasToRawRgba(canvas) {
  if (!canvas?.width || !canvas?.height) return null;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  return {
    width: canvas.width,
    height: canvas.height,
    pixels: new Uint8ClampedArray(image.data),
  };
}

function snapshotToRawRgba(width, height, pixels) {
  if (!width || !height || !pixels || pixels.length !== width * height * 4) return null;
  return {
    width,
    height,
    pixels: new Uint8ClampedArray(pixels),
  };
}

function rawRgbaToCanvas(raw) {
  const width = Math.max(1, Number(raw?.width) || 0);
  const height = Math.max(1, Number(raw?.height) || 0);
  const pixels = raw?.pixels;
  if (!pixels || pixels.length !== width * height * 4) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const copiedPixels = pixels instanceof Uint8ClampedArray
    ? new Uint8ClampedArray(pixels)
    : new Uint8ClampedArray(pixels.buffer || pixels);
  context.putImageData(new ImageData(copiedPixels, width, height), 0, 0);
  return canvas;
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    if (!canvas?.toBlob) {
      reject(new Error('Canvas blob export is unavailable.'));
      return;
    }
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Unable to encode the 3D paint stroke.'));
    }, 'image/png');
  });
}

async function blobToPaintPreviewCanvas(blob, previewSize = 1024) {
  if (!blob) return null;
  const targetSize = Math.max(64, Math.min(2048, Math.round(Number(previewSize) || 1024)));
  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) return null;

  if (typeof createImageBitmap === 'function') {
    let bitmap = null;
    try {
      bitmap = await createImageBitmap(blob, {
        resizeWidth: targetSize,
        resizeHeight: targetSize,
        resizeQuality: 'medium',
      });
    } catch {
      bitmap = await createImageBitmap(blob);
    }
    context.drawImage(bitmap, 0, 0, targetSize, targetSize);
    bitmap.close?.();
    return canvas;
  }

  const decoded = await blobToCanvas(blob);
  if (!decoded) return null;
  context.drawImage(decoded, 0, 0, targetSize, targetSize);
  return canvas;
}

async function blobToCanvas(blob) {
  if (!blob) return null;
  const canvas = document.createElement('canvas');
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    canvas.width = Math.max(1, bitmap.width);
    canvas.height = Math.max(1, bitmap.height);
    canvas.getContext('2d', { alpha: true, willReadFrequently: true })?.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    return canvas;
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unable to decode saved 3D paint.'));
      element.src = url;
    });
    canvas.width = Math.max(1, image.naturalWidth || image.width || 1);
    canvas.height = Math.max(1, image.naturalHeight || image.height || 1);
    canvas.getContext('2d', { alpha: true, willReadFrequently: true })?.drawImage(image, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function normalisePbrPath(path = '') {
  return String(path || '').replace(/\\/g, '/');
}

function getPbrMapType(name = '') {
  const normalised = normalisePbrPath(name).toLowerCase();
  return PBR_MAP_TYPE_PATTERNS.find(([, pattern]) => pattern.test(normalised))?.[0] || null;
}

function getPbrSetKeyFromPath(path = '') {
  const normalised = normalisePbrPath(path);
  const filename = normalised.split('/').pop() || normalised;
  const filenameWithoutExtension = filename.replace(/\.[^.]+$/, '');
  const stripped = filenameWithoutExtension
    .replace(/[._-]+/g, ' ')
    .replace(/\b(?:albedo|base ?colou?r|basecolou?r|basecolor|diffuse|color|colour|col|alb|normal|normals|nor|nrm|opengl|ogl|gl|directx|dx|roughness|rough|rgh|metallic|metalness|metal|mtl|met|height|displacement|disp|bump|depth|ao|ambient ?occlusion|occlusion|arm|orm|rma|1k|2k|4k|8k|16k|jpg|jpeg|png|tga|tif|tiff|exr)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped) return stripped.toLowerCase();
  const parts = normalised.split('/').filter(Boolean);
  const parent = parts.length > 1 ? parts[parts.length - 2] : '';
  return (parent || 'pbr set').replace(/[._-]+/g, ' ').trim().toLowerCase();
}

function formatPbrSetLabel(key = '') {
  const value = String(key || 'pbr set').trim();
  if (!value) return 'PBR set';
  return value.replace(/\b\w/g, (match) => match.toUpperCase());
}

function getNormalisedPbrGroupKey(key = '') {
  return String(key || 'pbr set')
    .toLowerCase()
    .replace(/\b(?:opengl|ogl|gl|directx|dx|arm|orm|rma)\b/g, ' ')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'pbr set';
}

function rankPbrSet(setLike) {
  const mapTypes = new Set(Array.isArray(setLike?.mapTypes)
    ? setLike.mapTypes
    : Object.keys(setLike?.maps || {}));
  let score = mapTypes.size;
  if (mapTypes.has('albedo')) score += 100;
  if (mapTypes.has('normal')) score += 30;
  if (mapTypes.has('height')) score += 25;
  if (mapTypes.has('roughness')) score += 20;
  if (mapTypes.has('metallic')) score += 20;
  if (mapTypes.has('ao')) score += 10;
  return score;
}

function getPreferredPbrSetKey(sets = []) {
  const ranked = [...(Array.isArray(sets) ? sets : [])].sort((left, right) => (
    rankPbrSet(right) - rankPbrSet(left)
  ) || String(left?.label || left?.key || '').localeCompare(String(right?.label || right?.key || '')));
  return ranked[0]?.key || '';
}


function collectPbrSetsFromEntries(entries = []) {
  const groups = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const entryPath = normalisePbrPath(entry?.path || entry?.name || '');
    if (!entryPath || !PBR_IMAGE_FILE_EXTENSION_PATTERN.test(entryPath)) return;
    const mapType = getPbrMapType(entryPath);
    if (!mapType) return;
    const key = getNormalisedPbrGroupKey(getPbrSetKeyFromPath(entryPath));
    const group = groups.get(key) || {
      key,
      label: formatPbrSetLabel(key),
      maps: {},
    };
    if (!group.maps[mapType]) {
      group.maps[mapType] = {
        type: mapType,
        name: entry?.name || entryPath.split('/').pop() || `${key}-${mapType}.png`,
        path: entryPath,
        blob: entry?.blob || entry?.file || null,
      };
    }
    groups.set(key, group);
  });
  return Array.from(groups.values())
    .filter((set) => Object.keys(set.maps || {}).length > 0)
    .sort((left, right) => (
      rankPbrSet(right) - rankPbrSet(left)
    ) || left.label.localeCompare(right.label));
}


async function loadCanvasFromBlobSource(source) {
  if (!source) return null;
  const blob = source instanceof Blob ? source : new Blob([source]);
  return blobToCanvas(blob);
}

function createSolidColourCanvas(width, height, red, green, blue, alpha = 255) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Number(width) || 1);
  canvas.height = Math.max(1, Number(height) || 1);
  const context = canvas.getContext('2d', { alpha: true });
  if (context) {
    context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  return canvas;
}

async function buildLoadedPbrSet(setRecord, defaultMaterialSettings = {}) {
  if (!setRecord?.key) return null;
  const loadedMaps = {};
  for (const [mapType, entry] of Object.entries(setRecord.maps || {})) {
    if (!entry?.blob) continue;
    const canvas = await loadCanvasFromBlobSource(entry.blob);
    if (!canvas) continue;
    loadedMaps[mapType] = {
      type: mapType,
      name: entry?.name || `${setRecord.key}-${mapType}.png`,
      blob: entry.blob,
      canvas,
    };
  }

  const albedoCanvas = loadedMaps.albedo?.canvas || null;
  const aoCanvas = loadedMaps.ao?.canvas || null;
  let shadedAlbedoCanvas = albedoCanvas;
  if (albedoCanvas && aoCanvas) {
    shadedAlbedoCanvas = document.createElement('canvas');
    shadedAlbedoCanvas.width = albedoCanvas.width;
    shadedAlbedoCanvas.height = albedoCanvas.height;
    const ctx = shadedAlbedoCanvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(albedoCanvas, 0, 0, shadedAlbedoCanvas.width, shadedAlbedoCanvas.height);
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(aoCanvas, 0, 0, shadedAlbedoCanvas.width, shadedAlbedoCanvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  const fallbackMaterial = {
    height: Math.max(0, Math.min(100, Number(defaultMaterialSettings?.height) || 0)),
    gloss: Math.max(0, Math.min(100, Number(defaultMaterialSettings?.gloss) || 0)),
    metalness: Math.max(0, Math.min(100, Number(defaultMaterialSettings?.metalness) || 0)),
  };

  const packedWidth = loadedMaps.height?.canvas?.width
    || loadedMaps.roughness?.canvas?.width
    || loadedMaps.metallic?.canvas?.width
    || shadedAlbedoCanvas?.width
    || loadedMaps.normal?.canvas?.width
    || 1;
  const packedHeight = loadedMaps.height?.canvas?.height
    || loadedMaps.roughness?.canvas?.height
    || loadedMaps.metallic?.canvas?.height
    || shadedAlbedoCanvas?.height
    || loadedMaps.normal?.canvas?.height
    || 1;
  const packedMaterialCanvas = document.createElement('canvas');
  packedMaterialCanvas.width = Math.max(1, packedWidth);
  packedMaterialCanvas.height = Math.max(1, packedHeight);
  const packedMaterialContext = packedMaterialCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
  if (packedMaterialContext) {
    const image = packedMaterialContext.createImageData(packedMaterialCanvas.width, packedMaterialCanvas.height);
    const heightSource = loadedMaps.height?.canvas?.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, loadedMaps.height.canvas.width, loadedMaps.height.canvas.height).data || null;
    const roughnessSource = loadedMaps.roughness?.canvas?.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, loadedMaps.roughness.canvas.width, loadedMaps.roughness.canvas.height).data || null;
    const metallicSource = loadedMaps.metallic?.canvas?.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, loadedMaps.metallic.canvas.width, loadedMaps.metallic.canvas.height).data || null;
    const defaultHeightByte = Math.round((fallbackMaterial.height / 100) * 255);
    const defaultRoughnessByte = Math.round((1 - (fallbackMaterial.gloss / 100)) * 255);
    const defaultMetallicByte = Math.round((fallbackMaterial.metalness / 100) * 255);
    for (let y = 0; y < packedMaterialCanvas.height; y += 1) {
      for (let x = 0; x < packedMaterialCanvas.width; x += 1) {
        const index = ((y * packedMaterialCanvas.width) + x) * 4;
        const heightIndex = heightSource ? (((y % loadedMaps.height.canvas.height) * loadedMaps.height.canvas.width) + (x % loadedMaps.height.canvas.width)) * 4 : 0;
        const roughnessIndex = roughnessSource ? (((y % loadedMaps.roughness.canvas.height) * loadedMaps.roughness.canvas.width) + (x % loadedMaps.roughness.canvas.width)) * 4 : 0;
        const metallicIndex = metallicSource ? (((y % loadedMaps.metallic.canvas.height) * loadedMaps.metallic.canvas.width) + (x % loadedMaps.metallic.canvas.width)) * 4 : 0;
        image.data[index] = heightSource ? heightSource[heightIndex] : defaultHeightByte;
        image.data[index + 1] = roughnessSource ? roughnessSource[roughnessIndex] : defaultRoughnessByte;
        image.data[index + 2] = metallicSource ? metallicSource[metallicIndex] : defaultMetallicByte;
        image.data[index + 3] = 255;
      }
    }
    packedMaterialContext.putImageData(image, 0, 0);
  }

  const normalCanvas = loadedMaps.normal?.canvas
    ? loadedMaps.normal.canvas
    : createSolidColourCanvas(
        shadedAlbedoCanvas?.width || packedMaterialCanvas.width || 1,
        shadedAlbedoCanvas?.height || packedMaterialCanvas.height || 1,
        FLAT_NORMAL_RGB.red,
        FLAT_NORMAL_RGB.green,
        FLAT_NORMAL_RGB.blue,
        255,
      );

  return {
    key: setRecord.key,
    label: setRecord.label || formatPbrSetLabel(setRecord.key),
    mapTypes: Object.keys(loadedMaps),
    originalMaps: Object.fromEntries(Object.entries(loadedMaps).map(([type, entry]) => [type, { name: entry.name, blob: entry.blob }])),
    albedoCanvas: shadedAlbedoCanvas,
    packedMaterialCanvas,
    normalCanvas,
    availableMaps: loadedMaps,
  };
}

function makePersistablePbrSetResource(resource) {
  if (!resource?.key) return null;
  return {
    key: resource.key,
    label: resource.label || formatPbrSetLabel(resource.key),
    maps: Object.fromEntries(Object.entries(resource.originalMaps || {}).map(([type, entry]) => [type, {
      name: entry?.name || `${resource.key}-${type}.png`,
      blob: entry?.blob || null,
    }]).filter(([, entry]) => entry.blob)),
  };
}

async function saveModelPaintSessionRecord(key, record) {
  if (!key) return;
  modelPaintRecordRestorePromises.set(key, Promise.resolve(record));
  const database = await openModelDatabase();
  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_PAINT_STORE_NAME, 'readwrite');
      transaction.objectStore(MODEL_PAINT_STORE_NAME).put(record, key);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('Unable to save 3D paint.'));
      transaction.onabort = () => reject(transaction.error || new Error('3D paint save was aborted.'));
    });
  } finally {
    database.close();
  }
}

async function loadModelPaintSessionRecordUncached(key) {
  if (!key) return null;
  const database = await openModelDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_PAINT_STORE_NAME, 'readonly');
      const request = transaction.objectStore(MODEL_PAINT_STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Unable to restore 3D paint.'));
    });
  } finally {
    database.close();
  }
}

async function loadModelPaintSessionRecord(key) {
  if (!key) return null;
  let restorePromise = modelPaintRecordRestorePromises.get(key);
  if (!restorePromise) {
    restorePromise = loadModelPaintSessionRecordUncached(key).catch((error) => {
      modelPaintRecordRestorePromises.delete(key);
      throw error;
    });
    modelPaintRecordRestorePromises.set(key, restorePromise);
  }
  return restorePromise;
}

function serializeProjectionCamera(camera) {
  if (!camera) return null;
  return {
    type: camera.isOrthographicCamera ? 'orthographic' : 'perspective',
    position: camera.position.toArray(),
    quaternion: camera.quaternion.toArray(),
    up: camera.up.toArray(),
    near: Number(camera.near),
    far: Number(camera.far),
    zoom: Number(camera.zoom) || 1,
    fov: Number(camera.fov) || 45,
    aspect: Number(camera.aspect) || 1,
    left: Number(camera.left) || -1,
    right: Number(camera.right) || 1,
    top: Number(camera.top) || 1,
    bottom: Number(camera.bottom) || -1,
    view: camera.view
      ? {
          enabled: camera.view.enabled !== false,
          fullWidth: Number(camera.view.fullWidth) || 1,
          fullHeight: Number(camera.view.fullHeight) || 1,
          offsetX: Number(camera.view.offsetX) || 0,
          offsetY: Number(camera.view.offsetY) || 0,
          width: Number(camera.view.width) || 1,
          height: Number(camera.view.height) || 1,
        }
      : null,
  };
}

function deserializeProjectionCamera(data) {
  if (!data) return null;
  const camera = data.type === 'orthographic'
    ? new THREE.OrthographicCamera(data.left, data.right, data.top, data.bottom, data.near, data.far)
    : new THREE.PerspectiveCamera(data.fov, data.aspect, data.near, data.far);
  camera.position.fromArray(Array.isArray(data.position) ? data.position : [0, 0, 1]);
  camera.quaternion.fromArray(Array.isArray(data.quaternion) ? data.quaternion : [0, 0, 0, 1]);
  camera.up.fromArray(Array.isArray(data.up) ? data.up : [0, 1, 0]);
  camera.zoom = Number(data.zoom) || 1;
  if (data.view && typeof camera.setViewOffset === 'function') {
    camera.setViewOffset(
      Math.max(1, Number(data.view.fullWidth) || 1),
      Math.max(1, Number(data.view.fullHeight) || 1),
      Math.max(0, Number(data.view.offsetX) || 0),
      Math.max(0, Number(data.view.offsetY) || 0),
      Math.max(1, Number(data.view.width) || 1),
      Math.max(1, Number(data.view.height) || 1),
    );
    if (data.view.enabled === false && typeof camera.clearViewOffset === 'function') {
      camera.clearViewOffset();
    }
  }
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
  return camera;
}

async function saveGeneratedUvCache(root, modelFingerprint, tileCount) {
  const key = generatedUvCacheKey(modelFingerprint, tileCount);
  if (!key || !root) return false;
  const meshes = getMeshList(root);
  const serializedMeshes = meshes.map((mesh) => serializeBufferGeometryForUvCache(
    mesh.geometry,
    mesh.userData?.voxemeraSourceGeometry?.getAttribute?.('position')?.count,
  ));
  if (!serializedMeshes.length || serializedMeshes.some((entry) => !entry?.attributes?.uv)) return false;

  const database = await openModelDatabase();
  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_STORE_NAME, 'readwrite');
      transaction.objectStore(MODEL_STORE_NAME).put({
        version: GENERATED_UV_CACHE_VERSION,
        modelFingerprint,
        tileCount: clampUvTileCount(tileCount),
        meshCount: serializedMeshes.length,
        meshes: serializedMeshes,
        savedAt: Date.now(),
      }, key);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('Unable to store generated UV cache.'));
      transaction.onabort = () => reject(transaction.error || new Error('Generated UV cache storage was aborted.'));
    });
    return true;
  } finally {
    database.close();
  }
}

async function restoreGeneratedUvCache(root, modelFingerprint, tileCount) {
  const key = generatedUvCacheKey(modelFingerprint, tileCount);
  if (!key || !root) return false;
  const database = await openModelDatabase();
  try {
    const record = await new Promise((resolve, reject) => {
      const transaction = database.transaction(MODEL_STORE_NAME, 'readonly');
      const request = transaction.objectStore(MODEL_STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Unable to read generated UV cache.'));
    });
    if (!record || record.version !== GENERATED_UV_CACHE_VERSION) return false;
    if (record.modelFingerprint !== modelFingerprint || clampUvTileCount(record.tileCount) !== clampUvTileCount(tileCount)) return false;

    const meshes = getMeshList(root);
    if (meshes.length !== record.meshCount || meshes.length !== record.meshes?.length) return false;

    for (let index = 0; index < meshes.length; index += 1) {
      const mesh = meshes[index];
      const cached = record.meshes[index];
      const sourcePositionCount = mesh.geometry?.getAttribute?.('position')?.count || 0;
      if (Number(cached?.sourcePositionCount) !== Number(sourcePositionCount)) return false;
    }

    const restoredGeometries = record.meshes.map((cached) => deserializeBufferGeometryFromUvCache(cached, tileCount));
    if (restoredGeometries.some((geometry) => !geometry)) {
      restoredGeometries.forEach((geometry) => geometry?.dispose?.());
      return false;
    }

    meshes.forEach((mesh, index) => {
      if (!mesh.userData.voxemeraSourceGeometry) mesh.userData.voxemeraSourceGeometry = mesh.geometry.clone();
      const previousGeometry = mesh.geometry;
      mesh.geometry = restoredGeometries[index];
      if (previousGeometry && previousGeometry !== mesh.userData.voxemeraSourceGeometry) previousGeometry.dispose?.();
    });
    root.userData = {
      ...(root.userData || {}),
      voxemeraUvTileCount: clampUvTileCount(tileCount),
      voxemeraUvLayoutMode: UV_LAYOUT_MODE_GENERATED,
    };
    return true;
  } finally {
    database.close();
  }
}

function transformStorageKey(modelName) {
  return `${TRANSFORM_PREFIX}${String(modelName || 'model')}`;
}

function saveStoredTransform(modelName, model) {
  if (!modelName || !model) return;
  try {
    localStorage.setItem(transformStorageKey(modelName), JSON.stringify({
      position: model.position.toArray(),
      quaternion: model.quaternion.toArray(),
      scale: model.scale.toArray(),
    }));
  } catch {}
}

function loadStoredTransform(modelName, model) {
  if (!modelName || !model) return false;
  try {
    const value = localStorage.getItem(transformStorageKey(modelName));
    if (!value) return false;
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed.position) && parsed.position.length === 3) model.position.fromArray(parsed.position);
    if (Array.isArray(parsed.quaternion) && parsed.quaternion.length === 4) model.quaternion.fromArray(parsed.quaternion);
    if (Array.isArray(parsed.scale) && parsed.scale.length === 3) model.scale.fromArray(parsed.scale);
    model.updateMatrixWorld(true);
    return true;
  } catch {
    return false;
  }
}

function centreAndGroundObject(object) {
  if (!object) return null;
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return null;

  const centre = box.getCenter(new THREE.Vector3());
  object.position.add(new THREE.Vector3(-centre.x, -box.min.y, -centre.z));
  object.updateMatrixWorld(true);
  return new THREE.Box3().setFromObject(object);
}

function fitDirectionalShadowToObject(runtime, object) {
  if (!runtime?.keyLight || !runtime?.keyLightTarget || !object) return;
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const centre = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z, 0.001) * 0.75;
  const shadowExtent = Math.max(radius * 1.8, 1);
  const lightDistance = Math.max(radius * 3.5, 4);

  runtime.keyLightTarget.position.copy(centre);
  runtime.keyLight.position.copy(centre).add(
    new THREE.Vector3(0.65, 1, 0.8).normalize().multiplyScalar(lightDistance),
  );

  const shadowCamera = runtime.keyLight.shadow.camera;
  shadowCamera.left = -shadowExtent;
  shadowCamera.right = shadowExtent;
  shadowCamera.top = shadowExtent;
  shadowCamera.bottom = -shadowExtent;
  shadowCamera.near = Math.max(lightDistance - (radius * 2.5), 0.01);
  shadowCamera.far = lightDistance + (radius * 2.5);
  shadowCamera.updateProjectionMatrix();
  runtime.keyLight.shadow.bias = -0.00015;
  runtime.keyLight.shadow.normalBias = Math.max(radius * 0.002, 0.002);
  runtime.keyLight.shadow.needsUpdate = true;
}

function frameObject(camera, controls, object, padding = 1.35) {
  if (!camera || !controls || !object) return;
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const size = box.getSize(new THREE.Vector3());
  const centre = box.getCenter(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z, 0.001);
  const verticalFov = THREE.MathUtils.degToRad(camera.fov);
  const distanceForHeight = maxDimension / (2 * Math.tan(verticalFov / 2));
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * camera.aspect);
  const distanceForWidth = maxDimension / (2 * Math.tan(horizontalFov / 2));
  const distance = Math.max(distanceForHeight, distanceForWidth) * padding;

  camera.position.copy(centre).add(new THREE.Vector3(1, 0.72, 1).normalize().multiplyScalar(distance));
  camera.up.set(0, 1, 0);
  camera.near = Math.max(distance / 1000, 0.0001);
  camera.far = Math.max(distance * 100, 1000);
  camera.updateProjectionMatrix();

  controls.target.copy(centre);
  controls.minDistance = Math.max(maxDimension * 0.02, 0.001);
  controls.maxDistance = Math.max(maxDimension * 100, 1000);
  controls.update();
}

const VIEW_ORBIT_STEP_RADIANS = THREE.MathUtils.degToRad(22.5);
const VIEW_PAN_STEP_RATIO = 0.08;
const VIEW_ZOOM_STEP_RATIO = 0.18;

function panCameraByStep(camera, controls, direction) {
  if (!camera || !controls) return;

  const distance = camera.position.distanceTo(controls.target);
  if (!Number.isFinite(distance) || distance <= 0.00000001) return;

  const step = Math.max(distance * VIEW_PAN_STEP_RATIO, 0.0001);
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).normalize();
  const delta = new THREE.Vector3();

  if (direction === 'left') delta.copy(right).multiplyScalar(-step);
  else if (direction === 'right') delta.copy(right).multiplyScalar(step);
  else if (direction === 'up') delta.copy(up).multiplyScalar(step);
  else if (direction === 'down') delta.copy(up).multiplyScalar(-step);
  else return;

  camera.position.add(delta);
  controls.target.add(delta);
  camera.updateMatrixWorld(true);
  controls.update();
}

function zoomCameraByStep(camera, controls, direction) {
  if (!camera || !controls) return;

  const target = controls.target.clone();
  const offset = camera.position.clone().sub(target);
  const currentDistance = offset.length();
  if (!Number.isFinite(currentDistance) || currentDistance <= 0.00000001) return;

  const minDistance = Math.max(Number(controls.minDistance) || 0.0001, 0.0001);
  const maxDistance = Math.max(Number(controls.maxDistance) || currentDistance * 100, minDistance);
  const multiplier = direction === 'in'
    ? 1 - VIEW_ZOOM_STEP_RATIO
    : direction === 'out'
      ? 1 + VIEW_ZOOM_STEP_RATIO
      : 1;

  if (multiplier === 1) return;

  const nextDistance = THREE.MathUtils.clamp(currentDistance * multiplier, minDistance, maxDistance);
  offset.setLength(nextDistance);
  camera.position.copy(target).add(offset);
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
  camera.updateProjectionMatrix();
  controls.update();
}

function orbitCameraByStep(camera, controls, direction) {
  if (!camera || !controls) return;

  const target = controls.target.clone();
  const offset = camera.position.clone().sub(target);
  if (!Number.isFinite(offset.lengthSq()) || offset.lengthSq() <= 0.00000001) return;

  const quaternion = new THREE.Quaternion();

  if (direction === 'left' || direction === 'right') {
    const angle = direction === 'left' ? VIEW_ORBIT_STEP_RADIANS : -VIEW_ORBIT_STEP_RADIANS;
    quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle);
    offset.applyQuaternion(quaternion);
    camera.up.set(0, 1, 0);
  } else if (direction === 'up' || direction === 'down') {
    const viewDirection = target.clone().sub(camera.position).normalize();
    const rightAxis = viewDirection.clone().cross(camera.up).normalize();
    if (!Number.isFinite(rightAxis.lengthSq()) || rightAxis.lengthSq() <= 0.00000001) return;

    const angle = direction === 'up' ? VIEW_ORBIT_STEP_RADIANS : -VIEW_ORBIT_STEP_RADIANS;
    quaternion.setFromAxisAngle(rightAxis, angle);
    offset.applyQuaternion(quaternion);
    camera.up.applyQuaternion(quaternion).normalize();
  } else {
    return;
  }

  camera.position.copy(target).add(offset);
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
  camera.updateProjectionMatrix();
  controls.update();
}

function ViewportButton({ children, onClick, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        minHeight: 30,
        borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.14)',
        background: disabled ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.08)',
        color: disabled ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.94)',
        padding: '0 10px',
        fontSize: 12,
        fontWeight: 800,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function GizmoButton({ label, title, onClick, background, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        position: 'absolute',
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.45)',
        background,
        color: '#fff',
        fontSize: 11,
        fontWeight: 900,
        lineHeight: '26px',
        padding: 0,
        textAlign: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
        ...style,
      }}
    >
      {label}
    </button>
  );
}

function OrientationGizmo({ onMove, onZoom, onReset, disabled }) {
  const [mode, setMode] = useState('rotate');
  const dragStateRef = useRef(null);

  const runMove = useCallback((direction) => {
    if (disabled || !onMove) return;

    let mappedDirection = direction;
    if (mode === 'rotate') {
      if (direction === 'left') mappedDirection = 'right';
      else if (direction === 'right') mappedDirection = 'left';
    } else if (mode === 'pan') {
      if (direction === 'up') mappedDirection = 'down';
      else if (direction === 'down') mappedDirection = 'up';
    }

    onMove(mappedDirection, mode);
  }, [disabled, mode, onMove]);

  const endDrag = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handlePadPointerDown = useCallback((event) => {
    if (disabled) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      lastX: event.clientX,
      lastY: event.clientY,
      accumulatedX: 0,
      accumulatedY: 0,
    };
    if (event.currentTarget && event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
  }, [disabled]);

  const handlePadPointerMove = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - state.lastX;
    const deltaY = event.clientY - state.lastY;
    state.lastX = event.clientX;
    state.lastY = event.clientY;
    state.accumulatedX += deltaX;
    state.accumulatedY += deltaY;

    const dragStepPixels = 12;
    while (Math.abs(state.accumulatedX) >= dragStepPixels) {
      runMove(state.accumulatedX > 0 ? 'right' : 'left');
      state.accumulatedX += state.accumulatedX > 0 ? -dragStepPixels : dragStepPixels;
    }
    while (Math.abs(state.accumulatedY) >= dragStepPixels) {
      runMove(state.accumulatedY > 0 ? 'down' : 'up');
      state.accumulatedY += state.accumulatedY > 0 ? -dragStepPixels : dragStepPixels;
    }

    event.preventDefault();
  }, [runMove]);

  const handlePadPointerUp = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    if (event.currentTarget && event.currentTarget.releasePointerCapture && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    endDrag();
  }, [endDrag]);

  const arrowButtonStyle = {
    position: 'absolute',
    width: 34,
    height: 34,
    borderRadius: 9,
    border: '1px solid rgba(255,255,255,0.38)',
    background: 'rgba(58,58,58,0.94)',
    color: '#fff',
    fontSize: 19,
    fontWeight: 900,
    lineHeight: '32px',
    padding: 0,
    textAlign: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
  };

  const actionLabel = mode === 'pan' ? 'Pan' : 'Rotate';

  return (
    <div
      style={{
        position: 'absolute',
        top: 68,
        right: 18,
        zIndex: 4,
        width: 122,
        height: 122,
        borderRadius: 14,
        background: 'rgba(28,28,28,0.76)',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          right: 132,
          top: 21,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={() => onZoom && onZoom('in')}
          title="Zoom in"
          aria-label="Zoom in"
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            border: '1px solid rgba(255,255,255,0.38)',
            background: 'rgba(58,58,58,0.94)',
            color: '#fff',
            fontSize: 23,
            fontWeight: 900,
            lineHeight: '34px',
            padding: 0,
            textAlign: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
          }}
        >
          +
        </button>
        <button
          type="button"
          onClick={() => onZoom && onZoom('out')}
          title="Zoom out"
          aria-label="Zoom out"
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            border: '1px solid rgba(255,255,255,0.38)',
            background: 'rgba(58,58,58,0.94)',
            color: '#fff',
            fontSize: 23,
            fontWeight: 900,
            lineHeight: '32px',
            padding: 0,
            textAlign: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
          }}
        >
          −
        </button>
      </div>
      <button
        type="button"
        onClick={onReset}
        title="Reset view"
        aria-label="Reset view"
        style={{
          position: 'absolute',
          left: 5,
          top: 5,
          width: 34,
          height: 34,
          borderRadius: 9,
          border: '1px solid rgba(255,255,255,0.55)',
          background: 'rgba(245,245,245,0.96)',
          color: '#222',
          fontSize: 17,
          fontWeight: 900,
          lineHeight: '32px',
          padding: 0,
          textAlign: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
        }}
      >
        ⌂
      </button>
      <button
        type="button"
        onClick={() => setMode((current) => (current === 'rotate' ? 'pan' : 'rotate'))}
        title={`Current mode: ${actionLabel}. Click to switch to ${mode === 'rotate' ? 'Pan' : 'Rotate'}`}
        aria-label={`Current mode: ${actionLabel}. Switch to ${mode === 'rotate' ? 'Pan' : 'Rotate'}`}
        style={{
          position: 'absolute',
          right: 5,
          top: 5,
          width: 34,
          height: 34,
          borderRadius: 9,
          border: mode === 'pan'
            ? '1px solid rgba(96,165,250,0.95)'
            : '1px solid rgba(255,255,255,0.3)',
          background: mode === 'pan'
            ? 'rgba(37,99,235,0.92)'
            : 'rgba(58,58,58,0.94)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 900,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {mode === 'pan' ? 'P' : 'R'}
      </button>
      <button
        type="button"
        onClick={() => runMove('down')}
        title={`${actionLabel} up${mode === 'rotate' ? ' by 22.5°' : ''}`}
        aria-label={`${actionLabel} up`}
        style={{ ...arrowButtonStyle, left: 44, top: 5 }}
      >
        ↑
      </button>
      <button
        type="button"
        onClick={() => runMove('left')}
        title={`${actionLabel} left${mode === 'rotate' ? ' by 22.5°' : ''}`}
        aria-label={`${actionLabel} left`}
        style={{ ...arrowButtonStyle, left: 5, top: 44 }}
      >
        ←
      </button>
      <button
        type="button"
        onClick={() => runMove('right')}
        title={`${actionLabel} right${mode === 'rotate' ? ' by 22.5°' : ''}`}
        aria-label={`${actionLabel} right`}
        style={{ ...arrowButtonStyle, right: 5, top: 44 }}
      >
        →
      </button>
      <button
        type="button"
        onClick={() => runMove('up')}
        title={`${actionLabel} down${mode === 'rotate' ? ' by 22.5°' : ''}`}
        aria-label={`${actionLabel} down`}
        style={{ ...arrowButtonStyle, left: 44, bottom: 5 }}
      >
        ↓
      </button>
      <div
        role="button"
        tabIndex={0}
        onPointerDown={handlePadPointerDown}
        onPointerMove={handlePadPointerMove}
        onPointerUp={handlePadPointerUp}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        title={`Hold and drag to ${actionLabel.toLowerCase()} the view`}
        aria-label={`Hold and drag to ${actionLabel.toLowerCase()} the view`}
        style={{
          position: 'absolute',
          left: 43,
          top: 43,
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.55)',
          background: 'rgba(80,80,80,0.96)',
          color: '#fff',
          fontSize: 9,
          fontWeight: 900,
          lineHeight: '10px',
          padding: 0,
          textAlign: 'center',
          cursor: 'grab',
          boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          whiteSpace: 'pre-line',
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
          }
        }}
      >
        drag
      </div>
    </div>
  );
}

export default function CanvasModelViewport({
  modelFile,
  onChooseModel,
  onClose,
  paintStageRef,
  documentWidth,
  documentHeight,
  onProjectionStrokeCommitted,
  onPhysicalPaintStrokeCommitted,
  modelViewportRef,
  paintMaterialSettings,
  lock3dPaintToStartingSurface = false,
  activeTool,
  layers = [],
  activeLayerId = '',
  onUpdateDecalLayer,
  onSelectLayer,
  textSettings = {},
  onCreateTextLayer,
  onUpdateTextLayer,
}) {
  const containerRef = useRef(null);
  const runtimeRef = useRef(null);
  const currentModelRef = useRef(null);
  const paintConfigRef = useRef({ paintStageRef, documentWidth, documentHeight, onProjectionStrokeCommitted, onPhysicalPaintStrokeCommitted });
  paintConfigRef.current = { paintStageRef, documentWidth, documentHeight, onProjectionStrokeCommitted, onPhysicalPaintStrokeCommitted };
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;
  const layerStateRef = useRef({ layers: Array.isArray(layers) ? layers : [], activeLayerId });
  layerStateRef.current = { layers: Array.isArray(layers) ? layers : [], activeLayerId };
  const layersPropRef = useRef(Array.isArray(layers) ? layers : []);
  layersPropRef.current = Array.isArray(layers) ? layers : [];
  const activeLayerIdRef = useRef(String(activeLayerId || ''));
  activeLayerIdRef.current = String(activeLayerId || '');
  const onUpdateDecalLayerRef = useRef(onUpdateDecalLayer);
  onUpdateDecalLayerRef.current = onUpdateDecalLayer;
  const onSelectLayerRef = useRef(onSelectLayer);
  onSelectLayerRef.current = onSelectLayer;
  const textSettingsRef = useRef(textSettings || {});
  textSettingsRef.current = textSettings || {};
  const onCreateTextLayerRef = useRef(onCreateTextLayer);
  onCreateTextLayerRef.current = onCreateTextLayer;
  const onUpdateTextLayerRef = useRef(onUpdateTextLayer);
  onUpdateTextLayerRef.current = onUpdateTextLayer;
  const activeDecalLayer = useMemo(() => (Array.isArray(layers) ? layers : []).find((layer) => (
    String(layer?.id || '') === String(activeLayerId || '')
    && String(layer?.contentType || layer?.type || '') === 'decal'
    && layer?.decalData
  )) || null, [activeLayerId, layers]);
  const updateSelectedDecalLayer = useCallback((patchOrUpdater) => {
    if (!activeDecalLayer || typeof onUpdateDecalLayerRef.current !== 'function') return;
    const current = activeDecalLayer.decalData && typeof activeDecalLayer.decalData === 'object'
      ? activeDecalLayer.decalData
      : {};
    const patch = typeof patchOrUpdater === 'function' ? patchOrUpdater(current) : patchOrUpdater;
    onUpdateDecalLayerRef.current(String(activeDecalLayer.id || ''), {
      ...current,
      ...(patch && typeof patch === 'object' ? patch : {}),
    });
  }, [activeDecalLayer]);
  const lock3dPaintToStartingSurfaceRef = useRef(Boolean(lock3dPaintToStartingSurface));
  lock3dPaintToStartingSurfaceRef.current = Boolean(lock3dPaintToStartingSurface);
  const projectionStrokeCountRef = useRef(0);
  const livePbrPreviewStrokeRef = useRef(null);
  const livePbrPreviewFrameRef = useRef(0);
  const livePbrPreviewLastUpdateAtRef = useRef(0);
  const zoomReferenceDistanceRef = useRef(0);
  const zoomIndicatorLastUpdateRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const loadDebugEntries = [];
  const setLoadDebugEntries = () => {};
  const loadProfilerRef = useRef(null);
  const longTaskObserverRef = useRef(null);
  const [error, setError] = useState('');
  const [modelName, setModelName] = useState('');
  const [zoomPercent, setZoomPercent] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [toolbarPortalTarget, setToolbarPortalTarget] = useState(null);
  const toolbarPortalOwnerIdRef = useRef(`canvas-3d-toolbar-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`);
  const [showTransformPanel, setShowTransformPanel] = useState(false);
  const [showControlsPanel, setShowControlsPanel] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px), (pointer: coarse)').matches
      : false
  ));
  const [restoredFile, setRestoredFile] = useState(null);
  const skipNextUvLayoutEffectRef = useRef(false);
  const [uvTileCount, setUvTileCount] = useState(1);
  const [uvResolutionPerTile, setUvResolutionPerTile] = useState(DEFAULT_GENERATED_UV_RESOLUTION);
  const [maxTextureSize, setMaxTextureSize] = useState(8192);
  const [uvLayoutMode, setUvLayoutMode] = useState(UV_LAYOUT_MODE_ORIGINAL);
  const [modelSupportsOriginalUvs, setModelSupportsOriginalUvs] = useState(false);
  const [showUvColours, setShowUvColours] = useState(false);
  const [showModelEdges, setShowModelEdges] = useState(false);
  const showModelEdgesDebug = false;
  const [viewportDisplayMode, setViewportDisplayMode] = useState('3d');
  // Surface tools: 'paint' paints (restricted to the selection when one
  // exists), 'faces' and 'material' turn left-click into face picking.
  const [surfaceMode, setSurfaceModeState] = useState('paint');
  const surfaceModeRef = useRef('paint');
  surfaceModeRef.current = surfaceMode;
  const [surfaceSelectionInfo, setSurfaceSelectionInfo] = useState({
    shape: '',
    kind: '',
    count: 0,
    inverted: false,
    overlapFaces: null,
  });
  const [exportOverlay, setExportOverlay] = useState({
    active: false,
    title: '',
    detail: '',
  });
  const viewportDisplayModeRef = useRef('3d');
  viewportDisplayModeRef.current = viewportDisplayMode;
  const textureViewCanvasRef = useRef(null);
  const texturePaintStateRef = useRef(null);
  const bakeReplicaAppearanceRef = useRef(null);
  const viewSettingsRef = useRef({ showUvColours: false, showModelEdges: false, showModelEdgesDebug: false });
  viewSettingsRef.current = { showUvColours, showModelEdges, showModelEdgesDebug };

  const handleTextureViewPointerDown = useCallback((event) => {
    const runtime = runtimeRef.current;
    runtime?.handleTextureViewPointerDown?.(event);
  }, []);

  const handleTextureViewPointerMove = useCallback((event) => {
    runtimeRef.current?.handleTextureViewPointerMove?.(event);
  }, []);

  const handleTextureViewPointerUp = useCallback((event) => {
    runtimeRef.current?.handleTextureViewPointerUp?.(event);
  }, []);

  const handleTextureViewPointerCancel = useCallback((event) => {
    runtimeRef.current?.handleTextureViewPointerCancel?.(event);
  }, []);


  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    let frame = 0;
    const ownerId = toolbarPortalOwnerIdRef.current;

    const findToolbarTarget = () => {
      const target = document.getElementById('canvas-3d-toolbar-portal-target');
      if (target) {
        const currentOwner = target.dataset.voxemeraToolbarOwner || '';
        if (!currentOwner || currentOwner === ownerId) {
          target.dataset.voxemeraToolbarOwner = ownerId;
          setToolbarPortalTarget(target);
          return;
        }
      }
      frame = window.requestAnimationFrame(findToolbarTarget);
    };

    findToolbarTarget();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      const target = document.getElementById('canvas-3d-toolbar-portal-target');
      if (target?.dataset?.voxemeraToolbarOwner === ownerId) {
        delete target.dataset.voxemeraToolbarOwner;
      }
    };
  }, []);

  const materialSettingsRef = useRef({
    gloss: DEFAULT_3D_PAINT_GLOSS,
    height: DEFAULT_3D_PAINT_HEIGHT,
    metalness: DEFAULT_3D_PAINT_METALNESS,
  });
  materialSettingsRef.current = {
    gloss: Math.max(0, Math.min(100, Number(paintMaterialSettings?.gloss ?? DEFAULT_3D_PAINT_GLOSS) || 0)),
    height: Math.max(0, Math.min(100, Number(paintMaterialSettings?.height ?? DEFAULT_3D_PAINT_HEIGHT) || 0)),
    metalness: Math.max(0, Math.min(100, Number(paintMaterialSettings?.metalness ?? DEFAULT_3D_PAINT_METALNESS) || 0)),
  };

  const pbrFilesInputRef = useRef(null);
  const pbrFolderInputRef = useRef(null);
  const pbrZipInputRef = useRef(null);
  const pbrSetResourcesRef = useRef(new Map());
  const [pbrBrushEnabled, setPbrBrushEnabled] = useState(false);
  const [pbrBrushLoading, setPbrBrushLoading] = useState(false);
  const [pbrBrushError, setPbrBrushError] = useState('');
  const [pbrBrushSourceLabel, setPbrBrushSourceLabel] = useState('');
  const [pbrBrushSets, setPbrBrushSets] = useState([]);
  const [pbrBrushSelectedSetKey, setPbrBrushSelectedSetKey] = useState('');
  const [pbrBrushScalePercent, setPbrBrushScalePercent] = useState(DEFAULT_PBR_TEXTURE_SCALE);
  const [pbrBrushAngleDegrees, setPbrBrushAngleDegrees] = useState(0);
  const pbrBrushStateRef = useRef({
    enabled: false,
    loading: false,
    sourceLabel: '',
    sets: [],
    selectedSetKey: '',
    scalePercent: DEFAULT_PBR_TEXTURE_SCALE,
  });
  pbrBrushStateRef.current = {
    enabled: pbrBrushEnabled,
    loading: pbrBrushLoading,
    sourceLabel: pbrBrushSourceLabel,
    sets: pbrBrushSets,
    selectedSetKey: pbrBrushSelectedSetKey,
    scalePercent: pbrBrushScalePercent,
    angleDegrees: pbrBrushAngleDegrees,
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const detail = {
      active: Boolean(
        pbrBrushEnabled
        && pbrBrushSelectedSetKey
        && pbrSetResourcesRef.current.has(pbrBrushSelectedSetKey)
      ),
    };
    window.dispatchEvent(new CustomEvent('voxemera:3d-pbr-active-change', { detail }));
    return () => {
      window.dispatchEvent(new CustomEvent('voxemera:3d-pbr-active-change', {
        detail: { active: false },
      }));
    };
  }, [pbrBrushEnabled, pbrBrushSelectedSetKey, pbrBrushSets]);

  useEffect(() => {
    [pbrFolderInputRef.current].forEach((input) => {
      if (!input?.setAttribute) return;
      input.setAttribute('webkitdirectory', '');
      input.setAttribute('directory', '');
    });
  }, []);

  const clearPbrBrushSelection = useCallback(() => {
    pbrSetResourcesRef.current = new Map();
    setPbrBrushEnabled(false);
    setPbrBrushLoading(false);
    setPbrBrushError('');
    setPbrBrushSourceLabel('');
    setPbrBrushSets([]);
    setPbrBrushSelectedSetKey('');
    setPbrBrushScalePercent(DEFAULT_PBR_TEXTURE_SCALE);
    setPbrBrushAngleDegrees(0);
  }, []);

  const restorePbrBrushFromRecord = useCallback(async (record) => {
    const saved = record?.pbrBrush;
    if (!saved?.sets?.length) {
      clearPbrBrushSelection();
      return false;
    }

    const loadedEntries = await Promise.all((saved.sets || []).map((setRecord) => buildLoadedPbrSet(setRecord, materialSettingsRef.current)));
    const validResources = loadedEntries.filter(Boolean);
    if (!validResources.length) {
      clearPbrBrushSelection();
      return false;
    }

    const resourceMap = new Map(validResources.map((entry) => [entry.key, entry]));
    pbrSetResourcesRef.current = resourceMap;
    const nextSets = validResources.map((entry) => ({
      key: entry.key,
      label: entry.label,
      mapTypes: entry.mapTypes || [],
    }));
    const selectedKey = saved.selectedSetKey && resourceMap.has(saved.selectedSetKey)
      ? saved.selectedSetKey
      : getPreferredPbrSetKey(nextSets);
    setPbrBrushSets(nextSets);
    setPbrBrushSelectedSetKey(selectedKey);
    setPbrBrushSourceLabel(saved.sourceLabel || 'Restored PBR set');
    setPbrBrushScalePercent(Math.max(10, Math.min(800, Number(saved.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)));
    setPbrBrushAngleDegrees(((Number(saved.angleDegrees) || 0) % 360 + 360) % 360);
    setPbrBrushEnabled(Boolean(saved.enabled));
    setPbrBrushLoading(false);
    setPbrBrushError('');
    return true;
  }, [clearPbrBrushSelection]);

  const importResolvedPbrSets = useCallback(async (setRecords, sourceLabel = '') => {
    const discoveredSets = Array.isArray(setRecords) ? setRecords : [];
    if (!discoveredSets.length) {
      setPbrBrushError('No usable PBR texture maps were found.');
      return false;
    }

    setPbrBrushLoading(true);
    setPbrBrushError('');
    try {
      const loadedSets = await Promise.all(discoveredSets.map((setRecord) => buildLoadedPbrSet(setRecord, materialSettingsRef.current)));
      const validResources = loadedSets.filter(Boolean);
      if (!validResources.length) {
        setPbrBrushError('The PBR set could not be decoded.');
        return false;
      }
      // Keep every PBR resource already used by existing strokes. Replacing
      // this map here meant that importing a second texture discarded the
      // first texture's runtime resource. The old strokes retained only their
      // pbrSetKey, so the next save omitted that resource and reload rebuilt
      // those strokes with the latest selected PBR instead.
      const resourceMap = new Map(pbrSetResourcesRef.current);
      validResources.forEach((entry) => {
        resourceMap.set(entry.key, entry);
      });
      pbrSetResourcesRef.current = resourceMap;

      const importedKeys = new Set(validResources.map((entry) => entry.key));
      const previousSets = Array.isArray(pbrBrushStateRef.current?.sets)
        ? pbrBrushStateRef.current.sets
        : [];
      const nextSetsByKey = new Map(
        previousSets.map((entry) => [String(entry?.key || ''), entry]),
      );
      validResources.forEach((entry) => {
        nextSetsByKey.set(entry.key, {
          key: entry.key,
          label: entry.label,
          mapTypes: entry.mapTypes || [],
        });
      });
      const nextSets = Array.from(nextSetsByKey.values()).filter((entry) => entry?.key);
      const importedSets = nextSets.filter((entry) => importedKeys.has(entry.key));
      setPbrBrushSets(nextSets);
      setPbrBrushSelectedSetKey(getPreferredPbrSetKey(importedSets));
      setPbrBrushSourceLabel(sourceLabel || (importedSets.length === 1 ? importedSets[0].label : `${importedSets.length} PBR sets loaded`));
      setPbrBrushEnabled(true);
      return true;
    } catch (error) {
      setPbrBrushError(error?.message || 'Unable to load the PBR set.');
      return false;
    } finally {
      setPbrBrushLoading(false);
    }
  }, []);

  const handlePbrFileListImport = useCallback(async (entries, sourceLabel = '') => {
    const sets = collectPbrSetsFromEntries(entries);
    return importResolvedPbrSets(sets, sourceLabel);
  }, [importResolvedPbrSets]);

  const handleChoosePbrFiles = useCallback(() => {
    pbrFilesInputRef.current?.click?.();
  }, []);

  const handleChoosePbrFolder = useCallback(() => {
    pbrFolderInputRef.current?.click?.();
  }, []);

  const handleChoosePbrZip = useCallback(() => {
    pbrZipInputRef.current?.click?.();
  }, []);

  const handlePbrFilesSelected = useCallback(async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    await handlePbrFileListImport(
      files.map((file) => ({ name: file.name, path: file.name, file, blob: file })),
      files.length === 1 ? files[0].name : `${files.length} files`,
    );
    event.target.value = '';
  }, [handlePbrFileListImport]);

  const handlePbrFolderSelected = useCallback(async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    await handlePbrFileListImport(
      files.map((file) => ({
        name: file.name,
        path: file.webkitRelativePath || file.name,
        file,
        blob: file,
      })),
      files[0]?.webkitRelativePath?.split('/')?.[0] || `${files.length} folder files`,
    );
    event.target.value = '';
  }, [handlePbrFileListImport]);

  const handlePbrZipSelected = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setPbrBrushLoading(true);
    setPbrBrushError('');
    try {
      const JSZip = await loadJsZip();
      const archive = await JSZip.loadAsync(file);
      const entries = [];
      const archiveFiles = Object.values(archive.files || {});
      for (const zipEntry of archiveFiles) {
        if (!zipEntry || zipEntry.dir || !PBR_IMAGE_FILE_EXTENSION_PATTERN.test(zipEntry.name || '')) continue;
        const blob = await zipEntry.async('blob');
        entries.push({
          name: zipEntry.name.split('/').pop() || zipEntry.name,
          path: zipEntry.name,
          blob,
        });
      }
      const sets = collectPbrSetsFromEntries(entries);
      if (!sets.length) {
        setPbrBrushError('No usable PBR texture maps were found inside that ZIP.');
      } else {
        await importResolvedPbrSets(sets, file.name);
      }
    } catch (error) {
      setPbrBrushError(error?.message || 'Unable to load the PBR ZIP.');
    } finally {
      setPbrBrushLoading(false);
      event.target.value = '';
    }
  }, [importResolvedPbrSets]);

  useEffect(() => {
    if (!modelViewportRef || typeof modelViewportRef !== 'object') return undefined;
    modelViewportRef.current = {
      getProjectionStrokeCount: () => projectionStrokeCountRef.current,
      setProjectionStrokeCount: (count) => {
        const safeCount = Math.max(0, Number(count) || 0);
        const runtime = runtimeRef.current;
        if (runtime && typeof runtime.setProjectionStrokeCount === 'function') {
          return runtime.setProjectionStrokeCount(safeCount);
        }
        projectionStrokeCountRef.current = safeCount;
        return projectionStrokeCountRef.current;
      },
      // Physical Paint Tool strokes on the model (one Canvas undo step each).
      getPhysical3dStrokeCount: () => runtimeRef.current?.physicalPaint3d?.strokeCount?.() || 0,
      setPhysical3dStrokeCount: (count) => runtimeRef.current?.physicalPaint3d?.setStrokeCount?.(count) || 0,
      clearProjectionStrokes: () => {
        const runtime = runtimeRef.current;
        if (runtime && typeof runtime.clearProjectionPaintCompletely === 'function') {
          return runtime.clearProjectionPaintCompletely();
        }
        projectionStrokeCountRef.current = 0;
        return 0;
      },
      getDefaultDecalPlacement: () => {
        const runtime = runtimeRef.current;
        return runtime && typeof runtime.getDefaultDecalPlacement === 'function'
          ? runtime.getDefaultDecalPlacement()
          : null;
      },
      exportModel: (format) => {
        const runtime = runtimeRef.current;
        if (!runtime || typeof runtime.exportModel !== 'function') {
          return Promise.reject(new Error('The 3D exporter is not ready yet.'));
        }
        return runtime.exportModel(format);
      },
    };
    return () => {
      if (modelViewportRef.current) modelViewportRef.current = null;
    };
  }, [modelViewportRef]);

  const hasExplicitModelFile = Boolean(modelFile);

  useEffect(() => {
    if (hasExplicitModelFile) return undefined;
    let cancelled = false;
    setLoadDebugEntries([]);
    const profiler = createModelLoadProfiler((entry, entries) => {
      if (!cancelled) setLoadDebugEntries(entries);
    });
    loadProfilerRef.current = profiler;
    profiler.mark('Reload restore: start', 'Looking in IndexedDB for the last imported 3D model.');
    profiler.measureAsync('IndexedDB model restore', 'Opening the model database, reading the saved bytes and rebuilding a File object.', loadSavedModelFile)
      .then((file) => {
        if (!cancelled && file) {
          profiler.mark('IndexedDB model restored', 'Saved model bytes are now in memory.', { name: file.name, bytes: file.size });
          setRestoredFile(file);
        } else if (!cancelled) {
          profiler.mark('IndexedDB contained no model', 'There is no saved model to restore.');
        }
      })
      .catch((error) => {
        profiler.mark('IndexedDB restore failed', error?.message || 'Unknown IndexedDB error.');
      });
    return () => {
      cancelled = true;
    };
  }, [hasExplicitModelFile]);

  const activeModelFile = modelFile || restoredFile;
  const shouldPersistExplicitModelFile = Boolean(modelFile && activeModelFile === modelFile);

  const resetView = useCallback(() => {
    const runtime = runtimeRef.current;
    const model = currentModelRef.current;
    if (!runtime || !model) return;
    frameObject(runtime.camera, runtime.controls, model);
    runtime.defaultOrbitTarget?.copy?.(runtime.controls.target);
    const framedDistance = runtime.camera.position.distanceTo(runtime.controls.target);
    zoomReferenceDistanceRef.current = Math.max(framedDistance, 0.00000001);
    setZoomPercent(100);
  }, []);

  const waitForNextPaint = useCallback(() => new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  }), []);

  const showExportOverlay = useCallback((title, detail = '') => {
    setExportOverlay({
      active: true,
      title: title || 'Exporting model…',
      detail,
    });
  }, []);

  const hideExportOverlay = useCallback(() => {
    setExportOverlay({
      active: false,
      title: '',
      detail: '',
    });
  }, []);


  const transformModel = useCallback((action) => {
    const model = currentModelRef.current;
    if (!model) return;
    const quarterTurn = Math.PI / 2;

    switch (action) {
      case 'rotate-x-negative': model.rotateX(-quarterTurn); break;
      case 'rotate-x-positive': model.rotateX(quarterTurn); break;
      case 'rotate-y-negative': model.rotateY(-quarterTurn); break;
      case 'rotate-y-positive': model.rotateY(quarterTurn); break;
      case 'rotate-z-negative': model.rotateZ(-quarterTurn); break;
      case 'rotate-z-positive': model.rotateZ(quarterTurn); break;
      case 'flip-x': model.scale.x *= -1; break;
      case 'flip-y': model.scale.y *= -1; break;
      case 'flip-z': model.scale.z *= -1; break;
      case 'reset': {
        const original = model.userData?.voxemeraOriginalTransform;
        if (original) {
          model.position.copy(original.position);
          model.quaternion.copy(original.quaternion);
          model.scale.copy(original.scale);
        }
        break;
      }
      default: return;
    }

    model.updateMatrixWorld(true);
    saveStoredTransform(modelName, model);
    const runtime = runtimeRef.current;
    if (runtime) fitDirectionalShadowToObject(runtime, model);
  }, [modelName]);

  const moveViewByStep = useCallback((direction, mode) => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    if (mode === 'pan') {
      panCameraByStep(runtime.camera, runtime.controls, direction);
      return;
    }
    orbitCameraByStep(runtime.camera, runtime.controls, direction);
  }, []);

  const zoomViewByStep = useCallback((direction) => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    zoomCameraByStep(runtime.camera, runtime.controls, direction);
  }, []);

  const setSurfaceMode = useCallback((mode) => {
    surfaceModeRef.current = mode;
    setSurfaceModeState(mode);
    runtimeRef.current?.setSurfaceMode?.(mode);
  }, []);

  const runSurfaceAction = useCallback((action, ...args) => {
    const runtime = runtimeRef.current;
    if (!runtime || typeof runtime[action] !== 'function') return false;
    return runtime[action](...args);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const bootProfiler = loadProfilerRef.current || createModelLoadProfiler((entry, entries) => setLoadDebugEntries(entries));
    loadProfilerRef.current = bootProfiler;
    bootProfiler.mark('3D runtime effect: start', 'Creating Three.js scene, renderer, controls, lighting and animation loop.');

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(DEFAULT_BACKGROUND);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 10000);
    camera.position.set(4, 3, 4);

    bootProfiler.mark('WebGL renderer: constructing', 'Browser creates a WebGL context. Driver/context creation can stall.');
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    bootProfiler.mark('WebGL renderer: constructed', 'WebGL context and renderer capabilities are available.', { maxTextureSize: renderer.capabilities.maxTextureSize });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(DEFAULT_BACKGROUND, 1);
    setMaxTextureSize(Math.max(2048, Number(renderer.capabilities.maxTextureSize) || 8192));

    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.touchAction = 'none';
    renderer.domElement.style.background = '#666666';

    // Fill the WebGL drawing buffer with the viewport background before the
    // canvas is attached. A newly-created browser canvas is otherwise briefly
    // composited using its default white backing surface before Three.js gets
    // its first animation frame.
    const initialViewportWidth = Math.max(1, container.clientWidth || 1);
    const initialViewportHeight = Math.max(1, container.clientHeight || 1);
    renderer.setSize(initialViewportWidth, initialViewportHeight, false);
    camera.aspect = initialViewportWidth / initialViewportHeight;
    camera.updateProjectionMatrix();
    renderer.clear(true, true, true);
    renderer.render(scene, camera);

    container.appendChild(renderer.domElement);
    bootProfiler.mark('Renderer canvas attached', 'The WebGL canvas was inserted after its background frame had already been rendered.');

    const selectionCanvas = document.createElement('canvas');
    selectionCanvas.width = 1;
    selectionCanvas.height = 1;
    selectionCanvas.style.position = 'absolute';
    selectionCanvas.style.inset = '0';
    selectionCanvas.style.width = '100%';
    selectionCanvas.style.height = '100%';
    selectionCanvas.style.pointerEvents = 'none';
    selectionCanvas.style.zIndex = '2';
    container.appendChild(selectionCanvas);
    const selectionContext = selectionCanvas.getContext('2d', { alpha: true });

    // Editable 3D polygon selections are rendered directly by WebGL. The old
    // 2D overlay had to project and redraw every handle whenever the camera
    // changed. These line/point buffers remain in model world space, so camera
    // movement is handled entirely by the GPU in the normal scene render.
    const gpuSelectionOverlay = new THREE.Group();
    gpuSelectionOverlay.name = 'Voxemera GPU selection overlay';
    gpuSelectionOverlay.renderOrder = 5000;
    scene.add(gpuSelectionOverlay);

    const faceSelectionOverlayGroup = new THREE.Group();
    faceSelectionOverlayGroup.name = 'Voxemera face selection overlay';
    scene.add(faceSelectionOverlayGroup);
    const faceSelectionOverlayMaterial = new THREE.MeshBasicMaterial({
      color: 0xff9f1c,
      transparent: true,
      opacity: 0.34,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -8,
      polygonOffsetUnits: -8,
      toneMapped: false,
    });

    const gpuSelectionLineGeometry = new THREE.BufferGeometry();
    const gpuSelectionLineBaseMaterial = new THREE.LineBasicMaterial({
      color: 0x111111,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const gpuSelectionLineDashMaterial = new THREE.LineDashedMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      dashSize: 0.035,
      gapSize: 0.025,
      scale: 1,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const gpuSelectionLineBase = new THREE.Line(gpuSelectionLineGeometry, gpuSelectionLineBaseMaterial);
    const gpuSelectionLineDash = new THREE.Line(gpuSelectionLineGeometry, gpuSelectionLineDashMaterial);
    gpuSelectionLineBase.frustumCulled = false;
    gpuSelectionLineDash.frustumCulled = false;
    gpuSelectionLineBase.renderOrder = 5000;
    gpuSelectionLineDash.renderOrder = 5001;
    gpuSelectionOverlay.add(gpuSelectionLineBase, gpuSelectionLineDash);

    const gpuSelectionHandleGeometry = new THREE.BufferGeometry();
    const gpuSelectionHandleOuterMaterial = new THREE.PointsMaterial({
      color: 0x1f6feb,
      size: 12,
      sizeAttenuation: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1,
      toneMapped: false,
    });
    const gpuSelectionHandleInnerMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 7,
      sizeAttenuation: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1,
      toneMapped: false,
    });
    const gpuSelectionHandleOuter = new THREE.Points(gpuSelectionHandleGeometry, gpuSelectionHandleOuterMaterial);
    const gpuSelectionHandleInner = new THREE.Points(gpuSelectionHandleGeometry, gpuSelectionHandleInnerMaterial);
    gpuSelectionHandleOuter.frustumCulled = false;
    gpuSelectionHandleInner.frustumCulled = false;
    gpuSelectionHandleOuter.renderOrder = 5002;
    gpuSelectionHandleInner.renderOrder = 5003;
    gpuSelectionOverlay.add(gpuSelectionHandleOuter, gpuSelectionHandleInner);

    const gpuSelectionMidpointGeometry = new THREE.BufferGeometry();
    const gpuSelectionMidpointOuterMaterial = new THREE.PointsMaterial({
      color: 0x111111,
      size: 8,
      sizeAttenuation: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
    });
    const gpuSelectionMidpointInnerMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 4,
      sizeAttenuation: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.95,
      toneMapped: false,
    });
    const gpuSelectionMidpointOuter = new THREE.Points(gpuSelectionMidpointGeometry, gpuSelectionMidpointOuterMaterial);
    const gpuSelectionMidpointInner = new THREE.Points(gpuSelectionMidpointGeometry, gpuSelectionMidpointInnerMaterial);
    gpuSelectionMidpointOuter.frustumCulled = false;
    gpuSelectionMidpointInner.frustumCulled = false;
    gpuSelectionMidpointOuter.renderOrder = 5004;
    gpuSelectionMidpointInner.renderOrder = 5005;
    gpuSelectionOverlay.add(gpuSelectionMidpointOuter, gpuSelectionMidpointInner);
    gpuSelectionOverlay.visible = false;

    let activeSelection = null;
    let selectionDraft = null;
    let activeSelectionPointerId = null;
    // Face-selection presentation state. Declared here, ahead of every
    // function that can redraw the selection overlay.
    let faceSelectionPresentationReady = false;
    let surfaceSelectionOverlapTimer = 0;
    let lastPresentedSelection;
    let lastPresentedInverted = false;
    let lastPresentedSurfaceMode = '';

    // Strokes and fills remember the face restriction they were painted with.
    // Baking, export and reload then reproduce that restriction instead of
    // using whatever selection happens to be active at that later moment.
    const captureSelectionSnapshot = () => {
      if (!activeSelection?.selectedFaces) return null;
      return {
        selectedFaces: cloneFaceMap(activeSelection.selectedFaces),
        boundaryFaces: cloneFaceMap(activeSelection.boundaryFaces),
        inverted: Boolean(activeSelection.inverted),
      };
    };

    const selectionFromSnapshot = (snapshot) => {
      if (!snapshot?.selectedFaces) return null;
      const selection = createFaceSelection(cloneFaceMap(snapshot.selectedFaces), { inverted: snapshot.inverted });
      selection.boundaryFaces = cloneFaceMap(snapshot.boundaryFaces);
      return selection;
    };

    const runWithSelection = (selection, task) => {
      const previousSelection = activeSelection;
      activeSelection = selection;
      try {
        return task();
      } finally {
        activeSelection = previousSelection;
      }
    };

    // Mesh uuids change on every load; persisted restrictions use the mesh's
    // traversal position and name instead.
    const getModelMeshKeyMaps = () => {
      const keyByUuid = new Map();
      const uuidByKey = new Map();
      let meshIndex = 0;
      currentModelRef.current?.traverse?.((child) => {
        if (!child?.isMesh || child.userData?.voxemeraProjectionDecal) return;
        const key = buildStableMeshKey(meshIndex, child.name);
        meshIndex += 1;
        keyByUuid.set(child.uuid, key);
        uuidByKey.set(key, child.uuid);
      });
      return { keyByUuid, uuidByKey };
    };
    let editableSelectionVertexIndex = -1;
    let editableSelectionDirty = false;
    const selectionTopologyCache = new WeakMap();

    const selectionDebugPanel = {
      style: { display: 'none' },
      textContent: '',
      remove: () => {},
    };

    const selectionDebugState = {
      event: 'initialised',
      stroke: null,
      meshes: [],
    };

    const formatSelectionDebug = () => {
      const selection = activeSelection;
      const lines = ['3D SELECTION DEBUG'];
      lines.push(`event: ${selectionDebugState.event}`);
      lines.push(`active: ${selection ? 'yes' : 'no'}  shape: ${selection?.shape || '-'}  inverted: ${selection?.inverted ? 'yes' : 'no'}`);
      lines.push(`samples: ${selection?.surfaceSamples?.length || 0}  selected mesh entries: ${selection?.selectedFaces?.size || 0}`);
      if (selectionDebugState.stroke) {
        const stroke = selectionDebugState.stroke;
        lines.push(`stroke canvas: ${stroke.width}x${stroke.height}  alpha pixels: ${stroke.alphaPixels}  alpha bounds: ${stroke.bounds || 'none'}`);
      }
      if (selectionDebugState.meshes.length) {
        lines.push('meshes:');
        selectionDebugState.meshes.forEach((mesh) => {
          lines.push(`- ${mesh.name || '(unnamed)'} uuid=${mesh.uuid}`);
          lines.push(`  total=${mesh.total} selected=${mesh.selected} boundary=${mesh.boundary} usable=${mesh.usable} result=${mesh.result}`);
        });
      }
      return lines.join('\n');
    };

    const refreshSelectionDebug = (eventName = selectionDebugState.event) => {
      selectionDebugState.event = eventName;
    };

    const isSelectionTool = (tool = activeToolRef.current) => (
      tool === 'marquee_rect' || tool === 'marquee_ellipse' || tool === 'marquee_polygon' || tool === 'marquee_polygon_edit'
    );

    const normalizeSelectionBounds = (start, end) => ({
      x: Math.min(start.x, end.x),
      y: Math.min(start.y, end.y),
      width: Math.abs(end.x - start.x),
      height: Math.abs(end.y - start.y),
    });

    const projectWorldPointToViewport = (worldPoint) => {
      if (!worldPoint) return null;
      const rect = renderer.domElement.getBoundingClientRect();
      const projected = worldPoint.clone().project(camera);
      if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || projected.z < -1 || projected.z > 1) return null;
      return {
        x: (projected.x * 0.5 + 0.5) * rect.width,
        y: (-projected.y * 0.5 + 0.5) * rect.height,
      };
    };

    const appendSelectionPath = (ctx, selection, scale = 1, offsetX = 0, offsetY = 0, options = null) => {
      if (!ctx || !selection) return false;
      if (selection.surfaceSamples?.length) {
        const projectedPoints = selection.surfaceSamples
          .map((sample) => projectWorldPointToViewport(sample.worldPoint))
          .filter(Boolean);
        if (projectedPoints.length < 2) return false;
        ctx.moveTo((projectedPoints[0].x + offsetX) * scale, (projectedPoints[0].y + offsetY) * scale);
        for (let index = 1; index < projectedPoints.length; index += 1) {
          ctx.lineTo((projectedPoints[index].x + offsetX) * scale, (projectedPoints[index].y + offsetY) * scale);
        }
        if (selection.closed) ctx.closePath();
        return true;
      }
      const shape = selection.shape || 'rectangle';
      if (shape === 'polygon') {
        const points = Array.isArray(selection.points) ? selection.points : [];
        if (points.length < 2) return false;
        ctx.moveTo((points[0].x + offsetX) * scale, (points[0].y + offsetY) * scale);
        for (let index = 1; index < points.length; index += 1) {
          ctx.lineTo((points[index].x + offsetX) * scale, (points[index].y + offsetY) * scale);
        }
        if (selection.closed) ctx.closePath();
        return true;
      }
      const bounds = selection.bounds;
      if (!bounds || bounds.width < 0.5 || bounds.height < 0.5) return false;
      const x = (bounds.x + offsetX) * scale;
      const y = (bounds.y + offsetY) * scale;
      const width = bounds.width * scale;
      const height = bounds.height * scale;
      if (shape === 'ellipse') {
        ctx.ellipse(x + width / 2, y + height / 2, Math.max(0.5, width / 2), Math.max(0.5, height / 2), 0, 0, Math.PI * 2);
      } else {
        ctx.rect(x, y, width, height);
      }
      return true;
    };

    const replaceGeometryPoints = (geometry, points) => {
      const safePoints = Array.isArray(points) ? points : [];
      const values = new Float32Array(safePoints.length * 3);
      safePoints.forEach((point, index) => {
        const offset = index * 3;
        values[offset] = Number(point?.x) || 0;
        values[offset + 1] = Number(point?.y) || 0;
        values[offset + 2] = Number(point?.z) || 0;
      });
      geometry.setAttribute('position', new THREE.BufferAttribute(values, 3));
      geometry.setDrawRange(0, safePoints.length);
      if (safePoints.length) geometry.computeBoundingSphere();
      else geometry.boundingSphere = null;
    };

    const updateGpuSelectionOverlay = (selection) => {
      const handleSamples = selection?.editablePolygon
        ? (selection.editableVertices || selection.rawSurfaceSamples || selection.surfaceSamples || []).filter((sample) => sample?.worldPoint)
        : [];
      const lineSamples = selection?.editablePolygon
        ? (selection.wrappedSurfaceSamples || selection.surfaceSamples || handleSamples).filter((sample) => sample?.worldPoint)
        : [];
      const midpointSamples = selection?.editablePolygon
        ? (selection.editableMidpoints || []).filter((entry) => entry?.sample?.worldPoint)
        : [];
      if (!handleSamples.length) {
        gpuSelectionOverlay.visible = false;
        replaceGeometryPoints(gpuSelectionLineGeometry, []);
        replaceGeometryPoints(gpuSelectionHandleGeometry, []);
        replaceGeometryPoints(gpuSelectionMidpointGeometry, []);
        return false;
      }

      const handlePoints = handleSamples.map((sample) => sample.worldPoint);
      const midpointPoints = midpointSamples.map((entry) => entry.sample.worldPoint);
      const linePoints = lineSamples.map((sample) => sample.worldPoint);
      if (selection.closed && linePoints.length > 2 && !linePoints[0].equals(linePoints[linePoints.length - 1])) {
        linePoints.push(linePoints[0]);
      }

      // setFromPoints() attempts to reuse an existing position attribute and
      // cannot enlarge it after more polygon vertices are added. Replace the
      // attribute with an exactly sized GPU buffer on every topology change.
      replaceGeometryPoints(gpuSelectionLineGeometry, linePoints);
      gpuSelectionLineBase.computeLineDistances();
      gpuSelectionLineDash.computeLineDistances();
      replaceGeometryPoints(gpuSelectionHandleGeometry, handlePoints);
      replaceGeometryPoints(gpuSelectionMidpointGeometry, midpointPoints);
      gpuSelectionOverlay.visible = true;
      return true;
    };

    const drawSelectionOverlay = () => {
      syncFaceSelectionPresentation();
      if (!selectionContext || !selectionCanvas) return;
      selectionContext.setTransform(1, 0, 0, 1, 0, 0);
      selectionContext.clearRect(0, 0, selectionCanvas.width, selectionCanvas.height);
      const selection = selectionDraft || activeSelection;
      if (!selection) {
        updateGpuSelectionOverlay(null);
        return;
      }

      // The editable surface polygon is now a true WebGL overlay. Its vertices
      // remain in world space and therefore need no canvas reprojection while
      // orbiting, zooming or idling. Other selection modes keep their existing
      // 2D overlay because they are defined in viewport coordinates.
      if (updateGpuSelectionOverlay(selection)) return;

      selectionContext.save();
      selectionContext.scale(selectionCanvas.width / Math.max(1, selectionCanvas.clientWidth), selectionCanvas.height / Math.max(1, selectionCanvas.clientHeight));
      selectionContext.beginPath();
      if (!appendSelectionPath(selectionContext, selection)) {
        selectionContext.restore();
        return;
      }
      selectionContext.setLineDash([7, 5]);
      selectionContext.lineWidth = 1;
      selectionContext.strokeStyle = 'rgba(255,255,255,0.98)';
      selectionContext.stroke();
      selectionContext.lineDashOffset = 6;
      selectionContext.strokeStyle = 'rgba(0,0,0,0.92)';
      selectionContext.stroke();
      selectionContext.restore();
    };

    const applySelectionMaskToCanvas = (canvas) => {
      if (!canvas || !activeSelection) return true;
      if (activeSelection.shape === FACE_SELECTION_SHAPE) return true;
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      const maskContext = maskCanvas.getContext('2d');
      const targetContext = canvas.getContext('2d');
      if (!maskContext || !targetContext) return false;
      const scale = previewRenderScale;
      const offset = previewOverscan;
      maskContext.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      maskContext.fillStyle = '#fff';
      maskContext.beginPath();
      if (activeSelection.inverted) maskContext.rect(0, 0, maskCanvas.width, maskCanvas.height);
      if (!appendSelectionPath(maskContext, activeSelection, scale, offset, offset)) return false;
      maskContext.fill(activeSelection.inverted ? 'evenodd' : 'nonzero');
      targetContext.save();
      targetContext.globalCompositeOperation = 'destination-in';
      targetContext.drawImage(maskCanvas, 0, 0);
      targetContext.restore();
      return true;
    };

    const getGeometryTopology = (geometry) => {
      if (!geometry?.attributes?.position) return null;
      const cached = selectionTopologyCache.get(geometry);
      if (cached) return cached;
      const index = geometry.index;
      const position = geometry.attributes.position;
      const triangleCount = Math.floor((index ? index.count : position.count) / 3);
      const adjacency = Array.from({ length: triangleCount }, () => new Set());
      const edgeOwners = new Map();
      const vertexIndex = (triangle, corner) => index ? index.getX(triangle * 3 + corner) : triangle * 3 + corner;

      // Imported meshes commonly duplicate vertices along UV/material seams.
      // Raw index adjacency therefore treats a visually continuous surface as
      // hundreds of disconnected islands. Build edge keys from quantised local
      // positions instead, welding coincident seam vertices for selection only.
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      const diagonal = box
        ? Math.hypot(
          box.max.x - box.min.x,
          box.max.y - box.min.y,
          box.max.z - box.min.z,
        )
        : 1;
      const weldTolerance = Math.max(1e-7, diagonal * 1e-6);
      const inverseTolerance = 1 / weldTolerance;
      const vertexKeyCache = new Map();
      const vertexKey = (vertex) => {
        const cachedKey = vertexKeyCache.get(vertex);
        if (cachedKey) return cachedKey;
        const key = `${Math.round(position.getX(vertex) * inverseTolerance)},${Math.round(position.getY(vertex) * inverseTolerance)},${Math.round(position.getZ(vertex) * inverseTolerance)}`;
        vertexKeyCache.set(vertex, key);
        return key;
      };

      const centroids = Array.from({ length: triangleCount }, () => new THREE.Vector3());
      const transitionPoints = new Map();
      const transitionKey = (leftFace, rightFace) => leftFace < rightFace
        ? `${leftFace}:${rightFace}`
        : `${rightFace}:${leftFace}`;
      let weldedEdgeLinks = 0;
      for (let triangle = 0; triangle < triangleCount; triangle += 1) {
        const vertices = [vertexIndex(triangle, 0), vertexIndex(triangle, 1), vertexIndex(triangle, 2)];
        centroids[triangle].set(
          (position.getX(vertices[0]) + position.getX(vertices[1]) + position.getX(vertices[2])) / 3,
          (position.getY(vertices[0]) + position.getY(vertices[1]) + position.getY(vertices[2])) / 3,
          (position.getZ(vertices[0]) + position.getZ(vertices[1]) + position.getZ(vertices[2])) / 3,
        );
        const keys = vertices.map(vertexKey);
        for (let edge = 0; edge < 3; edge += 1) {
          const a = keys[edge];
          const b = keys[(edge + 1) % 3];
          const key = a < b ? `${a}|${b}` : `${b}|${a}`;
          const owners = edgeOwners.get(key);
          if (!owners) {
            edgeOwners.set(key, [triangle]);
            continue;
          }
          owners.forEach((owner) => {
            if (owner === triangle) return;
            if (!adjacency[triangle].has(owner)) weldedEdgeLinks += 1;
            adjacency[triangle].add(owner);
            adjacency[owner].add(triangle);
            const pairKey = transitionKey(owner, triangle);
            if (!transitionPoints.has(pairKey)) {
              transitionPoints.set(pairKey, new THREE.Vector3(
                (position.getX(vertices[edge]) + position.getX(vertices[(edge + 1) % 3])) * 0.5,
                (position.getY(vertices[edge]) + position.getY(vertices[(edge + 1) % 3])) * 0.5,
                (position.getZ(vertices[edge]) + position.getZ(vertices[(edge + 1) % 3])) * 0.5,
              ));
            }
          });
          owners.push(triangle);
        }
      }
      const result = {
        triangleCount,
        adjacency,
        centroids,
        transitionPoints,
        transitionKey,
        weldTolerance,
        weldedEdgeLinks,
      };
      selectionTopologyCache.set(geometry, result);
      return result;
    };

    const buildCentreHitSurfaceLock = (hit) => {
      if (!hit?.object?.isMesh || !Number.isInteger(hit.faceIndex)) {
                return null;
      }

      // Keep pointer-down cheap. Building the full welded triangle topology on a
      // dense mesh can take hundreds of milliseconds and causes the browser to
      // miss the entire drag. The exact centre hit is enough to begin recording;
      // the connected-face mask is completed after pointer-up.
      const lock = {
        allowedMeshUuid: hit.object.uuid,
        startingFaceIndex: hit.faceIndex,
        startingMesh: hit.object,
        allowedFaceIndicesByMesh: new Map([[hit.object.uuid, new Set([hit.faceIndex])]]),
        surfaceFacesReady: false,
        startingRayDistance: Number(hit.distance),
        lastAcceptedRayDistance: Number(hit.distance),
        startingWorldPoint: hit.point?.clone?.() || null,
      };
            return lock;
    };

    const completeDeferredSurfaceLock = async (stroke) => {
      if (!stroke?.allowedMeshUuid || stroke.surfaceFacesReady) return;

      // The first centre pixel chooses a camera-depth layer. Do not attempt to
      // infer a semantic part by flooding connected triangles: sculpted models
      // commonly weld teeth, gums and the inside of the mouth into one mesh.
      // Restriction is applied per fragment in the projection shader instead.
      stroke.allowedFaceIndicesByMesh = null;
      stroke.surfaceFacesReady = true;
          };

    const shortestTrianglePath = (topology, startFace, endFace) => {
      if (!topology || startFace === endFace) return [startFace];
      const { adjacency, centroids } = topology;
      if (!adjacency?.[startFace] || !adjacency?.[endFace]) return [startFace, endFace];

      // Binary-heap A*: large editable-polygon jumps can cross tens of
      // thousands of triangles. Scanning a Set for the lowest score on every
      // step made one click feel frozen on dense models.
      const heap = [];
      const push = (face, score) => {
        let index = heap.length;
        heap.push({ face, score });
        while (index > 0) {
          const parent = Math.floor((index - 1) / 2);
          if (heap[parent].score <= score) break;
          heap[index] = heap[parent];
          index = parent;
        }
        heap[index] = { face, score };
      };
      const pop = () => {
        if (!heap.length) return null;
        const root = heap[0];
        const tail = heap.pop();
        if (heap.length && tail) {
          let index = 0;
          while (true) {
            const left = (index * 2) + 1;
            const right = left + 1;
            if (left >= heap.length) break;
            let child = left;
            if (right < heap.length && heap[right].score < heap[left].score) child = right;
            if (heap[child].score >= tail.score) break;
            heap[index] = heap[child];
            index = child;
          }
          heap[index] = tail;
        }
        return root;
      };

      const cameFrom = new Map();
      const gScore = new Map([[startFace, 0]]);
      const closed = new Set();
      const heuristic = (face) => centroids?.[face] && centroids?.[endFace]
        ? centroids[face].distanceTo(centroids[endFace])
        : 0;
      push(startFace, heuristic(startFace));

      while (heap.length) {
        const entry = pop();
        if (!entry) break;
        const current = entry.face;
        if (closed.has(current)) continue;
        if (current === endFace) {
          const path = [current];
          let cursor = current;
          while (cameFrom.has(cursor)) {
            cursor = cameFrom.get(cursor);
            path.push(cursor);
          }
          return path.reverse();
        }
        closed.add(current);
        const currentScore = gScore.get(current) ?? Infinity;
        for (const neighbour of adjacency[current] || []) {
          if (closed.has(neighbour)) continue;
          const edgeCost = centroids?.[current] && centroids?.[neighbour]
            ? centroids[current].distanceTo(centroids[neighbour])
            : 1;
          const tentative = currentScore + Math.max(edgeCost, 1e-9);
          if (tentative >= (gScore.get(neighbour) ?? Infinity)) continue;
          cameFrom.set(neighbour, current);
          gScore.set(neighbour, tentative);
          push(neighbour, tentative + heuristic(neighbour));
        }
      }
      return [startFace, endFace];
    };

    const getSurfaceTransitionWorldPoint = (object, topology, leftFace, rightFace) => {
      if (!object?.matrixWorld || !topology?.transitionPoints || !topology?.transitionKey) return null;
      const localPoint = topology.transitionPoints.get(topology.transitionKey(leftFace, rightFace));
      if (!localPoint) return null;
      return localPoint.clone().applyMatrix4(object.matrixWorld);
    };

    const getEditableSurfacePath = (samples, closed = true) => {
      const vertices = (Array.isArray(samples) ? samples : []).filter(
        (sample) => sample?.object?.isMesh && Number.isInteger(sample.faceIndex) && sample.worldPoint,
      );
      if (vertices.length < 2) return vertices;
      const result = [];
      const segmentCount = closed ? vertices.length : vertices.length - 1;
      for (let index = 0; index < segmentCount; index += 1) {
        const current = vertices[index];
        const next = vertices[(index + 1) % vertices.length];
        if (!result.length) result.push(current);
        if (current.object === next.object) {
          const topology = getGeometryTopology(current.object.geometry);
          current.object.updateMatrixWorld(true);
          const path = shortestTrianglePath(topology, current.faceIndex, next.faceIndex);
          for (let pathIndex = 0; pathIndex < path.length - 1; pathIndex += 1) {
            const worldPoint = getSurfaceTransitionWorldPoint(
              current.object,
              topology,
              path[pathIndex],
              path[pathIndex + 1],
            );
            if (!worldPoint) continue;
            result.push({
              object: current.object,
              faceIndex: path[pathIndex + 1],
              worldPoint,
              generatedSurfaceBridge: true,
            });
          }
        }
        result.push(next);
      }
      return result;
    };

    const buildEditableMidpoints = (samples, closed = true) => {
      const vertices = (Array.isArray(samples) ? samples : []).filter(
        (sample) => sample?.object?.isMesh && Number.isInteger(sample.faceIndex) && sample.worldPoint,
      );
      if (vertices.length < 2) return [];
      const edgeCount = closed ? vertices.length : vertices.length - 1;
      const midpoints = [];
      for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex += 1) {
        const nextIndex = (edgeIndex + 1) % vertices.length;
        const route = getEditableSurfacePath([vertices[edgeIndex], vertices[nextIndex]], false);
        const usableRoute = route.length ? route : [vertices[edgeIndex], vertices[nextIndex]];
        const midpointSample = usableRoute[Math.floor((usableRoute.length - 1) * 0.5)] || vertices[edgeIndex];
        midpoints.push({
          edgeIndex,
          nextIndex,
          sample: {
            ...midpointSample,
            worldPoint: midpointSample.worldPoint.clone?.() || midpointSample.worldPoint,
            generatedEditableMidpoint: true,
          },
        });
      }
      return midpoints;
    };

    const buildSurfaceSelection = (samples, options = {}) => {
      const wrapEverySegment = Boolean(options.wrapEverySegment);
      const validSamples = (samples || []).filter((sample) => sample?.object?.isMesh && Number.isInteger(sample.faceIndex));
      if (validSamples.length < 3) return null;
      const samplesByObject = new Map();
      validSamples.forEach((sample) => {
        if (!samplesByObject.has(sample.object)) samplesByObject.set(sample.object, []);
        samplesByObject.get(sample.object).push(sample);
      });
      const selectedFaces = new Map();
      const boundaryFaces = new Map();
      const clingingSurfaceSamples = [];
      const fullyExpandedSelectionDebugMeshes = [];
      const selectionDebugPointInPolygon = (point, polygon) => {
        if (!point || !Array.isArray(polygon) || polygon.length < 3) return false;
        let inside = false;
        for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
          const current = polygon[index];
          const previous = polygon[previousIndex];
          const crosses = ((current.y > point.y) !== (previous.y > point.y))
            && (point.x < ((previous.x - current.x) * (point.y - current.y)) / ((previous.y - current.y) || Number.EPSILON) + current.x);
          if (crosses) inside = !inside;
        }
        return inside;
      };
      samplesByObject.forEach((objectSamples, object) => {
        const topology = getGeometryTopology(object.geometry);
        if (!topology || objectSamples.length < 2) return;
        const collapsedSamples = [];
        objectSamples.forEach((sample) => {
          const previous = collapsedSamples[collapsedSamples.length - 1];
          if (!previous || previous.faceIndex !== sample.faceIndex) collapsedSamples.push(sample);
        });
        if (collapsedSamples.length > 1 && collapsedSamples[0].faceIndex === collapsedSamples[collapsedSamples.length - 1].faceIndex) {
          collapsedSamples.pop();
        }

        object.updateMatrixWorld(true);
        const sampledFaces = new Set(collapsedSamples.map((sample) => sample.faceIndex));
        const boundary = new Set(sampledFaces);
        const objectClingingSamples = [];
        for (let index = 0; index < collapsedSamples.length; index += 1) {
          const current = collapsedSamples[index];
          const next = collapsedSamples[(index + 1) % collapsedSamples.length];
          const isClosingSegment = index === collapsedSamples.length - 1;
          const isResumedSegment = Boolean(next.bridgeFromPrevious);
          const path = shortestTrianglePath(topology, current.faceIndex, next.faceIndex);

          // The topology path is used internally to seal the selected region,
          // but it must never replace or move the points the user actually drew.
          path.forEach((face) => boundary.add(face));
          if (!objectClingingSamples.length) objectClingingSamples.push(current);

          // Only a real gap (resume after Ctrl/view movement) or the final A-to-B
          // closure gets generated surface points. Each generated point sits on
          // the shared edge between adjacent triangles, so the bridge clings to
          // the mesh without snapping the user's visible lasso to triangle
          // centres or vertices.
          if (wrapEverySegment || isClosingSegment || isResumedSegment) {
            for (let pathIndex = 0; pathIndex < path.length - 1; pathIndex += 1) {
              const leftFace = path[pathIndex];
              const rightFace = path[pathIndex + 1];
              const worldPoint = getSurfaceTransitionWorldPoint(object, topology, leftFace, rightFace);
              if (!worldPoint) continue;
              objectClingingSamples.push({
                object,
                faceIndex: rightFace,
                worldPoint,
                generatedSurfaceBridge: true,
              });
            }
          }

          // Every normal segment remains exactly the sampled line. For a bridge,
          // append the user's destination point after the generated surface path.
          // The first point is not repeated for the closing segment because the
          // canvas path closes back to it naturally.
          if (!isClosingSegment) objectClingingSamples.push(next);
        }
        clingingSurfaceSamples.push(...objectClingingSamples);

        const getComponents = (blockedFaces) => {
          const visited = new Set(blockedFaces);
          const result = [];
          for (let face = 0; face < topology.triangleCount; face += 1) {
            if (visited.has(face)) continue;
            const component = [];
            const queue = [face];
            visited.add(face);
            for (let cursor = 0; cursor < queue.length; cursor += 1) {
              const current = queue[cursor];
              component.push(current);
              for (const neighbour of topology.adjacency[current] || []) {
                if (visited.has(neighbour)) continue;
                visited.add(neighbour);
                queue.push(neighbour);
              }
            }
            result.push(component);
          }
          return result;
        };

        let effectiveBoundary = boundary;
        let components = getComponents(effectiveBoundary);
        if (!components.length) {
          // Never allow an over-connected boundary reconstruction to disable
          // painting completely. The faces actually touched by the lasso are
          // the reliable minimum boundary and still split ordinary selections.
          effectiveBoundary = sampledFaces;
          components = getComponents(effectiveBoundary);
        }
        // A production mesh can contain tiny disconnected slivers, cracks or
        // one-face islands. Picking the globally smallest component therefore
        // frequently selects a single stray triangle instead of the area
        // enclosed by the lasso. Only components that materially touch the
        // reconstructed boundary are candidates for either side of the lasso.
        const componentCandidates = components.map((component) => {
          const componentSet = new Set(component);
          const touchingBoundaryFaces = new Set();
          let boundaryConnections = 0;
          component.forEach((face) => {
            for (const neighbour of topology.adjacency[face] || []) {
              if (!effectiveBoundary.has(neighbour)) continue;
              touchingBoundaryFaces.add(neighbour);
              boundaryConnections += 1;
            }
          });
          return {
            component,
            componentSet,
            boundaryTouchCount: touchingBoundaryFaces.size,
            boundaryConnections,
          };
        });

        const maximumBoundaryTouch = componentCandidates.reduce(
          (maximum, candidate) => Math.max(maximum, candidate.boundaryTouchCount),
          0,
        );
        const meaningfulTouchThreshold = Math.max(2, Math.floor(maximumBoundaryTouch * 0.35));
        let realSides = componentCandidates.filter(
          (candidate) => candidate.boundaryTouchCount >= meaningfulTouchThreshold,
        );

        // Ordinarily there are two meaningful sides of the closed boundary.
        // Keep the two with the strongest contact and choose the smaller one as
        // the enclosed side. This deliberately ignores tiny topology fragments
        // that happen to be disconnected elsewhere in the model.
        realSides.sort((a, b) => {
          if (b.boundaryTouchCount !== a.boundaryTouchCount) {
            return b.boundaryTouchCount - a.boundaryTouchCount;
          }
          if (b.boundaryConnections !== a.boundaryConnections) {
            return b.boundaryConnections - a.boundaryConnections;
          }
          return b.component.length - a.component.length;
        });
        if (realSides.length > 2) realSides = realSides.slice(0, 2);

        let selectedCandidate = null;
        if (realSides.length >= 2) {
          selectedCandidate = realSides[0].component.length <= realSides[1].component.length
            ? realSides[0]
            : realSides[1];
        } else if (realSides.length === 1) {
          selectedCandidate = realSides[0];
        } else if (componentCandidates.length) {
          // Defensive fallback: use strongest boundary contact, never the
          // globally smallest disconnected component.
          componentCandidates.sort((a, b) => {
            if (b.boundaryTouchCount !== a.boundaryTouchCount) {
              return b.boundaryTouchCount - a.boundaryTouchCount;
            }
            if (b.boundaryConnections !== a.boundaryConnections) {
              return b.boundaryConnections - a.boundaryConnections;
            }
            return b.component.length - a.component.length;
          });
          selectedCandidate = componentCandidates[0];
        }

        const selected = new Set(selectedCandidate?.component || []);
        selectedFaces.set(object.uuid, selected);
        boundaryFaces.set(object.uuid, effectiveBoundary);

        const rendererRect = renderer.domElement.getBoundingClientRect();
        const rawPolygonViewport = collapsedSamples.map((sample) => {
          const projected = sample.worldPoint?.clone?.().project(camera);
          return projected ? {
            x: (projected.x * 0.5 + 0.5) * rendererRect.width,
            y: (-projected.y * 0.5 + 0.5) * rendererRect.height,
          } : null;
        }).filter(Boolean);
        const expandedCandidates = componentCandidates.map((candidate, candidateIndex) => {
          let projectedInsideCount = 0;
          let projectedFiniteCount = 0;
          let projectedBehindCount = 0;
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          const worldCentre = new THREE.Vector3();
          let worldCentreCount = 0;
          candidate.component.forEach((faceIndex) => {
            const localCentroid = topology.centroids?.[faceIndex];
            if (!localCentroid) return;
            const worldCentroid = localCentroid.clone().applyMatrix4(object.matrixWorld);
            worldCentre.add(worldCentroid);
            worldCentreCount += 1;
            const projected = worldCentroid.clone().project(camera);
            if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || !Number.isFinite(projected.z)) return;
            if (projected.z < -1 || projected.z > 1) {
              projectedBehindCount += 1;
              return;
            }
            const viewportPoint = {
              x: (projected.x * 0.5 + 0.5) * rendererRect.width,
              y: (-projected.y * 0.5 + 0.5) * rendererRect.height,
            };
            projectedFiniteCount += 1;
            minX = Math.min(minX, viewportPoint.x);
            minY = Math.min(minY, viewportPoint.y);
            maxX = Math.max(maxX, viewportPoint.x);
            maxY = Math.max(maxY, viewportPoint.y);
            if (selectionDebugPointInPolygon(viewportPoint, rawPolygonViewport)) projectedInsideCount += 1;
          });
          if (worldCentreCount) worldCentre.multiplyScalar(1 / worldCentreCount);
          return {
            candidateIndex,
            selectedByCurrentLogic: candidate === selectedCandidate,
            faceCount: candidate.component.length,
            boundaryTouchCount: candidate.boundaryTouchCount,
            boundaryConnections: candidate.boundaryConnections,
            projectedFiniteCount,
            projectedBehindCount,
            projectedInsideCount,
            projectedInsideRatio: projectedFiniteCount ? projectedInsideCount / projectedFiniteCount : 0,
            projectedBounds: projectedFiniteCount ? { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY } : null,
            worldCentre: worldCentreCount ? { x: worldCentre.x, y: worldCentre.y, z: worldCentre.z } : null,
            allFaceIndices: candidate.component,
          };
        });
        fullyExpandedSelectionDebugMeshes.push({
          name: object.name || '',
          uuid: object.uuid,
          triangleCount: topology.triangleCount,
          collapsedInputFaceIndices: collapsedSamples.map((sample) => sample.faceIndex),
          sampledFaceIndices: Array.from(sampledFaces),
          effectiveBoundaryFaceIndices: Array.from(effectiveBoundary),
          rawPolygonViewport,
          maximumBoundaryTouch,
          meaningfulTouchThreshold,
          selectedCandidateIndex: expandedCandidates.findIndex((candidate) => candidate.selectedByCurrentLogic),
          candidates: expandedCandidates,
        });

        object.userData.voxemeraSelectionRegionDebug = {
          componentCount: components.length,
          maximumBoundaryTouch,
          meaningfulTouchThreshold,
          candidates: componentCandidates
            .map((candidate) => ({
              faces: candidate.component.length,
              boundaryTouch: candidate.boundaryTouchCount,
              boundaryConnections: candidate.boundaryConnections,
              selected: candidate === selectedCandidate,
            }))
            .sort((a, b) => b.boundaryTouch - a.boundaryTouch)
            .slice(0, 8),
        };
      });
      const selectionClipSamples = clingingSurfaceSamples.length ? clingingSurfaceSamples : validSamples;
      const selectionClipPolygonsByMesh = new Map();
      selectionClipSamples.forEach((sample) => {
        if (!sample?.object?.uuid || !sample?.worldPoint) return;
        const projected = sample.worldPoint.clone().project(camera);
        if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) return;
        const meshPolygon = selectionClipPolygonsByMesh.get(sample.object.uuid) || [];
        const previous = meshPolygon[meshPolygon.length - 1];
        if (!previous || Math.hypot(previous.x - projected.x, previous.y - projected.y) > 0.000001) {
          meshPolygon.push({ x: projected.x, y: projected.y });
        }
        selectionClipPolygonsByMesh.set(sample.object.uuid, meshPolygon);
      });
      const selectionClipViewProjection = new THREE.Matrix4().multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse,
      );
      const builtSelection = selectedFaces.size ? {
        shape: 'surface',
        surfaceSamples: selectionClipSamples,
        wrappedSurfaceSamples: selectionClipSamples,
        rawSurfaceSamples: validSamples,
        editableVertices: wrapEverySegment ? validSamples : undefined,
        selectedFaces,
        boundaryFaces,
        clipPolygonsByMesh: selectionClipPolygonsByMesh,
        clipViewProjectionMatrix: selectionClipViewProjection,
        closed: true,
        inverted: false,
      } : null;
      return builtSelection;
    };

    const applyExactSurfaceSelectionClip = (material, sourceMesh) => {
      const polygon = activeSelection?.clipPolygonsByMesh?.get?.(sourceMesh?.uuid) || [];
      const viewProjection = activeSelection?.clipViewProjectionMatrix;
      if (!material || polygon.length < 3 || !viewProjection?.isMatrix4) return false;

      const maximumPolygonPoints = 256;
      const clippedPolygon = polygon.length <= maximumPolygonPoints
        ? polygon
        : Array.from({ length: maximumPolygonPoints }, (_, index) => (
            polygon[Math.floor(index * polygon.length / maximumPolygonPoints)]
          ));
      const polygonUniform = Array.from({ length: maximumPolygonPoints }, (_, index) => {
        const point = clippedPolygon[index] || clippedPolygon[clippedPolygon.length - 1] || { x: 0, y: 0 };
        return new THREE.Vector2(point.x, point.y);
      });
      const savedViewProjection = viewProjection.clone();
      const previousOnBeforeCompile = material.onBeforeCompile;
      const previousCacheKey = material.customProgramCacheKey;

      material.onBeforeCompile = (shader, rendererInstance) => {
        if (typeof previousOnBeforeCompile === 'function') {
          previousOnBeforeCompile.call(material, shader, rendererInstance);
        }
        shader.uniforms.voxemeraSelectionClipViewProjection = { value: savedViewProjection };
        shader.uniforms.voxemeraSelectionClipPointCount = { value: clippedPolygon.length };
        shader.uniforms.voxemeraSelectionClipPolygon = { value: polygonUniform };
        shader.vertexShader = shader.vertexShader.replace(
          '#include <common>',
          `#include <common>
varying vec3 voxemeraSelectionClipWorldPosition;`,
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <worldpos_vertex>',
          `#include <worldpos_vertex>
voxemeraSelectionClipWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <common>',
          `#include <common>
varying vec3 voxemeraSelectionClipWorldPosition;
uniform mat4 voxemeraSelectionClipViewProjection;
uniform int voxemeraSelectionClipPointCount;
uniform vec2 voxemeraSelectionClipPolygon[${maximumPolygonPoints}];

bool voxemeraPointInsideSelectionPolygon(vec2 point) {
  bool inside = false;
  vec2 previousPoint = voxemeraSelectionClipPolygon[voxemeraSelectionClipPointCount - 1];
  for (int polygonIndex = 0; polygonIndex < ${maximumPolygonPoints}; polygonIndex += 1) {
    if (polygonIndex >= voxemeraSelectionClipPointCount) break;
    vec2 currentPoint = voxemeraSelectionClipPolygon[polygonIndex];
    bool crosses = ((currentPoint.y > point.y) != (previousPoint.y > point.y));
    float denominator = previousPoint.y - currentPoint.y;
    float crossingX = (previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)
      / (abs(denominator) < 0.0000001 ? (denominator < 0.0 ? -0.0000001 : 0.0000001) : denominator)
      + currentPoint.x;
    if (crosses && point.x < crossingX) inside = !inside;
    previousPoint = currentPoint;
  }
  return inside;
}`,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <clipping_planes_fragment>',
          `#include <clipping_planes_fragment>
vec4 voxemeraSelectionClipPosition = voxemeraSelectionClipViewProjection * vec4(voxemeraSelectionClipWorldPosition, 1.0);
if (voxemeraSelectionClipPosition.w <= 0.0) discard;
vec2 voxemeraSelectionClipNdc = voxemeraSelectionClipPosition.xy / voxemeraSelectionClipPosition.w;
if (!voxemeraPointInsideSelectionPolygon(voxemeraSelectionClipNdc)) discard;`,
        );
      };
      material.customProgramCacheKey = () => `${typeof previousCacheKey === 'function' ? previousCacheKey.call(material) : ''}|voxemeraExactSurfaceSelectionClip_v1_${clippedPolygon.length}`;
      material.needsUpdate = true;
      material.userData = {
        ...(material.userData || {}),
        voxemeraExactSurfaceSelectionClip: true,
        voxemeraExactSurfaceSelectionClipPointCount: clippedPolygon.length,
        voxemeraExactSurfaceSelectionClipData: {
          viewProjection: savedViewProjection.clone(),
          polygon: clippedPolygon.map((point) => ({ x: point.x, y: point.y })),
        },
      };
      return true;
    };


    const ensureStableEdgeWearAttributes = (geometry) => {
      if (!geometry?.getAttribute?.('position')) return geometry;
      if (geometry.userData?.voxemeraEdgeWearMeta) return geometry;

      const position = geometry.getAttribute('position');
      const index = geometry.index;
      geometry.computeBoundingBox?.();
      const bounds = geometry.boundingBox;
      const scale = bounds && !bounds.isEmpty()
        ? Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z, 0.000001)
        : 1;
      const quantise = 100000 / scale;
      const vertex = new THREE.Vector3();
      const a = new THREE.Vector3();
      const b = new THREE.Vector3();
      const c = new THREE.Vector3();
      const ab = new THREE.Vector3();
      const ac = new THREE.Vector3();
      const faceNormal = new THREE.Vector3();
      const vertexKeys = new Array(position.count);
      const triangleNormals = [];
      const triangleCentroids = [];
      const edgeOwners = new Map();
      const triangleEdgeSharpness = [];
      const vertexJoinSharpness = new Map();
      const detectedEdgeKeysByVertex = new Map();
      const detectedEdgeSegments = [];
      const weldedVertexPositions = new Map();
      const weldedVertexGraph = new Map();

      const keyForVertex = (vertexIndex) => {
        if (vertexKeys[vertexIndex]) return vertexKeys[vertexIndex];
        vertex.fromBufferAttribute(position, vertexIndex);
        const key = `${Math.round(vertex.x * quantise)},${Math.round(vertex.y * quantise)},${Math.round(vertex.z * quantise)}`;
        vertexKeys[vertexIndex] = key;
        if (!weldedVertexPositions.has(key)) weldedVertexPositions.set(key, vertex.clone());
        return key;
      };

      const connectWeldedVertices = (leftKey, leftPosition, rightKey, rightPosition) => {
        if (!leftKey || !rightKey || leftKey === rightKey) return;
        if (!weldedVertexPositions.has(leftKey)) weldedVertexPositions.set(leftKey, leftPosition.clone());
        if (!weldedVertexPositions.has(rightKey)) weldedVertexPositions.set(rightKey, rightPosition.clone());
        const edgeDistance = leftPosition.distanceTo(rightPosition);
        if (!(edgeDistance > 1.0e-12)) return;
        const addNeighbour = (fromKey, toKey) => {
          let neighbours = weldedVertexGraph.get(fromKey);
          if (!neighbours) {
            neighbours = [];
            weldedVertexGraph.set(fromKey, neighbours);
          }
          const existing = neighbours.find((entry) => entry.key === toKey);
          if (existing) existing.distance = Math.min(existing.distance, edgeDistance);
          else neighbours.push({ key: toKey, distance: edgeDistance });
        };
        addNeighbour(leftKey, rightKey);
        addNeighbour(rightKey, leftKey);
      };

      const triangleCount = Math.floor((index ? index.count : position.count) / 3);
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const base = triangleIndex * 3;
        const ia = index ? index.getX(base) : base;
        const ib = index ? index.getX(base + 1) : base + 1;
        const ic = index ? index.getX(base + 2) : base + 2;
        a.fromBufferAttribute(position, ia);
        b.fromBufferAttribute(position, ib);
        c.fromBufferAttribute(position, ic);
        ab.subVectors(b, a);
        ac.subVectors(c, a);
        faceNormal.crossVectors(ab, ac);
        const normalLength = faceNormal.length();
        const normal = normalLength > 1.0e-12
          ? faceNormal.clone().multiplyScalar(1 / normalLength)
          : new THREE.Vector3(0, 1, 0);
        triangleNormals[triangleIndex] = normal;
        triangleCentroids[triangleIndex] = new THREE.Vector3().addVectors(a, b).add(c).multiplyScalar(1 / 3);
        triangleEdgeSharpness[triangleIndex] = [0, 0, 0];

        const vertices = [ia, ib, ic];
        const keys = vertices.map(keyForVertex);
        const triangleVertexPositions = [a.clone(), b.clone(), c.clone()];
        connectWeldedVertices(keys[0], triangleVertexPositions[0], keys[1], triangleVertexPositions[1]);
        connectWeldedVertices(keys[1], triangleVertexPositions[1], keys[2], triangleVertexPositions[2]);
        connectWeldedVertices(keys[2], triangleVertexPositions[2], keys[0], triangleVertexPositions[0]);
        [
          { vertices: [1, 2], component: 0 },
          { vertices: [2, 0], component: 1 },
          { vertices: [0, 1], component: 2 },
        ].forEach(({ vertices: localEdge, component }) => {
          const leftKey = keys[localEdge[0]];
          const rightKey = keys[localEdge[1]];
          const edgeKey = leftKey < rightKey ? `${leftKey}|${rightKey}` : `${rightKey}|${leftKey}`;
          const owners = edgeOwners.get(edgeKey) || [];
          owners.push({
            triangleIndex,
            component,
            startVertexIndex: vertices[localEdge[0]],
            endVertexIndex: vertices[localEdge[1]],
          });
          edgeOwners.set(edgeKey, owners);
        });
      }

      // Cycles' Bevel node samples nearby surfaces with a compact cubic
      // radial kernel, accumulates their normals and normalises the result.
      // WebGL cannot ray-trace from every fragment, so perform the equivalent
      // neighbourhood gather once on model geometry and cache the virtual
      // bevel response used by every paint-layer shader.
      const bevelRadius = Math.max(scale * 0.018, 1.0e-6);
      const inverseCellSize = 1 / bevelRadius;
      const triangleGrid = new Map();
      const gridKey = (x, y, z) => `${x}|${y}|${z}`;
      triangleCentroids.forEach((centroid, triangleIndex) => {
        if (!centroid) return;
        const key = gridKey(
          Math.floor(centroid.x * inverseCellSize),
          Math.floor(centroid.y * inverseCellSize),
          Math.floor(centroid.z * inverseCellSize),
        );
        const bucket = triangleGrid.get(key) || [];
        bucket.push(triangleIndex);
        triangleGrid.set(key, bucket);
      });

      const sampleNearbyBevelNormal = (samplePoint, fallbackNormal) => {
        const cellX = Math.floor(samplePoint.x * inverseCellSize);
        const cellY = Math.floor(samplePoint.y * inverseCellSize);
        const cellZ = Math.floor(samplePoint.z * inverseCellSize);
        const summedNormal = new THREE.Vector3();
        let totalWeight = 0;
        for (let dz = -1; dz <= 1; dz += 1) {
          for (let dy = -1; dy <= 1; dy += 1) {
            for (let dx = -1; dx <= 1; dx += 1) {
              const bucket = triangleGrid.get(gridKey(cellX + dx, cellY + dy, cellZ + dz));
              if (!bucket) continue;
              bucket.forEach((triangleIndex) => {
                const centroid = triangleCentroids[triangleIndex];
                const normal = triangleNormals[triangleIndex];
                if (!centroid || !normal) return;
                const distance = centroid.distanceTo(samplePoint);
                if (distance >= bevelRadius) return;
                // Blender's svm_bevel_cubic_pdf is proportional to
                // (radius - distance)^3. The normalisation factor cancels when
                // the accumulated normal is normalised below.
                const remaining = 1 - (distance / bevelRadius);
                const weight = remaining * remaining * remaining;
                summedNormal.addScaledVector(normal, weight);
                totalWeight += weight;
              });
            }
          }
        }
        if (totalWeight <= 1.0e-8 || summedNormal.lengthSq() <= 1.0e-12) {
          return fallbackNormal.clone();
        }
        return summedNormal.normalize();
      };

      edgeOwners.forEach((owners, edgeKey) => {
        if (!owners.length) return;
        let edgeSharpness = 0;

        if (owners.length === 1) {
          // Cycles' bevel result at an open boundary is unstable without a
          // neighbouring surface. Keep borders subtle rather than treating
          // every cut edge as heavily worn.
          edgeSharpness = 0.08;
        } else {
          const firstOwner = owners[0];
          const startPoint = new THREE.Vector3().fromBufferAttribute(position, firstOwner.startVertexIndex);
          const endPoint = new THREE.Vector3().fromBufferAttribute(position, firstOwner.endVertexIndex);
          const midpoint = new THREE.Vector3().addVectors(startPoint, endPoint).multiplyScalar(0.5);
          const edgeDirection = new THREE.Vector3().subVectors(endPoint, startPoint).normalize();
          const ownerNormal = owners.reduce((sum, owner) => {
            const normal = triangleNormals[owner.triangleIndex];
            return normal ? sum.add(normal) : sum;
          }, new THREE.Vector3()).normalize();
          const tangentAcrossEdge = new THREE.Vector3().crossVectors(ownerNormal, edgeDirection).normalize();
          const sampleOffsets = [
            [0, 0],
            [0.34, 0],
            [-0.34, 0],
            [0, 0.34],
            [0, -0.34],
          ];
          const accumulatedBevelNormal = new THREE.Vector3();
          sampleOffsets.forEach(([alongEdge, acrossEdge]) => {
            const samplePoint = midpoint.clone()
              .addScaledVector(edgeDirection, alongEdge * bevelRadius)
              .addScaledVector(tangentAcrossEdge, acrossEdge * bevelRadius);
            accumulatedBevelNormal.add(sampleNearbyBevelNormal(samplePoint, ownerNormal));
          });
          const bevelNormal = accumulatedBevelNormal.normalize();

          let maximumConvexResponse = 0;
          for (let left = 0; left < owners.length; left += 1) {
            const leftOwner = owners[left];
            const leftNormal = triangleNormals[leftOwner.triangleIndex];
            const leftCentroid = triangleCentroids[leftOwner.triangleIndex];
            if (!leftNormal || !leftCentroid) continue;
            for (let right = left + 1; right < owners.length; right += 1) {
              const rightOwner = owners[right];
              const rightNormal = triangleNormals[rightOwner.triangleIndex];
              const rightCentroid = triangleCentroids[rightOwner.triangleIndex];
              if (!rightNormal || !rightCentroid) continue;
              const faceDot = THREE.MathUtils.clamp(leftNormal.dot(rightNormal), -1, 1);
              const faceAngle = Math.acos(faceDot);
              if (faceAngle < THREE.MathUtils.degToRad(6) || faceAngle > THREE.MathUtils.degToRad(150)) continue;

              // Keep the exposed/convex half. Recessed joins are left intact,
              // matching the edge-wear material setup built on top of Blender's
              // Bevel normal output.
              const leftPlaneSide = leftNormal.dot(new THREE.Vector3().subVectors(rightCentroid, midpoint));
              const rightPlaneSide = rightNormal.dot(new THREE.Vector3().subVectors(leftCentroid, midpoint));
              const edgeLength = Math.max(startPoint.distanceTo(endPoint), 1.0e-6);
              const convexGate = THREE.MathUtils.clamp(
                Math.max(0, -(leftPlaneSide + rightPlaneSide)) / edgeLength,
                0,
                1,
              );
              if (convexGate <= 0) continue;

              // A Blender wear mask compares the true surface normal with the
              // ray-sampled virtual bevel normal. Flat regions remain nearly 1;
              // rounded edge regions diverge according to bevel radius.
              const leftDifference = 1 - THREE.MathUtils.clamp(leftNormal.dot(bevelNormal), -1, 1);
              const rightDifference = 1 - THREE.MathUtils.clamp(rightNormal.dot(bevelNormal), -1, 1);
              const normalDifference = Math.max(leftDifference, rightDifference);
              const bevelResponse = THREE.MathUtils.clamp(normalDifference * 3.4, 0, 1);
              maximumConvexResponse = Math.max(
                maximumConvexResponse,
                Math.pow(bevelResponse, 0.72) * Math.pow(convexGate, 0.45),
              );
            }
          }
          edgeSharpness = maximumConvexResponse;
        }

        if (edgeSharpness <= 0) return;
        if (edgeSharpness >= 0.065) {
          const firstOwner = owners[0];
          detectedEdgeSegments.push({
            start: new THREE.Vector3().fromBufferAttribute(position, firstOwner.startVertexIndex),
            end: new THREE.Vector3().fromBufferAttribute(position, firstOwner.endVertexIndex),
          });
        }
        owners.forEach((owner) => {
          const triangleRecord = triangleEdgeSharpness[owner.triangleIndex];
          if (!triangleRecord) return;
          triangleRecord[owner.component] = Math.max(triangleRecord[owner.component], edgeSharpness);

          const startKey = keyForVertex(owner.startVertexIndex);
          const endKey = keyForVertex(owner.endVertexIndex);
          vertexJoinSharpness.set(
            startKey,
            Math.max(vertexJoinSharpness.get(startKey) || 0, edgeSharpness),
          );
          vertexJoinSharpness.set(
            endKey,
            Math.max(vertexJoinSharpness.get(endKey) || 0, edgeSharpness),
          );

          if (edgeSharpness >= 0.065) {
            const startEdges = detectedEdgeKeysByVertex.get(startKey) || new Set();
            startEdges.add(edgeKey);
            detectedEdgeKeysByVertex.set(startKey, startEdges);
            const endEdges = detectedEdgeKeysByVertex.get(endKey) || new Set();
            endEdges.add(edgeKey);
            detectedEdgeKeysByVertex.set(endKey, endEdges);
          }
        });
      });

      // Add triangle-interior nodes to the surface graph. A vertex-only
      // graph makes distance travel along triangle edges, which creates a
      // directional/diamond-shaped erosion front. Centroid nodes and direct
      // links across shared mesh edges let the distance wave cross triangle
      // interiors at nearly the same physical speed in every direction.
      const triangleGraphKeys = new Array(triangleCount);
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const triangleKey = `triangle:${triangleIndex}`;
        triangleGraphKeys[triangleIndex] = triangleKey;
        const centroid = triangleCentroids[triangleIndex];
        if (centroid) weldedVertexPositions.set(triangleKey, centroid.clone());
        const base = triangleIndex * 3;
        const ia = index ? index.getX(base) : base;
        const ib = index ? index.getX(base + 1) : base + 1;
        const ic = index ? index.getX(base + 2) : base + 2;
        const vertexIndices = [ia, ib, ic];
        vertexIndices.forEach((vertexIndex) => {
          const vertexKey = keyForVertex(vertexIndex);
          const vertexPoint = new THREE.Vector3().fromBufferAttribute(position, vertexIndex);
          connectWeldedVertices(triangleKey, centroid, vertexKey, vertexPoint);
        });
      }
      edgeOwners.forEach((owners) => {
        if (owners.length < 2) return;
        for (let left = 0; left < owners.length; left += 1) {
          for (let right = left + 1; right < owners.length; right += 1) {
            const leftTriangleIndex = owners[left].triangleIndex;
            const rightTriangleIndex = owners[right].triangleIndex;
            const leftCentroid = triangleCentroids[leftTriangleIndex];
            const rightCentroid = triangleCentroids[rightTriangleIndex];
            if (!leftCentroid || !rightCentroid) continue;
            connectWeldedVertices(
              triangleGraphKeys[leftTriangleIndex],
              leftCentroid,
              triangleGraphKeys[rightTriangleIndex],
              rightCentroid,
            );
          }
        }
      });

      const pointToSegmentDistance = (pointValue, startPoint, endPoint) => {
        const segment = new THREE.Vector3().subVectors(endPoint, startPoint);
        const segmentLengthSquared = Math.max(segment.lengthSq(), 1.0e-16);
        const along = THREE.MathUtils.clamp(
          new THREE.Vector3().subVectors(pointValue, startPoint).dot(segment) / segmentLengthSquared,
          0,
          1,
        );
        const closestPoint = startPoint.clone().addScaledVector(segment, along);
        return pointValue.distanceTo(closestPoint);
      };

      const vertexSurfaceDistance = new Map();
      const dijkstraHeap = [];
      const dijkstraSwap = (leftIndex, rightIndex) => {
        const swapValue = dijkstraHeap[leftIndex];
        dijkstraHeap[leftIndex] = dijkstraHeap[rightIndex];
        dijkstraHeap[rightIndex] = swapValue;
      };
      const dijkstraPush = (entry) => {
        dijkstraHeap.push(entry);
        let indexInHeap = dijkstraHeap.length - 1;
        while (indexInHeap > 0) {
          const parentIndex = Math.floor((indexInHeap - 1) / 2);
          if (dijkstraHeap[parentIndex].distance <= dijkstraHeap[indexInHeap].distance) break;
          dijkstraSwap(parentIndex, indexInHeap);
          indexInHeap = parentIndex;
        }
      };
      const dijkstraPop = () => {
        if (!dijkstraHeap.length) return null;
        const result = dijkstraHeap[0];
        const tail = dijkstraHeap.pop();
        if (dijkstraHeap.length && tail) {
          dijkstraHeap[0] = tail;
          let indexInHeap = 0;
          while (true) {
            const leftIndex = (indexInHeap * 2) + 1;
            const rightIndex = leftIndex + 1;
            let smallestIndex = indexInHeap;
            if (leftIndex < dijkstraHeap.length && dijkstraHeap[leftIndex].distance < dijkstraHeap[smallestIndex].distance) {
              smallestIndex = leftIndex;
            }
            if (rightIndex < dijkstraHeap.length && dijkstraHeap[rightIndex].distance < dijkstraHeap[smallestIndex].distance) {
              smallestIndex = rightIndex;
            }
            if (smallestIndex === indexInHeap) break;
            dijkstraSwap(indexInHeap, smallestIndex);
            indexInHeap = smallestIndex;
          }
        }
        return result;
      };
      const seedVertexSurfaceDistance = (vertexKey, distanceValue) => {
        if (!vertexKey) return;
        const safeDistance = Math.max(0, Number(distanceValue) || 0);
        if (safeDistance >= (vertexSurfaceDistance.get(vertexKey) ?? Infinity)) return;
        vertexSurfaceDistance.set(vertexKey, safeDistance);
        dijkstraPush({ key: vertexKey, distance: safeDistance });
      };

      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const base = triangleIndex * 3;
        const ia = index ? index.getX(base) : base;
        const ib = index ? index.getX(base + 1) : base + 1;
        const ic = index ? index.getX(base + 2) : base + 2;
        const points = [
          new THREE.Vector3().fromBufferAttribute(position, ia),
          new THREE.Vector3().fromBufferAttribute(position, ib),
          new THREE.Vector3().fromBufferAttribute(position, ic),
        ];
        const keys = [keyForVertex(ia), keyForVertex(ib), keyForVertex(ic)];
        const strengths = triangleEdgeSharpness[triangleIndex] || [0, 0, 0];
        const detectedComponents = [0, 1, 2].filter((component) => (strengths[component] || 0) >= 0.065);
        detectedComponents.forEach((component) => {
          const edgeCorners = component === 0 ? [1, 2] : (component === 1 ? [2, 0] : [0, 1]);
          const startPoint = points[edgeCorners[0]];
          const endPoint = points[edgeCorners[1]];
          for (let corner = 0; corner < 3; corner += 1) {
            seedVertexSurfaceDistance(keys[corner], pointToSegmentDistance(points[corner], startPoint, endPoint));
          }
          const centroid = triangleCentroids[triangleIndex];
          if (centroid) {
            seedVertexSurfaceDistance(
              triangleGraphKeys[triangleIndex],
              pointToSegmentDistance(centroid, startPoint, endPoint),
            );
          }
        });
      }

      while (dijkstraHeap.length) {
        const current = dijkstraPop();
        if (!current) break;
        if (current.distance > (vertexSurfaceDistance.get(current.key) ?? Infinity) + 1.0e-8) continue;
        const neighbours = weldedVertexGraph.get(current.key) || [];
        neighbours.forEach((neighbour) => {
          const nextDistance = current.distance + neighbour.distance;
          if (nextDistance + 1.0e-8 < (vertexSurfaceDistance.get(neighbour.key) ?? Infinity)) {
            vertexSurfaceDistance.set(neighbour.key, nextDistance);
            dijkstraPush({ key: neighbour.key, distance: nextDistance });
          }
        });
      }

      let maxSurfaceDistance = 0;
      vertexSurfaceDistance.forEach((distanceValue) => {
        if (Number.isFinite(distanceValue)) maxSurfaceDistance = Math.max(maxSurfaceDistance, distanceValue);
      });

      const triangleVertexJoinSharpness = new Array(triangleCount);
      const triangleVertexDetectedEdgeCounts = new Array(triangleCount);
      const triangleVertexSurfaceDistances = new Array(triangleCount);
      const triangleInteriorDistanceLifts = new Array(triangleCount);
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const base = triangleIndex * 3;
        const ia = index ? index.getX(base) : base;
        const ib = index ? index.getX(base + 1) : base + 1;
        const ic = index ? index.getX(base + 2) : base + 2;
        const keyA = keyForVertex(ia);
        const keyB = keyForVertex(ib);
        const keyC = keyForVertex(ic);
        triangleVertexJoinSharpness[triangleIndex] = [
          vertexJoinSharpness.get(keyA) || 0,
          vertexJoinSharpness.get(keyB) || 0,
          vertexJoinSharpness.get(keyC) || 0,
        ];
        triangleVertexDetectedEdgeCounts[triangleIndex] = [
          detectedEdgeKeysByVertex.get(keyA)?.size || 0,
          detectedEdgeKeysByVertex.get(keyB)?.size || 0,
          detectedEdgeKeysByVertex.get(keyC)?.size || 0,
        ];
        triangleVertexSurfaceDistances[triangleIndex] = [
          vertexSurfaceDistance.get(keyA) ?? 0,
          vertexSurfaceDistance.get(keyB) ?? 0,
          vertexSurfaceDistance.get(keyC) ?? 0,
        ];

        const pointA = new THREE.Vector3().fromBufferAttribute(position, ia);
        const pointB = new THREE.Vector3().fromBufferAttribute(position, ib);
        const pointC = new THREE.Vector3().fromBufferAttribute(position, ic);
        const centroid = new THREE.Vector3().addVectors(pointA, pointB).add(pointC).multiplyScalar(1 / 3);
        const vertexDistances = triangleVertexSurfaceDistances[triangleIndex];
        const interpolatedCentroidDistance = (vertexDistances[0] + vertexDistances[1] + vertexDistances[2]) / 3;
        let estimatedCentroidDistance = vertexSurfaceDistance.get(triangleGraphKeys[triangleIndex]);
        if (!Number.isFinite(estimatedCentroidDistance)) {
          estimatedCentroidDistance = Math.min(
            vertexDistances[0] + centroid.distanceTo(pointA),
            vertexDistances[1] + centroid.distanceTo(pointB),
            vertexDistances[2] + centroid.distanceTo(pointC),
          );
        }
        const edgeStrengths = triangleEdgeSharpness[triangleIndex] || [0, 0, 0];
        if ((edgeStrengths[0] || 0) >= 0.065) {
          estimatedCentroidDistance = Math.min(estimatedCentroidDistance, pointToSegmentDistance(centroid, pointB, pointC));
        }
        if ((edgeStrengths[1] || 0) >= 0.065) {
          estimatedCentroidDistance = Math.min(estimatedCentroidDistance, pointToSegmentDistance(centroid, pointC, pointA));
        }
        if ((edgeStrengths[2] || 0) >= 0.065) {
          estimatedCentroidDistance = Math.min(estimatedCentroidDistance, pointToSegmentDistance(centroid, pointA, pointB));
        }
        triangleInteriorDistanceLifts[triangleIndex] = estimatedCentroidDistance - interpolatedCentroidDistance;
        maxSurfaceDistance = Math.max(maxSurfaceDistance, estimatedCentroidDistance);
      }

      const nearestSegmentCellSize = Math.max(scale * 0.035, 1.0e-6);
      const nearestSegmentInverseCellSize = 1 / nearestSegmentCellSize;
      const nearestSegmentGrid = new Map();
      const nearestSegmentGridKey = (x, y, z) => `${x}|${y}|${z}`;
      detectedEdgeSegments.forEach((segment, segmentIndex) => {
        const midpoint = new THREE.Vector3().addVectors(segment.start, segment.end).multiplyScalar(0.5);
        const key = nearestSegmentGridKey(
          Math.floor(midpoint.x * nearestSegmentInverseCellSize),
          Math.floor(midpoint.y * nearestSegmentInverseCellSize),
          Math.floor(midpoint.z * nearestSegmentInverseCellSize),
        );
        const bucket = nearestSegmentGrid.get(key) || [];
        bucket.push(segmentIndex);
        nearestSegmentGrid.set(key, bucket);
      });

      const triangleNearestDetectedSegments = new Array(triangleCount);
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const centroid = triangleCentroids[triangleIndex] || new THREE.Vector3();
        const cellX = Math.floor(centroid.x * nearestSegmentInverseCellSize);
        const cellY = Math.floor(centroid.y * nearestSegmentInverseCellSize);
        const cellZ = Math.floor(centroid.z * nearestSegmentInverseCellSize);
        const candidateIndices = new Set();
        for (let radius = 0; radius <= 5 && candidateIndices.size < 12; radius += 1) {
          for (let z = -radius; z <= radius; z += 1) {
            for (let y = -radius; y <= radius; y += 1) {
              for (let x = -radius; x <= radius; x += 1) {
                if (radius > 0 && Math.max(Math.abs(x), Math.abs(y), Math.abs(z)) !== radius) continue;
                const bucket = nearestSegmentGrid.get(nearestSegmentGridKey(cellX + x, cellY + y, cellZ + z));
                if (bucket) bucket.forEach((segmentIndex) => candidateIndices.add(segmentIndex));
              }
            }
          }
        }
        const candidates = candidateIndices.size
          ? [...candidateIndices]
          : detectedEdgeSegments.map((_, segmentIndex) => segmentIndex);
        const nearest = candidates
          .map((segmentIndex) => ({
            segmentIndex,
            distance: pointToSegmentDistance(
              centroid,
              detectedEdgeSegments[segmentIndex].start,
              detectedEdgeSegments[segmentIndex].end,
            ),
          }))
          .sort((left, right) => left.distance - right.distance)
          .slice(0, 2)
          .map((entry) => detectedEdgeSegments[entry.segmentIndex]);
        triangleNearestDetectedSegments[triangleIndex] = nearest;
      }

      geometry.userData = {
        ...(geometry.userData || {}),
        voxemeraEdgeWearMeta: {
          triangleEdgeSharpness,
          triangleVertexJoinSharpness,
          triangleVertexDetectedEdgeCounts,
          triangleVertexSurfaceDistances,
          triangleInteriorDistanceLifts,
          triangleNearestDetectedSegments,
          detectedEdgeSegments,
          maxSurfaceDistance: Math.max(maxSurfaceDistance, scale * 0.000001),
        },
      };
      return geometry;
    };


    const armorPaintCurvatureTextureCache = new WeakMap();
    const getArmorPaintCurvatureTexture = (geometry) => {
      if (!geometry?.getAttribute?.('uv') || !geometry?.getAttribute?.('position')) return null;
      const cached = armorPaintCurvatureTextureCache.get(geometry);
      if (cached) return cached;
      ensureStableEdgeWearAttributes(geometry);
      const triangleEdgeSharpness = geometry.userData?.voxemeraEdgeWearMeta?.triangleEdgeSharpness || [];
      const uv = geometry.getAttribute('uv');
      const index = geometry.index;
      const size = 512;
      const pixels = new Uint8Array(size * size * 4);
      const triangleCount = Math.floor((index ? index.count : uv.count) / 3);
      const edgeWidthPixels = 7.0;
      const edgeWidthUv = edgeWidthPixels / size;
      const getVertexIndex = (triangleIndex, corner) => (
        index ? index.getX((triangleIndex * 3) + corner) : ((triangleIndex * 3) + corner)
      );
      const wrapUv = (value) => value - Math.floor(value);
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const ia = getVertexIndex(triangleIndex, 0);
        const ib = getVertexIndex(triangleIndex, 1);
        const ic = getVertexIndex(triangleIndex, 2);
        let ax = wrapUv(uv.getX(ia)); let ay = wrapUv(uv.getY(ia));
        let bx = wrapUv(uv.getX(ib)); let by = wrapUv(uv.getY(ib));
        let cx = wrapUv(uv.getX(ic)); let cy = wrapUv(uv.getY(ic));
        // UV islands should not interpolate across the 0/1 seam.
        if (Math.max(ax, bx, cx) - Math.min(ax, bx, cx) > 0.5) {
          if (ax < 0.5) ax += 1; if (bx < 0.5) bx += 1; if (cx < 0.5) cx += 1;
        }
        if (Math.max(ay, by, cy) - Math.min(ay, by, cy) > 0.5) {
          if (ay < 0.5) ay += 1; if (by < 0.5) by += 1; if (cy < 0.5) cy += 1;
        }
        const denominator = ((by - cy) * (ax - cx)) + ((cx - bx) * (ay - cy));
        if (Math.abs(denominator) < 1.0e-12) continue;
        const minX = Math.floor(Math.min(ax, bx, cx) * size) - 1;
        const maxX = Math.ceil(Math.max(ax, bx, cx) * size) + 1;
        const minY = Math.floor(Math.min(ay, by, cy) * size) - 1;
        const maxY = Math.ceil(Math.max(ay, by, cy) * size) + 1;
        const strengths = triangleEdgeSharpness[triangleIndex] || [0, 0, 0];
        for (let py = minY; py <= maxY; py += 1) {
          const sampleY = (py + 0.5) / size;
          for (let px = minX; px <= maxX; px += 1) {
            const sampleX = (px + 0.5) / size;
            const wa = (((by - cy) * (sampleX - cx)) + ((cx - bx) * (sampleY - cy))) / denominator;
            const wb = (((cy - ay) * (sampleX - cx)) + ((ax - cx) * (sampleY - cy))) / denominator;
            const wc = 1 - wa - wb;
            if (wa < -0.001 || wb < -0.001 || wc < -0.001) continue;
            const edge0 = (strengths[0] || 0) * Math.max(0, 1 - (wa / edgeWidthUv));
            const edge1 = (strengths[1] || 0) * Math.max(0, 1 - (wb / edgeWidthUv));
            const edge2 = (strengths[2] || 0) * Math.max(0, 1 - (wc / edgeWidthUv));
            const response = Math.max(edge0, edge1, edge2);
            if (response <= 0) continue;
            const wrappedX = ((px % size) + size) % size;
            const wrappedY = ((py % size) + size) % size;
            const offset = ((wrappedY * size) + wrappedX) * 4;
            const value = Math.max(pixels[offset], Math.round(Math.min(1, response) * 255));
            pixels[offset] = value;
            pixels[offset + 1] = value;
            pixels[offset + 2] = value;
            pixels[offset + 3] = 255;
          }
        }
      }
      const texture = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
      texture.name = 'Voxemera ArmorPaint-style curvature bake';
      texture.colorSpace = THREE.NoColorSpace;
      texture.flipY = false;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = true;
      texture.needsUpdate = true;
      armorPaintCurvatureTextureCache.set(geometry, texture);
      return texture;
    };


    const edgeWearDistanceTextureCache = new WeakMap();
    const edgeWearDistanceFallbackTextureCache = new Map();
    const getEdgeWearDistanceFallbackTexture = (maxDistance) => {
      const safeDistance = Math.max(0.000001, Number(maxDistance) || 1);
      const cacheKey = safeDistance.toFixed(6);
      const cached = edgeWearDistanceFallbackTextureCache.get(cacheKey);
      if (cached) return cached;
      const pixels = new Float32Array([safeDistance, safeDistance, safeDistance, 1]);
      const texture = new THREE.DataTexture(pixels, 1, 1, THREE.RGBAFormat, THREE.FloatType);
      texture.name = 'Voxemera edge wear distance fallback';
      texture.colorSpace = THREE.NoColorSpace;
      texture.flipY = false;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      edgeWearDistanceFallbackTextureCache.set(cacheKey, texture);
      return texture;
    };

    const getEdgeWearDistanceTexture = (geometry) => {
      if (!geometry?.getAttribute?.('position') || !geometry?.getAttribute?.('uv')) {
        return getEdgeWearDistanceFallbackTexture(1);
      }
      ensureStableEdgeWearAttributes(geometry);
      const cached = edgeWearDistanceTextureCache.get(geometry);
      if (cached) return cached;
      const edgeWearMeta = geometry.userData?.voxemeraEdgeWearMeta || null;
      const detectedEdgeSegments = Array.isArray(edgeWearMeta?.detectedEdgeSegments)
        ? edgeWearMeta.detectedEdgeSegments
        : [];
      const maximumDistance = Math.max(0.000001, Number(edgeWearMeta?.maxSurfaceDistance) || 1);
      if (!detectedEdgeSegments.length) {
        const fallback = getEdgeWearDistanceFallbackTexture(maximumDistance);
        edgeWearDistanceTextureCache.set(geometry, fallback);
        return fallback;
      }

      const size = 512;
      const pixels = new Float32Array(size * size * 4);
      const coverage = new Uint8Array(size * size);
      for (let pixelIndex = 0; pixelIndex < size * size; pixelIndex += 1) {
        const offset = pixelIndex * 4;
        pixels[offset] = maximumDistance;
        pixels[offset + 1] = maximumDistance;
        pixels[offset + 2] = maximumDistance;
        pixels[offset + 3] = 1;
      }

      const position = geometry.getAttribute('position');
      const uv = geometry.getAttribute('uv');
      const index = geometry.index;
      geometry.computeBoundingBox?.();
      const bounds = geometry.boundingBox;
      const scale = bounds && !bounds.isEmpty()
        ? Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z, 0.000001)
        : 1;
      const segmentCellSize = Math.max(scale * 0.025, 0.000001);
      const inverseSegmentCellSize = 1 / segmentCellSize;
      const segmentGrid = new Map();
      const segmentGridKey = (x, y, z) => `${x}|${y}|${z}`;
      const addSegmentToGrid = (key, segmentIndex) => {
        const bucket = segmentGrid.get(key) || [];
        bucket.push(segmentIndex);
        segmentGrid.set(key, bucket);
      };
      detectedEdgeSegments.forEach((segment, segmentIndex) => {
        const minX = Math.floor(Math.min(segment.start.x, segment.end.x) * inverseSegmentCellSize);
        const maxX = Math.floor(Math.max(segment.start.x, segment.end.x) * inverseSegmentCellSize);
        const minY = Math.floor(Math.min(segment.start.y, segment.end.y) * inverseSegmentCellSize);
        const maxY = Math.floor(Math.max(segment.start.y, segment.end.y) * inverseSegmentCellSize);
        const minZ = Math.floor(Math.min(segment.start.z, segment.end.z) * inverseSegmentCellSize);
        const maxZ = Math.floor(Math.max(segment.start.z, segment.end.z) * inverseSegmentCellSize);
        for (let z = minZ; z <= maxZ; z += 1) {
          for (let y = minY; y <= maxY; y += 1) {
            for (let x = minX; x <= maxX; x += 1) addSegmentToGrid(segmentGridKey(x, y, z), segmentIndex);
          }
        }
      });

      const wrapUv = (value) => {
        const wrapped = value % 1;
        return wrapped < 0 ? wrapped + 1 : wrapped;
      };
      const samplePoint = new THREE.Vector3();
      const segmentVector = new THREE.Vector3();
      const pointVector = new THREE.Vector3();
      const closestPoint = new THREE.Vector3();
      const pointToSegmentDistance = (pointValue, startPoint, endPoint) => {
        segmentVector.subVectors(endPoint, startPoint);
        const segmentLengthSquared = Math.max(segmentVector.lengthSq(), 1.0e-16);
        const along = THREE.MathUtils.clamp(pointVector.subVectors(pointValue, startPoint).dot(segmentVector) / segmentLengthSquared, 0, 1);
        closestPoint.copy(startPoint).addScaledVector(segmentVector, along);
        return pointValue.distanceTo(closestPoint);
      };
      const getNearestDetectedEdgeDistance = (pointValue) => {
        const cellX = Math.floor(pointValue.x * inverseSegmentCellSize);
        const cellY = Math.floor(pointValue.y * inverseSegmentCellSize);
        const cellZ = Math.floor(pointValue.z * inverseSegmentCellSize);
        const candidateIndices = new Set();
        for (let radius = 0; radius <= 4 && candidateIndices.size < 32; radius += 1) {
          for (let z = -radius; z <= radius; z += 1) {
            for (let y = -radius; y <= radius; y += 1) {
              for (let x = -radius; x <= radius; x += 1) {
                if (radius > 0 && Math.max(Math.abs(x), Math.abs(y), Math.abs(z)) !== radius) continue;
                const bucket = segmentGrid.get(segmentGridKey(cellX + x, cellY + y, cellZ + z));
                if (bucket) bucket.forEach((segmentIndex) => candidateIndices.add(segmentIndex));
              }
            }
          }
        }
        const indices = candidateIndices.size ? [...candidateIndices] : detectedEdgeSegments.map((_, segmentIndex) => segmentIndex);
        let nearestDistance = maximumDistance;
        indices.forEach((segmentIndex) => {
          const segment = detectedEdgeSegments[segmentIndex];
          if (!segment) return;
          nearestDistance = Math.min(nearestDistance, pointToSegmentDistance(pointValue, segment.start, segment.end));
        });
        return nearestDistance;
      };

      const triangleCount = Math.floor((index ? index.count : position.count) / 3);
      const pointA = new THREE.Vector3();
      const pointB = new THREE.Vector3();
      const pointC = new THREE.Vector3();
      const wrapPixelIndex = (value) => ((value % size) + size) % size;
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const base = triangleIndex * 3;
        const ia = index ? index.getX(base) : base;
        const ib = index ? index.getX(base + 1) : base + 1;
        const ic = index ? index.getX(base + 2) : base + 2;
        pointA.fromBufferAttribute(position, ia);
        pointB.fromBufferAttribute(position, ib);
        pointC.fromBufferAttribute(position, ic);
        let ax = wrapUv(uv.getX(ia)); let ay = wrapUv(uv.getY(ia));
        let bx = wrapUv(uv.getX(ib)); let by = wrapUv(uv.getY(ib));
        let cx = wrapUv(uv.getX(ic)); let cy = wrapUv(uv.getY(ic));
        if (Math.max(ax, bx, cx) - Math.min(ax, bx, cx) > 0.5) {
          if (ax < 0.5) ax += 1; if (bx < 0.5) bx += 1; if (cx < 0.5) cx += 1;
        }
        if (Math.max(ay, by, cy) - Math.min(ay, by, cy) > 0.5) {
          if (ay < 0.5) ay += 1; if (by < 0.5) by += 1; if (cy < 0.5) cy += 1;
        }
        const denominator = ((by - cy) * (ax - cx)) + ((cx - bx) * (ay - cy));
        if (Math.abs(denominator) < 1.0e-12) continue;
        const minX = Math.floor(Math.min(ax, bx, cx) * size) - 1;
        const maxX = Math.ceil(Math.max(ax, bx, cx) * size) + 1;
        const minY = Math.floor(Math.min(ay, by, cy) * size) - 1;
        const maxY = Math.ceil(Math.max(ay, by, cy) * size) + 1;
        for (let py = minY; py <= maxY; py += 1) {
          const sampleY = (py + 0.5) / size;
          for (let px = minX; px <= maxX; px += 1) {
            const sampleX = (px + 0.5) / size;
            const wa = (((by - cy) * (sampleX - cx)) + ((cx - bx) * (sampleY - cy))) / denominator;
            const wb = (((cy - ay) * (sampleX - cx)) + ((ax - cx) * (sampleY - cy))) / denominator;
            const wc = 1 - wa - wb;
            if (wa < -0.001 || wb < -0.001 || wc < -0.001) continue;
            samplePoint.copy(pointA).multiplyScalar(wa).addScaledVector(pointB, wb).addScaledVector(pointC, wc);
            const nearestDistance = getNearestDetectedEdgeDistance(samplePoint);
            const wrappedX = wrapPixelIndex(px);
            const wrappedY = wrapPixelIndex(py);
            const pixelIndex = (wrappedY * size) + wrappedX;
            const offset = pixelIndex * 4;
            if (!coverage[pixelIndex] || nearestDistance < pixels[offset]) {
              pixels[offset] = nearestDistance;
              pixels[offset + 1] = nearestDistance;
              pixels[offset + 2] = nearestDistance;
              pixels[offset + 3] = 1;
            }
            coverage[pixelIndex] = 1;
          }
        }
      }

      for (let pass = 0; pass < 2; pass += 1) {
        const nextCoverage = coverage.slice();
        const nextPixels = pixels.slice();
        for (let y = 0; y < size; y += 1) {
          for (let x = 0; x < size; x += 1) {
            const pixelIndex = (y * size) + x;
            if (coverage[pixelIndex]) continue;
            let found = false;
            let nearestDistance = maximumDistance;
            for (let dy = -1; dy <= 1; dy += 1) {
              for (let dx = -1; dx <= 1; dx += 1) {
                if (!dx && !dy) continue;
                const nx = wrapPixelIndex(x + dx);
                const ny = wrapPixelIndex(y + dy);
                const neighbourIndex = (ny * size) + nx;
                if (!coverage[neighbourIndex]) continue;
                found = true;
                nearestDistance = Math.min(nearestDistance, pixels[neighbourIndex * 4]);
              }
            }
            if (found) {
              const offset = pixelIndex * 4;
              nextPixels[offset] = nearestDistance;
              nextPixels[offset + 1] = nearestDistance;
              nextPixels[offset + 2] = nearestDistance;
              nextPixels[offset + 3] = 1;
              nextCoverage[pixelIndex] = 1;
            }
          }
        }
        coverage.set(nextCoverage);
        pixels.set(nextPixels);
      }

      const texture = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat, THREE.FloatType);
      texture.name = 'Voxemera edge wear distance texture';
      texture.colorSpace = THREE.NoColorSpace;
      texture.flipY = false;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      edgeWearDistanceTextureCache.set(geometry, texture);
      return texture;
    };

    const createEdgeWearAnnotatedGeometry = (sourceGeometry, workingGeometry, triangleFaceOrder = null) => {
      if (!sourceGeometry?.getAttribute?.('position') || !workingGeometry?.getAttribute?.('position')) return workingGeometry;
      ensureStableEdgeWearAttributes(sourceGeometry);
      const edgeWearMeta = sourceGeometry.userData?.voxemeraEdgeWearMeta || null;
      const triangleEdgeSharpness = edgeWearMeta?.triangleEdgeSharpness || [];
      const triangleVertexJoinSharpness = edgeWearMeta?.triangleVertexJoinSharpness || [];
      const triangleVertexDetectedEdgeCounts = edgeWearMeta?.triangleVertexDetectedEdgeCounts || [];
      const triangleVertexSurfaceDistances = edgeWearMeta?.triangleVertexSurfaceDistances || [];
      const triangleInteriorDistanceLifts = edgeWearMeta?.triangleInteriorDistanceLifts || [];
      const triangleNearestDetectedSegments = edgeWearMeta?.triangleNearestDetectedSegments || [];
      const geometry = workingGeometry.index ? workingGeometry.toNonIndexed() : workingGeometry.clone();
      const position = geometry.getAttribute('position');
      const triangleCount = Math.floor((position?.count || 0) / 3);
      const barycentric = new Float32Array(triangleCount * 9);
      const sharpEdges = new Float32Array(triangleCount * 9);
      const vertexJoins = new Float32Array(triangleCount * 9);
      const vertexDetectedEdgeCounts = new Float32Array(triangleCount * 9);
      const edgeAltitudes = new Float32Array(triangleCount * 9);
      const surfaceDistance = new Float32Array(triangleCount * 3);
      const interiorDistanceLift = new Float32Array(triangleCount * 3);
      const nearestEdgeStart0 = new Float32Array(triangleCount * 9);
      const nearestEdgeEnd0 = new Float32Array(triangleCount * 9);
      const nearestEdgeStart1 = new Float32Array(triangleCount * 9);
      const nearestEdgeEnd1 = new Float32Array(triangleCount * 9);
      const nearestEdgeValid = new Float32Array(triangleCount * 6);
      const triangleVertexA = new Float32Array(triangleCount * 9);
      const triangleVertexB = new Float32Array(triangleCount * 9);
      const triangleVertexC = new Float32Array(triangleCount * 9);
      const legacySharpness = new Float32Array(triangleCount * 3);
      const triangleA = new THREE.Vector3();
      const triangleB = new THREE.Vector3();
      const triangleC = new THREE.Vector3();
      const edgeVector = new THREE.Vector3();
      const pointVector = new THREE.Vector3();
      const baryValues = [
        [1, 0, 0],
        [0, 1, 0],
        [0, 0, 1],
      ];
      for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const sourceTriangleIndex = Array.isArray(triangleFaceOrder) ? triangleFaceOrder[triangleIndex] : triangleIndex;
        const edgeStrengths = triangleEdgeSharpness[sourceTriangleIndex] || [0, 0, 0];
        const joinStrengths = triangleVertexJoinSharpness[sourceTriangleIndex] || [0, 0, 0];
        const detectedEdgeCounts = triangleVertexDetectedEdgeCounts[sourceTriangleIndex] || [0, 0, 0];
        const surfaceDistances = triangleVertexSurfaceDistances[sourceTriangleIndex] || [0, 0, 0];
        const triangleInteriorLift = Math.max(0, Number(triangleInteriorDistanceLifts[sourceTriangleIndex]) || 0);
        const nearestSegments = triangleNearestDetectedSegments[sourceTriangleIndex] || [];
        const segment0 = nearestSegments[0] || null;
        const segment1 = nearestSegments[1] || null;
        const maxStrength = Math.max(edgeStrengths[0] || 0, edgeStrengths[1] || 0, edgeStrengths[2] || 0);
        triangleA.fromBufferAttribute(position, (triangleIndex * 3));
        triangleB.fromBufferAttribute(position, (triangleIndex * 3) + 1);
        triangleC.fromBufferAttribute(position, (triangleIndex * 3) + 2);
        const altitudeA = edgeVector.subVectors(triangleC, triangleB).lengthSq() > 1.0e-16
          ? pointVector.subVectors(triangleA, triangleB).cross(edgeVector).length() / edgeVector.length()
          : 0;
        const altitudeB = edgeVector.subVectors(triangleA, triangleC).lengthSq() > 1.0e-16
          ? pointVector.subVectors(triangleB, triangleC).cross(edgeVector).length() / edgeVector.length()
          : 0;
        const altitudeC = edgeVector.subVectors(triangleB, triangleA).lengthSq() > 1.0e-16
          ? pointVector.subVectors(triangleC, triangleA).cross(edgeVector).length() / edgeVector.length()
          : 0;
        for (let vertexIndex = 0; vertexIndex < 3; vertexIndex += 1) {
          const offset = ((triangleIndex * 3) + vertexIndex) * 3;
          barycentric[offset] = baryValues[vertexIndex][0];
          barycentric[offset + 1] = baryValues[vertexIndex][1];
          barycentric[offset + 2] = baryValues[vertexIndex][2];
          sharpEdges[offset] = edgeStrengths[0] || 0;
          sharpEdges[offset + 1] = edgeStrengths[1] || 0;
          sharpEdges[offset + 2] = edgeStrengths[2] || 0;
          vertexJoins[offset] = joinStrengths[0] || 0;
          vertexJoins[offset + 1] = joinStrengths[1] || 0;
          vertexJoins[offset + 2] = joinStrengths[2] || 0;
          // Repeat all three counts on every triangle vertex so interpolation
          // cannot fade a connected corner's status across the face.
          vertexDetectedEdgeCounts[offset] = detectedEdgeCounts[0] || 0;
          vertexDetectedEdgeCounts[offset + 1] = detectedEdgeCounts[1] || 0;
          vertexDetectedEdgeCounts[offset + 2] = detectedEdgeCounts[2] || 0;
          edgeAltitudes[offset] = altitudeA;
          edgeAltitudes[offset + 1] = altitudeB;
          edgeAltitudes[offset + 2] = altitudeC;
          surfaceDistance[(triangleIndex * 3) + vertexIndex] = surfaceDistances[vertexIndex] || 0;
          interiorDistanceLift[(triangleIndex * 3) + vertexIndex] = triangleInteriorLift;
          const validOffset = ((triangleIndex * 3) + vertexIndex) * 2;
          nearestEdgeValid[validOffset] = segment0 ? 1 : 0;
          nearestEdgeValid[validOffset + 1] = segment1 ? 1 : 0;
          const start0 = segment0?.start || triangleA;
          const end0 = segment0?.end || triangleA;
          const start1 = segment1?.start || triangleA;
          const end1 = segment1?.end || triangleA;
          nearestEdgeStart0[offset] = start0.x;
          nearestEdgeStart0[offset + 1] = start0.y;
          nearestEdgeStart0[offset + 2] = start0.z;
          nearestEdgeEnd0[offset] = end0.x;
          nearestEdgeEnd0[offset + 1] = end0.y;
          nearestEdgeEnd0[offset + 2] = end0.z;
          nearestEdgeStart1[offset] = start1.x;
          nearestEdgeStart1[offset + 1] = start1.y;
          nearestEdgeStart1[offset + 2] = start1.z;
          nearestEdgeEnd1[offset] = end1.x;
          nearestEdgeEnd1[offset + 1] = end1.y;
          nearestEdgeEnd1[offset + 2] = end1.z;
          triangleVertexA[offset] = triangleA.x;
          triangleVertexA[offset + 1] = triangleA.y;
          triangleVertexA[offset + 2] = triangleA.z;
          triangleVertexB[offset] = triangleB.x;
          triangleVertexB[offset + 1] = triangleB.y;
          triangleVertexB[offset + 2] = triangleB.z;
          triangleVertexC[offset] = triangleC.x;
          triangleVertexC[offset + 1] = triangleC.y;
          triangleVertexC[offset + 2] = triangleC.z;
          legacySharpness[(triangleIndex * 3) + vertexIndex] = maxStrength;
        }
      }
      geometry.setAttribute('voxemeraEdgeWearBarycentric', new THREE.Float32BufferAttribute(barycentric, 3));
      geometry.setAttribute('voxemeraEdgeWearSharpEdges', new THREE.Float32BufferAttribute(sharpEdges, 3));
      geometry.setAttribute('voxemeraEdgeWearVertexJoins', new THREE.Float32BufferAttribute(vertexJoins, 3));
      geometry.setAttribute('voxemeraEdgeWearVertexDetectedEdgeCounts', new THREE.Float32BufferAttribute(vertexDetectedEdgeCounts, 3));
      geometry.setAttribute('voxemeraEdgeWearAltitudes', new THREE.Float32BufferAttribute(edgeAltitudes, 3));
      geometry.setAttribute('voxemeraEdgeWearSurfaceDistance', new THREE.Float32BufferAttribute(surfaceDistance, 1));
      geometry.setAttribute('voxemeraEdgeWearInteriorLift', new THREE.Float32BufferAttribute(interiorDistanceLift, 1));
      geometry.setAttribute('voxemeraEdgeWearNearestStart0', new THREE.Float32BufferAttribute(nearestEdgeStart0, 3));
      geometry.setAttribute('voxemeraEdgeWearNearestEnd0', new THREE.Float32BufferAttribute(nearestEdgeEnd0, 3));
      geometry.setAttribute('voxemeraEdgeWearNearestStart1', new THREE.Float32BufferAttribute(nearestEdgeStart1, 3));
      geometry.setAttribute('voxemeraEdgeWearNearestEnd1', new THREE.Float32BufferAttribute(nearestEdgeEnd1, 3));
      geometry.setAttribute('voxemeraEdgeWearNearestValid', new THREE.Float32BufferAttribute(nearestEdgeValid, 2));
      geometry.setAttribute('voxemeraEdgeWearTriangleA', new THREE.Float32BufferAttribute(triangleVertexA, 3));
      geometry.setAttribute('voxemeraEdgeWearTriangleB', new THREE.Float32BufferAttribute(triangleVertexB, 3));
      geometry.setAttribute('voxemeraEdgeWearTriangleC', new THREE.Float32BufferAttribute(triangleVertexC, 3));
      geometry.setAttribute('voxemeraEdgeWear', new THREE.Float32BufferAttribute(legacySharpness, 1));
      geometry.clearGroups();
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      geometry.userData = {
        ...(workingGeometry.userData || {}),
        ...(geometry.userData || {}),
        voxemeraEdgeWearMaxDistance: Math.max(0.000001, Number(edgeWearMeta?.maxSurfaceDistance) || 0.000001),
      };
      return geometry;
    };

    const createSelectionGeometry = (sourceMesh, allowedFaceIndices = null, includeEdgeWear = true) => {
      const sourceGeometry = includeEdgeWear
        ? ensureStableEdgeWearAttributes(sourceMesh.geometry)
        : sourceMesh.geometry;
      if (!activeSelection?.selectedFaces) {
        const clonedGeometry = sourceGeometry.clone();
        clonedGeometry.clearGroups();
        clonedGeometry.computeBoundingBox();
        clonedGeometry.computeBoundingSphere();
        if (!includeEdgeWear) return clonedGeometry;
        return createEdgeWearAnnotatedGeometry(sourceGeometry, clonedGeometry, null);
      }
      const sourceIndex = sourceGeometry.index;
      const triangleCount = Math.floor((sourceIndex ? sourceIndex.count : sourceGeometry.attributes.position.count) / 3);
      const explicitlySelected = activeSelection.selectedFaces.get(sourceMesh.uuid) || new Set();
      const boundary = activeSelection.boundaryFaces?.get(sourceMesh.uuid) || new Set();
      // For wrapped 3D lasso selections, keep the complete enclosed region
      // plus every triangle crossed by the lasso boundary. The projected pixel
      // mask trims those boundary triangles at the exact lasso line. Excluding
      // them here creates the large inward triangular gaps visible at the edge.
      const selected = activeSelection.inverted
        ? new Set(Array.from({ length: triangleCount }, (_, faceIndex) => faceIndex).filter((faceIndex) => !explicitlySelected.has(faceIndex)))
        : new Set([...explicitlySelected, ...boundary]);
      if (allowedFaceIndices?.size) {
        [...selected].forEach((faceIndex) => {
          if (!allowedFaceIndices.has(faceIndex)) selected.delete(faceIndex);
        });
      }
      const debugEntry = {
        name: sourceMesh.name,
        uuid: sourceMesh.uuid,
        total: triangleCount,
        selected: explicitlySelected.size,
        boundary: boundary.size,
        usable: selected.size,
        result: selected.size ? 'geometry-created' : (explicitlySelected.size ? 'selection-produced-no-usable-faces' : 'mesh-uuid-not-in-selection'),
      };
      const existingDebugIndex = selectionDebugState.meshes.findIndex((entry) => entry.uuid === sourceMesh.uuid);
      if (existingDebugIndex >= 0) selectionDebugState.meshes[existingDebugIndex] = debugEntry;
      else selectionDebugState.meshes.push(debugEntry);
      refreshSelectionDebug('selection-geometry-test');
      if (!selected.size) return null;
      const selectedIndices = [];
      const faceOrder = [];
      selected.forEach((faceIndex) => {
        const base = faceIndex * 3;
        if (sourceIndex) {
          if (base + 2 < sourceIndex.count) {
            selectedIndices.push(sourceIndex.getX(base), sourceIndex.getX(base + 1), sourceIndex.getX(base + 2));
            faceOrder.push(faceIndex);
          }
        } else if (base + 2 < sourceGeometry.attributes.position.count) {
          selectedIndices.push(base, base + 1, base + 2);
          faceOrder.push(faceIndex);
        }
      });
      if (!selectedIndices.length) return null;
      const indexedGeometry = sourceGeometry.clone();
      indexedGeometry.setIndex(selectedIndices);
      indexedGeometry.clearGroups();
      indexedGeometry.computeBoundingBox();
      indexedGeometry.computeBoundingSphere();
      indexedGeometry.userData = {
        ...(indexedGeometry.userData || {}),
        voxemeraSelectionGeometry: true,
      };
      const geometry = includeEdgeWear
        ? createEdgeWearAnnotatedGeometry(sourceGeometry, indexedGeometry, faceOrder)
        : indexedGeometry;
      geometry.userData = {
        ...(geometry.userData || {}),
        voxemeraSelectionGeometry: true,
      };
      return geometry;
    };

    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = 1;
    previewCanvas.height = 1;
    previewCanvas.style.position = 'absolute';
    previewCanvas.style.inset = '0';
    previewCanvas.style.width = '100%';
    previewCanvas.style.height = '100%';
    previewCanvas.style.pointerEvents = 'none';
    previewCanvas.style.zIndex = '1';
    container.appendChild(previewCanvas);
    const previewContext = previewCanvas.getContext('2d', { alpha: true });

    // Lightweight 3D brush cursor. This is deliberately separate from the
    // paint preview canvas so it follows pointer movement immediately.
    // Airbrush is excluded entirely because its distance-based spray footprint
    // is handled by the airbrush renderer and must not be altered here.
    const brushCursorOverlay = document.createElement('div');
    brushCursorOverlay.style.position = 'absolute';
    brushCursorOverlay.style.left = '0';
    brushCursorOverlay.style.top = '0';
    brushCursorOverlay.style.width = '1px';
    brushCursorOverlay.style.height = '1px';
    brushCursorOverlay.style.border = '1px solid rgba(255,255,255,0.98)';
    brushCursorOverlay.style.borderRadius = '50%';
    brushCursorOverlay.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.92)';
    brushCursorOverlay.style.background = 'transparent';
    brushCursorOverlay.style.pointerEvents = 'none';
    brushCursorOverlay.style.zIndex = '3';
    brushCursorOverlay.style.display = 'none';
    brushCursorOverlay.style.transform = 'translate(-50%, -50%)';
    brushCursorOverlay.style.willChange = 'left, top, width, height';
    container.appendChild(brushCursorOverlay);
    let previewRenderScale = Math.min(window.devicePixelRatio || 1, 2);
    let previewDisplayOpacity = 1;
    let previewOverscan = 0;
    let previewViewportWidth = initialViewportWidth;
    let previewViewportHeight = initialViewportHeight;

    const configurePreviewResolution = (width, height, requestedScale = previewRenderScale, requestedOverscan = 0) => {
      // This is a CPU 2D canvas used only for the temporary paint raster.
      // Do not clamp it to the WebGL max texture size, because the final stroke
      // is cropped before upload. Using the GPU limit here was forcing zoomed-out
      // airbrush strokes to be rasterised too coarsely.
      const maxPreviewCanvasDimension = 8192;
      const maxPreviewCanvasPixels = 64 * 1024 * 1024;
      const safeWidth = Math.max(1, Number(width) || 1);
      const safeHeight = Math.max(1, Number(height) || 1);
      const safeOverscan = Math.max(0, Math.ceil(Number(requestedOverscan) || 0));
      const expandedWidth = safeWidth + (safeOverscan * 2);
      const expandedHeight = safeHeight + (safeOverscan * 2);
      const dimensionScale = Math.min(maxPreviewCanvasDimension / expandedWidth, maxPreviewCanvasDimension / expandedHeight);
      const areaScale = Math.sqrt(maxPreviewCanvasPixels / Math.max(1, expandedWidth * expandedHeight));
      const maximumScale = Math.max(1, Math.min(dimensionScale, areaScale));
      previewRenderScale = Math.max(1, Math.min(Number(requestedScale) || 1, maximumScale));
      previewOverscan = safeOverscan;
      previewViewportWidth = safeWidth;
      previewViewportHeight = safeHeight;
      previewCanvas.width = Math.max(1, Math.round(expandedWidth * previewRenderScale));
      previewCanvas.height = Math.max(1, Math.round(expandedHeight * previewRenderScale));
      previewCanvas.style.inset = 'auto';
      previewCanvas.style.left = `${-safeOverscan}px`;
      previewCanvas.style.top = `${-safeOverscan}px`;
      previewCanvas.style.width = `${expandedWidth}px`;
      previewCanvas.style.height = `${expandedHeight}px`;
      if (previewContext) {
        previewContext.setTransform(
          previewRenderScale,
          0,
          0,
          previewRenderScale,
          safeOverscan * previewRenderScale,
          safeOverscan * previewRenderScale,
        );
      }
    };

    const createOverscannedProjectionCamera = (sourceCamera, viewportWidth, viewportHeight, overscan) => {
      const projectionCamera = sourceCamera.clone();
      projectionCamera.position.copy(sourceCamera.position);
      projectionCamera.quaternion.copy(sourceCamera.quaternion);
      projectionCamera.scale.copy(sourceCamera.scale);
      projectionCamera.up.copy(sourceCamera.up);
      projectionCamera.near = sourceCamera.near;
      projectionCamera.far = sourceCamera.far;
      projectionCamera.zoom = sourceCamera.zoom;

      const safeViewportWidth = Math.max(1, Number(viewportWidth) || 1);
      const safeViewportHeight = Math.max(1, Number(viewportHeight) || 1);
      const safeOverscan = Math.max(0, Number(overscan) || 0);
      const expandedWidth = safeViewportWidth + (safeOverscan * 2);
      const expandedHeight = safeViewportHeight + (safeOverscan * 2);

      if (projectionCamera.isPerspectiveCamera) {
        const sourceVerticalFov = THREE.MathUtils.degToRad(sourceCamera.fov || 45);
        const expandedVerticalFov = 2 * Math.atan(
          Math.tan(sourceVerticalFov * 0.5) * (expandedHeight / safeViewportHeight),
        );
        projectionCamera.fov = THREE.MathUtils.radToDeg(expandedVerticalFov);
        projectionCamera.aspect = expandedWidth / expandedHeight;
      } else if (projectionCamera.isOrthographicCamera) {
        const centreX = (sourceCamera.left + sourceCamera.right) * 0.5;
        const centreY = (sourceCamera.top + sourceCamera.bottom) * 0.5;
        const halfWidth = ((sourceCamera.right - sourceCamera.left) * 0.5) * (expandedWidth / safeViewportWidth);
        const halfHeight = ((sourceCamera.top - sourceCamera.bottom) * 0.5) * (expandedHeight / safeViewportHeight);
        projectionCamera.left = centreX - halfWidth;
        projectionCamera.right = centreX + halfWidth;
        projectionCamera.bottom = centreY - halfHeight;
        projectionCamera.top = centreY + halfHeight;
      }

      projectionCamera.updateProjectionMatrix();
      projectionCamera.updateMatrixWorld(true);
      return projectionCamera;
    };

    const createProjectionSubCamera = (sourceCamera, fullWidth, fullHeight, pixelRect) => {
      if (!sourceCamera || !pixelRect) return sourceCamera;
      const subCamera = sourceCamera.clone();
      subCamera.position.copy(sourceCamera.position);
      subCamera.quaternion.copy(sourceCamera.quaternion);
      subCamera.scale.copy(sourceCamera.scale);
      subCamera.up.copy(sourceCamera.up);
      subCamera.near = sourceCamera.near;
      subCamera.far = sourceCamera.far;
      if (typeof sourceCamera.zoom === 'number') subCamera.zoom = sourceCamera.zoom;
      const viewX = Math.max(0, Math.floor(Number(pixelRect.x) || 0));
      const viewY = Math.max(0, Math.floor(Number(pixelRect.y) || 0));
      const viewWidth = Math.max(1, Math.floor(Number(pixelRect.width) || 1));
      const viewHeight = Math.max(1, Math.floor(Number(pixelRect.height) || 1));
      const safeFullWidth = Math.max(viewWidth, Math.floor(Number(fullWidth) || viewWidth));
      const safeFullHeight = Math.max(viewHeight, Math.floor(Number(fullHeight) || viewHeight));
      if (typeof subCamera.setViewOffset === 'function') {
        subCamera.setViewOffset(safeFullWidth, safeFullHeight, viewX, viewY, viewWidth, viewHeight);
      }
      subCamera.updateProjectionMatrix();
      subCamera.updateMatrixWorld(true);
      return subCamera;
    };

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enabled = false;
    controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
    controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
    controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    const handleControlsChange = () => {
      const selection = selectionDraft || activeSelection;
      // GPU editable polygons already live in world space. OrbitControls only
      // changes camera matrices, so no selection geometry or overlay redraw is
      // needed here. Legacy viewport-space selections still need reprojection.
      if (!(selection?.editablePolygon && selection?.surfaceSamples?.length) && selection?.surfaceSamples?.length) {
        drawSelectionOverlay();
      }
      updateDecalTransformOverlay();
    };
    controls.addEventListener('change', handleControlsChange);

    // Show the active OrbitControls target while Alt is held. The marker is a
    // small always-visible 3D crosshair that follows whichever pivot the user
    // selected, including the default model-centre target.
    const orbitPivotMarker = new THREE.Group();
    orbitPivotMarker.visible = false;
    orbitPivotMarker.renderOrder = 10000;

    const orbitPivotDotGeometry = new THREE.SphereGeometry(0.12, 16, 12);
    const orbitPivotDotMaterial = new THREE.MeshBasicMaterial({
      color: 0xffd43b,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.98,
    });
    const orbitPivotDot = new THREE.Mesh(orbitPivotDotGeometry, orbitPivotDotMaterial);
    orbitPivotDot.renderOrder = 10001;
    orbitPivotMarker.add(orbitPivotDot);

    const orbitPivotRingGeometry = new THREE.TorusGeometry(0.42, 0.045, 8, 32);
    const orbitPivotRingMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.95,
    });
    const orbitPivotRing = new THREE.Mesh(orbitPivotRingGeometry, orbitPivotRingMaterial);
    orbitPivotRing.renderOrder = 10000;
    orbitPivotMarker.add(orbitPivotRing);

    scene.add(orbitPivotMarker);
    let altKeyHeld = false;

    const updateOrbitPivotMarker = () => {
      orbitPivotMarker.visible = altKeyHeld;
      if (!altKeyHeld) return;
      orbitPivotMarker.position.copy(controls.target);
      orbitPivotMarker.quaternion.copy(camera.quaternion);
      const distance = Math.max(0.0001, camera.position.distanceTo(controls.target));
      const markerScale = Math.max(0.02, distance * 0.035);
      orbitPivotMarker.scale.setScalar(markerScale);
    };

    scene.add(new THREE.HemisphereLight(0xffffff, 0x2d3440, 1.35));

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.25);
    keyLight.position.set(5, 8, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.bias = -0.0002;
    keyLight.shadow.normalBias = 0.025;
    const keyLightTarget = new THREE.Object3D();
    scene.add(keyLightTarget);
    keyLight.target = keyLightTarget;
    scene.add(keyLight);

    const grid = new THREE.GridHelper(GRID_SIZE, GRID_DIVISIONS, 0x8b8b8b, 0x777777);
    grid.position.y = -0.002;
    scene.add(grid);

    const paintSessions = new Map();
    let activePaintSession = null;

    const createPaintSession = (sessionKey, width, height) => {
      const profiler = loadProfilerRef.current;
      const existing = paintSessions.get(sessionKey);
      if (existing) return existing;
      profiler?.mark('Paint session: allocate canvas', 'Allocating the full UV paint atlas. Large dimensions consume substantial CPU/GPU memory.', { sessionKey, width, height });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.min(Number(width) || ORIGINAL_UV_TEXTURE_RESOLUTION, renderer.capabilities.maxTextureSize));
      canvas.height = Math.max(1, Math.min(Number(height) || ORIGINAL_UV_TEXTURE_RESOLUTION, renderer.capabilities.maxTextureSize));
      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      profiler?.mark('Paint session: 2D context ready', 'Backing bitmap memory for the UV atlas has been allocated.', { actualWidth: canvas.width, actualHeight: canvas.height, megapixels: ((canvas.width * canvas.height) / 1000000).toFixed(2) });
      profiler?.mark('Paint session: creating GPU texture wrapper', 'Creating the Three.js CanvasTexture; actual GPU upload occurs during rendering.');
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = false;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.userData = { ...(texture.userData || {}), voxemeraSharedTexture: true };

      // One packed linear texture supplies all three universal material channels:
      // red = height/bump, green = roughness (inverse gloss), blue = metallic.
      const materialCanvas = document.createElement('canvas');
      materialCanvas.width = canvas.width;
      materialCanvas.height = canvas.height;
      const materialContext = materialCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
      const materialTexture = new THREE.CanvasTexture(materialCanvas);
      materialTexture.colorSpace = THREE.NoColorSpace;
      materialTexture.flipY = false;
      materialTexture.wrapS = THREE.ClampToEdgeWrapping;
      materialTexture.wrapT = THREE.ClampToEdgeWrapping;
      materialTexture.minFilter = THREE.LinearFilter;
      materialTexture.magFilter = THREE.LinearFilter;
      materialTexture.generateMipmaps = false;
      materialTexture.userData = { ...(materialTexture.userData || {}), voxemeraSharedTexture: true };

      const normalCanvas = document.createElement('canvas');
      normalCanvas.width = canvas.width;
      normalCanvas.height = canvas.height;
      const normalContext = normalCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
      const normalTexture = new THREE.CanvasTexture(normalCanvas);
      normalTexture.colorSpace = THREE.NoColorSpace;
      normalTexture.flipY = false;
      normalTexture.wrapS = THREE.ClampToEdgeWrapping;
      normalTexture.wrapT = THREE.ClampToEdgeWrapping;
      normalTexture.minFilter = THREE.LinearFilter;
      normalTexture.magFilter = THREE.LinearFilter;
      normalTexture.generateMipmaps = false;
      normalTexture.userData = { ...(normalTexture.userData || {}), voxemeraSharedTexture: true };
      if (normalContext) {
        normalContext.fillStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
        normalContext.fillRect(0, 0, normalCanvas.width, normalCanvas.height);
      }

      const baselineCanvas = document.createElement('canvas');
      baselineCanvas.width = canvas.width;
      baselineCanvas.height = canvas.height;
      const baselineContext = baselineCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      const baselineTexture = new THREE.CanvasTexture(baselineCanvas);
      baselineTexture.colorSpace = THREE.SRGBColorSpace;
      baselineTexture.flipY = false;
      baselineTexture.wrapS = THREE.ClampToEdgeWrapping;
      baselineTexture.wrapT = THREE.ClampToEdgeWrapping;
      baselineTexture.minFilter = THREE.LinearFilter;
      baselineTexture.magFilter = THREE.LinearFilter;
      baselineTexture.generateMipmaps = false;
      baselineTexture.userData = { ...(baselineTexture.userData || {}), voxemeraSharedTexture: true };

      // GPU-only authoritative paint map. RGB stores the final merged paint
      // colour and alpha stores exact projected coverage. It is rendered in UV
      // space directly from the working decal projection shader; no pixel
      // readback or CPU rasterisation is involved.
      const gpuPaintTarget = new THREE.WebGLRenderTarget(canvas.width, canvas.height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      gpuPaintTarget.texture.name = `VoxemeraGpuPaintMap-${sessionKey}`;
      gpuPaintTarget.texture.colorSpace = THREE.SRGBColorSpace;
      gpuPaintTarget.texture.generateMipmaps = false;

      const session = {
        key: sessionKey,
        canvas,
        context,
        texture,
        materialCanvas,
        materialContext,
        materialTexture,
        normalCanvas,
        normalContext,
        normalTexture,
        baselineCanvas,
        baselineContext,
        baselineTexture,
        gpuPaintTarget,
        gpuPaintTexture: gpuPaintTarget.texture,
        gpuPaintInitialized: false,
        initialized: false,
      };
      paintSessions.set(sessionKey, session);
      profiler?.mark('Paint session: created', 'Canvas and texture are ready for initialization.', { sessionKey });
      return session;
    };

    const disposePaintSessions = () => {
      paintSessions.forEach((session) => {
        session?.texture?.dispose?.();
        session?.materialTexture?.dispose?.();
        session?.normalTexture?.dispose?.();
        session?.baselineTexture?.dispose?.();
        session?.gpuPaintTarget?.dispose?.();
      });
      paintSessions.clear();
      activePaintSession = null;
    };

    const getOrCreatePaintSession = (sessionKey, model, layoutMode, width, height) => {
      const session = createPaintSession(sessionKey, width, height);
      if (!session.initialized) {
        loadProfilerRef.current?.mark('Paint atlas initialization: start', 'Copying source materials/textures or clearing the generated UV atlas.');
        initialisePaintCanvasFromModel(session, model, layoutMode);
        if (session.baselineContext && session.baselineCanvas) {
          session.baselineContext.setTransform(1, 0, 0, 1, 0, 0);
          session.baselineContext.clearRect(0, 0, session.baselineCanvas.width, session.baselineCanvas.height);
          session.baselineContext.drawImage(session.canvas, 0, 0);
          session.baselineTexture.needsUpdate = true;
        }
        loadProfilerRef.current?.mark('Paint atlas initialization: complete', 'CPU paint bitmap and untouched colour baseline are initialized.');
      }
      return session;
    };

    const raycaster = new THREE.Raycaster();
    const pointerNdc = new THREE.Vector2();
    let activePaintStroke = null;
    let uvLayoutRevision = 0;

    const getCurrentLayerState = () => {
      const state = layerStateRef.current || {};
      return {
        layers: Array.isArray(state.layers) ? state.layers.filter(Boolean) : [],
        activeLayerId: String(state.activeLayerId || ''),
      };
    };

    const getProjectionLayerInfo = (layerId) => {
      const { layers: currentLayers } = getCurrentLayerState();
      const safeLayerId = String(layerId || '');
      const index = currentLayers.findIndex((layer) => String(layer?.id || '') === safeLayerId);
      const layer = index >= 0 ? currentLayers[index] : null;
      const missingNamedLayer = Boolean(safeLayerId) && currentLayers.length > 0 && index < 0;
      return {
        layer,
        index,
        visible: !missingNamedLayer && layer?.visible !== false,
        opacity: Math.max(0, Math.min(1, Number(layer?.opacity ?? 1))),
        edgeWear: Math.max(-1, Math.min(1, Number(layer?.edgeWearPercent ?? 0) / 100)),
      };
    };

    const scheduleEdgeWearDebugReport = () => {};

    const getOrderedVisibleProjectionStrokes = () => {
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const visibleCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
      const visibleStrokes = strokes.slice(0, visibleCount);
      const layerDecals = runtime?.layerDecals instanceof Map ? runtime.layerDecals : new Map();
      const { layers: currentLayers } = getCurrentLayerState();
      const bottomToTopLayerIds = currentLayers
        .slice()
        .reverse()
        .map((layer) => String(layer?.id || ''));
      const knownLayerIds = new Set(bottomToTopLayerIds);
      const ordered = [];
      const included = new Set();

      const appendStroke = (stroke) => {
        if (!stroke || included.has(stroke)) return;
        if (!getProjectionLayerInfo(stroke.layerId).visible) return;
        const visibleMeshes = Array.isArray(stroke.meshes)
          ? stroke.meshes.some((mesh) => mesh?.visible !== false)
          : false;
        if (!visibleMeshes) return;
        included.add(stroke);
        ordered.push(stroke);
      };

      // Build the exact same bottom-to-top sequence used by the live renderer.
      // Ordinary projected paint is held in projectionStrokes, while imported
      // image decals and wrapped text are held separately in layerDecals as a
      // projectedStroke. The previous bake only consumed projectionStrokes, so
      // text and decals were visible in Voxemera but absent from every export
      // channel. Feed both kinds through the same colour/material/normal passes.
      bottomToTopLayerIds.forEach((layerId) => {
        visibleStrokes.forEach((stroke) => {
          if (String(stroke?.layerId || '') === layerId) appendStroke(stroke);
        });
        appendStroke(layerDecals.get(layerId)?.projectedStroke);
      });

      visibleStrokes.forEach((stroke) => {
        if (!knownLayerIds.has(String(stroke?.layerId || ''))) appendStroke(stroke);
      });
      layerDecals.forEach((record, layerId) => {
        if (!knownLayerIds.has(String(layerId || ''))) appendStroke(record?.projectedStroke);
      });

      return ordered;
    };

    const refreshLayerNormalMixing = () => {
      const runtime = runtimeRef.current;
      if (!runtime) return;

      const strokes = Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes : [];
      const visibleCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
      const visibleStrokes = strokes.slice(0, visibleCount);
      const { layers: currentLayers } = getCurrentLayerState();
      const layerById = new Map(currentLayers.map((layer) => [String(layer?.id || ''), layer]));

      const getLatestPbrStrokeForLayer = (layerId) => {
        const safeLayerId = String(layerId || '');
        for (let index = visibleStrokes.length - 1; index >= 0; index -= 1) {
          const candidate = visibleStrokes[index];
          if (String(candidate?.layerId || '') === safeLayerId && candidate?.isPbr === true) return candidate;
        }
        return null;
      };

      const applyNormalMixToStroke = (stroke, targetLayer) => {
        if (!stroke || !targetLayer) return;
        const sourceLayerId = String(targetLayer.normalSourceLayerId || '');
        const usesAllBelow = sourceLayerId === '__all_below__';
        const selfMix = Math.max(0, Math.min(1, Number(targetLayer.normalSelfPercent ?? 100) / 100));
        const sourceStroke = !usesAllBelow && sourceLayerId ? getLatestPbrStrokeForLayer(sourceLayerId) : null;
        const sourceSetKey = sourceStroke?.brushConfig?.pbrSetKey || '';
        const sourceResource = sourceSetKey ? pbrSetResourcesRef.current.get(sourceSetKey) : null;
        const sourceTextures = getPbrRuntimeTextures(sourceResource);
        const sourceScale = Math.max(
          0.1,
          Math.min(8, (Number(sourceStroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100),
        );
        const sourceAngle = THREE.MathUtils.degToRad(
          Number(sourceStroke?.brushConfig?.pbrAngleDegrees) || 0,
        );

        (stroke.meshes || []).forEach((mesh) => {
          const uniforms = mesh?.material?.userData?.voxemeraProjectionUniforms;
          if (!uniforms?.normalLayerMix || !uniforms?.normalLayerMap) return;
          uniforms.normalLayerMix.value = (sourceTextures?.normal || usesAllBelow) ? selfMix : 1;
          uniforms.normalLayerMap.value = sourceTextures?.normal || uniforms.pbrNormalMap?.value || uniforms.normalLayerMap.value;
          if (uniforms.normalLayerHasMap) uniforms.normalLayerHasMap.value = sourceTextures?.normal ? 1 : 0;
          if (uniforms.normalLayerScale) uniforms.normalLayerScale.value = sourceScale;
          if (uniforms.normalLayerAngle) uniforms.normalLayerAngle.value = sourceAngle;
          if (!usesAllBelow && uniforms.normalBelowHasMap) uniforms.normalBelowHasMap.value = 0;
        });
      };

      visibleStrokes.forEach((stroke) => {
        applyNormalMixToStroke(stroke, layerById.get(String(stroke?.layerId || '')) || null);
      });

      const applyBelowTextures = (belowTextureByLayerId) => {
        visibleStrokes.forEach((stroke) => {
          const uniformsTexture = belowTextureByLayerId.get(String(stroke?.layerId || ''));
          (stroke.meshes || []).forEach((mesh) => {
            const uniforms = mesh?.material?.userData?.voxemeraProjectionUniforms;
            if (!uniforms?.normalBelowHasMap) return;
            if (uniformsTexture) {
              if (uniforms.normalBelowMap) uniforms.normalBelowMap.value = uniformsTexture;
              uniforms.normalBelowHasMap.value = 1;
            } else if (String(layerById.get(String(stroke?.layerId || ''))?.normalSourceLayerId || '') === '__all_below__') {
              uniforms.normalBelowHasMap.value = 0;
            }
          });
        });

        const layerDecals = runtime.layerDecals instanceof Map ? runtime.layerDecals : null;
        layerDecals?.forEach((record, layerId) => {
          const projectedStroke = record?.projectedStroke;
          applyNormalMixToStroke(projectedStroke, layerById.get(String(layerId || '')) || null);
          const texture = belowTextureByLayerId.get(String(layerId || ''));
          (projectedStroke?.meshes || []).forEach((mesh) => {
            const uniforms = mesh?.material?.userData?.voxemeraProjectionUniforms;
            if (!uniforms?.normalBelowHasMap) return;
            if (texture) {
              if (uniforms.normalBelowMap) uniforms.normalBelowMap.value = texture;
              uniforms.normalBelowHasMap.value = 1;
            } else if (String(layerById.get(String(layerId || ''))?.normalSourceLayerId || '') === '__all_below__') {
              uniforms.normalBelowHasMap.value = 0;
            }
          });
        });
      };

      const allBelowLayers = currentLayers.filter(
        (layer) => String(layer?.normalSourceLayerId || '') === '__all_below__',
      );
      if (!allBelowLayers.length) {
        if (runtime.normalBelowRefreshTimer) {
          if (runtime.normalBelowRefreshUsesIdleCallback && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(runtime.normalBelowRefreshTimer);
          } else {
            clearTimeout(runtime.normalBelowRefreshTimer);
          }
          runtime.normalBelowRefreshTimer = null;
          runtime.normalBelowRefreshUsesIdleCallback = false;
        }
        if (Array.isArray(runtime.normalBelowTextures)) {
          runtime.normalBelowTextures.forEach((texture) => texture?.dispose?.());
        }
        runtime.normalBelowTextures = [];
        runtime.normalBelowTextureByLayerId = new Map();
        runtime.normalBelowCacheKey = '';
        runtime.normalBelowPendingCacheKey = '';
        runtime.normalBelowRebuildInProgress = false;
        applyBelowTextures(runtime.normalBelowTextureByLayerId);
        return;
      }


      // Only changes to layers/strokes BELOW a target layer can invalidate that
      // target's composed normal map. The previous cache key included the target
      // layer itself, its blend slider and every top-layer stroke. That caused a
      // fresh 2K CPU normal bake while painting, moving the slider and restoring
      // the document on reload.
      const orderedLayerIdsForBelowCache = currentLayers.map((layer) => String(layer?.id || ''));
      const allBelowCacheState = allBelowLayers.map((targetLayer) => {
        const targetLayerId = String(targetLayer?.id || '');
        const targetIndex = orderedLayerIdsForBelowCache.indexOf(targetLayerId);
        const belowLayerIds = new Set(
          targetIndex >= 0 ? orderedLayerIdsForBelowCache.slice(targetIndex + 1) : [],
        );
        const belowLayers = currentLayers
          .filter((layer) => belowLayerIds.has(String(layer?.id || '')))
          .map((layer) => ({
            id: String(layer?.id || ''),
            visible: layer?.visible !== false,
            opacity: Number(layer?.opacity ?? 1),
          }));
        const belowStrokes = visibleStrokes
          .filter((stroke) => (
            belowLayerIds.has(String(stroke?.layerId || '')) && stroke?.isPbr === true
          ))
          .map((stroke) => ({
            layerId: String(stroke?.layerId || ''),
            kind: String(stroke?.kind || ''),
            pbrSetKey: String(stroke?.brushConfig?.pbrSetKey || ''),
            pbrScalePercent: Number(stroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE,
            pbrAngleDegrees: ((Number(stroke?.brushConfig?.pbrAngleDegrees) || 0) % 360 + 360) % 360,
            opacity: Number(stroke?.brushConfig?.opacity ?? 1),
            normalStrength: Number(stroke?.materialSettings?.height ?? 0),
            sourceWidth: Number(stroke?.sourceCanvas?.width || 0),
            sourceHeight: Number(stroke?.sourceCanvas?.height || 0),
            pointCount: Array.isArray(stroke?.points) ? stroke.points.length : 0,
            meshCount: Array.isArray(stroke?.meshes) ? stroke.meshes.length : 0,
          }));
        return { targetLayerId, belowLayers, belowStrokes };
      });

      const cacheKey = JSON.stringify({
        uvLayoutRevision,
        atlasWidth: Number(activePaintSession?.normalCanvas?.width || activePaintSession?.canvas?.width || 0),
        atlasHeight: Number(activePaintSession?.normalCanvas?.height || activePaintSession?.canvas?.height || 0),
        targets: allBelowCacheState,
      });

      if (
        runtime.normalBelowCacheKey === cacheKey
        && runtime.normalBelowTextureByLayerId instanceof Map
      ) {
        applyBelowTextures(runtime.normalBelowTextureByLayerId);
        return;
      }

      // If this exact lower-layer state is already queued or rebuilding, do not
      // cancel/requeue it. Presentation refreshes can fire more than once per
      // committed stroke; repeatedly replacing the idle job caused generation
      // storms and prevented the cache from ever becoming current.
      if (
        runtime.normalBelowPendingCacheKey === cacheKey
        && (runtime.normalBelowRefreshTimer || runtime.normalBelowRebuildInProgress)
      ) {
        if (runtime.normalBelowTextureByLayerId instanceof Map) {
          applyBelowTextures(runtime.normalBelowTextureByLayerId);
        }
        return;
      }

      // Never perform the UV normal composition inside the dropdown/layer refresh.
      // Debounce it so repeated React presentation passes collapse into one job.
      if (runtime.normalBelowRefreshTimer) {
        if (runtime.normalBelowRefreshUsesIdleCallback && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(runtime.normalBelowRefreshTimer);
        } else {
          clearTimeout(runtime.normalBelowRefreshTimer);
        }
      }
      const generation = Number(runtime.normalBelowGeneration || 0) + 1;
      runtime.normalBelowGeneration = generation;
      runtime.normalBelowPendingCacheKey = cacheKey;
      const runNormalBelowRefresh = () => {
        runtime.normalBelowRefreshTimer = null;
        runtime.normalBelowRefreshUsesIdleCallback = false;
        runtime.normalBelowRebuildInProgress = true;
        if (runtimeRef.current !== runtime || runtime.normalBelowGeneration !== generation) {
          runtime.normalBelowRebuildInProgress = false;
          if (runtime.normalBelowPendingCacheKey === cacheKey) runtime.normalBelowPendingCacheKey = '';
          return;
        }
        const orderedLayerIds = currentLayers.map((layer) => String(layer?.id || ''));
        const orderedVisibleStrokes = getOrderedVisibleProjectionStrokes();
        const nextTextureByLayerId = new Map();
        const nextTextures = [];

        allBelowLayers.forEach((targetLayer) => {
          const targetLayerId = String(targetLayer?.id || '');
          const targetIndex = orderedLayerIds.indexOf(targetLayerId);
          if (targetIndex < 0) return;
          const belowIds = new Set(orderedLayerIds.slice(targetIndex + 1));
          const belowStrokes = orderedVisibleStrokes.filter((stroke) => (
            belowIds.has(String(stroke?.layerId || '')) && stroke?.isPbr === true
          ));
          if (!belowStrokes.length) return;

          // Preserve the exact model-atlas texel grid. The previous 1024px
          // safety resize changed the apparent frequency of the lower material's
          // normal map and softened/shifted rotated details. The expensive work is
          // already deferred and is invalidated only by lower-layer changes, so the
          // correct fix is to render at the canonical normal-atlas resolution.
          const sourceWidth = Math.max(
            1,
            Math.round(Number(
              activePaintSession?.normalCanvas?.width
              || activePaintSession?.canvas?.width
              || 1024
            )),
          );
          const sourceHeight = Math.max(
            1,
            Math.round(Number(
              activePaintSession?.normalCanvas?.height
              || activePaintSession?.canvas?.height
              || sourceWidth
            )),
          );
          const atlasWidth = sourceWidth;
          const atlasHeight = sourceHeight;
          const belowCanvas = renderVisibleProjectionPbrOverlayToCanvas(
            atlasWidth,
            atlasHeight,
            'normal',
            { visibleStrokes: belowStrokes, skipPresentationRefresh: true, gpuComposite: true },
          );
          if (!belowCanvas) return;

          const compositeCanvas = document.createElement('canvas');
          compositeCanvas.width = atlasWidth;
          compositeCanvas.height = atlasHeight;
          const compositeContext = compositeCanvas.getContext('2d', { alpha: false });
          if (!compositeContext) return;
          compositeContext.imageSmoothingEnabled = false;
          compositeContext.fillStyle = 'rgb(128,128,255)';
          compositeContext.fillRect(0, 0, atlasWidth, atlasHeight);
          if (activePaintSession?.normalCanvas) {
            compositeContext.drawImage(activePaintSession.normalCanvas, 0, 0, atlasWidth, atlasHeight);
          }
          compositeContext.drawImage(belowCanvas, 0, 0, atlasWidth, atlasHeight);

          const texture = new THREE.CanvasTexture(compositeCanvas);
          texture.colorSpace = THREE.NoColorSpace;
          texture.flipY = false;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          // This texture already matches the canonical atlas texel-for-texel.
          // Generating a complete mip chain for a 4K/8K CanvasTexture causes a
          // large synchronous GPU upload and was the main source of interaction
          // stalls. Keep the native pixels, but upload only the base level.
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          texture.anisotropy = 1;
          texture.needsUpdate = true;
          nextTextures.push(texture);
          nextTextureByLayerId.set(targetLayerId, texture);
        });

        if (runtimeRef.current !== runtime || runtime.normalBelowGeneration !== generation) {
          nextTextures.forEach((texture) => texture?.dispose?.());
          runtime.normalBelowRebuildInProgress = false;
          if (runtime.normalBelowPendingCacheKey === cacheKey) runtime.normalBelowPendingCacheKey = '';
          return;
        }
        if (Array.isArray(runtime.normalBelowTextures)) {
          runtime.normalBelowTextures.forEach((texture) => texture?.dispose?.());
        }
        runtime.normalBelowTextures = nextTextures;
        runtime.normalBelowTextureByLayerId = nextTextureByLayerId;
        runtime.normalBelowCacheKey = cacheKey;
        runtime.normalBelowPendingCacheKey = '';
        runtime.normalBelowRebuildInProgress = false;
        applyBelowTextures(nextTextureByLayerId);
      };

      // A valid cached texture remains bound while this runs. Rebuild only when
      // the browser has breathing room, rather than in the middle of painting,
      // slider movement or the reload presentation burst.
      if (typeof window.requestIdleCallback === 'function') {
        runtime.normalBelowRefreshUsesIdleCallback = true;
        runtime.normalBelowRefreshTimer = window.requestIdleCallback(
          runNormalBelowRefresh,
          { timeout: 500 },
        );
      } else {
        runtime.normalBelowRefreshUsesIdleCallback = false;
        runtime.normalBelowRefreshTimer = setTimeout(runNormalBelowRefresh, 120);
      }
    };


    const appendLatestEraserToActiveLayerMasks = (visibleStrokes, latestEraser) => {
      const runtime = runtimeRef.current;
      if (!runtime || latestEraser?.isEraser !== true) return false;

      const strokes = Array.isArray(visibleStrokes) ? visibleStrokes : [];
      const latestIndex = strokes.lastIndexOf(latestEraser);
      if (latestIndex < 0) return false;

      const layerId = String(latestEraser.layerId || '');
      if (!layerId) return false;

      const atlasWidth = Math.max(1, Number(activePaintSession?.canvas?.width) || 1);
      const atlasHeight = Math.max(1, Number(activePaintSession?.canvas?.height) || 1);
      const projected = createProjectionStrokeUvMask(latestEraser, atlasWidth, atlasHeight);
      if (!projected) return false;

      const expanded = document.createElement('canvas');
      expanded.width = atlasWidth;
      expanded.height = atlasHeight;
      const expandedContext = expanded.getContext('2d', { alpha: true });
      if (!expandedContext) return false;
      expandedContext.clearRect(0, 0, atlasWidth, atlasHeight);
      for (let offsetY = -2; offsetY <= 2; offsetY += 1) {
        for (let offsetX = -2; offsetX <= 2; offsetX += 1) {
          expandedContext.drawImage(projected, offsetX, offsetY);
        }
      }

      // Convert the new eraser alpha into opaque black once. Reuse this small
      // amount of work for every existing mask texture instead of replaying all
      // historical erasers into several new 4096x4096 canvases.
      const blackMask = document.createElement('canvas');
      blackMask.width = atlasWidth;
      blackMask.height = atlasHeight;
      const blackContext = blackMask.getContext('2d', { alpha: true });
      if (!blackContext) return false;
      blackContext.drawImage(expanded, 0, 0);
      blackContext.globalCompositeOperation = 'source-in';
      blackContext.fillStyle = '#000000';
      blackContext.fillRect(0, 0, atlasWidth, atlasHeight);
      blackContext.globalCompositeOperation = 'source-over';

      if (!(runtime.activeLayerEraserMaskTextures instanceof Set)) {
        runtime.activeLayerEraserMaskTextures = new Set();
      }
      const ownedTextures = runtime.activeLayerEraserMaskTextures;
      const updatedTextures = new Set();
      let newSharedTexture = null;
      let visitedStrokeCount = 0;
      let visitedMeshCount = 0;
      let visitedMaterialCount = 0;
      let appendedTextureCount = 0;
      let assignedSharedTextureCount = 0;

      const createWhiteMaskTexture = () => {
        const canvas = document.createElement('canvas');
        canvas.width = atlasWidth;
        canvas.height = atlasHeight;
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) return null;
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, atlasWidth, atlasHeight);
        context.drawImage(blackMask, 0, 0);

        const texture = new THREE.CanvasTexture(canvas);
        texture.name = `Voxemera active-layer incremental erase mask ${layerId}`;
        texture.colorSpace = THREE.NoColorSpace;
        texture.flipY = false;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        texture.needsUpdate = true;
        ownedTextures.add(texture);
        return texture;
      };

      const appendToTexture = (texture) => {
        if (!texture || updatedTextures.has(texture)) return texture;
        const canvas = texture.image;
        const context = canvas?.getContext?.('2d', { alpha: false });
        if (!context || canvas.width !== atlasWidth || canvas.height !== atlasHeight) return null;
        context.drawImage(blackMask, 0, 0);
        texture.needsUpdate = true;
        updatedTextures.add(texture);
        appendedTextureCount += 1;
        return texture;
      };

      const applyLatestMask = (stroke) => {
        if (!stroke || stroke.isEraser === true || String(stroke.layerId || '') !== layerId) return;
        visitedStrokeCount += 1;
        (stroke.meshes || []).forEach((mesh) => {
          visitedMeshCount += 1;
          const materials = Array.isArray(mesh?.material) ? mesh.material : [mesh?.material];
          materials.filter(Boolean).forEach((material) => {
            visitedMaterialCount += 1;
            let texture = ownedTextures.has(material.alphaMap)
              ? appendToTexture(material.alphaMap)
              : null;
            if (!texture) {
              if (!newSharedTexture) newSharedTexture = createWhiteMaskTexture();
              texture = newSharedTexture;
              if (texture) assignedSharedTextureCount += 1;
            }
            if (!texture) return;
            material.alphaMap = texture;
            material.alphaTest = 0.01;
            material.transparent = true;
            material.userData = {
              ...(material.userData || {}),
              voxemeraActiveLayerEraserMask: true,
            };
            material.needsUpdate = true;
            // Keep the current destructive layer result directly on the paint
            // stroke. Persistence can restore this finished alpha state without
            // replaying historical eraser actions on page load.
            if (texture?.image?.getContext) {
              stroke.bakedEraserMaskCanvas = texture.image;
            }
          });
        });
      };

      // Only strokes before the newly committed eraser can be affected by it.
      for (let index = 0; index < latestIndex; index += 1) {
        applyLatestMask(strokes[index]);
      }

      const layerDecals = runtime.layerDecals instanceof Map ? runtime.layerDecals : new Map();
      const decalRecord = layerDecals.get(layerId);
      if (decalRecord?.projectedStroke) applyLatestMask(decalRecord.projectedStroke);

      runtime.activeLayerEraserMaskSignature = `${strokes.map((stroke, index) => (
        stroke?.isEraser === true
          ? `${index}:${String(stroke.layerId || '')}:${stroke.sourceCanvas?.width || 0}x${stroke.sourceCanvas?.height || 0}`
          : '-'
      )).join('|')}::${Array.from(layerDecals.entries()).map(([entryLayerId, record]) => (
        `${String(entryLayerId || '')}:${String(record?.projectedStroke?.meshes?.[0]?.uuid || '')}`
      )).join('|')}`;
      return true;
    };

    const rebuildActiveLayerEraserMasks = (visibleStrokes) => {
      const runtime = runtimeRef.current;
      if (!runtime) return;

      const strokes = Array.isArray(visibleStrokes) ? visibleStrokes : [];
      const layerDecals = runtime.layerDecals instanceof Map ? runtime.layerDecals : new Map();
      const signature = `${strokes.map((stroke, index) => (
        stroke?.isEraser === true
          ? `${index}:${String(stroke.layerId || '')}:${stroke.sourceCanvas?.width || 0}x${stroke.sourceCanvas?.height || 0}`
          : '-'
      )).join('|')}::${Array.from(layerDecals.entries()).map(([layerId, record]) => (
        `${String(layerId || '')}:${String(record?.projectedStroke?.meshes?.[0]?.uuid || '')}`
      )).join('|')}`;
      if (runtime.activeLayerEraserMaskSignature === signature) {
        return;
      }

      // Restored projects already contain the finished destructive alpha state
      // on each paint stroke. With no live eraser actions to replay, preserve
      // those masks instead of clearing and rebuilding them.
      if (!strokes.some((stroke) => stroke?.isEraser === true)
          && strokes.some((stroke) => Boolean(stroke?.bakedEraserMaskCanvas))) {
        runtime.activeLayerEraserMaskSignature = signature;
        return;
      }

      const oldTextures = runtime.activeLayerEraserMaskTextures instanceof Set
        ? runtime.activeLayerEraserMaskTextures
        : new Set();

      const clearOwnedMask = (stroke) => {
        if (stroke) stroke.bakedEraserMaskCanvas = null;
        (stroke?.meshes || []).forEach((mesh) => {
          const materials = Array.isArray(mesh?.material) ? mesh.material : [mesh?.material];
          materials.filter(Boolean).forEach((material) => {
            if (oldTextures.has(material.alphaMap)) material.alphaMap = null;
            material.alphaTest = 0;
            if (material.userData) delete material.userData.voxemeraActiveLayerEraserMask;
            material.needsUpdate = true;
          });
        });
      };

      (runtime.projectionStrokes || []).forEach(clearOwnedMask);
      layerDecals.forEach((record) => clearOwnedMask(record?.projectedStroke));
      oldTextures.forEach((texture) => texture?.dispose?.());

      runtime.activeLayerEraserMaskTextures = new Set();
      runtime.activeLayerEraserMaskSignature = signature;
      if (!strokes.some((stroke) => stroke?.isEraser === true)) return;

      const atlasWidth = Math.max(1, Number(activePaintSession?.canvas?.width) || 1);
      const atlasHeight = Math.max(1, Number(activePaintSession?.canvas?.height) || 1);
      const eraserCanvasCache = new Map();
      const inverseTextureCache = new Map();

      const getExpandedEraserCanvas = (eraserStroke) => {
        if (eraserCanvasCache.has(eraserStroke)) return eraserCanvasCache.get(eraserStroke);
        const projected = createProjectionStrokeUvMask(eraserStroke, atlasWidth, atlasHeight);
        if (!projected) {
          eraserCanvasCache.set(eraserStroke, null);
          return null;
        }

        // Close only tiny UV-triangle raster cracks with inexpensive canvas
        // compositing. This replaces the previous full-atlas multi-pass pixel
        // scans that caused pointer-up hangs.
        const expanded = document.createElement('canvas');
        expanded.width = atlasWidth;
        expanded.height = atlasHeight;
        const context = expanded.getContext('2d', { alpha: true });
        if (!context) {
          eraserCanvasCache.set(eraserStroke, projected);
          return projected;
        }
        context.clearRect(0, 0, atlasWidth, atlasHeight);
        for (let offsetY = -2; offsetY <= 2; offsetY += 1) {
          for (let offsetX = -2; offsetX <= 2; offsetX += 1) {
            context.drawImage(projected, offsetX, offsetY);
          }
        }
        eraserCanvasCache.set(eraserStroke, expanded);
        return expanded;
      };

      const getInverseMaskTexture = (erasers, cacheKey) => {
        const cached = inverseTextureCache.get(cacheKey);
        if (cached) return cached;

        const inverseCanvas = document.createElement('canvas');
        inverseCanvas.width = atlasWidth;
        inverseCanvas.height = atlasHeight;
        const inverseContext = inverseCanvas.getContext('2d', { alpha: false });
        if (!inverseContext) return null;
        inverseContext.fillStyle = '#ffffff';
        inverseContext.fillRect(0, 0, atlasWidth, atlasHeight);

        erasers.forEach((eraserStroke) => {
          const eraserCanvas = getExpandedEraserCanvas(eraserStroke);
          if (!eraserCanvas) return;

          // alphaMap reads the green channel. Convert the eraser's alpha to
          // opaque black without reading the entire 4K atlas back to JavaScript.
          const blackMask = document.createElement('canvas');
          blackMask.width = atlasWidth;
          blackMask.height = atlasHeight;
          const blackContext = blackMask.getContext('2d', { alpha: true });
          if (!blackContext) return;
          blackContext.drawImage(eraserCanvas, 0, 0);
          blackContext.globalCompositeOperation = 'source-in';
          blackContext.fillStyle = '#000000';
          blackContext.fillRect(0, 0, atlasWidth, atlasHeight);
          blackContext.globalCompositeOperation = 'source-over';
          inverseContext.drawImage(blackMask, 0, 0);
        });

        const texture = new THREE.CanvasTexture(inverseCanvas);
        texture.name = `Voxemera active-layer erase mask ${cacheKey}`;
        texture.colorSpace = THREE.NoColorSpace;
        texture.flipY = false;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        texture.needsUpdate = true;
        inverseTextureCache.set(cacheKey, texture);
        runtime.activeLayerEraserMaskTextures.add(texture);
        return texture;
      };

      const applyMask = (stroke, texture) => {
        if (!stroke || !texture) return;
        (stroke.meshes || []).forEach((mesh) => {
          const materials = Array.isArray(mesh?.material) ? mesh.material : [mesh?.material];
          materials.filter(Boolean).forEach((material) => {
            material.alphaMap = texture;
            material.alphaTest = 0.01;
            material.transparent = true;
            material.userData = {
              ...(material.userData || {}),
              voxemeraActiveLayerEraserMask: true,
            };
            material.needsUpdate = true;
          });
        });
        if (texture?.image?.getContext) stroke.bakedEraserMaskCanvas = texture.image;
      };

      strokes.forEach((stroke, strokeIndex) => {
        if (!stroke || stroke.isEraser === true) return;
        const layerId = String(stroke.layerId || '');
        if (!layerId) return;
        const laterErasers = [];
        const laterIndices = [];
        for (let index = strokeIndex + 1; index < strokes.length; index += 1) {
          const candidate = strokes[index];
          if (candidate?.isEraser !== true || String(candidate.layerId || '') !== layerId) continue;
          laterErasers.push(candidate);
          laterIndices.push(index);
        }
        if (!laterErasers.length) return;
        applyMask(stroke, getInverseMaskTexture(laterErasers, `${layerId}:${laterIndices.join(',')}`));
      });

      layerDecals.forEach((record, layerIdValue) => {
        const projectedStroke = record?.projectedStroke;
        if (!projectedStroke) return;
        const layerId = String(layerIdValue || projectedStroke.layerId || '');
        const erasers = [];
        const indices = [];
        strokes.forEach((candidate, index) => {
          if (candidate?.isEraser !== true || String(candidate.layerId || '') !== layerId) return;
          erasers.push(candidate);
          indices.push(index);
        });
        if (!erasers.length) return;
        applyMask(projectedStroke, getInverseMaskTexture(erasers, `decal:${layerId}:${indices.join(',')}`));
      });
    };

    const scheduleActiveLayerEraserMaskRebuild = (visibleStrokes) => {
      const runtime = runtimeRef.current;
      if (!runtime) return;

      const strokesSnapshot = Array.isArray(visibleStrokes) ? visibleStrokes.slice() : [];
      const hasErasers = strokesSnapshot.some((stroke) => stroke?.isEraser === true);

      if (!hasErasers) {
        if (runtime.activeLayerEraserMaskRebuildTimer) {
          clearTimeout(runtime.activeLayerEraserMaskRebuildTimer);
          runtime.activeLayerEraserMaskRebuildTimer = null;
        }
        if (runtime.activeLayerEraserMaskRebuildIdleHandle != null
          && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(runtime.activeLayerEraserMaskRebuildIdleHandle);
          runtime.activeLayerEraserMaskRebuildIdleHandle = null;
        }
        rebuildActiveLayerEraserMasks(strokesSnapshot);
        return;
      }

      runtime.pendingActiveLayerEraserMaskStrokes = strokesSnapshot;
      runtime.activeLayerEraserMaskRebuildGeneration =
        (Number(runtime.activeLayerEraserMaskRebuildGeneration) || 0) + 1;
      const generation = runtime.activeLayerEraserMaskRebuildGeneration;

      if (runtime.activeLayerEraserMaskRebuildTimer) {
        clearTimeout(runtime.activeLayerEraserMaskRebuildTimer);
      }
      if (runtime.activeLayerEraserMaskRebuildIdleHandle != null
        && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(runtime.activeLayerEraserMaskRebuildIdleHandle);
        runtime.activeLayerEraserMaskRebuildIdleHandle = null;
      }


      runtime.activeLayerEraserMaskRebuildTimer = setTimeout(() => {
        runtime.activeLayerEraserMaskRebuildTimer = null;

        const runRebuild = () => {
          runtime.activeLayerEraserMaskRebuildIdleHandle = null;
          if (generation !== runtime.activeLayerEraserMaskRebuildGeneration) {
            return;
          }

          const latestStrokes = Array.isArray(runtime.pendingActiveLayerEraserMaskStrokes)
            ? runtime.pendingActiveLayerEraserMaskStrokes
            : strokesSnapshot;
          rebuildActiveLayerEraserMasks(latestStrokes);
          renderer.render(scene, camera);
        };

        // Let the model, its base materials and restored paint meshes reach the
        // screen before reconstructing historical eraser masks. Restore causes
        // several presentation passes; this coalesces all of them into one job.
        if (typeof window.requestIdleCallback === 'function') {
          runtime.activeLayerEraserMaskRebuildIdleHandle = window.requestIdleCallback(
            runRebuild,
            { timeout: 750 },
          );
        } else {
          runtime.activeLayerEraserMaskRebuildTimer = setTimeout(runRebuild, 0);
        }
      }, 250);
    };

    const refreshProjectionLayerPresentation = () => {
      refreshLayerNormalMixing();
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const visibleCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
      const visibleStrokes = strokes.slice(0, visibleCount);
      const layerDecals = runtime?.layerDecals instanceof Map ? runtime.layerDecals : new Map();
      const { layers: currentLayers } = getCurrentLayerState();
      const bottomToTopLayerIds = currentLayers
        .slice()
        .reverse()
        .map((layer) => String(layer?.id || ''));
      const knownLayerIds = new Set(bottomToTopLayerIds);
      scheduleActiveLayerEraserMaskRebuild(visibleStrokes);
      scheduleEdgeWearDebugReport('refreshProjectionLayerPresentation');
      let renderOrderCursor = 1000;

      const applyStrokePresentation = (stroke, historyVisible, layerInfo) => {
        const visible = Boolean(historyVisible && layerInfo.visible && stroke?.isEraser !== true);
        (stroke?.meshes || []).forEach((mesh) => {
          if (!mesh) return;
          mesh.visible = visible;
          if (visible) {
            mesh.renderOrder = renderOrderCursor;
          }
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach((material) => {
            if (!material) return;
            if (!Number.isFinite(material.userData?.voxemeraBaseLayerOpacity)) {
              material.userData = {
                ...(material.userData || {}),
                voxemeraBaseLayerOpacity: Number.isFinite(material.opacity) ? material.opacity : 1,
              };
            }
            material.transparent = true;
            material.opacity = material.userData.voxemeraBaseLayerOpacity * layerInfo.opacity;
            const uniforms = material.userData?.voxemeraProjectionUniforms;
            if (uniforms?.edgeWear) uniforms.edgeWear.value = layerInfo.edgeWear;
            material.depthWrite = false;
            material.needsUpdate = true;
          });
        });
        if (visible) renderOrderCursor += 1;
      };

      const applyDecalPresentation = (record, layerInfo) => {
        const visible = Boolean(layerInfo.visible && record?.projectedStroke);
        (record?.projectedStroke?.meshes || []).forEach((mesh) => {
          if (!mesh) return;
          mesh.visible = visible;
          if (visible) {
            mesh.renderOrder = renderOrderCursor;
          }
          const uniforms = mesh.material?.userData?.voxemeraProjectionUniforms;
          if (uniforms?.decalOpacity) {
            uniforms.decalOpacity.value = layerInfo.opacity;
          }
          if (uniforms?.edgeWear) uniforms.edgeWear.value = layerInfo.edgeWear;
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach((material) => {
            if (!material) return;
            material.transparent = true;
            material.depthWrite = false;
            material.needsUpdate = true;
          });
        });
        if (visible) renderOrderCursor += 1;
      };

      strokes.forEach((stroke, strokeIndex) => {
        if (strokeIndex < visibleCount) return;
        applyStrokePresentation(stroke, false, getProjectionLayerInfo(stroke?.layerId));
      });

      layerDecals.forEach((record, layerId) => {
        if (knownLayerIds.has(String(layerId || ''))) return;
        applyDecalPresentation(record, { visible: false, opacity: 1 });
      });

      bottomToTopLayerIds.forEach((layerId) => {
        const layerInfo = getProjectionLayerInfo(layerId);
        visibleStrokes.forEach((stroke) => {
          if (String(stroke?.layerId || '') !== layerId) return;
          applyStrokePresentation(stroke, true, layerInfo);
        });
        const decalRecord = layerDecals.get(layerId);
        if (decalRecord) applyDecalPresentation(decalRecord, layerInfo);
      });

      visibleStrokes.forEach((stroke) => {
        const layerId = String(stroke?.layerId || '');
        if (knownLayerIds.has(layerId)) return;
        applyStrokePresentation(stroke, true, getProjectionLayerInfo(layerId));
      });

      layerDecals.forEach((record, layerId) => {
        const safeLayerId = String(layerId || '');
        if (knownLayerIds.has(safeLayerId)) return;
        applyDecalPresentation(record, getProjectionLayerInfo(safeLayerId));
      });
    };

    const applyPaintMaterial = (texture) => {
      const profiler = loadProfilerRef.current;
      profiler?.mark('Paint material assignment: start', 'Traversing every mesh and assigning the shared paint texture.');
      const model = currentModelRef.current;
      if (!model || !texture) return;
      model.traverse((child) => {
        if (!child?.isMesh) return;

        // Projected paint meshes own their specialised camera-projection shader.
        // Replacing that material with the ordinary UV-atlas material makes the
        // stroke disappear from the 3D model even though it remains in the
        // derived GPU/Texture view.
        if (child.userData?.voxemeraProjectionDecal) return;

        if (!child.userData.voxemeraSourceMaterial) child.userData.voxemeraSourceMaterial = child.material;
        if (!child.userData.voxemeraPaintMaterial) {
          child.userData.voxemeraPaintMaterial = new THREE.MeshStandardMaterial({
            map: texture,
            color: 0xffffff,
            roughness: 0.82,
            metalness: 0,
            side: THREE.DoubleSide,
          });
        }
        child.userData.voxemeraPaintMaterial.map = texture;
        const packedMaterialTexture = activePaintSession?.materialTexture || null;
        const packedNormalTexture = activePaintSession?.normalTexture || null;
        child.userData.voxemeraPaintMaterial.roughness = packedMaterialTexture ? 1 : 0.82;
        child.userData.voxemeraPaintMaterial.metalness = packedMaterialTexture ? 1 : 0;
        child.userData.voxemeraPaintMaterial.roughnessMap = packedMaterialTexture;
        child.userData.voxemeraPaintMaterial.metalnessMap = packedMaterialTexture;
        child.userData.voxemeraPaintMaterial.bumpMap = packedMaterialTexture;
        child.userData.voxemeraPaintMaterial.bumpScale = packedMaterialTexture ? 0.08 : 0;
        child.userData.voxemeraPaintMaterial.normalMap = packedNormalTexture;
        child.userData.voxemeraPaintMaterial.normalScale = packedNormalTexture ? new THREE.Vector2(1, 1) : new THREE.Vector2(0, 0);
        const paintMaterial = child.userData.voxemeraPaintMaterial;
        child.material = paintMaterial;
        child.material.needsUpdate = true;
      });
      profiler?.mark('Paint material assignment: complete', 'All mesh materials now reference the UV paint texture. Shader compilation/upload may still occur on the next render.');
    };

    const clearGpuPaintMap = (session = activePaintSession) => {
      const target = session?.gpuPaintTarget;
      if (!target) return false;
      const previousTarget = renderer.getRenderTarget();
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, false, false);
      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(previousClearColor, previousClearAlpha);
      session.gpuPaintInitialized = true;
      return true;
    };

    const getPbrRuntimeTextures = (resource) => {
      if (!resource) return null;
      if (resource.threeTextures) return resource.threeTextures;
      const makeTexture = (canvas, colorSpace) => {
        if (!canvas) return null;
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = colorSpace;
        texture.flipY = true;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = true;
        texture.needsUpdate = true;
        texture.userData = { ...(texture.userData || {}), voxemeraSharedTexture: true };
        return texture;
      };
      resource.threeTextures = {
        albedo: makeTexture(resource.albedoCanvas, THREE.SRGBColorSpace),
        material: makeTexture(resource.packedMaterialCanvas, THREE.NoColorSpace),
        normal: makeTexture(resource.normalCanvas, THREE.NoColorSpace),
      };
      return resource.threeTextures;
    };

    const renderProjectionStrokeToGpuPaintMap = (stroke, clearFirst = false) => runWithSelection(
      selectionFromSnapshot(stroke?.selectionSnapshot),
      () => renderProjectionStrokeToGpuPaintMapWithActiveSelection(stroke, clearFirst),
    );

    const renderProjectionStrokeToGpuPaintMapWithActiveSelection = (stroke, clearFirst = false) => {
      const target = activePaintSession?.gpuPaintTarget;
      if (!target || !stroke) {
        return false;
      }
      if (clearFirst || !activePaintSession.gpuPaintInitialized) {
        clearGpuPaintMap(activePaintSession);
      }

      const bakeScene = new THREE.Scene();
      const bakeCamera = new THREE.Camera();
      bakeCamera.position.z = 1;
      const temporaryMaterials = [];
      let meshCount = 0;

      const liveModel = currentModelRef.current;
      const temporaryGeometries = [];

      (stroke.meshes || []).forEach((decalMesh) => {
        // Projection decals keep a cloned selection geometry from the moment the
        // stroke was created. That geometry can contain the previous UV layout
        // after switching between Original UVs and Generated UVs. The viewport
        // still looks correct because the decal shader is projector-mapped, but
        // baking through that stale clone writes the stroke into the wrong atlas
        // islands. Rebuild the bake geometry from the decal's live source mesh so
        // the export always uses the UV layout currently shown in Voxemera.
        const sourceMeshUuid = String(decalMesh?.userData?.voxemeraPaintRegion?.sourceMeshUuid || '');

        const liveSourceMesh = sourceMeshUuid && liveModel?.getObjectByProperty
          ? liveModel.getObjectByProperty('uuid', sourceMeshUuid)
          : null;

        const allowedFaces = liveSourceMesh
          ? (stroke?.allowedFaceIndicesByMesh?.get?.(liveSourceMesh.uuid) || null)
          : null;

        // The GPU UV-atlas bake only needs position/normal/UV data. Do not
        // generate edge-wear topology here: ensureStableEdgeWearAttributes() is
        // intentionally deferred from model load because it can be very expensive
        // on large meshes. Calling createSelectionGeometry() with its default
        // includeEdgeWear=true moved that one-time cost onto the first paint commit,
        // causing the several-second UI freeze immediately after the first stroke.
        const liveSelectionGeometry = liveSourceMesh
          ? createSelectionGeometry(liveSourceMesh, allowedFaces, false)
          : null;

        const geometry = liveSelectionGeometry || liveSourceMesh?.geometry || decalMesh?.geometry;
        if (liveSelectionGeometry && liveSelectionGeometry !== liveSourceMesh?.geometry) {
          temporaryGeometries.push(liveSelectionGeometry);
        }
        const uv = geometry?.getAttribute?.('uv');
        if (!geometry || !uv || uv.count < 3) return;

        if (stroke.kind === 'uvFloodFill') {
          const fillMap = stroke.strokeTexture;
          if (!fillMap) return;
          let material;
          if (stroke.isPbr) {
            const savedPbrSetKey = String(stroke?.brushConfig?.pbrSetKey || '');
            const pbrResource = savedPbrSetKey
              ? (pbrSetResourcesRef.current.get(savedPbrSetKey) || null)
              : getRuntimeActivePbrBrushResource();
            const pbrTextures = pbrResource ? getPbrRuntimeTextures(pbrResource) : null;
            const model = currentModelRef.current;
            const pbrWorldBounds = new THREE.Box3().setFromObject(model);
            const pbrWorldOrigin = pbrWorldBounds.isEmpty()
              ? new THREE.Vector3()
              : pbrWorldBounds.min.clone();
            const pbrWorldDimensions = pbrWorldBounds.isEmpty()
              ? new THREE.Vector3(1, 1, 1)
              : pbrWorldBounds.getSize(new THREE.Vector3());
            const pbrWorldTileSize = Math.max(
              0.00001,
              Math.max(pbrWorldDimensions.x, pbrWorldDimensions.y, pbrWorldDimensions.z, 0.001) * 0.12,
            );
            material = new THREE.ShaderMaterial({
              uniforms: {
                fillMap: { value: fillMap },
                pbrAlbedoMap: { value: pbrTextures?.albedo || fillMap },
                pbrHasAlbedo: { value: pbrTextures?.albedo ? 1 : 0 },
                pbrScale: { value: Math.max(0.1, Math.min(8, (Number(stroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100)) },
                pbrAngle: { value: THREE.MathUtils.degToRad(Number(stroke?.brushConfig?.pbrAngleDegrees) || 0) },
                pbrWorldOrigin: { value: pbrWorldOrigin },
                pbrWorldTileSize: { value: pbrWorldTileSize },
              },
              vertexShader: `
                varying vec2 vUv;
                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;
                void main() {
                  vUv = uv;
                  vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                  vWorldNormal = normalize(mat3(modelMatrix) * normal);
                  gl_Position = vec4((uv.x * 2.0) - 1.0, (uv.y * 2.0) - 1.0, 0.0, 1.0);
                }
              `,
              fragmentShader: `
                precision highp float;
                uniform sampler2D fillMap;
                uniform sampler2D pbrAlbedoMap;
                uniform float pbrHasAlbedo;
                uniform float pbrScale;
                uniform float pbrAngle;
                uniform vec3 pbrWorldOrigin;
                uniform float pbrWorldTileSize;
                varying vec2 vUv;
                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;

                vec2 rotateUv(vec2 uv) {
                  float c = cos(pbrAngle);
                  float s = sin(pbrAngle);
                  return mat2(c, -s, s, c) * uv;
                }

                vec3 pbrWeights() {
                  vec3 weights = pow(abs(normalize(vWorldNormal)), vec3(6.0));
                  return weights / max(weights.x + weights.y + weights.z, 0.0001);
                }

                vec3 pbrCoords() {
                  return (vWorldPosition - pbrWorldOrigin)
                    / max(pbrWorldTileSize * pbrScale, 0.00001);
                }

                vec3 samplePbrRgb(sampler2D sourceMap) {
                  vec3 coords = pbrCoords();
                  vec3 weights = pbrWeights();
                  vec2 uvX = fract(rotateUv(coords.zy));
                  vec2 uvY = fract(rotateUv(coords.xz));
                  vec2 uvZ = fract(rotateUv(coords.xy));
                  return (texture2D(sourceMap, uvX).rgb * weights.x)
                    + (texture2D(sourceMap, uvY).rgb * weights.y)
                    + (texture2D(sourceMap, uvZ).rgb * weights.z);
                }

                void main() {
                  vec4 paint = texture2D(fillMap, vUv);
                  if (paint.a <= 0.001) discard;
                  vec3 albedo = pbrHasAlbedo > 0.5 ? samplePbrRgb(pbrAlbedoMap) : paint.rgb;
                  gl_FragColor = vec4(albedo, paint.a);
                }
              `,
              transparent: true,
              blending: THREE.NormalBlending,
              depthTest: false,
              depthWrite: false,
              side: THREE.DoubleSide,
              toneMapped: false,
            });
          } else {
            material = new THREE.ShaderMaterial({
              uniforms: { fillMap: { value: fillMap } },
              vertexShader: `
                varying vec2 vUv;
                void main() {
                  vUv = uv;
                  gl_Position = vec4((uv.x * 2.0) - 1.0, (uv.y * 2.0) - 1.0, 0.0, 1.0);
                }
              `,
              fragmentShader: `
                precision highp float;
                uniform sampler2D fillMap;
                varying vec2 vUv;
                void main() {
                  vec4 paint = texture2D(fillMap, vUv);
                  if (paint.a <= 0.001) discard;
                  gl_FragColor = paint;
                }
              `,
              transparent: true,
              blending: THREE.NormalBlending,
              depthTest: false,
              depthWrite: false,
              side: THREE.DoubleSide,
              toneMapped: false,
            });
          }
          const bakeMesh = new THREE.Mesh(geometry, material);
          bakeMesh.position.copy(decalMesh.position);
          bakeMesh.quaternion.copy(decalMesh.quaternion);
          bakeMesh.scale.copy(decalMesh.scale);
          bakeMesh.frustumCulled = false;
          bakeMesh.renderOrder = Number(decalMesh.renderOrder) || 0;
          bakeScene.add(bakeMesh);
          temporaryMaterials.push(material);
          meshCount += 1;
          return;
        }

        const projectionUniforms = decalMesh?.material?.userData?.voxemeraProjectionUniforms;
        if (!projectionUniforms) return;
        const isPbrBake = stroke?.isPbr === true && Number(projectionUniforms?.pbrHasAlbedo?.value || 0) > 0.5;
        const bakeUniforms = {
          strokeMap: projectionUniforms.strokeMap,
          projectorDepthMap: projectionUniforms.projectorDepthMap,
          projectorMatrix: projectionUniforms.projectorMatrix,
          projectorCameraLocal: projectionUniforms.projectorCameraLocal,
          depthTolerance: projectionUniforms.depthTolerance,
          decalOpacity: projectionUniforms.decalOpacity,
          incidenceAlpha: projectionUniforms.incidenceAlpha || { value: 0 },
          strokeOpacity: projectionUniforms.strokeOpacity || { value: 1 },
          projectionRect: projectionUniforms.projectionRect,
          penColour: projectionUniforms.penColour,
          penSoftnessActive: projectionUniforms.penSoftnessActive || { value: 0 },
          pbrAlbedoMap: projectionUniforms.pbrAlbedoMap,
          pbrHasAlbedo: projectionUniforms.pbrHasAlbedo || { value: 0 },
          pbrScale: projectionUniforms.pbrScale || { value: 1 },
          pbrAngle: projectionUniforms.pbrAngle || { value: 0 },
          pbrWorldOrigin: projectionUniforms.pbrWorldOrigin || { value: new THREE.Vector3() },
          pbrWorldTileSize: projectionUniforms.pbrWorldTileSize || { value: 1 },
          sourceWorldMatrix: { value: (liveSourceMesh?.matrixWorld || decalMesh.matrixWorld).clone() },
        };
        const material = new THREE.ShaderMaterial({
          uniforms: bakeUniforms,
          vertexShader: `
            uniform mat4 projectorMatrix;
            uniform mat4 sourceWorldMatrix;
            varying vec4 vProjectedPosition;
            varying vec3 vLocalPosition;
            varying vec3 vLocalNormal;
            varying vec3 vPbrWorldPosition;
            varying vec3 vPbrWorldNormal;
            void main() {
              vLocalPosition = position;
              vLocalNormal = normal;
              vPbrWorldPosition = (sourceWorldMatrix * vec4(position, 1.0)).xyz;
              vPbrWorldNormal = normalize(mat3(sourceWorldMatrix) * normal);
              vProjectedPosition = projectorMatrix * vec4(position, 1.0);
              gl_Position = vec4((uv.x * 2.0) - 1.0, (uv.y * 2.0) - 1.0, 0.0, 1.0);
            }
          `,
          fragmentShader: `
            precision highp float;
            uniform sampler2D strokeMap;
            uniform sampler2D projectorDepthMap;
            uniform sampler2D pbrAlbedoMap;
            uniform vec3 projectorCameraLocal;
            uniform float depthTolerance;
            uniform float decalOpacity;
            uniform float incidenceAlpha;
            uniform float strokeOpacity;
            uniform vec4 projectionRect;
            uniform vec3 penColour;
            uniform float penSoftnessActive;
            uniform float pbrHasAlbedo;
            uniform float pbrScale;
            uniform float pbrAngle;
            uniform vec3 pbrWorldOrigin;
            uniform float pbrWorldTileSize;
            varying vec4 vProjectedPosition;
            varying vec3 vLocalPosition;
            varying vec3 vLocalNormal;
            varying vec3 vPbrWorldPosition;
            varying vec3 vPbrWorldNormal;

            vec2 rotatePbrUv(vec2 uv) {
              float c = cos(pbrAngle);
              float s = sin(pbrAngle);
              return mat2(c, -s, s, c) * uv;
            }

            vec3 pbrWeights() {
              vec3 weights = pow(abs(normalize(vPbrWorldNormal)), vec3(6.0));
              return weights / max(weights.x + weights.y + weights.z, 0.0001);
            }

            vec3 samplePbrAlbedo() {
              vec3 coords = (vPbrWorldPosition - pbrWorldOrigin)
                / max(pbrWorldTileSize * pbrScale, 0.00001);
              vec3 weights = pbrWeights();
              vec2 uvX = fract(rotatePbrUv(coords.zy));
              vec2 uvY = fract(rotatePbrUv(coords.xz));
              vec2 uvZ = fract(rotatePbrUv(coords.xy));
              return (texture2D(pbrAlbedoMap, uvX).rgb * weights.x)
                + (texture2D(pbrAlbedoMap, uvY).rgb * weights.y)
                + (texture2D(pbrAlbedoMap, uvZ).rgb * weights.z);
            }

            void main() {
              if (vProjectedPosition.w <= 0.0) discard;
              vec3 projected = vProjectedPosition.xyz / vProjectedPosition.w;
              vec2 projectorUv = projected.xy * 0.5 + 0.5;
              if (projectorUv.x <= 0.0 || projectorUv.x >= 1.0 || projectorUv.y <= 0.0 || projectorUv.y >= 1.0) discard;
              float fragmentDepth = projected.z * 0.5 + 0.5;
              float visibleDepth = texture2D(projectorDepthMap, projectorUv).x;
              float tolerance = max(depthTolerance, fwidth(fragmentDepth) * 0.35);
              if (abs(fragmentDepth - visibleDepth) > tolerance) discard;
              vec2 strokeUv = (projectorUv - projectionRect.xy) / projectionRect.zw;
              if (strokeUv.x <= 0.0 || strokeUv.x >= 1.0 || strokeUv.y <= 0.0 || strokeUv.y >= 1.0) discard;
              vec4 paint = texture2D(strokeMap, strokeUv);
              vec3 projectorDirection = normalize(projectorCameraLocal - vLocalPosition);
              float incidence = abs(dot(normalize(vLocalNormal), projectorDirection));
              // Threshold the stroke's own coverage first, then apply opacity.
              // Thresholding after opacity made pens under 45% bake to nothing
              // and everything above bake fully opaque. Textured brushes
              // (airbrush, pencil, imported) keep their real alpha, matching
              // the live projection, instead of baking as hard blobs.
              float coverage = paint.a * smoothstep(0.22, 0.52, incidence);
              if (penSoftnessActive > 0.5 || incidenceAlpha > 0.5) {
                if (coverage <= 0.001) discard;
              } else {
                if (coverage < 0.45) discard;
                coverage = 1.0;
              }
              paint.a = coverage * decalOpacity * strokeOpacity;
              if (paint.a <= 0.001) discard;

              vec3 exportedColour = ${isPbrBake ? 'samplePbrAlbedo()' : 'penColour'};
              gl_FragColor = vec4(exportedColour, paint.a);
            }
          `,
          transparent: true,
          blending: THREE.NormalBlending,
          depthTest: false,
          depthWrite: false,
          side: THREE.DoubleSide,
          toneMapped: false,
        });
        const bakeMesh = new THREE.Mesh(geometry, material);
        if (liveSourceMesh) {
          bakeMesh.position.copy(liveSourceMesh.position);
          bakeMesh.quaternion.copy(liveSourceMesh.quaternion);
          bakeMesh.scale.copy(liveSourceMesh.scale);
        } else {
          bakeMesh.position.copy(decalMesh.position);
          bakeMesh.quaternion.copy(decalMesh.quaternion);
          bakeMesh.scale.copy(decalMesh.scale);
        }
        bakeMesh.frustumCulled = false;
        bakeScene.add(bakeMesh);
        temporaryMaterials.push(material);
        meshCount += 1;
      });

      if (!meshCount) {
        return false;
      }
      const previousTarget = renderer.getRenderTarget();
      const previousToneMapping = renderer.toneMapping;
      const previousAutoClear = renderer.autoClear;
      renderer.setRenderTarget(target);
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.autoClear = false;
      renderer.render(bakeScene, bakeCamera);
      renderer.autoClear = previousAutoClear;
      renderer.toneMapping = previousToneMapping;
      renderer.setRenderTarget(previousTarget);
      temporaryMaterials.forEach((material) => material.dispose());
      temporaryGeometries.forEach((geometry) => geometry?.dispose?.());
      activePaintSession.gpuPaintInitialized = true;
      return true;
    };

    const rebuildGpuPaintMap = () => {
      if (!activePaintSession?.gpuPaintTarget) return false;
      clearGpuPaintMap(activePaintSession);
      const orderedStrokes = getOrderedVisibleProjectionStrokes();
      for (let index = 0; index < orderedStrokes.length; index += 1) {
        renderProjectionStrokeToGpuPaintMap(orderedStrokes[index], false);
      }
      refreshProjectionLayerPresentation();
      refreshVisibleMaterial();
      return true;
    };

    const updateModelEdgeOverlay = (visible = false) => {
      const model = currentModelRef.current;
      if (!model) return;

      const enabled = Boolean(visible);
      const debugEnabled = enabled && Boolean(viewSettingsRef.current?.showModelEdgesDebug);
      model.updateMatrixWorld(true);
      const modelBounds = new THREE.Box3().setFromObject(model);
      const modelBoundsSize = new THREE.Vector3();
      modelBounds.getSize(modelBoundsSize);
      const modelWorldSize = Math.max(modelBoundsSize.x, modelBoundsSize.y, modelBoundsSize.z, 0.000001);
      const sourceMeshes = [];
      model.traverse((child) => {
        if (!child?.isMesh) return;
        if (child.userData?.voxemeraProjectionDecal) return;
        if (child.userData?.voxemeraModelEdgeOverlay) return;
        sourceMeshes.push(child);
      });

      const removeModelEdgeDebugOverlay = (child) => {
        const existing = child?.userData?.voxemeraEdgeDebugOverlay || null;
        if (!existing) return;
        existing.traverse?.((entry) => {
          entry.geometry?.dispose?.();
          if (Array.isArray(entry.material)) entry.material.forEach((material) => material?.dispose?.());
          else entry.material?.dispose?.();
        });
        existing.removeFromParent?.();
        delete child.userData.voxemeraEdgeDebugOverlay;
      };

      sourceMeshes.forEach((child) => {
        let edgeOverlay = child.userData?.voxemeraEdgeOverlay || null;
        if (!debugEnabled) removeModelEdgeDebugOverlay(child);
        if (!edgeOverlay && enabled) {
          ensureStableEdgeWearAttributes(child.geometry);
          const overlayGeometry = createEdgeWearAnnotatedGeometry(
            child.geometry,
            child.geometry.clone(),
            null,
          );
          const barycentric = overlayGeometry.getAttribute('voxemeraEdgeWearBarycentric');
          const sharpEdges = overlayGeometry.getAttribute('voxemeraEdgeWearSharpEdges');
          const vertexJoins = overlayGeometry.getAttribute('voxemeraEdgeWearVertexJoins');
          const vertexDetectedEdgeCounts = overlayGeometry.getAttribute('voxemeraEdgeWearVertexDetectedEdgeCounts');
          const edgeAltitudes = overlayGeometry.getAttribute('voxemeraEdgeWearAltitudes');
          const triangleA = overlayGeometry.getAttribute('voxemeraEdgeWearTriangleA');
          const triangleB = overlayGeometry.getAttribute('voxemeraEdgeWearTriangleB');
          const triangleC = overlayGeometry.getAttribute('voxemeraEdgeWearTriangleC');

          if (barycentric?.count && sharpEdges?.count && vertexJoins?.count
            && vertexDetectedEdgeCounts?.count && edgeAltitudes?.count
            && triangleA?.count && triangleB?.count && triangleC?.count) {
            // Measure the visible edge radius in world/model space rather
            // than approximating it from each mesh's local scale. This keeps
            // every Show Edges line the same physical distance from the real
            // model edge, including across differently scaled child meshes.
            const worldEdgeRadius = modelWorldSize * 0.000275;
            const edgeMaterial = new THREE.ShaderMaterial({
              uniforms: {
                voxemeraModelEdgeRadius: { value: worldEdgeRadius },
              },
              vertexShader: `
                attribute vec3 voxemeraEdgeWearBarycentric;
                attribute vec3 voxemeraEdgeWearSharpEdges;
                attribute vec3 voxemeraEdgeWearVertexJoins;
                attribute vec3 voxemeraEdgeWearVertexDetectedEdgeCounts;
                attribute vec3 voxemeraEdgeWearAltitudes;
                attribute vec3 voxemeraEdgeWearTriangleA;
                attribute vec3 voxemeraEdgeWearTriangleB;
                attribute vec3 voxemeraEdgeWearTriangleC;
                varying vec3 vVoxemeraEdgeBarycentric;
                varying vec3 vVoxemeraEdgeSharpness;
                varying vec3 vVoxemeraEdgeVertexJoins;
                varying vec3 vVoxemeraEdgeVertexDetectedEdgeCounts;
                varying vec3 vVoxemeraEdgeAltitudes;
                varying vec3 vVoxemeraTriangleA;
                varying vec3 vVoxemeraTriangleB;
                varying vec3 vVoxemeraTriangleC;
                varying vec3 vVoxemeraWorldPosition;

                void main() {
                  vVoxemeraEdgeBarycentric = voxemeraEdgeWearBarycentric;
                  vVoxemeraEdgeSharpness = voxemeraEdgeWearSharpEdges;
                  vVoxemeraEdgeVertexJoins = voxemeraEdgeWearVertexJoins;
                  vVoxemeraEdgeVertexDetectedEdgeCounts = voxemeraEdgeWearVertexDetectedEdgeCounts;
                  vVoxemeraEdgeAltitudes = voxemeraEdgeWearAltitudes;
                  vVoxemeraTriangleA = (modelMatrix * vec4(voxemeraEdgeWearTriangleA, 1.0)).xyz;
                  vVoxemeraTriangleB = (modelMatrix * vec4(voxemeraEdgeWearTriangleB, 1.0)).xyz;
                  vVoxemeraTriangleC = (modelMatrix * vec4(voxemeraEdgeWearTriangleC, 1.0)).xyz;
                  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                  vVoxemeraWorldPosition = worldPosition.xyz;
                  gl_Position = projectionMatrix * viewMatrix * worldPosition;
                }
              `,
              fragmentShader: `
                uniform float voxemeraModelEdgeRadius;
                varying vec3 vVoxemeraEdgeBarycentric;
                varying vec3 vVoxemeraEdgeSharpness;
                varying vec3 vVoxemeraEdgeVertexJoins;
                varying vec3 vVoxemeraEdgeVertexDetectedEdgeCounts;
                varying vec3 vVoxemeraEdgeAltitudes;
                varying vec3 vVoxemeraTriangleA;
                varying vec3 vVoxemeraTriangleB;
                varying vec3 vVoxemeraTriangleC;
                varying vec3 vVoxemeraWorldPosition;

                float distanceToSegment(vec3 point, vec3 startPoint, vec3 endPoint) {
                  vec3 segment = endPoint - startPoint;
                  float lengthSquared = max(dot(segment, segment), 0.000000000001);
                  float along = clamp(dot(point - startPoint, segment) / lengthSquared, 0.0, 1.0);
                  return length(point - (startPoint + (segment * along)));
                }

                void main() {
                  float strength0 = step(0.065, vVoxemeraEdgeSharpness.x);
                  float strength1 = step(0.065, vVoxemeraEdgeSharpness.y);
                  float strength2 = step(0.065, vVoxemeraEdgeSharpness.z);

                  float edgeRadius = max(voxemeraModelEdgeRadius, 0.000001);
                  vec3 distanceFromEdgeWorld = vec3(
                    distanceToSegment(vVoxemeraWorldPosition, vVoxemeraTriangleB, vVoxemeraTriangleC),
                    distanceToSegment(vVoxemeraWorldPosition, vVoxemeraTriangleC, vVoxemeraTriangleA),
                    distanceToSegment(vVoxemeraWorldPosition, vVoxemeraTriangleA, vVoxemeraTriangleB)
                  );
                  vec3 edgeAa = max(fwidth(distanceFromEdgeWorld), vec3(0.000001));

                  float edge0 = strength0 * (1.0 - smoothstep(
                    edgeRadius - edgeAa.x,
                    edgeRadius + edgeAa.x,
                    distanceFromEdgeWorld.x
                  ));
                  float edge1 = strength1 * (1.0 - smoothstep(
                    edgeRadius - edgeAa.y,
                    edgeRadius + edgeAa.y,
                    distanceFromEdgeWorld.y
                  ));
                  float edge2 = strength2 * (1.0 - smoothstep(
                    edgeRadius - edgeAa.z,
                    edgeRadius + edgeAa.z,
                    distanceFromEdgeWorld.z
                  ));

                  // Connected edge segments frequently meet across different
                  // triangles, so a join cannot depend on both segments being
                  // present in this one triangle. For every globally connected
                  // detected-edge vertex, fill a half-width surface sector on
                  // each incident triangle that already owns one of those edges.
                  // The sectors overlap at the shared mesh point and produce one
                  // seamless, constant-width black line without inventing edges.
                  float corner0Connected = step(1.5, vVoxemeraEdgeVertexDetectedEdgeCounts.x);
                  float corner1Connected = step(1.5, vVoxemeraEdgeVertexDetectedEdgeCounts.y);
                  float corner2Connected = step(1.5, vVoxemeraEdgeVertexDetectedEdgeCounts.z);
                  // A connected vertex join must cover every face sector around
                  // that welded vertex, including the small triangle sector
                  // between the two detected edge-owning faces. Restricting the
                  // join to only triangles that contain an edge is what left the
                  // visible wedge-shaped hole.

                  float corner0Distance = length(vVoxemeraWorldPosition - vVoxemeraTriangleA);
                  float corner1Distance = length(vVoxemeraWorldPosition - vVoxemeraTriangleB);
                  float corner2Distance = length(vVoxemeraWorldPosition - vVoxemeraTriangleC);
                  float corner0Aa = max(fwidth(corner0Distance), 0.000001);
                  float corner1Aa = max(fwidth(corner1Distance), 0.000001);
                  float corner2Aa = max(fwidth(corner2Distance), 0.000001);
                  // Standard round join: radius equals the line half-width.
                  // Because it is evaluated on every incident face sector, the
                  // union is one complete surface join with no notch and no
                  // oversized black blob.
                  float joinRadius = edgeRadius;

                  float corner0 = corner0Connected * (1.0 - smoothstep(
                    joinRadius - corner0Aa,
                    joinRadius + corner0Aa,
                    corner0Distance
                  ));
                  float corner1 = corner1Connected * (1.0 - smoothstep(
                    joinRadius - corner1Aa,
                    joinRadius + corner1Aa,
                    corner1Distance
                  ));
                  float corner2 = corner2Connected * (1.0 - smoothstep(
                    joinRadius - corner2Aa,
                    joinRadius + corner2Aa,
                    corner2Distance
                  ));

                  float edgeMask = max(
                    max(max(edge0, edge1), edge2),
                    max(max(corner0, corner1), corner2)
                  );
                  if (edgeMask <= 0.001) discard;
                  gl_FragColor = vec4(0.0, 0.0, 0.0, edgeMask);
                }
              `,
              transparent: true,
              depthTest: true,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -2,
              polygonOffsetUnits: -2,
              side: THREE.DoubleSide,
              extensions: { derivatives: true },
            });
            edgeMaterial.name = 'Voxemera wear-mask edge overlay material';

            edgeOverlay = new THREE.Mesh(overlayGeometry, edgeMaterial);
            edgeOverlay.name = 'Voxemera model-scale wear-mask edges';
            edgeOverlay.renderOrder = 900000;
            edgeOverlay.frustumCulled = child.frustumCulled;
            edgeOverlay.raycast = () => {};
            edgeOverlay.userData.voxemeraModelEdgeOverlay = true;
            child.add(edgeOverlay);
            child.userData.voxemeraEdgeOverlay = edgeOverlay;
          } else {
            overlayGeometry.dispose();
          }
        }

        if (edgeOverlay) edgeOverlay.visible = enabled;

        if (debugEnabled) {
          removeModelEdgeDebugOverlay(child);
          const sourceGeometry = ensureStableEdgeWearAttributes(child.geometry);
          const position = sourceGeometry?.getAttribute?.('position');
          const index = sourceGeometry?.index || null;
          const edgeMeta = sourceGeometry?.userData?.voxemeraEdgeWearMeta || null;
          const triangleEdgeSharpness = edgeMeta?.triangleEdgeSharpness || [];
          const triangleVertexDetectedEdgeCounts = edgeMeta?.triangleVertexDetectedEdgeCounts || [];
          if (position && triangleEdgeSharpness.length) {
            sourceGeometry.computeBoundingBox?.();
            const localBounds = sourceGeometry.boundingBox;
            const localBoundsSize = new THREE.Vector3();
            localBounds?.getSize?.(localBoundsSize);
            const localModelSize = Math.max(localBoundsSize.x, localBoundsSize.y, localBoundsSize.z, 0.000001);
            const debugChildWorldScale = new THREE.Vector3();
            child.getWorldScale(debugChildWorldScale);
            const childScale = Math.max(
              Math.abs(debugChildWorldScale.x),
              Math.abs(debugChildWorldScale.y),
              Math.abs(debugChildWorldScale.z),
              0.000001,
            );
            const debugWorldEdgeRadius = modelWorldSize * 0.000275;
            const debugLocalEdgeRadius = debugWorldEdgeRadius / childScale;
            const quantise = 100000 / localModelSize;
            const debugVertices = new Map();
            const tempPoint = new THREE.Vector3();
            const triangleCount = Math.floor((index ? index.count : position.count) / 3);
            const cornerIncidentComponents = [[1, 2], [0, 2], [0, 1]];

            const getVertexIndex = (triangleIndex, corner) => (
              index ? index.getX((triangleIndex * 3) + corner) : ((triangleIndex * 3) + corner)
            );
            const getVertexKey = (vertexIndex) => {
              tempPoint.fromBufferAttribute(position, vertexIndex);
              return `${Math.round(tempPoint.x * quantise)},${Math.round(tempPoint.y * quantise)},${Math.round(tempPoint.z * quantise)}`;
            };

            for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
              const strengths = triangleEdgeSharpness[triangleIndex] || [0, 0, 0];
              const counts = triangleVertexDetectedEdgeCounts[triangleIndex] || [0, 0, 0];
              for (let corner = 0; corner < 3; corner += 1) {
                const vertexIndex = getVertexIndex(triangleIndex, corner);
                const key = getVertexKey(vertexIndex);
                const incidentComponents = cornerIncidentComponents[corner];
                const incidentDetectedComponents = incidentComponents.filter((component) => (strengths[component] || 0) >= 0.065);
                const detectedEdgeCount = Number(counts[corner] || 0);
                let record = debugVertices.get(key);
                if (!record) {
                  tempPoint.fromBufferAttribute(position, vertexIndex);
                  record = {
                    key,
                    localPosition: { x: tempPoint.x, y: tempPoint.y, z: tempPoint.z },
                    detectedEdgeCount,
                    incidentTriangles: [],
                    incidentDetectedEdgeReferences: [],
                  };
                  debugVertices.set(key, record);
                }
                record.detectedEdgeCount = Math.max(record.detectedEdgeCount, detectedEdgeCount);
                if (incidentDetectedComponents.length) {
                  record.incidentTriangles.push({
                    triangleIndex,
                    corner,
                    edgeStrengths: strengths.map((value) => Number(value || 0)),
                    detectedEdgeCount,
                    incidentDetectedComponents,
                  });
                  incidentDetectedComponents.forEach((component) => {
                    record.incidentDetectedEdgeReferences.push(`${triangleIndex}:${component}`);
                  });
                }
              }
            }

            const connectedVertices = [...debugVertices.values()]
              .filter((record) => record.detectedEdgeCount >= 2 && record.incidentTriangles.length)
              .map((record) => ({
                ...record,
                incidentDetectedEdgeReferences: [...new Set(record.incidentDetectedEdgeReferences)],
              }));
            const endpointVertices = [...debugVertices.values()]
              .filter((record) => record.detectedEdgeCount === 1 && record.incidentTriangles.length);
            const branchingVertices = connectedVertices.filter((record) => record.detectedEdgeCount > 2);

            const debugGroup = new THREE.Group();
            debugGroup.name = 'Voxemera Show edges debug overlay';
            debugGroup.userData.voxemeraModelEdgeOverlay = true;
            debugGroup.renderOrder = 1000000;
            const markerRadius = Math.max(debugLocalEdgeRadius * 1.8, localModelSize * 0.00015);
            const markerGeometry = new THREE.SphereGeometry(markerRadius, 10, 8);
            const connectedMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff66, depthTest: false, depthWrite: false });
            const branchMaterial = new THREE.MeshBasicMaterial({ color: 0xff00ff, depthTest: false, depthWrite: false });
            const endpointMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00, depthTest: false, depthWrite: false });
            const markerLimit = 2500;

            connectedVertices.slice(0, markerLimit).forEach((record) => {
              const material = record.detectedEdgeCount > 2 ? branchMaterial : connectedMaterial;
              const marker = new THREE.Mesh(markerGeometry, material);
              marker.position.set(record.localPosition.x, record.localPosition.y, record.localPosition.z);
              marker.renderOrder = 1000001;
              marker.raycast = () => {};
              debugGroup.add(marker);
            });
            endpointVertices.slice(0, Math.max(0, markerLimit - connectedVertices.length)).forEach((record) => {
              const marker = new THREE.Mesh(markerGeometry, endpointMaterial);
              marker.scale.setScalar(0.65);
              marker.position.set(record.localPosition.x, record.localPosition.y, record.localPosition.z);
              marker.renderOrder = 1000001;
              marker.raycast = () => {};
              debugGroup.add(marker);
            });

            child.add(debugGroup);
            child.userData.voxemeraEdgeDebugOverlay = debugGroup;

            const fullyExpandedReport = {
              event: 'show-edges-debug-report-fully-expanded',
              mesh: {
                name: child.name || '(unnamed mesh)',
                uuid: child.uuid,
                positionCount: position.count,
                triangleCount,
                indexed: Boolean(index),
              },
              rendering: {
                modelWorldSize,
                childScale,
                localModelSize,
                worldEdgeRadius: debugWorldEdgeRadius,
                localEdgeRadius: debugLocalEdgeRadius,
                shaderJoinRadius: debugWorldEdgeRadius,
                edgeDetectionThreshold: 0.065,
                polygonOffsetFactor: -2,
                polygonOffsetUnits: -2,
              },
              topology: {
                uniqueVerticesExamined: debugVertices.size,
                connectedVertexCount: connectedVertices.length,
                endpointVertexCount: endpointVertices.length,
                branchingVertexCount: branchingVertices.length,
                visualMarkerLimit: markerLimit,
                markerLegend: {
                  green: 'Exactly two detected edges meet here',
                  magenta: 'Three or more detected edges meet here',
                  yellow: 'Only one detected edge reaches this vertex',
                },
              },
              connectedVertices,
              endpointVertices,
              branchingVertices,
              diagnosisGuide: {
                notchAtGreenMarker: 'Connectivity is detected correctly; the shader join is clipped or geometrically too small on one or more incident triangles.',
                notchAtYellowMarker: 'The second continuation edge was not classified as a detected edge at this vertex.',
                notchWithNoMarker: 'The visible corner is represented by non-welded/coincident vertices or the overlay is using different geometry than this debug pass.',
                blackBlobAtGreenMarker: 'The join radius is too large for the local incident angle.',
              },
            };
          }
        }
      });
    };

    const getLayoutSessionKey = (layoutMode, tileCount, resolutionPerTile) => (
      layoutMode === UV_LAYOUT_MODE_ORIGINAL
        ? `original:${ORIGINAL_UV_TEXTURE_RESOLUTION}`
        : `generated:${clampUvTileCount(tileCount)}:${clampUvResolution(resolutionPerTile)}`
    );

    const setActivePaintSession = (session) => {
      activePaintSession = session || null;
      if (activePaintSession?.texture) applyPaintMaterial(activePaintSession.texture);
      updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
    };

    const refreshVisibleMaterial = () => {
      const model = currentModelRef.current;
      if (!model) return;
      const layoutMode = clampUvLayoutMode(model.userData?.voxemeraUvLayoutMode);
      const shouldShowDebugUvs = layoutMode === UV_LAYOUT_MODE_GENERATED && Boolean(viewSettingsRef.current?.showUvColours);
      updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
      if (shouldShowDebugUvs) {
        setUvDebugVisible(model, true);
        return;
      }
      if (activePaintSession?.texture) applyPaintMaterial(activePaintSession.texture);
      else setUvDebugVisible(model, false);
    };

    const switchUvLayout = async (model, layoutMode, tileCount, resolutionPerTile) => {
      const profiler = loadProfilerRef.current;
      profiler?.mark('UV layout switch: start', 'Applying original or generated UVs before creating the paint atlas.', { layoutMode, tileCount, resolutionPerTile });
      if (!model) return null;
      if (layoutMode === UV_LAYOUT_MODE_ORIGINAL) {
        profiler?.mark('UV layout: restoring originals', 'Copying original UV attributes back to every mesh.');
        applyOriginalUvLayout(model);
      } else {
        const modelFingerprint = String(model.userData?.voxemeraModelUvCacheFingerprint || '');
        profiler?.mark('UV cache lookup: start', 'Checking IndexedDB for generated geometry and UV coordinates from an earlier load.', { modelFingerprint, tileCount: clampUvTileCount(tileCount) });
        let restoredFromCache = false;
        try {
          restoredFromCache = await restoreGeneratedUvCache(model, modelFingerprint, tileCount);
        } catch (cacheError) {
          profiler?.mark('UV cache lookup: failed', cacheError?.message || 'Generated UV cache could not be read.');
        }
        if (restoredFromCache) {
          profiler?.mark('UV cache restored', 'Generated geometry and UV coordinates were restored directly; island generation was skipped.');
          setUvDebugVisible(model, false);
        } else {
          profiler?.mark('UV cache miss', 'No valid generated UV cache exists for this model and tile count.');
          profiler?.mark('UV layout: generating atlas', 'Packing connected triangle islands and writing generated UV attributes. Dense geometry can block the main thread.');
          applyDebugUvLayout(model, tileCount, false);
          profiler?.mark('UV layout: generated', 'Generated UV attributes have been written.');
          profiler?.mark('UV cache save: start', 'Saving generated geometry and UV coordinates so subsequent reloads can skip atlas generation.');
          try {
            const saved = await saveGeneratedUvCache(model, modelFingerprint, tileCount);
            profiler?.mark(saved ? 'UV cache saved' : 'UV cache not saved', saved
              ? 'Generated geometry and UV coordinates are now persisted in IndexedDB.'
              : 'Generated geometry did not contain a cacheable UV result.');
          } catch (cacheError) {
            profiler?.mark('UV cache save: failed', cacheError?.message || 'Generated UV cache could not be stored.');
          }
        }
      }
      const maximumResolution = getMaximumUvResolutionForTextureSize(tileCount, renderer.capabilities.maxTextureSize);
      const effectiveResolution = Math.min(clampUvResolution(resolutionPerTile), maximumResolution);
      const generatedDimensions = getGeneratedAtlasDimensions(tileCount, effectiveResolution);
      const targetWidth = layoutMode === UV_LAYOUT_MODE_ORIGINAL ? ORIGINAL_UV_TEXTURE_RESOLUTION : generatedDimensions.width;
      const targetHeight = layoutMode === UV_LAYOUT_MODE_ORIGINAL ? ORIGINAL_UV_TEXTURE_RESOLUTION : generatedDimensions.height;
      const sessionKey = getLayoutSessionKey(layoutMode, tileCount, effectiveResolution);
      profiler?.mark('UV atlas dimensions resolved', 'Choosing paint bitmap size within the GPU texture and combined paint-session memory limits.', {
        targetWidth,
        targetHeight,
        sessionKey,
        maxTextureSize: renderer.capabilities.maxTextureSize,
        requestedResolutionPerTile: clampUvResolution(resolutionPerTile),
        effectiveResolutionPerTile: effectiveResolution,
        estimatedCombinedAllocation: formatTextureMemory(
          targetWidth * UV_PAINT_SESSION_FULL_SIZE_BUFFER_COUNT,
          targetHeight,
        ),
      });
      const session = getOrCreatePaintSession(sessionKey, model, layoutMode, targetWidth, targetHeight);
      setActivePaintSession(session);
      refreshVisibleMaterial();
      profiler?.mark('UV layout switch: complete', 'UVs, paint session and visible materials are active.');
      return session;
    };

    const getPaintHitAtScreenPoint = (screenX, screenY, rect, pressure = 1) => {
      const model = currentModelRef.current;
      if (!model || !rect?.width || !rect?.height) return null;
      pointerNdc.x = (Number(screenX) / rect.width) * 2 - 1;
      pointerNdc.y = -((Number(screenY) / rect.height) * 2 - 1);
      raycaster.setFromCamera(pointerNdc, camera);
      const hits = raycaster.intersectObject(model, true);
      const hit = hits.find((entry) => entry?.uv && entry.object?.isMesh && !entry.object.userData?.voxemeraProjectionDecal);
      if (!hit) return null;
      const faceNormal = hit.face?.normal?.clone?.()
        ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize()
        : camera.position.clone().sub(hit.point?.clone?.() || new THREE.Vector3()).normalize();
      return {
        uv: hit.uv.clone(),
        point: hit.point?.clone?.() || null,
        normal: faceNormal,
        distance: Number(hit.distance) || 0,
        uvLayoutRevision,
        object: hit.object,
        faceIndex: hit.faceIndex,
        screenX: Number(screenX),
        screenY: Number(screenY),
        viewportWidth: rect.width,
        viewportHeight: rect.height,
        pressure,
      };
    };

    const getPaintHit = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const pressure = readPointerPressure(event);
      return getPaintHitAtScreenPoint(
        event.clientX - rect.left,
        event.clientY - rect.top,
        rect,
        pressure,
      );
    };

    const getSelectionAllowedFaces = (mesh) => {
      if (!activeSelection?.selectedFaces || !mesh?.uuid) return null;
      const topology = getGeometryTopology(mesh.geometry);
      const triangleCount = topology?.triangleCount || 0;
      const selected = activeSelection.selectedFaces.get(mesh.uuid) || new Set();
      const boundary = activeSelection.boundaryFaces?.get(mesh.uuid) || new Set();
      const explicit = new Set([...selected, ...boundary]);
      if (!activeSelection.inverted) return explicit;
      return new Set(Array.from({ length: triangleCount }, (_, faceIndex) => faceIndex).filter((faceIndex) => !explicit.has(faceIndex)));
    };

    const getCanvasAlphaDebug = (canvas) => {
      if (!canvas?.width || !canvas?.height) return null;
      const context = canvas.getContext?.('2d', { willReadFrequently: true });
      if (!context) return null;
      const image = context.getImageData(0, 0, canvas.width, canvas.height);
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = -1;
      let maxY = -1;
      let nonTransparentPixels = 0;
      let alphaTotal = 0;
      for (let pixel = 0; pixel < image.data.length; pixel += 4) {
        const alpha = image.data[pixel + 3];
        if (alpha <= 0) continue;
        const pixelIndex = pixel / 4;
        const x = pixelIndex % canvas.width;
        const y = Math.floor(pixelIndex / canvas.width);
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        nonTransparentPixels += 1;
        alphaTotal += alpha;
      }
      return {
        width: canvas.width,
        height: canvas.height,
        nonTransparentPixels,
        averageNonTransparentAlpha: nonTransparentPixels ? alphaTotal / nonTransparentPixels : 0,
        alphaBounds: nonTransparentPixels
          ? { minX, minY, maxX, maxY, width: maxX - minX + 1, height: maxY - minY + 1 }
          : null,
      };
    };

    const getPaintSelectionDebug = (hit = null) => {
      if (!activeSelection?.selectedFaces) {
        return {
          hasActiveSelection: false,
          inverted: false,
          hit: hit ? {
            meshUuid: String(hit.object?.uuid || ''),
            meshName: String(hit.object?.name || ''),
            faceIndex: Number.isInteger(hit.faceIndex) ? hit.faceIndex : null,
            worldPoint: hit.point ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null,
            uv: hit.uv ? { x: hit.uv.x, y: hit.uv.y } : null,
          } : null,
          meshes: [],
        };
      }
      const meshes = [];
      activeSelection.selectedFaces.forEach((selectedFaces, meshUuid) => {
        const boundaryFaces = activeSelection.boundaryFaces?.get?.(meshUuid) || new Set();
        const explicitFaces = new Set([...selectedFaces, ...boundaryFaces]);
        meshes.push({
          meshUuid,
          selectedFaceCount: selectedFaces.size,
          boundaryFaceCount: boundaryFaces.size,
          explicitFaceCount: explicitFaces.size,
          firstSelectedFaceIndices: Array.from(selectedFaces).slice(0, 40),
          firstBoundaryFaceIndices: Array.from(boundaryFaces).slice(0, 40),
          hitFaceSelected: hit?.object?.uuid === meshUuid && Number.isInteger(hit.faceIndex)
            ? selectedFaces.has(hit.faceIndex)
            : null,
          hitFaceBoundary: hit?.object?.uuid === meshUuid && Number.isInteger(hit.faceIndex)
            ? boundaryFaces.has(hit.faceIndex)
            : null,
          hitFaceExplicitlyAllowed: hit?.object?.uuid === meshUuid && Number.isInteger(hit.faceIndex)
            ? explicitFaces.has(hit.faceIndex)
            : null,
        });
      });
      return {
        hasActiveSelection: true,
        inverted: Boolean(activeSelection.inverted),
        shape: activeSelection.shape || null,
        editablePolygon: Boolean(activeSelection.editablePolygon),
        hit: hit ? {
          meshUuid: String(hit.object?.uuid || ''),
          meshName: String(hit.object?.name || ''),
          faceIndex: Number.isInteger(hit.faceIndex) ? hit.faceIndex : null,
          worldPoint: hit.point ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null,
          uv: hit.uv ? { x: hit.uv.x, y: hit.uv.y } : null,
          projectedViewport: hit.point ? projectWorldPointToViewport(hit.point) : null,
        } : null,
        meshes,
      };
    };

    const getModelBrushWorldDiameter = (configuredSize) => {
      const model = currentModelRef.current;
      const safeSize = Math.max(1, Number(configuredSize) || 1);
      if (!model) return safeSize / 512;
      model.updateMatrixWorld(true);
      const bounds = new THREE.Box3().setFromObject(model);
      if (bounds.isEmpty()) return safeSize / 512;
      const dimensions = bounds.getSize(new THREE.Vector3());
      const modelSpan = Math.max(dimensions.x, dimensions.y, dimensions.z, 0.001);
      return modelSpan * (safeSize / 512);
    };

    // The 2D Paint Tool's physical engine on the model surface (shared engine,
    // brushes and settings; see modelPhysicalPaint.js).
    const physicalPaint3d = createModelPhysicalPaint({
      THREE,
      renderer,
      getModel: () => currentModelRef.current,
      getMeshes: () => getModelPaintableMeshes(),
      getSession: () => activePaintSession,
      getHitAt: (x, y, rect, pressure) => getPaintHitAtScreenPoint(x, y, rect, pressure),
      getBrushWorldDiameter: (size) => getModelBrushWorldDiameter(size),
      getPaintSettings: () => paintConfigRef.current.paintStageRef?.current?.getProjectionBrushConfig?.()?.paintSettings || {},
      getCacheKey: () => (currentModelRef.current && activePaintSession ? getCurrentPaintCacheKey() : ''),
      saveRecord: (key, record) => saveModelPaintSessionRecord(key, record),
      loadRecord: (key) => loadModelPaintSessionRecord(key),
      onStrokeCommitted: (count) => {
        const callback = paintConfigRef.current.onPhysicalPaintStrokeCommitted;
        if (typeof callback === 'function') callback(count);
      },
    });
    // Debug hooks (the Canvas can mount more than one viewport).
    if (typeof window !== 'undefined') {
      window.__vxPhysicalPaint3d = physicalPaint3d;
      window.__vxPhysicalPaint3dAll = [...(window.__vxPhysicalPaint3dAll || []), { bridge: physicalPaint3d, canvas: renderer.domElement }];
    }

    const getWorldUnitsPerScreenPixel = (hit, rect) => {
      if (!rect?.height) return 1;
      if (camera.isOrthographicCamera) {
        return Math.abs(camera.top - camera.bottom) / Math.max(1, camera.zoom || 1) / Math.max(1, rect.height);
      }
      const worldPoint = hit?.point;
      if (!worldPoint) return 1;
      camera.updateMatrixWorld(true);
      const cameraPoint = worldPoint.clone().applyMatrix4(camera.matrixWorldInverse);
      const depth = Math.max(0.000001, Math.abs(cameraPoint.z));
      const verticalFov = THREE.MathUtils.degToRad(camera.fov || 45);
      return (2 * depth * Math.tan(verticalFov * 0.5)) / Math.max(1, rect.height);
    };

    const getBrushScreenSize = (brushConfig, hit, rect) => {
      const configuredSize = Math.max(1, Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const worldDiameter = Number(brushConfig.worldBrushDiameter);
      if (!Number.isFinite(worldDiameter) || worldDiameter <= 0 || !hit) return configuredSize;
      const worldUnitsPerPixel = getWorldUnitsPerScreenPixel(hit, rect);
      return Math.max(0.25, worldDiameter / Math.max(worldUnitsPerPixel, 1e-9));
    };

    const getAirbrushScreenDiameter = (strokeOrConfig, hit, rect) => {
      const brushConfig = strokeOrConfig?.brushConfig || strokeOrConfig || {};
      const requestedSize = Math.max(
        1,
        Number(brushConfig.size ?? brushConfig.brushSize) || 1,
      );
      const storedWorldDiameter = Number(
        strokeOrConfig?.airbrushWorldDiameter
        ?? brushConfig.worldBrushDiameter,
      );
      const worldDiameter = Number.isFinite(storedWorldDiameter) && storedWorldDiameter > 0
        ? storedWorldDiameter
        : getModelBrushWorldDiameter(requestedSize);

      if (!hit) return requestedSize;

      const worldUnitsPerPixel = getWorldUnitsPerScreenPixel(hit, rect);
      return Math.max(
        0.25,
        worldDiameter / Math.max(worldUnitsPerPixel, 1e-9),
      );
    };

    // Target the same world-space texel density as painting at 250% zoom.
    // The actual canvas scale is adjusted inversely with camera zoom so a stroke
    // made while zoomed out contains the same dot detail as one made at 250%.
    const AIRBRUSH_3D_REFERENCE_ZOOM_PERCENT = 250;
    const AIRBRUSH_3D_MIN_RESOLUTION_SCALE = 1;
    const AIRBRUSH_3D_MAX_RESOLUTION_SCALE = 8;

    const getUvPixelsPerScreenPixel = (hit, rect) => {
      const cachedScale = Number(hit?.uvPixelsPerScreenPixel);
      if (Number.isFinite(cachedScale) && cachedScale > 0) return cachedScale;

      const activeCanvas = activePaintSession?.canvas;
      if (!activeCanvas) return ORIGINAL_UV_TEXTURE_RESOLUTION / Math.max(1, rect.width);
      if (!hit) return activeCanvas.width / Math.max(1, rect.width);
      const rightHit = getPaintHitAtScreenPoint(hit.screenX + 1, hit.screenY, rect, hit.pressure);
      const downHit = getPaintHitAtScreenPoint(hit.screenX, hit.screenY + 1, rect, hit.pressure);
      const candidates = [];
      if (rightHit && rightHit.object === hit.object && rightHit.faceIndex === hit.faceIndex) {
        candidates.push(hit.uv.distanceTo(rightHit.uv) * activeCanvas.width);
      }
      if (downHit && downHit.object === hit.object && downHit.faceIndex === hit.faceIndex) {
        candidates.push(hit.uv.distanceTo(downHit.uv) * activeCanvas.height);
      }
      const valid = candidates.filter((value) => Number.isFinite(value) && value > 0.0001 && value < activeCanvas.width);
      if (!valid.length) return activeCanvas.width / Math.max(1, rect.width);
      return valid.reduce((sum, value) => sum + value, 0) / valid.length;
    };

    const getCurrentDebugZoomPercent = () => {
      const currentDistance = camera.position.distanceTo(controls.target);
      const referenceDistance = Math.max(zoomReferenceDistanceRef.current || currentDistance, 0.00000001);
      return Math.max(1, (referenceDistance / Math.max(currentDistance, 0.00000001)) * 100);
    };

    const get3dAirbrushResolutionDetails = () => {
      const currentZoomPercent = Math.max(1, getCurrentDebugZoomPercent());
      const rawScale = AIRBRUSH_3D_REFERENCE_ZOOM_PERCENT / currentZoomPercent;
      const resolutionScale = Math.max(
        AIRBRUSH_3D_MIN_RESOLUTION_SCALE,
        Math.min(AIRBRUSH_3D_MAX_RESOLUTION_SCALE, rawScale),
      );

      return {
        currentZoomPercent,
        targetZoomPercent: AIRBRUSH_3D_REFERENCE_ZOOM_PERCENT,
        rawScale,
        resolutionScale,
        effectiveZoomPercent: currentZoomPercent * resolutionScale,
        capped: Math.abs(rawScale - resolutionScale) > 0.0001,
      };
    };

    const get3dAirbrushResolutionScale = () => get3dAirbrushResolutionDetails().resolutionScale;

    const get3dAirbrushProjectionScale = () => get3dAirbrushResolutionScale();

    const clearPreviewCanvas = () => {
      if (!previewContext || !previewCanvas) return;
      previewDisplayOpacity = 1;
      if (previewCanvas.style && previewCanvas.style.opacity !== '0') previewCanvas.style.opacity = '1';
      previewContext.save();
      previewContext.setTransform(1, 0, 0, 1, 0, 0);
      previewContext.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      previewContext.restore();
    };

    const setPreviewCanvasVisible = (visible) => {
      if (!previewCanvas?.style) return;
      previewCanvas.style.opacity = visible ? String(previewDisplayOpacity) : '0';
    };

    const hide3dBrushCursor = () => {
      brushCursorOverlay.style.display = 'none';
    };

    const position3dBrushCursor = (point, diameter = null) => {
      if (!point) {
        hide3dBrushCursor();
        return;
      }

      brushCursorOverlay.style.left = `${Number(point.screenX) || 0}px`;
      brushCursorOverlay.style.top = `${Number(point.screenY) || 0}px`;

      const numericDiameter = Number(diameter);
      if (Number.isFinite(numericDiameter) && numericDiameter > 0) {
        const safeDiameter = Math.max(1, numericDiameter);
        brushCursorOverlay.style.width = `${safeDiameter}px`;
        brushCursorOverlay.style.height = `${safeDiameter}px`;
      }

      brushCursorOverlay.style.display = 'block';
    };

    const updateIdle3dBrushCursor = (event) => {
      if (activePaintStroke) return;
      if (surfaceModeRef.current !== 'paint') {
        hide3dBrushCursor();
        return;
      }

      const stage = paintConfigRef.current.paintStageRef?.current;
      const brushConfig = stage?.getProjectionBrushConfig?.() || {};
      const activeBrushPreset = String(brushConfig.activeBrushPreset || 'pen');
      const isAirbrush = brushConfig.activeTool === 'pen'
        && activeBrushPreset === 'airbrush'
        && !brushConfig.importedBrush;
      const isEraser = brushConfig.activeTool === 'eraser';
      const isSupportedBrush = isEraser || (brushConfig.activeTool === 'pen'
        && (
          activeBrushPreset === 'pen'
          || activeBrushPreset === 'pencil'
          || Boolean(brushConfig.importedBrush)
        ));

      // Never show or calculate a cursor for airbrush.
      if (isAirbrush || !isSupportedBrush) {
        hide3dBrushCursor();
        return;
      }

      const point = getPreviewPointerPoint(event);

      // Move the DOM circle first, before doing the model raycast, so pointer
      // position never waits for geometry intersection work.
      position3dBrushCursor(point);

      const hit = getPaintHit(event);
      if (!hit) {
        hide3dBrushCursor();
        return;
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const requestedBrushSize = Math.max(
        1,
        Number(brushConfig.size ?? brushConfig.brushSize) || 1,
      );
      const cursorBrushConfig = {
        ...brushConfig,
        size: requestedBrushSize,
        brushSize: requestedBrushSize,
        worldBrushDiameter: getModelBrushWorldDiameter(requestedBrushSize),
      };
      const screenDiameter = getBrushScreenSize(cursorBrushConfig, hit, rect);
      position3dBrushCursor(point, screenDiameter);
    };

    const getPreviewPointerPointInRect = (event, rect) => ({
      screenX: event.clientX - rect.left,
      screenY: event.clientY - rect.top,
      pressure: readPointerPressure(event),
      tiltX: Number(event.tiltX) || 0,
      tiltY: Number(event.tiltY) || 0,
      twist: Number(event.twist) || 0,
    });

    const getPreviewPointerPoint = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const pressure = readPointerPressure(event);
      return {
        screenX: event.clientX - rect.left,
        screenY: event.clientY - rect.top,
        pressure,
        tiltX: Number(event.tiltX) || 0,
        tiltY: Number(event.tiltY) || 0,
        twist: Number(event.twist) || 0,
      };
    };

    const captureActiveLayerErasedPreview = (activeLayerId) => {
      const model = currentModelRef.current;
      if (!model || !renderer || !scene || !camera) return null;

      const targetLayerId = String(activeLayerId || '');
      if (!targetLayerId) return null;

      const rect = renderer.domElement.getBoundingClientRect();
      const hiddenPaintMeshes = [];
      const runtime = runtimeRef.current;
      const activeLayerMeshes = new Set();

      (runtime?.projectionStrokes || []).forEach((stroke) => {
        if (String(stroke?.layerId || '') !== targetLayerId) return;
        (stroke?.meshes || []).forEach((mesh) => {
          if (mesh?.isMesh) activeLayerMeshes.add(mesh);
        });
      });

      activeLayerMeshes.forEach((mesh) => {
        if (!mesh.visible) return;
        hiddenPaintMeshes.push(mesh);
        mesh.visible = false;
      });

      const snapshot = document.createElement('canvas');
      snapshot.width = Math.max(1, Math.round(rect.width));
      snapshot.height = Math.max(1, Math.round(rect.height));
      const snapshotContext = snapshot.getContext('2d', { alpha: true });

      try {
        renderer.render(scene, camera);
        if (!snapshotContext) return null;
        snapshotContext.imageSmoothingEnabled = true;
        snapshotContext.clearRect(0, 0, snapshot.width, snapshot.height);
        snapshotContext.drawImage(
          renderer.domElement,
          0,
          0,
          renderer.domElement.width,
          renderer.domElement.height,
          0,
          0,
          snapshot.width,
          snapshot.height,
        );
      } finally {
        hiddenPaintMeshes.forEach((child) => {
          child.visible = true;
        });
        renderer.render(scene, camera);
      }

      // Keep a matching snapshot with the active layer visible. On pointer-up
      // the eraser can compare these two images only beneath its preview mask.
      // If they are identical there, the stroke touched no active-layer paint
      // and all projection, mask-rebuild and persistence work can be skipped.
      const visibleSnapshot = document.createElement('canvas');
      visibleSnapshot.width = snapshot.width;
      visibleSnapshot.height = snapshot.height;
      const visibleContext = visibleSnapshot.getContext('2d', { alpha: true });
      if (visibleContext) {
        visibleContext.imageSmoothingEnabled = true;
        visibleContext.clearRect(0, 0, visibleSnapshot.width, visibleSnapshot.height);
        visibleContext.drawImage(
          renderer.domElement,
          0,
          0,
          renderer.domElement.width,
          renderer.domElement.height,
          0,
          0,
          visibleSnapshot.width,
          visibleSnapshot.height,
        );
        snapshot.voxemeraVisibleSource = visibleSnapshot;
      }

      return snapshot;
    };

    const eraserPreviewTouchesActiveLayer = (brushConfig) => {
      const hiddenSource = brushConfig?.eraserPreviewSource;
      const visibleSource = hiddenSource?.voxemeraVisibleSource;
      if (!previewContext || !previewCanvas || !hiddenSource || !visibleSource) return true;

      const width = Math.max(1, Math.min(
        previewCanvas.width,
        hiddenSource.width,
        visibleSource.width,
      ));
      const height = Math.max(1, Math.min(
        previewCanvas.height,
        hiddenSource.height,
        visibleSource.height,
      ));

      try {
        const previewPixels = previewContext.getImageData(0, 0, width, height).data;
        const hiddenContext = hiddenSource.getContext('2d', { alpha: true, willReadFrequently: true });
        const visibleContext = visibleSource.getContext('2d', { alpha: true, willReadFrequently: true });
        if (!hiddenContext || !visibleContext) return true;
        const hiddenPixels = hiddenContext.getImageData(0, 0, width, height).data;
        const visiblePixels = visibleContext.getImageData(0, 0, width, height).data;

        // Ignore tiny antialias/render noise. A real active-layer contribution
        // changes at least one colour/alpha channel by more than this threshold.
        const differenceThreshold = 6;
        for (let index = 0; index < previewPixels.length; index += 4) {
          if (previewPixels[index + 3] <= 2) continue;
          if (
            Math.abs(visiblePixels[index] - hiddenPixels[index]) > differenceThreshold
            || Math.abs(visiblePixels[index + 1] - hiddenPixels[index + 1]) > differenceThreshold
            || Math.abs(visiblePixels[index + 2] - hiddenPixels[index + 2]) > differenceThreshold
            || Math.abs(visiblePixels[index + 3] - hiddenPixels[index + 3]) > differenceThreshold
          ) {
            return true;
          }
        }
        return false;
      } catch (error) {
        // Preserve erase behaviour if a browser refuses a canvas readback.
        return true;
      }
    };

    const compositeEraserPreviewSource = (brushConfig) => {
      const source = brushConfig?.eraserPreviewSource;
      if (!previewContext || !source) return false;

      previewContext.save();
      previewContext.globalCompositeOperation = 'source-in';
      previewContext.globalAlpha = 1;
      previewContext.shadowColor = 'rgba(0,0,0,0)';
      previewContext.shadowBlur = 0;
      previewContext.drawImage(
        source,
        0,
        0,
        Math.max(1, previewViewportWidth),
        Math.max(1, previewViewportHeight),
      );
      previewContext.restore();
      return true;
    };

    const drawPreviewRoundDab = (point, brushConfig, pressure = 1) => {
      if (!previewContext || !point) return false;
      const safePressure = Math.max(0.01, Math.min(1, Number(pressure) || 1));
      const size = Math.max(0.25, Number(point.brushSize) || Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const radius = Math.max(0.25, size * safePressure * 0.5);
      const softnessPercent = Math.max(0, Math.min(100, Number(brushConfig.softness ?? brushConfig.brushSoftness) || 0));
      const softness = softnessPercent / 100;
      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig.opacity) || 1));
      const color = String(brushConfig.strokeColor || brushConfig.color || '#000000');

      const transparentColor = (() => {
        const value = color.trim();
        const shortHex = value.match(/^#([0-9a-f]{3})$/i);
        if (shortHex) {
          const [red, green, blue] = shortHex[1]
            .split('')
            .map((part) => parseInt(`${part}${part}`, 16));
          return `rgba(${red}, ${green}, ${blue}, 0)`;
        }
        const longHex = value.match(/^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i);
        if (longHex) {
          const rgb = longHex[1];
          return `rgba(${parseInt(rgb.slice(0, 2), 16)}, ${parseInt(rgb.slice(2, 4), 16)}, ${parseInt(rgb.slice(4, 6), 16)}, 0)`;
        }
        const rgbFunction = value.match(/^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)/i);
        if (rgbFunction) {
          return `rgba(${rgbFunction[1]}, ${rgbFunction[2]}, ${rgbFunction[3]}, 0)`;
        }
        return 'rgba(0, 0, 0, 0)';
      })();

      previewContext.save();
      previewContext.globalCompositeOperation = 'source-over';

      if (softness > 0.001) {
        const innerRadius = radius * Math.max(0, 1 - softness);
        const gradient = previewContext.createRadialGradient(
          point.screenX,
          point.screenY,
          innerRadius,
          point.screenX,
          point.screenY,
          radius,
        );
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, transparentColor);
        previewContext.fillStyle = gradient;
        previewContext.globalAlpha = opacity * safePressure;
        previewContext.beginPath();
        previewContext.arc(point.screenX, point.screenY, radius, 0, Math.PI * 2);
        previewContext.fill();
      } else {
        previewContext.fillStyle = color;
        previewContext.globalAlpha = opacity * safePressure;
        previewContext.beginPath();
        previewContext.arc(point.screenX, point.screenY, radius, 0, Math.PI * 2);
        previewContext.fill();
      }

      previewContext.restore();
      compositeEraserPreviewSource(brushConfig);
      return true;
    };

    const toAirbrushPreviewPoint = (point) => ({
      x: Number(point?.screenX) || 0,
      y: Number(point?.screenY) || 0,
      pressure: Math.max(0.01, Math.min(1, Number(point?.pressure) || 1)),
      tiltX: Number(point?.tiltX) || 0,
      tiltY: Number(point?.tiltY) || 0,
      twist: Number(point?.twist) || 0,
    });

    const AIRBRUSH_3D_SPEED_MULTIPLIER = 2;
    const AIRBRUSH_3D_WIDTH_SCALE = 0.5;
    const AIRBRUSH_3D_DOT_SCALE_RATIO = 0.5;
    const AIRBRUSH_3D_LIVE_FRAME_INTERVAL_MS = 1000 / 60;
    const AIRBRUSH_3D_GPU_RENDERER_MIN_SIZE = 1024;
    const AIRBRUSH_3D_GPU_RENDERER_MAX_SIZE = 8192;
    const AIRBRUSH_3D_STATIONARY_EMULATION_DISTANCE_PX = 1.5;
    const AIRBRUSH_3D_STATIONARY_EMULATION_RADIUS_RATIO = 0.05;
    const AIRBRUSH_3D_STATIONARY_EMULATION_ROTATIONS_PER_SECOND = 25;

    const get3dAirbrushRequiredOverscan = (brushConfig, previewPoint, projectionScale) => {
      const requestedSize = Math.max(1, Number(brushConfig?.size ?? brushConfig?.brushSize) || 1);
      const screenSize = Math.max(
        0.25,
        Number(previewPoint?.brushSize)
        || getAirbrushScreenDiameter(brushConfig, null, renderer.domElement.getBoundingClientRect()),
      );
      const worldScale = Math.max(0.01, (screenSize / requestedSize) * AIRBRUSH_3D_WIDTH_SCALE);
      const settings = getAirbrushRendererSettings({
        ...(brushConfig || {}),
        brushSize: requestedSize,
        size: requestedSize,
        airbrushDiameterScale: worldScale,
        airbrushDotScale: Math.max(0.01, worldScale * AIRBRUSH_3D_DOT_SCALE_RATIO),
        // Overscan is measured in CSS/screen pixels. Applying the high-resolution
        // projection scale here inflated the border itself, which then consumed the
        // GPU texture budget and forced zoomed-out strokes back to a blurry raster.
        // The canvas transform supplies the resolution scale separately.
        airbrushResolutionScale: 1,
      });
      if (!settings) return Math.max(32, Math.ceil(screenSize));

      const sprayRadius = Math.max(
        Number(settings.smallDiameter) || 0,
        Number(settings.mediumDiameter) || 0,
        Number(settings.largeDiameter) || 0,
      ) * 0.5;
      const dotRadius = Math.max(
        Number(settings.smallMax) || 0,
        Number(settings.mediumMax) || 0,
        Number(settings.largeMax) || 0,
      ) * 0.5;
      const splatterReach = Math.max(0, Number(settings.splatterMaxLength) || 0);
      const naturalRadius = Math.max(
        screenSize * 0.5,
        sprayRadius + dotRadius + splatterReach,
      );
      return Math.max(
        64,
        Math.ceil(naturalRadius + 96),
      );
    };

    const getAirbrushGpuRendererSize = (
      screenDiameter,
      brushConfig,
      resolutionScale = get3dAirbrushResolutionScale(),
    ) => {
      const safeDiameter = Math.max(1, Number(screenDiameter) || 1);
      const settings = getAirbrushRendererSettings(brushConfig || {}) || {};

      const largestDotDiameter = Math.max(
        Number(settings.smallMax) || 0,
        Number(settings.mediumMax) || 0,
        Number(settings.largeMax) || 0,
      );
      const splatterLength = Math.max(
        Number(settings.splatterMinLength) || 0,
        Number(settings.splatterMaxLength) || 0,
      );

      // The airbrush utility works in resolution-scaled renderer coordinates.
      // Include the entire world-space circle, the largest dot radius, splatter,
      // and a generous transparent gutter so no zoom level can expose the GPU
      // surface's square boundary.
      const requiredDiameter = (
        safeDiameter
        + largestDotDiameter
        + (splatterLength * 2)
        + 128
      ) * Math.max(1, Number(resolutionScale) || 1);

      let rendererSize = AIRBRUSH_3D_GPU_RENDERER_MIN_SIZE;
      while (
        rendererSize < requiredDiameter
        && rendererSize < AIRBRUSH_3D_GPU_RENDERER_MAX_SIZE
      ) {
        rendererSize *= 2;
      }

      return Math.min(
        AIRBRUSH_3D_GPU_RENDERER_MAX_SIZE,
        Math.max(AIRBRUSH_3D_GPU_RENDERER_MIN_SIZE, rendererSize),
      );
    };


    const get3dAirbrushStationaryPreviewPoints = (stroke, previousPoint, currentPoint, currentDiameter, elapsedSeconds) => {
      if (!currentPoint || !Number.isFinite(Number(elapsedSeconds)) || Number(elapsedSeconds) <= 0) {
        return {
          previous: previousPoint,
          current: currentPoint,
        };
      }

      const airbrushDistance = Number(stroke?.brushConfig?.airbrushDistance);
      if (Number.isFinite(airbrushDistance) && airbrushDistance < 50) {
        stroke.airbrushStationaryOrbitAngle = 0;
        return {
          previous: previousPoint,
          current: currentPoint,
        };
      }

      const previous = previousPoint || currentPoint;
      const movement = Math.hypot(
        (Number(currentPoint?.x) || 0) - (Number(previous?.x) || 0),
        (Number(currentPoint?.y) || 0) - (Number(previous?.y) || 0),
      );
      if (movement > AIRBRUSH_3D_STATIONARY_EMULATION_DISTANCE_PX) {
        stroke.airbrushStationaryOrbitAngle = 0;
        return {
          previous: previousPoint,
          current: currentPoint,
        };
      }

      // Keep the stationary rotation strictly proportional to the actual
      // on-screen stroke diameter. The old 2px minimum made a 5mm spray orbit
      // several times wider than the painted line, while larger sprays looked
      // correct only after they grew beyond that fixed minimum.
      const orbitRadius = Math.max(0, Number(currentDiameter) || 0)
        * AIRBRUSH_3D_STATIONARY_EMULATION_RADIUS_RATIO;
      const startAngle = Number(stroke.airbrushStationaryOrbitAngle) || 0;
      const endAngle = startAngle
        + (Number(elapsedSeconds) * AIRBRUSH_3D_STATIONARY_EMULATION_ROTATIONS_PER_SECOND * Math.PI * 2);
      stroke.airbrushStationaryOrbitAngle = endAngle;

      const makeOrbitPoint = (sourcePoint, angle) => ({
        ...(sourcePoint || currentPoint),
        x: (Number(currentPoint?.x) || 0) + (Math.cos(angle) * orbitRadius),
        y: (Number(currentPoint?.y) || 0) + (Math.sin(angle) * orbitRadius),
      });

      return {
        previous: makeOrbitPoint(previous, startAngle),
        current: makeOrbitPoint(currentPoint, endAngle),
      };
    };

    const build3dAirbrushOptions = (stroke, elapsedSeconds = null) => {
      const brushConfig = stroke?.brushConfig || {};
      const requestedSize = Math.max(1, Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const screenSize = Math.max(
        0.25,
        Number(stroke?.lastPreviewPoint?.brushSize)
        || getAirbrushScreenDiameter(
          stroke,
          stroke?.lastHit,
          renderer.domElement.getBoundingClientRect(),
        ),
      );
      const sizeScale = screenSize / requestedSize;
      const worldScale = Math.max(0.01, sizeScale * AIRBRUSH_3D_WIDTH_SCALE);
      const resolutionDetails = get3dAirbrushResolutionDetails();
      const resolutionScale = resolutionDetails.resolutionScale;
      const airbrushDistance = Number(brushConfig.airbrushDistance);
      const disableRotationForCloseRange = Number.isFinite(airbrushDistance)
        && airbrushDistance < 50;
      const rendererSize = getAirbrushGpuRendererSize(
        screenSize,
        {
          ...brushConfig,
          airbrushDiameterScale: worldScale,
          airbrushDotScale: Math.max(
            0.01,
            worldScale * AIRBRUSH_3D_DOT_SCALE_RATIO,
          ),
          ...(disableRotationForCloseRange
            ? { brushRotationEnabled: 0 }
            : {}),
        },
        resolutionScale,
      );


      return {
        ...brushConfig,
        strokeId: stroke?.airbrushStrokeId,
        liveStrokeId: stroke?.airbrushStrokeId,
        strokeKey: stroke?.airbrushStrokeId,
        color: brushConfig.color || brushConfig.strokeColor || '#000000',
        strokeColor: brushConfig.strokeColor || brushConfig.color || '#000000',
        brushSize: requestedSize,
        size: requestedSize,
        airbrushDiameterScale: worldScale,
        airbrushDotScale: Math.max(0.01, worldScale * AIRBRUSH_3D_DOT_SCALE_RATIO),
        airbrushRendererSize: rendererSize,
        airbrushResolutionScale: resolutionScale,
        // 3D uses an exact fractional accumulator so configured dots-per-second
        // are emitted continuously instead of being randomly rounded per frame.
        airbrushExactEmission: true,
        airbrushExactEmissionMultiplier: 10,
        airbrushLiteralEmissionRate: true,
        // Very close 3D spray footprints are too small for the rotation orbit
        // to remain visually stable. Keep rotation exactly as configured from
        // 50 mm upward, but disable it completely below 50 mm.
        ...(disableRotationForCloseRange
          ? { brushRotationEnabled: 0 }
          : {}),
        // 3D movement is projected from screen space onto the model. Long
        // splatter streaks become hook/arrow artefacts at low spray distances,
        // especially at the start, end, and timer boundaries of a stroke. Keep
        // the particle mist, but disable those directional streaks in 3D.
        splatterEnabled: 0,
        airbrushEmissionSeconds: Number.isFinite(Number(elapsedSeconds))
          ? Math.max(0, Math.min(0.1, Number(elapsedSeconds) * AIRBRUSH_3D_SPEED_MULTIPLIER))
          : undefined,
      };
    };

    const drawPreviewAirbrushSample = (
      stroke,
      previousPoint,
      point,
      elapsedSeconds = null,
    ) => {
      if (!previewContext || !stroke || !point) return false;

      const options = build3dAirbrushOptions(stroke, elapsedSeconds);
      const current = toAirbrushPreviewPoint(point);
      const previous = previousPoint ? toAirbrushPreviewPoint(previousPoint) : null;

      // The particle renderer uses a square working region. Clip every emission
      // to the actual world-sized spray footprint before compositing it into the
      // preview atlas. The preview canvas still extends beyond the viewport, so
      // this circular footprint can paint off-screen naturally.
      const currentDiameter = Math.max(
        0.25,
        Number(point?.brushSize)
        || getAirbrushScreenDiameter(
          stroke,
          stroke?.lastHit,
          renderer.domElement.getBoundingClientRect(),
        ),
      );
      const currentRadius = currentDiameter * 0.5;

      const previousDiameter = Math.max(
        0.25,
        Number(previousPoint?.brushSize) || currentDiameter,
      );
      const previousRadius = previousDiameter * 0.5;
      const emulatedPoints = get3dAirbrushStationaryPreviewPoints(
        stroke,
        previous,
        current,
        currentDiameter,
        elapsedSeconds,
      );
      const drawPrevious = emulatedPoints.previous;
      const drawCurrent = emulatedPoints.current;

      previewContext.save();
      previewContext.beginPath();

      if (previous) {
        // Clip to a rounded capsule along the actual raw pointer segment. 3D
        // airbrush must keep a straight centreline; only the spray coverage is
        // softened. Curving the path itself bows straight drags into arcs.
        const clipPoints = [previous, current];
        const clipRadii = [previousRadius, currentRadius];

        clipPoints.forEach((clipPoint, index) => {
          const radius = clipRadii[index];
          previewContext.moveTo(clipPoint.x + radius, clipPoint.y);
          previewContext.arc(clipPoint.x, clipPoint.y, radius, 0, Math.PI * 2);
        });

        for (let index = 1; index < clipPoints.length; index += 1) {
          const from = clipPoints[index - 1];
          const to = clipPoints[index];
          const fromRadius = clipRadii[index - 1];
          const toRadius = clipRadii[index];
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const length = Math.hypot(dx, dy);
          if (length <= 0.001) continue;
          const nx = -dy / length;
          const ny = dx / length;
          previewContext.moveTo(from.x + (nx * fromRadius), from.y + (ny * fromRadius));
          previewContext.lineTo(to.x + (nx * toRadius), to.y + (ny * toRadius));
          previewContext.lineTo(to.x - (nx * toRadius), to.y - (ny * toRadius));
          previewContext.lineTo(from.x - (nx * fromRadius), from.y - (ny * fromRadius));
          previewContext.closePath();
        }
      } else {
        previewContext.arc(
          current.x,
          current.y,
          currentRadius,
          0,
          Math.PI * 2,
        );
      }

      previewContext.clip();

      if (Number.isFinite(Number(elapsedSeconds))) {
        const scaledElapsedSeconds = elapsedSeconds * AIRBRUSH_3D_SPEED_MULTIPLIER;
        drawAirbrushContinuousWithinSelection(
          previewContext,
          drawPrevious,
          drawCurrent,
          options,
          null,
          scaledElapsedSeconds,
        );
      } else {
        drawAirbrushSprayWithinSelection(
          previewContext,
          drawPrevious,
          drawCurrent,
          options,
          null,
        );
      }

      previewContext.restore();
      return true;
    };

    const stop3dAirbrushTimer = (stroke = activePaintStroke) => {
      if (stroke?.airbrushAnimationFrame) {
        window.cancelAnimationFrame(stroke.airbrushAnimationFrame);
        stroke.airbrushAnimationFrame = 0;
      }
    };

    const start3dAirbrushTimer = (stroke) => {
      stop3dAirbrushTimer(stroke);
      const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      stroke.airbrushLastFrameTime = startedAt;
      stroke.airbrushLastLiveRenderTime = startedAt;
      stroke.airbrushLastRenderedPoint = stroke.lastPreviewPoint;
      stroke.airbrushPreviousRenderedPoint = null;
      const sprayFrame = (nowValue) => {
        if (activePaintStroke !== stroke || !stroke?.isAirbrush) {
          stop3dAirbrushTimer(stroke);
          return;
        }
        const now = Number(nowValue) || (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const previousRenderTime = Number(stroke.airbrushLastLiveRenderTime) || now;
        if (now - previousRenderTime >= AIRBRUSH_3D_LIVE_FRAME_INTERVAL_MS) {
          const elapsedSeconds = Math.max(0.001, Math.min(0.1, (now - previousRenderTime) / 1000));
          const currentPoint = stroke.lastPreviewPoint;
          const previousPoint = stroke.airbrushLastRenderedPoint || currentPoint;
          const movementDistance = Math.hypot(
            Number(currentPoint?.screenX) - Number(previousPoint?.screenX),
            Number(currentPoint?.screenY) - Number(previousPoint?.screenY),
          );
          const hasMoved = Number.isFinite(movementDistance) && movementDistance > 0.35;

          drawPreviewAirbrushSample(
            stroke,
            previousPoint,
            currentPoint,
            elapsedSeconds,
          );

          // Only advance the path history when the pointer has genuinely moved.
          // Timer frames emitted while hovering must build paint at the current
          // position without fabricating a movement segment.
          if (hasMoved) {
            stroke.airbrushPreviousRenderedPoint = previousPoint;
            stroke.airbrushLastRenderedPoint = currentPoint;
          }
          stroke.airbrushLastLiveRenderTime = now;
          stroke.airbrushLastFrameTime = now;
        }
        stroke.airbrushAnimationFrame = window.requestAnimationFrame(sprayFrame);
      };
      stroke.airbrushAnimationFrame = window.requestAnimationFrame(sprayFrame);
    };

    // ---- Incremental pen preview -----------------------------------------
    // The original preview cleared the overlay and redrew the complete
    // polyline on every pointer move (48 times for soft brushes), froze pen
    // pressure at the first sample and joined samples with straight lines.
    // Long strokes therefore slowed down quadratically and fast strokes had
    // visible corners. Plain pen strokes now stamp each dab exactly once, at
    // an even spacing, along the same midpoint curves as the 2D canvas, with
    // live pressure. The overlay holds coverage only; brush opacity is
    // applied by the overlay's CSS opacity while drawing and by the projected
    // material on commit, so the two always match.
    const INCREMENTAL_PEN_SPACING_RATIO = 0.08;
    const INCREMENTAL_SOFT_PEN_SPACING_RATIO = 0.1;
    const INCREMENTAL_PEN_PRESSURE_SMOOTHING = 0.3;

    const getBrushSoftnessUnit = (brushConfig) => Math.max(
      0,
      Math.min(100, Number(brushConfig?.softness ?? brushConfig?.brushSoftness) || 0),
    ) / 100;

    const toCssRgba = (colourValue, alpha) => {
      const colour = new THREE.Color();
      try {
        colour.set(String(colourValue || '#000000'));
      } catch {
        colour.set('#000000');
      }
      const rgb = { r: 0, g: 0, b: 0 };
      colour.getRGB(rgb, THREE.SRGBColorSpace);
      return `rgba(${Math.round(rgb.r * 255)}, ${Math.round(rgb.g * 255)}, ${Math.round(rgb.b * 255)}, ${Math.max(0, Math.min(1, alpha))})`;
    };

    const createPenDabSprite = (brushConfig, diameterPx) => {
      const size = Math.max(4, Math.min(1024, Math.ceil((Number(diameterPx) || 1) * previewRenderScale) + 2));
      const sprite = document.createElement('canvas');
      sprite.width = size;
      sprite.height = size;
      const context = sprite.getContext('2d');
      if (!context) return sprite;
      const centre = size / 2;
      const radius = Math.max(0.5, centre - 0.5);
      const colour = brushConfig?.strokeColor || brushConfig?.color || '#000000';
      const softness = getBrushSoftnessUnit(brushConfig);
      if (softness > 0.001) {
        const inner = Math.max(0, 1 - softness);
        const gradient = context.createRadialGradient(centre, centre, 0, centre, centre, radius);
        gradient.addColorStop(0, toCssRgba(colour, 1));
        const steps = 12;
        for (let step = 0; step <= steps; step += 1) {
          const t = step / steps;
          const smooth = t * t * (3 - (2 * t));
          gradient.addColorStop(Math.min(1, inner + ((1 - inner) * t)), toCssRgba(colour, 1 - smooth));
        }
        context.fillStyle = gradient;
        context.fillRect(0, 0, size, size);
      } else {
        context.fillStyle = toCssRgba(colour, 1);
        context.beginPath();
        context.arc(centre, centre, radius, 0, Math.PI * 2);
        context.fill();
      }
      return sprite;
    };

    const applyPreviewCanvasDisplayOpacity = () => {
      if (!previewCanvas?.style || previewCanvas.style.opacity === '0') return;
      previewCanvas.style.opacity = String(previewDisplayOpacity);
    };

    const getIncrementalPenSpacing = (state) => (dab) => Math.max(
      0.35,
      state.baseSize * Math.max(0.05, Number(dab.pressure) || 1) * state.spacingRatio,
    );

    const expandIncrementalPenDirtyRect = (state, minimumX, minimumY, maximumX, maximumY) => {
      const current = state.dirtyRect;
      state.dirtyRect = current
        ? {
            minimumX: Math.min(current.minimumX, minimumX),
            minimumY: Math.min(current.minimumY, minimumY),
            maximumX: Math.max(current.maximumX, maximumX),
            maximumY: Math.max(current.maximumY, maximumY),
          }
        : { minimumX, minimumY, maximumX, maximumY };
    };

    // Clip freshly stamped dabs to the paintable surface mask so paint never
    // appears over air, behind silhouettes or on unselected faces while the
    // stroke is live. Only the region touched since the last call is masked.
    const applyIncrementalPenSurfaceMask = (state) => {
      if (!state?.dirtyRect || !state.surfaceMaskResolved) return;
      const rect = state.dirtyRect;
      state.dirtyRect = null;
      const mask = state.surfaceMask;
      if (!mask || !previewContext || !previewCanvas) return;
      const scale = previewRenderScale;
      const offset = previewOverscan;
      const x = Math.max(0, Math.floor((rect.minimumX + offset) * scale) - 2);
      const y = Math.max(0, Math.floor((rect.minimumY + offset) * scale) - 2);
      const right = Math.min(previewCanvas.width, Math.ceil((rect.maximumX + offset) * scale) + 2);
      const bottom = Math.min(previewCanvas.height, Math.ceil((rect.maximumY + offset) * scale) + 2);
      if (right <= x || bottom <= y) return;
      previewContext.save();
      previewContext.setTransform(1, 0, 0, 1, 0, 0);
      previewContext.beginPath();
      previewContext.rect(x, y, right - x, bottom - y);
      previewContext.clip();
      previewContext.globalCompositeOperation = 'destination-in';
      previewContext.globalAlpha = 1;
      previewContext.imageSmoothingEnabled = false;
      previewContext.drawImage(mask, 0, 0, previewCanvas.width, previewCanvas.height);
      previewContext.restore();
    };

    const drawIncrementalPenDabs = (state, draw) => {
      if (!previewContext || !state?.sprite) return;
      previewContext.save();
      previewContext.globalCompositeOperation = 'source-over';
      previewContext.globalAlpha = state.dabAlpha;
      const emit = (dab) => {
        const diameter = Math.max(0.5, state.baseSize * Math.max(0.05, Math.min(1, Number(dab.pressure) || 1)));
        const radius = diameter * 0.5;
        previewContext.drawImage(state.sprite, dab.x - radius, dab.y - radius, diameter, diameter);
        expandIncrementalPenDirtyRect(state, dab.x - radius, dab.y - radius, dab.x + radius, dab.y + radius);
      };
      draw(getIncrementalPenSpacing(state), emit);
      previewContext.restore();
      applyIncrementalPenSurfaceMask(state);
    };

    const beginIncrementalPenPreview = (brushConfig, point, timeStamp) => {
      const baseSize = Math.max(0.25, Number(point?.brushSize) || Number(brushConfig?.size ?? brushConfig?.brushSize) || 1);
      const softness = getBrushSoftnessUnit(brushConfig);
      const spacingRatio = softness > 0.001 ? INCREMENTAL_SOFT_PEN_SPACING_RATIO : INCREMENTAL_PEN_SPACING_RATIO;
      const state = {
        builder: createCurveStrokeBuilder(),
        stabiliser: createPointStabiliser(),
        pressureSmoother: createPressureSmoother(INCREMENTAL_PEN_PRESSURE_SMOOTHING),
        sprite: createPenDabSprite(brushConfig, baseSize),
        baseSize,
        spacingRatio,
        dabAlpha: softness > 0.001 ? getCompensatedDabFlow(spacingRatio) : 1,
        lastPoint: null,
        lastRawPoint: null,
        finished: false,
        dirtyRect: null,
        surfaceMask: null,
        surfaceMaskResolved: false,
        surfaceMaskTimer: 0,
      };
      previewDisplayOpacity = Math.max(0.01, Math.min(1, Number(brushConfig?.opacity) || 1));
      applyPreviewCanvasDisplayOpacity();
      const pressure = state.pressureSmoother.next(point?.pressure);
      const filtered = state.stabiliser.next({ x: Number(point?.screenX) || 0, y: Number(point?.screenY) || 0 }, timeStamp);
      const first = { x: filtered.x, y: filtered.y, pressure };
      state.lastPoint = first;
      state.lastRawPoint = first;
      drawIncrementalPenDabs(state, (spacing, emit) => state.builder.begin(first, spacing, emit));
      return state;
    };

    const appendIncrementalPenPoint = (state, point, timeStamp) => {
      if (!state || !point || state.finished) return;
      const pressure = state.pressureSmoother.next(point.pressure);
      const filtered = state.stabiliser.next({ x: Number(point.screenX) || 0, y: Number(point.screenY) || 0 }, timeStamp);
      state.lastRawPoint = { x: Number(point.screenX) || 0, y: Number(point.screenY) || 0, pressure };
      const next = { x: filtered.x, y: filtered.y, pressure };
      const last = state.lastPoint;
      if (last && Math.hypot(next.x - last.x, next.y - last.y) < 0.2) return;
      state.lastPoint = next;
      drawIncrementalPenDabs(state, (spacing, emit) => state.builder.add(next, spacing, emit));
    };

    // Finish on the real final pointer position so the stabiliser never
    // shortens a stroke.
    const finishIncrementalPenPreview = (state) => {
      if (!state || state.finished) return;
      if (state.surfaceMaskTimer) {
        window.clearTimeout(state.surfaceMaskTimer);
        state.surfaceMaskTimer = 0;
      }
      // The committed projection is restricted on the GPU anyway; a mask
      // that is still pending is no longer needed.
      state.surfaceMaskResolved = true;
      state.finished = true;
      drawIncrementalPenDabs(state, (spacing, emit) => {
        const raw = state.lastRawPoint;
        const last = state.lastPoint;
        if (raw && last && Math.hypot(raw.x - last.x, raw.y - last.y) > 0.25) state.builder.add(raw, spacing, emit);
        state.builder.finish(spacing, emit);
      });
    };

    const drawPreviewPenStroke = (stroke) => {
      if (!previewContext || !stroke) return false;
      const points = Array.isArray(stroke.previewPoints) ? stroke.previewPoints : [];
      const drawablePoints = points.filter(Boolean);
      if (!drawablePoints.length) return false;

      clearPreviewCanvas();

      const brushConfig = stroke.brushConfig || {};
      const firstPoint = drawablePoints[0];
      const fixedPressure = Math.max(0.01, Math.min(1, Number(firstPoint.pressure) || 1));
      const fixedSize = Math.max(
        0.25,
        Number(firstPoint.brushSize) || Number(brushConfig.size ?? brushConfig.brushSize) || 1,
      );
      const lineWidth = Math.max(0.5, fixedSize * fixedPressure);
      const radius = Math.max(0.25, lineWidth * 0.5);
      const softnessPercent = Math.max(0, Math.min(100, Number(brushConfig.softness ?? brushConfig.brushSoftness) || 0));
      const softness = softnessPercent / 100;
      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig.opacity) || 1));
      const color = String(brushConfig.strokeColor || brushConfig.color || '#000000');

      previewContext.save();
      previewContext.globalCompositeOperation = 'source-over';
      previewContext.strokeStyle = color;
      previewContext.fillStyle = color;
      previewContext.lineCap = 'round';
      previewContext.lineJoin = 'round';

      const drawStrokePath = (width, alpha) => {
        previewContext.globalAlpha = alpha;
        previewContext.lineWidth = Math.max(0.5, width);

        let segmentStarted = false;
        let segmentPointCount = 0;
        let lastSegmentPoint = null;

        const fillPoint = (point) => {
          if (!point) return;
          previewContext.beginPath();
          previewContext.arc(
            point.screenX,
            point.screenY,
            Math.max(0.25, width * 0.5),
            0,
            Math.PI * 2,
          );
          previewContext.fill();
        };

        previewContext.beginPath();
        for (let index = 0; index < points.length; index += 1) {
          const point = points[index];
          if (!point) {
            if (segmentStarted && segmentPointCount === 1) fillPoint(lastSegmentPoint);
            if (segmentStarted && segmentPointCount > 1) previewContext.stroke();
            previewContext.beginPath();
            segmentStarted = false;
            segmentPointCount = 0;
            lastSegmentPoint = null;
            continue;
          }

          lastSegmentPoint = point;
          if (!segmentStarted) {
            previewContext.moveTo(point.screenX, point.screenY);
            segmentStarted = true;
            segmentPointCount = 1;
          } else {
            previewContext.lineTo(point.screenX, point.screenY);
            segmentPointCount += 1;
          }
        }

        if (segmentStarted && segmentPointCount === 1) fillPoint(lastSegmentPoint);
        if (segmentStarted && segmentPointCount > 1) previewContext.stroke();
      };

      if (softness > 0.001) {
        // Draw the entire stroke once per opacity band. The outside diameter
        // always stays at the selected brush size; softness only replaces the
        // solid outer area with a smooth alpha falloff. At 100% there is no hard
        // core, just a full-radius soft profile.
        const layerCount = 48;
        let accumulatedOpacity = 0;

        for (let layerIndex = 1; layerIndex <= layerCount; layerIndex += 1) {
          const progress = layerIndex / layerCount;
          const smoothProgress = progress * progress * (3 - (2 * progress));
          const targetOpacity = opacity * fixedPressure * smoothProgress;
          const remainingOpacity = Math.max(0.000001, 1 - accumulatedOpacity);
          const layerAlpha = Math.max(
            0,
            Math.min(1, (targetOpacity - accumulatedOpacity) / remainingOpacity),
          );
          const widthScale = Math.max(0.01, 1 - (softness * progress));

          if (layerAlpha > 0.00001) {
            drawStrokePath(lineWidth * widthScale, layerAlpha);
            accumulatedOpacity += (1 - accumulatedOpacity) * layerAlpha;
          }
        }
      } else {
        drawStrokePath(lineWidth, opacity * fixedPressure);
      }

      previewContext.restore();
      compositeEraserPreviewSource(brushConfig);
      return true;
    };

    const drawPreviewPencilStroke = (stroke) => {
      if (!previewContext || !stroke) return false;
      const sourcePoints = Array.isArray(stroke.previewPoints) ? stroke.previewPoints : [];
      if (!sourcePoints.length) return false;

      const brushConfig = stroke.brushConfig || {};
      const firstPoint = sourcePoints[0];
      const configuredSize = Math.max(1, Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const screenSize = Math.max(0.5, Number(firstPoint?.brushSize) || configuredSize);
      const screenScale = screenSize / configuredSize;
      const points = sourcePoints.map((point) => ({
        x: Number(point?.screenX) || 0,
        y: Number(point?.screenY) || 0,
        pressure: Math.max(0.01, Math.min(1, Number(point?.pressure) || 1)),
        tiltX: Number(point?.tiltX) || 0,
        tiltY: Number(point?.tiltY) || 0,
        pencilOrientation: point?.pencilOrientation,
      }));

      clearPreviewCanvas();
      previewContext.save();
      previewContext.globalCompositeOperation = 'source-over';
      drawPencilStroke(previewContext, points, {
        ...brushConfig,
        color: brushConfig.color || brushConfig.strokeColor || '#000000',
        rgba: brushConfig.color || brushConfig.strokeColor || '#000000',
        size: Math.max(1, configuredSize * screenScale),
        brushSize: Math.max(1, configuredSize * screenScale),
        strokeSeed: Number(brushConfig.strokeSeed) || 0,
      });
      previewContext.restore();
      return true;
    };

    const toImportedBrushPoint = (point) => ({
      x: Number(point?.screenX) || 0,
      y: Number(point?.screenY) || 0,
      pressure: Math.max(0.01, Math.min(1, Number(point?.pressure) || 1)),
      tiltX: Number(point?.tiltX) || 0,
      tiltY: Number(point?.tiltY) || 0,
      twist: Number(point?.twist) || 0,
    });

    const drawImportedPreviewStart = (point, stroke) => {
      if (!previewContext || !point || !stroke?.brushConfig?.importedBrush) return false;
      const importedPoint = toImportedBrushPoint(point);
      stroke.importedDabIndex = drawImportedBrushImageSegment(
        previewContext,
        importedPoint,
        null,
        stroke.brushConfig,
        stroke.importedDabIndex || 0,
        true,
      );
      stroke.importedPoints = [importedPoint];
      return true;
    };

    const drawImportedPreviewMove = (point, stroke) => {
      if (!previewContext || !point || !stroke?.brushConfig?.importedBrush) return false;
      const currentPoint = toImportedBrushPoint(point);
      const points = Array.isArray(stroke.importedPoints) ? stroke.importedPoints : [];
      const previousPoint = points.length ? points[points.length - 1] : currentPoint;
      const previousPreviousPoint = points.length >= 2 ? points[points.length - 2] : null;

      if (previousPreviousPoint) {
        stroke.importedDabIndex = drawImportedBrushImageCurveSegmentWithinSelection(
          previewContext,
          previousPreviousPoint,
          previousPoint,
          currentPoint,
          stroke.brushConfig,
          null,
          stroke.importedDabIndex || 0,
        );
      } else {
        const midpoint = {
          x: (previousPoint.x + currentPoint.x) * 0.5,
          y: (previousPoint.y + currentPoint.y) * 0.5,
          pressure: (previousPoint.pressure + currentPoint.pressure) * 0.5,
        };
        stroke.importedDabIndex = drawImportedBrushImageSegment(
          previewContext,
          previousPoint,
          midpoint,
          stroke.brushConfig,
          stroke.importedDabIndex || 0,
          false,
        );
      }

      stroke.importedPoints = [...points, currentPoint].slice(-4);
      return true;
    };

    const finishImportedPreviewStroke = (stroke) => {
      if (!previewContext || !stroke?.brushConfig?.importedBrush) return;
      const points = Array.isArray(stroke.importedPoints) ? stroke.importedPoints : [];
      if (points.length < 2) return;
      const previousPoint = points[points.length - 2];
      const currentPoint = points[points.length - 1];
      stroke.importedDabIndex = drawImportedBrushImageFinalTailWithinSelection(
        previewContext,
        previousPoint,
        currentPoint,
        stroke.brushConfig,
        null,
        stroke.importedDabIndex || 0,
      );
    };

    const paintRoundPenDab = (hit, brushConfig, rect, pressure = 1, cachedUvPixelsPerScreenPixel = null) => {
      if (!hit || hit.uvLayoutRevision !== uvLayoutRevision || !activePaintSession?.canvas || !activePaintSession?.context) return false;
      const { canvas: paintCanvas, context: paintContext } = activePaintSession;
      const atlasPoint = uvToCanonicalAtlasPoint(hit.uv, paintCanvas);
      if (!atlasPoint) return false;
      const { x, y } = atlasPoint;
      const uvPixelsPerScreenPixel = Number.isFinite(cachedUvPixelsPerScreenPixel)
        ? cachedUvPixelsPerScreenPixel
        : getUvPixelsPerScreenPixel(hit, rect);
      const safePressure = Math.max(0.01, Math.min(1, Number(pressure) || 1));
      const screenSize = getBrushScreenSize(brushConfig, hit, rect);
      const radius = Math.max(0.25, screenSize * safePressure * uvPixelsPerScreenPixel * 0.5);
      const softnessPercent = Math.max(0, Math.min(100, Number(brushConfig.softness ?? brushConfig.brushSoftness) || 0));
      const softness = softnessPercent / 100;
      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig.opacity) || 1));
      const color = String(brushConfig.strokeColor || brushConfig.color || '#000000');

      if (softness > 0.001) {
        const innerRadius = radius * Math.max(0, 1 - softness);
        const gradient = paintContext.createRadialGradient(x, y, innerRadius, x, y, radius);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        paintContext.fillStyle = gradient;
      } else {
        paintContext.fillStyle = color;
      }

      paintContext.globalCompositeOperation = 'source-over';
      paintContext.globalAlpha = opacity * safePressure;
      paintContext.beginPath();
      paintContext.arc(x, y, radius, 0, Math.PI * 2);
      paintContext.fill();
      paintContext.globalAlpha = 1;
      return true;
    };

    const paintContinuousPenUvSegment = (fromHit, toHit, brushConfig, rect) => {
      if (!fromHit || !toHit || !activePaintSession?.canvas || !activePaintSession?.context) return false;
      if (fromHit.uvLayoutRevision !== uvLayoutRevision || toHit.uvLayoutRevision !== uvLayoutRevision) return false;

      const { canvas: paintCanvas, context: paintContext } = activePaintSession;
      const fromPressure = Math.max(0.01, Math.min(1, Number(fromHit.pressure) || 1));
      const toPressure = Math.max(0.01, Math.min(1, Number(toHit.pressure) || 1));
      const fromScale = getUvPixelsPerScreenPixel(fromHit, rect);
      const toScale = getUvPixelsPerScreenPixel(toHit, rect);
      const fromScreenSize = getBrushScreenSize(brushConfig, fromHit, rect);
      const toScreenSize = getBrushScreenSize(brushConfig, toHit, rect);
      const fromWidth = fromScreenSize * fromPressure * fromScale;
      const toWidth = toScreenSize * toPressure * toScale;
      const lineWidth = Math.max(0.5, (fromWidth + toWidth) * 0.5);
      const averagePressure = (fromPressure + toPressure) * 0.5;
      const softnessPercent = Math.max(0, Math.min(100, Number(brushConfig.softness ?? brushConfig.brushSoftness) || 0));
      const softness = softnessPercent / 100;
      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig.opacity) || 1));
      const color = String(brushConfig.strokeColor || brushConfig.color || '#000000');

      paintContext.save();
      paintContext.globalCompositeOperation = 'source-over';
      paintContext.globalAlpha = opacity * averagePressure;
      paintContext.strokeStyle = color;
      paintContext.lineWidth = lineWidth;
      paintContext.lineCap = 'round';
      paintContext.lineJoin = 'round';
      if (softness > 0.001) {
        paintContext.shadowColor = color;
        paintContext.shadowBlur = Math.max(0.5, lineWidth * softness * 0.5);
      }
      paintContext.beginPath();
      paintContext.moveTo(fromHit.uv.x * paintCanvas.width, fromHit.uv.y * paintCanvas.height);
      paintContext.lineTo(toHit.uv.x * paintCanvas.width, toHit.uv.y * paintCanvas.height);
      paintContext.stroke();
      paintContext.restore();
      return true;
    };

    const paintPenSegment = (fromHit, toHit, brushConfig, rect, uploadAfterSegment = false) => {
      if (!toHit) return false;
      const from = fromHit || toHit;
      const dx = toHit.screenX - from.screenX;
      const dy = toHit.screenY - from.screenY;
      const distance = Math.hypot(dx, dy);
      if (distance < 0.01) return false;

      // Pointer-move already raycasted and recorded both endpoints. Do not perform
      // any new dense-model raycasts during pointer-up.
      const sameSurface = from.object === toHit.object;
      const uvDistance = from.uv && toHit.uv ? from.uv.distanceTo(toHit.uv) : Infinity;
      let painted = false;

      if (sameSurface && uvDistance < 0.1) {
        // Crossing triangle boundaries is fine when the UVs are continuous.
        painted = paintContinuousPenUvSegment(from, toHit, brushConfig, rect);
      } else {
        // At a real UV seam, drawing a UV-space line would cut across the atlas.
        // Paint the recorded endpoint instead; neighbouring dabs preserve the
        // visible stroke without additional raycasts.
        painted = paintRoundPenDab(
          toHit,
          brushConfig,
          rect,
          toHit.pressure,
          toHit.uvPixelsPerScreenPixel,
        );
      }

      if (painted && uploadAfterSegment && activePaintSession?.texture) {
        activePaintSession.texture.needsUpdate = true;
        refreshVisibleMaterial();
      }
      return painted;
    };

    const expandProjectedStrokeCoverage = (sourceCanvas, radius = 1) => {
      if (!sourceCanvas || radius <= 0) return sourceCanvas;

      const width = sourceCanvas.width;
      const height = sourceCanvas.height;
      if (width <= 0 || height <= 0) return sourceCanvas;

      const context = sourceCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!context) return sourceCanvas;

      const sourceImage = context.getImageData(0, 0, width, height);
      const sourcePixels = sourceImage.data;
      const expandedImage = context.createImageData(width, height);
      const expandedPixels = expandedImage.data;
      const wholeRadius = Math.max(1, Math.round(radius));

      // Use a true alpha maximum filter rather than shifted canvas copies.
      // Every newly covered pixel inherits the strongest neighbouring alpha,
      // so the old colour cannot shine through as isolated yellow teeth. The
      // requested radius remains one source-canvas pixel.
      for (let y = 0; y < height; y += 1) {
        const minY = Math.max(0, y - wholeRadius);
        const maxY = Math.min(height - 1, y + wholeRadius);
        for (let x = 0; x < width; x += 1) {
          const minX = Math.max(0, x - wholeRadius);
          const maxX = Math.min(width - 1, x + wholeRadius);
          let strongestAlpha = 0;
          let strongestOffset = ((y * width) + x) * 4;

          for (let sampleY = minY; sampleY <= maxY; sampleY += 1) {
            for (let sampleX = minX; sampleX <= maxX; sampleX += 1) {
              const dx = sampleX - x;
              const dy = sampleY - y;
              if ((dx * dx) + (dy * dy) > (radius + 0.35) * (radius + 0.35)) continue;
              const sampleOffset = ((sampleY * width) + sampleX) * 4;
              const sampleAlpha = sourcePixels[sampleOffset + 3];
              if (sampleAlpha > strongestAlpha) {
                strongestAlpha = sampleAlpha;
                strongestOffset = sampleOffset;
              }
            }
          }

          const targetOffset = ((y * width) + x) * 4;
          expandedPixels[targetOffset] = sourcePixels[strongestOffset];
          expandedPixels[targetOffset + 1] = sourcePixels[strongestOffset + 1];
          expandedPixels[targetOffset + 2] = sourcePixels[strongestOffset + 2];
          expandedPixels[targetOffset + 3] = strongestAlpha;
        }
      }

      context.putImageData(expandedImage, 0, 0);
      return sourceCanvas;
    };

    const expandProjectedStrokeEdgeColour = (sourceCanvas, radius = 2) => {
      if (!sourceCanvas || radius <= 0) return;
      const context = sourceCanvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      const width = sourceCanvas.width;
      const height = sourceCanvas.height;
      if (width <= 0 || height <= 0) return;

      const image = context.getImageData(0, 0, width, height);
      let source = new Uint8ClampedArray(image.data);
      const output = image.data;

      for (let pass = 0; pass < radius; pass += 1) {
        output.set(source);
        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const index = ((y * width) + x) * 4;
            if (source[index + 3] !== 0) continue;

            let red = 0;
            let green = 0;
            let blue = 0;
            let weight = 0;

            for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
              const sampleY = y + offsetY;
              if (sampleY < 0 || sampleY >= height) continue;
              for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
                if (offsetX === 0 && offsetY === 0) continue;
                const sampleX = x + offsetX;
                if (sampleX < 0 || sampleX >= width) continue;
                const sampleIndex = ((sampleY * width) + sampleX) * 4;
                const sampleAlpha = source[sampleIndex + 3];
                if (sampleAlpha === 0) continue;
                red += source[sampleIndex] * sampleAlpha;
                green += source[sampleIndex + 1] * sampleAlpha;
                blue += source[sampleIndex + 2] * sampleAlpha;
                weight += sampleAlpha;
              }
            }

            if (weight > 0) {
              output[index] = Math.round(red / weight);
              output[index + 1] = Math.round(green / weight);
              output[index + 2] = Math.round(blue / weight);
              // Keep these pixels transparent. Only their hidden RGB is expanded
              // so linear filtering cannot blend the stroke edge with black.
              output[index + 3] = 0;
            }
          }
        }
        source = new Uint8ClampedArray(output);
      }

      image.data.set(source);
      context.putImageData(image, 0, 0);
    };

    const cropAirbrushCanvasFromRecordedPath = (
      sourceCanvas,
      stroke,
      padding = 8,
    ) => {
      if (!sourceCanvas || !stroke) return null;
      const points = Array.isArray(stroke.previewPoints)
        ? stroke.previewPoints.filter(Boolean)
        : [];
      if (!points.length) return null;

      const width = sourceCanvas.width;
      const height = sourceCanvas.height;
      const scale = Math.max(0.000001, Number(stroke.projectionRenderScale) || 1);
      const overscan = Math.max(0, Number(stroke.projectionOverscan) || 0);

      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      points.forEach((point) => {
        const radius = Math.max(
          1,
          (
            Number(point.brushSize)
            || Number(stroke.lastPreviewPoint?.brushSize)
            || Number(stroke.brushConfig?.size)
            || 1
          ) * 0.5 * scale,
        );
        const x = (Number(point.screenX) + overscan) * scale;
        const y = (Number(point.screenY) + overscan) * scale;

        minX = Math.min(minX, Math.floor(x - radius));
        minY = Math.min(minY, Math.floor(y - radius));
        maxX = Math.max(maxX, Math.ceil(x + radius));
        maxY = Math.max(maxY, Math.ceil(y + radius));
      });

      if (maxX < minX || maxY < minY) return null;

      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(width - 1, maxX + padding);
      maxY = Math.min(height - 1, maxY + padding);

      const cropWidth = Math.max(1, maxX - minX + 1);
      const cropHeight = Math.max(1, maxY - minY + 1);
      const cropped = document.createElement('canvas');
      cropped.width = cropWidth;
      cropped.height = cropHeight;

      const croppedContext = cropped.getContext('2d');
      if (!croppedContext) return null;
      croppedContext.drawImage(
        sourceCanvas,
        minX,
        minY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      );

      return {
        canvas: cropped,
        rect: {
          x: minX / width,
          y: 1 - ((maxY + 1) / height),
          width: cropWidth / width,
          height: cropHeight / height,
        },
        pixelRect: {
          x: minX,
          y: minY,
          width: cropWidth,
          height: cropHeight,
        },
        fullWidth: width,
        fullHeight: height,
      };
    };

    const cropProjectionStrokeCanvas = (sourceCanvas, padding = 2) => {
      if (!sourceCanvas) return null;
      const context = sourceCanvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      const width = sourceCanvas.width;
      const height = sourceCanvas.height;
      if (width <= 0 || height <= 0) return null;
      const pixels = context.getImageData(0, 0, width, height).data;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      for (let y = 0; y < height; y += 1) {
        const row = y * width * 4;
        for (let x = 0; x < width; x += 1) {
          if (pixels[row + (x * 4) + 3] === 0) continue;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < minX || maxY < minY) return null;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(width - 1, maxX + padding);
      maxY = Math.min(height - 1, maxY + padding);
      const cropped = document.createElement('canvas');
      cropped.width = Math.max(1, maxX - minX + 1);
      cropped.height = Math.max(1, maxY - minY + 1);
      cropped.getContext('2d')?.drawImage(
        sourceCanvas,
        minX, minY, cropped.width, cropped.height,
        0, 0, cropped.width, cropped.height,
      );
      return {
        canvas: cropped,
        rect: {
          x: minX / width,
          // Projector UV space uses a bottom-left origin, while the source
          // canvas uses a top-left origin. Store the cropped rectangle in the
          // projector's UV space so committed strokes land at the same vertical
          // position as the on-screen preview.
          y: 1 - ((maxY + 1) / height),
          width: cropped.width / width,
          height: cropped.height / height,
        },
        pixelRect: {
          x: minX,
          y: minY,
          width: cropped.width,
          height: cropped.height,
        },
        fullWidth: width,
        fullHeight: height,
      };
    };

    const createProjectedHeightTexture = (sourceCanvas) => {
      if (!sourceCanvas) return null;
      const heightCanvas = document.createElement('canvas');
      heightCanvas.width = sourceCanvas.width;
      heightCanvas.height = sourceCanvas.height;
      const heightContext = heightCanvas.getContext('2d');
      if (!heightContext) return null;
      const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
      if (!sourceContext) return null;
      const sourceImage = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
      const heightImage = heightContext.createImageData(sourceCanvas.width, sourceCanvas.height);
      for (let index = 0; index < sourceImage.data.length; index += 4) {
        const alpha = sourceImage.data[index + 3];
        heightImage.data[index] = alpha;
        heightImage.data[index + 1] = alpha;
        heightImage.data[index + 2] = alpha;
        heightImage.data[index + 3] = 255;
      }
      heightContext.putImageData(heightImage, 0, 0);
      const heightTexture = new THREE.CanvasTexture(heightCanvas);
      heightTexture.name = `VoxemeraExactPenHeight-${Date.now()}`;
      heightTexture.colorSpace = THREE.NoColorSpace;
      heightTexture.flipY = true;
      heightTexture.wrapS = THREE.ClampToEdgeWrapping;
      heightTexture.wrapT = THREE.ClampToEdgeWrapping;
      heightTexture.minFilter = THREE.LinearFilter;
      heightTexture.magFilter = THREE.LinearFilter;
      heightTexture.generateMipmaps = false;
      heightTexture.needsUpdate = true;
      return heightTexture;
    };

    const applyProjectionMaterialSettingsToMaterial = (material, settings = materialSettingsRef.current) => {
      if (!material) return;
      const safeGloss = Math.max(0, Math.min(100, Number(settings?.gloss) || 0));
      const safeHeight = Math.max(0, Math.min(100, Number(settings?.height) || 0));
      const safeMetalness = Math.max(0, Math.min(100, Number(settings?.metalness) || 0));
      material.roughness = 1 - (safeGloss / 100);
      material.metalness = safeMetalness / 100;
      material.opacity = 1;
      material.transparent = true;
      material.needsUpdate = true;
      const projectionUniforms = material.userData?.voxemeraProjectionUniforms;
      if (projectionUniforms?.decalOpacity) projectionUniforms.decalOpacity.value = 1;
      if (projectionUniforms?.heightScale) projectionUniforms.heightScale.value = safeHeight / 100;
    };

    const refreshProjectionStrokeMaterials = (settings = materialSettingsRef.current) => {
      const runtime = runtimeRef.current;
      (runtime?.projectionStrokes || []).forEach((stroke) => {
        (stroke.meshes || []).forEach((mesh) => applyProjectionMaterialSettingsToMaterial(mesh?.material, settings));
      });
    };

    const releaseProjectionDepthTarget = (depthEntry) => {
      if (!depthEntry) return;
      depthEntry.refCount = Math.max(0, (depthEntry.refCount || 0) - 1);
      if (depthEntry.refCount > 0) return;
      depthEntry.target?.dispose?.();
      if (depthEntry.cacheKey) projectionDepthCache.delete(depthEntry.cacheKey);
    };

    const disposeExactProjectionStroke = (stroke) => {
      if (!stroke) return;
      (stroke.meshes || []).forEach((mesh) => {
        if (mesh?.parent) mesh.parent.remove(mesh);
        mesh?.material?.dispose?.();
        if (mesh?.geometry?.userData?.voxemeraSelectionGeometry) mesh.geometry.dispose?.();
      });
      stroke.strokeTexture?.dispose?.();
      stroke.heightTexture?.dispose?.();
      releaseProjectionDepthTarget(stroke.depthEntry);
      if (!stroke.depthEntry) stroke.depthTarget?.dispose?.();
      if (stroke.sourceCanvas) {
        stroke.sourceCanvas.width = 1;
        stroke.sourceCanvas.height = 1;
      }
    };

    const disposeLivePbrPreviewStroke = () => {
      if (livePbrPreviewFrameRef.current) {
        window.cancelAnimationFrame(livePbrPreviewFrameRef.current);
        livePbrPreviewFrameRef.current = 0;
      }
      if (livePbrPreviewStrokeRef.current) {
        disposeExactProjectionStroke(livePbrPreviewStrokeRef.current);
        livePbrPreviewStrokeRef.current = null;
      }
    };

    const removeOrphanedProjectionDecalMeshes = () => {
      // Decals are added to sourceMesh.parent. When the imported model itself is
      // a mesh, that parent can be the Three.js scene, making the decal a sibling
      // of currentModelRef.current rather than one of its descendants. Search the
      // complete scene so flattening always removes both nested and sibling decals.
      if (!scene?.traverse) return 0;
      const orphanedMeshes = [];
      scene.traverse((child) => {
        if (!child?.isMesh || !child.userData?.voxemeraProjectionDecal) return;
        orphanedMeshes.push(child);
      });
      orphanedMeshes.forEach((mesh) => {
        if (mesh.parent) mesh.parent.remove(mesh);
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((material) => material?.dispose?.());
        if (mesh.geometry?.userData?.voxemeraSelectionGeometry) mesh.geometry.dispose?.();
      });
      return orphanedMeshes.length;
    };

    const clearExactProjectionStrokes = () => {
      const runtime = runtimeRef.current;
      (runtime?.projectionStrokes || []).forEach(disposeExactProjectionStroke);
      if (runtime) runtime.projectionStrokes = [];
      const removedOrphans = removeOrphanedProjectionDecalMeshes();
      projectionStrokeCountRef.current = 0;
      clearGpuPaintMap(activePaintSession);
      return removedOrphans;
    };

    const setProjectionStrokeCount = (count) => {
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const safeCount = Math.max(0, Math.min(strokes.length, Number(count) || 0));
      strokes.forEach((stroke, index) => {
        const visible = index < safeCount;
        (stroke?.meshes || []).forEach((mesh) => {
          if (mesh) mesh.visible = visible;
        });
      });
      projectionStrokeCountRef.current = safeCount;
      refreshProjectionLayerPresentation();
      rebuildGpuPaintMap();
      scheduleProjectionPaintSave();
      return safeCount;
    };

    const discardProjectionRedoStrokes = () => {
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const keepCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
      if (keepCount >= strokes.length) return;
      strokes.slice(keepCount).forEach(disposeExactProjectionStroke);
      runtime.projectionStrokes = strokes.slice(0, keepCount);
    };

    const getProjectionDecalSide = (sourceMesh) => {
      const material = Array.isArray(sourceMesh?.material)
        ? sourceMesh.material.find((entry) => entry && Number.isFinite(entry.side))
        : sourceMesh?.material;
      const side = material && Number.isFinite(material.side) ? material.side : THREE.FrontSide;
      if (side === THREE.BackSide) return THREE.BackSide;
      if (side === THREE.DoubleSide) return THREE.DoubleSide;
      return THREE.FrontSide;
    };

    const projectionDepthCache = new Map();

    const roundProjectionCacheNumber = (value) => {
      const numeric = Number(value) || 0;
      const factor = 10 ** PROJECTION_CAMERA_CACHE_PRECISION;
      return Math.round(numeric * factor) / factor;
    };

    const normaliseProjectionCameraForCache = (cameraData) => {
      if (!cameraData || typeof cameraData !== 'object') return cameraData;
      const normaliseValue = (value) => {
        if (Array.isArray(value)) return value.map(normaliseValue);
        if (value && typeof value === 'object') {
          return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, normaliseValue(child)]));
        }
        return typeof value === 'number' ? roundProjectionCacheNumber(value) : value;
      };
      return normaliseValue(cameraData);
    };

    const getSafeProjectionDepthDimensions = (width, height) => {
      const safeWidth = Math.max(1, Number(width) || 1);
      const safeHeight = Math.max(1, Number(height) || 1);
      const largestDimension = Math.max(safeWidth, safeHeight);
      const scale = largestDimension > MAX_PROJECTION_DEPTH_DIMENSION
        ? MAX_PROJECTION_DEPTH_DIMENSION / largestDimension
        : 1;
      return {
        width: Math.max(1, Math.round(safeWidth * scale)),
        height: Math.max(1, Math.round(safeHeight * scale)),
      };
    };

    const getProjectionDepthCacheKey = (projectionCamera, width, height) => JSON.stringify({
      width,
      height,
      camera: normaliseProjectionCameraForCache(serializeProjectionCamera(projectionCamera)),
    });

    const captureProjectionDepth = (model, width, height, projectionCamera = camera) => {
      const depthDimensions = getSafeProjectionDepthDimensions(width, height);
      const cacheKey = getProjectionDepthCacheKey(projectionCamera, depthDimensions.width, depthDimensions.height);
      const cached = projectionDepthCache.get(cacheKey);
      if (cached) {
        cached.refCount += 1;
        cached.lastUsed = performance.now();
        return cached;
      }
      const target = new THREE.WebGLRenderTarget(depthDimensions.width, depthDimensions.height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.generateMipmaps = false;
      target.texture.colorSpace = THREE.NoColorSpace;
      target.depthTexture = new THREE.DepthTexture(
        depthDimensions.width,
        depthDimensions.height,
        THREE.UnsignedIntType,
      );
      target.depthTexture.format = THREE.DepthFormat;
      target.depthTexture.type = THREE.UnsignedIntType;
      target.depthTexture.minFilter = THREE.NearestFilter;
      target.depthTexture.magFilter = THREE.NearestFilter;
      target.depthTexture.generateMipmaps = false;
      target.depthTexture.name = `VoxemeraProjectionVisibleDepth-${Date.now()}`;

      const depthMaterialsBySide = new Map();
      const getDepthMaterial = (side) => {
        const safeSide = side === THREE.BackSide || side === THREE.DoubleSide ? side : THREE.FrontSide;
        if (depthMaterialsBySide.has(safeSide)) return depthMaterialsBySide.get(safeSide);
        const material = new THREE.MeshDepthMaterial({
          depthPacking: THREE.RGBADepthPacking,
          side: safeSide,
        });
        material.blending = THREE.NoBlending;
        depthMaterialsBySide.set(safeSide, material);
        return material;
      };

      const savedMaterials = [];
      const savedDecalVisibility = [];
      model.traverse((child) => {
        if (!child?.isMesh) return;
        if (child.userData?.voxemeraProjectionDecal) {
          savedDecalVisibility.push([child, child.visible]);
          child.visible = false;
          return;
        }
        const originalMaterial = child.material;
        const sourceMaterials = Array.isArray(originalMaterial) ? originalMaterial : [originalMaterial];
        const depthMaterials = sourceMaterials.map((sourceMaterial) => {
          const sourceSide = sourceMaterial && Number.isFinite(sourceMaterial.side)
            ? sourceMaterial.side
            : THREE.FrontSide;
          return getDepthMaterial(sourceSide);
        });
        savedMaterials.push([child, originalMaterial, child.visible]);
        child.material = Array.isArray(originalMaterial) ? depthMaterials : depthMaterials[0];
        child.visible = true;
      });

      const savedVisibility = scene.children.map((child) => [child, child.visible]);
      scene.children.forEach((child) => { child.visible = child === model; });
      const previousTarget = renderer.getRenderTarget();
      const previousToneMapping = renderer.toneMapping;
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();

      renderer.toneMapping = THREE.NoToneMapping;
      renderer.setRenderTarget(target);
      renderer.setClearColor(0xffffff, 1);
      renderer.clear(true, true, true);
      renderer.render(scene, projectionCamera);
      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(previousClearColor, previousClearAlpha);
      renderer.toneMapping = previousToneMapping;

      savedVisibility.forEach(([child, visible]) => { child.visible = visible; });
      savedMaterials.forEach(([child, material, visible]) => {
        child.material = material;
        child.visible = visible;
      });
      savedDecalVisibility.forEach(([child, visible]) => { child.visible = visible; });
      depthMaterialsBySide.forEach((material) => material.dispose());
      const entry = { cacheKey, target, refCount: 1, lastUsed: performance.now() };
      projectionDepthCache.set(cacheKey, entry);
      if (projectionDepthCache.size > MAX_PROJECTION_DEPTH_CACHE_ENTRIES) {
        [...projectionDepthCache.values()]
          .filter((candidate) => candidate !== entry && candidate.refCount <= 0)
          .sort((a, b) => a.lastUsed - b.lastUsed)
          .slice(0, projectionDepthCache.size - MAX_PROJECTION_DEPTH_CACHE_ENTRIES)
          .forEach((candidate) => {
            candidate.target?.dispose?.();
            projectionDepthCache.delete(candidate.cacheKey);
          });
      }
      return entry;
    };

    const applyAirbrushFinalPathMask = (
      canvas,
      stroke,
      croppedStroke,
    ) => {
      if (
        !canvas?.width
        || !canvas?.height
        || !stroke
        || !croppedStroke?.pixelRect
      ) return canvas;

      const points = Array.isArray(stroke.previewPoints)
        ? stroke.previewPoints.filter(Boolean)
        : [];
      if (!points.length) return canvas;

      const scale = Math.max(0.000001, Number(stroke.projectionRenderScale) || 1);
      const overscan = Math.max(0, Number(stroke.projectionOverscan) || 0);
      const cropX = Number(croppedStroke.pixelRect.x) || 0;
      const cropY = Number(croppedStroke.pixelRect.y) || 0;

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      const maskContext = maskCanvas.getContext('2d');
      const targetContext = canvas.getContext('2d');
      if (!maskContext || !targetContext) return canvas;

      const mapPoint = (point) => ({
        x: ((Number(point.screenX) + overscan) * scale) - cropX,
        y: ((Number(point.screenY) + overscan) * scale) - cropY,
        radius: Math.max(
          0.5,
          (Number(point.brushSize)
            || Number(stroke.lastPreviewPoint?.brushSize)
            || Number(stroke.brushConfig?.size)
            || 1) * 0.5 * scale,
        ),
      });

      const mapped = points.map(mapPoint);
      maskContext.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      maskContext.fillStyle = '#fff';

      // Draw a union of round endpoint dabs and connecting capsules. This is the
      // exact screen projection of the fixed world-sized brush path, including
      // portions outside the visible viewport because overscan is included.
      for (let index = 0; index < mapped.length; index += 1) {
        const current = mapped[index];

        maskContext.beginPath();
        maskContext.arc(
          current.x,
          current.y,
          current.radius,
          0,
          Math.PI * 2,
        );
        maskContext.fill();

        if (index === 0) continue;
        const previous = mapped[index - 1];
        const dx = current.x - previous.x;
        const dy = current.y - previous.y;
        const length = Math.hypot(dx, dy);
        if (length <= 0.0001) continue;

        const nx = -dy / length;
        const ny = dx / length;
        maskContext.beginPath();
        maskContext.moveTo(
          previous.x + (nx * previous.radius),
          previous.y + (ny * previous.radius),
        );
        maskContext.lineTo(
          current.x + (nx * current.radius),
          current.y + (ny * current.radius),
        );
        maskContext.lineTo(
          current.x - (nx * current.radius),
          current.y - (ny * current.radius),
        );
        maskContext.lineTo(
          previous.x - (nx * previous.radius),
          previous.y - (ny * previous.radius),
        );
        maskContext.closePath();
        maskContext.fill();
      }

      targetContext.save();
      targetContext.globalCompositeOperation = 'destination-in';
      targetContext.drawImage(maskCanvas, 0, 0);
      targetContext.restore();
      return canvas;
    };



    const commitExactPreviewProjection = (stroke, options = {}) => {

      const model = currentModelRef.current;
      const runtime = runtimeRef.current;
      const previewOnly = options?.previewOnly === true;
      if (!model || !runtime || !previewCanvas || !previewContext) return false;

      let strokeCanvas = stroke?.restoredCanvas || document.createElement('canvas');
      let projectionRect = stroke?.projectionRect || null;
      let projectionFullWidth = Number(stroke?.projectionFullWidth) || 0;
      let projectionFullHeight = Number(stroke?.projectionFullHeight) || 0;
      let projectionCamera = stroke?.projectionCamera || camera;
      if (!stroke?.restoredCanvas) {
        strokeCanvas.width = previewCanvas.width;
        strokeCanvas.height = previewCanvas.height;
        const strokeContext = strokeCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
        if (!strokeContext) return false;
        strokeContext.imageSmoothingEnabled = false;
        strokeContext.clearRect(0, 0, strokeCanvas.width, strokeCanvas.height);
        strokeContext.drawImage(previewCanvas, 0, 0);
        if (!applySelectionMaskToCanvas(strokeCanvas)) {
          refreshSelectionDebug('selection-mask-failed');
          return false;
        }
        const croppedStroke = stroke?.isAirbrush
          ? cropAirbrushCanvasFromRecordedPath(strokeCanvas, stroke)
          : cropProjectionStrokeCanvas(strokeCanvas);
        if (!croppedStroke) {
          refreshSelectionDebug('crop-returned-empty');
          return false;
        }
        strokeCanvas.width = 1;
        strokeCanvas.height = 1;
        strokeCanvas = croppedStroke.canvas;

        if (stroke?.isAirbrush) {
          // The shared 2D renderer already produced genuine transparent alpha.
          applyAirbrushFinalPathMask(
            strokeCanvas,
            stroke,
            croppedStroke,
          );
        }

        // Commit airbrush/pen projections from the cropped sub-view instead of
        // treating the stroke as a tiny island inside a giant full-viewport
        // projection texture. This keeps the usable texel density high.
        projectionRect = { x: 0, y: 0, width: 1, height: 1 };
        projectionFullWidth = strokeCanvas.width;
        projectionFullHeight = strokeCanvas.height;
        projectionCamera = createProjectionSubCamera(
          stroke?.projectionCamera || camera,
          croppedStroke.fullWidth,
          croppedStroke.fullHeight,
          croppedStroke.pixelRect,
        );
      }
      if (!projectionRect) projectionRect = { x: 0, y: 0, width: 1, height: 1 };
      if (!projectionFullWidth) projectionFullWidth = strokeCanvas.width;
      if (!projectionFullHeight) projectionFullHeight = strokeCanvas.height;


      const strokeTexture = new THREE.CanvasTexture(strokeCanvas);
      const heightTexture = createProjectedHeightTexture(strokeCanvas);
      strokeTexture.name = `VoxemeraExactPenProjection-${Date.now()}`;
      strokeTexture.colorSpace = THREE.SRGBColorSpace;
      strokeTexture.flipY = true;
      strokeTexture.wrapS = THREE.ClampToEdgeWrapping;
      strokeTexture.wrapT = THREE.ClampToEdgeWrapping;
      strokeTexture.minFilter = THREE.LinearFilter;
      strokeTexture.magFilter = THREE.LinearFilter;
      strokeTexture.generateMipmaps = false;
      strokeTexture.needsUpdate = true;

      model.updateMatrixWorld(true);
      projectionCamera.updateMatrixWorld(true);
      projectionCamera.updateProjectionMatrix();
      const depthEntry = captureProjectionDepth(model, projectionFullWidth, projectionFullHeight, projectionCamera);
      const depthTarget = depthEntry.target;
      const projectionViewMatrix = new THREE.Matrix4().multiplyMatrices(
        projectionCamera.projectionMatrix,
        projectionCamera.matrixWorldInverse,
      );
      const meshes = [];
      const materialSettings = stroke?.materialSettings || materialSettingsRef.current;
      const pbrSetKey = stroke?.brushConfig?.pbrSetKey || '';
      const pbrResource = stroke?.isPbr
        ? (pbrSetResourcesRef.current.get(pbrSetKey) || getRuntimeActivePbrBrushResource())
        : null;
      const pbrTextures = getPbrRuntimeTextures(pbrResource);
      const strokeLayerInfo = getProjectionLayerInfo(stroke?.layerId);
      const strokeNeedsEdgeWear = Math.abs(Number(strokeLayerInfo?.edgeWear) || 0) > 0.0001;

      const startingSurfaceWorldPoint = stroke?.startingSurfaceWorldPoint?.isVector3
        ? stroke.startingSurfaceWorldPoint.clone()
        : (Array.isArray(stroke?.startingSurfaceWorldPoint)
          ? new THREE.Vector3(...stroke.startingSurfaceWorldPoint)
          : null);
      let surfaceLockDepth = 0;
      let surfaceLockDepthTolerance = 1;
      const surfaceLockEnabled = Boolean(
        stroke?.allowedMeshUuid
        && startingSurfaceWorldPoint
        && Number.isFinite(Number(stroke?.surfaceDepthTolerance))
      );
      if (surfaceLockEnabled) {
        const projectedSeed = startingSurfaceWorldPoint.clone().project(projectionCamera);
        surfaceLockDepth = (projectedSeed.z * 0.5) + 0.5;

        const cameraDirection = new THREE.Vector3();
        projectionCamera.getWorldDirection(cameraDirection);
        const worldTolerance = Math.max(0.000001, Number(stroke.surfaceDepthTolerance) || 0.000001);
        const projectedNear = startingSurfaceWorldPoint.clone()
          .addScaledVector(cameraDirection, worldTolerance)
          .project(projectionCamera);
        const projectedFar = startingSurfaceWorldPoint.clone()
          .addScaledVector(cameraDirection, -worldTolerance)
          .project(projectionCamera);
        surfaceLockDepthTolerance = Math.max(
          0.00002,
          Math.abs(((projectedNear.z * 0.5) + 0.5) - surfaceLockDepth),
          Math.abs(((projectedFar.z * 0.5) + 0.5) - surfaceLockDepth),
        );
              }

      const pbrWorldBounds = new THREE.Box3().setFromObject(model);
      const pbrWorldOrigin = pbrWorldBounds.isEmpty()
        ? new THREE.Vector3()
        : pbrWorldBounds.min.clone();
      const pbrWorldDimensions = pbrWorldBounds.isEmpty()
        ? new THREE.Vector3(1, 1, 1)
        : pbrWorldBounds.getSize(new THREE.Vector3());
      const pbrWorldTileSize = Math.max(
        0.00001,
        Math.max(pbrWorldDimensions.x, pbrWorldDimensions.y, pbrWorldDimensions.z, 0.001) * 0.12,
      );
      const strokeOpacity = getProjectionStrokeOpacity(stroke);

      model.traverse((sourceMesh) => {
        if (!sourceMesh?.isMesh || sourceMesh.userData?.voxemeraProjectionDecal) return;
        // A committed stroke belongs to the surface mesh selected on pointer-down.
        // Reject unrelated model meshes before allocating their projection shader.
        // Previously this check happened after a full material/shader was created.
        if (stroke?.allowedMeshUuid && sourceMesh.uuid !== stroke.allowedMeshUuid) return;
        const projectorMatrix = new THREE.Matrix4().multiplyMatrices(projectionViewMatrix, sourceMesh.matrixWorld);
        const projectorCameraLocal = sourceMesh.worldToLocal(projectionCamera.position.clone());
        const projectionSide = getProjectionDecalSide(sourceMesh);
        const projectionUniforms = {
          strokeMap: { value: strokeTexture },
          projectorDepthMap: { value: depthTarget.depthTexture || depthTarget.texture },
          heightMap: { value: heightTexture },
          projectorMatrix: { value: projectorMatrix },
          projectorCameraLocal: { value: projectorCameraLocal },
          depthTolerance: { value: stroke?.isTextDecal === true ? 0.0015 : 0.00002 },
          surfaceLockEnabled: { value: surfaceLockEnabled ? 1 : 0 },
          surfaceLockDepth: { value: surfaceLockDepth },
          surfaceLockDepthTolerance: { value: surfaceLockDepthTolerance },
          decalOpacity: { value: 1 },
          edgeWear: { value: strokeNeedsEdgeWear ? strokeLayerInfo.edgeWear : 0 },
          edgeWearMaxDistance: { value: strokeNeedsEdgeWear
            ? Math.max(0.000001, Number(sourceMesh.geometry?.userData?.voxemeraEdgeWearMeta?.maxSurfaceDistance) || 0.000001)
            : 1 },
          edgeWearDistanceMap: { value: strokeNeedsEdgeWear
            ? getEdgeWearDistanceTexture(sourceMesh.geometry)
            : getEdgeWearDistanceFallbackTexture(1) },
          voxemeraCurvatureMap: { value: strokeNeedsEdgeWear
            ? getArmorPaintCurvatureTexture(sourceMesh.geometry)
            : strokeTexture },
          // Hard pen strokes use binary coverage so antialiased mask pixels cannot
          // create dark overlap seams. Airbrush and pencil must preserve their
          // real per-pixel alpha because that alpha contains their soft/textured
          // appearance. Imported Procreate/Krita brushes also carry grain, scatter
          // and pressure information in this alpha texture.
          incidenceAlpha: { value: (stroke?.isAirbrush || stroke?.isPencil || stroke?.isImportedBrush) ? 1 : 0 },
          strokeOpacity: { value: strokeOpacity },
          imageDecalProjection: { value: stroke?.isImageDecal === true ? 1 : 0 },
          textDecalProjection: { value: stroke?.isTextDecal === true ? 1 : 0 },
          heightScale: { value: Math.max(0, Math.min(1, (Number(materialSettings?.height) || 0) / 100)) },
          projectionRect: { value: new THREE.Vector4(projectionRect.x, projectionRect.y, projectionRect.width, projectionRect.height) },
          strokeTexelSize: { value: new THREE.Vector2(1 / Math.max(1, strokeCanvas.width), 1 / Math.max(1, strokeCanvas.height)) },
          penColour: {
            value: new THREE.Color(
              stroke?.brushConfig?.strokeColor
              || stroke?.brushConfig?.color
              || '#000000',
            ),
          },
          penSoftnessActive: {
            value: Math.max(
              0,
              Math.min(
                100,
                Number(stroke?.brushConfig?.softness ?? stroke?.brushConfig?.brushSoftness) || 0,
              ),
            ) > 0 ? 1 : 0,
          },
          airbrushProjection: { value: stroke?.isAirbrush === true ? 1 : 0 },
          pbrAlbedoMap: { value: pbrTextures?.albedo || strokeTexture },
          pbrMaterialMap: { value: pbrTextures?.material || strokeTexture },
          pbrNormalMap: { value: pbrTextures?.normal || strokeTexture },
          normalLayerMap: { value: pbrTextures?.normal || strokeTexture },
          normalLayerHasMap: { value: 0 },
          normalLayerMix: { value: 1 },
          normalLayerScale: { value: Math.max(0.1, Math.min(8, (Number(stroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100)) },
          normalLayerAngle: { value: THREE.MathUtils.degToRad(Number(stroke?.brushConfig?.pbrAngleDegrees) || 0) },
          normalBelowMap: { value: strokeTexture },
          normalBelowHasMap: { value: 0 },
          pbrHasAlbedo: { value: pbrTextures?.albedo ? 1 : 0 },
          pbrHasMaterial: { value: pbrTextures?.material ? 1 : 0 },
          pbrHasNormal: { value: pbrTextures?.normal ? 1 : 0 },
          pbrScale: { value: Math.max(0.1, Math.min(8, (Number(stroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100)) },
          pbrAngle: { value: THREE.MathUtils.degToRad(Number(stroke?.brushConfig?.pbrAngleDegrees) || 0) },
          pbrWorldOrigin: { value: pbrWorldOrigin },
          pbrWorldTileSize: { value: pbrWorldTileSize },
        };

        let material;
        const isEraserProjection = stroke?.isEraser === true;
        const penSoftnessPercent = Math.max(
          0,
          Math.min(
            100,
            Number(stroke?.brushConfig?.softness ?? stroke?.brushConfig?.brushSoftness) || 0,
          ),
        );
        const isTexturedProjection = stroke?.isAirbrush === true
          || stroke?.isPencil === true
          || stroke?.isImportedBrush === true;

        if (isEraserProjection) {
          const baselineTexture = activePaintSession?.baselineTexture || activePaintSession?.texture || null;
          material = new THREE.MeshStandardMaterial({
            map: baselineTexture,
            color: 0xffffff,
            roughness: 0.82,
            metalness: 0,
            opacity: 1,
            transparent: false,
            blending: THREE.NoBlending,
            depthTest: true,
            depthWrite: false,
            side: projectionSide,
            polygonOffset: true,
            polygonOffsetFactor: -3,
            polygonOffsetUnits: -3,
            toneMapped: true,
          });
          material.userData = {
            ...(material.userData || {}),
            voxemeraProjectionUniforms: projectionUniforms,
            voxemeraEraserProjection: true,
          };
          material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, projectionUniforms);
            material.userData.voxemeraProjectionShader = shader;
            shader.vertexShader = `
              uniform mat4 projectorMatrix;
              attribute vec3 voxemeraEdgeWearBarycentric;
              attribute vec3 voxemeraEdgeWearSharpEdges;
              attribute float voxemeraEdgeWearSurfaceDistance;
              attribute float voxemeraEdgeWearInteriorLift;
              attribute vec3 voxemeraEdgeWearAltitudes;
              attribute vec3 voxemeraEdgeWearNearestStart0;
              attribute vec3 voxemeraEdgeWearNearestEnd0;
              attribute vec3 voxemeraEdgeWearNearestStart1;
              attribute vec3 voxemeraEdgeWearNearestEnd1;
              attribute vec2 voxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              varying vec4 vProjectorPosition;
              varying vec3 vNormalBlendWorldPosition;
              varying vec3 vNormalBlendWorldNormal;
            ` + shader.vertexShader
              .replace(
                '#include <beginnormal_vertex>',
                `#include <beginnormal_vertex>
                vNormalBlendWorldNormal = normalize(mat3(modelMatrix) * objectNormal);`,
              )
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                vVoxemeraEdgeWearBarycentric = voxemeraEdgeWearBarycentric;
                vVoxemeraEdgeWearSharpEdges = voxemeraEdgeWearSharpEdges;
                vVoxemeraEdgeWearSurfaceDistance = voxemeraEdgeWearSurfaceDistance;
                vVoxemeraEdgeWearInteriorLift = voxemeraEdgeWearInteriorLift;
                vVoxemeraEdgeWearAltitudes = voxemeraEdgeWearAltitudes;
                vVoxemeraEdgeWearNearestStart0 = voxemeraEdgeWearNearestStart0;
                vVoxemeraEdgeWearNearestEnd0 = voxemeraEdgeWearNearestEnd0;
                vVoxemeraEdgeWearNearestStart1 = voxemeraEdgeWearNearestStart1;
                vVoxemeraEdgeWearNearestEnd1 = voxemeraEdgeWearNearestEnd1;
                vVoxemeraEdgeWearNearestValid = voxemeraEdgeWearNearestValid;
                vVoxemeraWearPosition = transformed;
                vVoxemeraCurvatureUv = uv;
                vVoxemeraCurvatureNormal = normalize(objectNormal);
                vec4 normalBlendWorldPosition = modelMatrix * vec4(transformed, 1.0);
                vNormalBlendWorldPosition = normalBlendWorldPosition.xyz;
                vProjectorPosition = projectorMatrix * vec4(transformed, 1.0);`,
              );
            shader.fragmentShader = `
              uniform sampler2D strokeMap;
              uniform sampler2D projectorDepthMap;
              uniform float depthTolerance;
              uniform float edgeWear;
              uniform float edgeWearMaxDistance;
              uniform sampler2D edgeWearDistanceMap;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              float voxemeraStableWearKeep(float wearAmount) {
                if (abs(wearAmount) <= 0.0001) return 1.0;
                float signedAmount = clamp(wearAmount, -1.0, 1.0);
                float amount = abs(signedAmount);
                float maximumDistance = max(edgeWearMaxDistance, 0.000001);
                vec2 wearUv = vec2(fract(vVoxemeraCurvatureUv.x), fract(vVoxemeraCurvatureUv.y));
                float surfaceDistance = texture2D(edgeWearDistanceMap, wearUv).r;
                if (signedAmount > 0.0) {
                  if (signedAmount >= 0.9999) return 0.0;
                  float wearFront = pow(amount, 2.8) * maximumDistance;
                  float featherWidth = max(wearFront * 0.5, maximumDistance * 0.002);
                  float innerWearFront = max(0.0, wearFront - featherWidth);
                  return smoothstep(innerWearFront, wearFront, surfaceDistance);
                }
                // Remap the left dial so the behaviour that used to live
                // in stages 0-85 now occupies the first 25% of the dial, and
                // the old 86-100 range now occupies the final 75%.
                float dialStage = floor((amount * 100.0) + 0.5) / 100.0;
                if (dialStage >= 1.0) return 0.0;
                float remappedStage = dialStage <= 0.25
                  ? (dialStage / 0.25) * 0.85
                  : 0.85 + (((dialStage - 0.25) / 0.75) * 0.15);
                float oneStageDistance = maximumDistance / 100.0;
                float retainedEdgeWidth = maximumDistance * (1.0 - remappedStage);
                float featherWidth = max(oneStageDistance * 0.5, maximumDistance * 0.00002);
                float fullyPaintedEdgeWidth = max(0.0, retainedEdgeWidth - featherWidth);
                return 1.0 - smoothstep(
                  fullyPaintedEdgeWidth,
                  retainedEdgeWidth,
                  surfaceDistance
                );
              }

              uniform float surfaceLockEnabled;
              uniform float surfaceLockDepth;
              uniform float surfaceLockDepthTolerance;
              uniform vec4 projectionRect;
              varying vec4 vProjectorPosition;
            ` + shader.fragmentShader.replace(
              '#include <map_fragment>',
              `
              if (vProjectorPosition.w <= 0.0) discard;
              vec3 projected = vProjectorPosition.xyz / vProjectorPosition.w;
              vec2 projectorUv = projected.xy * 0.5 + 0.5;
              if (projectorUv.x <= 0.0 || projectorUv.x >= 1.0 || projectorUv.y <= 0.0 || projectorUv.y >= 1.0) discard;
              float fragmentDepth = projected.z * 0.5 + 0.5;
              float visibleDepth = texture2D(projectorDepthMap, projectorUv).x;
              float adaptiveTolerance = max(depthTolerance, fwidth(fragmentDepth) * 0.35);
              if (abs(fragmentDepth - visibleDepth) > adaptiveTolerance) discard;
              if (
                surfaceLockEnabled > 0.5
                && abs(fragmentDepth - surfaceLockDepth) > surfaceLockDepthTolerance
              ) discard;
              vec2 strokeUv = (projectorUv - projectionRect.xy) / projectionRect.zw;
              if (strokeUv.x <= 0.0 || strokeUv.x >= 1.0 || strokeUv.y <= 0.0 || strokeUv.y >= 1.0) discard;
              float coverage = texture2D(strokeMap, strokeUv).a;
              if (coverage < 0.35) discard;
              #include <map_fragment>
              diffuseColor.a = 1.0;
              `,
            );
          };
          material.customProgramCacheKey = () => 'voxemeraEraserProjection_v1';
        } else if (stroke?.isPbr === true && pbrResource) {
          const isSoftPbrPen = penSoftnessPercent > 0
            && stroke?.isAirbrush !== true
            && stroke?.isPencil !== true
            && stroke?.isImportedBrush !== true;
          const pbrPenNeedsBlending = isSoftPbrPen || strokeOpacity < 1;
          material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.82,
            metalness: 0,
            opacity: strokeOpacity,
            transparent: pbrPenNeedsBlending,
            blending: pbrPenNeedsBlending ? THREE.NormalBlending : THREE.NoBlending,
            depthTest: true,
            depthWrite: false,
            side: projectionSide,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            toneMapped: true,
          });
          material.userData = {
            ...(material.userData || {}),
            voxemeraProjectionUniforms: projectionUniforms,
            voxemeraPbrProjection: true,
          };
          material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, projectionUniforms);
            material.userData.voxemeraProjectionShader = shader;
            shader.vertexShader = `
              uniform mat4 projectorMatrix;
              attribute vec3 voxemeraEdgeWearBarycentric;
              attribute vec3 voxemeraEdgeWearSharpEdges;
              attribute float voxemeraEdgeWearSurfaceDistance;
              attribute float voxemeraEdgeWearInteriorLift;
              attribute vec3 voxemeraEdgeWearAltitudes;
              attribute vec3 voxemeraEdgeWearNearestStart0;
              attribute vec3 voxemeraEdgeWearNearestEnd0;
              attribute vec3 voxemeraEdgeWearNearestStart1;
              attribute vec3 voxemeraEdgeWearNearestEnd1;
              attribute vec2 voxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              varying vec4 vProjectorPosition;
              varying vec3 vPbrWorldPosition;
              varying vec3 vPbrWorldNormal;
              varying vec2 vNormalBelowUv;
            ` + shader.vertexShader
              .replace(
                '#include <beginnormal_vertex>',
                `#include <beginnormal_vertex>
                vPbrWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
                vNormalBelowUv = uv;`,
              )
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                vVoxemeraEdgeWearBarycentric = voxemeraEdgeWearBarycentric;
                vVoxemeraEdgeWearSharpEdges = voxemeraEdgeWearSharpEdges;
                vVoxemeraEdgeWearSurfaceDistance = voxemeraEdgeWearSurfaceDistance;
                vVoxemeraEdgeWearInteriorLift = voxemeraEdgeWearInteriorLift;
                vVoxemeraEdgeWearAltitudes = voxemeraEdgeWearAltitudes;
                vVoxemeraEdgeWearNearestStart0 = voxemeraEdgeWearNearestStart0;
                vVoxemeraEdgeWearNearestEnd0 = voxemeraEdgeWearNearestEnd0;
                vVoxemeraEdgeWearNearestStart1 = voxemeraEdgeWearNearestStart1;
                vVoxemeraEdgeWearNearestEnd1 = voxemeraEdgeWearNearestEnd1;
                vVoxemeraEdgeWearNearestValid = voxemeraEdgeWearNearestValid;
                vVoxemeraWearPosition = transformed;
                vVoxemeraCurvatureUv = uv;
                vVoxemeraCurvatureNormal = normalize(objectNormal);
                vec4 pbrWorldPosition = modelMatrix * vec4(transformed, 1.0);
                vPbrWorldPosition = pbrWorldPosition.xyz;
                vProjectorPosition = projectorMatrix * vec4(transformed, 1.0);`,
              );
            shader.fragmentShader = `
              uniform sampler2D strokeMap;
              uniform sampler2D projectorDepthMap;
              uniform sampler2D pbrAlbedoMap;
              uniform sampler2D pbrMaterialMap;
              uniform sampler2D pbrNormalMap;
              uniform sampler2D normalLayerMap;
              uniform float normalLayerHasMap;
              uniform float normalLayerMix;
              uniform float normalLayerScale;
              uniform float normalLayerAngle;
              uniform sampler2D normalBelowMap;
              uniform float normalBelowHasMap;
              uniform float pbrHasAlbedo;
              uniform float pbrHasMaterial;
              uniform float pbrHasNormal;
              uniform float pbrScale;
              uniform float pbrAngle;
              uniform vec3 pbrWorldOrigin;
              uniform float pbrWorldTileSize;
              uniform float heightScale;
              uniform float depthTolerance;
              uniform float edgeWear;
              uniform float edgeWearMaxDistance;
              uniform sampler2D edgeWearDistanceMap;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              float voxemeraStableWearKeep(float wearAmount) {
                if (abs(wearAmount) <= 0.0001) return 1.0;
                float signedAmount = clamp(wearAmount, -1.0, 1.0);
                float amount = abs(signedAmount);
                float maximumDistance = max(edgeWearMaxDistance, 0.000001);
                vec2 wearUv = vec2(fract(vVoxemeraCurvatureUv.x), fract(vVoxemeraCurvatureUv.y));
                float surfaceDistance = texture2D(edgeWearDistanceMap, wearUv).r;
                if (signedAmount > 0.0) {
                  if (signedAmount >= 0.9999) return 0.0;
                  float wearFront = pow(amount, 2.8) * maximumDistance;
                  float featherWidth = max(wearFront * 0.5, maximumDistance * 0.002);
                  float innerWearFront = max(0.0, wearFront - featherWidth);
                  return smoothstep(innerWearFront, wearFront, surfaceDistance);
                }
                // Remap the left dial so the behaviour that used to live
                // in stages 0-85 now occupies the first 25% of the dial, and
                // the old 86-100 range now occupies the final 75%.
                float dialStage = floor((amount * 100.0) + 0.5) / 100.0;
                if (dialStage >= 1.0) return 0.0;
                float remappedStage = dialStage <= 0.25
                  ? (dialStage / 0.25) * 0.85
                  : 0.85 + (((dialStage - 0.25) / 0.75) * 0.15);
                float oneStageDistance = maximumDistance / 100.0;
                float retainedEdgeWidth = maximumDistance * (1.0 - remappedStage);
                float featherWidth = max(oneStageDistance * 0.5, maximumDistance * 0.00002);
                float fullyPaintedEdgeWidth = max(0.0, retainedEdgeWidth - featherWidth);
                return 1.0 - smoothstep(
                  fullyPaintedEdgeWidth,
                  retainedEdgeWidth,
                  surfaceDistance
                );
              }

              uniform float penSoftnessActive;
              uniform vec4 projectionRect;
              varying vec4 vProjectorPosition;
              varying vec3 vPbrWorldPosition;
              varying vec3 vPbrWorldNormal;
              varying vec2 vNormalBelowUv;

              vec2 voxemeraRotatePbrUv(vec2 uv) {
                float c = cos(pbrAngle);
                float s = sin(pbrAngle);
                return mat2(c, -s, s, c) * uv;
              }

              vec3 voxemeraPbrWeights() {
                vec3 weights = pow(abs(normalize(vPbrWorldNormal)), vec3(6.0));
                return weights / max(weights.x + weights.y + weights.z, 0.0001);
              }

              vec3 voxemeraPbrCoords() {
                return (vPbrWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * pbrScale, 0.00001);
              }

              vec3 voxemeraSamplePbrRgb(sampler2D sourceMap) {
                vec3 coords = voxemeraPbrCoords();
                vec3 weights = voxemeraPbrWeights();
                vec2 uvX = fract(voxemeraRotatePbrUv(coords.zy));
                vec2 uvY = fract(voxemeraRotatePbrUv(coords.xz));
                vec2 uvZ = fract(voxemeraRotatePbrUv(coords.xy));
                return (texture2D(sourceMap, uvX).rgb * weights.x)
                  + (texture2D(sourceMap, uvY).rgb * weights.y)
                  + (texture2D(sourceMap, uvZ).rgb * weights.z);
              }

              vec2 voxemeraRotateSourceNormalUv(vec2 uv) {
                float c = cos(normalLayerAngle);
                float s = sin(normalLayerAngle);
                return mat2(c, -s, s, c) * uv;
              }

              vec3 voxemeraSampleSourceNormalRgb() {
                vec3 coords = (vPbrWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * normalLayerScale, 0.00001);
                vec3 weights = voxemeraPbrWeights();
                vec2 uvX = fract(voxemeraRotateSourceNormalUv(coords.zy));
                vec2 uvY = fract(voxemeraRotateSourceNormalUv(coords.xz));
                vec2 uvZ = fract(voxemeraRotateSourceNormalUv(coords.xy));
                return (texture2D(normalLayerMap, uvX).rgb * weights.x)
                  + (texture2D(normalLayerMap, uvY).rgb * weights.y)
                  + (texture2D(normalLayerMap, uvZ).rgb * weights.z);
              }
            ` + shader.fragmentShader.replace(
              '#include <map_fragment>',
              `
              if (vProjectorPosition.w <= 0.0) discard;
              vec3 projected = vProjectorPosition.xyz / vProjectorPosition.w;
              vec2 projectorUv = projected.xy * 0.5 + 0.5;
              if (projectorUv.x <= 0.0 || projectorUv.x >= 1.0 || projectorUv.y <= 0.0 || projectorUv.y >= 1.0) discard;
              float fragmentDepth = projected.z * 0.5 + 0.5;
              float visibleDepth = texture2D(projectorDepthMap, projectorUv).x;
              if (abs(fragmentDepth - visibleDepth) > max(depthTolerance, fwidth(fragmentDepth) * 0.35)) discard;
              vec2 strokeUv = (projectorUv - projectionRect.xy) / projectionRect.zw;
              if (strokeUv.x <= 0.0 || strokeUv.x >= 1.0 || strokeUv.y <= 0.0 || strokeUv.y >= 1.0) discard;
              float coverage = texture2D(strokeMap, strokeUv).a;
              if (penSoftnessActive > 0.5) {
                if (coverage <= 0.001) discard;
              } else {
                if (coverage < 0.45) discard;
                coverage = 1.0;
              }
              diffuseColor.rgb = pbrHasAlbedo > 0.5
                ? voxemeraSamplePbrRgb(pbrAlbedoMap)
                : vec3(1.0);
              float voxemeraWearKeep = voxemeraStableWearKeep(edgeWear);
              coverage *= voxemeraWearKeep;
              if (coverage <= 0.001) discard;
              diffuseColor.a *= coverage;
              `,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <roughnessmap_fragment>',
              `#include <roughnessmap_fragment>
              if (pbrHasMaterial > 0.5) {
                roughnessFactor = voxemeraSamplePbrRgb(pbrMaterialMap).g;
              }`,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <metalnessmap_fragment>',
              `#include <metalnessmap_fragment>
              if (pbrHasMaterial > 0.5) {
                metalnessFactor = voxemeraSamplePbrRgb(pbrMaterialMap).b;
              }`,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <normal_fragment_maps>',
              `#include <normal_fragment_maps>
              if ((pbrHasNormal > 0.5 || normalLayerHasMap > 0.5 || normalBelowHasMap > 0.5) && heightScale > 0.0001) {
                vec3 ownNormal = pbrHasNormal > 0.5
                  ? (voxemeraSamplePbrRgb(pbrNormalMap) * 2.0 - 1.0)
                  : vec3(0.0, 0.0, 1.0);
                vec3 sourceNormal = normalBelowHasMap > 0.5
                  ? (texture2D(normalBelowMap, vNormalBelowUv).rgb * 2.0 - 1.0)
                  : (normalLayerHasMap > 0.5
                    ? (voxemeraSampleSourceNormalRgb() * 2.0 - 1.0)
                    : ownNormal);
                vec3 blendedNormal = normalize(mix(sourceNormal, ownNormal, clamp(normalLayerMix, 0.0, 1.0)));
                normal = normalize(normal + vec3(blendedNormal.xy * heightScale, 0.0));
              }`,
            );
          };
          material.customProgramCacheKey = () => `voxemeraProjectedPbr_v8_camera_stable_broad_chip_v1_uv_derivatives_v1_wear_below_normal_blend_${isSoftPbrPen ? 'soft-pen' : 'opaque'}`;
        } else if (!isTexturedProjection) {
          // Plain-colour pen paint is still a real surface material. Use the same
          // lit material path as the other 3D paint tools so model normals and
          // scene lights create the expected highlights and shadows.
          const penNeedsBlending = penSoftnessPercent > 0 || strokeOpacity < 1;
          material = new THREE.MeshStandardMaterial({
            color: projectionUniforms.penColour.value,
            roughness: 1 - (Math.max(0, Math.min(100, Number(materialSettings?.gloss) || 0)) / 100),
            metalness: Math.max(0, Math.min(100, Number(materialSettings?.metalness) || 0)) / 100,
            opacity: strokeOpacity,
            transparent: penNeedsBlending,
            blending: penNeedsBlending ? THREE.NormalBlending : THREE.NoBlending,
            depthTest: true,
            depthWrite: false,
            side: projectionSide,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            toneMapped: true,
          });

          material.userData = {
            ...(material.userData || {}),
            voxemeraProjectionUniforms: projectionUniforms,
            voxemeraLitPenProjection: true,
          };

          material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, projectionUniforms);
            material.userData.voxemeraProjectionShader = shader;

            shader.vertexShader = `
              uniform mat4 projectorMatrix;
              attribute vec3 voxemeraEdgeWearBarycentric;
              attribute vec3 voxemeraEdgeWearSharpEdges;
              attribute float voxemeraEdgeWearSurfaceDistance;
              attribute float voxemeraEdgeWearInteriorLift;
              attribute vec3 voxemeraEdgeWearAltitudes;
              attribute vec3 voxemeraEdgeWearNearestStart0;
              attribute vec3 voxemeraEdgeWearNearestEnd0;
              attribute vec3 voxemeraEdgeWearNearestStart1;
              attribute vec3 voxemeraEdgeWearNearestEnd1;
              attribute vec2 voxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              varying vec4 vProjectorPosition;
              varying vec3 vNormalBlendWorldPosition;
              varying vec3 vNormalBlendWorldNormal;
              varying vec2 vNormalBelowUv;
            ` + shader.vertexShader.replace(
              '#include <begin_vertex>',
              `#include <begin_vertex>
              vVoxemeraEdgeWearBarycentric = voxemeraEdgeWearBarycentric;
              vVoxemeraEdgeWearSharpEdges = voxemeraEdgeWearSharpEdges;
              vVoxemeraEdgeWearSurfaceDistance = voxemeraEdgeWearSurfaceDistance;
                vVoxemeraEdgeWearInteriorLift = voxemeraEdgeWearInteriorLift;
              vVoxemeraEdgeWearAltitudes = voxemeraEdgeWearAltitudes;
              vVoxemeraEdgeWearNearestStart0 = voxemeraEdgeWearNearestStart0;
              vVoxemeraEdgeWearNearestEnd0 = voxemeraEdgeWearNearestEnd0;
              vVoxemeraEdgeWearNearestStart1 = voxemeraEdgeWearNearestStart1;
              vVoxemeraEdgeWearNearestEnd1 = voxemeraEdgeWearNearestEnd1;
              vVoxemeraEdgeWearNearestValid = voxemeraEdgeWearNearestValid;
              vVoxemeraWearPosition = transformed;
                vVoxemeraCurvatureUv = uv;
                vVoxemeraCurvatureNormal = normalize(objectNormal);
              vec4 voxemeraPenWorldPosition = modelMatrix * vec4(transformed, 1.0);
              vNormalBlendWorldPosition = voxemeraPenWorldPosition.xyz;
              vNormalBlendWorldNormal = normalize(mat3(modelMatrix) * normal);
              vNormalBelowUv = uv;
              vProjectorPosition = projectorMatrix * vec4(transformed, 1.0);`,
            );

            shader.fragmentShader = `
              uniform sampler2D strokeMap;
              uniform sampler2D projectorDepthMap;
              uniform float depthTolerance;
              uniform float edgeWear;
              uniform float edgeWearMaxDistance;
              uniform sampler2D edgeWearDistanceMap;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              float voxemeraStableWearKeep(float wearAmount) {
                if (abs(wearAmount) <= 0.0001) return 1.0;
                float signedAmount = clamp(wearAmount, -1.0, 1.0);
                float amount = abs(signedAmount);
                float maximumDistance = max(edgeWearMaxDistance, 0.000001);
                vec2 wearUv = vec2(fract(vVoxemeraCurvatureUv.x), fract(vVoxemeraCurvatureUv.y));
                float surfaceDistance = texture2D(edgeWearDistanceMap, wearUv).r;
                if (signedAmount > 0.0) {
                  if (signedAmount >= 0.9999) return 0.0;
                  float wearFront = pow(amount, 2.8) * maximumDistance;
                  float featherWidth = max(wearFront * 0.5, maximumDistance * 0.002);
                  float innerWearFront = max(0.0, wearFront - featherWidth);
                  return smoothstep(innerWearFront, wearFront, surfaceDistance);
                }
                // Remap the left dial so the behaviour that used to live
                // in stages 0-85 now occupies the first 25% of the dial, and
                // the old 86-100 range now occupies the final 75%.
                float dialStage = floor((amount * 100.0) + 0.5) / 100.0;
                if (dialStage >= 1.0) return 0.0;
                float remappedStage = dialStage <= 0.25
                  ? (dialStage / 0.25) * 0.85
                  : 0.85 + (((dialStage - 0.25) / 0.75) * 0.15);
                float oneStageDistance = maximumDistance / 100.0;
                float retainedEdgeWidth = maximumDistance * (1.0 - remappedStage);
                float featherWidth = max(oneStageDistance * 0.5, maximumDistance * 0.00002);
                float fullyPaintedEdgeWidth = max(0.0, retainedEdgeWidth - featherWidth);
                return 1.0 - smoothstep(
                  fullyPaintedEdgeWidth,
                  retainedEdgeWidth,
                  surfaceDistance
                );
              }

              uniform float surfaceLockEnabled;
              uniform float surfaceLockDepth;
              uniform float surfaceLockDepthTolerance;
              uniform vec4 projectionRect;
              uniform vec3 penColour;
              uniform float penSoftnessActive;
              uniform sampler2D normalLayerMap;
              uniform float normalLayerHasMap;
              uniform float normalLayerMix;
              uniform float normalLayerScale;
              uniform float normalLayerAngle;
              uniform sampler2D normalBelowMap;
              uniform float normalBelowHasMap;
              uniform float pbrScale;
              uniform float pbrAngle;
              uniform vec3 pbrWorldOrigin;
              uniform float pbrWorldTileSize;
              varying vec4 vProjectorPosition;
              varying vec3 vNormalBlendWorldPosition;
              varying vec3 vNormalBlendWorldNormal;
              varying vec2 vNormalBelowUv;

              vec2 voxemeraNormalRotateUv(vec2 uv) {
                float c = cos(normalLayerAngle);
                float s = sin(normalLayerAngle);
                return mat2(c, -s, s, c) * uv;
              }

              float voxemeraDistanceToWorldSegment(vec3 pointValue, vec3 segmentStart, vec3 segmentEnd) {
                vec3 segment = segmentEnd - segmentStart;
                float segmentLengthSquared = max(dot(segment, segment), 1.0e-8);
                float t = clamp(dot(pointValue - segmentStart, segment) / segmentLengthSquared, 0.0, 1.0);
                vec3 closestPoint = segmentStart + (segment * t);
                return distance(pointValue, closestPoint);
              }

              vec3 voxemeraSampleLayerNormal() {
                vec3 coords = (vNormalBlendWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * normalLayerScale, 0.00001);
                vec3 weights = pow(abs(normalize(vNormalBlendWorldNormal)), vec3(6.0));
                weights /= max(weights.x + weights.y + weights.z, 0.0001);
                vec2 uvX = fract(voxemeraNormalRotateUv(coords.zy));
                vec2 uvY = fract(voxemeraNormalRotateUv(coords.xz));
                vec2 uvZ = fract(voxemeraNormalRotateUv(coords.xy));
                return ((texture2D(normalLayerMap, uvX).rgb * weights.x)
                  + (texture2D(normalLayerMap, uvY).rgb * weights.y)
                  + (texture2D(normalLayerMap, uvZ).rgb * weights.z)) * 2.0 - 1.0;
              }
            ` + shader.fragmentShader.replace(
              '#include <map_fragment>',
              `
              if (vProjectorPosition.w <= 0.0) discard;

              vec3 projected = vProjectorPosition.xyz / vProjectorPosition.w;
              vec2 projectorUv = projected.xy * 0.5 + 0.5;
              if (
                projectorUv.x <= 0.0 || projectorUv.x >= 1.0
                || projectorUv.y <= 0.0 || projectorUv.y >= 1.0
              ) discard;

              float fragmentDepth = projected.z * 0.5 + 0.5;
              float visibleDepth = texture2D(projectorDepthMap, projectorUv).x;
              float adaptiveTolerance = max(depthTolerance, fwidth(fragmentDepth) * 0.35);
              if (abs(fragmentDepth - visibleDepth) > adaptiveTolerance) discard;
              if (
                surfaceLockEnabled > 0.5
                && abs(fragmentDepth - surfaceLockDepth) > surfaceLockDepthTolerance
              ) discard;

              vec2 strokeUv = (projectorUv - projectionRect.xy) / projectionRect.zw;
              if (
                strokeUv.x <= 0.0 || strokeUv.x >= 1.0
                || strokeUv.y <= 0.0 || strokeUv.y >= 1.0
              ) discard;

              float coverage = texture2D(strokeMap, strokeUv).a;
              if (penSoftnessActive > 0.5) {
                if (coverage <= 0.001) discard;
              } else {
                if (coverage < 0.35) discard;
                coverage = 1.0;
              }

              // Keep normal pen colour and lighting, but preserve the canvas mask's
              // alpha when softness is enabled. This keeps the first dab, live
              // preview and committed projection visually identical.
              diffuseColor.rgb = penColour;
              float voxemeraWearKeep = voxemeraStableWearKeep(edgeWear);
              coverage *= voxemeraWearKeep;
              if (coverage <= 0.001) discard;
              diffuseColor.a *= coverage;
              `,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <normal_fragment_maps>',
              `#include <normal_fragment_maps>
              if ((normalBelowHasMap > 0.5 || normalLayerHasMap > 0.5) && normalLayerMix < 0.9999) {
                vec3 sourceNormal = normalBelowHasMap > 0.5
                  ? normalize(texture2D(normalBelowMap, vNormalBelowUv).rgb * 2.0 - 1.0)
                  : normalize(voxemeraSampleLayerNormal());
                vec3 blendedLayerNormal = normalize(mix(sourceNormal, vec3(0.0, 0.0, 1.0), clamp(normalLayerMix, 0.0, 1.0)));
                normal = normalize(normal + vec3(blendedLayerNormal.xy, 0.0));
              }`,
            );
          };

          material.customProgramCacheKey = () => `voxemeraLitPenProjection_v9_non_pbr_vertex_fix_${penSoftnessPercent > 0 ? 'soft' : 'hard'}`;

        } else {
          material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 1 - (Math.max(0, Math.min(100, Number(materialSettings?.gloss) || 0)) / 100),
            metalness: Math.max(0, Math.min(100, Number(materialSettings?.metalness) || 0)) / 100,
            opacity: 1,
            transparent: true,
            depthTest: true,
            depthWrite: false,
            side: projectionSide,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            map: strokeTexture,
            toneMapped: true,
          });
          material.userData = {
            ...(material.userData || {}),
            voxemeraProjectionUniforms: projectionUniforms,
            voxemeraTexturedProjection: true,
          };
          material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, projectionUniforms);
            material.userData.voxemeraProjectionShader = shader;
            shader.vertexShader = `
              uniform mat4 projectorMatrix;
              attribute vec3 voxemeraEdgeWearBarycentric;
              attribute vec3 voxemeraEdgeWearSharpEdges;
              attribute float voxemeraEdgeWearSurfaceDistance;
              attribute float voxemeraEdgeWearInteriorLift;
              attribute vec3 voxemeraEdgeWearAltitudes;
              attribute vec3 voxemeraEdgeWearNearestStart0;
              attribute vec3 voxemeraEdgeWearNearestEnd0;
              attribute vec3 voxemeraEdgeWearNearestStart1;
              attribute vec3 voxemeraEdgeWearNearestEnd1;
              attribute vec2 voxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              varying vec4 vProjectorPosition;
              varying vec3 vProjectionLocalPosition;
              varying vec3 vProjectionLocalNormal;
              varying vec3 vProjectionWorldPosition;
              varying vec2 vNormalBelowUv;
            ` + shader.vertexShader
              .replace(
                '#include <beginnormal_vertex>',
                `#include <beginnormal_vertex>
                vProjectionLocalNormal = normalize(objectNormal);`
              )
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                vVoxemeraEdgeWearBarycentric = voxemeraEdgeWearBarycentric;
                vVoxemeraEdgeWearSharpEdges = voxemeraEdgeWearSharpEdges;
                vVoxemeraEdgeWearSurfaceDistance = voxemeraEdgeWearSurfaceDistance;
                vVoxemeraEdgeWearInteriorLift = voxemeraEdgeWearInteriorLift;
                vVoxemeraEdgeWearAltitudes = voxemeraEdgeWearAltitudes;
                vVoxemeraEdgeWearNearestStart0 = voxemeraEdgeWearNearestStart0;
                vVoxemeraEdgeWearNearestEnd0 = voxemeraEdgeWearNearestEnd0;
                vVoxemeraEdgeWearNearestStart1 = voxemeraEdgeWearNearestStart1;
                vVoxemeraEdgeWearNearestEnd1 = voxemeraEdgeWearNearestEnd1;
                vVoxemeraEdgeWearNearestValid = voxemeraEdgeWearNearestValid;
                vVoxemeraWearPosition = transformed;
                vVoxemeraCurvatureUv = uv;
                vVoxemeraCurvatureNormal = normalize(objectNormal);
                vec4 localPosition = vec4( transformed, 1.0 );
                vProjectionLocalPosition = transformed;
                vProjectionWorldPosition = (modelMatrix * localPosition).xyz;
                vNormalBelowUv = uv;
                vProjectorPosition = projectorMatrix * localPosition;`
              );
            shader.fragmentShader = `
              uniform sampler2D strokeMap;
              uniform sampler2D projectorDepthMap;
              uniform sampler2D heightMap;
              uniform float depthTolerance;
              uniform float edgeWear;
              uniform float edgeWearMaxDistance;
              uniform sampler2D edgeWearDistanceMap;
              varying vec3 vVoxemeraEdgeWearBarycentric;
              varying vec3 vVoxemeraEdgeWearSharpEdges;
              varying float vVoxemeraEdgeWearSurfaceDistance;
              varying float vVoxemeraEdgeWearInteriorLift;
              varying vec3 vVoxemeraEdgeWearAltitudes;
              varying vec3 vVoxemeraEdgeWearNearestStart0;
              varying vec3 vVoxemeraEdgeWearNearestEnd0;
              varying vec3 vVoxemeraEdgeWearNearestStart1;
              varying vec3 vVoxemeraEdgeWearNearestEnd1;
              varying vec2 vVoxemeraEdgeWearNearestValid;
              varying vec3 vVoxemeraWearPosition;
              varying vec2 vVoxemeraCurvatureUv;
              varying vec3 vVoxemeraCurvatureNormal;

              float voxemeraStableWearKeep(float wearAmount) {
                if (abs(wearAmount) <= 0.0001) return 1.0;
                float signedAmount = clamp(wearAmount, -1.0, 1.0);
                float amount = abs(signedAmount);
                float maximumDistance = max(edgeWearMaxDistance, 0.000001);
                vec2 wearUv = vec2(fract(vVoxemeraCurvatureUv.x), fract(vVoxemeraCurvatureUv.y));
                float surfaceDistance = texture2D(edgeWearDistanceMap, wearUv).r;
                if (signedAmount > 0.0) {
                  if (signedAmount >= 0.9999) return 0.0;
                  float wearFront = pow(amount, 2.8) * maximumDistance;
                  float featherWidth = max(wearFront * 0.5, maximumDistance * 0.002);
                  float innerWearFront = max(0.0, wearFront - featherWidth);
                  return smoothstep(innerWearFront, wearFront, surfaceDistance);
                }
                // Remap the left dial so the behaviour that used to live
                // in stages 0-85 now occupies the first 25% of the dial, and
                // the old 86-100 range now occupies the final 75%.
                float dialStage = floor((amount * 100.0) + 0.5) / 100.0;
                if (dialStage >= 1.0) return 0.0;
                float remappedStage = dialStage <= 0.25
                  ? (dialStage / 0.25) * 0.85
                  : 0.85 + (((dialStage - 0.25) / 0.75) * 0.15);
                float oneStageDistance = maximumDistance / 100.0;
                float retainedEdgeWidth = maximumDistance * (1.0 - remappedStage);
                float featherWidth = max(oneStageDistance * 0.5, maximumDistance * 0.00002);
                float fullyPaintedEdgeWidth = max(0.0, retainedEdgeWidth - featherWidth);
                return 1.0 - smoothstep(
                  fullyPaintedEdgeWidth,
                  retainedEdgeWidth,
                  surfaceDistance
                );
              }

              uniform float surfaceLockEnabled;
              uniform float surfaceLockDepth;
              uniform float surfaceLockDepthTolerance;
              uniform float heightScale;
              uniform float decalOpacity;
              uniform float incidenceAlpha;
              uniform float imageDecalProjection;
              uniform float textDecalProjection;
              uniform sampler2D normalLayerMap;
              uniform float normalLayerHasMap;
              uniform float normalLayerMix;
              uniform float normalLayerScale;
              uniform float normalLayerAngle;
              uniform sampler2D normalBelowMap;
              uniform float normalBelowHasMap;
              uniform float pbrScale;
              uniform float pbrAngle;
              uniform vec3 pbrWorldOrigin;
              uniform float pbrWorldTileSize;
              uniform vec4 projectionRect;
              uniform vec2 strokeTexelSize;
              uniform vec3 penColour;
              uniform vec3 projectorCameraLocal;
              uniform float airbrushProjection;
              varying vec4 vProjectorPosition;
              varying vec3 vProjectionLocalPosition;
              varying vec3 vProjectionLocalNormal;
              varying vec3 vProjectionWorldPosition;
              varying vec2 vNormalBelowUv;

              vec2 voxemeraNormalRotateUv(vec2 uv) {
                float c = cos(normalLayerAngle);
                float s = sin(normalLayerAngle);
                return mat2(c, -s, s, c) * uv;
              }

              float voxemeraDistanceToWorldSegment(vec3 pointValue, vec3 segmentStart, vec3 segmentEnd) {
                vec3 segment = segmentEnd - segmentStart;
                float segmentLengthSquared = max(dot(segment, segment), 1.0e-8);
                float t = clamp(dot(pointValue - segmentStart, segment) / segmentLengthSquared, 0.0, 1.0);
                vec3 closestPoint = segmentStart + (segment * t);
                return distance(pointValue, closestPoint);
              }

              vec3 voxemeraSampleLayerNormal() {
                vec3 coords = (vProjectionWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * normalLayerScale, 0.00001);
                vec3 weights = pow(abs(normalize(vProjectionLocalNormal)), vec3(6.0));
                weights /= max(weights.x + weights.y + weights.z, 0.0001);
                vec2 uvX = fract(voxemeraNormalRotateUv(coords.zy));
                vec2 uvY = fract(voxemeraNormalRotateUv(coords.xz));
                vec2 uvZ = fract(voxemeraNormalRotateUv(coords.xy));
                return ((texture2D(normalLayerMap, uvX).rgb * weights.x)
                  + (texture2D(normalLayerMap, uvY).rgb * weights.y)
                  + (texture2D(normalLayerMap, uvZ).rgb * weights.z)) * 2.0 - 1.0;
              }
            ` + shader.fragmentShader
              .replace(
                '#include <map_fragment>',
                `
                if (vProjectorPosition.w <= 0.0) discard;
                vec3 projected = vProjectorPosition.xyz / vProjectorPosition.w;
                vec2 projectorUv = projected.xy * 0.5 + 0.5;
                if (projectorUv.x <= 0.0 || projectorUv.x >= 1.0 || projectorUv.y <= 0.0 || projectorUv.y >= 1.0) discard;
                float fragmentDepth = projected.z * 0.5 + 0.5;
                float visibleDepth = texture2D(projectorDepthMap, projectorUv).x;
                float adaptiveTolerance = textDecalProjection > 0.5
                  ? max(depthTolerance, fwidth(fragmentDepth) * 2.0)
                  : max(depthTolerance, fwidth(fragmentDepth) * 0.35);
                // The hardware depth texture contains the nearest visible face
                // for this exact camera pixel. Only that face may receive paint.
                if (abs(fragmentDepth - visibleDepth) > adaptiveTolerance) discard;
              if (
                surfaceLockEnabled > 0.5
                && abs(fragmentDepth - surfaceLockDepth) > surfaceLockDepthTolerance
              ) discard;
                vec2 croppedStrokeUv = (projectorUv - projectionRect.xy) / projectionRect.zw;
                if (croppedStrokeUv.x <= 0.0 || croppedStrokeUv.x >= 1.0 || croppedStrokeUv.y <= 0.0 || croppedStrokeUv.y >= 1.0) discard;
                vec4 projectedPaint = texture2D(strokeMap, croppedStrokeUv);

                // A flat camera projection stretches circular droplets into long
                // lines on surfaces viewed edge-on. Real sprayed paint receives
                // progressively less material as the surface normal turns away
                // from the nozzle, and essentially none at a grazing angle.
                vec3 projectorDirectionLocal = normalize(projectorCameraLocal - vProjectionLocalPosition);
                float incidence = abs(dot(normalize(vProjectionLocalNormal), projectorDirectionLocal));
                float incidenceCoverage = textDecalProjection > 0.5
                  ? 1.0
                  : smoothstep(0.22, 0.52, incidence);
                if (incidenceCoverage <= 0.001) discard;

                // Plain pen strokes use binary canvas coverage to prevent dark
                // antialias overlap seams. Airbrush, pencil and imported brushes
                // preserve canvas alpha because it is their actual textured coverage.
                if (incidenceAlpha < 0.5) {
                  if (projectedPaint.a <= 0.001) discard;
                  projectedPaint.a = decalOpacity;
                } else {
                  projectedPaint.a *= decalOpacity * incidenceCoverage;
                  if (projectedPaint.a <= 0.001) discard;
                }

                // Imported image layers preserve their own RGB. Brush projections
                // continue to use the selected colour with the canvas as coverage.
                diffuseColor.rgb = imageDecalProjection > 0.5
                  ? projectedPaint.rgb
                  : penColour;
                float voxemeraWearKeep = voxemeraStableWearKeep(edgeWear);
                projectedPaint.a *= voxemeraWearKeep;
                if (projectedPaint.a <= 0.001) discard;
                diffuseColor.a *= projectedPaint.a;
                `
              )
              .replace(
                '#include <normal_fragment_maps>',
                `
                vec3 projectedNormal = normal;
                if (heightScale > 0.0001 && vProjectorPosition.w > 0.0) {
                  vec3 projectedCoords = vProjectorPosition.xyz / vProjectorPosition.w;
                  vec2 projectedUvForHeight = projectedCoords.xy * 0.5 + 0.5;
                  if (projectedUvForHeight.x > 0.0 && projectedUvForHeight.x < 1.0 && projectedUvForHeight.y > 0.0 && projectedUvForHeight.y < 1.0) {
                    vec2 croppedHeightUv = (projectedUvForHeight - projectionRect.xy) / projectionRect.zw;
                    if (croppedHeightUv.x > 0.0 && croppedHeightUv.x < 1.0 && croppedHeightUv.y > 0.0 && croppedHeightUv.y < 1.0) {
                      float projectedAlpha = texture2D(strokeMap, croppedHeightUv).a;
                      if (projectedAlpha > 0.001) {
                        float heightValue = texture2D(heightMap, croppedHeightUv).r;
                      float heightDx = dFdx(heightValue);
                      float heightDy = dFdy(heightValue);
                        projectedNormal = normalize(projectedNormal + vec3(-heightDx * heightScale * 16.0, -heightDy * heightScale * 16.0, 0.0));
                      }
                    }
                  }
                }
                if ((normalBelowHasMap > 0.5 || normalLayerHasMap > 0.5) && normalLayerMix < 0.9999) {
                  vec3 sourceNormal = normalBelowHasMap > 0.5
                    ? normalize(texture2D(normalBelowMap, vNormalBelowUv).rgb * 2.0 - 1.0)
                    : normalize(voxemeraSampleLayerNormal());
                  vec3 ownLayerNormal = normalize(vec3(projectedNormal.xy, max(0.0001, projectedNormal.z)));
                  vec3 blendedLayerNormal = normalize(mix(sourceNormal, ownLayerNormal, clamp(normalLayerMix, 0.0, 1.0)));
                  projectedNormal = normalize(projectedNormal + vec3(blendedLayerNormal.xy, 0.0));
                }
                normal = projectedNormal;
                `
              );
          };
          material.customProgramCacheKey = () => 'voxemeraProjectedStandardDecal_v16_camera_stable_broad_chip_v1_uv_derivatives_v1_wear_below_normal_blend';
        }

        // Keep only the connected/touched faces calculated for this stroke.
        // The previous surface-lock branch passed null here, which cloned the
        // complete source mesh for every stroke. Those full duplicate meshes and
        // projection shaders remained in the scene and made orbiting progressively
        // slower after painting.
        const allowedFaces = stroke?.allowedFaceIndicesByMesh?.get?.(sourceMesh.uuid) || null;
        if (stroke?.restoring === true && !strokeNeedsEdgeWear) {
          loadProfilerRef.current?.mark?.(
            'Projection restore: edge wear skipped',
            'Layer edge wear is 0, so expensive mesh-wide edge topology is not generated during startup.',
            { meshName: sourceMesh.name || '', vertexCount: sourceMesh.geometry?.getAttribute?.('position')?.count || 0 },
          );
        }
        const selectionGeometry = createSelectionGeometry(sourceMesh, allowedFaces, strokeNeedsEdgeWear);
        if (!selectionGeometry) { material.dispose(); return; }
        const decal = new THREE.Mesh(selectionGeometry, material);
        decal.name = 'Voxemera exact projected pen stroke';
        decal.position.copy(sourceMesh.position);
        decal.quaternion.copy(sourceMesh.quaternion);
        decal.scale.copy(sourceMesh.scale);
        decal.renderOrder = (previewOnly ? 500000 : 1000) + (runtime.projectionStrokes?.length || 0);
        decal.frustumCulled = sourceMesh.frustumCulled;
        decal.receiveShadow = true;
        decal.castShadow = false;
        decal.userData.voxemeraProjectionDecal = true;
        decal.userData.voxemeraPaintRegion = {
          sourceMeshUuid: sourceMesh.uuid,
          projectionRect: { ...projectionRect },
          projectionFullWidth,
          projectionFullHeight,
        };
        decal.raycast = () => {};
        sourceMesh.parent?.add(decal);
        meshes.push(decal);
      });

      if (!meshes.length) {
        selectionDebugState.meshes = selectionDebugState.meshes.map((entry) => ({
          ...entry,
          result: entry.result === 'geometry-created' ? 'decal-not-added' : entry.result,
        }));
        refreshSelectionDebug('NO DECAL MESHES CREATED');
        strokeTexture.dispose();
        heightTexture?.dispose?.();
        releaseProjectionDepthTarget(depthEntry);
        return false;
      }
      refreshSelectionDebug(`commit-created-${meshes.length}-decal-meshes`);

      const committedStroke = {
        layerId: String(stroke?.layerId || getCurrentLayerState().activeLayerId || ''),
        meshes,
        strokeTexture,
        heightTexture,
        depthTarget,
        depthEntry,
        sourceCanvas: strokeCanvas,
        projectionRect: { ...projectionRect },
        projectionFullWidth,
        projectionFullHeight,
        projectionCameraData: serializeProjectionCamera(projectionCamera),
        materialSettings: { ...materialSettings },
        isAirbrush: stroke?.isAirbrush === true,
        isPencil: stroke?.isPencil === true,
        isImportedBrush: stroke?.isImportedBrush === true,
        isImageDecal: stroke?.isImageDecal === true,
        isEraser: stroke?.isEraser === true,
        isPbr: stroke?.isPbr === true,
        brushConfig: stroke?.brushConfig
          ? { ...stroke.brushConfig }
          : null,
        airbrushWorldDiameter: Number(stroke?.airbrushWorldDiameter) || 0,
        airbrushWorldPoints: Array.isArray(stroke?.airbrushWorldPoints)
          ? stroke.airbrushWorldPoints.map((point) => point.clone())
          : [],
        selectionSnapshot: stroke?.selectionSnapshot !== undefined ? stroke.selectionSnapshot : captureSelectionSnapshot(),
      };
      if (previewOnly) {
        return committedStroke;
      }
      runtime.projectionStrokes = runtime.projectionStrokes || [];
      if (!stroke?.restoring) discardProjectionRedoStrokes();
      runtime.projectionStrokes.push(committedStroke);
      if (!stroke?.restoring) {
      }
      // Make the newly committed stroke part of the visible history before the
      // layer compositor rebuilds. Updating this only later in finishPaintStroke
      // caused the model to render one stroke behind.
      if (!stroke?.restoring) {
        projectionStrokeCountRef.current = runtime.projectionStrokes.length;
      }
      // Persisted-stroke restore can contain hundreds of projected strokes.
      // Rebuilding/presenting the merged GPU map for every restored stroke makes
      // model startup effectively O(n²). During restore, only recreate the stroke
      // objects here; restorePersistedProjectionStrokes() performs one batched
      // presentation/GPU rebuild after the complete history has been reconstructed.
      if (stroke?.restoring) {
        return true;
      }

      if (committedStroke.isEraser) {
        const eraserCommitStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        // Eraser strokes do not add colour/PBR data to the merged GPU paint map.
        // Rebuilding that full map here replays every historical stroke and was
        // the main multi-second pointer-up stall. Hide the eraser projection and
        // update only the active-layer masks that actually changed.
        committedStroke.meshes.forEach((mesh) => {
          if (mesh) mesh.visible = false;
        });
        const visibleStrokes = runtime.projectionStrokes.slice(
          0,
          Math.max(0, Math.min(runtime.projectionStrokes.length, projectionStrokeCountRef.current)),
        );
        if (!appendLatestEraserToActiveLayerMasks(visibleStrokes, committedStroke)) {
          rebuildActiveLayerEraserMasks(visibleStrokes);
        }
      } else {
        // The live GPU paint target already contains every previously committed
        // stroke in history order. Append only this new stroke instead of
        // clearing the atlas and replaying the entire stroke history on every
        // pointer-up. The full rebuild is still used by undo/redo, restore and
        // explicit layer-order changes where historical ordering can change.
        renderProjectionStrokeToGpuPaintMap(committedStroke, false);
        refreshProjectionLayerPresentation();
        refreshVisibleMaterial();
      }
      updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
      return true;
    };

    const scheduleLivePbrPreviewProjectionUpdate = (stroke) => {
      if (!stroke?.isPbr) return;
      if (livePbrPreviewFrameRef.current) return;
      livePbrPreviewFrameRef.current = window.requestAnimationFrame(() => {
        livePbrPreviewFrameRef.current = 0;
        if (!activePaintStroke || activePaintStroke !== stroke || !stroke?.isPbr) return;

        // Building an exact PBR preview creates projection geometry, depth data
        // and PBR materials. Doing that at pointer-event/RAF frequency made the
        // pen feel dramatically slower than non-PBR painting. Keep the preview
        // exact, but cap expensive rebuilds to 8 Hz while the pointer moves.
        // The final pointer-up commit remains full quality and unthrottled.
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if ((now - Number(livePbrPreviewLastUpdateAtRef.current || 0)) < 125) return;

        const previewStroke = commitExactPreviewProjection(stroke, { previewOnly: true });
        if (!previewStroke) return;
        livePbrPreviewLastUpdateAtRef.current = now;
        if (livePbrPreviewStrokeRef.current) {
          disposeExactProjectionStroke(livePbrPreviewStrokeRef.current);
        }
        livePbrPreviewStrokeRef.current = previewStroke;
      });
    };

    const readCurrentUvColourPixels = () => {
      const session = activePaintSession;
      if (!session?.gpuPaintTarget || !session?.context || !session?.canvas) return null;
      // Startup intentionally leaves this expensive derived render target dirty.
      // Rebuild it only at the point a caller actually needs merged GPU pixels.
      // This preserves export correctness without making every model load pay the
      // full history replay cost.
      if (!session.gpuPaintInitialized) rebuildGpuPaintMap();
      const width = session.canvas.width;
      const height = session.canvas.height;
      const gpuPixels = new Uint8Array(width * height * 4);
      renderer.readRenderTargetPixels(session.gpuPaintTarget, 0, 0, width, height, gpuPixels);
      const basePixels = session.context.getImageData(0, 0, width, height).data;
      const result = new Uint8ClampedArray(width * height * 4);
      const coverage = new Uint8Array(width * height);
      // WebGL readRenderTargetPixels returns rows from the bottom of the
      // render target upward, while Canvas/ImageData stores rows from the top
      // downward. The live shader samples the render target correctly on the
      // GPU, but export previously copied those rows straight into a canvas.
      // That vertically inverted the baked paint relative to the exact live UVs.
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const outputIndex = ((y * width) + x) * 4;
          const gpuIndex = ((((height - 1) - y) * width) + x) * 4;
          const baseIndex = outputIndex;
          const alphaByte = gpuPixels[gpuIndex + 3];
          const alpha = alphaByte / 255;
          coverage[(y * width) + x] = alphaByte;
          if (alpha > 0.001) {
            // gpuPaintTarget is blended over a transparent render target. Its RGB
            // bytes are therefore already premultiplied by the accumulated alpha.
            // Multiplying them by alpha again creates a pale/white fringe around
            // soft strokes and can erase the textured layer underneath on export.
            // Composite the premultiplied GPU colour over the CPU base atlas once.
            result[outputIndex] = Math.round(Math.min(255, gpuPixels[gpuIndex] + (basePixels[baseIndex] * (1 - alpha))));
            result[outputIndex + 1] = Math.round(Math.min(255, gpuPixels[gpuIndex + 1] + (basePixels[baseIndex + 1] * (1 - alpha))));
            result[outputIndex + 2] = Math.round(Math.min(255, gpuPixels[gpuIndex + 2] + (basePixels[baseIndex + 2] * (1 - alpha))));
            result[outputIndex + 3] = 255;
          } else {
            result[outputIndex] = basePixels[baseIndex];
            result[outputIndex + 1] = basePixels[baseIndex + 1];
            result[outputIndex + 2] = basePixels[baseIndex + 2];
            result[outputIndex + 3] = basePixels[baseIndex + 3] || 255;
          }
        }
      }
      return { width, height, pixels: result, coverage };
    };


    const writeRenderTargetPixelsToCanvasContext = (context, pixels, width, height, label = 'render target readback') => {
      if (!context || !pixels || !width || !height) return false;
      const correctedPixels = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const outputIndex = ((y * width) + x) * 4;
          const sourceIndex = ((((height - 1) - y) * width) + x) * 4;
          correctedPixels[outputIndex] = pixels[sourceIndex];
          correctedPixels[outputIndex + 1] = pixels[sourceIndex + 1];
          correctedPixels[outputIndex + 2] = pixels[sourceIndex + 2];
          correctedPixels[outputIndex + 3] = pixels[sourceIndex + 3];
        }
      }
      context.putImageData(new ImageData(correctedPixels, width, height), 0, 0);
      void 0;
      return true;
    };

    const countNonDefaultAtlasPixels = (pixelData) => {
      if (!pixelData) return 0;
      let count = 0;
      for (let index = 0; index < pixelData.length; index += 4) {
        const red = pixelData[index];
        const green = pixelData[index + 1];
        const blue = pixelData[index + 2];
        const alpha = pixelData[index + 3];
        if (alpha > 0 && (red < 250 || green < 250 || blue < 250)) count += 1;
      }
      return count;
    };

    const reportAtlasMaterialState = (label) => {
      const session = activePaintSession;
      const model = currentModelRef.current;
      let meshCount = 0;
      let paintMaterialCount = 0;
      let matchingTextureCount = 0;
      model?.traverse?.((child) => {
        if (!child?.isMesh || child.userData?.voxemeraProjectionDecal) return;
        meshCount += 1;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (!material) return;
          if (material === child.userData?.voxemeraPaintMaterial) paintMaterialCount += 1;
          if (material.map === session?.texture) matchingTextureCount += 1;
        });
      });
    };

    const padPaintAtlasEdges = (
      sourcePixels,
      sourceCoverage,
      width,
      height,
      passes = 6,
    ) => {
      if (!sourcePixels || !sourceCoverage || !width || !height) {
        return {
          pixels: sourcePixels,
          coverage: sourceCoverage,
        };
      }

      let pixels = new Uint8ClampedArray(sourcePixels);
      let coverage = new Uint8Array(sourceCoverage);
      const safePasses = Math.max(1, Math.min(16, Math.round(Number(passes) || 6)));
      const neighbours = [
        [-1, 0], [1, 0], [0, -1], [0, 1],
        [-1, -1], [1, -1], [-1, 1], [1, 1],
      ];

      // UV textures need a coloured gutter outside every island. Without it,
      // bilinear filtering samples the untouched white atlas around the island
      // and exposes a bright seam on the model. Grow only into currently
      // uncovered texels; the padding remains outside the mapped triangles.
      for (let pass = 0; pass < safePasses; pass += 1) {
        const nextPixels = new Uint8ClampedArray(pixels);
        const nextCoverage = new Uint8Array(coverage);
        let changed = false;

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const flat = (y * width) + x;
            if (coverage[flat] > 0) continue;

            let bestFlat = -1;
            let bestCoverage = 0;

            for (const [offsetX, offsetY] of neighbours) {
              const sampleX = x + offsetX;
              const sampleY = y + offsetY;
              if (
                sampleX < 0
                || sampleY < 0
                || sampleX >= width
                || sampleY >= height
              ) continue;

              const sampleFlat = (sampleY * width) + sampleX;
              const sampleCoverage = coverage[sampleFlat] || 0;
              if (sampleCoverage > bestCoverage) {
                bestCoverage = sampleCoverage;
                bestFlat = sampleFlat;
              }
            }

            if (bestFlat < 0 || bestCoverage <= 0) continue;

            const targetIndex = flat * 4;
            const sourceIndex = bestFlat * 4;
            nextPixels[targetIndex] = pixels[sourceIndex];
            nextPixels[targetIndex + 1] = pixels[sourceIndex + 1];
            nextPixels[targetIndex + 2] = pixels[sourceIndex + 2];
            nextPixels[targetIndex + 3] = pixels[sourceIndex + 3];
            nextCoverage[flat] = bestCoverage;
            changed = true;
          }
        }

        pixels = nextPixels;
        coverage = nextCoverage;
        if (!changed) break;
      }

      return { pixels, coverage };
    };

    const dilateProjectionMaskGutter = (canvas, radius = 4) => {
      if (!canvas?.width || !canvas?.height) return canvas;
      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!context) return canvas;
      const width = canvas.width;
      const height = canvas.height;
      const image = context.getImageData(0, 0, width, height);
      let pixels = new Uint8ClampedArray(image.data);
      const safeRadius = Math.max(1, Math.min(8, Math.round(Number(radius) || 4)));

      // Extend each projected colour fragment into the unused texels around its
      // UV island. The model never samples those gutter texels directly, but
      // bilinear filtering and mipmaps do sample them at island borders. Keeping
      // their RGB/alpha equal to the nearest painted texel removes white seams
      // without altering the mesh UVs or the visible centre of the stroke.
      for (let pass = 0; pass < safeRadius; pass += 1) {
        const next = new Uint8ClampedArray(pixels);
        let changed = false;
        for (let y = 1; y < height - 1; y += 1) {
          for (let x = 1; x < width - 1; x += 1) {
            const flat = (y * width) + x;
            const index = flat * 4;
            if (pixels[index + 3] > 0) continue;

            let bestIndex = -1;
            let bestAlpha = 0;
            const neighbours = [flat - 1, flat + 1, flat - width, flat + width];
            for (const neighbourFlat of neighbours) {
              const neighbourIndex = neighbourFlat * 4;
              const alpha = pixels[neighbourIndex + 3];
              if (alpha > bestAlpha) {
                bestAlpha = alpha;
                bestIndex = neighbourIndex;
              }
            }
            if (bestIndex < 0 || bestAlpha <= 0) continue;
            next[index] = pixels[bestIndex];
            next[index + 1] = pixels[bestIndex + 1];
            next[index + 2] = pixels[bestIndex + 2];
            next[index + 3] = bestAlpha;
            changed = true;
          }
        }
        pixels = next;
        if (!changed) break;
      }

      image.data.set(pixels);
      context.putImageData(image, 0, 0);
      return canvas;
    };

    const createProjectionStrokeUvMask = (stroke, width, height) => {
      if (!stroke?.sourceCanvas || !Array.isArray(stroke.meshes) || !stroke.meshes.length) return null;
      const projectionCamera = deserializeProjectionCamera(stroke.projectionCameraData);
      if (!projectionCamera) return null;
      projectionCamera.updateMatrixWorld(true);
      projectionCamera.updateProjectionMatrix();

      const projectionRect = stroke.projectionRect || { x: 0, y: 0, width: 1, height: 1 };
      const rectX = Number(projectionRect.x) || 0;
      const rectY = Number(projectionRect.y) || 0;
      const rectWidth = Math.max(1e-9, Number(projectionRect.width) || 1);
      const rectHeight = Math.max(1e-9, Number(projectionRect.height) || 1);
      const sourceWidth = Math.max(1, Number(stroke.sourceCanvas.width) || 1);
      const sourceHeight = Math.max(1, Number(stroke.sourceCanvas.height) || 1);

      // Keep every projected triangle. Screen-space triangle bounds are not a
      // safe coverage test for perspective-clipped or seam-crossing triangles;
      // rejecting them caused parts of an erase to disappear in preview and then
      // return when the committed UV mask was applied. The indexed-geometry path
      // below retains the main performance win without dropping valid coverage.

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = Math.max(1, width);
      maskCanvas.height = Math.max(1, height);
      const maskContext = maskCanvas.getContext('2d', { alpha: true });
      if (!maskContext) return null;
      maskContext.clearRect(0, 0, maskCanvas.width, maskCanvas.height);

      const worldPoint = new THREE.Vector3();
      const projectedPoint = new THREE.Vector3();
      let painted = false;

      stroke.meshes.forEach((mesh) => {
        if (!mesh?.isMesh || !mesh.geometry) return;
        mesh.updateMatrixWorld(true);

        // Do not call geometry.toNonIndexed() here. On a detailed model that
        // duplicates the complete position/UV buffers for every eraser stroke
        // and was the main source of the pointer-up hitch. Read the existing
        // index buffer directly instead.
        const geometry = mesh.geometry;
        const indexAttribute = geometry.getIndex?.() || geometry.index || null;
        const uvAttribute = geometry.getAttribute?.('uv');
        const positionAttribute = geometry.getAttribute?.('position');
        if (!uvAttribute || !positionAttribute) return;

        const indexCount = indexAttribute ? indexAttribute.count : positionAttribute.count;
        const triangleCount = Math.floor(indexCount / 3);
        for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
          const destinationTriangle = [];
          const sourceTriangle = [];
          let validTriangle = true;

          for (let corner = 0; corner < 3; corner += 1) {
            const cornerIndex = (triangleIndex * 3) + corner;
            const vertexIndex = indexAttribute
              ? indexAttribute.getX(cornerIndex)
              : cornerIndex;
            const uvX = uvAttribute.getX(vertexIndex);
            const uvY = uvAttribute.getY(vertexIndex);
            if (!Number.isFinite(uvX) || !Number.isFinite(uvY)) {
              validTriangle = false;
              break;
            }
            destinationTriangle.push({ x: uvX * width, y: uvY * height });

            worldPoint.fromBufferAttribute(positionAttribute, vertexIndex).applyMatrix4(mesh.matrixWorld);
            projectedPoint.copy(worldPoint).project(projectionCamera);
            if (!Number.isFinite(projectedPoint.x) || !Number.isFinite(projectedPoint.y) || !Number.isFinite(projectedPoint.z)) {
              validTriangle = false;
              break;
            }
            const fullU = (projectedPoint.x * 0.5) + 0.5;
            const fullV = (projectedPoint.y * 0.5) + 0.5;
            const croppedU = (fullU - rectX) / rectWidth;
            const croppedV = (fullV - rectY) / rectHeight;
            sourceTriangle.push({
              x: croppedU * sourceWidth,
              y: (1 - croppedV) * sourceHeight,
            });
          }

          if (!validTriangle) continue;


          drawTextureTriangle(maskContext, stroke.sourceCanvas, sourceTriangle, destinationTriangle);
          painted = true;
        }
      });

      if (!painted) return null;
      return maskCanvas;
    };

    const makeStrengthAdjustedNormalCanvas = (sourceCanvas, strength) => {
      if (!sourceCanvas) return null;
      const safeStrength = Math.max(0, Math.min(1, Number(strength) || 0));
      if (safeStrength >= 0.999) return sourceCanvas;
      const adjustedCanvas = document.createElement('canvas');
      adjustedCanvas.width = Math.max(1, sourceCanvas.width || 1);
      adjustedCanvas.height = Math.max(1, sourceCanvas.height || 1);
      const adjustedContext = adjustedCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
      if (!adjustedContext) return sourceCanvas;
      adjustedContext.drawImage(sourceCanvas, 0, 0);
      const image = adjustedContext.getImageData(0, 0, adjustedCanvas.width, adjustedCanvas.height);
      for (let index = 0; index < image.data.length; index += 4) {
        image.data[index] = Math.round(FLAT_NORMAL_RGB.red + ((image.data[index] - FLAT_NORMAL_RGB.red) * safeStrength));
        image.data[index + 1] = Math.round(FLAT_NORMAL_RGB.green + ((image.data[index + 1] - FLAT_NORMAL_RGB.green) * safeStrength));
        image.data[index + 2] = Math.round(FLAT_NORMAL_RGB.blue + ((image.data[index + 2] - FLAT_NORMAL_RGB.blue) * safeStrength));
        image.data[index + 3] = 255;
      }
      adjustedContext.putImageData(image, 0, 0);
      return adjustedCanvas;
    };


    const renderVisibleProjectionPbrOverlayToCanvas = (width, height, channelType = 'material', options = {}) => {
      const gpuComposite = options.gpuComposite === true;
      const runtime = runtimeRef.current;
      if (!runtime || !renderer || !width || !height) {
        void 0;
        void 0;
        return null;
      }

      // Capture the exact renderer presentation state before baking. This is
      // important for material channels as well as colour: raw history order is
      // not the displayed layer order, and shared-normal uniforms are rebuilt
      // from the current layer settings here.
      if (options.skipPresentationRefresh !== true) refreshProjectionLayerPresentation();
      const visibleStrokes = Array.isArray(options.visibleStrokes)
        ? options.visibleStrokes
        : getOrderedVisibleProjectionStrokes();
      void 0;
      if (!visibleStrokes.length) {
        void 0;
        void 0;
        return null;
      }

      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = width;
      finalCanvas.height = height;
      const finalContext = finalCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!finalContext) {
        void 0;
        void 0;
        return null;
      }
      finalContext.clearRect(0, 0, width, height);
      finalContext.imageSmoothingEnabled = false;

      const renderTarget = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      renderTarget.texture.generateMipmaps = false;
      renderTarget.texture.colorSpace = THREE.NoColorSpace;

      // GPU compositing must not blend every overlapping UV triangle directly
      // into the final atlas. Expanded triangle gutters overlap slightly, so
      // direct NormalBlending darkens those overlaps into a crosshatch pattern.
      // Render one complete stroke into an isolated target with NoBlending,
      // then alpha-composite that finished stroke onto the final target once.
      const strokeRenderTarget = gpuComposite
        ? new THREE.WebGLRenderTarget(width, height, {
          format: THREE.RGBAFormat,
          type: THREE.UnsignedByteType,
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          depthBuffer: false,
          stencilBuffer: false,
        })
        : null;
      if (strokeRenderTarget) {
        strokeRenderTarget.texture.generateMipmaps = false;
        strokeRenderTarget.texture.colorSpace = THREE.NoColorSpace;
      }

      const strokeCompositeScene = gpuComposite ? new THREE.Scene() : null;
      const strokeCompositeCamera = gpuComposite
        ? new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
        : null;
      const strokeCompositeGeometry = gpuComposite
        ? new THREE.PlaneGeometry(2, 2)
        : null;
      const strokeCompositeMaterial = gpuComposite
        ? new THREE.MeshBasicMaterial({
          map: strokeRenderTarget.texture,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          blending: THREE.NormalBlending,
          toneMapped: false,
        })
        : null;
      if (strokeCompositeScene && strokeCompositeGeometry && strokeCompositeMaterial) {
        const strokeCompositeMesh = new THREE.Mesh(strokeCompositeGeometry, strokeCompositeMaterial);
        strokeCompositeMesh.frustumCulled = false;
        strokeCompositeScene.add(strokeCompositeMesh);
      }

      const bakeCamera = new THREE.Camera();
      bakeCamera.position.z = 1;
      // Force Three.js to compile UV attributes/varyings for this UV-space
      // render. The white map is neutral when the live map chunk runs.
      const bakeUvEnableTexture = new THREE.DataTexture(
        new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat,
      );
      bakeUvEnableTexture.colorSpace = THREE.SRGBColorSpace;
      bakeUvEnableTexture.needsUpdate = true;
      const previousTarget = renderer.getRenderTarget();
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();
      const previousToneMapping = renderer.toneMapping;
      const previousAutoClear = renderer.autoClear;
      const pixels = new Uint8Array(width * height * 4);
      let renderedStrokeCount = 0;
      let renderedMeshCount = 0;

      const makeBakeMaterialFromLiveProjection = (sourceMaterial, strokeIndex, meshIndex) => {
        if (!sourceMaterial?.onBeforeCompile) return null;
        const bakeMaterial = new THREE.MeshStandardMaterial({
          map: bakeUvEnableTexture,
          // Active-layer erasing is represented on the live projected stroke by
          // an atlas-space alphaMap. Keep that exact mask on the UV bake copy so
          // export sees the erased pixels immediately instead of repainting the
          // original, unmasked stroke into the baked atlas.
          alphaMap: sourceMaterial.alphaMap || null,
          color: sourceMaterial.color?.clone?.() || new THREE.Color(0xffffff),
          roughness: Number.isFinite(sourceMaterial.roughness) ? sourceMaterial.roughness : 1,
          metalness: Number.isFinite(sourceMaterial.metalness) ? sourceMaterial.metalness : 0,
          opacity: Number.isFinite(sourceMaterial.opacity) ? sourceMaterial.opacity : 1,
          transparent: true,
          side: sourceMaterial.side ?? THREE.DoubleSide,
          depthTest: false,
          depthWrite: false,
          blending: THREE.NoBlending,
          toneMapped: false,
        });
        bakeMaterial.alphaTest = sourceMaterial.alphaMap
          ? Math.max(0.001, Number(sourceMaterial.alphaTest) || 0.001)
          : (sourceMaterial.alphaTest || 0);
        bakeMaterial.defines = sourceMaterial.defines ? { ...sourceMaterial.defines } : undefined;
        // The normal audit reconstructs the UV tangent basis with dFdx/dFdy.
        // Flat-colour and shared-normal projection materials do not necessarily
        // enable derivatives themselves, unlike PBR materials with a normal map.
        // Force the extension for every bake material so the audit reaches the
        // actual pre/post comparison instead of failing on the first flat stroke.
        bakeMaterial.extensions = {
          ...(sourceMaterial.extensions || {}),
          derivatives: true,
        };
        bakeMaterial.userData = { ...(sourceMaterial.userData || {}) };
        const diagnosticModeUniform = { value: 0 };
        bakeMaterial.userData.voxemeraBakeDiagnosticModeUniform = diagnosticModeUniform;
        const liveCompile = sourceMaterial.onBeforeCompile;
        bakeMaterial.onBeforeCompile = (shader, activeRenderer) => {
          liveCompile.call(sourceMaterial, shader, activeRenderer);

          // Do not depend on Three.js deciding whether the stock `uv`
          // attribute is required by this particular material permutation.
          // Every bake geometry receives an explicit alias of its canonical UV
          // attribute, so this declaration is always valid and available.
          shader.vertexShader = `attribute vec2 voxemeraBakeUv;
varying vec2 vVoxemeraBakeUv;
` + shader.vertexShader;
          shader.fragmentShader = `varying vec2 vVoxemeraBakeUv;
` + shader.fragmentShader;

          shader.uniforms.voxBakeHeight = {
            value: Math.max(0, Math.min(1, Number(
              sourceMaterial.userData?.voxemeraProjectionUniforms?.heightScale?.value ?? 0,
            ) || 0)),
          };
          shader.uniforms.voxBakeDiagnosticMode = diagnosticModeUniform;
          shader.fragmentShader = `uniform float voxBakeHeight;
uniform float voxBakeDiagnosticMode;
vec3 voxBakeProjectionNormal = vec3(0.0, 0.0, 1.0);
float voxBakeProjectionNormalAssigned = 0.0;
` + shader.fragmentShader;
          const fragmentMainToken = 'void main() {';
          if (shader.fragmentShader.includes(fragmentMainToken)) {
            shader.fragmentShader = shader.fragmentShader.replace(
              fragmentMainToken,
              `${fragmentMainToken}
  if (voxBakeDiagnosticMode > 1.5) {
    gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0);
    return;
  }`,
            );
          } else {
            void 0;
          }

          const projectToken = '#include <project_vertex>';
          if (shader.vertexShader.includes(projectToken)) {
            // Keep Three.js's complete project_vertex chunk because it creates
            // mvPosition and performs batching/instancing/skinning transforms
            // used by later vertex chunks. Only override the final clip-space
            // destination after that stock setup has run.
            shader.vertexShader = shader.vertexShader.replace(
              projectToken,
              `${projectToken}
  vVoxemeraBakeUv = voxemeraBakeUv;
  gl_Position = vec4((voxemeraBakeUv.x * 2.0) - 1.0, (voxemeraBakeUv.y * 2.0) - 1.0, 0.0, 1.0);`,
            );
          } else {
            void 0;
          }

          // Capture the projection material's own normal result before the stock
          // lighting shader converts it into a final view-space surface normal.
          // The baked atlas must contain the normal owned by this stroke, not a
          // reconstruction that can expose a lower layer's normal beneath flat
          // paint. Shared-layer normals are captured after the exact live mix.
          const normalCaptureReplacements = [
            {
              find: 'vec3 blendedNormal = normalize(mix(sourceNormal, ownNormal, clamp(normalLayerMix, 0.0, 1.0)));\n                normal = normalize(normal + vec3(blendedNormal.xy * heightScale, 0.0));',
              replace: 'vec3 blendedNormal = normalize(mix(sourceNormal, ownNormal, clamp(normalLayerMix, 0.0, 1.0)));\n                voxBakeProjectionNormal = normalize(vec3(blendedNormal.xy * heightScale, blendedNormal.z));\n                voxBakeProjectionNormalAssigned = 1.0;\n                normal = normalize(normal + vec3(blendedNormal.xy * heightScale, 0.0));',
            },
            {
              find: 'vec3 blendedLayerNormal = normalize(mix(sourceNormal, vec3(0.0, 0.0, 1.0), clamp(normalLayerMix, 0.0, 1.0)));\n                normal = normalize(normal + vec3(blendedLayerNormal.xy, 0.0));',
              replace: 'vec3 blendedLayerNormal = normalize(mix(sourceNormal, vec3(0.0, 0.0, 1.0), clamp(normalLayerMix, 0.0, 1.0)));\n                voxBakeProjectionNormal = blendedLayerNormal;\n                voxBakeProjectionNormalAssigned = 1.0;\n                normal = normalize(normal + vec3(blendedLayerNormal.xy, 0.0));',
            },
            {
              find: 'vec3 blendedLayerNormal = normalize(mix(sourceNormal, ownLayerNormal, clamp(normalLayerMix, 0.0, 1.0)));\n                  projectedNormal = normalize(projectedNormal + vec3(blendedLayerNormal.xy, 0.0));',
              replace: 'vec3 blendedLayerNormal = normalize(mix(sourceNormal, ownLayerNormal, clamp(normalLayerMix, 0.0, 1.0)));\n                  voxBakeProjectionNormal = blendedLayerNormal;\n                  voxBakeProjectionNormalAssigned = 1.0;\n                  projectedNormal = normalize(projectedNormal + vec3(blendedLayerNormal.xy, 0.0));',
            },
          ];
          normalCaptureReplacements.forEach(({ find, replace }) => {
            if (shader.fragmentShader.includes(find)) {
              shader.fragmentShader = shader.fragmentShader.replace(find, replace);
            }
          });

          let outputCode = `
            if (voxBakeDiagnosticMode > 0.5) {
              gl_FragColor = vec4(1.0, 1.0, 1.0, diffuseColor.a);
            } else {
              gl_FragColor = vec4(diffuseColor.rgb, diffuseColor.a);
            }
          `;
          if (channelType === 'material') {
            outputCode = `
              if (voxBakeDiagnosticMode > 0.5) {
                gl_FragColor = vec4(1.0, 1.0, 1.0, diffuseColor.a);
              } else {
                gl_FragColor = vec4(
                  voxBakeHeight,
                  clamp(roughnessFactor, 0.0, 1.0),
                  clamp(metalnessFactor, 0.0, 1.0),
                  diffuseColor.a
                );
              }
            `;
          } else if (channelType === 'normal') {
            outputCode = `
              if (voxBakeDiagnosticMode > 0.5) {
                gl_FragColor = vec4(1.0, 1.0, 1.0, diffuseColor.a);
              } else {
                // Bake the exact per-stroke normal produced by the existing live
                // normal-sharing branches. Those branches already apply the chosen
                // source texture, its scale/rotation, this stroke's own/flat/height
                // normal, and the layer slider (0 = chosen, 1 = this layer).
                // Do not convert the final view-space surface normal back into a new
                // tangent map: that applies the geometry basis twice and destroys the
                // original sharing result.
                vec3 voxExactStrokeNormal = voxBakeProjectionNormalAssigned > 0.5
                  ? normalize(voxBakeProjectionNormal)
                  : vec3(0.0, 0.0, 1.0);
                gl_FragColor = vec4((voxExactStrokeNormal * 0.5) + 0.5, diffuseColor.a);
              }
            `;
          }

          const outputTokens = ['#include <opaque_fragment>', '#include <output_fragment>'];
          const outputToken = outputTokens.find((token) => shader.fragmentShader.includes(token));
          if (outputToken) {
            shader.fragmentShader = shader.fragmentShader.replace(outputToken, outputCode);
          } else {
            void 0;
          }
          if (strokeIndex === 0 && meshIndex === 0) {
            void 0;
            void 0;
            void 0;
            void 0;
          }
        };
        bakeMaterial.customProgramCacheKey = () => `voxemeraSharedLiveUvBake_${channelType}_${strokeIndex}_${meshIndex}_${sourceMaterial.customProgramCacheKey?.() || 'live'}`;
        bakeMaterial.needsUpdate = true;
        return bakeMaterial;
      };

      const previousShaderErrorHandler = renderer.debug?.onShaderError;
      if (renderer.debug) {
        renderer.debug.onShaderError = (gl, program, vertexShader, fragmentShader) => {
          const programLog = gl.getProgramInfoLog(program) || '';
          const vertexLog = gl.getShaderInfoLog(vertexShader) || '';
          const fragmentLog = gl.getShaderInfoLog(fragmentShader) || '';
          const vertexSource = gl.getShaderSource(vertexShader) || '';
          const fragmentSource = gl.getShaderSource(fragmentShader) || '';
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          if (typeof previousShaderErrorHandler === 'function') {
            previousShaderErrorHandler(gl, program, vertexShader, fragmentShader);
          }
        };
      }

      renderer.toneMapping = THREE.NoToneMapping;
      renderer.autoClear = !gpuComposite;
      if (gpuComposite) {
        renderer.setRenderTarget(renderTarget);
        renderer.setClearColor(0x000000, 0);
        renderer.clear(true, false, false);
      }

      const antialiasProjectedStrokeBoundary = (canvas) => {
        const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
        if (!context || canvas.width < 2 || canvas.height < 2) return;

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const source = imageData.data;
        const output = new Uint8ClampedArray(source);
        const widthPx = canvas.width;
        const heightPx = canvas.height;
        const weights = [1, 2, 1, 2, 4, 2, 1, 2, 1];

        for (let y = 1; y < heightPx - 1; y += 1) {
          for (let x = 1; x < widthPx - 1; x += 1) {
            const pixelOffset = ((y * widthPx) + x) * 4;
            let minimumAlpha = 255;
            let maximumAlpha = 0;
            let weightedAlpha = 0;
            let weightedRed = 0;
            let weightedGreen = 0;
            let weightedBlue = 0;
            let alphaWeightTotal = 0;
            let kernelIndex = 0;

            for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
              for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
                const neighbourOffset = ((((y + offsetY) * widthPx) + (x + offsetX)) * 4);
                const alpha = source[neighbourOffset + 3];
                const weight = weights[kernelIndex];
                kernelIndex += 1;
                minimumAlpha = Math.min(minimumAlpha, alpha);
                maximumAlpha = Math.max(maximumAlpha, alpha);
                weightedAlpha += alpha * weight;
                if (alpha > 0) {
                  const alphaWeight = alpha * weight;
                  weightedRed += source[neighbourOffset] * alphaWeight;
                  weightedGreen += source[neighbourOffset + 1] * alphaWeight;
                  weightedBlue += source[neighbourOffset + 2] * alphaWeight;
                  alphaWeightTotal += alphaWeight;
                }
              }
            }

            // Leave fully empty and fully covered areas byte-identical. Only the
            // one-pixel projected boundary is antialiased, so the PBR pattern and
            // the UV-seam gutter remain untouched.
            if (minimumAlpha === maximumAlpha) continue;

            output[pixelOffset + 3] = Math.round(weightedAlpha / 16);
            if (alphaWeightTotal > 0) {
              output[pixelOffset] = Math.round(weightedRed / alphaWeightTotal);
              output[pixelOffset + 1] = Math.round(weightedGreen / alphaWeightTotal);
              output[pixelOffset + 2] = Math.round(weightedBlue / alphaWeightTotal);
            }
          }
        }

        imageData.data.set(output);
        context.putImageData(imageData, 0, 0);
      };

      visibleStrokes.forEach((stroke, strokeIndex) => {
        const visibleMeshes = Array.isArray(stroke?.meshes)
          ? stroke.meshes.filter((mesh) => mesh?.visible !== false && mesh?.geometry?.getAttribute?.('uv'))
          : [];
        void 0;
        void 0;
        void 0;
        if (!visibleMeshes.length) {
          void 0;
          void 0;
          return;
        }

        const strokeCanvas = gpuComposite ? null : document.createElement('canvas');
        if (strokeCanvas) {
          strokeCanvas.width = width;
          strokeCanvas.height = height;
        }
        const strokeContext = strokeCanvas
          ? strokeCanvas.getContext('2d', { alpha: true, willReadFrequently: true })
          : null;
        if (!gpuComposite && !strokeContext) {
          void 0;
          void 0;
          return;
        }
        if (strokeContext) strokeContext.clearRect(0, 0, width, height);
        if (gpuComposite && strokeRenderTarget) {
          renderer.setRenderTarget(strokeRenderTarget);
          renderer.setClearColor(0x000000, 0);
          renderer.clear(true, false, false);
        }

        visibleMeshes.forEach((sourceMesh, meshIndex) => {
          const sourceMaterials = Array.isArray(sourceMesh.material) ? sourceMesh.material : [sourceMesh.material];
          sourceMaterials.forEach((sourceMaterial, materialIndex) => {
            const bakeMaterial = makeBakeMaterialFromLiveProjection(sourceMaterial, strokeIndex, (meshIndex * 100) + materialIndex);
            if (!bakeMaterial) {
              void 0;
              return;
            }
            const bakeScene = new THREE.Scene();
            const sourceGeometry = sourceMesh.geometry;
            const originalUv = sourceGeometry.getAttribute('uv');
            if (!originalUv) {
              void 0;
              bakeMaterial.dispose();
              return;
            }

            // Rasterise with the model's exact UV coordinates. Do not enlarge
            // individual UV triangles: moving the raster vertices while keeping
            // their world-position varyings at the original vertices stretches
            // the triplanar PBR independently inside every triangle and makes the
            // baked material appear crooked. Any seam padding must be performed
            // after the exact projection, without changing this UV interpolation.
            const bakeGeometry = sourceGeometry.index
              ? sourceGeometry.toNonIndexed()
              : sourceGeometry.clone();
            const sourceUv = bakeGeometry.getAttribute('uv');
            const exactUv = new Float32Array(sourceUv.count * 2);

            for (let vertexIndex = 0; vertexIndex < sourceUv.count; vertexIndex += 1) {
              exactUv[(vertexIndex * 2)] = sourceUv.getX(vertexIndex);
              exactUv[(vertexIndex * 2) + 1] = sourceUv.getY(vertexIndex);
            }

            bakeGeometry.setAttribute(
              'voxemeraBakeUv',
              new THREE.BufferAttribute(exactUv, 2),
            );
            const bakeMesh = new THREE.Mesh(bakeGeometry, bakeMaterial);
            sourceMesh.updateMatrixWorld(true);
            bakeMesh.matrixAutoUpdate = false;
            bakeMesh.matrix.copy(sourceMesh.matrixWorld);
            bakeMesh.matrixWorld.copy(sourceMesh.matrixWorld);
            bakeMesh.frustumCulled = false;
            bakeScene.add(bakeMesh);

            const diagnosticModeUniform = bakeMaterial.userData.voxemeraBakeDiagnosticModeUniform;
            const renderDiagnosticMode = (modeValue) => {
              if (diagnosticModeUniform) diagnosticModeUniform.value = modeValue;
              renderer.setRenderTarget(gpuComposite ? strokeRenderTarget : renderTarget);
              renderer.setClearColor(0x000000, 0);
              if (!gpuComposite) renderer.clear(true, false, false);
              renderer.render(bakeScene, bakeCamera);
              if (!gpuComposite) renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels);
            };

            renderDiagnosticMode(0);
            if (diagnosticModeUniform) diagnosticModeUniform.value = 0;
            if (!gpuComposite) {
            const meshCanvas = document.createElement('canvas');
            meshCanvas.width = width;
            meshCanvas.height = height;
            const meshContext = meshCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
            if (meshContext) {
              // The UV render target is NoColorSpace, so the colour pass is read
              // back as LINEAR RGB bytes. The finished colour atlas is an sRGB
              // texture. Storing those linear bytes unchanged made Three.js decode
              // them as sRGB a second time, which is exactly why the baked wall was
              // much darker than the live projected PBR.
              //
              // Encode only the colour pass back to sRGB before it enters the
              // canonical sRGB atlas. Material and normal channels must remain
              // linear and are therefore copied byte-for-byte.
              const canvasPixels = new Uint8ClampedArray(pixels);
              if (channelType === 'colour') {
                for (let pixelIndex = 0; pixelIndex < canvasPixels.length; pixelIndex += 4) {
                  if (canvasPixels[pixelIndex + 3] === 0) continue;
                  for (let channelIndex = 0; channelIndex < 3; channelIndex += 1) {
                    const linear = canvasPixels[pixelIndex + channelIndex] / 255;
                    const srgb = linear <= 0.0031308
                      ? linear * 12.92
                      : (1.055 * Math.pow(linear, 1 / 2.4)) - 0.055;
                    canvasPixels[pixelIndex + channelIndex] = Math.round(
                      THREE.MathUtils.clamp(srgb, 0, 1) * 255,
                    );
                  }
                }
              }

              // Do not flip this readback. This UV pass and the canonical atlas
              // both use the same bottom-left UV convention with flipY=false.
              meshContext.putImageData(
                new ImageData(canvasPixels, width, height),
                0,
                0,
              );
              strokeContext.save();
              strokeContext.globalCompositeOperation = 'source-over';
              strokeContext.globalAlpha = 1;
              strokeContext.drawImage(meshCanvas, 0, 0);
              strokeContext.restore();
            }
            }
            renderedMeshCount += 1;
            bakeGeometry.dispose();
            bakeMaterial.dispose();
          });
        });

        if (gpuComposite && strokeRenderTarget && strokeCompositeScene && strokeCompositeCamera) {
          renderer.setRenderTarget(renderTarget);
          renderer.setClearColor(0x000000, 0);
          renderer.render(strokeCompositeScene, strokeCompositeCamera);
        }

        // The bake triangles were already expanded in UV space before the
        // projection shader ran. Do not dilate this completed textured image:
        // that repeats one edge texel across the gutter and visibly stretches
        // the PBR pattern along jagged triangle boundaries.
        //
        // Smooth only the single-pixel alpha boundary left by UV-space rasterising.
        // RGB/PBR texels inside the stroke and the expanded seam gutter are not
        // rescaled or blurred, so the stretch fix remains unchanged.
        if (!gpuComposite && strokeCanvas && strokeContext) {
          antialiasProjectedStrokeBoundary(strokeCanvas);

          finalContext.save();
          finalContext.globalCompositeOperation = 'source-over';
          finalContext.globalAlpha = 1;
          finalContext.drawImage(strokeCanvas, 0, 0);
          finalContext.restore();
          const strokeImageData = strokeContext.getImageData(0, 0, width, height).data;
        }
        renderedStrokeCount += 1;
        void 0;
        void 0;
      });

      if (gpuComposite && renderedStrokeCount > 0) {
        renderer.setRenderTarget(renderTarget);
        renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels);
        const finalPixels = new Uint8ClampedArray(pixels);
        if (channelType === 'colour') {
          for (let pixelIndex = 0; pixelIndex < finalPixels.length; pixelIndex += 4) {
            if (finalPixels[pixelIndex + 3] === 0) continue;
            for (let channelIndex = 0; channelIndex < 3; channelIndex += 1) {
              const linear = finalPixels[pixelIndex + channelIndex] / 255;
              const srgb = linear <= 0.0031308
                ? linear * 12.92
                : (1.055 * Math.pow(linear, 1 / 2.4)) - 0.055;
              finalPixels[pixelIndex + channelIndex] = Math.round(
                THREE.MathUtils.clamp(srgb, 0, 1) * 255,
              );
            }
          }
        }
        finalContext.putImageData(new ImageData(finalPixels, width, height), 0, 0);
      }

      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(previousClearColor, previousClearAlpha);
      renderer.toneMapping = previousToneMapping;
      renderer.autoClear = previousAutoClear;
      if (renderer.debug) renderer.debug.onShaderError = previousShaderErrorHandler;
      renderTarget.dispose();
      strokeRenderTarget?.dispose?.();
      strokeCompositeGeometry?.dispose?.();
      strokeCompositeMaterial?.dispose?.();
      bakeUvEnableTexture.dispose();

      void 0;
      if (!gpuComposite) {
        const finalImageData = finalContext.getImageData(0, 0, width, height).data;
      }
      void 0;
      void 0;
      void 0;
      return renderedStrokeCount > 0 ? finalCanvas : null;
    };

    const renderSharedLayerNormalsToCanvas = (width, height) => {
      const runtime = runtimeRef.current;
      const sourceModel = currentModelRef.current;
      const { layers: currentLayers } = getCurrentLayerState();
      if (!runtime || !sourceModel || !renderer || !width || !height || !currentLayers.length) return null;

      const visibleCount = Math.max(
        0,
        Math.min(
          Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes.length : 0,
          Number(projectionStrokeCountRef.current) || 0,
        ),
      );
      const visibleStrokes = (runtime.projectionStrokes || []).slice(0, visibleCount);
      const targetLayers = currentLayers.filter((layer) => (
        layer?.visible !== false
        && String(layer?.normalSourceLayerId || '')
      ));
      if (!targetLayers.length) return null;

      const findLatestPbrStrokeForLayer = (layerId) => {
        const safeLayerId = String(layerId || '');
        for (let index = visibleStrokes.length - 1; index >= 0; index -= 1) {
          const candidate = visibleStrokes[index];
          if (String(candidate?.layerId || '') === safeLayerId && candidate?.isPbr === true) return candidate;
        }
        return null;
      };

      const makeLayerCoverageCanvas = (layerId) => {
        const coverageCanvas = document.createElement('canvas');
        coverageCanvas.width = width;
        coverageCanvas.height = height;
        const coverageContext = coverageCanvas.getContext('2d', { alpha: true });
        if (!coverageContext) return null;
        coverageContext.clearRect(0, 0, width, height);

        const drawStrokeCoverage = (stroke) => {
          if (!stroke?.sourceCanvas) return;
          if (Array.isArray(stroke.meshes) && !stroke.meshes.some((mesh) => mesh?.visible !== false)) return;
          const maskCanvas = stroke.kind === 'uvFloodFill'
            ? stroke.sourceCanvas
            : createProjectionStrokeUvMask(stroke, width, height);
          if (!maskCanvas) return;
          const opacity = Math.max(0, Math.min(1, Number(stroke.brushConfig?.opacity) || 1));
          coverageContext.save();
          coverageContext.globalCompositeOperation = 'source-over';
          coverageContext.globalAlpha = opacity;
          coverageContext.drawImage(maskCanvas, 0, 0, width, height);
          coverageContext.restore();
        };

        visibleStrokes.forEach((stroke) => {
          if (String(stroke?.layerId || '') === String(layerId || '')) drawStrokeCoverage(stroke);
        });
        const decalRecord = runtime.layerDecals instanceof Map
          ? runtime.layerDecals.get(String(layerId || ''))
          : null;
        if (decalRecord?.projectedStroke) drawStrokeCoverage(decalRecord.projectedStroke);
        return coverageCanvas;
      };

      const target = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      target.texture.generateMipmaps = false;
      target.texture.colorSpace = THREE.NoColorSpace;

      const bakeCamera = new THREE.Camera();
      bakeCamera.position.z = 1;
      const temporaryTextures = [];
      const temporaryMaterials = [];
      const temporaryGeometries = [];
      const pbrWorldBounds = new THREE.Box3().setFromObject(sourceModel);
      const pbrWorldOrigin = pbrWorldBounds.isEmpty() ? new THREE.Vector3() : pbrWorldBounds.min.clone();
      const pbrWorldDimensions = pbrWorldBounds.isEmpty()
        ? new THREE.Vector3(1, 1, 1)
        : pbrWorldBounds.getSize(new THREE.Vector3());
      const pbrWorldTileSize = Math.max(
        0.00001,
        Math.max(pbrWorldDimensions.x, pbrWorldDimensions.y, pbrWorldDimensions.z, 0.001) * 0.12,
      );
      let renderedLayerCount = 0;

      const previousTarget = renderer.getRenderTarget();
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();
      const previousToneMapping = renderer.toneMapping;
      const previousAutoClear = renderer.autoClear;
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, false, false);
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.autoClear = false;

      targetLayers.slice().reverse().forEach((targetLayer) => {
        const sourceLayerId = String(targetLayer.normalSourceLayerId || '');
        const sourceStroke = findLatestPbrStrokeForLayer(sourceLayerId);
        if (!sourceStroke) return;
        const sourceSetKey = String(sourceStroke.brushConfig?.pbrSetKey || '');
        const sourceResource = sourceSetKey ? pbrSetResourcesRef.current.get(sourceSetKey) : null;
        const sourceTextures = getPbrRuntimeTextures(sourceResource);
        if (!sourceTextures?.normal) return;

        const coverageCanvas = makeLayerCoverageCanvas(targetLayer.id);
        if (!coverageCanvas) return;
        const coverageTexture = new THREE.CanvasTexture(coverageCanvas);
        coverageTexture.colorSpace = THREE.NoColorSpace;
        coverageTexture.flipY = false;
        coverageTexture.wrapS = THREE.ClampToEdgeWrapping;
        coverageTexture.wrapT = THREE.ClampToEdgeWrapping;
        coverageTexture.minFilter = THREE.LinearFilter;
        coverageTexture.magFilter = THREE.LinearFilter;
        coverageTexture.generateMipmaps = false;
        coverageTexture.needsUpdate = true;
        temporaryTextures.push(coverageTexture);

        const targetOwnStroke = findLatestPbrStrokeForLayer(targetLayer.id);
        const ownSetKey = String(targetOwnStroke?.brushConfig?.pbrSetKey || '');
        const ownResource = ownSetKey ? pbrSetResourcesRef.current.get(ownSetKey) : null;
        const ownTextures = getPbrRuntimeTextures(ownResource);
        const selfMix = Math.max(0, Math.min(1, Number(targetLayer.normalSelfPercent ?? 100) / 100));
        const sourceScale = Math.max(
          0.1,
          Math.min(8, (Number(sourceStroke.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100),
        );
        const sourceAngle = THREE.MathUtils.degToRad(Number(sourceStroke.brushConfig?.pbrAngleDegrees) || 0);
        const ownScale = Math.max(
          0.1,
          Math.min(8, (Number(targetOwnStroke?.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100),
        );
        const ownAngle = THREE.MathUtils.degToRad(Number(targetOwnStroke?.brushConfig?.pbrAngleDegrees) || 0);
        const heightScale = Math.max(
          0,
          Math.min(1, (Number((targetOwnStroke || visibleStrokes.find((stroke) => String(stroke?.layerId || '') === String(targetLayer.id || '')))?.materialSettings?.height) || 0) / 100),
        );

        const bakeScene = new THREE.Scene();
        sourceModel.updateMatrixWorld(true);
        sourceModel.traverse((sourceMesh) => {
          if (!sourceMesh?.isMesh || sourceMesh.userData?.voxemeraProjectionDecal || !sourceMesh.geometry) return;
          const geometry = sourceMesh.geometry.clone();
          if (!geometry.getAttribute?.('uv') || !geometry.getAttribute?.('position') || !geometry.getAttribute?.('normal')) {
            geometry.dispose();
            return;
          }
          temporaryGeometries.push(geometry);
          const material = new THREE.ShaderMaterial({
            uniforms: {
              coverageMap: { value: coverageTexture },
              sourceNormalMap: { value: sourceTextures.normal },
              ownNormalMap: { value: ownTextures?.normal || sourceTextures.normal },
              hasOwnNormal: { value: ownTextures?.normal ? 1 : 0 },
              selfMix: { value: selfMix },
              sourceScale: { value: sourceScale },
              sourceAngle: { value: sourceAngle },
              ownScale: { value: ownScale },
              ownAngle: { value: ownAngle },
              heightScale: { value: heightScale },
              pbrWorldOrigin: { value: pbrWorldOrigin },
              pbrWorldTileSize: { value: pbrWorldTileSize },
              sourceWorldMatrix: { value: sourceMesh.matrixWorld.clone() },
            },
            vertexShader: `
              uniform mat4 sourceWorldMatrix;
              varying vec2 vUv;
              varying vec3 vWorldPosition;
              varying vec3 vWorldNormal;
              void main() {
                vUv = uv;
                vWorldPosition = (sourceWorldMatrix * vec4(position, 1.0)).xyz;
                vWorldNormal = normalize(mat3(sourceWorldMatrix) * normal);
                gl_Position = vec4((uv.x * 2.0) - 1.0, (uv.y * 2.0) - 1.0, 0.0, 1.0);
              }
            `,
            fragmentShader: `
              precision highp float;
              uniform sampler2D coverageMap;
              uniform sampler2D sourceNormalMap;
              uniform sampler2D ownNormalMap;
              uniform float hasOwnNormal;
              uniform float selfMix;
              uniform float sourceScale;
              uniform float sourceAngle;
              uniform float ownScale;
              uniform float ownAngle;
              uniform float heightScale;
              uniform vec3 pbrWorldOrigin;
              uniform float pbrWorldTileSize;
              varying vec2 vUv;
              varying vec3 vWorldPosition;
              varying vec3 vWorldNormal;

              vec2 rotateUv(vec2 uvValue, float angleValue) {
                float c = cos(angleValue);
                float s = sin(angleValue);
                return mat2(c, -s, s, c) * uvValue;
              }
              vec3 weightsForNormal() {
                vec3 weights = pow(abs(normalize(vWorldNormal)), vec3(6.0));
                return weights / max(weights.x + weights.y + weights.z, 0.0001);
              }
              vec3 sampleNormalRgb(sampler2D sourceMap, float scaleValue, float angleValue) {
                vec3 coords = (vWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * scaleValue, 0.00001);
                vec3 weights = weightsForNormal();
                vec2 uvX = fract(rotateUv(coords.zy, angleValue));
                vec2 uvY = fract(rotateUv(coords.xz, angleValue));
                vec2 uvZ = fract(rotateUv(coords.xy, angleValue));
                return (texture2D(sourceMap, uvX).rgb * weights.x)
                  + (texture2D(sourceMap, uvY).rgb * weights.y)
                  + (texture2D(sourceMap, uvZ).rgb * weights.z);
              }
              void main() {
                float coverage = texture2D(coverageMap, vUv).a;
                if (coverage <= 0.001) discard;
                vec3 sourceNormal = sampleNormalRgb(sourceNormalMap, sourceScale, sourceAngle) * 2.0 - 1.0;
                vec3 ownNormal = hasOwnNormal > 0.5
                  ? (sampleNormalRgb(ownNormalMap, ownScale, ownAngle) * 2.0 - 1.0)
                  : vec3(0.0, 0.0, 1.0);
                vec3 blendedNormal = normalize(mix(sourceNormal, ownNormal, clamp(selfMix, 0.0, 1.0)));
                vec3 bakedNormal = normalize(vec3(blendedNormal.xy * heightScale, blendedNormal.z));
                gl_FragColor = vec4((bakedNormal * 0.5) + 0.5, coverage);
              }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
            toneMapped: false,
          });
          temporaryMaterials.push(material);
          const bakeMesh = new THREE.Mesh(geometry, material);
          bakeMesh.frustumCulled = false;
          bakeScene.add(bakeMesh);
        });
        renderer.render(bakeScene, bakeCamera);
        renderedLayerCount += 1;
      });

      renderer.autoClear = previousAutoClear;
      renderer.toneMapping = previousToneMapping;
      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(previousClearColor, previousClearAlpha);

      if (!renderedLayerCount) {
        temporaryTextures.forEach((texture) => texture.dispose());
        temporaryMaterials.forEach((material) => material.dispose());
        temporaryGeometries.forEach((geometry) => geometry.dispose());
        target.dispose();
        return null;
      }

      const pixels = new Uint8Array(width * height * 4);
      renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      temporaryTextures.forEach((texture) => texture.dispose());
      temporaryMaterials.forEach((material) => material.dispose());
      temporaryGeometries.forEach((geometry) => geometry.dispose());
      target.dispose();

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!context) return null;
      context.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
      void 0;
      return canvas;
    };

    const bakeVisibleProjectionPbrMapsIntoCanonicalAtlases = (
      width,
      height,
      targetColourContext = activePaintSession?.context,
      targetMaterialContext = activePaintSession?.materialContext,
      targetNormalContext = activePaintSession?.normalContext,
      includeColour = true,
    ) => {
      const runtime = runtimeRef.current;
      if (!targetColourContext || !targetMaterialContext || !targetNormalContext || !runtime) return false;
      const visibleCount = Math.max(
        0,
        Math.min(
          Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes.length : 0,
          Number(projectionStrokeCountRef.current) || 0,
        ),
      );
      let painted = false;

      (runtime.projectionStrokes || []).slice(0, visibleCount).forEach((stroke) => {
        if (!stroke?.isPbr || !stroke?.sourceCanvas) return;
        if (Array.isArray(stroke.meshes) && !stroke.meshes.some((mesh) => mesh?.visible !== false)) return;
        const maskCanvas = stroke.kind === 'uvFloodFill'
          ? stroke.sourceCanvas
          : createProjectionStrokeUvMask(stroke, width, height);
        if (!maskCanvas) return;

        const savedPbrSetKey = String(stroke.brushConfig?.pbrSetKey || '');
        const pbrResource = savedPbrSetKey
          ? (pbrSetResourcesRef.current.get(savedPbrSetKey) || null)
          : null;
        if (!pbrResource) return;

        const opacity = Math.max(0.01, Math.min(1, Number(stroke.brushConfig?.opacity) || 1));
        const defaultMaterialBytes = getPackedMaterialChannelBytes(stroke.materialSettings || materialSettingsRef.current);
        const fallbackColour = new THREE.Color(stroke.brushConfig?.strokeColor || stroke.brushConfig?.color || '#000000');
        const fallbackColourStyle = `rgb(${Math.round(fallbackColour.r * 255)}, ${Math.round(fallbackColour.g * 255)}, ${Math.round(fallbackColour.b * 255)})`;
        const fallbackMaterialStyle = `rgb(${defaultMaterialBytes.red}, ${defaultMaterialBytes.green}, ${defaultMaterialBytes.blue})`;
        const fallbackNormalStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
        const scaleFactor = Math.max(0.1, Math.min(8, (Number(stroke.brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100));
        const angleDegrees = ((Number(stroke.brushConfig?.pbrAngleDegrees) || 0) % 360 + 360) % 360;

        // The live GPU colour target already contains the exact triplanar PBR
        // projection used in the viewport. Re-patterning that mask in UV space
        // changes its scale and angle, so export can deliberately skip this
        // colour pass while still baking the non-colour PBR channels below.
        const colourPainted = includeColour
          ? paintPatternMaskOntoContext({
            targetContext: targetColourContext,
            maskCanvas,
            sourceCanvas: pbrResource.albedoCanvas || null,
            fallbackFillStyle: fallbackColourStyle,
            opacity,
            scaleFactor,
            angleDegrees,
          })
          : false;
        const materialPainted = paintPatternMaskOntoContext({
          targetContext: targetMaterialContext,
          maskCanvas,
          sourceCanvas: pbrResource.packedMaterialCanvas || null,
          fallbackFillStyle: fallbackMaterialStyle,
          opacity,
          scaleFactor,
          angleDegrees,
        });
        const normalPainted = paintPatternMaskOntoContext({
          targetContext: targetNormalContext,
          maskCanvas,
          sourceCanvas: makeStrengthAdjustedNormalCanvas(
            pbrResource.normalCanvas || null,
            Math.max(0, Math.min(1, (Number(stroke.materialSettings?.height) || 0) / 100)),
          ),
          fallbackFillStyle: fallbackNormalStyle,
          opacity,
          scaleFactor,
          angleDegrees,
        });
        painted = colourPainted || materialPainted || normalPainted || painted;
      });

      if (painted && targetColourContext === activePaintSession?.context) {
        activePaintSession.texture.needsUpdate = true;
      }
      if (painted && targetMaterialContext === activePaintSession?.materialContext) {
        activePaintSession.materialTexture.needsUpdate = true;
      }
      if (painted && targetNormalContext === activePaintSession?.normalContext) {
        activePaintSession.normalTexture.needsUpdate = true;
      }
      return painted;
    };

    const applySolidFillMaskDirectlyToCanonicalAtlas = ({
      maskCanvas,
      fillHex,
      filledBounds = null,
      materialSettings = materialSettingsRef.current,
    }) => {
      const session = activePaintSession;
      if (!session?.context || !session?.canvas || !session?.materialContext || !maskCanvas) return false;

      const width = session.canvas.width;
      const height = session.canvas.height;
      const maskContext = maskCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!maskContext) return false;

      const boundsX = Math.max(0, Math.min(width - 1, Math.floor(Number(filledBounds?.minX) || 0)));
      const boundsY = Math.max(0, Math.min(height - 1, Math.floor(Number(filledBounds?.minY) || 0)));
      const boundsMaxX = Math.max(boundsX, Math.min(width - 1, Math.ceil(Number(filledBounds?.maxX) || (width - 1))));
      const boundsMaxY = Math.max(boundsY, Math.min(height - 1, Math.ceil(Number(filledBounds?.maxY) || (height - 1))));
      const regionWidth = Math.max(1, boundsMaxX - boundsX + 1);
      const regionHeight = Math.max(1, boundsMaxY - boundsY + 1);
      const maskImage = maskContext.getImageData(boundsX, boundsY, regionWidth, regionHeight);
      const colourImage = session.context.getImageData(boundsX, boundsY, regionWidth, regionHeight);
      const materialImage = session.materialContext.getImageData(boundsX, boundsY, regionWidth, regionHeight);
      const colour = new THREE.Color(fillHex || '#000000');
      const red = Math.round(colour.r * 255);
      const green = Math.round(colour.g * 255);
      const blue = Math.round(colour.b * 255);
      const channels = getPackedMaterialChannelBytes(materialSettings);
      let changedPixels = 0;

      for (let pixel = 0; pixel < regionWidth * regionHeight; pixel += 1) {
        const index = pixel * 4;
        if (maskImage.data[index + 3] <= 0) continue;
        colourImage.data[index] = red;
        colourImage.data[index + 1] = green;
        colourImage.data[index + 2] = blue;
        colourImage.data[index + 3] = 255;
        materialImage.data[index] = channels.red;
        materialImage.data[index + 1] = channels.green;
        materialImage.data[index + 2] = channels.blue;
        materialImage.data[index + 3] = 255;
        changedPixels += 1;
      }

      if (!changedPixels) return false;
      session.context.putImageData(colourImage, boundsX, boundsY);
      session.materialContext.putImageData(materialImage, boundsX, boundsY);
      session.texture.needsUpdate = true;
      session.materialTexture.needsUpdate = true;
      applyCurrentPaintMaterial();
      refreshVisibleMaterial();
      if (viewportDisplayModeRef.current === 'texture') {
        drawCurrentTextureView(textureViewCanvasRef.current);
      }

      return true;
    };

    const flattenVisiblePaintIntoCanonicalAtlas = (materialSettings = materialSettingsRef.current) => {
      const session = activePaintSession;
      if (!session?.context || !session?.canvas || !session?.materialContext) return false;
      const source = readCurrentUvColourPixels();
      if (!source) {
        return false;
      }
      const {
        width,
        height,
        pixels: rawPixels,
        coverage: rawCoverage,
      } = source;
      const padded = padPaintAtlasEdges(
        rawPixels,
        rawCoverage,
        width,
        height,
        6,
      );
      const pixels = padded.pixels;
      const coverage = padded.coverage;
      const coveredPixels = coverage
        ? coverage.reduce((count, value) => count + (value > 0 ? 1 : 0), 0)
        : 0;

      // The composite is already in canonical atlas coordinates. Store it
      // directly; applying another vertical conversion moves paint on the model.
      // The six-pixel gutter prevents white atlas texels being sampled at UV
      // island borders by bucket fill and airbrush.
      session.context.setTransform(1, 0, 0, 1, 0, 0);
      session.context.clearRect(0, 0, width, height);
      session.context.putImageData(
        new ImageData(new Uint8ClampedArray(pixels), width, height),
        0,
        0,
      );

      // PBR projection strokes use shader-only material/normal maps while they
      // are live. Bake those maps into the canonical UV atlases before the
      // temporary projection meshes are removed so GLB/glTF export receives
      // the same full PBR surface shown in the viewport.
      // Apply the current universal material channels to texels contributed by
      // non-PBR projected paint before that temporary layer is discarded.
      if (coverage) {
        const channels = getPackedMaterialChannelBytes(materialSettings);
        const materialImage = session.materialContext.getImageData(0, 0, width, height);
        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const flat = (y * width) + x;
            if ((coverage[flat] || 0) <= 0) continue;
            const index = flat * 4;
            materialImage.data[index] = channels.red;
            materialImage.data[index + 1] = channels.green;
            materialImage.data[index + 2] = channels.blue;
            materialImage.data[index + 3] = 255;
          }
        }
        session.materialContext.putImageData(materialImage, 0, 0);
      }

      // Re-apply the actual PBR channels after the generic projected material
      // values so imported roughness, metallic and normal maps win exactly
      // where a PBR stroke is visible.
      bakeVisibleProjectionPbrMapsIntoCanonicalAtlases(width, height);

      clearExactProjectionStrokes();
      clearGpuPaintMap(session);
      session.texture.needsUpdate = true;
      session.materialTexture.needsUpdate = true;
      applyCurrentPaintMaterial();
      refreshVisibleMaterial();
      if (viewportDisplayModeRef.current === 'texture') {
        drawCurrentTextureView(textureViewCanvasRef.current);
      }
      projectionStrokeCountRef.current = 0;
      const storedPixels = session.context.getImageData(0, 0, width, height).data;
      return true;
    };

    const copyCanvasPixels = (
      sourceCanvas,
      targetWidth = Number(sourceCanvas?.width) || 1,
      targetHeight = Number(sourceCanvas?.height) || 1,
      { smoothingEnabled = false } = {},
    ) => {
      if (!sourceCanvas || typeof document === 'undefined') return null;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Number(targetWidth) || Number(sourceCanvas.width) || 1);
      canvas.height = Math.max(1, Number(targetHeight) || Number(sourceCanvas.height) || 1);
      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!context) return null;
      context.imageSmoothingEnabled = smoothingEnabled === true;
      if ('imageSmoothingQuality' in context) context.imageSmoothingQuality = 'high';
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
      return { canvas, context };
    };

    const bleedBakedAtlasEdgesOnGpu = (
      replica,
      canvases,
      width,
      height,
      paddingPixels = 5,
    ) => {
      if (!replica || !renderer || !Array.isArray(canvases) || !canvases.length) return false;
      const safePadding = Math.max(1, Math.min(64, Math.round(Number(paddingPixels) || 32)));

      const coverageTarget = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      coverageTarget.texture.generateMipmaps = false;
      coverageTarget.texture.colorSpace = THREE.NoColorSpace;

      const targetA = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      const targetB = targetA.clone();
      targetA.texture.generateMipmaps = false;
      targetB.texture.generateMipmaps = false;
      targetA.texture.colorSpace = THREE.NoColorSpace;
      targetB.texture.colorSpace = THREE.NoColorSpace;

      const bakeCamera = new THREE.Camera();
      bakeCamera.position.z = 1;
      const coverageScene = new THREE.Scene();
      const coverageMaterial = new THREE.ShaderMaterial({
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4((uv.x * 2.0) - 1.0, (uv.y * 2.0) - 1.0, 0.0, 1.0);
          }
        `,
        fragmentShader: `
          precision highp float;
          void main() {
            gl_FragColor = vec4(1.0);
          }
        `,
        depthTest: false,
        depthWrite: false,
        transparent: false,
        blending: THREE.NoBlending,
        side: THREE.DoubleSide,
        toneMapped: false,
      });
      const coverageMeshes = [];
      replica.updateMatrixWorld(true);
      replica.traverse((child) => {
        if (!child?.isMesh || !child.geometry?.getAttribute?.('uv')) return;
        if (
          child.userData?.voxemeraProjectionDecal
          || child.userData?.voxemeraSelectionOverlay
          || child.userData?.voxemeraDecalTransformOverlay
          || String(child.name || '').startsWith('Voxemera exact projected')
        ) return;
        const mesh = new THREE.Mesh(child.geometry, coverageMaterial);
        mesh.frustumCulled = false;
        coverageScene.add(mesh);
        coverageMeshes.push(mesh);
      });

      if (!coverageMeshes.length) {
        coverageMaterial.dispose();
        coverageTarget.dispose();
        targetA.dispose();
        targetB.dispose();
        return false;
      }

      const quadGeometry = new THREE.PlaneGeometry(2, 2);
      const quadCamera = new THREE.Camera();
      quadCamera.position.z = 1;
      const quadScene = new THREE.Scene();
      const quadMaterial = new THREE.ShaderMaterial({
        uniforms: {
          sourceMap: { value: null },
          coverageMap: { value: null },
          texelSize: { value: new THREE.Vector2(1 / width, 1 / height) },
          initialise: { value: 1 },
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.0, 1.0);
          }
        `,
        fragmentShader: `
          precision highp float;
          uniform sampler2D sourceMap;
          uniform sampler2D coverageMap;
          uniform vec2 texelSize;
          uniform float initialise;
          varying vec2 vUv;

          void main() {
            vec4 centre = texture2D(sourceMap, vUv);
            float centreCoverage = initialise > 0.5
              ? texture2D(coverageMap, vUv).a
              : centre.a;
            if (centreCoverage > 0.5) {
              gl_FragColor = vec4(centre.rgb, 1.0);
              return;
            }

            vec2 offsets[8];
            offsets[0] = vec2(-1.0,  0.0);
            offsets[1] = vec2( 1.0,  0.0);
            offsets[2] = vec2( 0.0, -1.0);
            offsets[3] = vec2( 0.0,  1.0);
            offsets[4] = vec2(-1.0, -1.0);
            offsets[5] = vec2( 1.0, -1.0);
            offsets[6] = vec2(-1.0,  1.0);
            offsets[7] = vec2( 1.0,  1.0);

            vec4 best = vec4(0.0);
            float found = 0.0;
            for (int index = 0; index < 8; index += 1) {
              vec2 sampleUv = vUv + (offsets[index] * texelSize);
              vec4 sampleValue = texture2D(sourceMap, sampleUv);
              float sampleCoverage = initialise > 0.5
                ? texture2D(coverageMap, sampleUv).a
                : sampleValue.a;
              if (found < 0.5 && sampleCoverage > 0.5) {
                best = sampleValue;
                found = 1.0;
              }
            }
            gl_FragColor = found > 0.5 ? vec4(best.rgb, 1.0) : vec4(centre.rgb, 0.0);
          }
        `,
        depthTest: false,
        depthWrite: false,
        transparent: false,
        blending: THREE.NoBlending,
        toneMapped: false,
      });
      const quad = new THREE.Mesh(quadGeometry, quadMaterial);
      quad.frustumCulled = false;
      quadScene.add(quad);

      const previousTarget = renderer.getRenderTarget();
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();
      const previousToneMapping = renderer.toneMapping;
      const previousAutoClear = renderer.autoClear;
      const previousOutputColorSpace = renderer.outputColorSpace;
      const temporaryTextures = [];

      try {
        renderer.toneMapping = THREE.NoToneMapping;
        renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
        renderer.autoClear = true;
        renderer.setRenderTarget(coverageTarget);
        renderer.setClearColor(0x000000, 0);
        renderer.clear(true, false, false);
        renderer.render(coverageScene, bakeCamera);

        canvases.forEach((canvas, atlasIndex) => {
          if (!canvas) return;
          const inputTexture = new THREE.CanvasTexture(canvas);
          inputTexture.flipY = false;
          inputTexture.colorSpace = THREE.NoColorSpace;
          inputTexture.wrapS = THREE.ClampToEdgeWrapping;
          inputTexture.wrapT = THREE.ClampToEdgeWrapping;
          inputTexture.minFilter = THREE.NearestFilter;
          inputTexture.magFilter = THREE.NearestFilter;
          inputTexture.generateMipmaps = false;
          inputTexture.needsUpdate = true;
          temporaryTextures.push(inputTexture);

          quadMaterial.uniforms.sourceMap.value = inputTexture;
          quadMaterial.uniforms.coverageMap.value = coverageTarget.texture;
          quadMaterial.uniforms.initialise.value = 1;
          renderer.setRenderTarget(targetA);
          renderer.setClearColor(0x000000, 0);
          renderer.clear(true, false, false);
          renderer.render(quadScene, quadCamera);

          let readTarget = targetA;
          let writeTarget = targetB;
          for (let pass = 0; pass < safePadding; pass += 1) {
            quadMaterial.uniforms.sourceMap.value = readTarget.texture;
            quadMaterial.uniforms.initialise.value = 0;
            renderer.setRenderTarget(writeTarget);
            renderer.setClearColor(0x000000, 0);
            renderer.clear(true, false, false);
            renderer.render(quadScene, quadCamera);
            const swap = readTarget;
            readTarget = writeTarget;
            writeTarget = swap;
          }

          const pixels = new Uint8Array(width * height * 4);
          renderer.readRenderTargetPixels(readTarget, 0, 0, width, height, pixels);
          for (let index = 3; index < pixels.length; index += 4) pixels[index] = 255;
          const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
          if (!context) throw new Error(`Could not access baked atlas ${atlasIndex} for seam bleed.`);
          context.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
        });
      } finally {
        renderer.setRenderTarget(previousTarget);
        renderer.setClearColor(previousClearColor, previousClearAlpha);
        renderer.toneMapping = previousToneMapping;
        renderer.outputColorSpace = previousOutputColorSpace;
        renderer.autoClear = previousAutoClear;
        temporaryTextures.forEach((texture) => texture.dispose());
        coverageMeshes.forEach((mesh) => coverageScene.remove(mesh));
        coverageMaterial.dispose();
        quadMaterial.dispose();
        quadGeometry.dispose();
        coverageTarget.dispose();
        targetA.dispose();
        targetB.dispose();
      }

      return true;
    };

    const makeBakedAtlasTexture = (canvas, sourceTexture, colourTexture = false) => {
      const texture = new THREE.CanvasTexture(canvas);
      texture.name = `${sourceTexture?.name || 'Voxemera atlas'} baked export copy`;
      texture.flipY = sourceTexture?.flipY ?? false;
      texture.wrapS = sourceTexture?.wrapS ?? THREE.ClampToEdgeWrapping;
      texture.wrapT = sourceTexture?.wrapT ?? THREE.ClampToEdgeWrapping;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = Math.max(
        Number(sourceTexture?.anisotropy) || 1,
        Math.min(16, Number(renderer?.capabilities?.getMaxAnisotropy?.()) || 1),
      );
      texture.premultiplyAlpha = false;
      texture.unpackAlignment = sourceTexture?.unpackAlignment || 4;
      texture.colorSpace = colourTexture ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      texture.needsUpdate = true;
      texture.userData = {
        ...(sourceTexture?.userData || {}),
        voxemeraBakedExportTexture: true,
      };
      return texture;
    };

    const getBakeCanvasDebugStats = (canvas, label) => {
      if (!canvas || typeof canvas.getContext !== 'function') {
        return { label, available: false };
      }
      const width = Math.max(1, Number(canvas.width) || 1);
      const height = Math.max(1, Number(canvas.height) || 1);
      const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!context) return { label, available: false, width, height };
      const image = context.getImageData(0, 0, width, height).data;
      const channelMin = [255, 255, 255, 255];
      const channelMax = [0, 0, 0, 0];
      const channelSum = [0, 0, 0, 0];
      let transparentPixels = 0;
      let partialAlphaPixels = 0;
      let opaquePixels = 0;
      let nonDefaultPixels = 0;
      const sampleStride = Math.max(1, Math.floor((width * height) / 250000));
      let sampledPixels = 0;

      for (let pixel = 0; pixel < width * height; pixel += sampleStride) {
        const index = pixel * 4;
        sampledPixels += 1;
        for (let channel = 0; channel < 4; channel += 1) {
          const value = image[index + channel];
          channelMin[channel] = Math.min(channelMin[channel], value);
          channelMax[channel] = Math.max(channelMax[channel], value);
          channelSum[channel] += value;
        }
        const alpha = image[index + 3];
        if (alpha === 0) transparentPixels += 1;
        else if (alpha === 255) opaquePixels += 1;
        else partialAlphaPixels += 1;
        if (image[index] !== 0 || image[index + 1] !== 0 || image[index + 2] !== 0 || alpha !== 0) nonDefaultPixels += 1;
      }

      return {
        label,
        available: true,
        width,
        height,
        totalPixels: width * height,
        sampledPixels,
        sampleStride,
        channelMin,
        channelMax,
        channelAverage: channelSum.map((sum) => Number((sum / Math.max(1, sampledPixels)).toFixed(3))),
        transparentPixels,
        partialAlphaPixels,
        opaquePixels,
        nonDefaultPixels,
      };
    };

    const compareBakeCanvases = (leftCanvas, rightCanvas, label) => {
      if (!leftCanvas || !rightCanvas) return { label, available: false };
      const width = Math.min(Number(leftCanvas.width) || 0, Number(rightCanvas.width) || 0);
      const height = Math.min(Number(leftCanvas.height) || 0, Number(rightCanvas.height) || 0);
      if (!width || !height) return { label, available: false, width, height };
      const leftContext = leftCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      const rightContext = rightCanvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!leftContext || !rightContext) return { label, available: false, width, height };
      const left = leftContext.getImageData(0, 0, width, height).data;
      const right = rightContext.getImageData(0, 0, width, height).data;
      const channelAbsoluteDifference = [0, 0, 0, 0];
      const channelMaximumDifference = [0, 0, 0, 0];
      let changedPixels = 0;
      let alphaChangedPixels = 0;
      let rgbChangedPixels = 0;
      const sampleStride = Math.max(1, Math.floor((width * height) / 500000));
      let sampledPixels = 0;

      for (let pixel = 0; pixel < width * height; pixel += sampleStride) {
        const index = pixel * 4;
        sampledPixels += 1;
        let rgbChanged = false;
        let pixelChanged = false;
        for (let channel = 0; channel < 4; channel += 1) {
          const difference = Math.abs(left[index + channel] - right[index + channel]);
          channelAbsoluteDifference[channel] += difference;
          channelMaximumDifference[channel] = Math.max(channelMaximumDifference[channel], difference);
          if (difference > 0) pixelChanged = true;
          if (channel < 3 && difference > 0) rgbChanged = true;
        }
        if (pixelChanged) changedPixels += 1;
        if (rgbChanged) rgbChangedPixels += 1;
        if (left[index + 3] !== right[index + 3]) alphaChangedPixels += 1;
      }

      return {
        label,
        available: true,
        width,
        height,
        sampledPixels,
        sampleStride,
        changedPixels,
        changedPercent: Number(((changedPixels / Math.max(1, sampledPixels)) * 100).toFixed(4)),
        rgbChangedPixels,
        alphaChangedPixels,
        channelMeanAbsoluteDifference: channelAbsoluteDifference.map((sum) => Number((sum / Math.max(1, sampledPixels)).toFixed(4))),
        channelMaximumDifference,
      };
    };

    const describeBakeTexture = (texture) => {
      if (!texture) return null;
      return {
        uuid: texture.uuid,
        name: texture.name,
        type: texture.type,
        imageType: texture.image?.constructor?.name || typeof texture.image,
        imageWidth: Number(texture.image?.width || texture.image?.naturalWidth || texture.image?.videoWidth || 0),
        imageHeight: Number(texture.image?.height || texture.image?.naturalHeight || texture.image?.videoHeight || 0),
        flipY: texture.flipY,
        wrapS: texture.wrapS,
        wrapT: texture.wrapT,
        magFilter: texture.magFilter,
        minFilter: texture.minFilter,
        generateMipmaps: texture.generateMipmaps,
        anisotropy: texture.anisotropy,
        colorSpace: texture.colorSpace,
        matrixAutoUpdate: texture.matrixAutoUpdate,
        offset: texture.offset?.toArray?.(),
        repeat: texture.repeat?.toArray?.(),
        center: texture.center?.toArray?.(),
        rotation: texture.rotation,
      };
    };

    const describeBakeMaterial = (material) => {
      if (!material) return null;
      return {
        uuid: material.uuid,
        name: material.name,
        type: material.type,
        color: material.color?.getHexString?.(),
        roughness: material.roughness,
        metalness: material.metalness,
        bumpScale: material.bumpScale,
        normalScale: material.normalScale?.toArray?.(),
        opacity: material.opacity,
        transparent: material.transparent,
        alphaTest: material.alphaTest,
        side: material.side,
        wireframe: material.wireframe,
        blending: material.blending,
        depthTest: material.depthTest,
        depthWrite: material.depthWrite,
        toneMapped: material.toneMapped,
        hasOnBeforeCompile: typeof material.onBeforeCompile === 'function',
        customProgramCacheKey: typeof material.customProgramCacheKey === 'function'
          ? material.customProgramCacheKey()
          : null,
        map: describeBakeTexture(material.map),
        roughnessMap: describeBakeTexture(material.roughnessMap),
        metalnessMap: describeBakeTexture(material.metalnessMap),
        bumpMap: describeBakeTexture(material.bumpMap),
        normalMap: describeBakeTexture(material.normalMap),
        userDataKeys: Object.keys(material.userData || {}),
      };
    };

    const logBakeReplicaTree = (replica, label) => {
      void 0;
      const rows = [];
      replica?.traverse?.((child) => {
        if (!child?.isMesh) return;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        rows.push({
          name: child.name,
          uuid: child.uuid,
          visible: child.visible,
          renderOrder: child.renderOrder,
          projection: Boolean(child.userData?.voxemeraProjectionDecal),
          selectionOverlay: Boolean(child.userData?.voxemeraSelectionOverlay),
          geometryUuid: child.geometry?.uuid,
          vertexCount: child.geometry?.getAttribute?.('position')?.count || 0,
          uvCount: child.geometry?.getAttribute?.('uv')?.count || 0,
          materialCount: materials.length,
          materialTypes: materials.map((material) => material?.type).join(', '),
        });
      });
      void 0;
      replica?.traverse?.((child) => {
        if (!child?.isMesh) return;
        void 0;
        void 0;
        void 0;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material, index) => {
          void 0;
          void 0;
          void 0;
          void 0;
        });
        void 0;
      });
      void 0;
    };


    const captureBakeReplicaScreenPixels = (replica, label, { outputMode = 'beauty' } = {}) => {
      const domWidth = Math.max(1, Number(renderer?.domElement?.width) || 1);
      const domHeight = Math.max(1, Number(renderer?.domElement?.height) || 1);
      const maxDimension = 1024;
      const scale = Math.min(1, maxDimension / Math.max(domWidth, domHeight));
      const width = Math.max(1, Math.round(domWidth * scale));
      const height = Math.max(1, Math.round(domHeight * scale));
      const target = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.generateMipmaps = false;
      target.texture.colorSpace = outputMode === 'normal'
        ? THREE.NoColorSpace
        : THREE.SRGBColorSpace;

      const diagnosticScene = new THREE.Scene();
      diagnosticScene.background = scene.background;
      diagnosticScene.environment = scene.environment;
      diagnosticScene.fog = scene.fog;
      scene.traverse((sourceObject) => {
        if (!sourceObject?.isLight) return;
        const clonedLight = sourceObject.clone();
        clonedLight.position.copy(sourceObject.position);
        clonedLight.quaternion.copy(sourceObject.quaternion);
        clonedLight.scale.copy(sourceObject.scale);
        diagnosticScene.add(clonedLight);
      });

      const diagnosticCamera = camera.clone();
      diagnosticCamera.aspect = width / Math.max(1, height);
      diagnosticCamera.updateProjectionMatrix();
      diagnosticCamera.updateMatrixWorld(true);

      const previousParent = replica.parent || null;
      const previousIndex = previousParent ? previousParent.children.indexOf(replica) : -1;
      const previousTarget = renderer.getRenderTarget();
      const previousAutoClear = renderer.autoClear;
      const previousClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const previousClearAlpha = renderer.getClearAlpha();
      const previousToneMapping = renderer.toneMapping;
      const previousOutputColorSpace = renderer.outputColorSpace;
      const previousXrEnabled = renderer.xr.enabled;
      const pixels = new Uint8Array(width * height * 4);
      const diagnosticMaterialRecords = [];

      if (outputMode === 'normal') {
      replica.traverse((child) => {
          if (!child?.isMesh) return;
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material) => {
            if (!material) return;
            const originalOnBeforeCompile = material.onBeforeCompile;
            const originalCustomProgramCacheKey = material.customProgramCacheKey;
            diagnosticMaterialRecords.push({
              material,
              originalOnBeforeCompile,
              originalCustomProgramCacheKey,
            });
            material.onBeforeCompile = (shader, rendererInstance) => {
              if (typeof originalOnBeforeCompile === 'function') {
                originalOnBeforeCompile.call(material, shader, rendererInstance);
              }
              const normalOutput = 'gl_FragColor = vec4((normalize(normal) * 0.5) + 0.5, 1.0);';
              if (shader.fragmentShader.includes('#include <opaque_fragment>')) {
                shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', normalOutput);
              } else {
                void 0;
              }
            };
            material.customProgramCacheKey = () => `${
              typeof originalCustomProgramCacheKey === 'function'
                ? originalCustomProgramCacheKey.call(material)
                : ''
            }|voxemera-normal-audit-v1`;
            material.needsUpdate = true;
          });
        });
      }

      try {
        diagnosticScene.add(replica);
        replica.updateMatrixWorld(true);
        renderer.xr.enabled = false;
        renderer.autoClear = true;
        if (outputMode === 'normal') {
          renderer.toneMapping = THREE.NoToneMapping;
          renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
        }
        renderer.setRenderTarget(target);
        renderer.setClearColor(scene.background?.isColor ? scene.background : 0x000000, 0);
        renderer.clear(true, true, true);
        renderer.render(diagnosticScene, diagnosticCamera);
        renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      } finally {
        diagnosticScene.remove(replica);
        if (previousParent) {
          if (previousIndex >= 0 && previousIndex < previousParent.children.length) {
            previousParent.children.splice(previousIndex, 0, replica);
            replica.parent = previousParent;
          } else {
            previousParent.add(replica);
          }
        }
        diagnosticMaterialRecords.forEach((record) => {
          record.material.onBeforeCompile = record.originalOnBeforeCompile;
          record.material.customProgramCacheKey = record.originalCustomProgramCacheKey;
          record.material.needsUpdate = true;
        });
        renderer.setRenderTarget(previousTarget);
        renderer.autoClear = previousAutoClear;
        renderer.setClearColor(previousClearColor, previousClearAlpha);
        renderer.toneMapping = previousToneMapping;
        renderer.outputColorSpace = previousOutputColorSpace;
        renderer.xr.enabled = previousXrEnabled;
        target.dispose();
      }

      let nonTransparent = 0;
      let nonBlack = 0;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      for (let pixel = 0; pixel < width * height; pixel += 1) {
        const index = pixel * 4;
        if (pixels[index + 3] > 0) nonTransparent += 1;
        if (pixels[index] || pixels[index + 1] || pixels[index + 2]) {
          nonBlack += 1;
          const x = pixel % width;
          const y = Math.floor(pixel / width);
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
      void 0;
      return { label, width, height, pixels };
    };

    const compareBakeScreenCaptures = (beforeCapture, afterCapture, label) => {
      if (!beforeCapture || !afterCapture) return null;
      const width = Math.min(beforeCapture.width, afterCapture.width);
      const height = Math.min(beforeCapture.height, afterCapture.height);
      const before = beforeCapture.pixels;
      const after = afterCapture.pixels;
      let changedPixels = 0;
      let rgbChangedPixels = 0;
      let alphaChangedPixels = 0;
      let exactPixels = 0;
      let firstChangedPixel = -1;
      let lastChangedPixel = -1;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      const sumDifference = [0, 0, 0, 0];
      const maxDifference = [0, 0, 0, 0];
      const signedDifference = [0, 0, 0, 0];
      for (let pixel = 0; pixel < width * height; pixel += 1) {
        const index = pixel * 4;
        let pixelChanged = false;
        let rgbChanged = false;
        for (let channel = 0; channel < 4; channel += 1) {
          const signed = after[index + channel] - before[index + channel];
          const absolute = Math.abs(signed);
          sumDifference[channel] += absolute;
          signedDifference[channel] += signed;
          maxDifference[channel] = Math.max(maxDifference[channel], absolute);
          if (absolute) {
            pixelChanged = true;
            if (channel < 3) rgbChanged = true;
          }
        }
        if (!pixelChanged) {
          exactPixels += 1;
          continue;
        }
        changedPixels += 1;
        if (rgbChanged) rgbChangedPixels += 1;
        if (before[index + 3] !== after[index + 3]) alphaChangedPixels += 1;
        if (firstChangedPixel < 0) firstChangedPixel = pixel;
        lastChangedPixel = pixel;
        const x = pixel % width;
        const y = Math.floor(pixel / width);
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
      const total = width * height;
      const firstIndex = firstChangedPixel >= 0 ? firstChangedPixel * 4 : -1;
      const result = {
        label,
        width,
        height,
        total,
        exactPixels,
        changedPixels,
        changedPercent: Number(((changedPixels / Math.max(1, total)) * 100).toFixed(6)),
        rgbChangedPixels,
        alphaChangedPixels,
        meanAbsoluteDifference: sumDifference.map((value) => Number((value / Math.max(1, total)).toFixed(6))),
        meanSignedDifference: signedDifference.map((value) => Number((value / Math.max(1, total)).toFixed(6))),
        maxDifference,
        firstChangedPixel,
        firstChangedXY: firstChangedPixel >= 0 ? [firstChangedPixel % width, Math.floor(firstChangedPixel / width)] : null,
        firstBeforeRgba: firstIndex >= 0 ? Array.from(before.slice(firstIndex, firstIndex + 4)) : null,
        firstAfterRgba: firstIndex >= 0 ? Array.from(after.slice(firstIndex, firstIndex + 4)) : null,
        lastChangedPixel,
        changedBoundingBox: maxX >= 0 ? [minX, minY, maxX, maxY] : null,
        identical: changedPixels === 0,
      };
      void 0;
      return result;
    };


    const compareNormalScreenCaptures = (beforeCapture, afterCapture, label) => {
      if (!beforeCapture || !afterCapture) return null;
      const width = Math.min(beforeCapture.width, afterCapture.width);
      const height = Math.min(beforeCapture.height, afterCapture.height);
      const before = beforeCapture.pixels;
      const after = afterCapture.pixels;
      let comparedPixels = 0;
      let changedPixels = 0;
      let overOneDegree = 0;
      let overFiveDegrees = 0;
      let overFifteenDegrees = 0;
      let angleSum = 0;
      let maximumAngle = 0;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      const worst = [];
      const decodeNormal = (pixels, index) => {
        const vector = new THREE.Vector3(
          (pixels[index] / 255) * 2 - 1,
          (pixels[index + 1] / 255) * 2 - 1,
          (pixels[index + 2] / 255) * 2 - 1,
        );
        return vector.lengthSq() > 0.000001 ? vector.normalize() : new THREE.Vector3(0, 0, 1);
      };

      for (let pixel = 0; pixel < width * height; pixel += 1) {
        const index = pixel * 4;
        if (before[index + 3] === 0 && after[index + 3] === 0) continue;
        const beforeNormal = decodeNormal(before, index);
        const afterNormal = decodeNormal(after, index);
        const angle = THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(beforeNormal.dot(afterNormal), -1, 1)));
        comparedPixels += 1;
        angleSum += angle;
        maximumAngle = Math.max(maximumAngle, angle);
        if (angle > 0.25) {
          changedPixels += 1;
          const x = pixel % width;
          const y = Math.floor(pixel / width);
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
        if (angle > 1) overOneDegree += 1;
        if (angle > 5) overFiveDegrees += 1;
        if (angle > 15) overFifteenDegrees += 1;
        if (angle > 1) {
          worst.push({
            x: pixel % width,
            y: Math.floor(pixel / width),
            angleDegrees: Number(angle.toFixed(4)),
            preBakeRgb: Array.from(before.slice(index, index + 3)),
            postBakeRgb: Array.from(after.slice(index, index + 3)),
            preBakeNormal: beforeNormal.toArray().map((value) => Number(value.toFixed(5))),
            postBakeNormal: afterNormal.toArray().map((value) => Number(value.toFixed(5))),
          });
          worst.sort((left, right) => right.angleDegrees - left.angleDegrees);
          if (worst.length > 25) worst.length = 25;
        }
      }

      const result = {
        label,
        width,
        height,
        comparedPixels,
        changedPixels,
        changedPercent: Number(((changedPixels / Math.max(1, comparedPixels)) * 100).toFixed(6)),
        meanAngleDegrees: Number((angleSum / Math.max(1, comparedPixels)).toFixed(6)),
        maximumAngleDegrees: Number(maximumAngle.toFixed(6)),
        overOneDegree,
        overFiveDegrees,
        overFifteenDegrees,
        changedBoundingBox: maxX >= 0 ? [minX, minY, maxX, maxY] : null,
        worstPixels: worst,
        identicalWithinQuarterDegree: changedPixels === 0,
      };
      void 0;
      void 0;
      void 0;
      void 0;
      return result;
    };

    const logProjectionNormalState = () => {
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const visibleCount = Math.max(0, Math.min(strokes.length, Number(projectionStrokeCountRef.current) || 0));
      const { layers: currentLayers } = getCurrentLayerState();
      const layerById = new Map(currentLayers.map((layer) => [String(layer?.id || ''), layer]));
      const rows = [];
      strokes.slice(0, visibleCount).forEach((stroke, strokeIndex) => {
        const layer = layerById.get(String(stroke?.layerId || '')) || null;
        (stroke?.meshes || []).forEach((mesh, meshIndex) => {
          const uniforms = mesh?.material?.userData?.voxemeraProjectionUniforms || {};
          rows.push({
            strokeIndex,
            meshIndex,
            layerId: String(stroke?.layerId || ''),
            layerName: String(layer?.name || ''),
            layerVisible: layer?.visible !== false,
            layerOpacity: Number(layer?.opacity ?? 1),
            isPbr: stroke?.isPbr === true,
            pbrSetKey: String(stroke?.brushConfig?.pbrSetKey || ''),
            normalSourceLayerId: String(layer?.normalSourceLayerId || ''),
            normalSelfPercent: Number(layer?.normalSelfPercent ?? 100),
            shaderHasOwnNormal: Number(uniforms?.pbrHasNormal?.value || 0),
            shaderHasSharedNormal: Number(uniforms?.normalLayerHasMap?.value || 0),
            shaderSelfMix: Number(uniforms?.normalLayerMix?.value ?? 1),
            ownScale: Number(uniforms?.pbrScale?.value ?? 1),
            ownAngleRadians: Number(uniforms?.pbrAngle?.value ?? 0),
            sharedScale: Number(uniforms?.normalLayerScale?.value ?? 1),
            sharedAngleRadians: Number(uniforms?.normalLayerAngle?.value ?? 0),
            heightScale: Number(uniforms?.heightScale?.value ?? 0),
            meshVisible: mesh?.visible !== false,
            renderOrder: Number(mesh?.renderOrder || 0),
          });
        });
      });
      void 0;
      void 0;
      void 0;
      void 0;
      return rows;
    };

    const bakeReplicaAppearance = (replica) => {
      let bakedMeshCount = 0;
      const session = activePaintSession;
      const runtime = runtimeRef.current;
      if (!replica || !session?.canvas || !session?.context || !session?.materialCanvas || !session?.normalCanvas || !runtime) {
        throw new Error('The current model does not have a complete UV paint session to bake.');
      }

      // Do not run the old full-scene equality/debug captures during a normal
      // export. They render and read back the model several extra times and can
      // dominate the actual bake on detailed models.
      refreshProjectionLayerPresentation();

      const width = Math.max(
        Number(session.canvas.width) || 0,
        EXPORT_BAKE_TEST_MIN_RESOLUTION,
      );
      const height = Math.max(
        Number(session.canvas.height) || 0,
        EXPORT_BAKE_TEST_MIN_RESOLUTION,
      );
      const rendererTextureLimit = Number(renderer?.capabilities?.maxTextureSize || 0);
      const colourCopy = copyCanvasPixels(session.canvas, width, height, { smoothingEnabled: true });
      const materialCopy = copyCanvasPixels(session.materialCanvas, width, height, { smoothingEnabled: true });
      const normalCopy = copyCanvasPixels(session.normalCanvas, width, height, { smoothingEnabled: true });
      if (!colourCopy?.context || !materialCopy?.context || !normalCopy?.context) {
        throw new Error('Voxemera could not create the isolated export atlases.');
      }

      const visibleCount = Math.max(
        0,
        Math.min(
          Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes.length : 0,
          Number(projectionStrokeCountRef.current) || 0,
        ),
      );

      const orderedBakeStrokes = getOrderedVisibleProjectionStrokes();
      const visibleDecalCount = orderedBakeStrokes.filter((stroke) => stroke?.isDecal === true || stroke?.isTextDecal === true).length;

      // Render the live projected layers through the same projector/depth and
      // triplanar shaders used by Voxemera, but into UV space. Reconstructing
      // masks triangle-by-triangle on a 2D canvas painted rear-facing triangles
      // too and re-applied PBR scale/rotation incorrectly. These shader bakes
      // preserve front-surface depth rejection, incidence masking, layer order,
      // world-space PBR scale and PBR rotation exactly.
      // Rebuild presentation before each pass so all three atlases use the
      // current bottom-to-top layer order, layer opacity, and shared-normal
      // source/mix/scale/rotation values. No channel is allowed to use stale
      // projection uniforms from an earlier layer edit.
      refreshProjectionLayerPresentation();
      const projectedColour = renderVisibleProjectionPbrOverlayToCanvas(width, height, 'colour', { gpuComposite: true });
      refreshProjectionLayerPresentation();
      const projectedMaterial = renderVisibleProjectionPbrOverlayToCanvas(width, height, 'material', { gpuComposite: true });
      refreshProjectionLayerPresentation();
      const projectedNormal = renderVisibleProjectionPbrOverlayToCanvas(width, height, 'normal', { gpuComposite: true });


      if (projectedColour) {
        colourCopy.context.save();
        colourCopy.context.globalCompositeOperation = 'source-over';
        colourCopy.context.globalAlpha = 1;
        colourCopy.context.drawImage(projectedColour, 0, 0, width, height);
        colourCopy.context.restore();
      }
      if (projectedMaterial) {
        materialCopy.context.save();
        materialCopy.context.globalCompositeOperation = 'source-over';
        materialCopy.context.globalAlpha = 1;
        materialCopy.context.drawImage(projectedMaterial, 0, 0, width, height);
        materialCopy.context.restore();
      }
      if (projectedNormal) {
        normalCopy.context.save();
        normalCopy.context.globalCompositeOperation = 'source-over';
        normalCopy.context.globalAlpha = 1;
        normalCopy.context.drawImage(projectedNormal, 0, 0, width, height);
        normalCopy.context.restore();
      }

      bleedBakedAtlasEdgesOnGpu(
        replica,
        [colourCopy.canvas, materialCopy.canvas, normalCopy.canvas],
        width,
        height,
        32,
      );

      void 0;
      void 0;
      void 0;
      void 0;
      void 0;
      void 0;
      void 0;
      void 0;
      void 0;

      const colourTexture = makeBakedAtlasTexture(colourCopy.canvas, session.texture, true);
      const materialTexture = makeBakedAtlasTexture(materialCopy.canvas, session.materialTexture, false);
      const normalTexture = makeBakedAtlasTexture(normalCopy.canvas, session.normalTexture, false);


      const removable = [];
      const bakedBaseMaterialRecords = [];
      const cloneBakedBaseMaterial = (material, childName = 'Mesh') => {
        // Export uses standard PBR slots so GLTF/GLB can carry the baked result.
        // The colour atlas is already corrected from linear bake readback into
        // sRGB, while packed G/B remain linear roughness/metalness channels.
        const base = new THREE.MeshStandardMaterial({
          map: colourTexture,
          color: 0xffffff,
          roughness: 1,
          roughnessMap: materialTexture,
          metalness: 1,
          metalnessMap: materialTexture,
          normalMap: normalTexture,
          normalScale: new THREE.Vector2(1, 1),
          side: material?.side ?? THREE.DoubleSide,
          transparent: false,
          opacity: 1,
          alphaTest: 0,
          wireframe: false,
          toneMapped: material?.toneMapped !== false,
        });
        base.name = `${childName} baked export material`;
        base.depthTest = material?.depthTest !== false;
        base.depthWrite = material?.depthWrite !== false;
        base.blending = THREE.NoBlending;
        base.userData = {
          voxemeraBakedExportMaterial: true,
          voxemeraFullyExportablePbr: true,
        };
        base.needsUpdate = true;
        return base;
      };

      replica.traverse((child) => {
        if (child?.userData?.voxemeraProjectionDecal || child?.userData?.voxemeraSelectionOverlay || child?.userData?.voxemeraDecalTransformOverlay) {
          removable.push(child);
          return;
        }
        if (!child?.isMesh) return;

        bakedMeshCount += 1;
        const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
        const bakedMaterials = sourceMaterials.map((material) => cloneBakedBaseMaterial(material, child.name || 'Mesh'));
        bakedBaseMaterialRecords.push({ child, materials: bakedMaterials });
        child.material = Array.isArray(child.material) ? bakedMaterials : bakedMaterials[0];
        delete child.userData.voxemeraSharedSourceMaterial;
        child.castShadow = true;
        child.receiveShadow = true;
      });


      // The temporary live projection meshes have now been represented by the
      // baked atlases. Hide them before the equality capture, then remove them.
      removable.forEach((child) => {
        child.visible = false;
      });

      replica.updateMatrixWorld(true);

      removable.forEach((child, removableIndex) => {
        child.parent?.remove(child);
        child.geometry?.dispose?.();
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => material?.dispose?.());
      });

      replica.updateMatrixWorld(true);

      replica.userData = {
        ...(replica.userData || {}),
        voxemeraExportBakeComplete: true,
        voxemeraExportBakeWidth: width,
        voxemeraExportBakeHeight: height,
      };
      replica.updateMatrixWorld(true);
      logBakeReplicaTree(replica, 'Replica after bake');
      return { width, height };
    };
    bakeReplicaAppearanceRef.current = bakeReplicaAppearance;

    // Export always starts from an isolated deep copy. Every visible layer is
    // then flattened onto independent UV atlases before the popup is shown.
    // Nothing in the live Voxemera model, materials or paint session is changed.
    const exportModel = async (requestedFormat) => {
      const format = String(requestedFormat || 'glb').trim().toLowerCase();
      const sourceModel = currentModelRef.current;
      if (!sourceModel) {
        throw new Error('There is no loaded model to export.');
      }

      showExportOverlay(`Exporting baked ${format.toUpperCase()}…`, 'Preparing an isolated export copy…');
      await waitForNextPaint();

      const replica = createExactExportReplica(sourceModel);
      try {
        const bakeReplica = bakeReplicaAppearanceRef.current;
        if (typeof bakeReplica !== 'function') {
          throw new Error('The final export bake is not ready yet.');
        }

        showExportOverlay(`Exporting baked ${format.toUpperCase()}…`, 'Baking visible paint and PBR textures…');
        await waitForNextPaint();
        bakeReplica(replica);
        replica.updateMatrixWorld(true);

        showExportOverlay(`Exporting baked ${format.toUpperCase()}…`, `Writing the final ${format.toUpperCase()} file…`);
        await waitForNextPaint();
        await serializeAndDownloadBakedReplica(
          replica,
          format,
          sourceModel.name || modelName || '3D model',
        );
        return true;
      } catch (error) {
        throw error;
      } finally {
        disposeObject(replica);
        hideExportOverlay();
      }
    };

    const pointInsidePolygon2d = (point, polygon) => {
      if (!point || !Array.isArray(polygon) || polygon.length < 3) return false;
      let inside = false;
      let previous = polygon[polygon.length - 1];
      for (let index = 0; index < polygon.length; index += 1) {
        const current = polygon[index];
        const crosses = ((current.y > point.y) !== (previous.y > point.y));
        const denominator = previous.y - current.y;
        const safeDenominator = Math.abs(denominator) < 1e-9
          ? (denominator < 0 ? -1e-9 : 1e-9)
          : denominator;
        const crossingX = ((previous.x - current.x) * (point.y - current.y) / safeDenominator) + current.x;
        if (crosses && point.x < crossingX) inside = !inside;
        previous = current;
      }
      return inside;
    };

    const sampleCanvasAlphaAtUv = (canvas, uvPoint) => {
      if (!canvas || !uvPoint) return 0;
      const width = Math.max(1, canvas.width || 1);
      const height = Math.max(1, canvas.height || 1);
      const x = Math.max(0, Math.min(width - 1, Math.floor(uvPoint.x * width)));
      const yBottom = Math.max(0, Math.min(height - 1, Math.floor(uvPoint.y * height)));
      const context = canvas.getContext?.('2d', { willReadFrequently: true });
      if (!context) return 0;
      const directAlpha = context.getImageData(x, yBottom, 1, 1).data[3];
      const flippedY = (height - 1) - yBottom;
      if (flippedY === yBottom) return directAlpha;
      const flippedAlpha = context.getImageData(x, flippedY, 1, 1).data[3];
      return Math.max(directAlpha, flippedAlpha);
    };

    const getTopmostVisibleProjectionStrokeAtHit = (hit) => {
      if (!hit?.point || !hit?.object?.uuid) return null;
      const runtime = runtimeRef.current;
      const strokes = Array.isArray(runtime?.projectionStrokes) ? runtime.projectionStrokes : [];
      const visibleCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
      const sourceMeshUuid = String(hit.object.uuid || '');

      for (let strokeIndex = visibleCount - 1; strokeIndex >= 0; strokeIndex -= 1) {
        const stroke = strokes[strokeIndex];
        if (!stroke || stroke.isEraser || stroke.isPbr || stroke.isImageDecal) continue;
        const ownsHitMesh = (stroke.meshes || []).some((mesh) => (
          String(mesh?.userData?.voxemeraPaintRegion?.sourceMeshUuid || mesh?.parent?.uuid || '') === sourceMeshUuid
          || String(mesh?.userData?.voxemeraSourceMeshUuid || '') === sourceMeshUuid
          || mesh?.parent === hit.object.parent
        ));
        if (!ownsHitMesh) continue;

        if (stroke.kind === 'uvFloodFill') {
          const clipMesh = (stroke.meshes || []).find((mesh) => mesh?.material?.userData?.voxemeraExactSurfaceSelectionClip);
          const clipData = clipMesh?.material?.userData?.voxemeraExactSurfaceSelectionClipData;
          if (clipData?.viewProjection?.isMatrix4 && Array.isArray(clipData.polygon)) {
            const projected = hit.point.clone().applyMatrix4(clipData.viewProjection);
            if (!pointInsidePolygon2d({ x: projected.x, y: projected.y }, clipData.polygon)) continue;
          }
          if (!hit.uv || sampleCanvasAlphaAtUv(stroke.sourceCanvas, hit.uv) <= 8) continue;
          return { stroke, strokeIndex, coverageType: 'uv-flood-fill' };
        }

        const projectionCamera = deserializeProjectionCamera(stroke.projectionCameraData);
        const projectionRect = stroke.projectionRect;
        if (!projectionCamera || !projectionRect || !stroke.sourceCanvas) continue;
        projectionCamera.updateMatrixWorld(true);
        projectionCamera.updateProjectionMatrix();
        const projected = hit.point.clone().project(projectionCamera);
        if (projected.z < -1 || projected.z > 1) continue;
        const projectorUv = new THREE.Vector2((projected.x * 0.5) + 0.5, (projected.y * 0.5) + 0.5);
        const localUv = new THREE.Vector2(
          (projectorUv.x - projectionRect.x) / Math.max(1e-9, projectionRect.width),
          (projectorUv.y - projectionRect.y) / Math.max(1e-9, projectionRect.height),
        );
        if (localUv.x <= 0 || localUv.x >= 1 || localUv.y <= 0 || localUv.y >= 1) continue;
        if (sampleCanvasAlphaAtUv(stroke.sourceCanvas, localUv) <= 8) continue;
        return { stroke, strokeIndex, coverageType: 'projected-stroke' };
      }
      return null;
    };

    const recolourProjectionStroke = (strokeMatch, fillHex) => {
      const stroke = strokeMatch?.stroke;
      if (!stroke) return false;
      const colour = new THREE.Color(fillHex || '#000000');
      stroke.brushConfig = {
        ...(stroke.brushConfig || {}),
        color: fillHex,
        rgba: fillHex,
        strokeColor: fillHex,
      };
      if (stroke.kind === 'uvFloodFill') {
        stroke.solidFillHex = fillHex;
        const canvas = stroke.sourceCanvas;
        const context = canvas?.getContext?.('2d', { willReadFrequently: true });
        if (canvas && context) {
          const image = context.getImageData(0, 0, canvas.width, canvas.height);
          const red = Math.round(colour.r * 255);
          const green = Math.round(colour.g * 255);
          const blue = Math.round(colour.b * 255);
          for (let offset = 0; offset < image.data.length; offset += 4) {
            if (image.data[offset + 3] <= 0) continue;
            image.data[offset] = red;
            image.data[offset + 1] = green;
            image.data[offset + 2] = blue;
          }
          context.putImageData(image, 0, 0);
          if (stroke.strokeTexture) stroke.strokeTexture.needsUpdate = true;
        }
      }
      (stroke.meshes || []).forEach((mesh) => {
        const material = mesh?.material;
        const uniforms = material?.userData?.voxemeraProjectionUniforms;
        if (uniforms?.penColour?.value?.set) uniforms.penColour.value.set(fillHex);
        if (material?.color?.set && stroke.kind === 'uvFloodFill') material.color.set('#ffffff');
        if (material) material.needsUpdate = true;
      });
      rebuildGpuPaintMap();
      refreshProjectionLayerPresentation();
      updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
      scheduleProjectionPaintSave();
      return true;
    };

    const createConnectedColourMask = (hit, fillHex) => {
      if (!hit?.uv || !hit.object?.isMesh || !Number.isInteger(hit.faceIndex)) return null;
      const hasSurfaceSelection = Boolean(
        activeSelection
        && (activeSelection.shape === 'surface' || activeSelection.shape === FACE_SELECTION_SHAPE)
        && activeSelection.selectedFaces,
      );

      // An ordinary 3D bucket must sample the canonical atlas underneath live
      // projection fills. Sampling the composed GPU appearance is incorrect on
      // models with overlapping UVs: a small face-restricted red projection can
      // make an unrelated visibly white face report a red UV texel. Selected
      // fills still use the composed source because their saved screen-space
      // polygon is the authoritative mask.
      const composedSource = hasSurfaceSelection ? readCurrentUvColourPixels() : null;
      const session = activePaintSession;
      const canonicalImage = !hasSurfaceSelection && session?.context && session?.canvas
        ? session.context.getImageData(0, 0, session.canvas.width, session.canvas.height)
        : null;
      const source = composedSource || (canonicalImage ? {
        width: session.canvas.width,
        height: session.canvas.height,
        pixels: canonicalImage.data,
        coverage: null,
      } : null);
      if (!source) return null;

      const { width, height, pixels, coverage } = source;
      const geometry = hit.object.geometry;
      const position = geometry?.getAttribute?.('position');
      const uv = geometry?.getAttribute?.('uv');
      const index = geometry?.index;
      const topology = getGeometryTopology(geometry);
      if (!position || !uv || !topology) return null;

      const atlasPoint = uvToCanonicalAtlasPoint(hit.uv, { width, height });
      const startX = Math.max(0, Math.min(width - 1, Math.floor(atlasPoint.x)));
      const startY = Math.max(0, Math.min(height - 1, Math.floor(atlasPoint.y)));
      const startPixelIndex = ((startY * width) + startX) * 4;
      const startFlatPixel = (startY * width) + startX;
      const target = {
        red: pixels[startPixelIndex],
        green: pixels[startPixelIndex + 1],
        blue: pixels[startPixelIndex + 2],
        alpha: pixels[startPixelIndex + 3],
        painted: Boolean(coverage?.[startFlatPixel] > 8),
      };
      const tolerance = 4;

      const faceVertexIndex = (faceIndex, corner) => (
        index ? index.getX((faceIndex * 3) + corner) : (faceIndex * 3) + corner
      );

      // Bucket fills are ultimately flattened into the canonical UV atlas. The
      // overlay geometry is selection-restricted, but flattening only sees this
      // texture. Therefore the flood mask itself must also be clipped to the
      // active 3D selection or a uniform-colour flood can be baked elsewhere on
      // the model after the correctly positioned overlay is flattened.
      let selectionAllowedPixels = null;
      let selectionAllowedPixelCount = 0;
      let selectionAllowedFaceCount = 0;
      if (activeSelection?.selectedFaces) {
        const selectedFaces = activeSelection.selectedFaces.get(hit.object.uuid) || new Set();
        const boundaryFaces = activeSelection.boundaryFaces?.get(hit.object.uuid) || new Set();
        const triangleCount = topology.triangleCount;
        const allowedFaces = activeSelection.inverted
          ? new Set(Array.from({ length: triangleCount }, (_, faceIndex) => faceIndex)
            .filter((faceIndex) => !selectedFaces.has(faceIndex)))
          : new Set([...selectedFaces, ...boundaryFaces]);

        if (allowedFaces.size > 0) {
          const selectionCanvas = document.createElement('canvas');
          selectionCanvas.width = width;
          selectionCanvas.height = height;
          const selectionContext = selectionCanvas.getContext('2d', {
            alpha: true,
            willReadFrequently: true,
          });
          selectionContext.clearRect(0, 0, width, height);
          selectionContext.fillStyle = '#ffffff';
          selectionContext.beginPath();

          allowedFaces.forEach((faceIndex) => {
            if (!Number.isInteger(faceIndex) || faceIndex < 0 || faceIndex >= triangleCount) return;
            const a = faceVertexIndex(faceIndex, 0);
            const b = faceVertexIndex(faceIndex, 1);
            const c = faceVertexIndex(faceIndex, 2);
            if (a >= uv.count || b >= uv.count || c >= uv.count) return;
            selectionContext.moveTo(uv.getX(a) * width, uv.getY(a) * height);
            selectionContext.lineTo(uv.getX(b) * width, uv.getY(b) * height);
            selectionContext.lineTo(uv.getX(c) * width, uv.getY(c) * height);
            selectionContext.closePath();
            selectionAllowedFaceCount += 1;
          });
          selectionContext.fill();

          const selectionImage = selectionContext.getImageData(0, 0, width, height);
          selectionAllowedPixels = new Uint8Array(width * height);
          for (let pixel = 0; pixel < selectionAllowedPixels.length; pixel += 1) {
            if (selectionImage.data[(pixel * 4) + 3] <= 0) continue;
            selectionAllowedPixels[pixel] = 1;
            selectionAllowedPixelCount += 1;
          }
        }
      }

      const visited = new Uint8Array(width * height);
      const filled = new Uint8Array(width * height);
      const stack = new Int32Array(width * height);
      let stackLength = 0;

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskContext = maskCanvas.getContext('2d', { alpha: true });
      const maskImage = maskContext.createImageData(width, height);
      const expandFillMaskByFourPixels = () => {
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = width;
        sourceCanvas.height = height;
        const sourceContext = sourceCanvas.getContext('2d', { alpha: true });
        sourceContext.drawImage(maskCanvas, 0, 0);

        maskContext.save();
        maskContext.globalCompositeOperation = 'source-over';
        for (let offsetY = -4; offsetY <= 4; offsetY += 1) {
          for (let offsetX = -4; offsetX <= 4; offsetX += 1) {
            if ((offsetX * offsetX) + (offsetY * offsetY) > 16) continue;
            maskContext.drawImage(sourceCanvas, offsetX, offsetY);
          }
        }
        maskContext.restore();
      };
      const fill = new THREE.Color(fillHex || '#000000');
      const fillR = Math.round(fill.r * 255);
      const fillG = Math.round(fill.g * 255);
      const fillB = Math.round(fill.b * 255);
      let filledPixels = 0;

      const matchesTargetPixel = (pixel) => {
        if (pixel < 0 || pixel >= width * height) return false;
        if (selectionAllowedPixels && !selectionAllowedPixels[pixel]) return false;
        const pixelIndex = pixel * 4;
        const candidatePainted = Boolean(coverage?.[pixel] > 8);
        return (
          (!hasSurfaceSelection || candidatePainted === target.painted)
          && Math.abs(pixels[pixelIndex] - target.red) <= tolerance
          && Math.abs(pixels[pixelIndex + 1] - target.green) <= tolerance
          && Math.abs(pixels[pixelIndex + 2] - target.blue) <= tolerance
          && Math.abs(pixels[pixelIndex + 3] - target.alpha) <= tolerance
        );
      };

      // A 3D bucket fill is connected through the mesh surface, not through
      // neighbouring UV texels. Without a surface selection, every atlas texel
      // currently showing the clicked colour is part of the same fill target,
      // including disconnected UV islands. Building the mask globally prevents
      // white gaps at UV seams and also respects live projection colours because
      // readCurrentUvColourPixels() returns the current composed appearance.
      if (!hasSurfaceSelection) {
        let filledMinX = width;
        let filledMinY = height;
        let filledMaxX = -1;
        let filledMaxY = -1;
        const filledPixelIndices = [];

        for (let pixel = 0; pixel < width * height; pixel += 1) {
          if (!matchesTargetPixel(pixel)) continue;
          const pixelIndex = pixel * 4;
          const x = pixel % width;
          const y = Math.floor(pixel / width);
          filled[pixel] = 1;
          maskImage.data[pixelIndex] = fillR;
          maskImage.data[pixelIndex + 1] = fillG;
          maskImage.data[pixelIndex + 2] = fillB;
          maskImage.data[pixelIndex + 3] = 255;
          filledPixels += 1;
          filledMinX = Math.min(filledMinX, x);
          filledMinY = Math.min(filledMinY, y);
          filledMaxX = Math.max(filledMaxX, x);
          filledMaxY = Math.max(filledMaxY, y);
          filledPixelIndices.push(pixel);
        }

        if (!filledPixels) return null;
        maskContext.putImageData(maskImage, 0, 0);
        expandFillMaskByFourPixels();
        return {
          maskCanvas,
          filledPixels,
          debug: {
            atlas: { width, height },
            startPixel: { x: startX, y: startY, flatIndex: startFlatPixel },
            target,
            tolerance,
            hitFaceIndex: hit.faceIndex,
            hitMeshUuid: String(hit.object?.uuid || ''),
            hitMeshName: String(hit.object?.name || ''),
            hitUv: hit.uv ? { x: hit.uv.x, y: hit.uv.y } : null,
            hitWorldPoint: hit.point ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null,
            filledPixels,
            filledBounds: {
              minX: filledMinX,
              minY: filledMinY,
              maxX: filledMaxX,
              maxY: filledMaxY,
              width: filledMaxX - filledMinX + 1,
              height: filledMaxY - filledMinY + 1,
            },
            traversedFaceCount: topology.triangleCount,
            traversedFaceIndices: [],
            filledPixelIndices,
            selectionState: getPaintSelectionDebug(hit),
            selectionAllowedFaceCount: 0,
            selectionAllowedPixelCount: 0,
            selectionWasUsedToRestrictFloodTraversal: false,
            selectionWasUsedToRestrictFilledUvPixels: false,
            globalSurfaceColourMatchUsed: true,
          },
        };
      }

      const enqueuePixel = (x, y) => {
        if (x < 0 || y < 0 || x >= width || y >= height) return false;
        const pixel = (y * width) + x;
        if (visited[pixel]) return false;
        visited[pixel] = 1;
        if (!matchesTargetPixel(pixel)) return false;
        stack[stackLength++] = pixel;
        return true;
      };

      const floodFromSeed = (x, y) => {
        if (!enqueuePixel(x, y)) return false;
        let changed = false;
        while (stackLength > 0) {
          const pixel = stack[--stackLength];
          if (filled[pixel]) continue;
          filled[pixel] = 1;
          const pixelIndex = pixel * 4;
          maskImage.data[pixelIndex] = fillR;
          maskImage.data[pixelIndex + 1] = fillG;
          maskImage.data[pixelIndex + 2] = fillB;
          maskImage.data[pixelIndex + 3] = 255;
          filledPixels += 1;
          changed = true;
          const xPosition = pixel % width;
          const yPosition = Math.floor(pixel / width);
          if (xPosition > 0) enqueuePixel(xPosition - 1, yPosition);
          if (xPosition + 1 < width) enqueuePixel(xPosition + 1, yPosition);
          if (yPosition > 0) enqueuePixel(xPosition, yPosition - 1);
          if (yPosition + 1 < height) enqueuePixel(xPosition, yPosition + 1);
        }
        return changed;
      };

      const faceDataCache = new Map();
      const getFaceData = (faceIndex) => {
        if (faceDataCache.has(faceIndex)) return faceDataCache.get(faceIndex);
        const vertexIndices = [
          faceVertexIndex(faceIndex, 0),
          faceVertexIndex(faceIndex, 1),
          faceVertexIndex(faceIndex, 2),
        ];
        const positions = vertexIndices.map((vertexIndex) => new THREE.Vector3(
          position.getX(vertexIndex),
          position.getY(vertexIndex),
          position.getZ(vertexIndex),
        ));
        const uvs = vertexIndices.map((vertexIndex) => new THREE.Vector2(
          uv.getX(vertexIndex),
          uv.getY(vertexIndex),
        ));
        const data = { vertexIndices, positions, uvs };
        faceDataCache.set(faceIndex, data);
        return data;
      };

      const uvToPixel = (uvPoint) => ({
        x: Math.max(0, Math.min(width - 1, Math.floor(uvPoint.x * width))),
        y: Math.max(0, Math.min(height - 1, Math.floor(uvPoint.y * height))),
      });

      const interpolateUv = (faceData, barycentric) => new THREE.Vector2(
        (faceData.uvs[0].x * barycentric[0]) + (faceData.uvs[1].x * barycentric[1]) + (faceData.uvs[2].x * barycentric[2]),
        (faceData.uvs[0].y * barycentric[0]) + (faceData.uvs[1].y * barycentric[1]) + (faceData.uvs[2].y * barycentric[2]),
      );

      const sampleFaceIsFilled = (faceData) => {
        const samples = [
          [1 / 3, 1 / 3, 1 / 3],
          [0.80, 0.10, 0.10],
          [0.10, 0.80, 0.10],
          [0.10, 0.10, 0.80],
          [0.49, 0.49, 0.02],
          [0.49, 0.02, 0.49],
          [0.02, 0.49, 0.49],
        ];
        return samples.some((barycentric) => {
          const pixel = uvToPixel(interpolateUv(faceData, barycentric));
          return Boolean(filled[(pixel.y * width) + pixel.x]);
        });
      };

      const getSharedEdge = (leftFaceIndex, rightFaceIndex) => {
        const left = getFaceData(leftFaceIndex);
        const right = getFaceData(rightFaceIndex);
        const weldTolerance = Math.max(1e-7, Number(topology.weldTolerance) || 1e-6) * 2;
        const toleranceSquared = weldTolerance * weldTolerance;
        const leftPairs = [[0, 1], [1, 2], [2, 0]];
        const rightPairs = [[0, 1], [1, 2], [2, 0]];
        for (const leftPair of leftPairs) {
          const leftStart = left.positions[leftPair[0]];
          const leftEnd = left.positions[leftPair[1]];
          for (const rightPair of rightPairs) {
            const rightStart = right.positions[rightPair[0]];
            const rightEnd = right.positions[rightPair[1]];
            const sameDirection = (
              leftStart.distanceToSquared(rightStart) <= toleranceSquared
              && leftEnd.distanceToSquared(rightEnd) <= toleranceSquared
            );
            const oppositeDirection = (
              leftStart.distanceToSquared(rightEnd) <= toleranceSquared
              && leftEnd.distanceToSquared(rightStart) <= toleranceSquared
            );
            if (sameDirection || oppositeDirection) {
              return { leftPair, rightPair, oppositeDirection };
            }
          }
        }
        return null;
      };

      const seedAcrossSharedEdge = (leftFaceIndex, rightFaceIndex) => {
        const edge = getSharedEdge(leftFaceIndex, rightFaceIndex);
        if (!edge) return false;
        const left = getFaceData(leftFaceIndex);
        const right = getFaceData(rightFaceIndex);
        const rightOtherCorner = [0, 1, 2].find((corner) => !edge.rightPair.includes(corner));
        if (!Number.isInteger(rightOtherCorner)) return false;
        const edgeSamples = [0.08, 0.22, 0.38, 0.5, 0.62, 0.78, 0.92];
        let seeded = false;

        edgeSamples.forEach((amount) => {
          const leftStartUv = left.uvs[edge.leftPair[0]];
          const leftEndUv = left.uvs[edge.leftPair[1]];
          const leftUv = leftStartUv.clone().lerp(leftEndUv, amount);
          const leftPixel = uvToPixel(leftUv);
          const leftFlatPixel = (leftPixel.y * width) + leftPixel.x;
          if (!filled[leftFlatPixel]) return;

          const mappedAmount = edge.oppositeDirection ? 1 - amount : amount;
          const rightStartUv = right.uvs[edge.rightPair[0]];
          const rightEndUv = right.uvs[edge.rightPair[1]];
          const edgeUv = rightStartUv.clone().lerp(rightEndUv, mappedAmount);
          const interiorUv = right.uvs[rightOtherCorner];
          const seedUv = edgeUv.lerp(interiorUv, 0.004);
          const seedPixel = uvToPixel(seedUv);
          if (floodFromSeed(seedPixel.x, seedPixel.y)) seeded = true;
        });
        return seeded;
      };

      if (!floodFromSeed(startX, startY)) return null;

      // Walk the connected surface once. The previous implementation repeatedly
      // rescanned every triangle until no more UV pixels changed, which became
      // extremely expensive on dense models and made a bucket click appear to
      // have been ignored while the main thread was still working.
      const queuedFaces = new Uint8Array(topology.triangleCount);
      const faceQueue = [hit.faceIndex];
      queuedFaces[hit.faceIndex] = 1;
      let faceQueueIndex = 0;

      while (faceQueueIndex < faceQueue.length) {
        const faceIndex = faceQueue[faceQueueIndex++];
        const faceData = getFaceData(faceIndex);
        if (faceIndex !== hit.faceIndex && !sampleFaceIsFilled(faceData)) continue;

        for (const neighbourFaceIndex of topology.adjacency[faceIndex] || []) {
          if (queuedFaces[neighbourFaceIndex]) continue;
          const seeded = seedAcrossSharedEdge(faceIndex, neighbourFaceIndex);
          if (!seeded) continue;
          queuedFaces[neighbourFaceIndex] = 1;
          faceQueue.push(neighbourFaceIndex);
        }
      }

      if (!filledPixels) return null;
      maskContext.putImageData(maskImage, 0, 0);
      expandFillMaskByFourPixels();

      let filledMinX = width;
      let filledMinY = height;
      let filledMaxX = -1;
      let filledMaxY = -1;
      const filledPixelIndices = [];
      for (let pixel = 0; pixel < filled.length; pixel += 1) {
        if (!filled[pixel]) continue;
        const x = pixel % width;
        const y = Math.floor(pixel / width);
        filledMinX = Math.min(filledMinX, x);
        filledMinY = Math.min(filledMinY, y);
        filledMaxX = Math.max(filledMaxX, x);
        filledMaxY = Math.max(filledMaxY, y);
        filledPixelIndices.push(pixel);
      }

      return {
        maskCanvas,
        filledPixels,
        debug: {
          atlas: { width, height },
          startPixel: { x: startX, y: startY, flatIndex: startFlatPixel },
          target,
          tolerance,
          hitFaceIndex: hit.faceIndex,
          hitMeshUuid: String(hit.object?.uuid || ''),
          hitMeshName: String(hit.object?.name || ''),
          hitUv: hit.uv ? { x: hit.uv.x, y: hit.uv.y } : null,
          hitWorldPoint: hit.point ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null,
          filledPixels,
          filledBounds: filledPixels ? {
            minX: filledMinX,
            minY: filledMinY,
            maxX: filledMaxX,
            maxY: filledMaxY,
            width: filledMaxX - filledMinX + 1,
            height: filledMaxY - filledMinY + 1,
          } : null,
          traversedFaceCount: faceQueue.length,
          traversedFaceIndices: [...faceQueue],
          filledPixelIndices,
          selectionState: getPaintSelectionDebug(hit),
          selectionAllowedFaceCount,
          selectionAllowedPixelCount,
          startPixelAllowedBySelection: selectionAllowedPixels
            ? Boolean(selectionAllowedPixels[startFlatPixel])
            : null,
          selectionWasUsedToRestrictFloodTraversal: Boolean(selectionAllowedPixels),
          selectionWasUsedToRestrictFilledUvPixels: Boolean(selectionAllowedPixels),
        },
      };
    };

    const commitUvFloodFill = ({ maskCanvas, materialSettings, restoring = false, isPbr = false, brushConfig = null, layerId = '', forceSolidSelectionCoverage = false, solidFillHex = '#000000', selectionSnapshot = undefined }) => {
      const model = currentModelRef.current;
      const runtime = runtimeRef.current;
      if (!model || !runtime || !maskCanvas) return false;
      let textureSourceCanvas = maskCanvas;
      if (forceSolidSelectionCoverage) {
        // Preserve the complete UV-space selection mask. The old path replaced
        // it with a 1x1 colour texture and relied on the temporary selected-face
        // geometry to provide coverage. After persistence/rebuild that geometry
        // no longer existed, so the 1x1 texture spread across the model and
        // commonly restored as black. Colourise the original alpha mask instead.
        const solidCanvas = document.createElement('canvas');
        // Never retain one full 8K RGBA texture per bucket fill. At 8192x8192
        // that is roughly 256 MB of GPU memory for a single overlay and makes
        // orbit/pan stall badly. The mask still covers the full 0-1 UV range,
        // but is stored at a capped resolution because it is only an alpha/
        // solid-colour coverage texture, not the underlying painted atlas.
        const sourceWidth = Math.max(1, Number(maskCanvas.width) || 1);
        const sourceHeight = Math.max(1, Number(maskCanvas.height) || 1);
        const maxMaskDimension = 2048;
        const maskScale = Math.min(1, maxMaskDimension / Math.max(sourceWidth, sourceHeight));
        solidCanvas.width = Math.max(1, Math.round(sourceWidth * maskScale));
        solidCanvas.height = Math.max(1, Math.round(sourceHeight * maskScale));
        const solidContext = solidCanvas.getContext('2d', { alpha: true });
        if (!solidContext) return false;
        solidContext.clearRect(0, 0, solidCanvas.width, solidCanvas.height);
        solidContext.imageSmoothingEnabled = false;
        solidContext.globalCompositeOperation = 'source-over';
        solidContext.drawImage(
          maskCanvas,
          0,
          0,
          sourceWidth,
          sourceHeight,
          0,
          0,
          solidCanvas.width,
          solidCanvas.height,
        );
        solidContext.globalCompositeOperation = 'source-in';
        solidContext.fillStyle = solidFillHex || '#000000';
        solidContext.fillRect(0, 0, solidCanvas.width, solidCanvas.height);
        solidContext.globalCompositeOperation = 'source-over';
        textureSourceCanvas = solidCanvas;
      }
      const strokeTexture = new THREE.CanvasTexture(textureSourceCanvas);
      strokeTexture.name = `VoxemeraUvFloodFill-${Date.now()}`;
      strokeTexture.colorSpace = THREE.SRGBColorSpace;
      strokeTexture.flipY = false;
      strokeTexture.wrapS = THREE.ClampToEdgeWrapping;
      strokeTexture.wrapT = THREE.ClampToEdgeWrapping;
      strokeTexture.minFilter = THREE.NearestFilter;
      strokeTexture.magFilter = THREE.NearestFilter;
      strokeTexture.generateMipmaps = false;
      strokeTexture.needsUpdate = true;
      const settings = { ...materialSettingsRef.current, ...(materialSettings || {}) };
      const pbrBrushConfig = brushConfig
        ? { ...brushConfig }
        : null;
      const pbrSetKey = pbrBrushConfig?.pbrSetKey || '';
      const pbrResource = isPbr
        ? (pbrSetKey
            ? (pbrSetResourcesRef.current.get(pbrSetKey) || null)
            : getRuntimeActivePbrBrushResource())
        : null;
      const meshes = [];
      const profiler = loadProfilerRef.current;
      const uvFloodFillDebugStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      profiler?.mark?.('UV flood fill restore: constructor start', 'Beginning uvFloodFill runtime reconstruction.', {
        restoring: Boolean(restoring),
        isPbr: Boolean(isPbr),
        maskWidth: Number(maskCanvas?.width) || 0,
        maskHeight: Number(maskCanvas?.height) || 0,
      });

      const pbrWorldBoundsStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const pbrWorldBounds = new THREE.Box3().setFromObject(model);
      const pbrWorldOrigin = pbrWorldBounds.isEmpty()
        ? new THREE.Vector3()
        : pbrWorldBounds.min.clone();
      const pbrWorldDimensions = pbrWorldBounds.isEmpty()
        ? new THREE.Vector3(1, 1, 1)
        : pbrWorldBounds.getSize(new THREE.Vector3());
      const pbrWorldTileSize = Math.max(
        0.00001,
        Math.max(pbrWorldDimensions.x, pbrWorldDimensions.y, pbrWorldDimensions.z, 0.001) * 0.12,
      );
      const pbrTextures = pbrResource ? getPbrRuntimeTextures(pbrResource) : null;
      profiler?.mark?.('UV flood fill restore: bounds/PBR resources ready', 'Model bounds and PBR texture handles resolved.', {
        durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - pbrWorldBoundsStartedAt) * 10) / 10,
        restoring: Boolean(restoring),
        isPbr: Boolean(isPbr),
      });

      let uvFloodFillMeshIndex = 0;
      model.traverse((sourceMesh) => {
        if (!sourceMesh?.isMesh || sourceMesh.userData?.voxemeraProjectionDecal) return;
        uvFloodFillMeshIndex += 1;
        const meshStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        profiler?.mark?.('UV flood fill restore: edge wear skipped', 'UV flood fills have edgeWear fixed at 0, so expensive edge-wear topology data is intentionally not built.', {
          meshIndex: uvFloodFillMeshIndex,
          meshName: String(sourceMesh?.name || ''),
          vertexCount: Number(sourceMesh?.geometry?.getAttribute?.('position')?.count) || 0,
        });
        const exactClipPolygon = activeSelection?.clipPolygonsByMesh?.get?.(sourceMesh.uuid) || [];
        const useExactClipGeometry = activeSelection?.shape === 'surface'
          && exactClipPolygon.length >= 3
          && activeSelection?.clipViewProjectionMatrix?.isMatrix4;
        // The fragment shader supplies the pixel-exact lasso edge. Keep every
        // source triangle for this mesh so the clip cannot expose holes caused
        // by an incomplete boundary-face set. The screen-space clip still
        // prevents any fragment outside the saved selection polygon rendering.
        const selectionGeometryStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        let geometry = useExactClipGeometry
          ? sourceMesh.geometry.clone()
          : createSelectionGeometry(sourceMesh, null, false);
        profiler?.mark?.('UV flood fill restore: selection geometry ready', 'createSelectionGeometry/clone completed for flood-fill overlay.', {
          meshIndex: uvFloodFillMeshIndex,
          meshName: String(sourceMesh?.name || ''),
          useExactClipGeometry: Boolean(useExactClipGeometry),
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - selectionGeometryStartedAt) * 10) / 10,
          outputVertexCount: Number(geometry?.getAttribute?.('position')?.count) || 0,
        });
        if (useExactClipGeometry) {
          geometry.clearGroups();
          geometry.computeBoundingBox();
          geometry.computeBoundingSphere();
          geometry.userData = {
            ...(geometry.userData || {}),
            voxemeraExactSelectionFullSourceGeometry: true,
          };
        }
        if (!geometry?.getAttribute?.('uv')) return;
        let material;
        if (isPbr && pbrResource) {
          const projectionUniforms = {
            strokeMap: { value: strokeTexture },
            pbrAlbedoMap: { value: pbrTextures?.albedo || strokeTexture },
            pbrMaterialMap: { value: pbrTextures?.material || strokeTexture },
            pbrNormalMap: { value: pbrTextures?.normal || strokeTexture },
            normalLayerMap: { value: pbrTextures?.normal || strokeTexture },
            normalLayerHasMap: { value: 0 },
            normalLayerMix: { value: 1 },
            normalLayerScale: { value: Math.max(0.1, Math.min(8, (Number(pbrBrushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100)) },
            normalLayerAngle: { value: THREE.MathUtils.degToRad(Number(pbrBrushConfig?.pbrAngleDegrees) || 0) },
            normalBelowMap: { value: strokeTexture },
            normalBelowHasMap: { value: 0 },
            pbrHasAlbedo: { value: pbrTextures?.albedo ? 1 : 0 },
            pbrHasMaterial: { value: pbrTextures?.material ? 1 : 0 },
            pbrHasNormal: { value: pbrTextures?.normal ? 1 : 0 },
            pbrScale: { value: Math.max(0.1, Math.min(8, (Number(pbrBrushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100)) },
            pbrAngle: { value: THREE.MathUtils.degToRad(Number(pbrBrushConfig?.pbrAngleDegrees) || 0) },
            pbrWorldOrigin: { value: pbrWorldOrigin },
            pbrWorldTileSize: { value: pbrWorldTileSize },
            heightScale: { value: Math.max(0, Math.min(1, (Number(settings?.height) || 0) / 100)) },
          };
          material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.82,
            metalness: 0,
            opacity: 1,
            transparent: false,
            blending: THREE.NoBlending,
            depthTest: true,
            depthWrite: false,
            side: getProjectionDecalSide(sourceMesh),
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            toneMapped: true,
          });
          material.userData = {
            ...(material.userData || {}),
            voxemeraProjectionUniforms: projectionUniforms,
            voxemeraPbrProjection: true,
            voxemeraUvFloodFill: true,
          };
          material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, projectionUniforms);
            material.userData.voxemeraProjectionShader = shader;
            shader.vertexShader = `
              varying vec2 vUvFill;
              varying vec3 vPbrWorldPosition;
              varying vec3 vPbrWorldNormal;
            ` + shader.vertexShader
              .replace(
                '#include <beginnormal_vertex>',
                `#include <beginnormal_vertex>
                vPbrWorldNormal = normalize(mat3(modelMatrix) * objectNormal);`,
              )
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                vUvFill = uv;
                vec4 pbrWorldPosition = modelMatrix * vec4(transformed, 1.0);
                vPbrWorldPosition = pbrWorldPosition.xyz;`,
              );
            shader.fragmentShader = `
              uniform sampler2D strokeMap;
              uniform sampler2D pbrAlbedoMap;
              uniform sampler2D pbrMaterialMap;
              uniform sampler2D pbrNormalMap;
              uniform sampler2D normalLayerMap;
              uniform float normalLayerHasMap;
              uniform float normalLayerMix;
              uniform float normalLayerScale;
              uniform float normalLayerAngle;
              uniform sampler2D normalBelowMap;
              uniform float normalBelowHasMap;
              uniform float pbrHasAlbedo;
              uniform float pbrHasMaterial;
              uniform float pbrHasNormal;
              uniform float pbrScale;
              uniform float pbrAngle;
              uniform vec3 pbrWorldOrigin;
              uniform float pbrWorldTileSize;
              uniform float heightScale;
              varying vec2 vUvFill;
              varying vec3 vPbrWorldPosition;
              varying vec3 vPbrWorldNormal;

              vec2 voxemeraRotatePbrUv(vec2 uv) {
                float c = cos(pbrAngle);
                float s = sin(pbrAngle);
                return mat2(c, -s, s, c) * uv;
              }

              vec3 voxemeraPbrWeights() {
                vec3 weights = pow(abs(normalize(vPbrWorldNormal)), vec3(6.0));
                return weights / max(weights.x + weights.y + weights.z, 0.0001);
              }

              vec3 voxemeraPbrCoords() {
                return (vPbrWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * pbrScale, 0.00001);
              }

              vec3 voxemeraSamplePbrRgb(sampler2D sourceMap) {
                vec3 coords = voxemeraPbrCoords();
                vec3 weights = voxemeraPbrWeights();
                vec2 uvX = fract(voxemeraRotatePbrUv(coords.zy));
                vec2 uvY = fract(voxemeraRotatePbrUv(coords.xz));
                vec2 uvZ = fract(voxemeraRotatePbrUv(coords.xy));
                return (texture2D(sourceMap, uvX).rgb * weights.x)
                  + (texture2D(sourceMap, uvY).rgb * weights.y)
                  + (texture2D(sourceMap, uvZ).rgb * weights.z);
              }

              vec2 voxemeraRotateSourceNormalUv(vec2 uv) {
                float c = cos(normalLayerAngle);
                float s = sin(normalLayerAngle);
                return mat2(c, -s, s, c) * uv;
              }

              vec3 voxemeraSampleSourceNormalRgb() {
                vec3 coords = (vPbrWorldPosition - pbrWorldOrigin)
                  / max(pbrWorldTileSize * normalLayerScale, 0.00001);
                vec3 weights = voxemeraPbrWeights();
                vec2 uvX = fract(voxemeraRotateSourceNormalUv(coords.zy));
                vec2 uvY = fract(voxemeraRotateSourceNormalUv(coords.xz));
                vec2 uvZ = fract(voxemeraRotateSourceNormalUv(coords.xy));
                return (texture2D(normalLayerMap, uvX).rgb * weights.x)
                  + (texture2D(normalLayerMap, uvY).rgb * weights.y)
                  + (texture2D(normalLayerMap, uvZ).rgb * weights.z);
              }
            ` + shader.fragmentShader.replace(
              '#include <map_fragment>',
              `
              vec4 coverage = texture2D(strokeMap, vUvFill);
              if (coverage.a < 0.45) discard;
              diffuseColor.rgb = pbrHasAlbedo > 0.5
                ? voxemeraSamplePbrRgb(pbrAlbedoMap)
                : vec3(1.0);
              diffuseColor.a = 1.0;
              `,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <roughnessmap_fragment>',
              `#include <roughnessmap_fragment>
              if (pbrHasMaterial > 0.5) {
                roughnessFactor = voxemeraSamplePbrRgb(pbrMaterialMap).g;
              }`,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <metalnessmap_fragment>',
              `#include <metalnessmap_fragment>
              if (pbrHasMaterial > 0.5) {
                metalnessFactor = voxemeraSamplePbrRgb(pbrMaterialMap).b;
              }`,
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <normal_fragment_maps>',
              `#include <normal_fragment_maps>
              if ((pbrHasNormal > 0.5 || normalLayerHasMap > 0.5 || normalBelowHasMap > 0.5) && heightScale > 0.0001) {
                vec3 ownNormal = pbrHasNormal > 0.5
                  ? (voxemeraSamplePbrRgb(pbrNormalMap) * 2.0 - 1.0)
                  : vec3(0.0, 0.0, 1.0);
                vec3 sourceNormal = normalBelowHasMap > 0.5
                  ? (texture2D(normalBelowMap, vUvFill).rgb * 2.0 - 1.0)
                  : (normalLayerHasMap > 0.5
                    ? (voxemeraSampleSourceNormalRgb() * 2.0 - 1.0)
                    : ownNormal);
                vec3 blendedNormal = normalize(mix(sourceNormal, ownNormal, clamp(normalLayerMix, 0.0, 1.0)));
                normal = normalize(normal + vec3(blendedNormal.xy * heightScale, 0.0));
              }`,
            );
          };
          material.customProgramCacheKey = () => 'voxemeraUvFloodFillPbr_v4_no_edge_wear';
        } else {
          material = new THREE.MeshStandardMaterial({
            map: strokeTexture,
            transparent: true,
            alphaTest: 0.001,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            side: getProjectionDecalSide(sourceMesh),
            roughness: 1 - (Math.max(0, Math.min(100, Number(settings.gloss) || 0)) / 100),
            metalness: Math.max(0, Math.min(100, Number(settings.metalness) || 0)) / 100,
          });
          material.userData = {
            ...(material.userData || {}),
            voxemeraUvFloodFill: true,
          };
        }
        const exactSelectionClipApplied = applyExactSurfaceSelectionClip(material, sourceMesh);
        const overlay = new THREE.Mesh(geometry, material);
        overlay.name = isPbr ? 'Voxemera UV flood fill PBR' : 'Voxemera UV flood fill';
        overlay.position.copy(sourceMesh.position);
        overlay.quaternion.copy(sourceMesh.quaternion);
        overlay.scale.copy(sourceMesh.scale);
        overlay.renderOrder = 1000 + (runtime.projectionStrokes?.length || 0);
        overlay.frustumCulled = sourceMesh.frustumCulled;
        overlay.raycast = () => {};
        overlay.userData.voxemeraProjectionDecal = true;
        sourceMesh.parent?.add(overlay);
        meshes.push(overlay);
        profiler?.mark?.('UV flood fill restore: mesh overlay complete', 'Flood-fill overlay geometry/material was created and inserted.', {
          meshIndex: uvFloodFillMeshIndex,
          meshName: String(sourceMesh?.name || ''),
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - meshStartedAt) * 10) / 10,
          isPbr: Boolean(isPbr),
        });
      });
      profiler?.mark?.('UV flood fill restore: traversal complete', 'All source meshes were processed for the uvFloodFill stroke.', {
        meshCount: uvFloodFillMeshIndex,
        overlayCount: meshes.length,
        durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - uvFloodFillDebugStartedAt) * 10) / 10,
      });

      if (!meshes.length) {
        strokeTexture.dispose();
        return false;
      }
      runtime.projectionStrokes = runtime.projectionStrokes || [];
      if (!restoring) discardProjectionRedoStrokes();
      const committedStroke = {
        kind: 'uvFloodFill',
        layerId: String(layerId || getCurrentLayerState().activeLayerId || ''),
        meshes,
        strokeTexture,
        sourceCanvas: textureSourceCanvas,
        materialSettings: settings,
        isPbr: isPbr === true,
        brushConfig: pbrBrushConfig,
        forceSolidSelectionCoverage: Boolean(forceSolidSelectionCoverage),
        solidFillHex: String(solidFillHex || '#000000'),
        selectionSnapshot: selectionSnapshot !== undefined ? selectionSnapshot : captureSelectionSnapshot(),
      };
      runtime.projectionStrokes.push(committedStroke);
      if (!restoring) {
      }
      // Include the newly-created fill in the compositor immediately. Without
      // this, the non-PBR path flattened the previous GPU atlas and effectively
      // discarded the fill that had just been clicked.
      if (!restoring) {
        projectionStrokeCountRef.current = runtime.projectionStrokes.length;
      }

      // During persisted model restore the saved colour/material/normal atlases
      // have already been decoded and installed before history objects are rebuilt.
      // Replaying the GPU compositor here is both redundant and extremely costly:
      // a single restored UV flood-fill on a large mesh was blocking startup for
      // ~20 seconds before the remaining projected strokes could even restore.
      // Match the projection-decal restore path: recreate the runtime history
      // object only, then let the already-restored atlases + overlay objects render.
      if (restoring) {
        return true;
      }

      rebuildGpuPaintMap();
      refreshProjectionLayerPresentation();
      updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
      return true;
    };

    const performUvBucketFill = (event, brushConfig) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const hit = getPaintHit(event);
      if (!hit) return false;
      const fillHex = String(
        brushConfig?.strokeColor
        || brushConfig?.color
        || brushConfig?.rgba
        || paintConfigRef.current?.strokeColor
        || paintConfigRef.current?.color
        || '#000000'
      );
      const activePbrResourceAtClick = getRuntimeActivePbrBrushResource();
      const hasSurfaceSelectionAtClick = Boolean(
        activeSelection
        && (activeSelection.shape === 'surface' || activeSelection.shape === FACE_SELECTION_SHAPE)
        && activeSelection.selectedFaces,
      );
      const projectionStrokeMatch = !hasSurfaceSelectionAtClick
        ? getTopmostVisibleProjectionStrokeAtHit(hit)
        : null;

      // Photoshop-style bucket behaviour: the visible topmost region is the
      // target. When PBR is active, replace only that region with the selected
      // PBR material instead of falling through to the canonical atlas, which
      // would see the base colour underneath and flood the whole model.
      if (!hasSurfaceSelectionAtClick && activePbrResourceAtClick && projectionStrokeMatch) {
        const runtime = runtimeRef.current;
        const oldStroke = projectionStrokeMatch.stroke;
        const oldIndex = projectionStrokeMatch.strokeIndex;
        const pbrBrushConfig = {
          ...(oldStroke?.brushConfig || {}),
          ...brushConfig,
          pbrSetKey: activePbrResourceAtClick.key || '',
          pbrScalePercent: Math.max(10, Math.min(800, Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
          pbrAngleDegrees: ((Number(pbrBrushStateRef.current?.angleDegrees) || 0) % 360 + 360) % 360,
        };
        // Keep two independent pixel copies before any projection commit can
        // alter or dispose the existing stroke. One copy becomes the new PBR
        // coverage; the other is reserved for rebuilding the exact original
        // history entry when Undo needs it.
        const replacementCoverageCopy = oldStroke?.sourceCanvas
          ? copyCanvasPixels(
              oldStroke.sourceCanvas,
              oldStroke.sourceCanvas.width,
              oldStroke.sourceCanvas.height,
              { smoothingEnabled: false },
            )
          : null;
        const originalUndoCoverageCopy = oldStroke?.sourceCanvas
          ? copyCanvasPixels(
              oldStroke.sourceCanvas,
              oldStroke.sourceCanvas.width,
              oldStroke.sourceCanvas.height,
              { smoothingEnabled: false },
            )
          : null;
        const replacementCoverageCanvas = replacementCoverageCopy?.canvas || null;
        const originalUndoCoverageCanvas = originalUndoCoverageCopy?.canvas || null;
        // A PBR replacement sits above the original colour stroke. Expand only
        // the replacement coverage by one pixel so anti-aliased colour at the
        // old edge cannot remain visible as a thin outline underneath it.
        if (replacementCoverageCanvas) {
          expandProjectedStrokeCoverage(replacementCoverageCanvas, 1);
        }
        let replacementCreated = false;

        if (oldStroke?.kind === 'uvFloodFill' && replacementCoverageCanvas) {
          replacementCreated = runWithSelection(selectionFromSnapshot(oldStroke.selectionSnapshot), () => commitUvFloodFill({
            maskCanvas: replacementCoverageCanvas,
            materialSettings: { ...materialSettingsRef.current },
            isPbr: true,
            brushConfig: pbrBrushConfig,
            layerId: String(oldStroke.layerId || getCurrentLayerState().activeLayerId || ''),
            selectionSnapshot: oldStroke.selectionSnapshot || null,
          }));
        } else if (replacementCoverageCanvas && oldStroke?.projectionCameraData) {
          const replacementInput = {
            restoredCanvas: replacementCoverageCanvas,
            projectionRect: { ...(oldStroke.projectionRect || { x: 0, y: 0, width: 1, height: 1 }) },
            projectionFullWidth: Number(oldStroke.projectionFullWidth) || oldStroke.sourceCanvas.width,
            projectionFullHeight: Number(oldStroke.projectionFullHeight) || oldStroke.sourceCanvas.height,
            projectionCamera: deserializeProjectionCamera(oldStroke.projectionCameraData),
            materialSettings: { ...materialSettingsRef.current },
            brushConfig: pbrBrushConfig,
            layerId: String(oldStroke.layerId || getCurrentLayerState().activeLayerId || ''),
            isAirbrush: oldStroke.isAirbrush === true,
            isPencil: oldStroke.isPencil === true,
            isImportedBrush: oldStroke.isImportedBrush === true,
            isPbr: true,
            restoring: true,
            selectionSnapshot: oldStroke.selectionSnapshot || null,
          };
          replacementCreated = Boolean(replacementInput.projectionCamera)
            && runWithSelection(selectionFromSnapshot(oldStroke.selectionSnapshot), () => commitExactPreviewProjection(replacementInput));
        }

        if (replacementCreated && runtime?.projectionStrokes?.length) {
          const newStroke = runtime.projectionStrokes.pop();

          // Keep the original runtime stroke itself as the earlier history state.
          // The PBR fill is a new overlay immediately after it. That gives the
          // history the exact Photoshop-style sequence:
          //   original mark -> PBR replacement
          // Undo reveals the untouched original object; redo first restores that
          // original before the following redo restores the PBR overlay.
          //
          // Do not rebuild, clone into history, replace or dispose oldStroke here.
          // Rebuilding it changed the runtime identity behind the already-created
          // history snapshot and caused redo to resolve directly to the PBR entry.
          runtime.projectionStrokes = runtime.projectionStrokes.filter((stroke) => (
            stroke !== newStroke
          ));
          const currentOriginalIndex = runtime.projectionStrokes.indexOf(oldStroke);
          const insertionIndex = currentOriginalIndex >= 0
            ? currentOriginalIndex + 1
            : Math.max(0, Math.min(oldIndex + 1, runtime.projectionStrokes.length));
          runtime.projectionStrokes.splice(insertionIndex, 0, newStroke);

          projectionStrokeCountRef.current = runtime.projectionStrokes.length;
          runtime.projectionStrokes.forEach((stroke, index) => {
            const visible = index < projectionStrokeCountRef.current;
            (stroke?.meshes || []).forEach((mesh) => {
              if (!mesh) return;
              mesh.renderOrder = 1000 + index;
              mesh.visible = visible;
            });
          });
          rebuildGpuPaintMap();
          refreshProjectionLayerPresentation();
          updateModelEdgeOverlay(Boolean(viewSettingsRef.current?.showModelEdges));
          scheduleProjectionPaintSave();
          renderer.render(scene, camera);
          const callback = paintConfigRef.current.onProjectionStrokeCommitted;
          if (typeof callback === 'function') callback(runtime.projectionStrokes.length);
          return true;
        }
      }

      // Flat-colour bucket fills may recolour the topmost visible projection in
      // place. A PBR bucket uses the replacement path above.
      if (!hasSurfaceSelectionAtClick && !activePbrResourceAtClick) {
        if (projectionStrokeMatch && recolourProjectionStroke(projectionStrokeMatch, fillHex)) {
          renderer.render(scene, camera);
          const callback = paintConfigRef.current.onProjectionStrokeCommitted;
          if (typeof callback === 'function') callback(projectionStrokeCountRef.current);
          return true;
        }
      }
      const fill = createConnectedColourMask(hit, fillHex);
      if (!fill) {
        return false;
      }


      const hasSurfaceSelection = Boolean(
        activeSelection
        && (activeSelection.shape === 'surface' || activeSelection.shape === FACE_SELECTION_SHAPE)
        && activeSelection.selectedFaces,
      );
      const activePbrResource = activePbrResourceAtClick;
      if (activePbrResource) {
        const committed = commitUvFloodFill({
          maskCanvas: fill.maskCanvas,
          materialSettings: { ...materialSettingsRef.current },
          isPbr: true,
          brushConfig: {
            ...brushConfig,
            pbrSetKey: activePbrResource.key || '',
            pbrScalePercent: Math.max(10, Math.min(800, Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
            pbrAngleDegrees: ((Number(pbrBrushStateRef.current?.angleDegrees) || 0) % 360 + 360) % 360,
          },
          forceSolidSelectionCoverage: hasSurfaceSelection,
          solidFillHex: fillHex,
        });
        if (!committed) return false;
        const projectionCount = Array.isArray(runtimeRef.current?.projectionStrokes)
          ? runtimeRef.current.projectionStrokes.length
          : 0;
        projectionStrokeCountRef.current = projectionCount;
        scheduleProjectionPaintSave();
        if (viewportDisplayModeRef.current === 'texture') {
          window.setTimeout(() => {
            drawCurrentTextureView(textureViewCanvasRef.current);
          }, 0);
        }
        renderer.render(scene, camera);
        const callback = paintConfigRef.current.onProjectionStrokeCommitted;
        if (typeof callback === 'function') callback(projectionCount);
        return true;
      }

      if (!hasSurfaceSelection) {
        // Ordinary colour fills never need a temporary projection. Creating one
        // before the direct atlas update allowed shared UV texels and existing
        // projection colours to contaminate the bucket target. Apply this mask
        // straight to the canonical atlas and leave all face-restricted fills
        // untouched above it.
        const directlyApplied = applySolidFillMaskDirectlyToCanonicalAtlas({
          maskCanvas: fill.maskCanvas,
          fillHex,
          filledBounds: fill.debug?.filledBounds || null,
          materialSettings: materialSettingsRef.current,
        });
        if (!directlyApplied) return false;
        scheduleCanonicalAtlasSave();
        renderer.render(scene, camera);
        const callback = paintConfigRef.current.onProjectionStrokeCommitted;
        if (typeof callback === 'function') {
          const projectionCount = Array.isArray(runtimeRef.current?.projectionStrokes)
            ? runtimeRef.current.projectionStrokes.length
            : 0;
          callback(projectionCount);
        }
        return true;
      }

      const committed = commitUvFloodFill({
        maskCanvas: fill.maskCanvas,
        materialSettings: { ...materialSettingsRef.current },
        forceSolidSelectionCoverage: true,
        solidFillHex: fillHex,
      });
      if (!committed) return false;

      if (hasSurfaceSelection) {
        // A surface selection is face-specific, while the canonical colour atlas
        // is UV-specific. This model contains overlapping/shared UV texels, so
        // flattening a correctly clipped fill into the atlas makes unrelated
        // triangles that share those texels turn the same colour. Keep selected
        // fills as projection geometry instead; createSelectionGeometry already
        // limits the overlay to the selected faces, so the colour cannot appear
        // on a different part of the model.
        const projectionCount = Array.isArray(runtimeRef.current?.projectionStrokes)
          ? runtimeRef.current.projectionStrokes.length
          : 0;
        projectionStrokeCountRef.current = projectionCount;
        scheduleProjectionPaintSave();
        renderer.render(scene, camera);
        const callback = paintConfigRef.current.onProjectionStrokeCommitted;
        if (typeof callback === 'function') callback(projectionCount);
        return true;
      }

    };

    const rebuildProjectionResourcesAfterContextRestore = () => {
      const model = currentModelRef.current;
      const runtime = runtimeRef.current;
      if (!model || !runtime) return;

      projectionDepthCache.forEach((entry) => entry?.target?.dispose?.());
      projectionDepthCache.clear();

      (runtime.projectionStrokes || []).forEach((stroke) => {
        stroke.strokeTexture && (stroke.strokeTexture.needsUpdate = true);
        stroke.heightTexture && (stroke.heightTexture.needsUpdate = true);
        if (stroke.kind === 'uvFloodFill') return;

        const projectionCamera = deserializeProjectionCamera(stroke.projectionCameraData);
        if (!projectionCamera) return;
        projectionCamera.updateMatrixWorld(true);
        projectionCamera.updateProjectionMatrix();

        const depthEntry = captureProjectionDepth(
          model,
          stroke.projectionFullWidth || 1,
          stroke.projectionFullHeight || 1,
          projectionCamera,
        );
        stroke.depthEntry = depthEntry;
        stroke.depthTarget = depthEntry.target;

        (stroke.meshes || []).forEach((mesh) => {
          const uniforms = mesh?.material?.userData?.voxemeraProjectionUniforms;
          if (uniforms?.projectorDepthMap) uniforms.projectorDepthMap.value = depthEntry.target.depthTexture || depthEntry.target.texture;
          if (mesh?.material) mesh.material.needsUpdate = true;
        });
      });

      if (activePaintSession?.texture) activePaintSession.texture.needsUpdate = true;
      if (activePaintSession?.normalTexture) activePaintSession.normalTexture.needsUpdate = true;
      renderer.compile?.(scene, camera);
    };

    const commitPaintStroke = (stroke) => {
      if (!stroke?.hitSamples?.length) return false;
      const rect = renderer.domElement.getBoundingClientRect();
      let painted = false;
      const [firstHit, ...restHits] = stroke.hitSamples;

      // The direct-UV pointer-up path caches one stable stroke scale before
      // dirty-bounds calculation. This lookup is therefore constant-time.
      const firstScale = getUvPixelsPerScreenPixel(firstHit, rect);
      stroke.hitSamples.forEach((hit) => {
        if (hit) hit.uvPixelsPerScreenPixel = firstScale;
      });
      painted = paintRoundPenDab(firstHit, stroke.brushConfig, rect, firstHit.pressure, firstScale) || painted;

      let previousHit = firstHit;
      restHits.forEach((hit, index) => {
        if (index === 0 || (index + 1) % 5 === 0 || index === restHits.length - 1) {
        }
        painted = paintPenSegment(previousHit, hit, stroke.brushConfig, rect, false) || painted;
        previousHit = hit;
        if (index === 0 || (index + 1) % 5 === 0 || index === restHits.length - 1) {
        }
      });
      if (painted && activePaintSession?.texture) {
        activePaintSession.texture.needsUpdate = true;
      }
      return painted;
    };

    const getSelectionPointerPoint = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const getSurfaceSelectionSample = (event) => {
      const hit = getPaintHit(event);
      if (!hit?.point || !hit.object?.isMesh || !Number.isInteger(hit.faceIndex)) return null;
      return {
        object: hit.object,
        faceIndex: hit.faceIndex,
        worldPoint: hit.point.clone(),
      };
    };

    const getEditableSelectionVertices = (selection) => (
      selection?.editableVertices || selection?.rawSurfaceSamples || selection?.surfaceSamples || []
    );

    const findEditableSurfaceVertex = (point, selection) => {
      const samples = getEditableSelectionVertices(selection);
      let best = null;
      samples.forEach((sample, index) => {
        const projected = projectWorldPointToViewport(sample.worldPoint);
        if (!projected) return;
        const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
        if (distance <= 12 && (!best || distance < best.distance)) best = { index, distance };
      });
      return best;
    };

    const findEditableSurfaceMidpoint = (point, selection) => {
      const midpoints = Array.isArray(selection?.editableMidpoints) ? selection.editableMidpoints : [];
      let best = null;
      midpoints.forEach((entry, midpointIndex) => {
        const projected = projectWorldPointToViewport(entry?.sample?.worldPoint);
        if (!projected) return;
        const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
        if (distance <= 10 && (!best || distance < best.distance)) best = { ...entry, midpointIndex, distance };
      });
      return best;
    };

    const findEditableSurfaceEdge = (point, selection) => {
      const samples = getEditableSelectionVertices(selection);
      if (samples.length < 2) return null;
      let best = null;
      for (let index = 0; index < samples.length; index += 1) {
        const nextIndex = (index + 1) % samples.length;
        const start = projectWorldPointToViewport(samples[index].worldPoint);
        const end = projectWorldPointToViewport(samples[nextIndex].worldPoint);
        if (!start || !end) continue;
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lengthSq = (dx * dx) + (dy * dy);
        const t = lengthSq > 1e-9 ? Math.max(0, Math.min(1, (((point.x - start.x) * dx) + ((point.y - start.y) * dy)) / lengthSq)) : 0;
        const x = start.x + (dx * t);
        const y = start.y + (dy * t);
        const distance = Math.hypot(point.x - x, point.y - y);
        if (distance <= 10 && (!best || distance < best.distance)) best = { index, nextIndex, distance };
      }
      return best;
    };

    const rebuildEditableSurfaceSelection = (samples, inverted = false) => {
      // Keep the handles the user placed as the canonical editable vertex list.
      // buildSurfaceSelection may generate extra samples for its internal
      // boundary/region calculation; those must never replace or re-index the
      // editable handles, otherwise a dragged vertex snaps back on pointer-up.
      const editableSamples = (Array.isArray(samples) ? samples : [])
        .filter((sample) => sample?.object?.isMesh && Number.isInteger(sample.faceIndex) && sample.worldPoint)
        .map((sample) => ({
          ...sample,
          worldPoint: sample.worldPoint.clone?.() || sample.worldPoint,
          generatedSurfaceBridge: false,
          bridgeFromPrevious: false,
        }));
      const next = buildSurfaceSelection(editableSamples, { wrapEverySegment: true });
      if (!next) return null;
      next.editablePolygon = true;
      next.inverted = inverted;
      next.editableVertices = editableSamples;
      next.rawSurfaceSamples = editableSamples;
      next.wrappedSurfaceSamples = getEditableSurfacePath(editableSamples, true);
      next.surfaceSamples = next.wrappedSurfaceSamples;
      next.editableMidpoints = buildEditableMidpoints(editableSamples, true);
      return next;
    };

    const handleSelectionPointerDown = (event) => {
      const tool = activeToolRef.current;
      if (!isSelectionTool(tool) || event.ctrlKey || event.button !== 0) return false;
      event.preventDefault();
      event.stopPropagation();

      const point = getSelectionPointerPoint(event);
      if (tool === 'marquee_polygon_edit') {
        const sample = getSurfaceSelectionSample(event);
        if (activeSelection?.editablePolygon) {
          const vertexHit = findEditableSurfaceVertex(point, activeSelection);
          if (vertexHit) {
            editableSelectionVertexIndex = vertexHit.index;
            activeSelectionPointerId = event.pointerId;
            try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
            return true;
          }
          const midpointHit = findEditableSurfaceMidpoint(point, activeSelection);
          if (midpointHit) {
            const samples = [...getEditableSelectionVertices(activeSelection)];
            samples.splice(midpointHit.nextIndex, 0, midpointHit.sample);
            activeSelection = rebuildEditableSurfaceSelection(samples, activeSelection.inverted);
            editableSelectionVertexIndex = midpointHit.nextIndex;
            activeSelectionPointerId = event.pointerId;
            editableSelectionDirty = false;
            try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
            drawSelectionOverlay();
            return true;
          }

          const edgeHit = findEditableSurfaceEdge(point, activeSelection);
          if (edgeHit && sample) {
            const samples = [...getEditableSelectionVertices(activeSelection)];
            samples.splice(edgeHit.nextIndex, 0, sample);
            activeSelection = rebuildEditableSurfaceSelection(samples, activeSelection.inverted);
            editableSelectionVertexIndex = edgeHit.nextIndex;
            activeSelectionPointerId = event.pointerId;
            editableSelectionDirty = false;
            try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
            drawSelectionOverlay();
            return true;
          }

          activeSelection = null;
          selectionDraft = null;
          if (sample) {
            selectionDraft = {
              shape: 'surface',
              surfaceSamples: [sample],
              editableVertices: [sample],
              wrappedSurfaceSamples: [sample],
              editableMidpoints: [],
              closed: false,
              inverted: false,
              editablePolygon: true,
            };
          }
          drawSelectionOverlay();
          return true;
        }
        if (!sample) return true;
        const draftSamples = selectionDraft?.editablePolygon ? getEditableSelectionVertices(selectionDraft) : [];
        if (!draftSamples.length) {
          activeSelection = null;
          selectionDraft = { shape: 'surface', surfaceSamples: [sample], editableVertices: [sample], wrappedSurfaceSamples: [sample], editableMidpoints: [], closed: false, inverted: false, editablePolygon: true };
          drawSelectionOverlay();
          return true;
        }
        const firstProjected = projectWorldPointToViewport(draftSamples[0].worldPoint);
        const closeByFirst = draftSamples.length >= 3 && firstProjected && Math.hypot(point.x - firstProjected.x, point.y - firstProjected.y) <= 12;
        const closeByDouble = Number(event.detail) >= 2 && draftSamples.length >= 3;
        if (closeByFirst || closeByDouble) {
          activeSelection = rebuildEditableSurfaceSelection(draftSamples, false);
          selectionDraft = null;
          drawSelectionOverlay();
          return true;
        }
        const nextDraftSamples = [...draftSamples, sample];
        selectionDraft = {
          ...selectionDraft,
          surfaceSamples: nextDraftSamples,
          editableVertices: nextDraftSamples,
          wrappedSurfaceSamples: getEditableSurfacePath(nextDraftSamples, false),
          editableMidpoints: buildEditableMidpoints(nextDraftSamples, false),
        };
        drawSelectionOverlay();
        return true;
      }
      if (tool === 'marquee_polygon') {
        const sample = getSurfaceSelectionSample(event);
        // Consume clicks that miss the model, but do not leave a captured
        // pointer behind. A stale selection pointer would later swallow the
        // Pen/Airbrush pointer-up and leave those tools painting forever.
        if (!sample) return true;
        if (!selectionDraft || selectionDraft.shape !== 'surface') {
          selectionDraft = { shape: 'surface', surfaceSamples: [sample], closed: false, inverted: false };
        } else {
          // This is the first point after a paused lasso/view change. Preserve
          // the point exactly, but mark the missing A-to-B segment so completion
          // can bridge that gap over the model surface instead of through air.
          selectionDraft = {
            ...selectionDraft,
            surfaceSamples: [...selectionDraft.surfaceSamples, { ...sample, bridgeFromPrevious: true }],
          };
        }
      } else {
        selectionDraft = { shape: tool === 'marquee_ellipse' ? 'ellipse' : 'rectangle', start: point, end: point, bounds: { x: point.x, y: point.y, width: 0, height: 0 }, inverted: false };
      }

      activeSelectionPointerId = event.pointerId;
      try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
      drawSelectionOverlay();
      return true;
    };

    const handleSelectionPointerMove = (event) => {
      if (editableSelectionVertexIndex >= 0 && activeSelection?.editablePolygon && event.pointerId === activeSelectionPointerId && !event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        const sample = getSurfaceSelectionSample(event);
        if (sample) {
          const samples = [...getEditableSelectionVertices(activeSelection)];
          samples[editableSelectionVertexIndex] = sample;
          activeSelection = {
            ...activeSelection,
            editableVertices: samples,
            rawSurfaceSamples: samples,
            editableMidpoints: [],
            // Keep the previous wrapped edge route while dragging. The handle
            // itself follows the pointer immediately; the expensive surface
            // route and enclosed region rebuild once on pointer-up.
          };
          editableSelectionDirty = true;
          drawSelectionOverlay();
        }
        return true;
      }
      if (!selectionDraft || event.pointerId !== activeSelectionPointerId || event.ctrlKey) return false;
      event.preventDefault();
      event.stopPropagation();
      if (selectionDraft.shape === 'surface') {
        const sample = getSurfaceSelectionSample(event);
        const samples = selectionDraft.surfaceSamples || [];
        const last = samples[samples.length - 1];
        if (sample && (!last || last.object !== sample.object || last.faceIndex !== sample.faceIndex || last.worldPoint.distanceTo(sample.worldPoint) > 0.0001)) {
          selectionDraft = { ...selectionDraft, surfaceSamples: [...samples, sample] };
        }
      } else {
        const point = getSelectionPointerPoint(event);
        selectionDraft = { ...selectionDraft, end: point, bounds: normalizeSelectionBounds(selectionDraft.start, point) };
      }
      drawSelectionOverlay();
      return true;
    };

    const finishSelection = (event, forceClose = false) => {
      if (editableSelectionVertexIndex >= 0) {
        if (event && activeSelectionPointerId != null && event.pointerId !== activeSelectionPointerId) return false;
        const pointerId = activeSelectionPointerId;
        editableSelectionVertexIndex = -1;
        activeSelectionPointerId = null;
        if (editableSelectionDirty && activeSelection?.surfaceSamples?.length >= 3) {
          activeSelection = rebuildEditableSurfaceSelection(getEditableSelectionVertices(activeSelection), activeSelection.inverted);
        }
        editableSelectionDirty = false;
        try { if (pointerId != null) renderer.domElement.releasePointerCapture(pointerId); } catch {}
        drawSelectionOverlay();
        return true;
      }
      if (!selectionDraft) return false;

      // A surface lasso intentionally remains as a draft between view changes.
      // While it is paused, activeSelectionPointerId is null. Do not treat an
      // unrelated Pen/Airbrush pointer-up as a selection pointer-up, otherwise
      // the paint stroke never reaches its own stop/commit cleanup.
      if (!forceClose) {
        if (activeSelectionPointerId == null) return false;
        if (event && event.pointerId !== activeSelectionPointerId) return false;
      } else if (event && activeSelectionPointerId != null && event.pointerId !== activeSelectionPointerId) {
        return false;
      }

      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const pointerId = activeSelectionPointerId;
      activeSelectionPointerId = null;
      if (pointerId != null) {
        try { renderer.domElement.releasePointerCapture(pointerId); } catch {}
      }
      if (selectionDraft.shape === 'surface' && !forceClose) {
        drawSelectionOverlay();
        return true;
      }
      const draft = selectionDraft;
      if (draft.shape === 'surface') {
        // Editable polygons must be finalised through the editable rebuild path.
        // That path preserves the real user vertices and immediately creates
        // the passive midpoint handles. Using buildSurfaceSelection directly
        // left editableMidpoints empty until the first vertex was moved.
        activeSelection = draft.editablePolygon
          ? rebuildEditableSurfaceSelection(draft.surfaceSamples, Boolean(draft.inverted))
          : buildSurfaceSelection(draft.surfaceSamples);
      }
      else {
        const valid = draft.bounds && draft.bounds.width >= 1 && draft.bounds.height >= 1;
        activeSelection = valid ? { ...draft } : null;
      }
      selectionDraft = null;
      drawSelectionOverlay();
      return true;
    };

    const handleSelectionDoubleClick = (event) => {
      if (surfaceModeRef.current === 'faces') {
        event.preventDefault();
        event.stopPropagation();
        selectConnectedAtPointer(event);
        return;
      }
      if ((activeToolRef.current !== 'marquee_polygon' && activeToolRef.current !== 'marquee_polygon_edit') || !selectionDraft?.surfaceSamples?.length) return;
      finishSelection(event, true);
    };

    // ---- Blender-style face and material selection -------------------------
    const getTriangleCount = (geometry) => {
      const position = geometry?.getAttribute?.('position');
      if (!position) return 0;
      return Math.floor((geometry.index ? geometry.index.count : position.count) / 3);
    };

    const getFaceVertexIndex = (geometry, faceIndex, corner) => (
      geometry.index ? geometry.index.getX((faceIndex * 3) + corner) : (faceIndex * 3) + corner
    );

    const getModelPaintableMeshes = () => {
      const meshes = [];
      currentModelRef.current?.traverse?.((child) => {
        if (!child?.isMesh || child.userData?.voxemeraProjectionDecal) return;
        if (!child.geometry?.getAttribute?.('position')) return;
        meshes.push(child);
      });
      return meshes;
    };

    const getMeshSourceMaterials = (mesh) => {
      const source = mesh?.userData?.voxemeraSourceMaterial || mesh?.material;
      return Array.isArray(source) ? source : [source];
    };

    const getFaceMaterialKey = (mesh, faceIndex) => {
      const materials = getMeshSourceMaterials(mesh);
      const hasMaterialArray = Array.isArray(mesh?.userData?.voxemeraSourceMaterial || mesh?.material);
      const materialIndex = hasMaterialArray ? Math.max(0, getFaceMaterialIndex(mesh.geometry?.groups, faceIndex)) : 0;
      const material = materials[materialIndex] || materials[0] || null;
      return material?.uuid ? `material:${material.uuid}` : `mesh:${mesh.uuid}:${materialIndex}`;
    };

    // Every face, on every mesh, that uses the same original material.
    const collectMaterialFaceMap = (materialKey) => {
      const result = new Map();
      getModelPaintableMeshes().forEach((mesh) => {
        const materials = getMeshSourceMaterials(mesh);
        const hasMaterialArray = Array.isArray(mesh.userData?.voxemeraSourceMaterial || mesh.material);
        const triangleCount = getTriangleCount(mesh.geometry);
        const faces = new Set();
        materials.forEach((material, materialIndex) => {
          const key = material?.uuid ? `material:${material.uuid}` : `mesh:${mesh.uuid}:${materialIndex}`;
          if (key !== materialKey) return;
          const groupFaces = hasMaterialArray
            ? collectFacesWithMaterialIndex(mesh.geometry?.groups, triangleCount, materialIndex)
            : collectFacesWithMaterialIndex([], triangleCount, 0);
          groupFaces.forEach((face) => faces.add(face));
        });
        if (faces.size) result.set(mesh.uuid, faces);
      });
      return result;
    };

    const getEditableFaceMap = () => {
      if (!activeSelection?.selectedFaces) return new Map();
      if (activeSelection.shape === FACE_SELECTION_SHAPE && !activeSelection.inverted) {
        return cloneFaceMap(activeSelection.selectedFaces);
      }
      // Lasso and inverted selections are refined from every face they
      // currently allow, so clicking or growing never discards them.
      const allowedFaces = new Map();
      getModelPaintableMeshes().forEach((mesh) => {
        const allowed = getSelectionAllowedFaces(mesh);
        if (allowed?.size) allowedFaces.set(mesh.uuid, new Set(allowed));
      });
      return allowedFaces;
    };

    const setFaceSelectionMap = (faceMap, kind = 'faces') => {
      activeSelection = countSelectedFaces(faceMap) ? createFaceSelection(faceMap, { kind }) : null;
      selectionDraft = null;
      drawSelectionOverlay();
    };

    let faceSelectDrag = null;

    const pickFaceAtPointer = (event) => {
      const hit = getPaintHit(event);
      if (!hit?.object?.isMesh || !Number.isInteger(hit.faceIndex)) return null;
      return hit;
    };

    const applyPickedFaces = (hit, mode) => {
      const kind = surfaceModeRef.current === 'material' ? 'material' : 'faces';
      let nextMap;
      if (kind === 'material') {
        const materialFaces = collectMaterialFaceMap(getFaceMaterialKey(hit.object, hit.faceIndex));
        nextMap = mode === 'replace' ? new Map() : getEditableFaceMap();
        materialFaces.forEach((faces, meshUuid) => {
          nextMap = applyFacesToSelection(nextMap, meshUuid, faces, mode === 'remove' ? 'remove' : 'add');
        });
      } else {
        nextMap = applyFacesToSelection(getEditableFaceMap(), hit.object.uuid, [hit.faceIndex], mode);
      }
      setFaceSelectionMap(nextMap, kind);
    };

    const handleFaceSelectPointerDown = (event) => {
      if (surfaceModeRef.current === 'paint') return false;
      if (event.button !== 0 || event.ctrlKey || event.altKey) return false;
      event.preventDefault();
      event.stopPropagation();
      const hit = pickFaceAtPointer(event);
      if (!hit) {
        if (!event.shiftKey) setFaceSelectionMap(new Map());
        return true;
      }
      // Click replaces, Shift+click toggles (Blender). Dragging continues in
      // the same direction so faces can be brushed into or out of the set.
      const alreadySelected = getEditableFaceMap().get(hit.object.uuid)?.has(hit.faceIndex);
      const dragMode = event.shiftKey ? (alreadySelected ? 'remove' : 'add') : 'add';
      applyPickedFaces(hit, event.shiftKey ? dragMode : 'replace');
      faceSelectDrag = { pointerId: event.pointerId, mode: dragMode, pendingEvent: null, frame: 0 };
      try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
      return true;
    };

    const handleFaceSelectPointerMove = (event) => {
      if (surfaceModeRef.current !== 'paint' && !faceSelectDrag) {
        hide3dBrushCursor();
        return true;
      }
      if (!faceSelectDrag || event.pointerId !== faceSelectDrag.pointerId) return false;
      event.preventDefault();
      event.stopPropagation();
      // Raycasting dense meshes on every coalesced event is wasteful; pick
      // at most once per frame.
      faceSelectDrag.pendingEvent = { clientX: event.clientX, clientY: event.clientY, pointerType: event.pointerType, pressure: event.pressure };
      if (!faceSelectDrag.frame) {
        faceSelectDrag.frame = window.requestAnimationFrame(() => {
          if (!faceSelectDrag) return;
          faceSelectDrag.frame = 0;
          const pending = faceSelectDrag.pendingEvent;
          faceSelectDrag.pendingEvent = null;
          const hit = pending ? pickFaceAtPointer(pending) : null;
          if (!hit) return;
          const selected = getEditableFaceMap().get(hit.object.uuid)?.has(hit.faceIndex);
          if ((faceSelectDrag.mode === 'add') === Boolean(selected)) return;
          applyPickedFaces(hit, faceSelectDrag.mode);
        });
      }
      return true;
    };

    const finishFaceSelectDrag = (event) => {
      if (!faceSelectDrag || (event && event.pointerId !== faceSelectDrag.pointerId)) return false;
      if (faceSelectDrag.frame) window.cancelAnimationFrame(faceSelectDrag.frame);
      const pointerId = faceSelectDrag.pointerId;
      faceSelectDrag = null;
      try { renderer.domElement.releasePointerCapture(pointerId); } catch {}
      return true;
    };

    const transformSelectionFaces = (transform) => {
      if (!activeSelection?.selectedFaces) return false;
      const source = getEditableFaceMap();
      const next = new Map();
      getModelPaintableMeshes().forEach((mesh) => {
        const faces = source.get(mesh.uuid);
        if (!faces || !faces.size) return;
        const topology = getGeometryTopology(mesh.geometry);
        const result = topology ? transform(faces, topology, mesh) : faces;
        if (result && result.size) next.set(mesh.uuid, result);
      });
      setFaceSelectionMap(next, activeSelection?.kind || 'faces');
      return true;
    };

    // Blender "Select Linked": everything reachable over shared edges.
    // Material selections stay within their material.
    const selectConnectedSurfaceFaces = () => transformSelectionFaces((faces, topology, mesh) => {
      if (activeSelection?.kind !== 'material') return collectConnectedFaces(faces, topology.adjacency);
      const materialKey = getFaceMaterialKey(mesh, faces.values().next().value);
      return collectConnectedFaces(faces, topology.adjacency, (face) => getFaceMaterialKey(mesh, face) === materialKey);
    });
    const growSurfaceSelection = () => transformSelectionFaces((faces, topology) => growFaceSet(faces, topology.adjacency));
    const shrinkSurfaceSelection = () => transformSelectionFaces((faces, topology) => shrinkFaceSet(faces, topology.adjacency));

    const selectConnectedAtPointer = (event) => {
      const hit = pickFaceAtPointer(event);
      if (!hit) return false;
      const topology = getGeometryTopology(hit.object.geometry);
      if (!topology) return false;
      const connected = collectConnectedFaces([hit.faceIndex], topology.adjacency);
      const base = event.shiftKey ? getEditableFaceMap() : new Map();
      setFaceSelectionMap(applyFacesToSelection(base, hit.object.uuid, connected, 'add'), 'faces');
      return true;
    };

    const invertSurfaceSelection = () => {
      if (!activeSelection?.selectedFaces) return false;
      activeSelection = { ...activeSelection, inverted: !activeSelection.inverted };
      drawSelectionOverlay();
      return true;
    };

    const clearSurfaceSelection = () => {
      activeSelection = null;
      selectionDraft = null;
      activeSelectionPointerId = null;
      drawSelectionOverlay();
    };


    const disposeFaceSelectionOverlay = () => {
      [...faceSelectionOverlayGroup.children].forEach((child) => {
        faceSelectionOverlayGroup.remove(child);
        child.geometry?.dispose?.();
      });
    };

    const rebuildFaceSelectionOverlay = () => {
      disposeFaceSelectionOverlay();
      const selection = activeSelection;
      if (!selection || selection.shape !== FACE_SELECTION_SHAPE || !(selection.selectedFaces instanceof Map)) return;
      getModelPaintableMeshes().forEach((mesh) => {
        const geometry = mesh.geometry;
        const position = geometry.getAttribute('position');
        const explicit = selection.selectedFaces.get(mesh.uuid) || new Set();
        if (!selection.inverted && !explicit.size) return;
        const triangleCount = getTriangleCount(geometry);
        const faces = [];
        for (let face = 0; face < triangleCount; face += 1) {
          if (explicit.has(face) !== Boolean(selection.inverted)) faces.push(face);
        }
        if (!faces.length) return;
        // Copy only the selected triangles. Sharing the source position
        // attribute would make disposing the overlay drop the model's buffer.
        const values = new Float32Array(faces.length * 9);
        faces.forEach((face, faceOffset) => {
          for (let corner = 0; corner < 3; corner += 1) {
            const vertex = getFaceVertexIndex(geometry, face, corner);
            const offset = (faceOffset * 9) + (corner * 3);
            values[offset] = position.getX(vertex);
            values[offset + 1] = position.getY(vertex);
            values[offset + 2] = position.getZ(vertex);
          }
        });
        const overlayGeometry = new THREE.BufferGeometry();
        overlayGeometry.setAttribute('position', new THREE.BufferAttribute(values, 3));
        overlayGeometry.computeBoundingSphere();
        const overlay = new THREE.Mesh(overlayGeometry, faceSelectionOverlayMaterial);
        overlay.name = 'Voxemera selected faces';
        overlay.matrixAutoUpdate = false;
        mesh.updateMatrixWorld(true);
        overlay.matrix.copy(mesh.matrixWorld);
        overlay.renderOrder = 900000;
        overlay.frustumCulled = false;
        overlay.raycast = () => {};
        overlay.userData.voxemeraFaceSelectionSource = mesh;
        faceSelectionOverlayGroup.add(overlay);
      });
    };

    const syncFaceSelectionOverlayMatrices = () => {
      faceSelectionOverlayGroup.children.forEach((overlay) => {
        const source = overlay.userData?.voxemeraFaceSelectionSource;
        if (source) overlay.matrix.copy(source.matrixWorld);
      });
    };

    const updateFaceSelectionOverlayStyle = () => {
      // Strong in the selection tools, a faint reminder while painting.
      faceSelectionOverlayMaterial.opacity = surfaceModeRef.current === 'paint' ? 0.12 : 0.34;
    };

    // A texture can only isolate faces whose UV islands do not share texels.
    // Report the faces that would also receive paint on export or reload.
    const scheduleSurfaceSelectionOverlapCheck = () => {
      if (surfaceSelectionOverlapTimer) window.clearTimeout(surfaceSelectionOverlapTimer);
      surfaceSelectionOverlapTimer = 0;
      const selection = activeSelection;
      if (!selection?.selectedFaces) return;
      surfaceSelectionOverlapTimer = window.setTimeout(() => {
        surfaceSelectionOverlapTimer = 0;
        if (activeSelection !== selection) return;
        const meshes = getModelPaintableMeshes().map((mesh) => {
          const geometry = mesh.geometry;
          const uv = geometry.getAttribute('uv');
          const allowed = getSelectionAllowedFaces(mesh) || new Set();
          return {
            uuid: mesh.uuid,
            triangleCount: uv ? getTriangleCount(geometry) : 0,
            isSelected: (face) => allowed.has(face),
            getUvTriangle: (face) => {
              const a = getFaceVertexIndex(geometry, face, 0);
              const b = getFaceVertexIndex(geometry, face, 1);
              const c = getFaceVertexIndex(geometry, face, 2);
              return [uv.getX(a), uv.getY(a), uv.getX(b), uv.getY(b), uv.getX(c), uv.getY(c)];
            },
          };
        });
        const report = findUvOverlapWithSelection(meshes, 512);
        if (activeSelection !== selection) return;
        setSurfaceSelectionInfo((current) => ({ ...current, overlapFaces: report.overlappingFaces }));
      }, 160);
    };

    const publishSurfaceSelectionInfo = () => {
      const selection = activeSelection;
      if (!selection?.selectedFaces) {
        setSurfaceSelectionInfo({ shape: selection?.shape || '', kind: '', count: 0, inverted: false, overlapFaces: null });
        return;
      }
      let count = 0;
      getModelPaintableMeshes().forEach((mesh) => {
        count += (getSelectionAllowedFaces(mesh) || new Set()).size;
      });
      setSurfaceSelectionInfo({
        shape: selection.shape || '',
        kind: selection.kind || (selection.shape === 'surface' ? 'lasso' : 'faces'),
        count,
        inverted: Boolean(selection.inverted),
        overlapFaces: null,
      });
      scheduleSurfaceSelectionOverlapCheck();
    };

    function syncFaceSelectionPresentation() {
      if (!faceSelectionPresentationReady) return;
      const inverted = Boolean(activeSelection?.inverted);
      const mode = surfaceModeRef.current;
      if (activeSelection === lastPresentedSelection && inverted === lastPresentedInverted && mode === lastPresentedSurfaceMode) return;
      const selectionChanged = activeSelection !== lastPresentedSelection || inverted !== lastPresentedInverted;
      lastPresentedSelection = activeSelection;
      lastPresentedInverted = inverted;
      lastPresentedSurfaceMode = mode;
      updateFaceSelectionOverlayStyle();
      if (!selectionChanged) return;
      rebuildFaceSelectionOverlay();
      publishSurfaceSelectionInfo();
    }
    faceSelectionPresentationReady = true;

    // Fill every selected face with the current colour (or PBR material) as
    // one editable, undoable fill that stays restricted to those faces.
    const fillSelectedSurfaceFaces = () => {
      const session = activePaintSession;
      if (!activeSelection?.selectedFaces || !session?.canvas) return false;
      const width = session.canvas.width;
      const height = session.canvas.height;
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskContext = maskCanvas.getContext('2d', { alpha: true });
      if (!maskContext) return false;
      maskContext.fillStyle = '#ffffff';
      maskContext.strokeStyle = '#ffffff';
      // Stroking the triangle edges as well closes the antialiased hairline
      // gaps that would otherwise show between neighbouring filled faces.
      maskContext.lineWidth = 1.5;
      maskContext.lineJoin = 'round';
      let filledFaces = 0;
      getModelPaintableMeshes().forEach((mesh) => {
        const geometry = mesh.geometry;
        const uv = geometry.getAttribute('uv');
        const allowed = getSelectionAllowedFaces(mesh);
        if (!uv || !allowed?.size) return;
        maskContext.beginPath();
        allowed.forEach((face) => {
          const a = getFaceVertexIndex(geometry, face, 0);
          const b = getFaceVertexIndex(geometry, face, 1);
          const c = getFaceVertexIndex(geometry, face, 2);
          if (a >= uv.count || b >= uv.count || c >= uv.count) return;
          maskContext.moveTo(uv.getX(a) * width, uv.getY(a) * height);
          maskContext.lineTo(uv.getX(b) * width, uv.getY(b) * height);
          maskContext.lineTo(uv.getX(c) * width, uv.getY(c) * height);
          maskContext.closePath();
          filledFaces += 1;
        });
        maskContext.fill();
        maskContext.stroke();
      });
      if (!filledFaces) return false;

      const stage = paintConfigRef.current.paintStageRef?.current;
      const brushConfig = stage?.getProjectionBrushConfig?.() || {};
      const fillHex = String(brushConfig.strokeColor || brushConfig.color || '#000000');
      const activePbrResource = getRuntimeActivePbrBrushResource();
      const committed = commitUvFloodFill({
        maskCanvas,
        materialSettings: { ...materialSettingsRef.current },
        forceSolidSelectionCoverage: true,
        solidFillHex: fillHex,
        ...(activePbrResource ? {
          isPbr: true,
          brushConfig: {
            ...brushConfig,
            pbrSetKey: activePbrResource.key || '',
            pbrScalePercent: Math.max(10, Math.min(800, Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
            pbrAngleDegrees: ((Number(pbrBrushStateRef.current?.angleDegrees) || 0) % 360 + 360) % 360,
          },
        } : {}),
      });
      if (!committed) return false;
      const projectionCount = Array.isArray(runtimeRef.current?.projectionStrokes)
        ? runtimeRef.current.projectionStrokes.length
        : 0;
      projectionStrokeCountRef.current = projectionCount;
      scheduleProjectionPaintSave();
      if (viewportDisplayModeRef.current === 'texture') {
        window.setTimeout(() => drawCurrentTextureView(textureViewCanvasRef.current), 0);
      }
      renderer.render(scene, camera);
      const callback = paintConfigRef.current.onProjectionStrokeCommitted;
      if (typeof callback === 'function') callback(projectionCount);
      return true;
    };

    // ---- Navigation -------------------------------------------------------
    const frameWorldBox = (box) => {
      if (!box || box.isEmpty()) return false;
      const centre = box.getCenter(new THREE.Vector3());
      const radius = Math.max(box.getSize(new THREE.Vector3()).length() * 0.5, 0.0001);
      const direction = camera.position.clone().sub(controls.target);
      if (direction.lengthSq() < 1e-12) direction.set(0, 0, 1);
      direction.normalize();
      const verticalFov = THREE.MathUtils.degToRad(camera.fov || 45);
      const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(0.01, camera.aspect || 1));
      const distance = (radius / Math.sin(Math.min(verticalFov, horizontalFov) / 2)) * 1.1;
      controls.target.copy(centre);
      camera.position.copy(centre).addScaledVector(direction, distance);
      camera.near = Math.min(camera.near, Math.max(distance / 1000, 0.0001));
      camera.updateProjectionMatrix();
      controls.update();
      updateOrbitPivotMarker();
      hide3dBrushCursor();
      return true;
    };

    const frameSurfaceSelection = () => {
      const model = currentModelRef.current;
      if (!model) return false;
      if (!activeSelection?.selectedFaces) {
        frameObject(camera, controls, model);
        updateOrbitPivotMarker();
        return true;
      }
      const box = new THREE.Box3();
      const vertex = new THREE.Vector3();
      getModelPaintableMeshes().forEach((mesh) => {
        const allowed = getSelectionAllowedFaces(mesh);
        if (!allowed?.size) return;
        const position = mesh.geometry.getAttribute('position');
        mesh.updateMatrixWorld(true);
        allowed.forEach((face) => {
          for (let corner = 0; corner < 3; corner += 1) {
            vertex.fromBufferAttribute(position, getFaceVertexIndex(mesh.geometry, face, corner)).applyMatrix4(mesh.matrixWorld);
            box.expandByPoint(vertex);
          }
        });
      });
      return frameWorldBox(box);
    };

    const VIEW_PRESET_DIRECTIONS = {
      front: [0, 0, 1],
      back: [0, 0, -1],
      right: [1, 0, 0],
      left: [-1, 0, 0],
      // A tiny forward offset keeps OrbitControls away from its pole.
      top: [0, 1, 0.0001],
      bottom: [0, -1, 0.0001],
    };

    const setViewPreset = (name) => {
      const direction = VIEW_PRESET_DIRECTIONS[name];
      if (!direction || !currentModelRef.current) return false;
      const distance = Math.max(camera.position.distanceTo(controls.target), 0.0001);
      camera.up.set(0, 1, 0);
      camera.position.copy(controls.target).add(new THREE.Vector3(...direction).normalize().multiplyScalar(distance));
      camera.lookAt(controls.target);
      camera.updateMatrixWorld(true);
      controls.update();
      updateOrbitPivotMarker();
      hide3dBrushCursor();
      return true;
    };

    // Blender default: middle drag orbits, Shift+middle drag pans. Holding
    // Ctrl keeps the original left/middle/right mapping.
    let keyboardNavigationActive = false;
    let middleButtonNavigationPointerId = null;

    const beginMiddleButtonNavigation = (event) => {
      if (event.button !== 1 || keyboardNavigationActive) return false;
      event.preventDefault();
      if (activePaintStroke) return true;
      controls.mouseButtons.MIDDLE = event.shiftKey ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
      controls.enabled = true;
      middleButtonNavigationPointerId = event.pointerId;
      hide3dBrushCursor();
      return true;
    };

    const endMiddleButtonNavigation = (event) => {
      if (middleButtonNavigationPointerId == null || (event && event.pointerId !== middleButtonNavigationPointerId)) return false;
      middleButtonNavigationPointerId = null;
      controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
      controls.enabled = keyboardNavigationActive;
      return true;
    };

    // Wheel zooms towards the surface under the cursor, so the point being
    // inspected stays put instead of drifting towards the orbit centre.
    const handleViewportWheelZoom = (event) => {
      if (controls.enabled || event.defaultPrevented || !currentModelRef.current) return;
      event.preventDefault();
      const rect = renderer.domElement.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const deltaPixels = event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * rect.height
          : event.deltaY;
      let scale = Math.exp(Math.max(-400, Math.min(400, deltaPixels)) * 0.0012);
      const currentDistance = camera.position.distanceTo(controls.target);
      const minimumDistance = Math.max(Number(controls.minDistance) || 0.0001, 0.0001);
      const maximumDistance = Math.max(Number(controls.maxDistance) || currentDistance * 100, minimumDistance);
      const nextDistance = THREE.MathUtils.clamp(currentDistance * scale, minimumDistance, maximumDistance);
      scale = currentDistance > 0 ? nextDistance / currentDistance : 1;
      if (Math.abs(scale - 1) < 1e-6) return;

      pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointerNdc.y = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
      raycaster.setFromCamera(pointerNdc, camera);
      const hit = raycaster.intersectObject(currentModelRef.current, true).find((candidate) => (
        candidate?.object?.isMesh
        && candidate.object.visible !== false
        && !candidate.object.userData?.voxemeraProjectionDecal
      ));
      let pivot = hit?.point?.clone?.() || null;
      if (!pivot) {
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camera.getWorldDirection(new THREE.Vector3()), controls.target);
        pivot = raycaster.ray.intersectPlane(plane, new THREE.Vector3()) || controls.target.clone();
      }
      camera.position.sub(pivot).multiplyScalar(scale).add(pivot);
      controls.target.sub(pivot).multiplyScalar(scale).add(pivot);
      camera.updateMatrixWorld(true);
      controls.update();
      updateOrbitPivotMarker();
      hide3dBrushCursor();
    };

    const isTypingTarget = (target) => {
      const tag = String(target?.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(target?.isContentEditable);
    };

    const handleViewportNavigationKey = (event) => {
      if (isTypingTarget(event.target) || viewportDisplayModeRef.current !== '3d') return false;
      if (!container.isConnected || !container.offsetParent) return false;
      const presets = {
        Numpad1: event.ctrlKey ? 'back' : 'front',
        Numpad3: event.ctrlKey ? 'left' : 'right',
        Numpad7: event.ctrlKey ? 'bottom' : 'top',
      };
      if (presets[event.code]) {
        event.preventDefault();
        setViewPreset(presets[event.code]);
        return true;
      }
      if (event.code === 'NumpadDecimal') {
        event.preventDefault();
        frameSurfaceSelection();
        return true;
      }
      return false;
    };

    const applySurfaceMode = (mode) => {
      const nextMode = mode === 'faces' || mode === 'material' ? mode : 'paint';
      surfaceModeRef.current = nextMode;
      renderer.domElement.style.cursor = nextMode === 'paint' ? (controls.enabled ? 'grab' : 'default') : 'crosshair';
      if (nextMode !== 'paint') hide3dBrushCursor();
      drawSelectionOverlay();
    };

    const resetSurfaceSelection = () => {
      finishFaceSelectDrag(null);
      clearSurfaceSelection();
    };

    // ---- Live paintable-surface mask ----------------------------------------
    // Rendered once per camera view (and cached) from the stroke's own
    // projection camera: white where the model is visible, not at a grazing
    // angle and, when a selection exists, belongs to an allowed face.
    let surfacePreviewMaskCache = null;
    const surfacePreviewSelectionIds = new WeakMap();
    let surfacePreviewSelectionCounter = 0;
    const SURFACE_PREVIEW_MASK_MAX_PIXELS = 4 * 1024 * 1024;
    const surfacePreviewDepthMaterial = new THREE.MeshBasicMaterial({ colorWrite: false, side: THREE.DoubleSide });
    const surfacePreviewCoverageMaterial = new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vViewNormal;
        varying vec3 vViewPosition;
        void main() {
          vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
          vViewPosition = -viewPosition.xyz;
          vViewNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * viewPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vViewNormal;
        varying vec3 vViewPosition;
        void main() {
          // Matches the projection commit, which fades paint out between
          // incidence 0.22 and 0.52.
          float incidence = abs(dot(normalize(vViewNormal), normalize(vViewPosition)));
          if (incidence < 0.3) discard;
          gl_FragColor = vec4(1.0);
        }
      `,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      depthFunc: THREE.LessEqualDepth,
    });

    const getSurfacePreviewSelectionKey = (selection) => {
      if (!selection?.selectedFaces) return 'none';
      if (!surfacePreviewSelectionIds.has(selection)) {
        surfacePreviewSelectionCounter += 1;
        surfacePreviewSelectionIds.set(selection, surfacePreviewSelectionCounter);
      }
      return `${surfacePreviewSelectionIds.get(selection)}:${selection.inverted ? 1 : 0}`;
    };

    const buildAllowedFaceMaskGroup = () => {
      const group = new THREE.Group();
      getModelPaintableMeshes().forEach((mesh) => {
        const allowed = getSelectionAllowedFaces(mesh);
        if (!allowed?.size) return;
        const geometry = mesh.geometry;
        const position = geometry.getAttribute('position');
        const normal = geometry.getAttribute('normal');
        const positions = new Float32Array(allowed.size * 9);
        const normals = new Float32Array(allowed.size * 9);
        let faceOffset = 0;
        allowed.forEach((face) => {
          for (let corner = 0; corner < 3; corner += 1) {
            const vertex = getFaceVertexIndex(geometry, face, corner);
            const offset = (faceOffset * 9) + (corner * 3);
            positions[offset] = position.getX(vertex);
            positions[offset + 1] = position.getY(vertex);
            positions[offset + 2] = position.getZ(vertex);
            if (normal) {
              normals[offset] = normal.getX(vertex);
              normals[offset + 1] = normal.getY(vertex);
              normals[offset + 2] = normal.getZ(vertex);
            }
          }
          faceOffset += 1;
        });
        const maskGeometry = new THREE.BufferGeometry();
        maskGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        if (normal) maskGeometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        else maskGeometry.computeVertexNormals();
        const maskMesh = new THREE.Mesh(maskGeometry, surfacePreviewCoverageMaterial);
        maskMesh.matrixAutoUpdate = false;
        mesh.updateMatrixWorld(true);
        maskMesh.matrix.copy(mesh.matrixWorld);
        maskMesh.frustumCulled = false;
        group.add(maskMesh);
      });
      return group;
    };

    const buildSurfacePreviewMask = (projectionCamera, fullWidth, fullHeight) => {
      const model = currentModelRef.current;
      if (!model || !projectionCamera || !fullWidth || !fullHeight) return null;
      projectionCamera.updateMatrixWorld(true);
      model.updateMatrixWorld(true);
      const maskScale = Math.min(1, Math.sqrt(SURFACE_PREVIEW_MASK_MAX_PIXELS / Math.max(1, fullWidth * fullHeight)));
      const width = Math.max(1, Math.round(fullWidth * maskScale));
      const height = Math.max(1, Math.round(fullHeight * maskScale));
      const round = (values) => Array.from(values, (value) => value.toFixed(6)).join(',');
      const key = [
        width,
        height,
        round(projectionCamera.matrixWorld.elements),
        round(projectionCamera.projectionMatrix.elements),
        round(model.matrixWorld.elements),
        getModelPaintableMeshes().map((mesh) => mesh.geometry.uuid).join(','),
        getSurfacePreviewSelectionKey(activeSelection),
      ].join('|');
      if (surfacePreviewMaskCache?.key === key) return surfacePreviewMaskCache.canvas;

      const target = new THREE.WebGLRenderTarget(width, height, { depthBuffer: true });
      const selectionGroup = activeSelection?.selectedFaces ? buildAllowedFaceMaskGroup() : null;
      const hiddenSceneChildren = [];
      const previousTarget = renderer.getRenderTarget();
      const previousAutoClear = renderer.autoClear;
      const previousClearColor = renderer.getClearColor(new THREE.Color());
      const previousClearAlpha = renderer.getClearAlpha();
      const previousBackground = scene.background;
      const previousOverride = scene.overrideMaterial;
      const previousShadowAutoUpdate = renderer.shadowMap.autoUpdate;
      const pixels = new Uint8Array(width * height * 4);
      try {
        scene.children.forEach((child) => {
          if (child === model || !child.visible) return;
          child.visible = false;
          hiddenSceneChildren.push(child);
        });
        renderer.shadowMap.autoUpdate = false;
        scene.background = null;
        renderer.setRenderTarget(target);
        renderer.setClearColor(0x000000, 0);
        renderer.autoClear = false;
        renderer.clear(true, true, true);
        scene.overrideMaterial = surfacePreviewDepthMaterial;
        renderer.render(scene, projectionCamera);
        if (selectionGroup) {
          scene.overrideMaterial = previousOverride;
          renderer.render(selectionGroup, projectionCamera);
        } else {
          scene.overrideMaterial = surfacePreviewCoverageMaterial;
          renderer.render(scene, projectionCamera);
        }
        renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      } finally {
        scene.overrideMaterial = previousOverride;
        scene.background = previousBackground;
        hiddenSceneChildren.forEach((child) => { child.visible = true; });
        renderer.shadowMap.autoUpdate = previousShadowAutoUpdate;
        renderer.autoClear = previousAutoClear;
        renderer.setClearColor(previousClearColor, previousClearAlpha);
        renderer.setRenderTarget(previousTarget);
        target.dispose();
        selectionGroup?.children.forEach((child) => child.geometry?.dispose?.());
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return null;
      const image = context.createImageData(width, height);
      for (let row = 0; row < height; row += 1) {
        const sourceRow = (height - 1 - row) * width * 4;
        const targetRow = row * width * 4;
        for (let column = 0; column < width; column += 1) {
          const covered = pixels[sourceRow + (column * 4)] > 127;
          const index = targetRow + (column * 4);
          image.data[index] = 255;
          image.data[index + 1] = 255;
          image.data[index + 2] = 255;
          image.data[index + 3] = covered ? 255 : 0;
        }
      }
      context.putImageData(image, 0, 0);
      // Grow by one mask pixel. The committed projection clips exactly on
      // the GPU; this only prevents a hairline gap along the mask edge.
      const source = document.createElement('canvas');
      source.width = width;
      source.height = height;
      source.getContext('2d')?.drawImage(canvas, 0, 0);
      [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([dx, dy]) => {
        context.drawImage(source, dx, dy);
      });
      surfacePreviewMaskCache = { key, canvas };
      return canvas;
    };

    // Built just after the first dab is on screen so pressing the pen never
    // waits for the mask; dabs drawn in the meantime are masked retroactively.
    const attachIncrementalPenSurfaceMask = (state, projectionCamera) => {
      if (!state || state.finished) return;
      const fullWidth = previewCanvas.width;
      const fullHeight = previewCanvas.height;
      state.surfaceMaskTimer = window.setTimeout(() => {
        state.surfaceMaskTimer = 0;
        if (state.finished) return;
        try {
          state.surfaceMask = buildSurfacePreviewMask(projectionCamera, fullWidth, fullHeight);
        } catch {
          state.surfaceMask = null;
        }
        state.surfaceMaskResolved = true;
        if (!state.surfaceMask) {
          state.dirtyRect = null;
          return;
        }
        // Mask everything stamped so far.
        state.dirtyRect = {
          minimumX: -previewOverscan,
          minimumY: -previewOverscan,
          maximumX: (fullWidth / previewRenderScale) - previewOverscan,
          maximumY: (fullHeight / previewRenderScale) - previewOverscan,
        };
        applyIncrementalPenSurfaceMask(state);
      }, 0);
    };

    const setOrbitTargetFromPointer = (event) => {
      if (!event.altKey || event.button !== 0) return false;
      event.preventDefault();
      event.stopPropagation();

      const model = currentModelRef.current;
      if (!model) return true;

      const rect = renderer.domElement.getBoundingClientRect();
      pointerNdc.x = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
      pointerNdc.y = -(((event.clientY - rect.top) / Math.max(1, rect.height)) * 2 - 1);
      raycaster.setFromCamera(pointerNdc, camera);

      const hit = raycaster.intersectObject(model, true).find((candidate) => (
        candidate?.object?.visible !== false
        && candidate?.point
      ));
      if (!hit?.point) return true;

      controls.target.copy(hit.point);
      controls.update();
      updateOrbitPivotMarker();
      updateDecalTransformOverlay();
      renderer.render(scene, camera);
      hide3dBrushCursor();
      return true;
    };

    const handleSelectionContextMenu = (event) => {
      if (event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        const defaultTarget = runtimeRef.current?.defaultOrbitTarget;
        if (defaultTarget) {
          controls.target.copy(defaultTarget);
          controls.update();
          updateOrbitPivotMarker();
          renderer.render(scene, camera);
        }
        hide3dBrushCursor();
        return;
      }
      if (!activeSelection) return;
      event.preventDefault();
      event.stopPropagation();
      activeSelection = { ...activeSelection, inverted: !activeSelection.inverted };
      drawSelectionOverlay();
    };
    const build3dTextDecalCanvas = (text, settings = {}) => {
      const safeText = String(text || '').replace(/\r/g, '');
      const lines = safeText.split('\n');
      const fontSize = Math.max(6, Math.min(400, Number(settings.fontSize) || 48));
      const lineHeight = Math.max(0.8, Math.min(3, Number(settings.lineHeight) || 1.2));
      const letterSpacing = Math.max(-20, Math.min(100, Number(settings.letterSpacing) || 0));
      const fontFamily = String(settings.fontFamily || 'Arial').replace(/["']/g, '');
      const fontWeight = settings.bold ? '700' : '400';
      const fontStyle = settings.italic ? 'italic' : 'normal';
      const padding = Math.max(8, Math.ceil(fontSize * 0.25));
      const measureCanvas = document.createElement('canvas');
      const measureContext = measureCanvas.getContext('2d');
      if (!measureContext) return null;
      measureContext.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
      const measureLine = (line) => {
        const characters = Array.from(line || ' ');
        return Math.max(1, characters.reduce((width, character, index) => (
          width + measureContext.measureText(character).width + (index < characters.length - 1 ? letterSpacing : 0)
        ), 0));
      };
      const widths = lines.map(measureLine);
      const contentWidth = Math.max(1, ...widths);
      const contentHeight = Math.max(1, Math.ceil(lines.length * fontSize * lineHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(contentWidth + (padding * 2)));
      canvas.height = Math.max(1, Math.ceil(contentHeight + (padding * 2)));
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
      context.textBaseline = 'top';
      context.fillStyle = settings.color || '#000000';
      context.globalAlpha = Math.max(0.01, Math.min(1, Number(settings.opacity) || 1));
      const align = ['left', 'center', 'right'].includes(settings.align) ? settings.align : 'left';
      lines.forEach((line, lineIndex) => {
        const chars = Array.from(line || ' ');
        const lineWidth = widths[lineIndex] || 1;
        let x = padding;
        if (align === 'center') x += (contentWidth - lineWidth) * 0.5;
        if (align === 'right') x += contentWidth - lineWidth;
        const y = padding + (lineIndex * fontSize * lineHeight);
        chars.forEach((character, characterIndex) => {
          context.fillText(character, x, y);
          const characterWidth = context.measureText(character).width;
          if (settings.underline) {
            const underlineY = y + (fontSize * 1.02);
            context.fillRect(x, underlineY, Math.max(1, characterWidth), Math.max(1, fontSize * 0.055));
          }
          x += characterWidth + (characterIndex < chars.length - 1 ? letterSpacing : 0);
        });
      });
      context.globalAlpha = 1;
      return canvas;
    };

    const create3dTextAtPointer = (event) => {
      if (String(activeToolRef.current || '') !== 'text' || event.ctrlKey || event.button !== 0) return false;
      const clicked = getDecalLayerAtPointer(event);
      const clickedLayer = clicked?.layer?.decalData?.isText ? clicked.layer : null;
      const hit = clicked?.hit || getPaintHit(event);
      if (!hit?.point) return true;

      const currentTextObject = clickedLayer?.textObject && typeof clickedLayer.textObject === 'object'
        ? clickedLayer.textObject
        : null;
      const initialText = currentTextObject?.text || 'Placeholder Text';
      const entered = window.prompt('Text', initialText);
      if (entered == null || !String(entered).length) return true;

      const toolbarSettings = textSettingsRef.current || {};
      const settings = currentTextObject
        ? { ...toolbarSettings, ...currentTextObject }
        : toolbarSettings;
      const stage = paintConfigRef.current.paintStageRef?.current;
      const brushConfig = stage?.getProjectionBrushConfig?.() || {};
      const currentDecal = clickedLayer?.decalData && typeof clickedLayer.decalData === 'object'
        ? clickedLayer.decalData
        : {};
      const activePbrResource = getRuntimeActivePbrBrushResource();
      const existingTextUsesPbr = currentDecal.isPbr === true;
      const nextTextUsesPbr = existingTextUsesPbr || Boolean(activePbrResource);
      const nextTextPbrSetKey = String(
        currentDecal.pbrSetKey
        || (nextTextUsesPbr ? (activePbrResource?.key || '') : '')
        || ''
      );
      const nextTextPbrScalePercent = Math.max(
        10,
        Math.min(
          800,
          Number(currentDecal.pbrScalePercent)
          || Number(pbrBrushStateRef.current?.scalePercent)
          || DEFAULT_PBR_TEXTURE_SCALE,
        ),
      );
      const nextTextPbrAngleDegrees = ((
        Number.isFinite(Number(currentDecal.pbrAngleDegrees))
          ? Number(currentDecal.pbrAngleDegrees)
          : (Number(pbrBrushStateRef.current?.angleDegrees) || 0)
      ) % 360 + 360) % 360;
      const nextTextMaterialSettings = {
        ...(currentDecal.materialSettings && typeof currentDecal.materialSettings === 'object'
          ? currentDecal.materialSettings
          : {}),
        ...(materialSettingsRef.current && typeof materialSettingsRef.current === 'object'
          ? materialSettingsRef.current
          : {}),
      };
      const renderSettings = {
        ...settings,
        color: settings.color || brushConfig.color || brushConfig.strokeColor || '#000000',
        opacity: Number.isFinite(Number(settings.opacity))
          ? Number(settings.opacity)
          : Math.max(0.01, Math.min(1, Number(brushConfig.opacity) || 1)),
      };
      const canvas = build3dTextDecalCanvas(entered, renderSettings);
      if (!canvas) return true;
      const imageDataUrl = canvas.toDataURL('image/png');
      const textObject = {
        ...(currentTextObject || {}),
        text: String(entered),
        fontFamily: String(renderSettings.fontFamily || 'Arial'),
        fontSize: Math.max(6, Math.min(400, Number(renderSettings.fontSize) || 48)),
        lineHeight: Math.max(0.8, Math.min(3, Number(renderSettings.lineHeight) || 1.2)),
        letterSpacing: Math.max(-20, Math.min(100, Number(renderSettings.letterSpacing) || 0)),
        align: ['left', 'center', 'right'].includes(renderSettings.align) ? renderSettings.align : 'left',
        bold: renderSettings.bold === true,
        italic: renderSettings.italic === true,
        underline: renderSettings.underline === true,
        color: renderSettings.color || '#000000',
        opacity: Math.max(0.01, Math.min(1, Number(renderSettings.opacity) || 1)),
        is3dText: true,
      };

      if (clickedLayer) {
        const layerId = String(clickedLayer.id || '');
        if (typeof onUpdateTextLayerRef.current === 'function') {
          onUpdateTextLayerRef.current(layerId, textObject);
        }
        if (typeof onUpdateDecalLayerRef.current === 'function') {
          onUpdateDecalLayerRef.current(layerId, {
            ...currentDecal,
            imageDataUrl,
            imageWidth: canvas.width,
            imageHeight: canvas.height,
            isText: true,
            isPbr: nextTextUsesPbr,
            pbrSetKey: nextTextPbrSetKey,
            pbrScalePercent: nextTextPbrScalePercent,
            pbrAngleDegrees: nextTextPbrAngleDegrees,
            materialSettings: nextTextMaterialSettings,
          });
        }
        if (typeof onSelectLayerRef.current === 'function') onSelectLayerRef.current(layerId);
      } else if (typeof onCreateTextLayerRef.current === 'function') {
        const normal = hit.normal?.clone?.() || camera.position.clone().sub(hit.point).normalize();
        if (!Number.isFinite(normal.lengthSq()) || normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
        const decalData = {
          imageDataUrl,
          imageWidth: canvas.width,
          imageHeight: canvas.height,
          position: hit.point.toArray(),
          normal: normal.normalize().toArray(),
          surfaceMeshUuid: String(hit.object?.uuid || ''),
          baseWorldSize: getModelDecalBaseWorldSize(),
          scale: 1,
          rotation: 0,
          flipX: false,
          flipY: false,
          isText: true,
          isPbr: nextTextUsesPbr,
          pbrSetKey: nextTextPbrSetKey,
          pbrScalePercent: nextTextPbrScalePercent,
          pbrAngleDegrees: nextTextPbrAngleDegrees,
          materialSettings: nextTextMaterialSettings,
        };
        const layerId = onCreateTextLayerRef.current(textObject, decalData);
        if (layerId && typeof onSelectLayerRef.current === 'function') onSelectLayerRef.current(String(layerId));
      }

      event.preventDefault();
      event.stopPropagation();
      return true;
    };

    const handlePaintPointerDown = (event) => {
      if (beginMiddleButtonNavigation(event)) return;
      if (setOrbitTargetFromPointer(event)) return;
      if (handleFaceSelectPointerDown(event)) return;
      if (create3dTextAtPointer(event)) return;
      if (startDecalLayerDrag(event)) return;
      if (handleSelectionPointerDown(event)) return;
      if (event.ctrlKey || event.button !== 0) return;
      if (activeToolRef.current === 'paint') {
        // Physical Paint Tool: the shared GPU engine paints the model surface.
        if (isPhysicalPaint3dAvailable() && physicalPaint3d.pointerDown(event)) {
          event.preventDefault();
          event.stopPropagation();
          setShowUvColours(false);
          hide3dBrushCursor();
        }
        return;
      }
      const stage = paintConfigRef.current.paintStageRef?.current;
      const brushConfig = stage?.getProjectionBrushConfig?.() || {};
      if (brushConfig.activeTool === 'bucket' || activeToolRef.current === 'bucket') {
        event.preventDefault();
        event.stopPropagation();
        setShowUvColours(false);
        performUvBucketFill(event, brushConfig);
        return;
      }
      const isEraser = brushConfig.activeTool === 'eraser' || activeToolRef.current === 'eraser';
      const isImportedBrush = brushConfig.activeTool === 'pen' && Boolean(brushConfig.importedBrush);
      const activeBrushPreset = String(brushConfig.activeBrushPreset || 'pen');
      const isNormalPen = brushConfig.activeTool === 'pen'
        && activeBrushPreset === 'pen'
        && !brushConfig.importedBrush;
      const isAirbrush = brushConfig.activeTool === 'pen'
        && activeBrushPreset === 'airbrush'
        && !brushConfig.importedBrush;
      const isPencil = brushConfig.activeTool === 'pen'
        && activeBrushPreset === 'pencil'
        && !brushConfig.importedBrush;
      if (!isEraser && !isNormalPen && !isImportedBrush && !isAirbrush && !isPencil) return;
      const hit = getPaintHit(event);
            if (!hit) {
                return;
      }
      const activePbrResource = (isNormalPen || isImportedBrush)
        ? getRuntimeActivePbrBrushResource()
        : null;
      const startingSurfaceLock = lock3dPaintToStartingSurfaceRef.current
        ? buildCentreHitSurfaceLock(hit)
        : null;

      event.preventDefault();
      event.stopPropagation();
      try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
      setShowUvColours(false);
      const rect = renderer.domElement.getBoundingClientRect();
      const previewPoint = getPreviewPointerPoint(event);
      const requestedBrushSize = Math.max(1, Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const strokeBrushConfig = {
        ...brushConfig,
        color: brushConfig.color || brushConfig.strokeColor || '#000000',
        size: requestedBrushSize,
        brushSize: requestedBrushSize,
        worldBrushDiameter: getModelBrushWorldDiameter(requestedBrushSize),
        pbrSetKey: activePbrResource?.key || '',
        pbrScalePercent: Math.max(10, Math.min(800, Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
        pbrAngleDegrees: ((Number(pbrBrushStateRef.current?.angleDegrees) || 0) % 360 + 360) % 360,
        importedBrushStrokeState: {},
        strokeSeed: Math.floor(performance.now() * 1000) ^ Math.floor(Math.random() * 0x7fffffff),
        ...(isNormalPen ? { projectionOpacityMode: 'material' } : {}),
      };
      camera.updateMatrixWorld(true);
      camera.updateProjectionMatrix();
      previewPoint.brushSize = isAirbrush
        ? getAirbrushScreenDiameter(strokeBrushConfig, hit, rect)
        : getBrushScreenSize(strokeBrushConfig, hit, rect);

      if (isImportedBrush) {
        const projectedImportedBrushSize = Math.max(
          0.5,
          Number(previewPoint.brushSize) || requestedBrushSize,
        );
        strokeBrushConfig.configured3dBrushSize = requestedBrushSize;
        strokeBrushConfig.size = projectedImportedBrushSize;
        strokeBrushConfig.brushSize = projectedImportedBrushSize;
      }

      if (isAirbrush) {
        hide3dBrushCursor();
      } else {
        position3dBrushCursor(previewPoint, previewPoint.brushSize);
      }
      setPreviewCanvasVisible(!Boolean(activePbrResource));
      if (!activePbrResource) {
        disposeLivePbrPreviewStroke();
      }

      const basePreviewScale = isAirbrush
        ? get3dAirbrushProjectionScale(hit, rect)
        : Math.min(window.devicePixelRatio || 1, 2);

      // The visible viewport is only the centre crop of the projection. For an
      // airbrush, size the border from the actual distance-adjusted spray and
      // particle radius. A fixed cap exposes the square projection boundary
      // when the same model-space spray becomes very large on screen.
      const strokeOverscan = isAirbrush
        ? get3dAirbrushRequiredOverscan(strokeBrushConfig, previewPoint, basePreviewScale)
        : Math.max(32, Math.ceil((Number(previewPoint.brushSize) || requestedBrushSize) * 1.5));
      configurePreviewResolution(rect.width, rect.height, basePreviewScale, strokeOverscan);
      clearPreviewCanvas();
      if (isEraser) {
        const activeLayerId = String(getCurrentLayerState().activeLayerId || '');
        strokeBrushConfig.eraserPreviewSource = captureActiveLayerErasedPreview(activeLayerId);
      }

      // Put the first normal-pen dab on the visible overlay immediately. The old
      // path waited until after projection-camera creation and colour sampling,
      // which made the click feel roughly 100 ms late on dense models.
      const incrementalPen = isNormalPen
        ? beginIncrementalPenPreview(strokeBrushConfig, previewPoint, event.timeStamp)
        : null;
      if (isEraser) {
        drawPreviewRoundDab(
          previewPoint,
          strokeBrushConfig,
          previewPoint.pressure,
        );
      } else if (isPencil) {
        drawPreviewPencilStroke({
          previewPoints: [previewPoint],
          brushConfig: strokeBrushConfig,
        });
      }

      const projectionCamera = createOverscannedProjectionCamera(
        camera,
        rect.width,
        rect.height,
        strokeOverscan,
      );
      if (incrementalPen) attachIncrementalPenSurfaceMask(incrementalPen, projectionCamera);

      activePaintStroke = {
        pointerId: event.pointerId,
        layerId: String(getCurrentLayerState().activeLayerId || ''),
        lastPreviewPoint: previewPoint,
        previewPoints: [previewPoint],
        brushConfig: strokeBrushConfig,
        hitSamples: isAirbrush ? [] : [hit],
        lastHit: hit,
        lastAirbrushRaycastAt: isAirbrush ? performance.now() : 0,
        isImportedBrush,
        isAirbrush,
        isPencil,
        isEraser,
        isPbr: Boolean(activePbrResource),
        airbrushWorldDiameter: isAirbrush
          ? strokeBrushConfig.worldBrushDiameter
          : null,
        airbrushWorldPoints: isAirbrush && hit?.point
          ? [hit.point.clone()]
          : [],
        airbrushStrokeId: `3d-airbrush-${event.pointerId}-${Date.now()}-${Math.floor(Math.random() * 0x7fffffff)}`,
        airbrushAnimationFrame: 0,
        airbrushLastFrameTime: 0,
        airbrushLastLiveRenderTime: 0,
        airbrushLastRenderedPoint: previewPoint,
        airbrushStationaryOrbitAngle: 0,
        importedDabIndex: 0,
        importedPoints: [],
        materialSettings: { ...materialSettingsRef.current },
        allowedMeshUuid: startingSurfaceLock?.allowedMeshUuid || null,
        allowedFaceIndicesByMesh: startingSurfaceLock?.allowedFaceIndicesByMesh || null,
        startingSurfaceMesh: startingSurfaceLock?.startingMesh || null,
        startingSurfaceFaceIndex: Number.isInteger(startingSurfaceLock?.startingFaceIndex)
          ? startingSurfaceLock.startingFaceIndex
          : null,
        surfaceFacesReady: Boolean(startingSurfaceLock?.surfaceFacesReady),
        startingSurfaceRayDistance: Number(startingSurfaceLock?.startingRayDistance),
        lastAcceptedSurfaceRayDistance: Number(startingSurfaceLock?.lastAcceptedRayDistance),
        startingSurfaceWorldPoint: startingSurfaceLock?.startingWorldPoint?.clone?.() || null,
        surfaceDepthTolerance: lock3dPaintToStartingSurfaceRef.current
          ? Math.max(
              getModelBrushWorldDiameter(requestedBrushSize) * 1.25,
              getWorldUnitsPerScreenPixel(hit, rect) * 12,
            )
          : Infinity,
        surfaceLockDebug: {
          lockEnabled: Boolean(lock3dPaintToStartingSurfaceRef.current),
          sampleCount: 1,
          modelHitCount: 1,
          airCount: 0,
          acceptedCount: 1,
          rejectedMeshCount: 0,
          rejectedFaceCount: 0,
          rejectedDepthCount: 0,
          lastState: 'accepted',
        },
        projectionRenderScale: previewRenderScale,
        projectionOverscan: strokeOverscan,
        projectionViewportWidth: rect.width,
        projectionViewportHeight: rect.height,
        projectionCamera,
        incrementalPen,
      };
      if (isImportedBrush) {
        drawImportedPreviewStart(previewPoint, activePaintStroke);
      } else if (isAirbrush) {
        // Do not inject the utility's default 100 ms spray pulse on pointer-down.
        // The animation timer owns all 3D airbrush emission, so a click emits only
        // the particles earned by the real time the pointer is held down.
        start3dAirbrushTimer(activePaintStroke);
      }
      if (activePaintStroke.isPbr) {
        livePbrPreviewLastUpdateAtRef.current = 0;
        scheduleLivePbrPreviewProjectionUpdate(activePaintStroke);
      }

    };

    const handlePaintPointerMove = (event) => {
      if (middleButtonNavigationPointerId != null) return;
      if (physicalPaint3d.isStroking()) {
        event.preventDefault();
        event.stopPropagation();
        physicalPaint3d.pointerMove(event);
        return;
      }
      if (handleFaceSelectPointerMove(event)) return;
      if (continueDecalLayerDrag(event)) return;
      if (handleSelectionPointerMove(event)) return;
      if (!activePaintStroke || event.pointerId !== activePaintStroke.pointerId) {
        updateIdle3dBrushCursor(event);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (activePaintStroke.incrementalPen) {
        const strokeRect = renderer.domElement.getBoundingClientRect();
        const reportedSamples = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : null;
        const samples = reportedSamples && reportedSamples.length ? reportedSamples : [event];
        let latestPoint = null;
        samples.forEach((sample) => {
          latestPoint = getPreviewPointerPointInRect(sample, strokeRect);
          latestPoint.brushSize = activePaintStroke.incrementalPen.baseSize;
          appendIncrementalPenPoint(activePaintStroke.incrementalPen, latestPoint, sample.timeStamp);
        });
        if (latestPoint) {
          position3dBrushCursor(latestPoint, latestPoint.brushSize);
          activePaintStroke.previewPoints.push(latestPoint);
          activePaintStroke.lastPreviewPoint = latestPoint;
        }
        if (activePaintStroke.isPbr) {
          scheduleLivePbrPreviewProjectionUpdate(activePaintStroke);
        }
        return;
      }

      const previewPoint = getPreviewPointerPoint(event);
      const rect = renderer.domElement.getBoundingClientRect();
      if (!activePaintStroke.isAirbrush) {
        position3dBrushCursor(
          previewPoint,
          activePaintStroke.lastPreviewPoint?.brushSize
            || activePaintStroke.brushConfig?.size
            || activePaintStroke.brushConfig?.brushSize
            || 1,
        );
      }

      // The exact centre pixel is used only once, on pointer-down, to choose
      // the surface mask. It must not act as a leash for the stroke. The cursor
      // centre may move over air or other geometry; the final projection mask
      // independently accepts only pixels that land on the originally selected
      // connected surface.
      if (activePaintStroke.surfaceLockDebug) {
        const debug = activePaintStroke.surfaceLockDebug;
        debug.sampleCount += 1;
        debug.acceptedCount += 1;
        if (debug.lastState !== 'stroke-free-surface-mask') {
                  }
        debug.lastState = 'stroke-free-surface-mask';
      }

      // A normal pen stroke is committed as a camera projection. The projection
      // depth pass decides which model surface receives it, so live pointer-move
      // raycasts are unnecessary. Draw first and return immediately so the
      // preview follows the pointer without the initial catch-up jump.
      if (!activePaintStroke.isAirbrush && !activePaintStroke.isImportedBrush) {
        previewPoint.brushSize = activePaintStroke.lastPreviewPoint?.brushSize
          || activePaintStroke.previewPoints?.findLast?.(Boolean)?.brushSize
          || activePaintStroke.brushConfig?.size
          || activePaintStroke.brushConfig?.brushSize
          || 1;
        activePaintStroke.previewPoints.push(previewPoint);
        if (activePaintStroke.isPencil) {
          drawPreviewPencilStroke(activePaintStroke);
        } else {
          drawPreviewPenStroke(activePaintStroke);
        }
        activePaintStroke.lastPreviewPoint = previewPoint;
        if (activePaintStroke.isPbr) {
          scheduleLivePbrPreviewProjectionUpdate(activePaintStroke);
        }
        return;
      }

      let hit = null;
      if (activePaintStroke.isAirbrush) {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const previousRaycastAt = Number(activePaintStroke.lastAirbrushRaycastAt) || 0;
        if (
          !activePaintStroke.lastHit
          || (now - previousRaycastAt) >= AIRBRUSH_3D_LIVE_FRAME_INTERVAL_MS
        ) {
          hit = getPaintHitAtScreenPoint(
            previewPoint.screenX,
            previewPoint.screenY,
            rect,
            previewPoint.pressure,
          );
          activePaintStroke.lastAirbrushRaycastAt = now;
          if (hit) activePaintStroke.lastHit = hit;
        } else {
          hit = activePaintStroke.lastHit || null;
        }
      }

      if (activePaintStroke.isAirbrush) {
        previewPoint.brushSize = getAirbrushScreenDiameter(
          activePaintStroke,
          hit || activePaintStroke.lastHit,
          rect,
        );

        const previewPoints = activePaintStroke.previewPoints
          || (activePaintStroke.previewPoints = []);
        const previousPreviewPoint = previewPoints[previewPoints.length - 1];
        if (
          !previousPreviewPoint
          || Math.hypot(
            previewPoint.screenX - previousPreviewPoint.screenX,
            previewPoint.screenY - previousPreviewPoint.screenY,
          ) >= 0.5
        ) {
          previewPoints.push({ ...previewPoint });
          if (previewPoints.length > 2048) {
            previewPoints.splice(1, previewPoints.length - 2048);
          }
        }
      }

      if (activePaintStroke.isImportedBrush) {
        drawImportedPreviewMove(previewPoint, activePaintStroke);
        if (activePaintStroke.isPbr) {
          scheduleLivePbrPreviewProjectionUpdate(activePaintStroke);
        }
      }

      activePaintStroke.lastPreviewPoint = previewPoint;
      if (hit) {
        activePaintStroke.lastHit = hit;

        if (activePaintStroke.isAirbrush && hit.point) {
          const worldPoints = activePaintStroke.airbrushWorldPoints
            || (activePaintStroke.airbrushWorldPoints = []);
          const previousWorldPoint = worldPoints[worldPoints.length - 1];
          const minimumSpacing = Math.max(
            0.000001,
            Number(activePaintStroke.airbrushWorldDiameter) * 0.12,
          );

          if (
            !previousWorldPoint
            || previousWorldPoint.distanceTo(hit.point) >= minimumSpacing
          ) {
            worldPoints.push(hit.point.clone());
            if (worldPoints.length > 32) {
              worldPoints.splice(1, worldPoints.length - 32);
            }
          }
        }
      }
    };

    let paintSaveTimer = 0;
    let paintIdleSaveHandle = 0;
    let paintSaveGeneration = 0;
    let restoringPersistedPaint = false;

    const getCurrentPaintCacheKey = () => {
      const model = currentModelRef.current;
      const modelFingerprint = String(model?.userData?.voxemeraModelUvCacheFingerprint || '');
      return getModelPaintSessionKey(modelFingerprint, activePaintSession?.key || '');
    };

    const buildExactTextureSnapshots = (strokes, visibleCount) => {
      const session = activePaintSession;
      if (
        !session?.canvas
        || !session?.context
        || !session?.materialCanvas
        || !session?.materialContext
        || !session?.gpuPaintTarget
      ) return null;

      const colourSource = readCurrentUvColourPixels();
      if (!colourSource) return null;

      const width = colourSource.width;
      const height = colourSource.height;
      const paddedColour = padPaintAtlasEdges(
        colourSource.pixels,
        colourSource.coverage,
        width,
        height,
        6,
      );

      const materialImage = session.materialContext.getImageData(0, 0, width, height);
      const materialPixels = new Uint8ClampedArray(materialImage.data);
      const materialCoverage = new Uint8Array(width * height);
      const safeVisibleCount = Math.max(
        0,
        Math.min(strokes.length, Number(visibleCount) || 0),
      );
      const strokePixels = new Uint8Array(width * height * 4);

      // Re-render one visible stroke at a time into the UV target. Its alpha is
      // the exact texel coverage; write that stroke's own packed material
      // settings into a parallel material texture snapshot.
      for (let strokeIndex = 0; strokeIndex < safeVisibleCount; strokeIndex += 1) {
        const stroke = strokes[strokeIndex];
        clearGpuPaintMap(session);
        if (!renderProjectionStrokeToGpuPaintMap(stroke, false)) continue;
        renderer.readRenderTargetPixels(
          session.gpuPaintTarget,
          0,
          0,
          width,
          height,
          strokePixels,
        );

        const channels = getPackedMaterialChannelBytes(
          stroke?.materialSettings || materialSettingsRef.current,
        );
        for (let pixel = 0; pixel < width * height; pixel += 1) {
          const rgbaIndex = pixel * 4;
          const alpha = strokePixels[rgbaIndex + 3];
          if (alpha <= 0) continue;
          materialCoverage[pixel] = Math.max(materialCoverage[pixel], alpha);
          materialPixels[rgbaIndex] = channels.red;
          materialPixels[rgbaIndex + 1] = channels.green;
          materialPixels[rgbaIndex + 2] = channels.blue;
          materialPixels[rgbaIndex + 3] = 255;
        }
      }

      // Restore the live combined GPU map after the per-stroke snapshot pass.
      rebuildGpuPaintMap();

      const paddedMaterial = padPaintAtlasEdges(
        materialPixels,
        materialCoverage,
        width,
        height,
        6,
      );

      return {
        colour: snapshotToRawRgba(width, height, paddedColour.pixels),
        material: snapshotToRawRgba(width, height, paddedMaterial.pixels),
      };
    };

    const saveProjectionStrokeSnapshot = async (key, strokes, visibleCount, generation) => {
      if (!key) return;
      const meshKeys = getModelMeshKeyMaps();
      // Persist the finished destructive result, not historical eraser
      // projection actions. Erasers stay in the live history for undo/redo, but
      // each affected paint stroke carries its already-composited alpha mask.
      const persistableStrokes = strokes
        .slice(0, Math.max(0, Math.min(strokes.length, Number(visibleCount) || 0)))
        .filter((stroke) => stroke?.isEraser !== true);
      const encodedStrokes = await Promise.all(persistableStrokes.map(async (stroke) => {
        if (stroke?.kind === 'uvFloodFill') {
          return {
            kind: 'uvFloodFill',
            layerId: String(stroke.layerId || ''),
            selectionRestriction: serializeSelectionSnapshot(stroke.selectionSnapshot, meshKeys.keyByUuid),
            image: await canvasToBlob(stroke.sourceCanvas || stroke.strokeTexture?.image),
            materialSettings: stroke.materialSettings,
            isPbr: stroke.isPbr === true,
            forceSolidSelectionCoverage: stroke.forceSolidSelectionCoverage === true,
            solidFillHex: String(stroke.solidFillHex || '#000000'),
            brushConfig: stroke.brushConfig
              ? (() => {
                  const {
                    importedBrush,
                    importedBrushStrokeState,
                    eraserPreviewSource,
                    ...serializableBrushConfig
                  } = stroke.brushConfig;
                  return serializableBrushConfig;
                })()
              : null,
          };
        }
        return {
          kind: 'projectionDecal',
          layerId: String(stroke.layerId || ''),
          selectionRestriction: serializeSelectionSnapshot(stroke.selectionSnapshot, meshKeys.keyByUuid),
          allowedMeshKey: stroke.allowedMeshUuid ? (meshKeys.keyByUuid.get(stroke.allowedMeshUuid) || null) : null,
          image: await canvasToBlob(stroke.sourceCanvas || stroke.strokeTexture?.image),
          rawRgba: canvasToRawRgba(
            stroke.sourceCanvas || stroke.strokeTexture?.image,
          ),
          bakedEraserMaskRawRgba: stroke.bakedEraserMaskCanvas
            ? canvasToRawRgba(stroke.bakedEraserMaskCanvas)
            : null,
          projectionCamera: stroke.projectionCameraData,
          materialSettings: stroke.materialSettings,
          projectionRect: stroke.projectionRect || { x: 0, y: 0, width: 1, height: 1 },
          projectionFullWidth: stroke.projectionFullWidth || stroke.sourceCanvas?.width || 1,
          projectionFullHeight: stroke.projectionFullHeight || stroke.sourceCanvas?.height || 1,

          // Projection type and paint identity must survive reload. Without
          // these, an airbrush projection is reconstructed as a generic pen
          // decal, which changes its colour, size mask and world-distance path.
          isAirbrush: stroke.isAirbrush === true,
          isPencil: stroke.isPencil === true,
          isImportedBrush: stroke.isImportedBrush === true,
          isEraser: stroke.isEraser === true,
          isPbr: stroke.isPbr === true,
          brushConfig: stroke.brushConfig
            ? (() => {
                const {
                  importedBrush,
                  importedBrushStrokeState,
                  eraserPreviewSource,
                  ...serializableBrushConfig
                } = stroke.brushConfig;
                return serializableBrushConfig;
              })()
            : null,
          airbrushWorldDiameter: Number(stroke.airbrushWorldDiameter) || 0,
          allowedMeshUuid: stroke.allowedMeshUuid || null,
          allowedFaceIndices: stroke.allowedMeshUuid
            ? Array.from(stroke.allowedFaceIndicesByMesh?.get?.(stroke.allowedMeshUuid) || [])
            : [],
          startingSurfaceWorldPoint: stroke.startingSurfaceWorldPoint?.isVector3
            ? stroke.startingSurfaceWorldPoint.toArray()
            : (Array.isArray(stroke.startingSurfaceWorldPoint) ? stroke.startingSurfaceWorldPoint : null),
          surfaceDepthTolerance: Number(stroke.surfaceDepthTolerance),
          airbrushWorldPoints: Array.isArray(stroke.airbrushWorldPoints)
            ? stroke.airbrushWorldPoints.map((point) => ({
                x: Number(point?.x) || 0,
                y: Number(point?.y) || 0,
                z: Number(point?.z) || 0,
              }))
            : [],
        };
      }));
      if (generation !== paintSaveGeneration) {
        return;
      }
      // Do not rebuild exact full-model texture snapshots on every stroke save.
      // That path clears and re-renders the complete UV paint target once per
      // visible stroke, then reads the full render target back to the CPU. On
      // detailed models this blocks the main thread for several seconds.
      //
      // The persisted atlas canvases below already contain the current colour
      // and packed material state and are the primary restore source. Raw RGBA
      // fallbacks are captured directly from those same canvases after encoding.
      const atlasImage = await canvasToBlob(activePaintSession?.canvas);
      const materialAtlasImage = await canvasToBlob(activePaintSession?.materialCanvas);
      const normalAtlasImage = await canvasToBlob(activePaintSession?.normalCanvas);
      if (generation !== paintSaveGeneration) {
        return;
      }
      // PNG blobs above are the canonical atlas persistence format. Do not also
      // synchronously read three complete atlas canvases back to CPU memory.
      // At 8192x8192 each RGBA read is 256 MiB and blocks camera/input frames.
      await saveModelPaintSessionRecord(key, {
        version: MODEL_PAINT_CACHE_VERSION,
        updatedAt: Date.now(),
        visibleCount: encodedStrokes.length,
        strokes: encodedStrokes,
        atlasImage,
        materialAtlasImage,
        normalAtlasImage,
        textureImageRawRgba: null,
        materialTextureRawRgba: null,
        normalTextureRawRgba: null,
        pbrBrush: getPersistablePbrBrushRecord(),
      });
    };

    const persistProjectionStrokesNow = async () => {
      if (restoringPersistedPaint) return;
      const runtime = runtimeRef.current;
      const key = getCurrentPaintCacheKey();
      if (!runtime || !key) return;
      const generation = ++paintSaveGeneration;
      const strokes = Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes.slice() : [];
      const visibleCount = projectionStrokeCountRef.current;
      await saveProjectionStrokeSnapshot(key, strokes, visibleCount, generation);
    };

    const scheduleProjectionPaintSave = () => {
      if (restoringPersistedPaint) return;
      const runtime = runtimeRef.current;
      const key = getCurrentPaintCacheKey();
      if (!runtime || !key) return;
      const strokes = Array.isArray(runtime.projectionStrokes) ? runtime.projectionStrokes.slice() : [];
      const visibleCount = projectionStrokeCountRef.current;
      const generation = ++paintSaveGeneration;
      if (paintSaveTimer) window.clearTimeout(paintSaveTimer);
      if (paintIdleSaveHandle) {
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(paintIdleSaveHandle);
        else window.clearTimeout(paintIdleSaveHandle);
      }
      paintSaveTimer = window.setTimeout(() => {
        paintSaveTimer = 0;
        saveProjectionStrokeSnapshot(key, strokes, visibleCount, generation).catch(() => {});
      }, PROJECTION_SAVE_DEBOUNCE_MS);
    };

    const handlePageHidePaintSave = () => {
      persistProjectionStrokesNow().catch(() => {});
    };

    const persistCanonicalAtlasImmediately = async () => {
      if (restoringPersistedPaint) return false;
      const key = getCurrentPaintCacheKey();
      const session = activePaintSession;
      if (!key || !session?.canvas || !session?.materialCanvas) {
        return false;
      }

      if (paintSaveTimer) {
        window.clearTimeout(paintSaveTimer);
        paintSaveTimer = 0;
      }

      const generation = ++paintSaveGeneration;
      const [atlasImage, materialAtlasImage, normalAtlasImage] = await Promise.all([
        canvasToBlob(session.canvas),
        canvasToBlob(session.materialCanvas),
        canvasToBlob(session.normalCanvas),
      ]);
      if (generation !== paintSaveGeneration) return false;

      await saveModelPaintSessionRecord(key, {
        version: MODEL_PAINT_CACHE_VERSION,
        updatedAt: Date.now(),
        visibleCount: 0,
        strokes: [],
        atlasImage,
        materialAtlasImage,
        normalAtlasImage,
        textureImageRawRgba: null,
        materialTextureRawRgba: null,
        normalTextureRawRgba: null,
        pbrBrush: getPersistablePbrBrushRecord(),
      });
      return true;
    };

    const scheduleCanonicalAtlasSave = () => {
      if (restoringPersistedPaint) return;

      if (paintSaveTimer) {
        window.clearTimeout(paintSaveTimer);
        paintSaveTimer = 0;
      }
      if (paintIdleSaveHandle) {
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(paintIdleSaveHandle);
        } else {
          window.clearTimeout(paintIdleSaveHandle);
        }
        paintIdleSaveHandle = 0;
      }

      // Never PNG-encode the two full-size atlases inside pointer-up.
      paintSaveTimer = window.setTimeout(() => {
        paintSaveTimer = 0;

        const runSave = () => {
          paintIdleSaveHandle = 0;
          persistCanonicalAtlasImmediately().catch(() => {});
        };

        if (typeof window.requestIdleCallback === 'function') {
          paintIdleSaveHandle = window.requestIdleCallback(runSave, { timeout: 15000 });
        } else {
          paintIdleSaveHandle = window.setTimeout(runSave, 250);
        }
      }, 3000);
    };

    const clearProjectionPaintCompletely = () => {
            if (paintSaveTimer) {
        window.clearTimeout(paintSaveTimer);
        paintSaveTimer = 0;
      }
      if (paintIdleSaveHandle) {
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(paintIdleSaveHandle);
        } else {
          window.clearTimeout(paintIdleSaveHandle);
        }
        paintIdleSaveHandle = 0;
      }
      ++paintSaveGeneration; // invalidate any save already in flight

            if (activePaintStroke?.pointerId != null) {
        try { renderer.domElement.releasePointerCapture(activePaintStroke.pointerId); } catch {}
      }
      stop3dAirbrushTimer(activePaintStroke);
      activePaintStroke = null;
      texturePaintStateRef.current = null;
      clearPreviewCanvas();
      
            clearExactProjectionStrokes();
      
      if (
        activePaintSession?.canvas
        && activePaintSession?.context
        && activePaintSession?.materialCanvas
        && activePaintSession?.materialContext
        && activePaintSession?.normalCanvas
        && activePaintSession?.normalContext
      ) {
        const colourCanvas = activePaintSession.canvas;
        const colourContext = activePaintSession.context;
        const materialCanvas = activePaintSession.materialCanvas;
        const materialContext = activePaintSession.materialContext;
        const normalCanvas = activePaintSession.normalCanvas;
        const normalContext = activePaintSession.normalContext;

        // Clear always means a completely blank paint job, regardless of whether
        // the model uses generated or original UVs.
                colourContext.save();
        colourContext.setTransform(1, 0, 0, 1, 0, 0);
        colourContext.globalCompositeOperation = 'copy';
        colourContext.globalAlpha = 1;
        colourContext.fillStyle = '#ffffff';
        colourContext.fillRect(0, 0, colourCanvas.width, colourCanvas.height);
        colourContext.restore();
        
        // Packed material channels:
        // R = height       0   -> no height
        // G = roughness  255   -> gloss 0
        // B = metallic     0   -> metallic 0
                materialContext.save();
        materialContext.setTransform(1, 0, 0, 1, 0, 0);
        materialContext.globalCompositeOperation = 'copy';
        materialContext.globalAlpha = 1;
        materialContext.fillStyle = 'rgba(0, 255, 0, 1)';
        materialContext.fillRect(0, 0, materialCanvas.width, materialCanvas.height);
        materialContext.restore();

        normalContext.save();
        normalContext.setTransform(1, 0, 0, 1, 0, 0);
        normalContext.globalCompositeOperation = 'copy';
        normalContext.globalAlpha = 1;
        normalContext.fillStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
        normalContext.fillRect(0, 0, normalCanvas.width, normalCanvas.height);
        normalContext.restore();
        
                clearGpuPaintMap(activePaintSession);
        
        activePaintSession.initialized = true;
        activePaintSession.texture.needsUpdate = true;
        activePaintSession.materialTexture.needsUpdate = true;
        activePaintSession.normalTexture.needsUpdate = true;
        
                applyCurrentPaintMaterial();
        
                refreshVisibleMaterial();
        
        if (viewportDisplayModeRef.current === 'texture') {
                    drawCurrentTextureView(textureViewCanvasRef.current);
                  }
      }

      projectionStrokeCountRef.current = 0;
      const callback = paintConfigRef.current.onProjectionStrokeCommitted;
            if (typeof callback === 'function') callback(0);
      
      // Replace the persisted state with the freshly reset atlas and no history.
      const key = getCurrentPaintCacheKey();
      const generation = ++paintSaveGeneration;
      if (key && activePaintSession?.canvas) {
                Promise.all([
          canvasToBlob(activePaintSession.canvas),
          canvasToBlob(activePaintSession.materialCanvas),
          canvasToBlob(activePaintSession.normalCanvas),
        ])
          .then(([atlasImage, materialAtlasImage, normalAtlasImage]) => {
                        if (generation !== paintSaveGeneration) {
                            return;
            }
                        return saveModelPaintSessionRecord(key, {
              version: MODEL_PAINT_CACHE_VERSION,
              updatedAt: Date.now(),
              visibleCount: 0,
              strokes: [],
              atlasImage,
              materialAtlasImage,
              normalAtlasImage,
              textureImageRawRgba: canvasToRawRgba(activePaintSession.canvas),
              materialTextureRawRgba: canvasToRawRgba(activePaintSession.materialCanvas),
              normalTextureRawRgba: canvasToRawRgba(activePaintSession.normalCanvas),
              pbrBrush: getPersistablePbrBrushRecord(),
            }).then(() => {
                          });
          })
          .catch((error) => {
                      });
      } else {
              }

            return 0;
    };

    const restorePersistedPaintPreview = async () => {
      const key = getCurrentPaintCacheKey();
      if (!key || !activePaintSession?.texture) return false;
      const record = await loadModelPaintSessionRecord(key);
      if (!record || record.version !== MODEL_PAINT_CACHE_VERSION || !record.atlasImage) return false;
      const previewCanvas = await blobToPaintPreviewCanvas(record.atlasImage, 1024);
      if (!previewCanvas) return false;
      activePaintSession.texture.image = previewCanvas;
      activePaintSession.texture.generateMipmaps = false;
      activePaintSession.texture.minFilter = THREE.LinearFilter;
      activePaintSession.texture.magFilter = THREE.LinearFilter;
      activePaintSession.texture.needsUpdate = true;
      activePaintSession.paintPreviewCanvas = previewCanvas;
      return true;
    };

    const restorePersistedProjectionStrokes = async () => {
      const profiler = loadProfilerRef.current;
      const restoreStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const key = getCurrentPaintCacheKey();
      if (!key) return 0;
      profiler?.mark?.('Paint cache read: start', 'Reading the persisted paint-session record from IndexedDB.');
      const cacheReadStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const record = await loadModelPaintSessionRecord(key);
      profiler?.mark?.('Paint cache read: complete', 'Persisted paint-session record is in memory.', {
        durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - cacheReadStartedAt) * 10) / 10,
        strokeCount: Array.isArray(record?.strokes) ? record.strokes.length : 0,
      });
      if (!record || record.version !== MODEL_PAINT_CACHE_VERSION || !Array.isArray(record.strokes)) return 0;

      // PBR brush-set decoding and paint-atlas decoding are independent. Start
      // them together instead of paying their costs serially during startup.
      const pbrRestoreStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const pbrRestorePromise = restorePbrBrushFromRecord(record).then(() => {
        profiler?.mark?.('PBR brush restore: complete', 'Persisted PBR brush maps/settings restored.', {
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - pbrRestoreStartedAt) * 10) / 10,
        });
      });

      // A 4096x4096 UV flood-fill mask was previously decoded only after the
      // atlas/PBR work had finished, costing roughly another second on startup.
      // Start those independent image decodes now so they overlap the work above.
      const persistedFloodFillDecodePromises = new Map();
      (record.strokes || []).forEach((savedStroke, strokeIndex) => {
        if (savedStroke?.kind !== 'uvFloodFill' || !savedStroke?.image) return;
        persistedFloodFillDecodePromises.set(strokeIndex, blobToCanvas(savedStroke.image));
      });

      restoringPersistedPaint = true;
      try {
        // Decode the three large atlas images concurrently, while PBR brush
        // resources and persisted flood-fill masks decode in parallel above.
        const atlasDecodeStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const atlasDecodePromise = Promise.all([
          record.atlasImage ? blobToCanvas(record.atlasImage) : Promise.resolve(null),
          record.materialAtlasImage ? blobToCanvas(record.materialAtlasImage) : Promise.resolve(null),
          record.normalAtlasImage ? blobToCanvas(record.normalAtlasImage) : Promise.resolve(null),
        ]);
        const [, decodedAtlases] = await Promise.all([pbrRestorePromise, atlasDecodePromise]);
        const [decodedColourAtlas, decodedMaterialAtlas, decodedNormalAtlas] = decodedAtlases;
        profiler?.mark?.('Paint atlas decode: complete', 'Colour/material/normal atlas blobs decoded.', {
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - atlasDecodeStartedAt) * 10) / 10,
          colour: Boolean(decodedColourAtlas),
          material: Boolean(decodedMaterialAtlas),
          normal: Boolean(decodedNormalAtlas),
        });

        if (activePaintSession?.context && activePaintSession?.canvas) {
          const restoredAtlas = decodedColourAtlas
            || rawRgbaToCanvas(record.textureImageRawRgba);
          if (restoredAtlas) {
            activePaintSession.texture.image = activePaintSession.canvas;
            activePaintSession.paintPreviewCanvas = null;
            activePaintSession.context.clearRect(0, 0, activePaintSession.canvas.width, activePaintSession.canvas.height);
            activePaintSession.context.drawImage(restoredAtlas, 0, 0, activePaintSession.canvas.width, activePaintSession.canvas.height);
            activePaintSession.texture.needsUpdate = true;
          }
        }
        if (activePaintSession?.materialContext && activePaintSession?.materialCanvas) {
          const restoredMaterialAtlas = decodedMaterialAtlas
            || rawRgbaToCanvas(record.materialTextureRawRgba);
          if (restoredMaterialAtlas) {
            activePaintSession.materialContext.clearRect(0, 0, activePaintSession.materialCanvas.width, activePaintSession.materialCanvas.height);
            activePaintSession.materialContext.drawImage(
              restoredMaterialAtlas,
              0,
              0,
              activePaintSession.materialCanvas.width,
              activePaintSession.materialCanvas.height,
            );
            activePaintSession.materialTexture.needsUpdate = true;
          }
        }
        if (activePaintSession?.normalContext && activePaintSession?.normalCanvas) {
          const restoredNormalAtlas = decodedNormalAtlas
            || rawRgbaToCanvas(record.normalTextureRawRgba);
          if (restoredNormalAtlas) {
            activePaintSession.normalContext.clearRect(0, 0, activePaintSession.normalCanvas.width, activePaintSession.normalCanvas.height);
            activePaintSession.normalContext.drawImage(
              restoredNormalAtlas,
              0,
              0,
              activePaintSession.normalCanvas.width,
              activePaintSession.normalCanvas.height,
            );
          } else {
            activePaintSession.normalContext.fillStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
            activePaintSession.normalContext.fillRect(0, 0, activePaintSession.normalCanvas.width, activePaintSession.normalCanvas.height);
          }
          activePaintSession.normalTexture.needsUpdate = true;
        }
        const atlasInstallStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        clearExactProjectionStrokes();
        clearGpuPaintMap(activePaintSession);
        applyCurrentPaintMaterial();
        refreshVisibleMaterial();
        profiler?.mark?.('Paint atlas install/material reset: complete', 'Restored atlases installed and projection/material state reset.', {
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - atlasInstallStartedAt) * 10) / 10,
        });

        // Restore the original projected strokes for the 3D model. The saved
        // Texture Image is useful for export/preview, but using it as the model
        // texture changes paint footprint on stretched UVs.
        const restoreMeshKeys = getModelMeshKeyMaps();
        const strokeRestoreStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        let restoredStrokeIndex = 0;
        let slowestStrokeMs = 0;
        let slowestStrokeIndex = -1;
        for (let savedStrokeArrayIndex = 0; savedStrokeArrayIndex < record.strokes.length; savedStrokeArrayIndex += 1) {
          const savedStroke = record.strokes[savedStrokeArrayIndex];
          const oneStrokeStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
          restoredStrokeIndex += 1;
          if (savedStroke?.kind === 'uvFloodFill') {
            const restoredMask = persistedFloodFillDecodePromises.has(savedStrokeArrayIndex)
              ? await persistedFloodFillDecodePromises.get(savedStrokeArrayIndex)
              : await blobToCanvas(savedStroke?.image);
            if (restoredMask) {
              const restoredFillSelection = deserializeSelectionSnapshot(savedStroke?.selectionRestriction, restoreMeshKeys.uuidByKey);
              runWithSelection(restoredFillSelection, () => commitUvFloodFill({
                maskCanvas: restoredMask,
                materialSettings: savedStroke?.materialSettings,
                layerId: String(savedStroke?.layerId || getCurrentLayerState().activeLayerId || ''),
                restoring: true,
                isPbr: savedStroke?.isPbr === true,
                brushConfig: savedStroke?.brushConfig || null,
                // New records contain a colourised full-UV coverage mask capped to a
                // practical texture size, so restore it directly.
                forceSolidSelectionCoverage: false,
                solidFillHex: String(savedStroke?.solidFillHex || '#000000'),
                selectionSnapshot: restoredFillSelection,
              }));
            }
            const oneStrokeMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - oneStrokeStartedAt;
            if (oneStrokeMs > slowestStrokeMs) {
              slowestStrokeMs = oneStrokeMs;
              slowestStrokeIndex = restoredStrokeIndex;
            }
            profiler?.mark?.('Projected stroke restore progress', `Restored ${restoredStrokeIndex}/${record.strokes.length} saved strokes.`, {
              currentStrokeMs: Math.round(oneStrokeMs * 10) / 10,
              slowestStrokeMs: Math.round(slowestStrokeMs * 10) / 10,
              slowestStrokeIndex,
              kind: 'uvFloodFill',
              isPbr: savedStroke?.isPbr === true,
            });
            continue;
          }
          if (savedStroke?.kind !== 'projectionDecal') continue;
          const restoredCanvas = rawRgbaToCanvas(savedStroke?.rawRgba)
            || await blobToCanvas(savedStroke?.image);
          const projectionCamera = deserializeProjectionCamera(savedStroke?.projectionCamera);
          if (!restoredCanvas || !projectionCamera) continue;
          const restoredBrushConfig = {
            ...(savedStroke?.brushConfig || {}),
          };
          const restoredWorldPoints = Array.isArray(savedStroke?.airbrushWorldPoints)
            ? savedStroke.airbrushWorldPoints.map((point) => new THREE.Vector3(
                Number(point?.x) || 0,
                Number(point?.y) || 0,
                Number(point?.z) || 0,
              ))
            : [];

          const restoredStrokeSelection = deserializeSelectionSnapshot(savedStroke?.selectionRestriction, restoreMeshKeys.uuidByKey);
          const restoredAllowedMeshUuid = (savedStroke?.allowedMeshKey && restoreMeshKeys.uuidByKey.get(savedStroke.allowedMeshKey))
            || savedStroke?.allowedMeshUuid
            || null;
          const restoredProjectionCreated = runWithSelection(restoredStrokeSelection, () => commitExactPreviewProjection({
            restoredCanvas,
            projectionCamera,
            selectionSnapshot: restoredStrokeSelection,
            isAirbrush: savedStroke?.isAirbrush === true,
            isPencil: savedStroke?.isPencil === true,
            isImportedBrush: savedStroke?.isImportedBrush === true,
            isEraser: savedStroke?.isEraser === true,
            isPbr: savedStroke?.isPbr === true,
            allowedMeshUuid: restoredAllowedMeshUuid,
            allowedFaceIndicesByMesh: restoredAllowedMeshUuid && Array.isArray(savedStroke?.allowedFaceIndices)
              ? new Map([[restoredAllowedMeshUuid, new Set(savedStroke.allowedFaceIndices)]])
              : null,
            startingSurfaceWorldPoint: Array.isArray(savedStroke?.startingSurfaceWorldPoint)
              ? new THREE.Vector3(...savedStroke.startingSurfaceWorldPoint)
              : null,
            surfaceDepthTolerance: Number(savedStroke?.surfaceDepthTolerance),
            brushConfig: restoredBrushConfig,
            airbrushWorldDiameter: Number(savedStroke?.airbrushWorldDiameter) || 0,
            airbrushWorldPoints: restoredWorldPoints,
            materialSettings: {
              ...materialSettingsRef.current,
              ...(savedStroke?.materialSettings || {}),
            },
            projectionRect: savedStroke?.projectionRect,
            projectionFullWidth: savedStroke?.projectionFullWidth,
            projectionFullHeight: savedStroke?.projectionFullHeight,
            layerId: String(savedStroke?.layerId || getCurrentLayerState().activeLayerId || ''),
            restoring: true,
          }));
          if (restoredProjectionCreated && savedStroke?.bakedEraserMaskRawRgba) {
            const restoredMaskCanvas = rawRgbaToCanvas(savedStroke.bakedEraserMaskRawRgba);
            const restoredStroke = runtimeRef.current?.projectionStrokes?.[
              (runtimeRef.current?.projectionStrokes?.length || 1) - 1
            ];
            if (restoredMaskCanvas && restoredStroke && restoredStroke.isEraser !== true) {
              const restoredMaskTexture = new THREE.CanvasTexture(restoredMaskCanvas);
              restoredMaskTexture.name = `Voxemera persisted destructive erase mask ${String(restoredStroke.layerId || '')}`;
              restoredMaskTexture.colorSpace = THREE.NoColorSpace;
              restoredMaskTexture.flipY = false;
              restoredMaskTexture.wrapS = THREE.ClampToEdgeWrapping;
              restoredMaskTexture.wrapT = THREE.ClampToEdgeWrapping;
              restoredMaskTexture.minFilter = THREE.LinearFilter;
              restoredMaskTexture.magFilter = THREE.LinearFilter;
              restoredMaskTexture.generateMipmaps = false;
              restoredMaskTexture.needsUpdate = true;
              if (!(runtimeRef.current.activeLayerEraserMaskTextures instanceof Set)) {
                runtimeRef.current.activeLayerEraserMaskTextures = new Set();
              }
              runtimeRef.current.activeLayerEraserMaskTextures.add(restoredMaskTexture);
              restoredStroke.bakedEraserMaskCanvas = restoredMaskCanvas;
              (restoredStroke.meshes || []).forEach((mesh) => {
                const materials = Array.isArray(mesh?.material) ? mesh.material : [mesh?.material];
                materials.filter(Boolean).forEach((material) => {
                  material.alphaMap = restoredMaskTexture;
                  material.alphaTest = 0.01;
                  material.transparent = true;
                  material.userData = {
                    ...(material.userData || {}),
                    voxemeraActiveLayerEraserMask: true,
                    voxemeraPersistedDestructiveErase: true,
                  };
                  material.needsUpdate = true;
                });
              });
            }
          }
          const oneStrokeMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - oneStrokeStartedAt;
          if (oneStrokeMs > slowestStrokeMs) {
            slowestStrokeMs = oneStrokeMs;
            slowestStrokeIndex = restoredStrokeIndex;
          }
          if (oneStrokeMs >= 100 || restoredStrokeIndex === 1 || restoredStrokeIndex % 25 === 0 || restoredStrokeIndex === record.strokes.length) {
            profiler?.mark?.('Projected stroke restore progress', `Restored ${restoredStrokeIndex}/${record.strokes.length} saved strokes.`, {
              currentStrokeMs: Math.round(oneStrokeMs * 10) / 10,
              slowestStrokeMs: Math.round(slowestStrokeMs * 10) / 10,
              slowestStrokeIndex,
              kind: savedStroke?.kind || 'unknown',
              isPbr: savedStroke?.isPbr === true,
            });
          }
        }
        profiler?.mark?.('Projected stroke reconstruction: complete', 'All persisted projected-stroke geometry/material objects have been rebuilt.', {
          durationMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - strokeRestoreStartedAt) * 10) / 10,
          strokeCount: record.strokes.length,
          slowestStrokeMs: Math.round(slowestStrokeMs * 10) / 10,
          slowestStrokeIndex,
        });
        const restoredStrokes = runtimeRef.current?.projectionStrokes || [];
        const visibleCount = Math.max(0, Math.min(restoredStrokes.length, Number(record.visibleCount) || 0));

        // The persisted colour/material/normal atlases already contain the final
        // saved appearance. Do NOT call setProjectionStrokeCount() here: that
        // helper rebuilds the 4096x4096 GPU paint target by replaying every stroke
        // and was costing ~17 seconds on this model. Restore history visibility
        // directly; the GPU paint target is rebuilt only when an operation that
        // actually consumes it (for example export baking) asks for it.
        projectionStrokeCountRef.current = visibleCount;
        restoredStrokes.forEach((stroke, index) => {
          const visible = index < visibleCount;
          (stroke?.meshes || []).forEach((mesh) => {
            if (mesh) mesh.visible = visible;
          });
        });
        refreshProjectionLayerPresentation();
        if (activePaintSession) activePaintSession.gpuPaintInitialized = false;
        profiler?.mark?.('Startup GPU paint rebuild skipped', 'Persisted atlases are already final; history replay is deferred until the GPU paint target is actually required.', {
          visibleCount,
          strokeCount: restoredStrokes.length,
        });
        profiler?.mark?.('Persisted paint restore: return', 'Paint restore function finished.', {
          totalRestoreMs: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - restoreStartedAt) * 10) / 10,
          visibleCount,
          restoredStrokeObjects: restoredStrokes.length,
        });
        return visibleCount;
      } finally {
        restoringPersistedPaint = false;
      }
    };

    const finishPaintStroke = async (event) => {
      if (endMiddleButtonNavigation(event)) return;
      if (physicalPaint3d.isStroking()) {
        physicalPaint3d.pointerUp(event);
        return;
      }
      if (finishFaceSelectDrag(event)) return;
      // Only route pointer-up to selection when that exact selection gesture is
      // currently active. A paused lasso draft must not intercept brush cleanup.
      if ((selectionDraft || editableSelectionVertexIndex >= 0) && activeSelectionPointerId != null && finishSelection(event)) return;
      if (finishDecalLayerDrag(event)) return;
      if (!activePaintStroke || (event && event.pointerId !== activePaintStroke.pointerId)) return;
      const pointerId = activePaintStroke.pointerId;
      const stroke = activePaintStroke;
      // Flush only the real unrendered hold time on pointer-up. This is not a
      // default spray pulse: a very quick click emits a very small number of dots,
      // while a held click continues at the configured dots-per-second rate.
      if (stroke.isAirbrush) {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const previousRenderTime = Number(stroke.airbrushLastLiveRenderTime) || now;
        const elapsedSeconds = Math.max(0, Math.min(0.1, (now - previousRenderTime) / 1000));
        if (elapsedSeconds > 0.000001) {
          const currentPoint = stroke.lastPreviewPoint;
          const previousPoint = stroke.airbrushLastRenderedPoint || currentPoint;
          const movementDistance = Math.hypot(
            Number(currentPoint?.screenX) - Number(previousPoint?.screenX),
            Number(currentPoint?.screenY) - Number(previousPoint?.screenY),
          );
          const hasMoved = Number.isFinite(movementDistance) && movementDistance > 0.35;

          drawPreviewAirbrushSample(
            stroke,
            previousPoint,
            currentPoint,
            // Flush only the small amount of real unrendered hold time on
            // release. Do not replay an extra full frame, which caused the
            // large circular blob at the end of wider sprays.
            Math.min(elapsedSeconds, 0.01),
          );

          if (hasMoved) {
            stroke.airbrushPreviousRenderedPoint = previousPoint;
            stroke.airbrushLastRenderedPoint = currentPoint;
          }
          stroke.airbrushLastLiveRenderTime = now;
        }
      }
      if (stroke.incrementalPen) finishIncrementalPenPreview(stroke.incrementalPen);
      stop3dAirbrushTimer(stroke);
      activePaintStroke = null;
      hide3dBrushCursor();
      try { renderer.domElement.releasePointerCapture(pointerId); } catch {}
      if (stroke.isPbr) {
        disposeLivePbrPreviewStroke();
      }
      setPreviewCanvasVisible(true);
      if (stroke.isImportedBrush) finishImportedPreviewStroke(stroke);

      if (stroke.surfaceLockDebug?.lockEnabled) {
        await completeDeferredSurfaceLock(stroke);
      }

      // Never discard an eraser stroke based on the screen-snapshot no-op
      // detector. That comparison can report NO HIT when the active layer is
      // visually identical to layers beneath it, when lighting/PBR output
      // matches closely, or when antialiasing shifts the compared pixels. In
      // those cases the preview visibly erases paint but pointer-up previously
      // threw the stroke away and the paint returned immediately.
      //
      // The committed projection mask is now the source of truth. A genuinely
      // blank eraser gesture may create an empty mask, but a real erase can no
      // longer be rejected before commit.

      // Imported Procreate/Krita brushes already exist as the exact final
      // textured preview raster. Keep that projection alive exactly like pencil
      // and airbrush instead of flattening it through the UV bake, which changes
      // the imported tip shape and makes the saved result differ from preview.
      const isPersistentProjection = true;
      if (isPersistentProjection) {
        // Pen and airbrush are model-space paint. Keep them as projected 3D
        // strokes rather than synchronously reading the whole model back and
        // baking into the UV atlas on pointer-up.
        const painted = commitExactPreviewProjection(stroke);
        clearPreviewCanvas();
        if (!painted) return;

        const runtime = runtimeRef.current;
        const projectionCount = Array.isArray(runtime?.projectionStrokes)
          ? runtime.projectionStrokes.length
          : 0;

        // commitExactPreviewProjection has already appended the new stroke and
        // rendered only that stroke into the merged GPU paint map. Calling
        // setProjectionStrokeCount here rebuilt the entire GPU paint map from
        // every previous stroke on each pointer-up, which became especially
        // expensive for dense pencil strokes and made the viewport appear to
        // hang after drawing. Newly committed decal meshes are already visible,
        // so only advance the visible history count here. Undo/redo still uses
        // setProjectionStrokeCount and performs a full rebuild when required.
        projectionStrokeCountRef.current = projectionCount;
        scheduleProjectionPaintSave();

        if (viewportDisplayModeRef.current === 'texture') {
          // Defer the derived texture redraw so pointer-up can return first.
          window.setTimeout(() => {
            drawCurrentTextureView(textureViewCanvasRef.current);
          }, 0);
        }

        renderer.render(scene, camera);

        const callback = paintConfigRef.current.onProjectionStrokeCommitted;
        if (typeof callback === 'function') {
          callback(projectionCount);
        }
        return;
      }

      const painted = commitExactPreviewProjection(stroke);
      clearPreviewCanvas();
      if (!painted) return;
      const flattened = flattenVisiblePaintIntoCanonicalAtlas(stroke?.materialSettings || materialSettingsRef.current);
      if (!flattened) return;
      scheduleCanonicalAtlasSave();
      renderer.render(scene, camera);
      const callback = paintConfigRef.current.onProjectionStrokeCommitted;
      if (typeof callback === 'function') callback(0);
    };

    renderer.domElement.addEventListener('pointerdown', handlePaintPointerDown, true);
    renderer.domElement.addEventListener('pointermove', handlePaintPointerMove, true);
    renderer.domElement.addEventListener('pointerleave', hide3dBrushCursor, true);
    renderer.domElement.addEventListener('pointerup', finishPaintStroke, true);
    renderer.domElement.addEventListener('pointercancel', finishPaintStroke, true);
    // Pointer capture is not reliable here when overlays/toolbars reroute the
    // release event. Listen at window level too so every started 3D stroke is
    // committed, flattened into the canonical atlas, and saved. The finaliser
    // clears activePaintStroke before returning, so receiving the same release
    // through both listeners cannot commit twice.
    window.addEventListener('pointerup', finishPaintStroke, true);
    window.addEventListener('pointercancel', finishPaintStroke, true);
    renderer.domElement.addEventListener('contextmenu', handleSelectionContextMenu, true);
    renderer.domElement.addEventListener('dblclick', handleSelectionDoubleClick, true);

    const resetProjectionForUvLayout = () => {
      if (activePaintStroke?.pointerId != null) {
        try { renderer.domElement.releasePointerCapture(activePaintStroke.pointerId); } catch {}
      }
      stop3dAirbrushTimer(activePaintStroke);
      activePaintStroke = null;
      texturePaintStateRef.current = null;
      clearPreviewCanvas();
      clearExactProjectionStrokes();
      uvLayoutRevision += 1;
      projectionStrokeCountRef.current = 0;
      return uvLayoutRevision;
    };

    const applyCurrentPaintMaterial = () => {
      if (activePaintSession?.texture) {
        activePaintSession.texture.needsUpdate = true;
      }
      if (activePaintSession?.normalTexture) {
        activePaintSession.normalTexture.needsUpdate = true;
      }
      refreshVisibleMaterial();
    };

    const getPackedMaterialChannelBytes = (settings = materialSettingsRef.current) => {
      const safeHeight = Math.max(0, Math.min(100, Number(settings?.height) || 0));
      const safeGloss = Math.max(0, Math.min(100, Number(settings?.gloss) || 0));
      const safeMetalness = Math.max(0, Math.min(100, Number(settings?.metalness) || 0));
      return {
        red: Math.round((safeHeight / 100) * 255),
        green: Math.round((1 - (safeGloss / 100)) * 255),
        blue: Math.round((safeMetalness / 100) * 255),
      };
    };

    const getRuntimeActivePbrBrushResource = () => {
      const currentPbrState = pbrBrushStateRef.current;
      if (!currentPbrState?.enabled) return null;
      return pbrSetResourcesRef.current.get(currentPbrState.selectedSetKey) || null;
    };

    const getPersistablePbrBrushRecord = () => {
      const currentPbrState = pbrBrushStateRef.current;
      const sets = (currentPbrState?.sets || [])
        .map((setMeta) => makePersistablePbrSetResource(pbrSetResourcesRef.current.get(setMeta.key)))
        .filter(Boolean);
      if (!sets.length) return null;
      return {
        enabled: Boolean(currentPbrState?.enabled),
        sourceLabel: currentPbrState?.sourceLabel || '',
        selectedSetKey: currentPbrState?.selectedSetKey || sets[0]?.key || '',
        scalePercent: Math.max(10, Math.min(800, Number(currentPbrState?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
        angleDegrees: ((Number(currentPbrState?.angleDegrees) || 0) % 360 + 360) % 360,
        sets,
      };
    };
    const getPbrBrushFootprint = (hit, rect, screenDiameter, pressure = 1) => {
      const canvas = activePaintSession?.canvas;
      const safeScreenRadius = Math.max(0.125, (Number(screenDiameter) || 0.25) * Math.max(0.01, Number(pressure) || 1) * 0.5);
      const fallbackScale = getUvPixelsPerScreenPixel(hit, rect);
      const fallbackRadius = Math.max(0.25, safeScreenRadius * fallbackScale);
      if (!canvas || !hit?.uv) return { radiusX: fallbackRadius, radiusY: fallbackRadius, rotation: 0 };

      const rightHit = getPaintHitAtScreenPoint(hit.screenX + 1, hit.screenY, rect, hit.pressure);
      const downHit = getPaintHitAtScreenPoint(hit.screenX, hit.screenY + 1, rect, hit.pressure);
      if (!rightHit?.uv || !downHit?.uv || rightHit.object !== hit.object || downHit.object !== hit.object) {
        return { radiusX: fallbackRadius, radiusY: fallbackRadius, rotation: 0 };
      }

      const j00 = (rightHit.uv.x - hit.uv.x) * canvas.width;
      const j10 = (rightHit.uv.y - hit.uv.y) * canvas.height;
      const j01 = (downHit.uv.x - hit.uv.x) * canvas.width;
      const j11 = (downHit.uv.y - hit.uv.y) * canvas.height;

      const s00 = (j00 * j00) + (j01 * j01);
      const s01 = (j00 * j10) + (j01 * j11);
      const s11 = (j10 * j10) + (j11 * j11);
      const trace = s00 + s11;
      const determinant = Math.max(0, (s00 * s11) - (s01 * s01));
      const discriminant = Math.sqrt(Math.max(0, (trace * trace * 0.25) - determinant));
      const lambdaMajor = Math.max(0, (trace * 0.5) + discriminant);
      const lambdaMinor = Math.max(0, (trace * 0.5) - discriminant);
      const majorScale = Math.sqrt(lambdaMajor);
      const minorScale = Math.sqrt(lambdaMinor);
      if (!Number.isFinite(majorScale) || majorScale <= 0.0001 || !Number.isFinite(minorScale) || minorScale <= 0.0001) {
        return { radiusX: fallbackRadius, radiusY: fallbackRadius, rotation: 0 };
      }

      const rotation = Math.abs(s01) > 1e-9 || Math.abs(lambdaMajor - s00) > 1e-9
        ? Math.atan2(lambdaMajor - s00, s01)
        : 0;
      return {
        radiusX: Math.max(0.25, safeScreenRadius * majorScale),
        radiusY: Math.max(0.25, safeScreenRadius * minorScale),
        rotation,
      };
    };


    const paintPbrStrokeByScreenSampling = (fromPreviewPoint, toPreviewPoint, brushConfig, materialSettings) => {
      const session = activePaintSession;
      if (!session?.canvas || !fromPreviewPoint || !toPreviewPoint) return null;
      const rect = renderer.domElement.getBoundingClientRect();
      const dx = (Number(toPreviewPoint.screenX) || 0) - (Number(fromPreviewPoint.screenX) || 0);
      const dy = (Number(toPreviewPoint.screenY) || 0) - (Number(fromPreviewPoint.screenY) || 0);
      const distance = Math.hypot(dx, dy);
      const avgBrushSize = Math.max(
        1,
        ((Number(fromPreviewPoint.brushSize) || 0) + (Number(toPreviewPoint.brushSize) || 0)) * 0.5,
      );
      const spacing = Math.max(0.75, Math.min(4, avgBrushSize * 0.12));
      const steps = Math.max(1, Math.ceil(distance / spacing));
      let lastStamped = null;

      for (let step = 1; step <= steps; step += 1) {
        const t = steps > 0 ? step / steps : 1;
        const sampleX = (Number(fromPreviewPoint.screenX) || 0) + (dx * t);
        const sampleY = (Number(fromPreviewPoint.screenY) || 0) + (dy * t);
        const pressure = THREE.MathUtils.lerp(
          Math.max(0.01, Number(fromPreviewPoint.pressure) || 1),
          Math.max(0.01, Number(toPreviewPoint.pressure) || 1),
          t,
        );
        const hit = getPaintHitAtScreenPoint(sampleX, sampleY, rect, pressure);
        if (!hit?.uv) continue;
        const brushSize = getBrushScreenSize(brushConfig, hit, rect);
        const atlasPoint = uvToCanonicalAtlasPoint(hit.uv, session.canvas);
        if (!atlasPoint) continue;
        const footprint = getPbrBrushFootprint(hit, rect, brushSize, pressure);
        paintPbrStrokeToAtlases(atlasPoint, atlasPoint, footprint, brushConfig, materialSettings);
        lastStamped = {
          hit,
          brushSize,
          footprint,
        };
      }

      return lastStamped;
    };

    const paintPatternStrokeOntoContext = ({
      targetContext,
      sourceCanvas,
      fallbackFillStyle,
      fromPoint,
      toPoint,
      footprint,
      opacity,
      scaleFactor,
      softnessPercent,
    }) => {
      if (!targetContext || !fromPoint || !toPoint || !footprint?.radiusX || !footprint?.radiusY) return false;

      const createStrokeStyle = () => {
        if (!sourceCanvas) return fallbackFillStyle;
        const pattern = targetContext.createPattern(sourceCanvas, 'repeat');
        if (!pattern) return fallbackFillStyle;
        if (typeof pattern.setTransform === 'function') {
          const angleRadians = THREE.MathUtils.degToRad(Number(pbrBrushStateRef.current?.angleDegrees) || 0);
          const cosine = Math.cos(angleRadians) * scaleFactor;
          const sine = Math.sin(angleRadians) * scaleFactor;
          const matrix = new DOMMatrix([cosine, sine, -sine, cosine, 0, 0]);
          pattern.setTransform(matrix);
        }
        return pattern;
      };

      const strokeStyle = createStrokeStyle();
      const radiusX = Math.max(0.25, Number(footprint.radiusX) || 0.25);
      const radiusY = Math.max(0.25, Number(footprint.radiusY) || 0.25);
      const rotation = Number(footprint.rotation) || 0;
      const segmentDx = toPoint.x - fromPoint.x;
      const segmentDy = toPoint.y - fromPoint.y;
      const segmentLength = Math.hypot(segmentDx, segmentDy);
      const spacing = Math.max(0.75, Math.min(radiusX, radiusY) * 0.42);
      const steps = Math.max(1, Math.ceil(segmentLength / spacing));
      const softness = Math.max(0, Math.min(1, (Number(softnessPercent) || 0) / 100));

      const drawEllipse = (x, y, scale, alphaMultiplier) => {
        targetContext.save();
        targetContext.globalAlpha = Math.max(0, Math.min(1, opacity * alphaMultiplier));
        targetContext.fillStyle = strokeStyle;
        targetContext.beginPath();
        targetContext.ellipse(
          x,
          y,
          Math.max(0.25, radiusX * scale),
          Math.max(0.25, radiusY * scale),
          rotation,
          0,
          Math.PI * 2,
        );
        targetContext.fill();
        targetContext.restore();
      };

      for (let step = 0; step <= steps; step += 1) {
        const t = steps > 0 ? step / steps : 0;
        const x = fromPoint.x + (segmentDx * t);
        const y = fromPoint.y + (segmentDy * t);
        if (softness > 0.001) drawEllipse(x, y, 1, 0.3);
        drawEllipse(x, y, Math.max(0.5, 1 - softness), 1);
      }
      return true;
    };


    const paintPatternMaskOntoContext = ({
      targetContext,
      maskCanvas,
      sourceCanvas,
      fallbackFillStyle,
      opacity,
      scaleFactor,
      angleDegrees,
    }) => {
      if (!targetContext || !maskCanvas) return false;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = maskCanvas.width;
      tempCanvas.height = maskCanvas.height;
      const tempContext = tempCanvas.getContext('2d', { alpha: true });
      if (!tempContext) return false;

      let fillStyle = fallbackFillStyle;
      if (sourceCanvas) {
        const pattern = tempContext.createPattern(sourceCanvas, 'repeat');
        if (pattern) {
          if (typeof pattern.setTransform === 'function') {
            const angleRadians = THREE.MathUtils.degToRad(Number(angleDegrees) || 0);
            const cosine = Math.cos(angleRadians) * scaleFactor;
            const sine = Math.sin(angleRadians) * scaleFactor;
            pattern.setTransform(new DOMMatrix([cosine, sine, -sine, cosine, 0, 0]));
          }
          fillStyle = pattern;
        }
      }

      tempContext.save();
      tempContext.globalAlpha = Math.max(0, Math.min(1, Number(opacity) || 1));
      tempContext.fillStyle = fillStyle;
      tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempContext.restore();
      tempContext.globalCompositeOperation = 'destination-in';
      tempContext.drawImage(maskCanvas, 0, 0, tempCanvas.width, tempCanvas.height);
      tempContext.globalCompositeOperation = 'source-over';
      targetContext.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height);
      return true;
    };

    const applyPbrMaskToAtlases = ({ maskCanvas, brushConfig, materialSettings }) => {
      const session = activePaintSession;
      const savedPbrSetKey = String(brushConfig?.pbrSetKey || '');
      const pbrResource = savedPbrSetKey
        ? (pbrSetResourcesRef.current.get(savedPbrSetKey) || null)
        : getRuntimeActivePbrBrushResource();
      if (!session?.context || !session?.materialContext || !session?.normalContext || !maskCanvas || !pbrResource) return false;

      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig?.opacity) || 1));
      const defaultMaterialBytes = getPackedMaterialChannelBytes(materialSettings);
      const fallbackColour = new THREE.Color(brushConfig?.strokeColor || brushConfig?.color || '#000000');
      const fallbackColourStyle = `rgb(${Math.round(fallbackColour.r * 255)}, ${Math.round(fallbackColour.g * 255)}, ${Math.round(fallbackColour.b * 255)})`;
      const fallbackMaterialStyle = `rgb(${defaultMaterialBytes.red}, ${defaultMaterialBytes.green}, ${defaultMaterialBytes.blue})`;
      const fallbackNormalStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
      const scaleFactor = Math.max(0.1, Math.min(8, (Number(brushConfig?.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100));
      const angleDegrees = ((Number(brushConfig?.pbrAngleDegrees) || 0) % 360 + 360) % 360;

      const paintedColour = paintPatternMaskOntoContext({
        targetContext: session.context,
        maskCanvas,
        sourceCanvas: pbrResource.albedoCanvas || null,
        fallbackFillStyle: fallbackColourStyle,
        opacity,
        scaleFactor,
        angleDegrees,
      });
      const paintedMaterial = paintPatternMaskOntoContext({
        targetContext: session.materialContext,
        maskCanvas,
        sourceCanvas: pbrResource.packedMaterialCanvas || null,
        fallbackFillStyle: fallbackMaterialStyle,
        opacity,
        scaleFactor,
        angleDegrees,
      });
      const paintedNormal = paintPatternMaskOntoContext({
        targetContext: session.normalContext,
        maskCanvas,
        sourceCanvas: pbrResource.normalCanvas || null,
        fallbackFillStyle: fallbackNormalStyle,
        opacity,
        scaleFactor,
        angleDegrees,
      });

      if (!paintedColour && !paintedMaterial && !paintedNormal) return false;
      refreshTextureAtlasPresentation(true);
      return true;
    };

    const createTextureViewReplaceMask = ({ width, height, pixels, point }) => {
      if (!width || !height || !pixels || !point) return null;
      const startX = Math.max(0, Math.min(width - 1, Math.floor(point.x)));
      const startY = Math.max(0, Math.min(height - 1, Math.floor(point.y)));
      const startPixelIndex = ((startY * width) + startX) * 4;
      const targetRed = pixels[startPixelIndex];
      const targetGreen = pixels[startPixelIndex + 1];
      const targetBlue = pixels[startPixelIndex + 2];
      const targetAlpha = pixels[startPixelIndex + 3];
      const tolerance = 32;
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskContext = maskCanvas.getContext('2d', { alpha: true });
      if (!maskContext) return null;
      const maskImage = maskContext.createImageData(width, height);
      let changedPixels = 0;

      for (let index = 0; index < pixels.length; index += 4) {
        if (
          Math.abs(pixels[index] - targetRed) > tolerance
          || Math.abs(pixels[index + 1] - targetGreen) > tolerance
          || Math.abs(pixels[index + 2] - targetBlue) > tolerance
          || Math.abs(pixels[index + 3] - targetAlpha) > tolerance
        ) continue;
        maskImage.data[index] = 255;
        maskImage.data[index + 1] = 255;
        maskImage.data[index + 2] = 255;
        maskImage.data[index + 3] = 255;
        changedPixels += 1;
      }

      if (!changedPixels) return null;
      maskContext.putImageData(maskImage, 0, 0);
      return { maskCanvas, changedPixels };
    };

    const paintPbrStrokeToAtlases = (fromPoint, toPoint, footprint, brushConfig, materialSettings) => {
      const session = activePaintSession;
      const pbrResource = getRuntimeActivePbrBrushResource();
      if (!session?.context || !session?.materialContext || !session?.normalContext || !pbrResource || !fromPoint || !toPoint || !footprint?.radiusX || !footprint?.radiusY) return false;

      const opacity = Math.max(0.01, Math.min(1, Number(brushConfig?.opacity) || 1));
      const softnessPercent = Math.max(0, Math.min(100, Number(brushConfig?.softness ?? brushConfig?.brushSoftness) || 0));
      const defaultMaterialBytes = getPackedMaterialChannelBytes(materialSettings);
      const fallbackColour = new THREE.Color(brushConfig?.strokeColor || brushConfig?.color || '#000000');
      const fallbackColourStyle = `rgb(${Math.round(fallbackColour.r * 255)}, ${Math.round(fallbackColour.g * 255)}, ${Math.round(fallbackColour.b * 255)})`;
      const fallbackMaterialStyle = `rgb(${defaultMaterialBytes.red}, ${defaultMaterialBytes.green}, ${defaultMaterialBytes.blue})`;
      const fallbackNormalStyle = `rgb(${FLAT_NORMAL_RGB.red}, ${FLAT_NORMAL_RGB.green}, ${FLAT_NORMAL_RGB.blue})`;
      const scaleFactor = Math.max(0.1, Math.min(8, (Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE) / 100));

      const paintedColour = paintPatternStrokeOntoContext({
        targetContext: session.context,
        sourceCanvas: pbrResource.albedoCanvas || null,
        fallbackFillStyle: fallbackColourStyle,
        fromPoint,
        toPoint,
        footprint,
        opacity,
        scaleFactor,
        softnessPercent,
      });
      const paintedMaterial = paintPatternStrokeOntoContext({
        targetContext: session.materialContext,
        sourceCanvas: pbrResource.packedMaterialCanvas || null,
        fallbackFillStyle: fallbackMaterialStyle,
        fromPoint,
        toPoint,
        footprint,
        opacity,
        scaleFactor,
        softnessPercent,
      });
      const paintedNormal = paintPatternStrokeOntoContext({
        targetContext: session.normalContext,
        sourceCanvas: pbrResource.normalCanvas || null,
        fallbackFillStyle: fallbackNormalStyle,
        fromPoint,
        toPoint,
        footprint,
        opacity,
        scaleFactor,
        softnessPercent,
      });

      if (!paintedColour && !paintedMaterial && !paintedNormal) return false;
      refreshTextureAtlasPresentation();
      return true;
    };

    let textureAtlasPresentationFrame = 0;
    const flushTextureAtlasPresentation = () => {
      if (activePaintSession?.texture) activePaintSession.texture.needsUpdate = true;
      if (activePaintSession?.materialTexture) activePaintSession.materialTexture.needsUpdate = true;
      if (activePaintSession?.normalTexture) activePaintSession.normalTexture.needsUpdate = true;
      refreshVisibleMaterial();
      if (viewportDisplayMode === 'texture') drawCurrentTextureView(textureViewCanvasRef.current);
    };

    const refreshTextureAtlasPresentation = (immediate = false) => {
      if (immediate) {
        if (textureAtlasPresentationFrame) {
          window.cancelAnimationFrame(textureAtlasPresentationFrame);
          textureAtlasPresentationFrame = 0;
        }
        flushTextureAtlasPresentation();
        return;
      }
      if (textureAtlasPresentationFrame) return;
      textureAtlasPresentationFrame = window.requestAnimationFrame(() => {
        textureAtlasPresentationFrame = 0;
        flushTextureAtlasPresentation();
      });
    };

    const uvToCanonicalAtlasPoint = (uv, canvas = activePaintSession?.canvas) => {
      if (!uv || !canvas) return null;
      return {
        x: THREE.MathUtils.clamp(Number(uv.x) || 0, 0, 1) * canvas.width,
        y: THREE.MathUtils.clamp(Number(uv.y) || 0, 0, 1) * canvas.height,
      };
    };

    const getTextureViewPaintPoint = (event) => {
      const displayCanvas = textureViewCanvasRef.current;
      const session = activePaintSession;
      if (!displayCanvas || !session?.canvas) return null;
      const rect = displayCanvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      const localX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      const localY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
      const scaleX = session.canvas.width / Math.max(1, rect.width);
      const scaleY = session.canvas.height / Math.max(1, rect.height);
      const atlasY = localY * scaleY;
      return {
        x: localX * scaleX,
        y: Math.max(0, Math.min(session.canvas.height - 1, atlasY)),
        atlasY,
        localX,
        localY,
        scaleX,
        scaleY,
        pressure: Math.max(0.05, Number(event.pressure) || 0.5),
      };
    };

    const paintTextureViewStrokeSegment = (context, startPoint, endPoint, radius, fillStyle, alpha = 1) => {
      if (!context || !startPoint || !endPoint) return;
      context.save();
      context.globalAlpha = Math.max(0, Math.min(1, alpha));
      context.strokeStyle = fillStyle;
      context.fillStyle = fillStyle;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = Math.max(1, radius * 2);
      context.beginPath();
      context.moveTo(startPoint.x, startPoint.y);
      context.lineTo(endPoint.x, endPoint.y);
      context.stroke();
      context.beginPath();
      context.arc(endPoint.x, endPoint.y, Math.max(0.5, radius), 0, Math.PI * 2);
      context.fill();
      context.restore();
    };

    const paintTextureViewAirbrushSegment = (context, startPoint, endPoint, radius, red, green, blue, alpha = 0.14) => {
      if (!context || !startPoint || !endPoint) return;
      const distance = Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y);
      const step = Math.max(1, radius * 0.35);
      const samples = Math.max(1, Math.ceil(distance / step));
      context.save();
      for (let sampleIndex = 0; sampleIndex <= samples; sampleIndex += 1) {
        const t = samples ? (sampleIndex / samples) : 0;
        const sampleX = startPoint.x + ((endPoint.x - startPoint.x) * t);
        const sampleY = startPoint.y + ((endPoint.y - startPoint.y) * t);
        const gradient = context.createRadialGradient(sampleX, sampleY, 0, sampleX, sampleY, Math.max(1, radius));
        gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, ${Math.max(0.03, alpha)})`);
        gradient.addColorStop(0.45, `rgba(${red}, ${green}, ${blue}, ${Math.max(0.02, alpha * 0.55)})`);
        gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0)`);
        context.fillStyle = gradient;
        context.beginPath();
        context.arc(sampleX, sampleY, Math.max(1, radius), 0, Math.PI * 2);
        context.fill();
      }
      context.restore();
    };

    const paintTextureViewMaterialSegment = (context, startPoint, endPoint, radius, settings = materialSettingsRef.current, alpha = 1) => {
      if (!context || !startPoint || !endPoint) return;
      const channels = getPackedMaterialChannelBytes(settings);
      paintTextureViewStrokeSegment(
        context,
        startPoint,
        endPoint,
        radius,
        `rgba(${channels.red}, ${channels.green}, ${channels.blue}, 1)`,
        alpha,
      );
    };

    const performTextureViewBucketFill = (event, brushConfig) => {
      const session = activePaintSession;
      if (!session?.context || !session?.materialContext || !session?.canvas) return false;
      const point = getTextureViewPaintPoint(event);
      if (!point) return false;

      // Texture view is the source of truth for this operation. First read the
      // complete visible result, including the base atlas and all projected
      // strokes currently held in the GPU paint map.
      const source = readCurrentUvColourPixels();
      if (!source) return false;
      const { width, height, pixels } = source;
      const activePbrResource = getRuntimeActivePbrBrushResource();

      if (activePbrResource) {
        const mask = createTextureViewReplaceMask({ width, height, pixels, point });
        if (!mask) return false;
        flattenVisiblePaintIntoCanonicalAtlas(materialSettingsRef.current);
        const applied = applyPbrMaskToAtlases({
          maskCanvas: mask.maskCanvas,
          brushConfig: {
            ...brushConfig,
            pbrSetKey: activePbrResource.key || '',
            pbrScalePercent: Math.max(10, Math.min(800, Number(pbrBrushStateRef.current?.scalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
            pbrAngleDegrees: ((Number(pbrBrushStateRef.current?.angleDegrees) || 0) % 360 + 360) % 360,
          },
          materialSettings: { ...materialSettingsRef.current },
        });
        if (!applied) return false;
        scheduleCanonicalAtlasSave();
        const callback = paintConfigRef.current.onProjectionStrokeCommitted;
        if (typeof callback === 'function') callback(0);
        return true;
      }

      const startX = Math.max(0, Math.min(width - 1, Math.floor(point.x)));
      const startY = Math.max(0, Math.min(height - 1, Math.floor(point.y)));
      const startPixelIndex = ((startY * width) + startX) * 4;
      const targetRed = pixels[startPixelIndex];
      const targetGreen = pixels[startPixelIndex + 1];
      const targetBlue = pixels[startPixelIndex + 2];
      const targetAlpha = pixels[startPixelIndex + 3];

      const fillColour = new THREE.Color(brushConfig?.strokeColor || brushConfig?.color || '#000000');
      const fillRed = Math.round(fillColour.r * 255);
      const fillGreen = Math.round(fillColour.g * 255);
      const fillBlue = Math.round(fillColour.b * 255);
      const fillAlpha = 255;
      const tolerance = 32;
      const materialChannels = getPackedMaterialChannelBytes(materialSettingsRef.current);
      const materialImage = session.materialContext.getImageData(0, 0, width, height);
      const finalPixels = new Uint8ClampedArray(pixels);
      let changedPixels = 0;

      const colourMatches = (index) => (
        Math.abs(pixels[index] - targetRed) <= tolerance
        && Math.abs(pixels[index + 1] - targetGreen) <= tolerance
        && Math.abs(pixels[index + 2] - targetBlue) <= tolerance
        && Math.abs(pixels[index + 3] - targetAlpha) <= tolerance
      );

      // Global colour replacement: disconnected UV islands are deliberately
      // treated as one logical image region in Texture
      // view. Differently coloured paint remains untouched.
      for (let index = 0; index < finalPixels.length; index += 4) {
        if (!colourMatches(index)) continue;
        if (
          finalPixels[index] === fillRed
          && finalPixels[index + 1] === fillGreen
          && finalPixels[index + 2] === fillBlue
          && finalPixels[index + 3] === fillAlpha
        ) continue;
        finalPixels[index] = fillRed;
        finalPixels[index + 1] = fillGreen;
        finalPixels[index + 2] = fillBlue;
        finalPixels[index + 3] = fillAlpha;
        const materialIndex = index;
        materialImage.data[materialIndex] = materialChannels.red;
        materialImage.data[materialIndex + 1] = materialChannels.green;
        materialImage.data[materialIndex + 2] = materialChannels.blue;
        materialImage.data[materialIndex + 3] = 255;
        changedPixels += 1;
      }

      if (!changedPixels) return false;

      // finalPixels already uses the canonical atlas coordinates shared by
      // the model and Texture Image editor. Store it without conversion.
      const finalImage = new ImageData(finalPixels, width, height);
      session.context.setTransform(1, 0, 0, 1, 0, 0);
      session.context.clearRect(0, 0, width, height);
      session.context.putImageData(finalImage, 0, 0);
      session.materialContext.putImageData(materialImage, 0, 0);
      clearExactProjectionStrokes();
      clearGpuPaintMap(session);

      session.texture.needsUpdate = true;
      session.materialTexture.needsUpdate = true;
      refreshVisibleMaterial();
      drawCurrentTextureView(textureViewCanvasRef.current);
      scheduleProjectionPaintSave();

      const callback = paintConfigRef.current.onProjectionStrokeCommitted;
      if (typeof callback === 'function') callback(0);
      return true;
    };

    const handleTextureViewPointerDown = (event) => {
      const session = activePaintSession;
      const displayCanvas = textureViewCanvasRef.current;
      if (viewportDisplayModeRef.current !== 'texture') {
        return false;
      }
      if (!session?.context) {
        return false;
      }
      if (!displayCanvas) {
        return false;
      }
      if (event.button !== 0) {
        return false;
      }
      const stage = paintConfigRef.current.paintStageRef?.current;
      const brushConfig = stage?.getProjectionBrushConfig?.() || {};
      if (brushConfig.activeTool === 'bucket' || activeToolRef.current === 'bucket') {
        event.preventDefault();
        performTextureViewBucketFill(event, brushConfig);
        return true;
      }
      const isImportedBrush = brushConfig.activeTool === 'pen' && Boolean(brushConfig.importedBrush);
      const activeBrushPreset = String(brushConfig.activeBrushPreset || 'pen');
      const isNormalPen = brushConfig.activeTool === 'pen' && activeBrushPreset === 'pen' && !brushConfig.importedBrush;
      const isAirbrush = brushConfig.activeTool === 'pen' && activeBrushPreset === 'airbrush' && !brushConfig.importedBrush;
      const isPencil = brushConfig.activeTool === 'pen' && activeBrushPreset === 'pencil' && !brushConfig.importedBrush;
      if (!isNormalPen && !isAirbrush && !isImportedBrush && !isPencil) {
        return false;
      }
      const activePbrResource = (isNormalPen || isImportedBrush)
        ? getRuntimeActivePbrBrushResource()
        : null;
      const point = getTextureViewPaintPoint(event);
      if (!point) {
        return false;
      }
      event.preventDefault();
      try { displayCanvas.setPointerCapture(event.pointerId); } catch {}
      const requestedBrushSize = Math.max(1, Number(brushConfig.size ?? brushConfig.brushSize) || 1);
      const textureRadius = Math.max(0.5, ((requestedBrushSize * ((point.scaleX + point.scaleY) * 0.5)) / 2) * point.pressure);
      const colour = new THREE.Color(brushConfig.strokeColor || brushConfig.color || '#000000');
      const colourBytes = {
        red: Math.round(colour.r * 255),
        green: Math.round(colour.g * 255),
        blue: Math.round(colour.b * 255),
      };
      texturePaintStateRef.current = {
        pointerId: event.pointerId,
        isAirbrush,
        isPencil,
        isPbr: Boolean(activePbrResource),
        brushConfig: {
          ...brushConfig,
          color: brushConfig.color || brushConfig.strokeColor || '#000000',
          size: requestedBrushSize,
          brushSize: requestedBrushSize,
        },
        colourBytes,
        fillStyle: `rgba(${colourBytes.red}, ${colourBytes.green}, ${colourBytes.blue}, 1)`,
        radius: textureRadius,
        materialSettings: { ...materialSettingsRef.current },
        lastPoint: point,
      };
      if (activePbrResource) {
        paintPbrStrokeToAtlases(point, point, { radiusX: textureRadius, radiusY: textureRadius, rotation: 0 }, texturePaintStateRef.current.brushConfig, texturePaintStateRef.current.materialSettings);
        return true;
      }
      if (isAirbrush) {
        paintTextureViewAirbrushSegment(session.context, point, point, textureRadius, colourBytes.red, colourBytes.green, colourBytes.blue, 0.16);
        paintTextureViewMaterialSegment(session.materialContext, point, point, textureRadius, materialSettingsRef.current, 0.16);
      } else {
        paintTextureViewStrokeSegment(session.context, point, point, textureRadius, texturePaintStateRef.current.fillStyle, 1);
        paintTextureViewMaterialSegment(session.materialContext, point, point, textureRadius, materialSettingsRef.current, 1);
      }
      refreshTextureAtlasPresentation();
      return true;
    };

    const handleTextureViewPointerMove = (event) => {
      const session = activePaintSession;
      const stroke = texturePaintStateRef.current;
      if (viewportDisplayModeRef.current !== 'texture' || !session?.context || !stroke || event.pointerId !== stroke.pointerId) return false;
      const point = getTextureViewPaintPoint(event);
      if (!point) return false;
      event.preventDefault();
      if (stroke.isPbr) {
        paintPbrStrokeToAtlases(stroke.lastPoint, point, { radiusX: stroke.radius, radiusY: stroke.radius, rotation: 0 }, stroke.brushConfig, stroke.materialSettings);
        stroke.lastPoint = point;
        return true;
      }
      if (stroke.isAirbrush) {
        paintTextureViewAirbrushSegment(session.context, stroke.lastPoint, point, stroke.radius, stroke.colourBytes.red, stroke.colourBytes.green, stroke.colourBytes.blue, 0.16);
        paintTextureViewMaterialSegment(session.materialContext, stroke.lastPoint, point, stroke.radius, stroke.materialSettings, 0.16);
      } else {
        paintTextureViewStrokeSegment(session.context, stroke.lastPoint, point, stroke.radius, stroke.fillStyle, 1);
        paintTextureViewMaterialSegment(session.materialContext, stroke.lastPoint, point, stroke.radius, stroke.materialSettings, 1);
      }
      stroke.lastPoint = point;
      refreshTextureAtlasPresentation();
      return true;
    };

    const finishTextureViewStroke = (event) => {
      const displayCanvas = textureViewCanvasRef.current;
      const stroke = texturePaintStateRef.current;
      if (!stroke || (event && event.pointerId !== stroke.pointerId)) return false;
      if (event) event.preventDefault();
      texturePaintStateRef.current = null;
      if (displayCanvas && stroke.pointerId != null) {
        try { displayCanvas.releasePointerCapture(stroke.pointerId); } catch {}
      }
      refreshTextureAtlasPresentation();
      scheduleProjectionPaintSave();
      return true;
    };

    const drawCurrentTextureView = (targetCanvas) => {
      if (!targetCanvas || !activePaintSession?.canvas) return false;
      const source = readCurrentUvColourPixels();
      if (!source) return false;
      const { width, height, pixels } = source;
      if (targetCanvas.width !== width) targetCanvas.width = width;
      if (targetCanvas.height !== height) targetCanvas.height = height;
      const targetContext = targetCanvas.getContext('2d', { alpha: true });
      if (!targetContext) return false;
      const image = new ImageData(new Uint8ClampedArray(pixels), width, height);
      targetContext.setTransform(1, 0, 0, 1, 0, 0);
      targetContext.clearRect(0, 0, width, height);
      targetContext.putImageData(image, 0, 0);
      return true;
    };

    const decalTransformOverlay = document.createElement('div');
    decalTransformOverlay.style.position = 'absolute';
    decalTransformOverlay.style.left = '0';
    decalTransformOverlay.style.top = '0';
    decalTransformOverlay.style.width = '1px';
    decalTransformOverlay.style.height = '1px';
    decalTransformOverlay.style.border = '1px solid rgba(255,255,255,0.95)';
    decalTransformOverlay.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.65)';
    decalTransformOverlay.style.transformOrigin = '50% 50%';
    decalTransformOverlay.style.pointerEvents = 'none';
    decalTransformOverlay.style.display = 'none';
    decalTransformOverlay.style.zIndex = '8';
    decalTransformOverlay.style.boxSizing = 'border-box';
    container.appendChild(decalTransformOverlay);

    const decalTransformMoveSurface = document.createElement('div');
    decalTransformMoveSurface.style.position = 'absolute';
    decalTransformMoveSurface.style.inset = '0';
    decalTransformMoveSurface.style.cursor = 'move';
    decalTransformMoveSurface.style.pointerEvents = 'none';
    decalTransformOverlay.appendChild(decalTransformMoveSurface);

    const decalTransformHandles = [];
    const handlePositions = [
      ['nw', '-7px', '-7px', 'nwse-resize'],
      ['n', 'calc(50% - 7px)', '-7px', 'ns-resize'],
      ['ne', 'calc(100% - 7px)', '-7px', 'nesw-resize'],
      ['e', 'calc(100% - 7px)', 'calc(50% - 7px)', 'ew-resize'],
      ['se', 'calc(100% - 7px)', 'calc(100% - 7px)', 'nwse-resize'],
      ['s', 'calc(50% - 7px)', 'calc(100% - 7px)', 'ns-resize'],
      ['sw', '-7px', 'calc(100% - 7px)', 'nesw-resize'],
      ['w', '-7px', 'calc(50% - 7px)', 'ew-resize'],
    ];
    handlePositions.forEach(([name, left, top, cursor]) => {
      const handle = document.createElement('div');
      handle.dataset.transformHandle = name;
      handle.style.position = 'absolute';
      handle.style.left = left;
      handle.style.top = top;
      handle.style.width = '14px';
      handle.style.height = '14px';
      handle.style.borderRadius = '3px';
      handle.style.border = '1px solid rgba(0,0,0,0.85)';
      handle.style.background = '#ffffff';
      handle.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.25)';
      handle.style.cursor = cursor;
      handle.style.pointerEvents = 'auto';
      handle.style.boxSizing = 'border-box';
      decalTransformOverlay.appendChild(handle);
      decalTransformHandles.push(handle);
    });

    const decalRotationStem = document.createElement('div');
    decalRotationStem.style.position = 'absolute';
    decalRotationStem.style.left = '50%';
    decalRotationStem.style.top = '-28px';
    decalRotationStem.style.width = '1px';
    decalRotationStem.style.height = '28px';
    decalRotationStem.style.background = 'rgba(255,255,255,0.95)';
    decalRotationStem.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.45)';
    decalRotationStem.style.pointerEvents = 'none';
    decalTransformOverlay.appendChild(decalRotationStem);

    const decalRotationHandle = document.createElement('div');
    decalRotationHandle.style.position = 'absolute';
    decalRotationHandle.style.left = 'calc(50% - 7px)';
    decalRotationHandle.style.top = '-42px';
    decalRotationHandle.style.width = '14px';
    decalRotationHandle.style.height = '14px';
    decalRotationHandle.style.borderRadius = '50%';
    decalRotationHandle.style.border = '1px solid rgba(0,0,0,0.8)';
    decalRotationHandle.style.background = '#ffffff';
    decalRotationHandle.style.cursor = 'grab';
    decalRotationHandle.style.pointerEvents = 'auto';
    decalTransformOverlay.appendChild(decalRotationHandle);

    let decalLayerSyncGeneration = 0;
    let activeDecalDrag = null;
    let activeDecalTransformHandle = null;

    const getModelDecalBaseWorldSize = () => {
      const model = currentModelRef.current;
      if (!model) return 0.25;
      const bounds = new THREE.Box3().setFromObject(model);
      if (bounds.isEmpty()) return 0.25;
      const size = bounds.getSize(new THREE.Vector3());
      return Math.max(0.01, Math.max(size.x, size.y, size.z, 0.1) * 0.22);
    };

    const disposeLayerDecalRecord = (record) => {
      if (!record) return;
      if (record.projectedStroke) disposeExactProjectionStroke(record.projectedStroke);
      record.projectedStroke = null;
      if (record.sourceCanvas) {
        record.sourceCanvas.width = 1;
        record.sourceCanvas.height = 1;
      }
    };

    const loadDecalCanvasFromDataUrl = (dataUrl) => new Promise((resolve) => {
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
        resolve(null);
        return;
      }
      const image = new Image();
      image.onload = () => {
        const width = Math.max(1, image.naturalWidth || image.width || 1);
        const height = Math.max(1, image.naturalHeight || image.height || 1);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return resolve(null);
        context.clearRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        resolve({ canvas, width, height });
      };
      image.onerror = () => resolve(null);
      image.src = dataUrl;
    });

    const buildFallbackDecalPlacement = () => {
      const model = currentModelRef.current;
      if (!model) return { position: [0, 0, 0], normal: [0, 0, 1], surfaceMeshUuid: '', baseWorldSize: getModelDecalBaseWorldSize() };
      const bounds = new THREE.Box3().setFromObject(model);
      const center = bounds.isEmpty() ? new THREE.Vector3() : bounds.getCenter(new THREE.Vector3());
      const normal = camera.position.clone().sub(center).normalize();
      if (!Number.isFinite(normal.lengthSq()) || normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
      return { position: center.toArray(), normal: normal.toArray(), surfaceMeshUuid: '', baseWorldSize: getModelDecalBaseWorldSize() };
    };

    const getDefaultDecalPlacement = () => {
      const rect = renderer.domElement.getBoundingClientRect();
      const hit = getPaintHitAtScreenPoint(rect.width * 0.5, rect.height * 0.5, rect, 1);
      if (!hit?.point) return buildFallbackDecalPlacement();
      const normal = hit.normal?.clone?.() || camera.position.clone().sub(hit.point).normalize();
      if (!Number.isFinite(normal.lengthSq()) || normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
      return { position: hit.point.toArray(), normal: normal.normalize().toArray(), surfaceMeshUuid: String(hit.object?.uuid || ''), baseWorldSize: getModelDecalBaseWorldSize() };
    };

    const getActiveDecalLayer = () => {
      const safeLayerId = String(activeLayerIdRef.current || '');
      if (!safeLayerId) return null;
      return (Array.isArray(layersPropRef.current) ? layersPropRef.current : []).find((layer) => (
        String(layer?.id || '') === safeLayerId
        && String(layer?.contentType || layer?.type || '') === 'decal'
        && layer?.decalData
      )) || null;
    };

    const commitDecalLayerPatchFromHit = (layerId, hit) => {
      if (!hit?.point || typeof onUpdateDecalLayerRef.current !== 'function') return false;
      const safeLayerId = String(layerId || '');
      if (!safeLayerId) return false;
      const currentLayer = (Array.isArray(layersPropRef.current) ? layersPropRef.current : []).find((layer) => String(layer?.id || '') === safeLayerId);
      const currentDecal = currentLayer?.decalData && typeof currentLayer.decalData === 'object' ? currentLayer.decalData : {};
      const normal = hit.normal?.clone?.() || camera.position.clone().sub(hit.point).normalize();
      if (!Number.isFinite(normal.lengthSq()) || normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
      onUpdateDecalLayerRef.current(safeLayerId, {
        ...currentDecal,
        position: hit.point.toArray(),
        normal: normal.normalize().toArray(),
        surfaceMeshUuid: String(hit.object?.uuid || ''),
        baseWorldSize: Math.max(0.01, Number(currentDecal.baseWorldSize) || getModelDecalBaseWorldSize()),
      });
      return true;
    };

    const createTransformedDecalCanvas = (sourceCanvas, flipX, flipY) => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, sourceCanvas?.width || 1);
      canvas.height = Math.max(1, sourceCanvas?.height || 1);
      const context = canvas.getContext('2d');
      if (!context || !sourceCanvas) return canvas;
      context.save();
      context.translate(flipX ? canvas.width : 0, flipY ? canvas.height : 0);
      context.scale(flipX ? -1 : 1, flipY ? -1 : 1);
      context.drawImage(sourceCanvas, 0, 0);
      context.restore();
      return canvas;
    };

    const createDecalProjectionCamera = (position, normal, worldWidth, worldHeight, rotationDegrees) => {
      const model = currentModelRef.current;
      const bounds = model ? new THREE.Box3().setFromObject(model) : new THREE.Box3();
      const modelSize = bounds.isEmpty() ? 1 : Math.max(...bounds.getSize(new THREE.Vector3()).toArray(), 1);
      const distance = Math.max(modelSize * 1.5, worldWidth, worldHeight, 0.1);
      const projector = new THREE.OrthographicCamera(-worldWidth * 0.5, worldWidth * 0.5, worldHeight * 0.5, -worldHeight * 0.5, 0.001, distance * 3);
      const safeNormal = normal.clone().normalize();
      projector.position.copy(position).addScaledVector(safeNormal, distance);
      const referenceUp = Math.abs(safeNormal.dot(new THREE.Vector3(0, 1, 0))) > 0.96 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
      const right = new THREE.Vector3().crossVectors(referenceUp, safeNormal).normalize();
      const up = new THREE.Vector3().crossVectors(safeNormal, right).normalize();
      up.applyAxisAngle(safeNormal, THREE.MathUtils.degToRad(Number(rotationDegrees) || 0));
      projector.up.copy(up);
      projector.lookAt(position);
      projector.updateProjectionMatrix();
      projector.updateMatrixWorld(true);
      return projector;
    };

    const syncDecalLayers = async (layerList) => {
      const runtime = runtimeRef.current;
      if (!runtime) return;
      runtime.layerDecals = runtime.layerDecals || new Map();
      const decalLayers = (Array.isArray(layerList) ? layerList : []).filter((layer) => String(layer?.contentType || layer?.type || '') === 'decal' && layer?.decalData);
      const validIds = new Set(decalLayers.map((layer) => String(layer?.id || '')).filter(Boolean));
      runtime.layerDecals.forEach((record, layerId) => {
        if (validIds.has(layerId)) return;
        disposeLayerDecalRecord(record);
        runtime.layerDecals.delete(layerId);
      });
      const syncGeneration = ++decalLayerSyncGeneration;
      for (const layer of decalLayers) {
        const layerId = String(layer?.id || '');
        if (!layerId) continue;
        const decalData = layer.decalData && typeof layer.decalData === 'object' ? layer.decalData : {};
        let record = runtime.layerDecals.get(layerId);
        if (!record) {
          record = { layerId, projectedStroke: null, sourceCanvas: null, imageDataUrl: '', imageWidth: 1, imageHeight: 1 };
          runtime.layerDecals.set(layerId, record);
        }
        if (record.imageDataUrl !== decalData.imageDataUrl || !record.sourceCanvas) {
          const loaded = await loadDecalCanvasFromDataUrl(decalData.imageDataUrl);
          if (syncGeneration !== decalLayerSyncGeneration) return;
          if (!loaded) continue;
          if (record.projectedStroke) disposeExactProjectionStroke(record.projectedStroke);
          record.projectedStroke = null;
          record.sourceCanvas = loaded.canvas;
          record.imageDataUrl = decalData.imageDataUrl;
          record.imageWidth = loaded.width;
          record.imageHeight = loaded.height;
        }
        if (!record.sourceCanvas) continue;
        if (record.projectedStroke) disposeExactProjectionStroke(record.projectedStroke);
        record.projectedStroke = null;

        const fallbackPlacement = buildFallbackDecalPlacement();
        const position = Array.isArray(decalData.position) && decalData.position.length >= 3
          ? new THREE.Vector3(Number(decalData.position[0]) || 0, Number(decalData.position[1]) || 0, Number(decalData.position[2]) || 0)
          : new THREE.Vector3(...fallbackPlacement.position);
        const normal = Array.isArray(decalData.normal) && decalData.normal.length >= 3
          ? new THREE.Vector3(Number(decalData.normal[0]) || 0, Number(decalData.normal[1]) || 0, Number(decalData.normal[2]) || 1)
          : new THREE.Vector3(...fallbackPlacement.normal);
        if (!Number.isFinite(normal.lengthSq()) || normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
        normal.normalize();
        const imageWidth = Math.max(1, Number(decalData.imageWidth || record.imageWidth || 1));
        const imageHeight = Math.max(1, Number(decalData.imageHeight || record.imageHeight || 1));
        const aspectRatio = Math.max(0.0001, imageWidth / imageHeight);
        const baseWorldSize = Math.max(0.01, Number(decalData.baseWorldSize) || getModelDecalBaseWorldSize());
        const scale = Math.max(0.05, Number(decalData.scale) || 1);
        const worldWidth = baseWorldSize * scale * (aspectRatio >= 1 ? aspectRatio : 1);
        const worldHeight = baseWorldSize * scale * (aspectRatio >= 1 ? 1 : (1 / aspectRatio));
        const transformedCanvas = createTransformedDecalCanvas(record.sourceCanvas, Boolean(decalData.flipX), Boolean(decalData.flipY));
        const projectionCamera = createDecalProjectionCamera(position, normal, worldWidth, worldHeight, Number(decalData.rotation) || 0);
        const projectedStroke = commitExactPreviewProjection({
          restoredCanvas: transformedCanvas,
          projectionCamera,
          projectionRect: { x: 0, y: 0, width: 1, height: 1 },
          projectionFullWidth: transformedCanvas.width,
          projectionFullHeight: transformedCanvas.height,
          isImportedBrush: true,
          isImageDecal: true,
          isTextDecal: decalData.isText === true,
          isPbr: decalData.isPbr === true,
          brushConfig: {
            pbrSetKey: String(decalData.pbrSetKey || ''),
            pbrScalePercent: Math.max(10, Math.min(800, Number(decalData.pbrScalePercent) || DEFAULT_PBR_TEXTURE_SCALE)),
            pbrAngleDegrees: ((Number(decalData.pbrAngleDegrees) || 0) % 360 + 360) % 360,
          },
          materialSettings: {
            gloss: Math.max(0, Math.min(100, Number(decalData.materialSettings?.gloss) || 0)),
            height: Math.max(0, Math.min(100, Number(decalData.materialSettings?.height) || 0)),
            metalness: Math.max(0, Math.min(100, Number(decalData.materialSettings?.metalness) || 0)),
          },
          restoring: true,
        }, { previewOnly: true });
        if (!projectedStroke) {
          transformedCanvas.width = 1;
          transformedCanvas.height = 1;
          continue;
        }
        projectedStroke.layerId = layerId;
        record.projectedStroke = projectedStroke;
        const opacity = Math.max(0, Math.min(1, Number(layer.opacity ?? 1)));
        (projectedStroke.meshes || []).forEach((mesh) => {
          mesh.name = `Voxemera projected decal layer ${layerId}`;
          mesh.userData.voxemeraImportedImageLayer = true;
          mesh.userData.voxemeraImportedImageLayerId = layerId;
          const uniforms = mesh.material?.userData?.voxemeraProjectionUniforms;
          if (uniforms?.decalOpacity) uniforms.decalOpacity.value = opacity;
          if (uniforms?.edgeWear) uniforms.edgeWear.value = Math.max(-1, Math.min(1, Number(layer.edgeWearPercent ?? 0) / 100));
          if (mesh.material) mesh.material.needsUpdate = true;
        });
      }
      refreshProjectionLayerPresentation();
      updateDecalTransformOverlay();
      renderer.render(scene, camera);
    };

    const is3dLayerTransformToolActive = () => (
      viewportDisplayModeRef.current === '3d'
      && String(activeToolRef.current || '') === 'move'
    );

    const getDecalScreenTransform = (layer = getActiveDecalLayer()) => {
      if (!layer?.decalData || !currentModelRef.current) return null;
      const data = layer.decalData;
      const position = Array.isArray(data.position) && data.position.length >= 3
        ? new THREE.Vector3(Number(data.position[0]) || 0, Number(data.position[1]) || 0, Number(data.position[2]) || 0)
        : null;
      if (!position) return null;
      const normal = Array.isArray(data.normal) && data.normal.length >= 3
        ? new THREE.Vector3(Number(data.normal[0]) || 0, Number(data.normal[1]) || 0, Number(data.normal[2]) || 1)
        : new THREE.Vector3(0, 0, 1);
      if (normal.lengthSq() <= 0.000001) normal.set(0, 0, 1);
      normal.normalize();
      const imageWidth = Math.max(1, Number(data.imageWidth) || 1);
      const imageHeight = Math.max(1, Number(data.imageHeight) || 1);
      const aspectRatio = Math.max(0.0001, imageWidth / imageHeight);
      const baseWorldSize = Math.max(0.01, Number(data.baseWorldSize) || getModelDecalBaseWorldSize());
      const scale = Math.max(0.05, Number(data.scale) || 1);
      const worldWidth = baseWorldSize * scale * (aspectRatio >= 1 ? aspectRatio : 1);
      const worldHeight = baseWorldSize * scale * (aspectRatio >= 1 ? 1 : (1 / aspectRatio));
      const referenceUp = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.96
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 1, 0);
      const right = new THREE.Vector3().crossVectors(referenceUp, normal).normalize();
      const up = new THREE.Vector3().crossVectors(normal, right).normalize();
      const rotation = THREE.MathUtils.degToRad(Number(data.rotation) || 0);
      right.applyAxisAngle(normal, rotation);
      up.applyAxisAngle(normal, rotation);
      const rect = renderer.domElement.getBoundingClientRect();
      const projectPoint = (point) => {
        const projected = point.clone().project(camera);
        return {
          x: ((projected.x + 1) * 0.5) * rect.width,
          y: ((1 - projected.y) * 0.5) * rect.height,
        };
      };
      const centre = projectPoint(position);
      const rightPoint = projectPoint(position.clone().addScaledVector(right, worldWidth * 0.5));
      const upPoint = projectPoint(position.clone().addScaledVector(up, worldHeight * 0.5));
      const width = Math.max(24, Math.hypot(rightPoint.x - centre.x, rightPoint.y - centre.y) * 2);
      const height = Math.max(24, Math.hypot(upPoint.x - centre.x, upPoint.y - centre.y) * 2);
      const angle = Math.atan2(rightPoint.y - centre.y, rightPoint.x - centre.x) * 180 / Math.PI;
      return { centre, width, height, angle, scale, rotation: Number(data.rotation) || 0 };
    };

    const updateDecalTransformOverlay = () => {
      const layer = getActiveDecalLayer();
      if (!is3dLayerTransformToolActive() || !layer || layer.visible === false) {
        decalTransformOverlay.style.display = 'none';
        return;
      }
      const transform = getDecalScreenTransform(layer);
      if (!transform) {
        decalTransformOverlay.style.display = 'none';
        return;
      }
      decalTransformOverlay.style.display = 'block';
      decalTransformOverlay.style.pointerEvents = 'none';
      decalTransformOverlay.style.width = `${transform.width}px`;
      decalTransformOverlay.style.height = `${transform.height}px`;
      decalTransformOverlay.style.transform = `translate(${transform.centre.x - (transform.width * 0.5)}px, ${transform.centre.y - (transform.height * 0.5)}px) rotate(${transform.angle}deg)`;
    };

    const beginDecalTransformHandle = (event, mode) => {
      if (!is3dLayerTransformToolActive()) return;
      const decalLayer = getActiveDecalLayer();
      const transform = getDecalScreenTransform(decalLayer);
      if (!decalLayer || !transform) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = renderer.domElement.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      activeDecalTransformHandle = {
        pointerId: event.pointerId,
        layerId: String(decalLayer.id || ''),
        mode,
        centreX: transform.centre.x,
        centreY: transform.centre.y,
        startDistance: Math.max(1, Math.hypot(pointerX - transform.centre.x, pointerY - transform.centre.y)),
        startPointerAngle: Math.atan2(pointerY - transform.centre.y, pointerX - transform.centre.x),
        startScale: transform.scale,
        startRotation: transform.rotation,
      };
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
    };

    const updateDecalTransformHandle = (event) => {
      const drag = activeDecalTransformHandle;
      if (!drag || event.pointerId !== drag.pointerId || typeof onUpdateDecalLayerRef.current !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      const rect = renderer.domElement.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      const currentLayer = (Array.isArray(layersPropRef.current) ? layersPropRef.current : []).find((layer) => String(layer?.id || '') === drag.layerId);
      const current = currentLayer?.decalData && typeof currentLayer.decalData === 'object' ? currentLayer.decalData : {};
      if (drag.mode === 'scale') {
        const distance = Math.max(1, Math.hypot(pointerX - drag.centreX, pointerY - drag.centreY));
        onUpdateDecalLayerRef.current(drag.layerId, {
          ...current,
          scale: Math.max(0.05, Math.min(4, drag.startScale * (distance / drag.startDistance))),
        });
      } else if (drag.mode === 'rotate') {
        const pointerAngle = Math.atan2(pointerY - drag.centreY, pointerX - drag.centreX);
        const deltaDegrees = THREE.MathUtils.radToDeg(pointerAngle - drag.startPointerAngle);
        onUpdateDecalLayerRef.current(drag.layerId, {
          ...current,
          rotation: (((drag.startRotation + deltaDegrees) % 360) + 360) % 360,
        });
      }
    };

    const finishDecalTransformHandle = (event) => {
      if (!activeDecalTransformHandle || (event && event.pointerId !== activeDecalTransformHandle.pointerId)) return;
      activeDecalTransformHandle = null;
    };

    decalTransformMoveSurface.addEventListener('pointerdown', (event) => beginDecalTransformHandle(event, 'move'));
    decalTransformHandles.forEach((handle) => {
      handle.addEventListener('pointerdown', (event) => beginDecalTransformHandle(event, 'scale'));
    });
    decalRotationHandle.addEventListener('pointerdown', (event) => beginDecalTransformHandle(event, 'rotate'));
    window.addEventListener('pointermove', updateDecalTransformHandle, true);
    window.addEventListener('pointerup', finishDecalTransformHandle, true);
    window.addEventListener('pointercancel', finishDecalTransformHandle, true);

    const sampleDecalCanvasAlpha = (canvas, u, v) => {
      if (!canvas?.width || !canvas?.height) return 0;
      const x = Math.max(0, Math.min(canvas.width - 1, Math.floor(u * canvas.width)));
      const y = Math.max(0, Math.min(canvas.height - 1, Math.floor((1 - v) * canvas.height)));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return 0;
      try {
        return context.getImageData(x, y, 1, 1).data[3] || 0;
      } catch {
        return 0;
      }
    };

    const getDecalLayerAtPointer = (event) => {
      const hit = getPaintHit(event);
      const runtime = runtimeRef.current;
      if (!hit?.point || !(runtime?.layerDecals instanceof Map)) return null;
      const currentLayers = Array.isArray(layersPropRef.current) ? layersPropRef.current : [];
      // Layers are shown top-to-bottom in the menu. Test topmost first.
      for (const layer of currentLayers) {
        const layerId = String(layer?.id || '');
        if (!layerId || layer?.visible === false || String(layer?.contentType || layer?.type || '') !== 'decal') continue;
        const record = runtime.layerDecals.get(layerId);
        const stroke = record?.projectedStroke;
        const cameraData = stroke?.projectionCameraData;
        const projectionCamera = cameraData ? deserializeProjectionCamera(cameraData) : null;
        if (!projectionCamera || !record?.sourceCanvas) continue;
        projectionCamera.updateMatrixWorld(true);
        projectionCamera.updateProjectionMatrix();
        const projected = hit.point.clone().project(projectionCamera);
        const u = (projected.x * 0.5) + 0.5;
        const v = (projected.y * 0.5) + 0.5;
        if (projected.z < -1 || projected.z > 1 || u <= 0 || u >= 1 || v <= 0 || v >= 1) continue;
        if (sampleDecalCanvasAlpha(record.sourceCanvas, u, v) <= 8) continue;
        return { layer, hit };
      }
      return null;
    };

    const startDecalLayerDrag = (event) => {
      if (!is3dLayerTransformToolActive() || event.ctrlKey || event.button !== 0) return false;
      const clicked = getDecalLayerAtPointer(event);
      const decalLayer = clicked?.layer || null;
      if (!decalLayer) return false;
      const layerId = String(decalLayer.id || '');
      if (!layerId) return false;
      if (clicked?.layer && layerId !== String(activeLayerIdRef.current || '') && typeof onSelectLayerRef.current === 'function') {
        onSelectLayerRef.current(layerId);
      }
      activeDecalDrag = {
        pointerId: event.pointerId,
        layerId,
        startClientX: Number(event.clientX) || 0,
        startClientY: Number(event.clientY) || 0,
        dragging: false,
      };
      event.preventDefault();
      event.stopPropagation();
      try { renderer.domElement.setPointerCapture(event.pointerId); } catch {}
      return true;
    };

    const continueDecalLayerDrag = (event) => {
      if (!activeDecalDrag || event.pointerId !== activeDecalDrag.pointerId) return false;
      event.preventDefault();
      event.stopPropagation();
      const deltaX = (Number(event.clientX) || 0) - activeDecalDrag.startClientX;
      const deltaY = (Number(event.clientY) || 0) - activeDecalDrag.startClientY;
      if (!activeDecalDrag.dragging && Math.hypot(deltaX, deltaY) < 4) return true;
      activeDecalDrag.dragging = true;
      const hit = getPaintHit(event);
      if (hit) commitDecalLayerPatchFromHit(activeDecalDrag.layerId, hit);
      return true;
    };

    const finishDecalLayerDrag = (event) => {
      if (!activeDecalDrag || (event && event.pointerId !== activeDecalDrag.pointerId)) return false;
      const pointerId = activeDecalDrag.pointerId;
      activeDecalDrag = null;
      try { renderer.domElement.releasePointerCapture(pointerId); } catch {}
      return true;
    };

    const handleDecalLayerWheel = (event) => handleViewportWheelZoom(event);

    renderer.domElement.addEventListener('wheel', handleDecalLayerWheel, { capture: true, passive: false });


    runtimeRef.current = {
      scene,
      camera,
      renderer,
      controls,
      defaultOrbitTarget: controls.target.clone(),
      grid,
      keyLight,
      keyLightTarget,
      resetProjectionForUvLayout,
      setProjectionStrokeCount,
      physicalPaint3d,
      clearProjectionPaintCompletely,
      restorePersistedPaintPreview,
      restorePersistedProjectionStrokes,
      persistProjectionStrokesNow,
      applyCurrentPaintMaterial,
      switchUvLayout,
      refreshVisibleMaterial,
      updateModelEdgeOverlay,
      refreshProjectionStrokeMaterials,
      getUvLayoutRevision: () => uvLayoutRevision,
      getPaintRegions: () => {
        const strokes = Array.isArray(runtimeRef.current?.projectionStrokes) ? runtimeRef.current.projectionStrokes : [];
        const visibleCount = Math.max(0, Math.min(strokes.length, projectionStrokeCountRef.current));
        return strokes.slice(0, visibleCount).flatMap((stroke, strokeIndex) => (
          (stroke?.meshes || []).map((mesh) => ({
            strokeIndex,
            sourceMeshUuid: mesh?.userData?.voxemeraPaintRegion?.sourceMeshUuid || '',
            mesh,
            sourceCanvas: stroke?.sourceCanvas || null,
            projectionCameraData: stroke?.projectionCameraData || null,
            projectionRect: stroke?.projectionRect || null,
          }))
        ));
      },
      projectionStrokes: [],
      getGpuPaintMap: () => activePaintSession?.gpuPaintTexture || null,
      rebuildGpuPaintMap: () => rebuildGpuPaintMap(),
      refreshProjectionLayerPresentation,
      flattenVisiblePaintIntoCanonicalAtlas,
      exportModel,
      drawCurrentTextureView,
      handleTextureViewPointerDown,
      handleTextureViewPointerMove,
      handleTextureViewPointerUp: finishTextureViewStroke,
      handleTextureViewPointerCancel: finishTextureViewStroke,
      layerDecals: new Map(),
      syncDecalLayers,
      getDefaultDecalPlacement,
      updateDecalTransformOverlay,
      setSurfaceMode: applySurfaceMode,
      resetSurfaceSelection,
      clearSurfaceSelection,
      invertSurfaceSelection,
      selectConnectedSurfaceFaces,
      growSurfaceSelection,
      shrinkSurfaceSelection,
      fillSelectedSurfaceFaces,
      frameSurfaceSelection,
      setViewPreset,
    };
    runtimeRef.current.syncDecalLayers(layersPropRef.current, activeLayerIdRef.current);

    const resize = () => {
      const width = Math.max(1, container.clientWidth || 1);
      const height = Math.max(1, container.clientHeight || 1);
      renderer.setSize(width, height, false);
      runtimeRef.current?.updateModelEdgeOverlay?.(Boolean(viewSettingsRef.current?.showModelEdges));
      configurePreviewResolution(width, height, Math.min(window.devicePixelRatio || 1, 2));
      const selectionScale = Math.min(window.devicePixelRatio || 1, 2);
      selectionCanvas.width = Math.max(1, Math.round(width * selectionScale));
      selectionCanvas.height = Math.max(1, Math.round(height * selectionScale));
      selectionCanvas.style.width = `${width}px`;
      selectionCanvas.style.height = `${height}px`;
      drawSelectionOverlay();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      updateDecalTransformOverlay();
    };

    const updateControlsEnabled = (enabled) => {
      keyboardNavigationActive = Boolean(enabled);
      controls.enabled = Boolean(enabled);
      renderer.domElement.style.cursor = enabled ? 'grab' : 'default';
    };

    const handleKeyDown = (event) => {
      if (handleViewportNavigationKey(event)) return;
      if (event.key === 'Alt') {
        altKeyHeld = true;
        updateOrbitPivotMarker();
      }
      if (event.key === 'Escape' && (activeSelection || selectionDraft)) {
        activeSelection = null;
        selectionDraft = null;
        activeSelectionPointerId = null;
        drawSelectionOverlay();
        return;
      }
      if (event.key === 'Enter' && selectionDraft?.shape === 'surface') {
        finishSelection(null, true);
        return;
      }
      if (event.key !== 'Control' || event.repeat) return;
      updateControlsEnabled(true);
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Alt') {
        altKeyHeld = false;
        updateOrbitPivotMarker();
      }
      if (event.key !== 'Control') return;
      updateControlsEnabled(false);
    };

    const handleWindowBlur = () => {
      altKeyHeld = false;
      updateOrbitPivotMarker();
      updateControlsEnabled(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pagehide', handlePageHidePaintSave);

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    let animationFrame = 0;
    let animationRunning = false;
    let contextIsLost = false;

    const animate = (now = 0) => {
      if (!animationRunning || contextIsLost) return;
      animationFrame = requestAnimationFrame(animate);
      controls.update();
      updateOrbitPivotMarker();
      updateDecalTransformOverlay();
      if (faceSelectionOverlayGroup.children.length) syncFaceSelectionOverlayMatrices();
      physicalPaint3d.frame(now);

      // Debug zoom is relative to the framed-model camera distance. 100% is
      // the result of "Frame model"; moving closer increases the percentage.
      if (now - zoomIndicatorLastUpdateRef.current >= 80) {
        const currentDistance = camera.position.distanceTo(controls.target);
        const referenceDistance = Math.max(zoomReferenceDistanceRef.current || currentDistance, 0.00000001);
        const nextZoomPercent = Math.max(1, Math.min(99999, Math.round((referenceDistance / Math.max(currentDistance, 0.00000001)) * 100)));
        setZoomPercent((previous) => (previous === nextZoomPercent ? previous : nextZoomPercent));
        zoomIndicatorLastUpdateRef.current = now;
      }

      renderer.render(scene, camera);
    };

    const startAnimation = () => {
      if (animationRunning || contextIsLost) return;
      animationRunning = true;
      animate();
    };

    const stopAnimation = () => {
      animationRunning = false;
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    };

    const handleWebGlContextLost = (event) => {
      event.preventDefault();
      contextIsLost = true;
      stopAnimation();
      stop3dAirbrushTimer(activePaintStroke);
      activePaintStroke = null;
      setPreviewCanvasVisible(true);
      disposeLivePbrPreviewStroke();
      clearPreviewCanvas();
    };

    const handleWebGlContextRestored = () => {
      contextIsLost = false;
      rebuildProjectionResourcesAfterContextRestore();
      startAnimation();
    };

    renderer.domElement.addEventListener('webglcontextlost', handleWebGlContextLost, false);
    renderer.domElement.addEventListener('webglcontextrestored', handleWebGlContextRestored, false);
    startAnimation();
    bootProfiler.mark('Animation loop started', 'The viewport is now issuing a render every animation frame.');
    bootProfiler.nextFrame('First viewport frame reached', 'Measures whether setup blocked the browser before its first visible 3D frame.');

    return () => {
      stopAnimation();
      physicalPaint3d.dispose();
      if (typeof window !== 'undefined') {
        if (window.__vxPhysicalPaint3d === physicalPaint3d) window.__vxPhysicalPaint3d = null;
        window.__vxPhysicalPaint3dAll = (window.__vxPhysicalPaint3dAll || []).filter((entry) => entry.bridge !== physicalPaint3d);
      }
      renderer.domElement.removeEventListener('webglcontextlost', handleWebGlContextLost, false);
      renderer.domElement.removeEventListener('webglcontextrestored', handleWebGlContextRestored, false);
      resizeObserver.disconnect();
      setPreviewCanvasVisible(true);
      disposeLivePbrPreviewStroke();
      clearPreviewCanvas();
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pagehide', handlePageHidePaintSave);
      renderer.domElement.removeEventListener('pointerdown', handlePaintPointerDown, true);
      renderer.domElement.removeEventListener('wheel', handleDecalLayerWheel, true);
      window.removeEventListener('pointermove', updateDecalTransformHandle, true);
      window.removeEventListener('pointerup', finishDecalTransformHandle, true);
      window.removeEventListener('pointercancel', finishDecalTransformHandle, true);
      decalTransformOverlay.remove();
      renderer.domElement.removeEventListener('pointermove', handlePaintPointerMove, true);
      renderer.domElement.removeEventListener('pointerleave', hide3dBrushCursor, true);
      renderer.domElement.removeEventListener('pointerup', finishPaintStroke, true);
      renderer.domElement.removeEventListener('pointercancel', finishPaintStroke, true);
      window.removeEventListener('pointerup', finishPaintStroke, true);
      window.removeEventListener('pointercancel', finishPaintStroke, true);
      renderer.domElement.removeEventListener('contextmenu', handleSelectionContextMenu, true);
      renderer.domElement.removeEventListener('dblclick', handleSelectionDoubleClick, true);
      if (paintSaveTimer) window.clearTimeout(paintSaveTimer);
      persistProjectionStrokesNow().catch(() => {});
      clearExactProjectionStrokes();
      runtimeRef.current?.layerDecals?.forEach?.((record) => disposeLayerDecalRecord(record));
      projectionDepthCache.forEach((entry) => entry?.target?.dispose?.());
      projectionDepthCache.clear();
      disposePaintSessions();
      controls.removeEventListener('change', handleControlsChange);
      controls.dispose();
      scene.remove(orbitPivotMarker);
      orbitPivotDotGeometry.dispose();
      orbitPivotDotMaterial.dispose();
      orbitPivotRingGeometry.dispose();
      orbitPivotRingMaterial.dispose();
      if (currentModelRef.current) {
        scene.remove(currentModelRef.current);
        disposeObject(currentModelRef.current);
        currentModelRef.current = null;
      }
      scene.remove(gpuSelectionOverlay);
      if (surfaceSelectionOverlapTimer) window.clearTimeout(surfaceSelectionOverlapTimer);
      finishFaceSelectDrag(null);
      disposeFaceSelectionOverlay();
      scene.remove(faceSelectionOverlayGroup);
      faceSelectionOverlayMaterial.dispose();
      surfacePreviewDepthMaterial.dispose();
      surfacePreviewCoverageMaterial.dispose();
      surfacePreviewMaskCache = null;
      gpuSelectionLineGeometry.dispose();
      gpuSelectionLineBaseMaterial.dispose();
      gpuSelectionLineDashMaterial.dispose();
      gpuSelectionHandleGeometry.dispose();
      gpuSelectionHandleOuterMaterial.dispose();
      gpuSelectionHandleInnerMaterial.dispose();
      gpuSelectionMidpointGeometry.dispose();
      gpuSelectionMidpointOuterMaterial.dispose();
      gpuSelectionMidpointInnerMaterial.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      if (selectionCanvas.parentNode === container) container.removeChild(selectionCanvas);
      if (previewCanvas.parentNode === container) container.removeChild(previewCanvas);
      if (brushCursorOverlay.parentNode === container) container.removeChild(brushCursorOverlay);
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.grid) runtime.grid.visible = showGrid;
  }, [showGrid]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.refreshProjectionLayerPresentation?.();
    runtime.rebuildGpuPaintMap?.();
    runtime.renderer?.render?.(runtime.scene, runtime.camera);
  }, [layers, activeLayerId]);

  useEffect(() => {
    const model = currentModelRef.current;
    const runtime = runtimeRef.current;
    if (!model || !modelName || !runtime) return;
    if (skipNextUvLayoutEffectRef.current) {
      skipNextUvLayoutEffectRef.current = false;
      return;
    }

    const nextLayoutMode = clampUvLayoutMode(uvLayoutMode);
    if (nextLayoutMode === UV_LAYOUT_MODE_ORIGINAL && !modelSupportsOriginalUvs) {
      setError("This model has no usable imported UV layout. Choose Create Voxemera UVs in Transform > UV layout.");
      return;
    }
    setError('');

    let cancelled = false;
    runtime.resetProjectionForUvLayout?.();
    (async () => {
      await runtime.switchUvLayout?.(model, nextLayoutMode, uvTileCount, uvResolutionPerTile);
      await runtime.restorePersistedProjectionStrokes?.();
      if (cancelled) return;
      model.updateMatrixWorld(true);
      model.traverse((child) => {
        if (!child?.isMesh || !child.geometry) return;
        const uvAttribute = child.geometry.getAttribute?.('uv');
        if (uvAttribute) uvAttribute.needsUpdate = true;
        child.geometry.computeBoundingBox?.();
        child.geometry.computeBoundingSphere?.();
      });

      saveStoredUvTileCount(modelName, uvTileCount);
      saveStoredUvLayoutMode(modelName, nextLayoutMode);
      saveStoredUvResolution(modelName, uvResolutionPerTile);
      fitDirectionalShadowToObject(runtime, model);
      runtime.refreshVisibleMaterial?.();
    })().catch((layoutError) => {
      if (!cancelled) setError(layoutError?.message || 'Unable to change the UV layout.');
    });
    return () => { cancelled = true; };
  }, [uvTileCount, uvResolutionPerTile, uvLayoutMode, modelName, modelSupportsOriginalUvs]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || !currentModelRef.current) return;
    runtime.refreshVisibleMaterial?.();
  }, [showUvColours]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || !currentModelRef.current) return;
    runtime.updateModelEdgeOverlay?.(showModelEdges);
  }, [showModelEdges, showModelEdgesDebug]);

  useEffect(() => {
    if (viewportDisplayMode !== 'texture') return undefined;
    let cancelled = false;
    let timer = 0;

    const refreshTextureView = () => {
      if (cancelled) return;
      runtimeRef.current?.drawCurrentTextureView?.(textureViewCanvasRef.current);
      timer = window.setTimeout(refreshTextureView, 200);
    };

    refreshTextureView();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [viewportDisplayMode, modelName, uvLayoutMode, uvTileCount, uvResolutionPerTile]);

  useEffect(() => {
    let cancelled = false;
    const runtime = runtimeRef.current;
    if (!runtime || !activeModelFile) return undefined;

    setLoading(true);
    setError('');

    let profiler = loadProfilerRef.current;
    if (!profiler || shouldPersistExplicitModelFile) {
      setLoadDebugEntries([]);
      profiler = createModelLoadProfiler((entry, entries) => {
        if (!cancelled) setLoadDebugEntries(entries);
      });
      loadProfilerRef.current = profiler;
    }
    profiler.mark('Model load effect: start', 'React has received the active model File and is starting the loader.', { name: activeModelFile.name, bytes: activeModelFile.size });

    loadModelFile(activeModelFile, profiler)
      .then(async (model) => {
        if (cancelled) {
          disposeObject(model);
          return;
        }
        profiler.mark('Loader returned model', 'Parsing is complete; beginning scene preparation.');
        if (!model) throw new Error('The model did not contain a renderable scene.');

        if (currentModelRef.current) {
          profiler.mark('Previous model disposal: start', 'Removing old geometry, materials and textures before installing the new model.');
          runtime.scene.remove(currentModelRef.current);
          disposeObject(currentModelRef.current);
          profiler.mark('Previous model disposal: complete', 'Old Three.js resources were disposed.');
        }

        const name = String(activeModelFile.name || 'Imported model');
        model.name = name;
        profiler.mark('Renderable materials: start', 'Normalizing mesh materials and render flags.');
        ensureRenderableMaterials(model);
        profiler.mark('Renderable materials: complete', 'Material normalization completed.');

        const transformRoot = new THREE.Group();
        transformRoot.name = `${name} transform`;
        transformRoot.userData.voxemeraModelUvCacheFingerprint = getModelUvCacheFingerprint(activeModelFile);
        transformRoot.add(model);

        if (getExtension(name) === 'obj') {
          transformRoot.rotation.x = -Math.PI / 2;
        }

        profiler.mark('Bounds/centering: start', 'Computing world-space bounds across every mesh and moving the model to the ground.');
        centreAndGroundObject(transformRoot);
        profiler.mark('Bounds/centering: complete', 'Initial model bounds and transform are ready.');
        transformRoot.userData.voxemeraOriginalTransform = {
          position: transformRoot.position.clone(),
          quaternion: transformRoot.quaternion.clone(),
          scale: transformRoot.scale.clone(),
        };

        profiler.mark('Stored transform: start', 'Reading and applying the saved position/rotation/scale.');
        loadStoredTransform(name, transformRoot);
        profiler.mark('Stored transform: complete', 'Saved transform processing completed.');
        profiler.mark('UV capability scan: start', 'Traversing geometry to determine whether every mesh has usable original UVs.');
        const supportsOriginalUvs = modelSupportsOriginalUvLayout(transformRoot);
        profiler.mark('UV capability scan: complete', 'Original UV support determined.', { supportsOriginalUvs });
        const storedTileCount = loadStoredUvTileCount(name);
        const nextTileCount = clampUvTileCount(storedTileCount || uvTileCount || 1);
        const storedUvResolution = loadStoredUvResolution(name);
        const nextUvResolution = clampUvResolution(storedUvResolution || uvResolutionPerTile || DEFAULT_GENERATED_UV_RESOLUTION);
        const storedLayoutMode = (() => {
          try { return localStorage.getItem(uvLayoutModeStorageKey(name)); } catch { return null; }
        })();
        const nextLayoutMode = storedLayoutMode
          ? clampUvLayoutMode(storedLayoutMode)
          : (supportsOriginalUvs ? UV_LAYOUT_MODE_ORIGINAL : UV_LAYOUT_MODE_GENERATED);
        // The loading popup covers the viewport, so there is no need to clone the
        // complete model and all of its materials. Keep one real paint target
        // hidden until its restored appearance is ready.
        transformRoot.visible = false;
        profiler.mark('Scene insertion', 'Adding one hidden paint target behind the loading popup.');
        runtime.resetSurfaceSelection?.();
        runtime.scene.add(transformRoot);
        currentModelRef.current = transformRoot;
        await runtime.switchUvLayout?.(transformRoot, nextLayoutMode, nextTileCount, nextUvResolution);
        if (cancelled) {
          runtime.scene.remove(transformRoot);
          if (currentModelRef.current === transformRoot) currentModelRef.current = null;
          disposeObject(transformRoot);
          return;
        }
        profiler.mark('Shadow fitting: start', 'Recomputing model bounds to size and position the directional-light shadow camera.');
        fitDirectionalShadowToObject(runtime, transformRoot);
        profiler.mark('Shadow fitting: complete', 'Shadow camera is fitted to the model.');
        profiler.mark('Camera framing: start', 'Computing bounds again to position the camera around the complete model while it remains hidden.');
        frameObject(runtime.camera, runtime.controls, transformRoot);
        runtime.defaultOrbitTarget?.copy?.(runtime.controls.target);
        const framedDistance = runtime.camera.position.distanceTo(runtime.controls.target);
        zoomReferenceDistanceRef.current = Math.max(framedDistance, 0.00000001);
        setZoomPercent(100);
        runtime.controls?.update?.();
        // Do not reveal either the imported material or a colour-atlas-only
        // preview here. Projected strokes are restored separately, so either
        // intermediate would visibly flash a state that is not the user's final
        // painted model. Keep the loading cover in place until the complete
        // editable appearance is ready for one atomic reveal.
        transformRoot.visible = false;
        runtime.renderer.render(runtime.scene, runtime.camera);
        profiler.mark(
          'Camera framing: complete',
          'The final painted model remains covered while its atlas and projected strokes restore off-screen.',
        );
        // switchUvLayout above has already applied the requested atlas. The state
        // updates below would otherwise make the UV effect rebuild it immediately.
        skipNextUvLayoutEffectRef.current = true;
        setModelSupportsOriginalUvs(supportsOriginalUvs);
        setModelName(name);
        setUvTileCount(nextTileCount);
        setUvResolutionPerTile(nextUvResolution);
        setUvLayoutMode(nextLayoutMode);
        setShowTransformPanel(false);
        if (!supportsOriginalUvs && nextLayoutMode === UV_LAYOUT_MODE_ORIGINAL) {
          setError('This model has no usable imported UV layout. Choose Create Voxemera UVs in Transform > UV layout.');
        }
        profiler.mark('React state queued', 'Model metadata and UV settings were queued for React rendering.');

        // Restore the complete painted state behind the loading popup.
        profiler.mark('Persisted paint restore: start', 'Decoding saved paint while the single real model remains hidden.');
        try {
          await runtime.restorePersistedProjectionStrokes?.();
          profiler.mark('Persisted paint restore: complete', 'Saved paint and destructive erase state have been restored.');
        } catch (paintRestoreError) {
          profiler.mark('Persisted paint restore: failed', paintRestoreError?.message || 'Unable to restore saved paint.');
          setError(paintRestoreError?.message || 'The model loaded, but its saved paint could not be restored.');
        }
        if (cancelled) return;

        // Reveal the already-restored real model in one frame. The loading popup
        // has covered every temporary UV/material state without a duplicate scene.
        transformRoot.visible = true;
        runtime.renderer.render(runtime.scene, runtime.camera);
        profiler.mark('Final painted model visible', 'The single restored paint target was revealed atomically.');
        profiler.nextFrame('First frame with final painted model reached', 'A large delay here points to GPU texture upload, shader linking, shadow-map rendering or a blocked main thread.')
          .then(() => profiler.nextFrame('Second frame with model reached', 'Second frame separates one-time GPU setup from ongoing rendering cost.'))
          .then(() => profiler.mark('MODEL READY FOR INPUT', 'All measured load/setup stages and two rendered frames have completed.'));

        if (shouldPersistExplicitModelFile) {
          saveModelFile(activeModelFile).catch(() => {});
        }
      })
      .catch((loadError) => {
        if (cancelled) return;
        profiler.mark('MODEL LOAD FAILED', loadError?.message || 'Unable to load this model.');
        setError(loadError?.message || 'Unable to load this model.');
        setModelName('');
        setModelSupportsOriginalUvs(false);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          profiler.mark('Loading flag cleared', 'React loading overlay/state has been disabled.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeModelFile, shouldPersistExplicitModelFile]);


  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || typeof runtime.syncDecalLayers !== 'function') return;
    runtime.syncDecalLayers(Array.isArray(layers) ? layers : [], activeLayerId).catch?.(() => {});
  }, [activeLayerId, layers, modelName]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    runtime?.updateDecalTransformOverlay?.();
    runtime?.renderer?.render?.(runtime.scene, runtime.camera);
  }, [activeTool, activeLayerId, layers]);


  return (
    <section style={{ position: 'relative', flex: '1 1 auto', minWidth: 0, minHeight: 0, overflow: 'hidden', background: '#666666' }}>
      <div
        ref={containerRef}
        aria-label="3D model viewport"
        style={{ position: 'absolute', inset: 0, visibility: viewportDisplayMode === '3d' ? 'visible' : 'hidden' }}
        onContextMenu={(event) => event.preventDefault()}
      />

      {viewportDisplayMode === 'texture' ? (
        <div
          aria-label="Current UV colour texture"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            backgroundColor: '#4d4d4d',
            backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.045) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.045) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.045) 75%), linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.045) 75%)',
            backgroundSize: '24px 24px',
            backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0',
          }}
        >
          <canvas
            ref={textureViewCanvasRef}
            onPointerDown={handleTextureViewPointerDown}
            onPointerMove={handleTextureViewPointerMove}
            onPointerUp={handleTextureViewPointerUp}
            onPointerCancel={handleTextureViewPointerCancel}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              background: '#ffffff',
              boxShadow: '0 12px 40px rgba(0,0,0,0.38)',
              imageRendering: 'auto',
              cursor: 'crosshair',
              touchAction: 'none',
            }}
          />
        </div>
      ) : null}

      <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 2, pointerEvents: 'none' }}>
        <div style={{ maxWidth: 'min(620px, 72vw)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(35,35,35,0.86)', color: 'rgba(255,255,255,0.94)', padding: '8px 10px', boxShadow: '0 8px 30px rgba(0,0,0,0.28)', pointerEvents: 'auto' }}>
          <div style={{ fontSize: 12, fontWeight: 900 }}>{viewportDisplayMode === '3d' ? '3D model viewer' : 'Current UV colour texture'}</div>
          <div style={{ marginTop: 3, fontSize: 11, color: 'rgba(255,255,255,0.68)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {loading ? 'Loading model…' : modelName || 'No model loaded'}
          </div>
        </div>
      </div>



      {exportOverlay.active ? (
        <>
          <style>{`@keyframes voxemeraExportSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <div
            aria-live="polite"
            aria-busy="true"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(10, 10, 10, 0.42)',
              backdropFilter: 'blur(2px)',
              pointerEvents: 'all',
            }}
          >
            <div
              style={{
                minWidth: 280,
                maxWidth: 'min(88vw, 420px)',
                borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.16)',
                background: 'rgba(25,25,25,0.96)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
                padding: '18px 18px 16px',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  border: '4px solid rgba(255,255,255,0.18)',
                  borderTopColor: '#4dd0ff',
                  animation: 'voxemeraExportSpin 0.9s linear infinite',
                  flex: '0 0 auto',
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 900, lineHeight: 1.2 }}>
                  {exportOverlay.title || 'Exporting model…'}
                </div>
                <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.4, color: 'rgba(255,255,255,0.72)' }}>
                  {exportOverlay.detail || 'Please wait…'}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {viewportDisplayMode === '3d' ? (
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 3,
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(20,20,20,0.86)',
          color: '#ffffff',
          padding: '7px 10px',
          fontSize: 12,
          fontWeight: 900,
          fontVariantNumeric: 'tabular-nums',
          pointerEvents: 'none',
          boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
        }}
        title="Zoom relative to the framed model view"
      >
        Zoom: {zoomPercent}%
      </div>
      ) : null}

      <input
        ref={pbrFilesInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.bmp"
        multiple
        onChange={handlePbrFilesSelected}
        style={{ display: 'none' }}
      />
      <input
        ref={pbrFolderInputRef}
        type="file"
        multiple
        onChange={handlePbrFolderSelected}
        style={{ display: 'none' }}
      />
      <input
        ref={pbrZipInputRef}
        type="file"
        accept=".zip"
        onChange={handlePbrZipSelected}
        style={{ display: 'none' }}
      />

      {toolbarPortalTarget ? createPortal((
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
          <ViewportButton onClick={onChooseModel} title="Import a GLB, GLTF, OBJ, STL or ZIP model bundle">Import model</ViewportButton>
          <ViewportButton onClick={resetView} disabled={!currentModelRef.current} title="Frame the complete model">Frame model</ViewportButton>
          <ViewportButton onClick={() => setShowTransformPanel((value) => !value)} disabled={!currentModelRef.current} title="Rotate, flip or reset the imported model">
            Transform {showTransformPanel ? '▲' : '▼'}
          </ViewportButton>
          <ViewportButton onClick={() => setShowGrid((value) => !value)} title="Toggle the ground grid">{showGrid ? 'Hide grid' : 'Show grid'}</ViewportButton>
          <ViewportButton
            onClick={() => setViewportDisplayMode((mode) => (mode === '3d' ? 'texture' : '3d'))}
            disabled={!currentModelRef.current}
            title={viewportDisplayMode === '3d' ? 'Show the current live UV colour texture' : 'Return to the 3D model'}
          >
            {viewportDisplayMode === '3d' ? 'Texture view' : '3D view'}
          </ViewportButton>

          {showTransformPanel && currentModelRef.current ? (
            <div style={{ position: 'absolute', top: 34, left: 0, zIndex: 100000, width: 260, borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(30,30,30,0.98)', boxShadow: '0 12px 34px rgba(0,0,0,0.48)', padding: 10, color: 'rgba(255,255,255,0.94)' }}>
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Model transform</div>
          <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr', gap: 6, alignItems: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 900, textAlign: 'center' }}>X</div>
            <ViewportButton onClick={() => transformModel('rotate-x-negative')}>−90°</ViewportButton>
            <ViewportButton onClick={() => transformModel('rotate-x-positive')}>+90°</ViewportButton>
            <div style={{ fontSize: 11, fontWeight: 900, textAlign: 'center' }}>Y</div>
            <ViewportButton onClick={() => transformModel('rotate-y-negative')}>−90°</ViewportButton>
            <ViewportButton onClick={() => transformModel('rotate-y-positive')}>+90°</ViewportButton>
            <div style={{ fontSize: 11, fontWeight: 900, textAlign: 'center' }}>Z</div>
            <ViewportButton onClick={() => transformModel('rotate-z-negative')}>−90°</ViewportButton>
            <ViewportButton onClick={() => transformModel('rotate-z-positive')}>+90°</ViewportButton>
          </div>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '10px 0' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            <ViewportButton onClick={() => transformModel('flip-x')}>Flip X</ViewportButton>
            <ViewportButton onClick={() => transformModel('flip-y')}>Flip Y</ViewportButton>
            <ViewportButton onClick={() => transformModel('flip-z')}>Flip Z</ViewportButton>
          </div>
          <div style={{ marginTop: 8 }}>
            <ViewportButton onClick={() => transformModel('reset')}>Reset transform</ViewportButton>
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '10px 0' }} />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: 11,
              fontWeight: 850,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <span>Show controls panel</span>
            <input
              type="checkbox"
              checked={showControlsPanel}
              onChange={(event) => setShowControlsPanel(event.target.checked)}
            />
          </label>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '10px 0' }} />

          <div style={{ fontSize: 12, fontWeight: 900 }}>Image layers</div>
          {activeDecalLayer ? (
            <>
              <div style={{ marginTop: 6, fontSize: 11, fontWeight: 850, color: 'rgba(255,255,255,0.88)' }}>
                Selected: {activeDecalLayer.name || 'Imported image'}
              </div>
              <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.62)', lineHeight: 1.35 }}>
                With the Transform tool selected: drag the image to move it, drag a corner handle to resize it, or drag the round handle to rotate it.
              </div>
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>
                Scale: {Math.round((Math.max(0.05, Number(activeDecalLayer.decalData?.scale) || 1)) * 100)}%
              </div>
              <input
                type="range"
                min="10"
                max="400"
                step="1"
                value={Math.round((Math.max(0.05, Number(activeDecalLayer.decalData?.scale) || 1)) * 100)}
                onChange={(event) => updateSelectedDecalLayer({
                  scale: Math.max(0.05, Math.min(4, (Number(event.target.value) || 100) / 100)),
                })}
                style={{ width: '100%', marginTop: 6 }}
              />
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>
                Rotation: {Math.round(Number(activeDecalLayer.decalData?.rotation) || 0)}°
              </div>
              <input
                type="range"
                min="0"
                max="359"
                step="1"
                value={Math.round(Number(activeDecalLayer.decalData?.rotation) || 0)}
                onChange={(event) => updateSelectedDecalLayer({
                  rotation: Math.max(0, Math.min(359, Number(event.target.value) || 0)),
                })}
                style={{ width: '100%', marginTop: 6 }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
                <ViewportButton onClick={() => updateSelectedDecalLayer((current) => ({ flipX: !current.flipX }))}>Flip X</ViewportButton>
                <ViewportButton onClick={() => updateSelectedDecalLayer((current) => ({ flipY: !current.flipY }))}>Flip Y</ViewportButton>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.56)', lineHeight: 1.35 }}>
              Import an image while in 3D mode to create a movable image/decal layer. Select that layer, then drag it across the model here.
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 900 }}>UV layout</div>
          <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.62)', lineHeight: 1.4 }}>
            Generate UVs with ArmorPaint-style 66° chart grouping, minimum-area island rotation and skyline packing.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
            <ViewportButton
              onClick={() => setUvLayoutMode(UV_LAYOUT_MODE_ORIGINAL)}
              disabled={!modelSupportsOriginalUvs || uvLayoutMode === UV_LAYOUT_MODE_ORIGINAL}
            >
              Use imported UVs
            </ViewportButton>
            <ViewportButton
              onClick={() => setUvLayoutMode(UV_LAYOUT_MODE_GENERATED)}
              disabled={uvLayoutMode === UV_LAYOUT_MODE_GENERATED}
            >
              Create Voxemera UVs
            </ViewportButton>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>
            UV stacks: {uvTileCount}
          </div>
          <input
            type="range"
            min="1"
            max="16"
            step="1"
            value={uvTileCount}
            onChange={(event) => setUvTileCount(clampUvTileCount(event.target.value))}
            disabled={uvLayoutMode !== UV_LAYOUT_MODE_GENERATED}
            style={{ width: '100%', marginTop: 5 }}
          />
          <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>
            Resolution per stack: {uvResolutionPerTile === 1080 ? '1080p' : `${uvResolutionPerTile / 1024}K`}
          </div>
          <input
            type="range"
            min="0"
            max={UV_RESOLUTION_OPTIONS.length - 1}
            step="1"
            value={Math.max(0, UV_RESOLUTION_OPTIONS.indexOf(clampUvResolution(uvResolutionPerTile)))}
            onChange={(event) => setUvResolutionPerTile(UV_RESOLUTION_OPTIONS[Math.max(0, Math.min(UV_RESOLUTION_OPTIONS.length - 1, Number(event.target.value) || 0))])}
            disabled={uvLayoutMode !== UV_LAYOUT_MODE_GENERATED}
            style={{ width: '100%', marginTop: 5 }}
          />
          {(() => {
            const requested = getGeneratedAtlasDimensions(uvTileCount, uvResolutionPerTile);
            const maximum = getMaximumUvResolutionForTextureSize(uvTileCount, maxTextureSize);
            const effective = getGeneratedAtlasDimensions(uvTileCount, Math.min(uvResolutionPerTile, maximum));
            return (
              <div style={{ marginTop: 5, fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.35 }}>
                {uvLayoutMode === UV_LAYOUT_MODE_GENERATED
                  ? `Atlas: ${effective.width}×${effective.height}px (${uvTileCount} stack${uvTileCount === 1 ? '' : 's'} at ${effective.resolutionPerTile === 1080 ? '1080p' : `${effective.resolutionPerTile / 1024}K`} each).${maximum < uvResolutionPerTile ? ` ${uvResolutionPerTile === 1080 ? '1080p' : `${uvResolutionPerTile / 1024}K`} is selected, but this ${uvTileCount}-stack atlas exceeds the GPU's ${maxTextureSize}px texture limit.` : ''}`
                  : (modelSupportsOriginalUvs ? 'The model’s imported UV coordinates are active.' : 'No imported UVs are available; create Voxemera UVs.')}
              </div>
            );
          })()}
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer', userSelect: 'none' }}
          >
            <input
              type="checkbox"
              checked={showUvColours}
              disabled={uvLayoutMode !== UV_LAYOUT_MODE_GENERATED}
              onChange={(event) => {
                const checked = event.target.checked;
                setShowUvColours(checked);
              }}
            />
            Show generated UV stack colours
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={showModelEdges}
              onChange={(event) => setShowModelEdges(event.target.checked)}
            />
            Show edges
          </label>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '10px 0' }} />

          <div style={{ fontSize: 12, fontWeight: 900 }}>PBR pen texture</div>
          <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.62)', lineHeight: 1.35 }}>
            Adds a full PBR texture set to the normal pen tool and fill tool in both 3D view and Texture view. Airbrush, pencil and imported brushes continue to use the regular solid-colour path.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 8 }}>
            <ViewportButton onClick={handleChoosePbrFiles} disabled={pbrBrushLoading}>Files</ViewportButton>
            <ViewportButton onClick={handleChoosePbrFolder} disabled={pbrBrushLoading}>Folder</ViewportButton>
            <ViewportButton onClick={handleChoosePbrZip} disabled={pbrBrushLoading}>ZIP</ViewportButton>
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginTop: 10,
              fontSize: 11,
              fontWeight: 850,
              cursor: pbrBrushSets.length ? 'pointer' : 'default',
              userSelect: 'none',
              opacity: pbrBrushSets.length ? 1 : 0.55,
            }}
          >
            <span>Enable PBR pen painting</span>
            <input
              type="checkbox"
              checked={pbrBrushEnabled}
              disabled={!pbrBrushSets.length}
              onChange={(event) => setPbrBrushEnabled(event.target.checked)}
            />
          </label>
          {pbrBrushSets.length ? (
            <>
              <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.72)', lineHeight: 1.35 }}>
                Source: {pbrBrushSourceLabel || 'Loaded PBR set'}
              </div>
              {pbrBrushSets.length > 1 ? (
                <select
                  value={pbrBrushSelectedSetKey}
                  onChange={(event) => setPbrBrushSelectedSetKey(event.target.value)}
                  style={{ width: '100%', marginTop: 8, borderRadius: 6, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(18,18,18,0.92)', color: '#fff', padding: '7px 8px', fontSize: 12, fontWeight: 700 }}
                >
                  {pbrBrushSets.map((entry) => (
                    <option key={entry.key} value={entry.key}>{entry.label}</option>
                  ))}
                </select>
              ) : null}
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>Texture scale: {pbrBrushScalePercent}%</div>
              <input
                type="range"
                min="10"
                max="800"
                step="10"
                value={pbrBrushScalePercent}
                onChange={(event) => setPbrBrushScalePercent(Math.max(10, Math.min(800, Number(event.target.value) || DEFAULT_PBR_TEXTURE_SCALE)))}
                style={{ width: '100%', marginTop: 6 }}
              />
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 850 }}>Texture angle: {pbrBrushAngleDegrees}°</div>
              <input
                type="range"
                min="0"
                max="359"
                step="1"
                value={pbrBrushAngleDegrees}
                onChange={(event) => setPbrBrushAngleDegrees(Math.max(0, Math.min(359, Number(event.target.value) || 0)))}
                style={{ width: '100%', marginTop: 6 }}
              />
              <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.56)', lineHeight: 1.35 }}>
                Loaded maps: {(pbrBrushSets.find((entry) => entry.key === pbrBrushSelectedSetKey)?.mapTypes || [])
                  .map((type) => PBR_MAP_TYPE_LABELS[type] || type)
                  .join(', ') || 'None'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
                <ViewportButton onClick={() => setPbrBrushEnabled(false)} disabled={!pbrBrushEnabled}>Disable</ViewportButton>
                <ViewportButton onClick={clearPbrBrushSelection}>Remove</ViewportButton>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.56)', lineHeight: 1.35 }}>
              Load loose files, a folder, or a ZIP. The importer groups base colour/albedo, normal, roughness, metallic, height and AO maps into one or more usable PBR sets.
            </div>
          )}
          {pbrBrushLoading ? (
            <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.62)' }}>Loading PBR set…</div>
          ) : null}
          {pbrBrushError ? (
            <div style={{ marginTop: 8, fontSize: 10, color: '#ffb4b4', lineHeight: 1.35 }}>{pbrBrushError}</div>
          ) : null}

            </div>
          ) : null}
        </div>
      ), toolbarPortalTarget) : null}

      {viewportDisplayMode === '3d' && currentModelRef.current ? (() => {
        const hasSelection = surfaceSelectionInfo.count > 0;
        const selectingFaces = surfaceMode === 'faces' || surfaceMode === 'material';
        const modeButtons = [
          { id: 'free', label: 'Free paint', title: 'Paint anywhere on the model (clears the face selection)', active: surfaceMode === 'paint' && !hasSelection },
          { id: 'faces', label: 'Select faces', title: 'Click a face to select it · Shift+click adds or removes · drag to brush faces in · double-click selects connected faces', active: surfaceMode === 'faces' },
          { id: 'material', label: 'Select material', title: 'Click to select every face that uses the same imported material · Shift+click adds or removes', active: surfaceMode === 'material' },
          { id: 'paint', label: 'Paint selection', title: 'Paint, restricted to the selected faces', active: surfaceMode === 'paint' && hasSelection, disabled: !hasSelection },
        ];
        const chooseMode = (id) => {
          if (id === 'free') {
            runSurfaceAction('clearSurfaceSelection');
            setSurfaceMode('paint');
            return;
          }
          setSurfaceMode(id === 'paint' ? 'paint' : id);
        };
        const actionButtonStyle = {
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 6,
          background: 'rgba(255,255,255,0.06)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 800,
          padding: '4px 7px',
          cursor: 'pointer',
        };
        return (
          <div
            onPointerDown={(event) => event.stopPropagation()}
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxWidth: 'min(420px, calc(100% - 24px))',
              padding: 8,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.16)',
              background: 'rgba(20,20,20,0.88)',
              color: '#fff',
              boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
            }}
          >
            <div role="group" aria-label="3D surface tool" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {modeButtons.map((button) => (
                <button
                  key={button.id}
                  type="button"
                  title={button.title}
                  disabled={button.disabled}
                  aria-pressed={button.active}
                  onClick={() => chooseMode(button.id)}
                  style={{
                    ...actionButtonStyle,
                    background: button.active ? '#2f7cf6' : actionButtonStyle.background,
                    borderColor: button.active ? '#2f7cf6' : actionButtonStyle.border,
                    opacity: button.disabled ? 0.45 : 1,
                    cursor: button.disabled ? 'default' : 'pointer',
                  }}
                >
                  {button.label}
                </button>
              ))}
            </div>
            {hasSelection || selectingFaces ? (
              <>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', lineHeight: 1.35 }}>
                  {hasSelection
                    ? `${surfaceSelectionInfo.count.toLocaleString()} face${surfaceSelectionInfo.count === 1 ? '' : 's'} selected${surfaceSelectionInfo.inverted ? ' (inverted)' : ''}. Painting and fills stay on these faces.`
                    : surfaceMode === 'material'
                      ? 'Click the model to select a material.'
                      : 'Click to select · Shift+click add/remove · drag to brush · double-click: connected.'}
                </div>
                {hasSelection && surfaceSelectionInfo.overlapFaces > 0 ? (
                  <div role="status" style={{ fontSize: 11, color: '#ffd27a', lineHeight: 1.35 }}>
                    {`${surfaceSelectionInfo.overlapFaces.toLocaleString()} unselected face${surfaceSelectionInfo.overlapFaces === 1 ? '' : 's'} share texture space with this selection. The 3D view keeps paint on the selected faces, but the exported texture cannot separate them, so those faces will show the same paint after export.`}
                  </div>
                ) : null}
                {hasSelection ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <button type="button" style={actionButtonStyle} title="Fill the selected faces with the current colour (or PBR material)" onClick={() => runSurfaceAction('fillSelectedSurfaceFaces')}>Fill selection</button>
                    <button type="button" style={actionButtonStyle} title="Select every face connected to the selection" onClick={() => runSurfaceAction('selectConnectedSurfaceFaces')}>Connected</button>
                    <button type="button" style={actionButtonStyle} title="Grow the selection by one ring of faces" onClick={() => runSurfaceAction('growSurfaceSelection')}>Grow</button>
                    <button type="button" style={actionButtonStyle} title="Shrink the selection by one ring of faces" onClick={() => runSurfaceAction('shrinkSurfaceSelection')}>Shrink</button>
                    <button type="button" style={actionButtonStyle} title="Invert the selection" onClick={() => runSurfaceAction('invertSurfaceSelection')}>Invert</button>
                    <button type="button" style={actionButtonStyle} title="Frame the selection (Numpad .)" onClick={() => runSurfaceAction('frameSurfaceSelection')}>Frame</button>
                    <button type="button" style={actionButtonStyle} title="Clear the selection (Esc)" onClick={() => runSurfaceAction('clearSurfaceSelection')}>Clear</button>
                  </div>
                ) : null}
              </>
            ) : null}
            <div role="group" aria-label="View" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: 'rgba(255,255,255,0.6)', marginRight: 2 }}>VIEW</span>
              {[
                ['front', 'Front', 'Numpad 1'],
                ['back', 'Back', 'Ctrl+Numpad 1'],
                ['left', 'Left', 'Ctrl+Numpad 3'],
                ['right', 'Right', 'Numpad 3'],
                ['top', 'Top', 'Numpad 7'],
                ['bottom', 'Bottom', 'Ctrl+Numpad 7'],
              ].map(([preset, label, shortcut]) => (
                <button key={preset} type="button" style={{ ...actionButtonStyle, padding: '3px 6px' }} title={`${label} view (${shortcut})`} onClick={() => runSurfaceAction('setViewPreset', preset)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        );
      })() : null}

      {showControlsPanel ? (
        <OrientationGizmo disabled={!currentModelRef.current} onMove={moveViewByStep} onZoom={zoomViewByStep} onReset={resetView} />
      ) : null}

      {error ? (
        <div role="alert" style={{ position: 'absolute', left: 12, bottom: 72, zIndex: 3, maxWidth: 'min(620px, calc(100% - 24px))', borderRadius: 8, border: '1px solid rgba(248,113,113,0.45)', background: 'rgba(78,24,24,0.94)', color: '#ffe7e7', padding: '9px 11px', fontSize: 12, fontWeight: 750 }}>
          {error}
        </div>
      ) : null}


      <div style={{ position: 'absolute', right: 12, bottom: 12, zIndex: 2, borderRadius: 8, background: 'rgba(35,35,35,0.78)', color: 'rgba(255,255,255,0.76)', padding: '7px 9px', fontSize: 11, lineHeight: 1.45, pointerEvents: 'none' }}>
        Middle drag: orbit · Shift+Middle drag: pan · Wheel: zoom to cursor · Alt+click: orbit point · Numpad 1/3/7: views · Hold Ctrl: left drag pan, right drag orbit{activeDecalLayer ? (String(activeTool || '') === 'move' ? ' · Transform tool: drag=move · corner handles=resize · round handle=rotate' : ' · Select the Transform tool to move/rotate/scale the active image layer') : ''}
      </div>

    </section>
  );
}
